import * as i0 from '@angular/core';
import { inject, ElementRef, Directive, input, Renderer2, effect, TemplateRef, booleanAttribute, untracked, NgModule, computed, Injectable, Component, afterNextRender, linkedSignal, output, signal, contentChildren, DestroyRef, forwardRef, DOCUMENT, NgZone, ChangeDetectorRef, contentChild, Input, ChangeDetectionStrategy, IterableDiffers, numberAttribute, ViewChild, ContentChildren, ViewChildren, PLATFORM_ID, RendererFactory2, viewChildren, viewChild, ViewContainerRef, inputBinding, outputBinding, KeyValueDiffers, Pipe, HostBinding, model, afterEveryRender, afterRenderEffect, HostListener, Injector, runInInjectionContext } from '@angular/core';
import { NgTemplateOutlet, I18nPluralPipe, NgStyle, AsyncPipe, isPlatformServer, isPlatformBrowser, formatDate } from '@angular/common';
import { animation, animate, style, AnimationBuilder, useAnimation, trigger, state, transition, group, query, animateChild } from '@angular/animations';
import { toObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import * as i1 from '@angular/forms';
import { FormsModule, NG_VALUE_ACCESSOR, FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Subject, fromEvent, skip, switchMap, timer, merge, takeWhile, map as map$1, debounceTime as debounceTime$1 } from 'rxjs';
import { tap, filter, distinctUntilChanged, debounceTime, map, delay, combineLatestWith, take, finalize, zipWith, withLatestFrom } from 'rxjs/operators';
import * as i1$2 from '@angular/cdk/a11y';
import { FocusKeyManager, FocusMonitor, A11yModule } from '@angular/cdk/a11y';
import { createPopper } from '@popperjs/core';
import { SelectionModel } from '@angular/cdk/collections';
import { DomPortal, CdkPortalOutlet } from '@angular/cdk/portal';
import { DomSanitizer } from '@angular/platform-browser';
import * as i1$1 from '@angular/router';
import { RouterModule, Router, ActivatedRoute, NavigationEnd, RouterLink } from '@angular/router';
import { BreakpointObserver } from '@angular/cdk/layout';
import { IconDirective } from '@coreui/icons-angular';

var BreakpointInfix;
(function (BreakpointInfix) {
    BreakpointInfix["xs"] = "xs";
    BreakpointInfix["sm"] = "sm";
    BreakpointInfix["md"] = "md";
    BreakpointInfix["lg"] = "lg";
    BreakpointInfix["xl"] = "xl";
    BreakpointInfix["xxl"] = "xxl";
})(BreakpointInfix || (BreakpointInfix = {}));

class ElementRefDirective {
    elementRef = inject(ElementRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementRefDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: ElementRefDirective, isStandalone: true, selector: "[cElementRef]", exportAs: ["cElementRef"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementRefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cElementRef]',
                    exportAs: 'cElementRef'
                }]
        }] });

class HtmlAttributesDirective {
    cHtmlAttr = input(...(ngDevMode ? [undefined, { debugName: "cHtmlAttr" }] : []));
    #renderer = inject(Renderer2);
    #elementRef = inject(ElementRef);
    #attrEffect = effect(() => {
        const attribs = this.cHtmlAttr();
        for (const attr in attribs) {
            if (attr === 'style' && typeof attribs[attr] === 'object') {
                this.setStyle(attribs[attr]);
            }
            else if (attr === 'class') {
                this.addClass(attribs[attr]);
            }
            else {
                this.setAttrib(attr, attribs[attr]);
            }
        }
    }, ...(ngDevMode ? [{ debugName: "#attrEffect" }] : []));
    setStyle(styles) {
        for (const style in styles) {
            if (style) {
                this.#renderer.setStyle(this.#elementRef.nativeElement, style, styles[style]);
            }
        }
    }
    addClass(classes) {
        const classArray = Array.isArray(classes) ? classes : classes.split(' ');
        classArray
            .filter((element) => element.length > 0)
            .forEach((element) => {
            this.#renderer.addClass(this.#elementRef.nativeElement, element);
        });
    }
    setAttrib(key, value) {
        value !== null
            ? this.#renderer.setAttribute(this.#elementRef.nativeElement, key, value)
            : this.#renderer.removeAttribute(this.#elementRef.nativeElement, key);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HtmlAttributesDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: HtmlAttributesDirective, isStandalone: true, selector: "[cHtmlAttr]", inputs: { cHtmlAttr: { classPropertyName: "cHtmlAttr", publicName: "cHtmlAttr", isSignal: true, isRequired: false, transformFunction: null } }, exportAs: ["cHtmlAttr"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HtmlAttributesDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cHtmlAttr]',
                    exportAs: 'cHtmlAttr'
                }]
        }], propDecorators: { cHtmlAttr: [{ type: i0.Input, args: [{ isSignal: true, alias: "cHtmlAttr", required: false }] }] } });

class TemplateIdDirective {
    templateRef = inject(TemplateRef);
    cTemplateId = input.required(...(ngDevMode ? [{ debugName: "cTemplateId" }] : []));
    get id() {
        return this.cTemplateId();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TemplateIdDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TemplateIdDirective, isStandalone: true, selector: "[cTemplateId]", inputs: { cTemplateId: { classPropertyName: "cTemplateId", publicName: "cTemplateId", isSignal: true, isRequired: true, transformFunction: null } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TemplateIdDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTemplateId]'
                }]
        }], propDecorators: { cTemplateId: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTemplateId", required: true }] }] } });

class ThemeDirective {
    #hostElement = inject(ElementRef);
    #renderer = inject(Renderer2);
    /**
     * Add dark theme attribute.
     * @return 'dark' | 'light' | undefined
     */
    colorScheme = input(...(ngDevMode ? [undefined, { debugName: "colorScheme" }] : []));
    #colorSchemeChange = effect(() => {
        const colorScheme = this.colorScheme();
        colorScheme ? this.setTheme(colorScheme) : this.unsetTheme();
    }, ...(ngDevMode ? [{ debugName: "#colorSchemeChange" }] : []));
    dark = input(false, { ...(ngDevMode ? { debugName: "dark" } : {}), transform: booleanAttribute });
    #darkChange = effect(() => {
        const darkTheme = this.dark() || untracked(this.colorScheme) === 'dark';
        darkTheme ? this.setTheme('dark') : this.unsetTheme();
    }, ...(ngDevMode ? [{ debugName: "#darkChange" }] : []));
    setTheme(theme) {
        if (theme) {
            this.#renderer.setAttribute(this.#hostElement.nativeElement, 'data-coreui-theme', theme);
        }
    }
    unsetTheme() {
        this.#renderer.removeAttribute(this.#hostElement.nativeElement, 'data-coreui-theme');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ThemeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ThemeDirective, isStandalone: true, selector: "[cTheme]", inputs: { colorScheme: { classPropertyName: "colorScheme", publicName: "colorScheme", isSignal: true, isRequired: false, transformFunction: null }, dark: { classPropertyName: "dark", publicName: "dark", isSignal: true, isRequired: false, transformFunction: null } }, exportAs: ["cTheme"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ThemeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTheme]',
                    exportAs: 'cTheme'
                }]
        }], propDecorators: { colorScheme: [{ type: i0.Input, args: [{ isSignal: true, alias: "colorScheme", required: false }] }], dark: [{ type: i0.Input, args: [{ isSignal: true, alias: "dark", required: false }] }] } });

class SharedModule {
    static forRoot() {
        return {
            ngModule: SharedModule,
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SharedModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SharedModule, imports: [ElementRefDirective, HtmlAttributesDirective, TemplateIdDirective, ThemeDirective], exports: [ElementRefDirective, HtmlAttributesDirective, TemplateIdDirective, ThemeDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SharedModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SharedModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ElementRefDirective, HtmlAttributesDirective, TemplateIdDirective, ThemeDirective],
                    exports: [ElementRefDirective, HtmlAttributesDirective, TemplateIdDirective, ThemeDirective],
                }]
        }] });

class AccordionButtonDirective {
    /**
     * Toggles an accordion button collapsed state. Use in accordionHeaderTemplate. [docs]
     * @type boolean
     */
    collapsed = input(undefined, ...(ngDevMode ? [{ debugName: "collapsed" }] : []));
    /**
     * Default type for cAccordionButton. [docs]
     * @type string
     * @default 'button'
     */
    type = input('button', ...(ngDevMode ? [{ debugName: "type" }] : []));
    hostClasses = computed(() => {
        return {
            'accordion-button': true,
            collapsed: this.collapsed()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ariaExpanded = computed(() => !this.collapsed(), ...(ngDevMode ? [{ debugName: "ariaExpanded" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: AccordionButtonDirective, isStandalone: true, selector: "[cAccordionButton]", inputs: { collapsed: { classPropertyName: "collapsed", publicName: "collapsed", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.type": "type()", "attr.aria-expanded": "ariaExpanded()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cAccordionButton]',
                    host: { '[class]': 'hostClasses()', '[attr.type]': 'type()', '[attr.aria-expanded]': 'ariaExpanded()' }
                }]
        }], propDecorators: { collapsed: [{ type: i0.Input, args: [{ isSignal: true, alias: "collapsed", required: false }] }], type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }] } });

class AccordionService {
    items = [];
    alwaysOpen = false;
    addItem(item) {
        this.items.push(item);
    }
    removeItem(item) {
        const index = this.items.indexOf(item);
        if (index !== -1) {
            this.items.splice(index, 1);
        }
    }
    toggleItem(item) {
        item.itemVisible.update((value) => !value);
        this.closeOtherItems(item);
    }
    closeOtherItems(openItem) {
        if (!this.alwaysOpen) {
            this.items.forEach((item) => {
                if (item !== openItem) {
                    item.itemVisible.set(false);
                }
            });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionService, decorators: [{
            type: Injectable
        }] });

class AccordionComponent {
    #accordionService = inject(AccordionService);
    /**
     * Removes the default background-color, some borders, and some rounded corners to render accordions edge-to-edge with their parent container.
     * @type boolean
     */
    flush = input(false, { ...(ngDevMode ? { debugName: "flush" } : {}), transform: booleanAttribute });
    /**
     * Make accordion items stay open when another item is opened
     * @type boolean
     */
    alwaysOpen = input(false, { ...(ngDevMode ? { debugName: "alwaysOpen" } : {}), transform: booleanAttribute });
    #alwaysOpenEffect = effect(() => {
        this.#accordionService.alwaysOpen = this.alwaysOpen();
    }, ...(ngDevMode ? [{ debugName: "#alwaysOpenEffect" }] : []));
    hostClasses = computed(() => ({
        accordion: true,
        'accordion-flush': this.flush()
    }), ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: AccordionComponent, isStandalone: true, selector: "c-accordion", inputs: { flush: { classPropertyName: "flush", publicName: "flush", isSignal: true, isRequired: false, transformFunction: null }, alwaysOpen: { classPropertyName: "alwaysOpen", publicName: "alwaysOpen", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, providers: [AccordionService], exportAs: ["cAccordionItem"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-accordion', template: '<ng-content />', exportAs: 'cAccordionItem', providers: [AccordionService], host: { '[class]': 'hostClasses()' }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { flush: [{ type: i0.Input, args: [{ isSignal: true, alias: "flush", required: false }] }], alwaysOpen: [{ type: i0.Input, args: [{ isSignal: true, alias: "alwaysOpen", required: false }] }] } });

const expandAnimation = animation([animate('{{ time }} {{ easing }}')]);
const collapseAnimation = animation([
    style({ height: '*', minHeight: '*' }),
    animate('{{ time }} {{ easing }}', style({ height: 0, minHeight: 0 }))
]);
const expandHorizontalAnimation = animation([animate('{{ time }} {{ easing }}')]);
const collapseHorizontalAnimation = animation([animate('{{ time }} {{ easing }}')]);

class CollapseDirective {
    #animationBuilder = inject(AnimationBuilder);
    #hostElement = inject(ElementRef);
    #renderer = inject(Renderer2);
    #player = undefined;
    constructor() {
        afterNextRender({
            read: () => {
                this.#initialized.set(true);
            }
        });
    }
    /**
     * @ignore
     */
    animateInput = input(true, { ...(ngDevMode ? { debugName: "animateInput" } : {}), transform: booleanAttribute, alias: 'animate' });
    animate = linkedSignal({ ...(ngDevMode ? { debugName: "animate" } : {}), source: this.animateInput,
        computation: (value) => value });
    /**
     * Set horizontal collapsing to transition the width instead of height.
     * @type boolean
     * @default false
     */
    horizontal = input(false, { ...(ngDevMode ? { debugName: "horizontal" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of collapsible element.
     * @type boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visibleChange = output();
    visible = linkedSignal({ ...(ngDevMode ? { debugName: "visible" } : {}), source: this.visibleInput, computation: (value) => value });
    #initialized = signal(false, ...(ngDevMode ? [{ debugName: "#initialized" }] : []));
    #visibleEffect = effect(() => {
        const visible = this.visible();
        if (this.#initialized()) {
            this.createPlayer(visible);
        }
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    /**
     * Add `navbar` prop for grouping and hiding navbar contents by a parent breakpoint.
     * @type boolean
     * @default false
     */
    navbar = input(false, { ...(ngDevMode ? { debugName: "navbar" } : {}), transform: booleanAttribute });
    /**
     * @ignore
     */
    duration = input('350ms', ...(ngDevMode ? [{ debugName: "duration" }] : []));
    /**
     * @ignore
     */
    transition = input('ease', ...(ngDevMode ? [{ debugName: "transition" }] : []));
    /**
     * Event emitted on visibility change. [docs]
     * @type string
     */
    collapseChange = output();
    hostClasses = computed(() => {
        return {
            'navbar-collapse': this.navbar(),
            'collapse-horizontal': this.horizontal()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnDestroy() {
        this.destroyPlayer();
    }
    toggle(visible = !this.visible()) {
        this.visible.set(visible);
    }
    destroyPlayer() {
        this.#player?.destroy();
        this.#player = undefined;
    }
    createPlayer(visible = this.visible()) {
        if (this.#player?.hasStarted()) {
            this.destroyPlayer();
        }
        const host = this.#hostElement.nativeElement;
        if (visible) {
            this.#renderer.removeStyle(host, 'display');
        }
        const duration = this.animate() ? this.duration() : '0ms';
        const expand = this.horizontal() ? expandHorizontalAnimation : expandAnimation;
        const collapse = this.horizontal() ? collapseHorizontalAnimation : collapseAnimation;
        const dimension = this.horizontal() ? 'width' : 'height';
        const capitalizedDimension = dimension[0].toUpperCase() + dimension.slice(1);
        const scrollSize = `scroll${capitalizedDimension}`;
        const animationFactory = this.#animationBuilder?.build(useAnimation(visible ? expand : collapse, { params: { time: duration, easing: this.transition() } }));
        this.#player = animationFactory.create(host);
        !visible && host.offsetHeight && host.style[dimension] && host.scrollHeight;
        this.#renderer.setStyle(host, dimension, visible ? 0 : `${host.getBoundingClientRect()[dimension]}px`);
        this.#player.onStart(() => {
            this.setMaxSize();
            this.#renderer.removeClass(host, 'collapse');
            this.#renderer.addClass(host, 'collapsing');
            this.#renderer.removeClass(host, 'show');
            this.#renderer.setStyle(host, dimension, visible ? `${host[scrollSize]}px` : '');
            if (this.#player) {
                this.collapseChange?.emit(visible ? 'opening' : 'collapsing');
            }
        });
        this.#player.onDone(() => {
            this.#renderer.removeClass(host, 'collapsing');
            this.#renderer.addClass(host, 'collapse');
            if (visible) {
                this.#renderer.addClass(host, 'show');
                this.#renderer.setStyle(host, dimension, '');
            }
            else {
                this.#renderer.removeClass(host, 'show');
            }
            if (this.#player) {
                this.collapseChange?.emit(visible ? 'open' : 'collapsed');
                this.visibleChange?.emit(visible);
            }
            this.destroyPlayer();
        });
        this.#player?.play();
    }
    setMaxSize() {
        const host = this.#hostElement.nativeElement;
        if (this.horizontal()) {
            host.scrollWidth > 0 && this.#renderer.setStyle(host, 'maxWidth', `${host.scrollWidth}px`);
            // } else {
            //   host.scrollHeight > 0 && this.#renderer.setStyle(host, 'maxHeight', `${host.scrollHeight}px`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CollapseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: CollapseDirective, isStandalone: true, selector: "[cCollapse]", inputs: { animateInput: { classPropertyName: "animateInput", publicName: "animate", isSignal: true, isRequired: false, transformFunction: null }, horizontal: { classPropertyName: "horizontal", publicName: "horizontal", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, navbar: { classPropertyName: "navbar", publicName: "navbar", isSignal: true, isRequired: false, transformFunction: null }, duration: { classPropertyName: "duration", publicName: "duration", isSignal: true, isRequired: false, transformFunction: null }, transition: { classPropertyName: "transition", publicName: "transition", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange", collapseChange: "collapseChange" }, host: { properties: { "class": "hostClasses()", "style": "{ display: \"none\" }" } }, exportAs: ["cCollapse"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CollapseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCollapse]',
                    exportAs: 'cCollapse',
                    host: { '[class]': 'hostClasses()', '[style]': '{ display: "none" }' }
                }]
        }], ctorParameters: () => [], propDecorators: { animateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "animate", required: false }] }], horizontal: [{ type: i0.Input, args: [{ isSignal: true, alias: "horizontal", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], navbar: [{ type: i0.Input, args: [{ isSignal: true, alias: "navbar", required: false }] }], duration: [{ type: i0.Input, args: [{ isSignal: true, alias: "duration", required: false }] }], transition: [{ type: i0.Input, args: [{ isSignal: true, alias: "transition", required: false }] }], collapseChange: [{ type: i0.Output, args: ["collapseChange"] }] } });

class CollapseModule {
    static forRoot() {
        return { ngModule: CollapseModule, providers: [] };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CollapseModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CollapseModule, imports: [CollapseDirective], exports: [CollapseDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CollapseModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CollapseModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [CollapseDirective],
                    imports: [CollapseDirective]
                }]
        }] });

let nextId$3 = 0;
class AccordionItemComponent {
    #accordionService = inject(AccordionService);
    /**
     * Toggle an accordion item programmatically
     * @return boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    itemVisible = signal(false, ...(ngDevMode ? [{ debugName: "itemVisible" }] : []));
    #visibleInputChange = effect(() => {
        this.visible = this.visibleInput();
    }, ...(ngDevMode ? [{ debugName: "#visibleInputChange" }] : []));
    set visible(value) {
        this.itemVisible.set(value);
    }
    get visible() {
        return this.itemVisible();
    }
    contentId = `accordion-item-${nextId$3++}`;
    get itemContext() {
        return { $implicit: this.itemVisible() };
    }
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    templates = computed(() => {
        return this.contentTemplates().reduce((acc, child) => {
            acc[child.id] = child.templateRef;
            return acc;
        }, {});
    }, ...(ngDevMode ? [{ debugName: "templates" }] : []));
    ngOnInit() {
        this.#accordionService.addItem(this);
    }
    ngOnDestroy() {
        this.#accordionService.removeItem(this);
    }
    toggleItem() {
        this.#accordionService.toggleItem(this);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: AccordionItemComponent, isStandalone: true, selector: "c-accordion-item", inputs: { visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, host: { classAttribute: "accordion-item" }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cAccordionItem"], ngImport: i0, template: "@let tmpl = templates();\n<ng-container>\n  <div class=\"accordion-header\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionHeaderTemplate'] || defaultAccordionHeaderTemplate; context: itemContext\" />\n  </div>\n  <div class=\"accordion-collapse\" cCollapse [visible]=\"itemVisible()\" [attr.aria-expanded]=\"itemVisible()\" [id]=\"contentId\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionBodyTemplate'] || defaultAccordionBodyTemplate; context: itemContext\" />\n  </div>\n</ng-container>\n\n<ng-template #defaultAccordionHeaderTemplate>\n  <button cAccordionButton [collapsed]=\"!itemVisible()\" [attr.aria-controls]=\"contentId\" (click)=\"toggleItem()\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionHeader'] || defaultAccordionHeaderContentTemplate; context: itemContext\" />\n  </button>\n</ng-template>\n\n<ng-template #defaultAccordionHeaderContentTemplate>\n  <ng-content />\n</ng-template>\n\n<ng-template #defaultAccordionBodyTemplate>\n  <div class=\"accordion-body\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionBody'] || defaultAccordionBodyContentTemplate; context: itemContext\" />\n  </div>\n</ng-template>\n\n<ng-template #defaultAccordionBodyContentTemplate>\n  <ng-content />\n</ng-template>\n", styles: [":host{display:block;overflow:hidden}\n"], dependencies: [{ kind: "directive", type: AccordionButtonDirective, selector: "[cAccordionButton]", inputs: ["collapsed", "type"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CollapseDirective, selector: "[cCollapse]", inputs: ["animate", "horizontal", "visible", "navbar", "duration", "transition"], outputs: ["visibleChange", "collapseChange"], exportAs: ["cCollapse"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-accordion-item', exportAs: 'cAccordionItem', imports: [AccordionButtonDirective, NgTemplateOutlet, CollapseDirective], host: { class: 'accordion-item' }, template: "@let tmpl = templates();\n<ng-container>\n  <div class=\"accordion-header\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionHeaderTemplate'] || defaultAccordionHeaderTemplate; context: itemContext\" />\n  </div>\n  <div class=\"accordion-collapse\" cCollapse [visible]=\"itemVisible()\" [attr.aria-expanded]=\"itemVisible()\" [id]=\"contentId\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionBodyTemplate'] || defaultAccordionBodyTemplate; context: itemContext\" />\n  </div>\n</ng-container>\n\n<ng-template #defaultAccordionHeaderTemplate>\n  <button cAccordionButton [collapsed]=\"!itemVisible()\" [attr.aria-controls]=\"contentId\" (click)=\"toggleItem()\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionHeader'] || defaultAccordionHeaderContentTemplate; context: itemContext\" />\n  </button>\n</ng-template>\n\n<ng-template #defaultAccordionHeaderContentTemplate>\n  <ng-content />\n</ng-template>\n\n<ng-template #defaultAccordionBodyTemplate>\n  <div class=\"accordion-body\">\n    <ng-container *ngTemplateOutlet=\"tmpl['accordionBody'] || defaultAccordionBodyContentTemplate; context: itemContext\" />\n  </div>\n</ng-template>\n\n<ng-template #defaultAccordionBodyContentTemplate>\n  <ng-content />\n</ng-template>\n", styles: [":host{display:block;overflow:hidden}\n"] }]
        }], propDecorators: { visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class AccordionModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: AccordionModule, imports: [AccordionButtonDirective,
            AccordionComponent,
            AccordionItemComponent], exports: [AccordionComponent,
            AccordionButtonDirective,
            AccordionItemComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionModule, providers: [
            AccordionService
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AccordionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        AccordionButtonDirective,
                        AccordionComponent,
                        AccordionItemComponent
                    ],
                    exports: [
                        AccordionComponent,
                        AccordionButtonDirective,
                        AccordionItemComponent
                    ],
                    providers: [
                        AccordionService
                    ]
                }]
        }] });

const normalizeOptionsArray = (options) => {
    return options.map((option) => {
        if (typeof option === 'string') {
            option = { label: option };
        }
        if (option.options) {
            option.options = [...normalizeOptionsArray(option.options)];
        }
        return { ...option };
    });
};
const filterOptions = (options, search) => {
    const result = [];
    options.forEach((item) => {
        if ('options' in item && Array.isArray(item.options)) {
            const filteredOptions = filterOptions(item.options, search);
            if (filteredOptions.length > 0) {
                result.push({ ...item, options: filteredOptions });
            }
        }
        else if (getOptionLabel(item).toLowerCase().includes(search.toLowerCase())) {
            result.push(item);
        }
    });
    return result;
};
const flattenOptionsArray = (options) => {
    const optionsList = [];
    for (const option of options) {
        if ('options' in option && Array.isArray(option.options)) {
            optionsList.push(...option.options);
        }
        else {
            optionsList.push(option);
        }
    }
    return optionsList;
};
const isExternalSearch = (search) => {
    return ((typeof search === 'string' && search === 'external') || (typeof search === 'object' && search.external === true));
};
const isGlobalSearch = (search) => {
    return (typeof search === 'string' && search === 'global') || (typeof search === 'object' && search.global === true);
};
const getOptionLabel = (option) => (typeof option === 'string' ? option : option.label);
const highlightSubstring = (string, query) => {
    if (query) {
        const regex = new RegExp(query, 'gi');
        return string.replace(regex, (string) => `<strong>${string}</strong>`);
    }
    return string;
};
const isOptionDisabled = (option) => typeof option === 'string' ? false : option.disabled;
const isOptionSelected = (option, selected) => {
    if (!selected) {
        return false;
    }
    if (typeof option === 'string' && typeof selected === 'string') {
        return selected === option;
    }
    if (typeof option !== 'string' && typeof selected !== 'string') {
        return selected.label === option.label;
    }
    return false;
};
const getFirstOptionByLabel = (value, options) => {
    for (const option of options) {
        if ('options' in option && Array.isArray(option.options)) {
            const found = getFirstOptionByLabel(value, option.options);
            if (found) {
                return found;
            }
        }
        if (getOptionLabel(option).toLowerCase() === value.toLowerCase()) {
            return option;
        }
    }
    return null;
};
const getFirstOptionByValue = (value, options) => {
    for (const option of options) {
        if ('options' in option && Array.isArray(option.options)) {
            const found = getFirstOptionByValue(value, option.options);
            if (found) {
                return found;
            }
        }
        if ('value' in option && option.value === value) {
            return option;
        }
    }
    return null;
};
const findHintOption = (searchValue, options) => {
    return searchValue.length > 0
        ? options
            .flatMap((option) => (Array.isArray(option.options) ? option.options : [option]))
            .find((option) => getOptionLabel(option).toLowerCase().startsWith(searchValue.toLowerCase()))
        : undefined;
};

class DropdownDividerDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownDividerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: DropdownDividerDirective, isStandalone: true, selector: "[cDropdownDivider]", host: { classAttribute: "dropdown-divider" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownDividerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownDivider]',
                    host: { class: 'dropdown-divider' }
                }]
        }] });

class DropdownHeaderDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownHeaderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: DropdownHeaderDirective, isStandalone: true, selector: "[cDropdownHeader]", host: { classAttribute: "dropdown-header" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownHeader]',
                    host: { class: 'dropdown-header' }
                }]
        }] });

class DropdownService {
    #dropdownState = new BehaviorSubject({});
    dropdownState$ = this.#dropdownState.asObservable();
    toggle(state) {
        this.#dropdownState.next(state);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class DropdownMenuDirective {
    #destroyRef = inject(DestroyRef);
    elementRef = inject(ElementRef);
    #dropdownService = inject(DropdownService);
    #focusKeyManager;
    /**
     * Set alignment of dropdown menu.
     * @return 'start' | 'end'
     */
    alignment = input(...(ngDevMode ? [undefined, { debugName: "alignment" }] : []));
    /**
     * Toggle the visibility of dropdown menu component.
     * @return boolean
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal({ ...(ngDevMode ? { debugName: "visible" } : {}), source: this.visibleInput,
        computation: (value) => value });
    hostClasses = computed(() => {
        const alignment = this.alignment();
        const visible = this.visible();
        return {
            'dropdown-menu': true,
            [`dropdown-menu-${alignment}`]: !!alignment,
            show: visible
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    hostStyles = computed(() => {
        // workaround for popper position calculate (see also: dropdown.component)
        const visible = this.visible();
        return {
            visibility: visible ? null : '',
            display: visible ? null : ''
        };
    }, ...(ngDevMode ? [{ debugName: "hostStyles" }] : []));
    onKeyDown($event) {
        if (!this.visible()) {
            return;
        }
        if (['Space', 'ArrowDown'].includes($event.code)) {
            $event.preventDefault();
        }
        this.#focusKeyManager.onKeydown($event);
    }
    onKeyUp($event) {
        if (!this.visible()) {
            return;
        }
        if (['Tab'].includes($event.key)) {
            if (this.#focusKeyManager.activeItem) {
                $event.shiftKey ? this.#focusKeyManager.setPreviousItemActive() : this.#focusKeyManager.setNextItemActive();
            }
            else {
                this.#focusKeyManager.setFirstItemActive();
            }
        }
    }
    dropdownItemsContent = contentChildren(forwardRef(() => DropdownItemDirective), { ...(ngDevMode ? { debugName: "dropdownItemsContent" } : {}), descendants: true });
    items$ = toObservable(this.dropdownItemsContent);
    ngAfterContentInit() {
        this.focusKeyManagerInit();
        this.items$
            .pipe(tap((change) => {
            this.focusKeyManagerInit();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    ngOnInit() {
        this.#dropdownService.dropdownState$
            .pipe(tap((state) => {
            if ('visible' in state) {
                this.visible.update((visible) => (state.visible === 'toggle' ? !visible : state.visible));
                if (!this.visible()) {
                    this.#focusKeyManager?.setActiveItem(-1);
                }
            }
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    focusKeyManagerInit() {
        this.#focusKeyManager = new FocusKeyManager(this.dropdownItemsContent())
            .withHomeAndEnd()
            .withPageUpDown()
            .withWrap()
            .skipPredicate((dropdownItem) => dropdownItem.disabled === true);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownMenuDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.2.0", version: "21.1.4", type: DropdownMenuDirective, isStandalone: true, selector: "[cDropdownMenu]", inputs: { alignment: { classPropertyName: "alignment", publicName: "alignment", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "keydown": "onKeyDown($event)", "keyup": "onKeyUp($event)" }, properties: { "class": "hostClasses()", "style": "hostStyles()" }, classAttribute: "dropdown-menu" }, queries: [{ propertyName: "dropdownItemsContent", predicate: i0.forwardRef(() => DropdownItemDirective), descendants: true, isSignal: true }], exportAs: ["cDropdownMenu"], hostDirectives: [{ directive: ThemeDirective, inputs: ["dark", "dark"] }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownMenu]',
                    exportAs: 'cDropdownMenu',
                    hostDirectives: [{ directive: ThemeDirective, inputs: ['dark'] }],
                    host: {
                        class: 'dropdown-menu',
                        '[class]': 'hostClasses()',
                        '[style]': 'hostStyles()',
                        '(keydown)': 'onKeyDown($event)',
                        '(keyup)': 'onKeyUp($event)'
                    }
                }]
        }], propDecorators: { alignment: [{ type: i0.Input, args: [{ isSignal: true, alias: "alignment", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], dropdownItemsContent: [{ type: i0.ContentChildren, args: [forwardRef(() => DropdownItemDirective), { ...{ descendants: true }, isSignal: true }] }] } });

// lightweight injection token
class DropdownToken {
}
class DropdownToggleDirective {
    // injections
    #destroyRef = inject(DestroyRef);
    elementRef = inject(ElementRef);
    #dropdownService = inject(DropdownService);
    dropdown = inject(DropdownToken, { optional: true });
    /**
     * Reference to dropdown component.
     * @return DropdownComponent | undefined
     * @default undefined
     */
    dropdownComponent = input(...(ngDevMode ? [undefined, { debugName: "dropdownComponent" }] : []));
    /**
     * Disables the toggler.
     * @return boolean
     * @default false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    /**
     * Enables pseudo element caret on toggler.
     * @return boolean
     */
    caret = input(true, ...(ngDevMode ? [{ debugName: "caret" }] : []));
    /**
     * Create split button dropdowns with virtually the same markup as single button dropdowns,
     * but with the addition of `.dropdown-toggle-split` class for proper spacing around the dropdown caret.
     * @return boolean
     * @default false
     */
    split = input(false, { ...(ngDevMode ? { debugName: "split" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            'dropdown-toggle': this.caret(),
            'dropdown-toggle-split': this.split(),
            disabled: this.disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    #ariaExpanded = signal(false, ...(ngDevMode ? [{ debugName: "#ariaExpanded" }] : []));
    get ariaExpanded() {
        return this.#ariaExpanded();
    }
    onClick($event) {
        $event.preventDefault();
        !this.disabled() && this.#dropdownService.toggle({ visible: 'toggle', dropdown: this.dropdown });
    }
    ngAfterViewInit() {
        const dropdownComponent = this.dropdownComponent();
        if (dropdownComponent) {
            this.dropdown = dropdownComponent;
            this.#dropdownService = dropdownComponent?.dropdownService;
        }
        if (this.dropdown) {
            const dropdown = this.dropdown;
            dropdown?.visibleChange?.subscribe((visible) => {
                this.#ariaExpanded.set(visible);
            });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: DropdownToggleDirective, isStandalone: true, selector: "[cDropdownToggle]", inputs: { dropdownComponent: { classPropertyName: "dropdownComponent", publicName: "dropdownComponent", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, caret: { classPropertyName: "caret", publicName: "caret", isSignal: true, isRequired: false, transformFunction: null }, split: { classPropertyName: "split", publicName: "split", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "onClick($event)" }, properties: { "class": "hostClasses()", "attr.aria-expanded": "ariaExpanded" } }, providers: [{ provide: DropdownToken, useExisting: forwardRef(() => DropdownComponent) }], exportAs: ["cDropdownToggle"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownToggle]',
                    providers: [{ provide: DropdownToken, useExisting: forwardRef(() => DropdownComponent) }],
                    exportAs: 'cDropdownToggle',
                    host: {
                        '[class]': 'hostClasses()',
                        '[attr.aria-expanded]': 'ariaExpanded',
                        '(click)': 'onClick($event)'
                    }
                }]
        }], propDecorators: { dropdownComponent: [{ type: i0.Input, args: [{ isSignal: true, alias: "dropdownComponent", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], caret: [{ type: i0.Input, args: [{ isSignal: true, alias: "caret", required: false }] }], split: [{ type: i0.Input, args: [{ isSignal: true, alias: "split", required: false }] }] } });
class DropdownComponent {
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #elementRef = inject(ElementRef);
    #renderer = inject(Renderer2);
    #ngZone = inject(NgZone);
    #changeDetectorRef = inject(ChangeDetectorRef);
    dropdownService = inject(DropdownService);
    constructor() {
        this.dropdownStateSubscribe();
    }
    /**
     * Set alignment of dropdown menu.
     * @return {'start' | 'end' | { xs: 'start' | 'end' } | { sm: 'start' | 'end' } | { md: 'start' | 'end' } | { lg: 'start' | 'end' } | { xl: 'start' | 'end'} | { xxl: 'start' | 'end'}}
     */
    alignment = input(...(ngDevMode ? [undefined, { debugName: "alignment" }] : []));
    /**
     * Automatically close dropdown when clicking outside the dropdown menu.
     */
    autoClose = input(true, ...(ngDevMode ? [{ debugName: "autoClose" }] : []));
    /**
     * Sets a specified  direction and location of the dropdown menu.
     * @return 'dropup' | 'dropend' | 'dropstart'
     */
    direction = input(...(ngDevMode ? [undefined, { debugName: "direction" }] : []));
    /**
     * Describes the placement of your component after Popper.js has applied all the modifiers
     * that may have flipped or altered the originally provided placement property.
     * @return Placement
     */
    placement = input('bottom-start', ...(ngDevMode ? [{ debugName: "placement" }] : []));
    /**
     * If you want to disable dynamic positioning set this property to `false`.
     * @return boolean
     * @default true
     */
    popper = input(true, { ...(ngDevMode ? { debugName: "popper" } : {}), transform: booleanAttribute });
    /**
     * Optional popper Options object, placement prop takes precedence over
     * @return Partial<Options>
     */
    popperOptionsInput = input({}, { ...(ngDevMode ? { debugName: "popperOptionsInput" } : {}), alias: 'popperOptions' });
    #popperOptionsEffect = effect(() => {
        this.popperOptions = { ...untracked(this.#popperOptions), ...this.popperOptionsInput() };
    }, ...(ngDevMode ? [{ debugName: "#popperOptionsEffect" }] : []));
    set popperOptions(value) {
        this.#popperOptions.update((popperOptions) => ({ ...popperOptions, ...value }));
    }
    get popperOptions() {
        let placement = this.placement();
        switch (this.direction()) {
            case 'dropup': {
                placement = 'top-start';
                break;
            }
            case 'dropend': {
                placement = 'right-start';
                break;
            }
            case 'dropstart': {
                placement = 'left-start';
                break;
            }
            case 'center': {
                placement = 'bottom';
                break;
            }
            case 'dropup-center': {
                placement = 'top';
                break;
            }
        }
        if (this.alignment() === 'end') {
            placement = 'bottom-end';
        }
        this.#popperOptions.update((value) => ({ ...value, placement: placement }));
        return this.#popperOptions();
    }
    #popperOptions = signal({
        placement: this.placement(),
        modifiers: [],
        strategy: 'absolute'
    }, ...(ngDevMode ? [{ debugName: "#popperOptions" }] : []));
    /**
     * Set the dropdown variant to a btn-group, dropdown, input-group, and nav-item.
     */
    variant = input('dropdown', ...(ngDevMode ? [{ debugName: "variant" }] : []));
    /**
     * Toggle the visibility of dropdown menu component.
     * @return boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal({ ...(ngDevMode ? { debugName: "visible" } : {}), source: this.visibleInput,
        computation: (value) => value });
    #visibleEffect = effect(() => {
        const visible = this.visible();
        untracked(() => {
            this.activeTrap = visible;
            visible ? this.createPopperInstance() : this.destroyPopperInstance();
            this.setVisibleState(visible);
            this.visibleChange?.emit(visible);
        });
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    visibleChange = output();
    dropdownContext = { $implicit: this.visible() };
    _toggler = contentChild(DropdownToggleDirective, ...(ngDevMode ? [{ debugName: "_toggler" }] : []));
    _menu = contentChild(DropdownMenuDirective, ...(ngDevMode ? [{ debugName: "_menu" }] : []));
    _menuElementRef = contentChild(DropdownMenuDirective, { ...(ngDevMode ? { debugName: "_menuElementRef" } : {}), read: ElementRef });
    activeTrap = false;
    popperInstance;
    listeners = [];
    hostClasses = computed(() => {
        const direction = this.direction();
        const variant = this.variant();
        return {
            dropdown: (variant === 'dropdown' || variant === 'nav-item') && !direction,
            [`${direction}`]: !!direction,
            [`${variant}`]: !!variant,
            dropup: direction === 'dropup' || direction === 'dropup-center',
            show: this.visible()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    // todo: find better solution
    hostStyle = computed(() => {
        return this.variant() === 'input-group' ? { display: 'contents' } : {};
    }, ...(ngDevMode ? [{ debugName: "hostStyle" }] : []));
    clickedTarget;
    onHostClick($event) {
        this.clickedTarget = $event.target;
    }
    dropdownStateSubscribe() {
        this.dropdownService.dropdownState$
            .pipe(filter((state) => {
            return this === state.dropdown;
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((state) => {
            if ('visible' in state) {
                state?.visible === 'toggle' ? this.toggleDropdown() : this.visible.set(state.visible);
            }
        });
    }
    toggleDropdown() {
        this.visible.update((visible) => !visible);
    }
    onClick(event) {
        if (!this._toggler()?.elementRef.nativeElement.contains(event.target?.closest('[cDropdownToggle]'))) {
            this.toggleDropdown();
        }
    }
    #togglerEffect = effect(() => {
        const variant = this.variant();
        const _toggler = this._toggler();
        if (variant === 'nav-item' && _toggler) {
            this.#renderer.addClass(_toggler.elementRef.nativeElement, 'nav-link');
        }
    }, ...(ngDevMode ? [{ debugName: "#togglerEffect" }] : []));
    ngOnInit() {
        this.setVisibleState(this.visible());
    }
    ngOnDestroy() {
        this.clearListeners();
        this.destroyPopperInstance();
    }
    setVisibleState(value) {
        this.dropdownService.toggle({ visible: value, dropdown: this });
    }
    // todo: turn off popper in navbar-nav
    createPopperInstance() {
        const _toggler = this._toggler();
        const _menu = this._menu();
        if (_toggler && _menu) {
            this.#ngZone.runOutsideAngular(() => {
                // workaround for popper position calculate (see also: dropdown-menu.component)
                _menu.elementRef.nativeElement.style.visibility = 'hidden';
                _menu.elementRef.nativeElement.style.display = 'block';
                if (this.popper()) {
                    this.popperInstance = createPopper(_toggler.elementRef.nativeElement, _menu.elementRef.nativeElement, {
                        ...this.popperOptions
                    });
                }
                this.#ngZone.run(() => {
                    this.setListeners();
                    this.#changeDetectorRef.markForCheck();
                    this.#changeDetectorRef.detectChanges();
                });
            });
        }
    }
    destroyPopperInstance() {
        this.clearListeners();
        this.popperInstance?.destroy();
        this.popperInstance = undefined;
        this.#changeDetectorRef.markForCheck();
    }
    setListeners() {
        this.listeners.push(this.#renderer.listen(this.#document, 'click', (event) => {
            const target = event.target;
            if (this._menuElementRef()?.nativeElement.contains(event.target)) {
                this.clickedTarget = target;
            }
            if (this._toggler()?.elementRef.nativeElement.contains(event.target)) {
                return;
            }
            const autoClose = this.autoClose();
            if (autoClose === true) {
                this.setVisibleState(false);
                return;
            }
            if (this.clickedTarget === target && autoClose === 'inside') {
                this.setVisibleState(false);
                return;
            }
            if (this.clickedTarget !== target && autoClose === 'outside') {
                this.setVisibleState(false);
                return;
            }
        }));
        this.listeners.push(this.#renderer.listen(this.#elementRef.nativeElement, 'keyup', (event) => {
            if (event.key === 'Escape' && this.autoClose() !== false) {
                event.stopPropagation();
                this.setVisibleState(false);
                return;
            }
        }));
        this.listeners.push(this.#renderer.listen(this.#document, 'keyup', (event) => {
            if (event.key === 'Tab' &&
                this.autoClose() !== false &&
                !this.#elementRef.nativeElement.contains(event.target)) {
                this.setVisibleState(false);
                return;
            }
        }));
    }
    clearListeners() {
        this.listeners.forEach((unListen) => {
            unListen();
        });
        // @ts-ignore
        this.listeners.fill(undefined);
        this.listeners = [];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: DropdownComponent, isStandalone: true, selector: "c-dropdown", inputs: { alignment: { classPropertyName: "alignment", publicName: "alignment", isSignal: true, isRequired: false, transformFunction: null }, autoClose: { classPropertyName: "autoClose", publicName: "autoClose", isSignal: true, isRequired: false, transformFunction: null }, direction: { classPropertyName: "direction", publicName: "direction", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, popper: { classPropertyName: "popper", publicName: "popper", isSignal: true, isRequired: false, transformFunction: null }, popperOptionsInput: { classPropertyName: "popperOptionsInput", publicName: "popperOptions", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { listeners: { "click": "onHostClick($event)" }, properties: { "class": "hostClasses()", "style": "hostStyle()" } }, providers: [DropdownService], queries: [{ propertyName: "_toggler", first: true, predicate: DropdownToggleDirective, descendants: true, isSignal: true }, { propertyName: "_menu", first: true, predicate: DropdownMenuDirective, descendants: true, isSignal: true }, { propertyName: "_menuElementRef", first: true, predicate: DropdownMenuDirective, descendants: true, read: ElementRef, isSignal: true }], exportAs: ["cDropdown"], hostDirectives: [{ directive: ThemeDirective, inputs: ["dark", "dark"] }], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host-context(.dropdown,.dropup):not(.btn-group){display:block}:host-context(.dropstart,.dropend):not(.btn-group){display:inline-flex}:host-context(.input-group) :host.dropdown:first-child::ng-deep :first-child{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:first-child::ng-deep :not(:first-child):not(.dropdown-menu){margin-left:-1px;border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host.dropdown:first-child::ng-deep :not(:first-child):not(.dropdown-menu):not(:only-of-type){border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :first-child{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :first-child:not(:only-of-type){border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :not(:first-child):not(.dropdown-menu){margin-left:-1px;border-start-start-radius:0;border-end-start-radius:0}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-dropdown', template: '<ng-content />', exportAs: 'cDropdown', providers: [DropdownService], hostDirectives: [{ directive: ThemeDirective, inputs: ['dark'] }], host: {
                        '[class]': 'hostClasses()',
                        '[style]': 'hostStyle()',
                        '(click)': 'onHostClick($event)'
                    }, styles: [":host-context(.dropdown,.dropup):not(.btn-group){display:block}:host-context(.dropstart,.dropend):not(.btn-group){display:inline-flex}:host-context(.input-group) :host.dropdown:first-child::ng-deep :first-child{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:first-child::ng-deep :not(:first-child):not(.dropdown-menu){margin-left:-1px;border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host.dropdown:first-child::ng-deep :not(:first-child):not(.dropdown-menu):not(:only-of-type){border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :first-child{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :first-child:not(:only-of-type){border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group) :host.dropdown:last-child::ng-deep :not(:first-child):not(.dropdown-menu){margin-left:-1px;border-start-start-radius:0;border-end-start-radius:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { alignment: [{ type: i0.Input, args: [{ isSignal: true, alias: "alignment", required: false }] }], autoClose: [{ type: i0.Input, args: [{ isSignal: true, alias: "autoClose", required: false }] }], direction: [{ type: i0.Input, args: [{ isSignal: true, alias: "direction", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], popper: [{ type: i0.Input, args: [{ isSignal: true, alias: "popper", required: false }] }], popperOptionsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "popperOptions", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], _toggler: [{ type: i0.ContentChild, args: [i0.forwardRef(() => DropdownToggleDirective), { isSignal: true }] }], _menu: [{ type: i0.ContentChild, args: [i0.forwardRef(() => DropdownMenuDirective), { isSignal: true }] }], _menuElementRef: [{ type: i0.ContentChild, args: [i0.forwardRef(() => DropdownMenuDirective), { ...{ read: ElementRef }, isSignal: true }] }] } });

class DropdownItemDirective {
    #elementRef = inject(ElementRef);
    #dropdownService = inject(DropdownService);
    dropdown = inject(DropdownComponent, { optional: true });
    /**
     * Set active state to a dropdown-item.
     * @return boolean
     * @default undefined
     */
    active = input(...(ngDevMode ? [undefined, { debugName: "active" }] : []));
    /**
     * Configure dropdown-item close dropdown behavior.
     * @return boolean
     * @default true
     */
    autoClose = input(true, ...(ngDevMode ? [{ debugName: "autoClose" }] : []));
    /**
     * Disables a dropdown-item.
     * @return boolean
     * @default undefined
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    #disabled = linkedSignal({ ...(ngDevMode ? { debugName: "#disabled" } : {}), source: this.disabledInput,
        computation: (value) => value });
    set disabled(value) {
        this.#disabled.set(value);
    }
    get disabled() {
        return this.#disabled();
    }
    role = input('list-item', ...(ngDevMode ? [{ debugName: "role" }] : []));
    tabIndexInput = input('0', { ...(ngDevMode ? { debugName: "tabIndexInput" } : {}), alias: 'tabIndex' });
    tabIndex = linkedSignal({ ...(ngDevMode ? { debugName: "tabIndex" } : {}), source: this.tabIndexInput,
        computation: (value) => (this.disabled ? '-1' : value) });
    focus(origin) {
        this.#elementRef?.nativeElement?.focus();
    }
    getLabel() {
        return this.#elementRef?.nativeElement?.textContent.trim();
    }
    ariaCurrent = computed(() => {
        return this.active() ? 'true' : null;
    }, ...(ngDevMode ? [{ debugName: "ariaCurrent" }] : []));
    hostClasses = computed(() => {
        return {
            'dropdown-item': true,
            active: this.active(),
            disabled: this.disabled
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    onClick($event) {
        this.handleInteraction();
    }
    onKeyUp($event) {
        if ($event.key === 'Enter') {
            this.handleInteraction();
        }
    }
    handleInteraction() {
        if (this.autoClose()) {
            this.#dropdownService.toggle({ visible: 'toggle', dropdown: this.dropdown });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: DropdownItemDirective, isStandalone: true, selector: "[cDropdownItem]", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, autoClose: { classPropertyName: "autoClose", publicName: "autoClose", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, tabIndexInput: { classPropertyName: "tabIndexInput", publicName: "tabIndex", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "onClick($event)", "keyup": "onKeyUp($event)" }, properties: { "class": "hostClasses()", "attr.tabindex": "tabIndex()", "attr.aria-current": "ariaCurrent()", "attr.aria-disabled": "disabled || null", "attr.role": "role()" }, classAttribute: "dropdown-item" }, exportAs: ["cDropdownItem"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownItem]',
                    exportAs: 'cDropdownItem',
                    host: {
                        class: 'dropdown-item',
                        '[class]': 'hostClasses()',
                        '[attr.tabindex]': 'tabIndex()',
                        '[attr.aria-current]': 'ariaCurrent()',
                        '[attr.aria-disabled]': 'disabled || null',
                        '[attr.role]': 'role()',
                        '(click)': 'onClick($event)',
                        '(keyup)': 'onKeyUp($event)'
                    }
                }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], autoClose: [{ type: i0.Input, args: [{ isSignal: true, alias: "autoClose", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], tabIndexInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabIndex", required: false }] }] } });

class DropdownItemPlainDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownItemPlainDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: DropdownItemPlainDirective, isStandalone: true, selector: "[cDropdownItemPlain]", host: { classAttribute: "dropdown-item-text" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownItemPlainDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownItemPlain]',
                    host: { class: 'dropdown-item-text' }
                }]
        }] });

class DropdownCloseDirective {
    #dropdownService = inject(DropdownService);
    dropdown = inject(DropdownComponent, { optional: true });
    /**
     * Disables a dropdown-close directive.
     * @return boolean
     * @default false
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    disabled = linkedSignal({ ...(ngDevMode ? { debugName: "disabled" } : {}), source: this.disabledInput,
        computation: (value) => value || null });
    dropdownComponent = input(...(ngDevMode ? [undefined, { debugName: "dropdownComponent" }] : []));
    ngAfterViewInit() {
        const dropdownComponent = this.dropdownComponent();
        if (dropdownComponent) {
            this.dropdown = dropdownComponent;
            this.#dropdownService = dropdownComponent?.dropdownService;
        }
    }
    tabIndexInput = input(null, { ...(ngDevMode ? { debugName: "tabIndexInput" } : {}), alias: 'tabIndex' });
    tabIndex = linkedSignal({ ...(ngDevMode ? { debugName: "tabIndex" } : {}), source: this.tabIndexInput,
        computation: (value) => (this.disabled() ? '-1' : value) });
    onClick($event) {
        this.handleToggle();
    }
    onKeyUp($event) {
        if ($event.key === 'Enter') {
            this.handleToggle();
        }
    }
    handleToggle() {
        if (!this.disabled()) {
            this.#dropdownService.toggle({ visible: false, dropdown: this.dropdown });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownCloseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: DropdownCloseDirective, isStandalone: true, selector: "[cDropdownClose]", inputs: { disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, dropdownComponent: { classPropertyName: "dropdownComponent", publicName: "dropdownComponent", isSignal: true, isRequired: false, transformFunction: null }, tabIndexInput: { classPropertyName: "tabIndexInput", publicName: "tabIndex", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "onClick($event)", "keyup": "onKeyUp($event)" }, properties: { "class.disabled": "disabled()", "attr.aria-disabled": "disabled() || null", "attr.tabindex": "tabIndex()" } }, exportAs: ["cDropdownClose"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDropdownClose]',
                    exportAs: 'cDropdownClose',
                    host: {
                        '[class.disabled]': 'disabled()',
                        '[attr.aria-disabled]': 'disabled() || null',
                        '[attr.tabindex]': 'tabIndex()',
                        '(click)': 'onClick($event)',
                        '(keyup)': 'onKeyUp($event)'
                    }
                }]
        }], propDecorators: { disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], dropdownComponent: [{ type: i0.Input, args: [{ isSignal: true, alias: "dropdownComponent", required: false }] }], tabIndexInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabIndex", required: false }] }] } });

class DropdownModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: DropdownModule, imports: [DropdownComponent,
            DropdownCloseDirective,
            DropdownDividerDirective,
            DropdownHeaderDirective,
            DropdownItemDirective,
            DropdownItemPlainDirective,
            DropdownMenuDirective,
            DropdownToggleDirective], exports: [DropdownComponent,
            DropdownCloseDirective,
            DropdownDividerDirective,
            DropdownHeaderDirective,
            DropdownItemDirective,
            DropdownItemPlainDirective,
            DropdownMenuDirective,
            DropdownToggleDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownModule, providers: [DropdownService] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        DropdownComponent,
                        DropdownCloseDirective,
                        DropdownDividerDirective,
                        DropdownHeaderDirective,
                        DropdownItemDirective,
                        DropdownItemPlainDirective,
                        DropdownMenuDirective,
                        DropdownToggleDirective
                    ],
                    exports: [
                        DropdownComponent,
                        DropdownCloseDirective,
                        DropdownDividerDirective,
                        DropdownHeaderDirective,
                        DropdownItemDirective,
                        DropdownItemPlainDirective,
                        DropdownMenuDirective,
                        DropdownToggleDirective
                    ],
                    providers: [DropdownService]
                }]
        }] });

class SpinnerComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Label for accessibility.
     * @type string
     * @default 'Loading...'
     */
    label = input('Loading...', ...(ngDevMode ? [{ debugName: "label" }] : []));
    /**
     * Size the component small.
     * @return string
     * @values 'sm'
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Set the button variant to an outlined button or a ghost button.
     * @values 'border' | 'grow'
     * @default 'border'
     */
    variant = input('border', ...(ngDevMode ? [{ debugName: "variant" }] : []));
    /**
     * Default role attr for Spinner. [docs]
     * @type string
     * @default 'status'
     */
    role = input('status', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        const size = this.size();
        const variant = this.variant();
        return {
            [`spinner-${variant}`]: true,
            [`text-${color}`]: !!color,
            [`spinner-${variant}-${size}`]: !!size
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SpinnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: SpinnerComponent, isStandalone: true, selector: "c-spinner", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()", "class": "hostClasses()" } }, ngImport: i0, template: "<ng-content>\n  <span class=\"visually-hidden\">{{ label() }}</span>\n</ng-content>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SpinnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-spinner', host: {
                        '[attr.role]': 'role()',
                        '[class]': 'hostClasses()'
                    }, template: "<ng-content>\n  <span class=\"visually-hidden\">{{ label() }}</span>\n</ng-content>\n" }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], label: [{ type: i0.Input, args: [{ isSignal: true, alias: "label", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class SpinnerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SpinnerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SpinnerModule, imports: [SpinnerComponent], exports: [SpinnerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SpinnerModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SpinnerModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        SpinnerComponent
                    ],
                    imports: [
                        SpinnerComponent
                    ]
                }]
        }] });

class ElementCoverComponent {
    elementRef = inject(ElementRef);
    changeDetectorRef = inject(ChangeDetectorRef);
    #parent = signal(undefined, ...(ngDevMode ? [{ debugName: "#parent" }] : []));
    constructor() {
        afterNextRender({
            read: () => {
                this.#parent.set(this.elementRef.nativeElement.parentElement);
                this.changeDetectorRef.detectChanges();
            }
        });
    }
    /**
     * Array of custom boundaries. Use to create custom cover area (instead of parent element area). Area is defined by four sides: 'top', 'bottom', 'right', 'left'. If side is not defined by any custom boundary it is equal to parent element boundary. Each custom boundary is object with keys:
     * - sides (array) - select boundaries of element to define boundaries. Side names: 'top', 'bottom', 'right', 'left'.
     * - query (string) - query used to get element which define boundaries. Search will be done only inside parent element, by parent.querySelector(query) function. [docs]
     * @type {sides: string[], query: string}[]
     */
    boundaries = input(...(ngDevMode ? [undefined, { debugName: "boundaries" }] : []));
    /**
     * Opacity of cover. [docs]
     * @type <number>
     */
    opacity = input(0.4, ...(ngDevMode ? [{ debugName: "opacity" }] : []));
    #customBoundaries = computed(() => {
        if (!this.boundaries() || this.elementRef === null) {
            return {};
        }
        if (!this.#parent()) {
            return {};
        }
        const parentCoords = this.#parent()?.getBoundingClientRect();
        const _customBoundaries = {};
        this.boundaries()?.forEach((value) => {
            const element = this.#parent()?.querySelector(value.query);
            if (!element || !value.sides) {
                return;
            }
            const coords = element.getBoundingClientRect();
            value.sides.forEach((side) => {
                const sideMargin = Math.abs(coords[side] - parentCoords[side]);
                _customBoundaries[side] = `${sideMargin}px`;
            });
        });
        return _customBoundaries;
    }, ...(ngDevMode ? [{ debugName: "#customBoundaries" }] : []));
    #containerCoords = computed(() => {
        return {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            ...this.#customBoundaries()
        };
    }, ...(ngDevMode ? [{ debugName: "#containerCoords" }] : []));
    hostStyles = computed(() => {
        return {
            ...this.#containerCoords(),
            position: 'absolute',
            backgroundColor: `rgb(255,255,255,${this.opacity()})`
        };
    }, ...(ngDevMode ? [{ debugName: "hostStyles" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ElementCoverComponent, isStandalone: true, selector: "c-element-cover, [cElementCover]", inputs: { boundaries: { classPropertyName: "boundaries", publicName: "boundaries", isSignal: true, isRequired: false, transformFunction: null }, opacity: { classPropertyName: "opacity", publicName: "opacity", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "style": "hostStyles()" } }, ngImport: i0, template: "<div [style]=\"{position: 'absolute', 'top': '50%', 'left': '50%', transform: 'translateX(-50%) translateY(-50%)'}\">\n  <ng-content>\n    <c-spinner color=\"primary\" variant=\"grow\" />\n  </ng-content>\n</div>\n", styles: [":host{display:block}\n"], dependencies: [{ kind: "component", type: SpinnerComponent, selector: "c-spinner", inputs: ["color", "label", "size", "variant", "role"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-element-cover, [cElementCover]', imports: [SpinnerComponent], host: {
                        '[style]': 'hostStyles()'
                    }, template: "<div [style]=\"{position: 'absolute', 'top': '50%', 'left': '50%', transform: 'translateX(-50%) translateY(-50%)'}\">\n  <ng-content>\n    <c-spinner color=\"primary\" variant=\"grow\" />\n  </ng-content>\n</div>\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { boundaries: [{ type: i0.Input, args: [{ isSignal: true, alias: "boundaries", required: false }] }], opacity: [{ type: i0.Input, args: [{ isSignal: true, alias: "opacity", required: false }] }] } });

class ElementCoverModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverModule, imports: [ElementCoverComponent], exports: [ElementCoverComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ElementCoverModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ElementCoverComponent],
                    exports: [ElementCoverComponent]
                }]
        }] });

class MultiSelectService {
    #destroyRef = inject(DestroyRef);
    optionsStyle = signal('checkbox', ...(ngDevMode ? [{ debugName: "optionsStyle" }] : []));
    selectionModel = new SelectionModel(true);
    selectionModelChanged$ = new Subject();
    setSelectionModel(multiple = true, initiallySelectedValues = []) {
        if (this.selectionModel.isMultipleSelection() === !multiple) {
            this.selectionModel = new SelectionModel(multiple);
        }
        this.selectionModel.changed
            .pipe(distinctUntilChanged((previous, current) => {
            return (previous.added.length === current.added.length &&
                previous.removed.length === current.removed.length &&
                JSON.stringify(previous.added) === JSON.stringify(current.added) &&
                JSON.stringify(previous.removed) === JSON.stringify(current.removed));
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((change) => {
            this.selectionModelChanged$.next(change);
        });
    }
    multiSelectVisible = new BehaviorSubject(false);
    multiSelectVisible$ = this.multiSelectVisible.asObservable();
    toggleVisible(visible) {
        this.multiSelectVisible.next(visible);
    }
    multiSelectFocus = new Subject();
    multiSelectFocus$ = this.multiSelectFocus.asObservable();
    focusChange(option) {
        this.multiSelectFocus.next(option);
    }
    userOptionsSignal = signal([], ...(ngDevMode ? [{ debugName: "userOptionsSignal" }] : []));
    getUserOptions() {
        return computed(this.userOptionsSignal);
    }
    addUserOption(option) {
        this.userOptionsSignal.update((options) => [...options, option]);
    }
    removeUserOption(value) {
        const updatedOptions = this.userOptionsSignal().filter((option) => option.value !== value);
        this.userOptionsSignal.set(updatedOptions);
    }
    removeAllUserOptions() {
        this.userOptionsSignal.set([]);
    }
    #optionsArray = new BehaviorSubject([]);
    optionsArray$ = this.#optionsArray.asObservable();
    getOptionsArray() {
        return this.optionsArray$;
    }
    updateOptionsArray(options) {
        this.#optionsArray.next(options);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectService, decorators: [{
            type: Injectable
        }] });

class MultiSelectOptionComponent {
    elementRef = inject(ElementRef);
    #changeDetectorRef = inject(ChangeDetectorRef);
    #destroyRef = inject(DestroyRef);
    #focusMonitor = inject(FocusMonitor);
    #multiSelectService = inject(MultiSelectService);
    /**
     * Option style
     * @return ('checkbox' | 'text')
     * @default 'checkbox'
     */
    optionsStyleInput = input(undefined, { ...(ngDevMode ? { debugName: "optionsStyleInput" } : {}), alias: 'optionsStyle' });
    optionsStyle = linkedSignal(() => this.optionsStyleInput() ?? this.#multiSelectService.optionsStyle(), ...(ngDevMode ? [{ debugName: "optionsStyle" }] : []));
    /**
     * Option label
     * @type string
     * @default undefined
     */
    label;
    /**
     * Option inner text
     * @type string
     * @default undefined
     */
    text;
    /**
     * Option visible.
     * @type boolean
     * @default true
     */
    set visible(value) {
        this.#visible.set(value);
    }
    get visible() {
        return this.#visible();
    }
    #visible = signal(true, ...(ngDevMode ? [{ debugName: "#visible" }] : []));
    /**
     * Option disabled.
     * @type boolean
     * @default false
     */
    disabled = false;
    /**
     * Option selected.
     * @type boolean
     * @default false
     */
    set selected(value) {
        if (value !== undefined) {
            const selected = booleanAttribute(value);
            if (this.#selected() !== selected) {
                this.#selected.set(selected);
                if (this.value !== undefined) {
                    selected
                        ? this.#multiSelectService.selectionModel?.select(this.value)
                        : this.#multiSelectService.selectionModel?.deselect(this.value);
                }
            }
        }
    }
    get selected() {
        return this.#selected();
    }
    #selected = signal(undefined, ...(ngDevMode ? [{ debugName: "#selected" }] : []));
    /**
     * Emits option selected change
     * @return boolean
     */
    selectedChange = output();
    /**
     * Option value.
     * @type string | number
     * @default undefined
     */
    set value(value) {
        if (this.#value() !== value) {
            this.#value.set(value);
        }
    }
    get value() {
        return this.#value();
    }
    #value = signal(undefined, ...(ngDevMode ? [{ debugName: "#value" }] : []));
    #dropdownVisible = signal(false, ...(ngDevMode ? [{ debugName: "#dropdownVisible" }] : []));
    set active(value) {
        const active = value && this.#visible() && !this.disabled;
        if (this.#active() !== active) {
            this.#active.set(active);
        }
    }
    get active() {
        return this.#active() && this.#visible();
    }
    role = input('option', ...(ngDevMode ? [{ debugName: "role" }] : []));
    #active = signal(false, ...(ngDevMode ? [{ debugName: "#active" }] : []));
    #focused = signal(false, ...(ngDevMode ? [{ debugName: "#focused" }] : []));
    focusChange = output();
    hostClasses = computed(() => {
        const optionsStyle = this.optionsStyle();
        return {
            'form-multi-select-option': true,
            'form-multi-selected': this.#selected(),
            [`form-multi-select-option-with-${optionsStyle}`]: !!optionsStyle,
            disabled: this.disabled,
            'd-none': !this.#visible(),
            active: this.#active(),
            focused: this.#focused()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    get tabIndex() {
        return this.disabled || !this.#visible() ? -1 : this.#tabIndex();
    }
    #tabIndex = computed(() => {
        return this.#active() && this.#visible() ? 0 : -1;
    }, ...(ngDevMode ? [{ debugName: "#tabIndex" }] : []));
    get ariaDisabled() {
        return this.disabled || null;
    }
    ariaSelected = computed(() => {
        const value = this.#value();
        const selected = this.#selected();
        if (value !== undefined && selected !== undefined) {
            selected
                ? this.#multiSelectService.selectionModel?.select(value)
                : this.#multiSelectService.selectionModel?.deselect(value);
            this.selectedChange.emit(selected);
            this.#changeDetectorRef.markForCheck();
        }
        return selected ?? false;
    }, ...(ngDevMode ? [{ debugName: "ariaSelected" }] : []));
    onBlur() {
        this.#focused.set(false);
    }
    onFocus() {
        this.#focused.set(true);
    }
    onKeyDown($event) {
        if (['Space'].includes($event.code)) {
            $event.preventDefault();
        }
        if (['Tab'].includes($event.code)) {
            $event.preventDefault();
        }
    }
    onKeyUp($event) {
        if (this.#dropdownVisible()) {
            if (['Enter', 'Space'].includes($event.code)) {
                $event.stopImmediatePropagation();
                $event.preventDefault();
                this.selected = this.disabled ? this.#selected() : !this.#selected();
                this.focus('keyboard');
            }
        }
    }
    onClick($event) {
        $event.stopPropagation();
        this.selected = this.disabled ? this.#selected() : !this.#selected();
        this.focus('mouse');
    }
    ngAfterViewInit() {
        this.setSubscriptions();
        if (this.text === undefined) {
            const innerText = this.elementRef.nativeElement.textContent.trim() || this.elementRef.nativeElement.innerText.trim();
            this.text = innerText || this.value?.toString().trim();
        }
        if (this.label === undefined) {
            this.label = this.text?.trim();
        }
        if (this.value === undefined) {
            this.value = this.label?.trim() || '...';
        }
        if (!this.#changeDetectorRef.destroyed) {
            // setTimeout(() => {
            this.selected = this.isSelected(this.value);
            this.#changeDetectorRef.markForCheck();
            // });
        }
    }
    /** Get the label for this element which is required by the FocusableOption interface. */
    getLabel() {
        return (this.label ?? this.value);
    }
    focus(origin = 'program') {
        if (this.#dropdownVisible() && !this.disabled) {
            this.#focusMonitor.focusVia(this.elementRef.nativeElement, origin, { preventScroll: false });
            this.#multiSelectService.focusChange(this);
            this.#changeDetectorRef.markForCheck();
            this.focusChange.emit(this);
        }
    }
    setSubscriptions() {
        this.#multiSelectService.multiSelectVisible$
            .pipe(tap((visible) => {
            this.#dropdownVisible.set(visible);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.#multiSelectService.multiSelectFocus$
            .pipe(distinctUntilChanged(), filter((option) => this.active !== (this.value === option.value)), tap((option) => {
            this.active = this.value === option.value;
            this.#changeDetectorRef.markForCheck();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        if (!this.#changeDetectorRef.destroyed) {
            this.#multiSelectService.selectionModelChanged$
                .pipe(filter((change) => change.added.includes(this.value) || change.removed.includes(this.value)), tap((change) => {
                this.selected = change.added.includes(this.value);
                this.#changeDetectorRef.markForCheck();
            }), takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        }
    }
    isSelected(value = this.value) {
        return (this.#selected() || this.#multiSelectService.selectionModel?.isSelected(value)) ?? false;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: MultiSelectOptionComponent, isStandalone: true, selector: "c-multi-select-option", inputs: { optionsStyleInput: { classPropertyName: "optionsStyleInput", publicName: "optionsStyle", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: false, isRequired: false, transformFunction: null }, text: { classPropertyName: "text", publicName: "text", isSignal: false, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "visible", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: false, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, active: { classPropertyName: "active", publicName: "active", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { selectedChange: "selectedChange", focusChange: "focusChange" }, host: { listeners: { "blur": "onBlur()", "focus": "onFocus()", "keydown": "onKeyDown($event)", "keyup": "onKeyUp($event)", "click": "onClick($event)" }, properties: { "attr.disabled": "ariaDisabled", "attr.aria-disabled": "ariaDisabled", "attr.aria-selected": "ariaSelected()", "attr.role": "role()", "attr.tabindex": "tabIndex", "class": "hostClasses()" } }, exportAs: ["cMultiSelectOption"], ngImport: i0, template: `
    <div>
      <ng-content>
        {{ text ?? label ?? value?.toString() }}
      </ng-content>
    </div>
  `, isInline: true, styles: [":host{display:block;-webkit-user-select:none;user-select:none;white-space:nowrap;overflow:hidden}:host.active{border-radius:var(--cui-form-multi-select-option-indicator-border-radius, .375rem);box-shadow:0 0 .125rem 0 var(--cui-gray-400)}:host.focused,:host :focus-visible{box-shadow:0 0 0 .125rem #321fdb40;outline:none}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-multi-select-option', template: `
    <div>
      <ng-content>
        {{ text ?? label ?? value?.toString() }}
      </ng-content>
    </div>
  `, exportAs: 'cMultiSelectOption', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, host: {
                        '[attr.disabled]': 'ariaDisabled',
                        '[attr.aria-disabled]': 'ariaDisabled',
                        '[attr.aria-selected]': 'ariaSelected()',
                        '[attr.role]': 'role()',
                        '[attr.tabindex]': 'tabIndex',
                        '[class]': 'hostClasses()',
                        '(blur)': 'onBlur()',
                        '(focus)': 'onFocus()',
                        '(keydown)': 'onKeyDown($event)',
                        '(keyup)': 'onKeyUp($event)',
                        '(click)': 'onClick($event)'
                    }, styles: [":host{display:block;-webkit-user-select:none;user-select:none;white-space:nowrap;overflow:hidden}:host.active{border-radius:var(--cui-form-multi-select-option-indicator-border-radius, .375rem);box-shadow:0 0 .125rem 0 var(--cui-gray-400)}:host.focused,:host :focus-visible{box-shadow:0 0 0 .125rem #321fdb40;outline:none}\n"] }]
        }], propDecorators: { optionsStyleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsStyle", required: false }] }], label: [{
                type: Input
            }], text: [{
                type: Input
            }], visible: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], selected: [{
                type: Input
            }], selectedChange: [{ type: i0.Output, args: ["selectedChange"] }], value: [{
                type: Input
            }], active: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], focusChange: [{ type: i0.Output, args: ["focusChange"] }] } });

class MultiSelectTagComponent {
    #multiSelectService = inject(MultiSelectService);
    multiselectVisible = toSignal(this.#multiSelectService.multiSelectVisible$);
    option = input(...(ngDevMode ? [undefined, { debugName: "option" }] : []));
    disabled = input(true, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    label = input(...(ngDevMode ? [undefined, { debugName: "label" }] : []));
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    remove = output();
    hostClasses = computed(() => ({
        'pe-none': this.disabled()
    }), ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    handleRemove($event) {
        $event.stopPropagation();
        if (this.value()) {
            this.remove.emit(this.value());
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectTagComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: MultiSelectTagComponent, isStandalone: true, selector: "c-multi-select-tag", inputs: { option: { classPropertyName: "option", publicName: "option", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { remove: "remove" }, host: { properties: { "class": "hostClasses()" }, classAttribute: "form-multi-select-tag" }, ngImport: i0, template: "<span class=\"text-truncate\">{{ label() ?? value()?.toString() ?? '???' }}</span>\n@if (!disabled()) {\n  <button (click)=\"handleRemove($event)\"\n          [disabled]=\"((multiselectVisible()) ?? true) === false\"\n          aria-label=\"Clear\"\n          class=\"form-multi-select-tag-delete\"\n          type=\"button\">\n  </button>\n}\n", styles: [":host .btn-xs{padding-left:.125rem;padding-right:.125rem;font-size:.575rem;border-radius:.2rem}:host.form-multi-select-tag{-webkit-user-select:none;user-select:none;pointer-events:all;align-items:center;display:flex}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectTagComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-multi-select-tag', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'form-multi-select-tag',
                        '[class]': 'hostClasses()'
                    }, template: "<span class=\"text-truncate\">{{ label() ?? value()?.toString() ?? '???' }}</span>\n@if (!disabled()) {\n  <button (click)=\"handleRemove($event)\"\n          [disabled]=\"((multiselectVisible()) ?? true) === false\"\n          aria-label=\"Clear\"\n          class=\"form-multi-select-tag-delete\"\n          type=\"button\">\n  </button>\n}\n", styles: [":host .btn-xs{padding-left:.125rem;padding-right:.125rem;font-size:.575rem;border-radius:.2rem}:host.form-multi-select-tag{-webkit-user-select:none;user-select:none;pointer-events:all;align-items:center;display:flex}\n"] }]
        }], propDecorators: { option: [{ type: i0.Input, args: [{ isSignal: true, alias: "option", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], label: [{ type: i0.Input, args: [{ isSignal: true, alias: "label", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], remove: [{ type: i0.Output, args: ["remove"] }] } });

class MultiSelectSearchDirective {
    #elementRef = inject(ElementRef);
    #destroyRef = inject(DestroyRef);
    #value$ = new BehaviorSubject('');
    delay = input(300, ...(ngDevMode ? [{ debugName: "delay" }] : []));
    value = input('', ...(ngDevMode ? [{ debugName: "value" }] : []));
    #valueEffect = effect(() => {
        this.#value$.next(this.value());
    }, ...(ngDevMode ? [{ debugName: "#valueEffect" }] : []));
    valueChange = output();
    #backspace$ = fromEvent(this.#elementRef.nativeElement, 'keydown')
        .pipe(filter(($event) => ['Backspace', 'Delete'].includes($event.key)), tap(($event) => {
        if ($event.target.value !== '') {
            $event.stopImmediatePropagation();
        }
    }), takeUntilDestroyed(this.#destroyRef))
        .subscribe();
    #input$ = fromEvent(this.#elementRef.nativeElement, 'input').pipe(debounceTime(this.delay()), map((event) => {
        return event.target.value;
    }), takeUntilDestroyed(this.#destroyRef));
    ngAfterViewInit() {
        this.setSubscription();
    }
    setSubscription() {
        this.#value$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            this.valueChange.emit(value);
        });
        this.#input$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            this.#value$.next(value);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectSearchDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: MultiSelectSearchDirective, isStandalone: true, selector: "[cMultiSelectSearch]", inputs: { delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { valueChange: "valueChange" }, exportAs: ["cMultiSelectSearch"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectSearchDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cMultiSelectSearch]',
                    exportAs: 'cMultiSelectSearch',
                    standalone: true
                }]
        }], propDecorators: { delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }] } });

const observeReferenceResizeModifier = {
    name: 'observeReferenceResizeModifier',
    enabled: true,
    phase: 'main',
    fn({ state }) {
        /* empty */
    },
    effect: ({ state, instance }) => {
        const RO_PROP = '__popperjsRO__';
        const { reference } = state.elements;
        let resizeObserver = new ResizeObserver((entries) => {
            if (entries.find((entry) => entry.target === reference)) {
                instance.update();
            }
        });
        resizeObserver.observe(reference);
        return () => {
            resizeObserver?.disconnect();
            resizeObserver = null;
        };
    }
};
const sameAsReferenceWidthModifier = {
    name: 'sameAsReferenceWidthModifier',
    enabled: true,
    fn: ({ state }) => {
        state.styles['popper'].minWidth = `${state.rects.reference.width}px`;
    },
    phase: 'beforeWrite',
    requires: ['computeStyles']
};
const defaultPopperOptions = {
    strategy: 'fixed',
    modifiers: [
        observeReferenceResizeModifier,
        sameAsReferenceWidthModifier,
        {
            name: 'offset',
            options: {
                offset: () => [0, 4]
            }
        }
    ]
};
class MultiSelectComponent {
    static ngAcceptInputType_allowCreateOptions;
    static ngAcceptInputType_clearSearchOnSelect;
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_itemSize;
    static ngAcceptInputType_itemMinWidthInput;
    static ngAcceptInputType_loading;
    static ngAcceptInputType_multiple;
    static ngAcceptInputType_resetSelectionOnOptionsChange;
    static ngAcceptInputType_selectAll;
    static ngAcceptInputType_virtualScroller;
    static ngAcceptInputType_visibleInput;
    elementRef = inject(ElementRef);
    renderer = inject(Renderer2);
    ngZone = inject(NgZone);
    changeDetectorRef = inject(ChangeDetectorRef);
    focusMonitor = inject(FocusMonitor);
    multiSelectService = inject(MultiSelectService);
    iterableDiffers = inject(IterableDiffers);
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #i18nPlural = inject(I18nPluralPipe);
    constructor() {
        this.differ = this.iterableDiffers.find(this.#value()).create();
    }
    differ;
    #destroy$ = new Subject();
    #optionsReady$ = new Subject();
    userOptionsArray$ = new BehaviorSubject([]);
    #options = new Map();
    optionTags = signal([], ...(ngDevMode ? [{ debugName: "optionTags" }] : []));
    optionsSelected = signal(new Map(), ...(ngDevMode ? [{ debugName: "optionsSelected" }] : []));
    value$ = new BehaviorSubject([]);
    #optionsContent = [];
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    writeValue(value) {
        if (value !== undefined && value !== null) {
            this.value = Array.isArray(value) ? [...value] : [value];
        }
        else {
            this.value = Array(0);
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled.set(isDisabled);
    }
    /**
     * Allow users to create options if they are not in the list of options.
     * @return boolean
     * @default false
     * @since 4.5.11
     */
    allowCreateOptions = input(false, { ...(ngDevMode ? { debugName: "allowCreateOptions" } : {}), transform: booleanAttribute });
    /**
     * Enables selection cleaner element
     * @return boolean
     * @default true | 'active'
     */
    cleaner = input(true, ...(ngDevMode ? [{ debugName: "cleaner" }] : []));
    /**
     * Clear current search on selecting an item
     * @return boolean
     * @default false
     * @since 4.5.11
     */
    clearSearchOnSelect = input(false, { ...(ngDevMode ? { debugName: "clearSearchOnSelect" } : {}), transform: booleanAttribute });
    /**
     * Disables multi-select component
     * @return boolean
     * @default false
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), alias: 'disabled', transform: booleanAttribute });
    disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    #disabledEffect = effect(() => {
        this.setFocusMonitor(!this.disabled());
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    /**
     * Add loading spinner and reduced opacity.
     * @return boolean
     * @default false
     * @since 4.5.2
     */
    loading = input(false, { ...(ngDevMode ? { debugName: "loading" } : {}), transform: booleanAttribute });
    /**
     * Specifies that multiple options can be selected at once
     * @returns boolean
     * @default false
     */
    multiple = input(false, { ...(ngDevMode ? { debugName: "multiple" } : {}), transform: booleanAttribute });
    #multipleEffect = effect(() => {
        const multiple = this.multiple();
        this.multiSelectService.setSelectionModel(multiple);
        if (!multiple) {
            this.multiSelectService.selectionModelChanged$
                .pipe(filter(() => !multiple), delay(0), tap((change) => {
                if (change.added.length > 0) {
                    // close dropdown for single select (!multiple)
                    this.visible.set(false);
                    this.searchValue = '';
                }
            }), skip(1), tap((change) => {
                this.setFocus('program', 'selectionModel.changed (single)');
            }), takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        }
    }, ...(ngDevMode ? [{ debugName: "#multipleEffect" }] : []));
    /**
     * List of option elements
     * @type IOption[]
     * @default []
     */
    set options(options) {
        if (Array.isArray(options)) {
            this.#options.clear();
            this.makeOptions(options);
        }
    }
    get options() {
        return [...this.#options.values()];
    }
    /**
     * Sets maxHeight of options list in px
     * @return string | number
     * @default 'auto'
     */
    optionsMaxHeightInput = input('auto', { ...(ngDevMode ? { debugName: "optionsMaxHeightInput" } : {}), alias: 'optionsMaxHeight' });
    optionsMaxHeight = linkedSignal(this.optionsMaxHeightInput, ...(ngDevMode ? [{ debugName: "optionsMaxHeight" }] : []));
    /**
     * Sets option style
     * @return 'checkbox' | 'text'
     * @default 'checkbox'
     */
    optionsStyle = input('checkbox', ...(ngDevMode ? [{ debugName: "optionsStyle" }] : []));
    #optionsStyleEffect = effect(() => {
        this.multiSelectService.optionsStyle.set(this.optionsStyle());
    }, ...(ngDevMode ? [{ debugName: "#optionsStyleEffect" }] : []));
    /**
     * Specifies a short hint that is visible in the search input
     * @return string
     * @default 'Select...'
     */
    placeholder = input('Select...', ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    /**
     * Resets selection when options are changed
     * When set to true, any previously selected options will be reset whenever the options list undergoes a change.
     * This ensures that outdated selections are not retained when new options are provided.
     * @default false
     * */
    resetSelectionOnOptionsChange = input(false, { ...(ngDevMode ? { debugName: "resetSelectionOnOptionsChange" } : {}), transform: booleanAttribute });
    /**
     * Enables search input element
     * @return boolean | SearchFn | 'external'
     * @default true
     */
    search = input(true, ...(ngDevMode ? [{ debugName: "search" }] : []));
    /**
     * Sets the label for no results when filtering
     * @return string
     * @default 'no items'
     */
    searchNoResultsLabel = input('no items', ...(ngDevMode ? [{ debugName: "searchNoResultsLabel" }] : []));
    /**
     * Sets initial search string
     * @type string
     * @default ''
     */
    set searchValue(value) {
        if (this._searchValue !== value.trimStart()) {
            this._searchValue = value.trimStart();
            this.visible.set(this.searchValue.length > 0 && this.subtreeFocused ? true : this.visible());
            this.filterOptions(this.searchValue);
            this.inputElementSize();
            this.searchValueChange.emit(this._searchValue);
        }
    }
    get searchValue() {
        return this._searchValue;
    }
    _searchValue = '';
    /**
     * Emits searchValue string for external filtering
     * @return string
     */
    searchValueChange = output();
    /**
     * Enables select all button
     * @return boolean
     * @default true
     */
    selectAll = input(true, { ...(ngDevMode ? { debugName: "selectAll" } : {}), transform: booleanAttribute });
    /**
     * Sets the select all button label
     * @return string
     * @default 'Select all options'
     */
    selectAllLabel = input('Select all options', ...(ngDevMode ? [{ debugName: "selectAllLabel" }] : []));
    /**
     * Selection type
     * @return 'counter' | 'tags' | 'text'
     * @default 'tags'
     */
    selectionType = input('tags', ...(ngDevMode ? [{ debugName: "selectionType" }] : []));
    /**
     * Counter selection label value
     * @return string
     * @default 'item(s) selected'
     */
    selectionTypeCounterText = input('item(s) selected', ...(ngDevMode ? [{ debugName: "selectionTypeCounterText" }] : []));
    /**
     * Counter selection label plural map for I18nPluralPipe
     * @return IPluralMap
     * @default { '=1': 'item selected', 'other': 'items selected' }
     */
    selectionTypeCounterTextPluralMap = input({
        '=1': 'item selected',
        other: 'items selected'
    }, ...(ngDevMode ? [{ debugName: "selectionTypeCounterTextPluralMap" }] : []));
    /**
     * Size the component small or large.
     * @return 'sm' | 'lg' | undefined
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Initial value of multi-select
     * @type (TValue | TValue[])
     */
    set value(value) {
        const newValue = Array.isArray(value) ? [...value] : [value];
        // if (this.differ) {
        const changes = this.differ?.diff(newValue);
        if (changes) {
            if (newValue.length === 0) {
                this.multiSelectService.selectionModel.clear();
            }
            this.value$.next([...newValue]);
            this.onChange(this.value);
        }
        // }
    }
    get value() {
        const value = this.multiSelectService.selectionModel?.selected ?? [...this.#value()];
        return this.multiple() ? [...value] : value[0];
    }
    #value = signal([], ...(ngDevMode ? [{ debugName: "#value" }] : []));
    /**
     * Emits valueChange
     * @return TValue | TValue[]
     */
    valueChange = output();
    /**
     * Toggle visual validation feedback.
     * @return boolean | undefined
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Enable virtual scroller for options list.
     * @return boolean
     * @default false
     */
    virtualScroller = input(false, { ...(ngDevMode ? { debugName: "virtualScroller" } : {}), transform: booleanAttribute });
    /**
     * Amount of visible options, if set - overwrites optionsMaxHeight
     * @return number
     * @default 8
     */
    visibleItemsInput = input(8, { ...(ngDevMode ? { debugName: "visibleItemsInput" } : {}), alias: 'visibleItems', transform: numberAttribute });
    visibleItems = computed(() => {
        const visibleItems = this.visibleItemsInput();
        return visibleItems > 0 ? visibleItems : 8;
    }, ...(ngDevMode ? [{ debugName: "visibleItems" }] : []));
    #visibleItemsEffect = effect(() => {
        this.optionsMaxHeight.set((this.itemSize() * this.visibleItems() + 16).toString());
    }, ...(ngDevMode ? [{ debugName: "#visibleItemsEffect" }] : []));
    /**
     * The size of the option item in the list (in pixels).
     * @return number
     * @default 40
     */
    itemSize = input(40, { ...(ngDevMode ? { debugName: "itemSize" } : {}), transform: numberAttribute });
    /**
     * Min width of the options list (in pixels).
     * @return number
     * @default 196
     */
    itemMinWidthInput = input(196, { ...(ngDevMode ? { debugName: "itemMinWidthInput" } : {}), alias: 'itemMinWidth', transform: numberAttribute });
    itemMinWidth = linkedSignal(this.itemMinWidthInput, ...(ngDevMode ? [{ debugName: "itemMinWidth" }] : []));
    minWidth = linkedSignal(this.itemMinWidth, ...(ngDevMode ? [{ debugName: "minWidth" }] : []));
    /**
     * Toggle the visibility of the dropdown select component.
     * @type boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), alias: 'visible', transform: booleanAttribute });
    visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    #visibleEffect = effect(() => {
        const visible = this.visible();
        this.multiSelectService.toggleVisible(visible);
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    /**
     * Emits visibleChange
     * @return boolean
     */
    visibleChange = output();
    /**
     * Optional popper Options object
     * @type Partial<Options>
     */
    popperOptionsInput = input(defaultPopperOptions, { ...(ngDevMode ? { debugName: "popperOptionsInput" } : {}), alias: 'popperOptions' });
    popperOptions = computed(() => {
        const options = this.popperOptionsInput();
        const prevOptions = defaultPopperOptions;
        const { modifiers: prevModifiers = [] } = prevOptions;
        const { modifiers: currModifiers = [] } = options;
        const currOptions = {
            ...options,
            modifiers: [
                ...prevModifiers.filter((prevModifier) => !currModifiers.find((currModifier) => currModifier.name === prevModifier.name)),
                ...currModifiers
            ]
        };
        return { ...prevOptions, ...currOptions };
    }, ...(ngDevMode ? [{ debugName: "popperOptions" }] : []));
    isDropdownVisible = signal(false, ...(ngDevMode ? [{ debugName: "isDropdownVisible" }] : []));
    selectedOptions = computed(() => {
        return [...this.optionsSelected().values()];
    }, ...(ngDevMode ? [{ debugName: "selectedOptions" }] : []));
    get selectedOptionsText() {
        return this.selectedOptions()
            .map((option) => option.label)
            .join(', ');
    }
    counterText = computed(() => {
        return `${this.selectedOptions().length} ${this.selectionTypeCounterText()}`;
    }, ...(ngDevMode ? [{ debugName: "counterText" }] : []));
    counterTextType = computed(() => {
        return typeof (this.selectionTypeCounterTextPluralMap() ?? this.selectionTypeCounterText());
    }, ...(ngDevMode ? [{ debugName: "counterTextType" }] : []));
    counterPlaceholderText = computed(() => {
        const selectedOptions = this.selectedOptions();
        if (selectedOptions.length === 0 || this.selectionType() !== 'counter') {
            return null;
        }
        if (this.counterTextType() === 'string') {
            return this.counterText();
        }
        else {
            return `${selectedOptions.length} ${this.#i18nPlural.transform(selectedOptions.length, this.selectionTypeCounterTextPluralMap() ?? { other: this.selectionTypeCounterText() ?? '+' })}`;
        }
    }, ...(ngDevMode ? [{ debugName: "counterPlaceholderText" }] : []));
    focusMonitorSubscription;
    multiSelectOptionsContent;
    multiSelectOptionsView;
    scrollViewportView;
    optionsElementRef;
    scrollViewport;
    templates = {};
    contentTemplates;
    focusKeyManager;
    inputElement;
    dropdownMenu;
    dropdown;
    dropdownToggle;
    hostClasses = computed(() => {
        const valid = this.valid();
        return {
            'form-multi-select': true,
            disabled: this.disabled(),
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    multiselectClasses = computed(() => {
        const size = this.size();
        const valid = this.valid();
        return {
            'form-multi-select': true,
            [`form-multi-select-${size}`]: !!size,
            show: this.visible(),
            disabled: this.disabled(),
            'is-valid': valid === true,
            'is-invalid': valid === false,
            dropdown: false
        };
    }, ...(ngDevMode ? [{ debugName: "multiselectClasses" }] : []));
    multiselectSelectionClasses = computed(() => {
        const valid = this.valid();
        return {
            'form-multi-select-selection': true,
            // 'form-multi-select-selection-tags': this.selectionType() === 'tags' && this.selectedOptions.length,
            'form-control': valid === true || valid === false,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "multiselectSelectionClasses" }] : []));
    get tabIndex() {
        return this._tabIndex;
    }
    set tabIndex(value) {
        setTimeout(() => {
            this._tabIndex = this.disabled() || this.search() ? -1 : value === null ? null : 0;
        });
    }
    _tabIndex = -1;
    onBlur() {
        this.onTouched();
    }
    onKeyUp($event) {
        if (this.disabled()) {
            return;
        }
        const targetTagName = $event.target.tagName;
        if ($event.key === 'Escape') {
            this.visible.set(false);
            this.searchValue = '';
            this.setFocus('keyboard', 'onKeyUp');
            return;
        }
        if (['Enter', 'Space', 'ArrowDown'].includes($event.code) &&
            [this.inputElement?.nativeElement, this.elementRef.nativeElement].includes($event.target)) {
            $event.stopPropagation();
            this.visible.set(true);
            if (!this.search()) {
                this.setFocus('keyboard', 'onKeyUp');
            }
            return;
        }
        if (['Tab'].includes($event.key)) {
            // todo focus on input when tab in
            // if ($event.target === this.inputElement?.nativeElement) {
            //   console.log($event.key, $event.target)
            // }
            if ($event.target === this.elementRef.nativeElement) {
                this.visible.set(true);
                if (!this.search()) {
                    this.setFocus('keyboard', 'onKeyUp');
                }
                this.tabTarget = targetTagName;
                return;
            }
            if (this.visible() && this.subtreeFocused) {
                if (targetTagName === 'C-MULTI-SELECT-OPTION') {
                    if (this.tabTarget === 'C-MULTI-SELECT-OPTION') {
                        if ($event.shiftKey &&
                            this.focusKeyManager.activeItem?.value ===
                                this.options.filter((option) => option.visible && !option.disabled).slice(0)[0].value) {
                            this.setFocus('keyboard', 'onKeyUp');
                            this.tabTarget = targetTagName;
                            return;
                        }
                        if (!$event.shiftKey &&
                            this.focusKeyManager.activeItem?.value ===
                                this.options.filter((option) => option.visible && !option.disabled).slice(-1)[0].value) {
                            this.setFocus('keyboard', 'onKeyUp');
                            this.tabTarget = targetTagName;
                            this.updateActiveItem(0);
                            return;
                        }
                    }
                    if (!this.focusKeyManager.activeItem || this.tabTarget !== targetTagName) {
                        if (this.activeOption) {
                            this.focusKeyManager.setActiveItem(this.activeOption);
                        }
                        else {
                            this.setFirstItemActive();
                        }
                    }
                    else {
                        $event.shiftKey ? this.focusKeyManager.setPreviousItemActive() : this.focusKeyManager.setNextItemActive();
                    }
                }
                this.tabTarget = targetTagName;
                return;
            }
        }
    }
    onKeyDown($event) {
        const targetTagName = $event.target.tagName;
        if ($event.key === 'Enter' && targetTagName === 'INPUT') {
            $event.preventDefault();
        }
        if ($event.key === 'Tab') {
            this.tabTarget = targetTagName;
            return;
        }
        if ($event.key === 'ArrowDown' && targetTagName !== 'C-MULTI-SELECT-OPTION') {
            $event.preventDefault();
            return;
        }
        if ($event.key === 'Escape') {
            this.visible.set(false);
            this.searchValue = '';
            this.setFocus('keyboard', 'onKeyDown');
            return;
        }
        if (this.disabled()) {
            return;
        }
        // avoid setting focus to option while on input element
        if ($event.target === this.inputElement?.nativeElement) {
            return;
        }
        if ($event.key !== 'Tab') {
            this.focusKeyManager?.onKeydown($event);
        }
    }
    onClick($event) {
        if (this.disabled()) {
            return;
        }
        if ([this.dropdown.nativeElement].includes($event.target)) {
            this.visible.update((visible) => !visible);
        }
        else {
            this.visible.set(true);
        }
        this.setFocus('mouse', 'onClick');
    }
    visibleOptions = signal(false, ...(ngDevMode ? [{ debugName: "visibleOptions" }] : []));
    get subtreeFocused() {
        return this._subtreeFocused;
    }
    set subtreeFocused(focused) {
        this._subtreeFocused = this.disabled() ? false : focused;
        if (!focused) {
            this.searchValue = '';
        }
        if (!this.virtualScroller()) {
            this.visible.set(focused === false ? false : this.visible());
        }
    }
    _subtreeFocused = this.focusOrigin(null);
    focusOrigin(origin) {
        if (this.disabled()) {
            return false;
        }
        return !!origin;
    }
    ngOnInit() {
        this.multiSelectService.setSelectionModel(this.multiple());
        this.#optionsReady$
            .pipe(filter(() => !this.virtualScroller()), delay(0), tap(() => {
            this.setFocusKeyManager([...this.multiSelectOptionsContent, ...this.multiSelectOptionsView]);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        // this.multiSelectService.selectionModelChanged$
        //   .pipe(
        //     tap((change) => {
        //       const { added } = { ...change };
        //       if (added.length > 0 && this.clearSearchOnSelect()) {
        //         this.searchValue = '';
        //       }
        //     }),
        //     takeUntilDestroyed(this.#destroyRef)
        //   )
        //   .subscribe();
        this.multiSelectService.selectionModelChanged$
            .pipe(
        // delay(0),
        tap((change) => {
            const { added } = { ...change };
            if (added.length > 0 && this.clearSearchOnSelect()) {
                this.searchValue = '';
            }
        }), combineLatestWith(this.#optionsReady$), tap(([change, ready]) => {
            const { removed } = { ...change };
            removed?.length &&
                this.optionsSelected.update((optSelected) => {
                    const newOptSelected = new Map(optSelected);
                    removed.forEach((value) => {
                        newOptSelected.delete(value);
                    });
                    return newOptSelected;
                });
            this.multiSelectService.selectionModel.selected.forEach((key) => {
                const value = this.#options.get(key);
                if (value) {
                    this.optionsSelected.update((optSelected) => {
                        const newOptSelected = new Map(optSelected);
                        newOptSelected.set(key, value);
                        return newOptSelected;
                    });
                }
            });
            this.optionTags.set(Array.from(this.optionsSelected().values()));
            this.value = [...this.multiSelectService.selectionModel.selected];
            this.inputElementSize();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        // set initial values
        this.multiSelectService.selectionModel?.select(...this.#value());
        this.value$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
            if (!value.length) {
                this.clearAllOptions();
                return;
            }
            this.multiSelectService.selectionModel.setSelection(...value);
            this.#value.set(this.multiSelectService.selectionModel.selected);
        });
        this.value$
            .pipe(debounceTime(100), distinctUntilChanged(), tap((value) => {
            this.valueChange.emit(this.value);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.multiSelectService.optionsArray$
            .pipe(combineLatestWith(this.userOptionsArray$), tap(([options, userOptions]) => {
            this.visibleOptions.set(options.some((item) => item.visible) || userOptions.some((item) => item.visible));
            this.activeOption = null;
            if (this.virtualScroller()) {
                this.multiSelectOptionsContent?.forEach((option) => {
                    option.active = false;
                });
                this.scrollViewport?.setRenderedRange({ start: 0, end: this.visibleItems() });
                this.scrollViewport?.scrollToIndex(0);
            }
            else {
                this.multiSelectOptionsContent?.forEach((option) => {
                    const found = options.find((item) => item.value === option.value);
                    option.visible = found?.visible ?? false;
                    option.active = false;
                });
                const firstFocusable = this.firstFocusableItem;
                if (firstFocusable) {
                    this.updateActiveItem(firstFocusable);
                }
            }
            if (!this.activeOption) {
                setTimeout(() => {
                    this.updateActiveItem(0);
                });
            }
            this.multiSelectOptionsContent?.notifyOnChanges();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.tabIndex = 0;
    }
    ngOnDestroy() {
        this.#destroy$.next();
        this.#destroy$.complete();
        this.setFocusMonitor(false);
    }
    ngOnChanges(changes) {
        if (changes['value']) {
            const valueChanges = changes['value'];
            if (valueChanges.currentValue.length === 0) {
                this.clearAllOptions();
            }
            this.inputElementSize();
        }
    }
    makeOptions(options) {
        if (this.resetSelectionOnOptionsChange() && this.search() !== 'external') {
            this.clearAllOptions();
        }
        if (this.search() === 'external' && this.searchValue.length) {
            this.#options.forEach((option, key) => {
                option.visible = false;
            });
        }
        options.forEach((option) => {
            const key = option.value;
            const value = {
                value: option.value ?? option.label,
                label: option.label ?? option.value?.toString(),
                disabled: option.disabled === true,
                visible: this.search() === 'external'
                    ? true
                    : option.custom && option.selected
                        ? option.visible
                        : option.visible !== false,
                text: option.text ?? option.label ?? option.value?.toString(),
                selected: option.selected,
                custom: option.custom
                // selected: this.multiSelectService.selectionModel.isSelected(option.value),
                // id: option.id
            };
            this.#options.set(key, { ...value });
        });
        this.filterOptions(this.searchValue);
        this.multiSelectOptionsContent?.notifyOnChanges();
        this.multiSelectOptionsView?.notifyOnChanges();
        this.#optionsReady$.next();
    }
    userOptions = this.multiSelectService.getUserOptions();
    // #effectUserOptions = effect(() => {
    //   if (this.allowCreateOptions) {
    //     this.userOptionsArray$.next(Array.from(this.userOptions().filter(option => option.visible)));
    //   }
    // });
    addUserOption() {
        const allowCreateOptions = this.allowCreateOptions();
        if (allowCreateOptions) {
            if (this.userOptions().some((option) => option.value === this.searchValue)) {
                return;
            }
            if (this.#options.get(this.searchValue)) {
                return;
            }
            const userOption = {
                value: this.searchValue,
                label: this.searchValue,
                text: this.searchValue,
                visible: true,
                custom: true,
                selected: true
            };
            this.searchValue = this.clearSearchOnSelect() || allowCreateOptions ? '' : this.searchValue;
            this.multiSelectService.addUserOption(userOption);
            this.multiSelectService.selectionModel.select(userOption.value);
            this.optionsSelected.update((optSelected) => {
                const newOptSelected = new Map(optSelected);
                newOptSelected.set(userOption.value, userOption);
                return newOptSelected;
            });
            this.makeOptions([...this.options, ...this.userOptions()]);
        }
    }
    removeAllUserOptions() {
        this.multiSelectService.removeAllUserOptions();
    }
    ngAfterViewInit() {
        this.setVirtualScroller();
        this.multiSelectOptionsContent?.changes
            .pipe(delay(0), distinctUntilChanged((previous, current) => {
            const prev = previous.toArray().map((option) => option.value);
            const curr = current.toArray().map((option) => option.value);
            if (this.virtualScroller()) {
                return JSON.stringify(prev) === JSON.stringify(curr);
            }
            return JSON.stringify(this.#optionsContent) === JSON.stringify(curr);
        }), tap((changes) => {
            this.#optionsContent = changes.toArray().map((option) => option.value);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((next) => {
            this.visibleOptions.set(this.multiSelectOptionsContent?.some((option) => option.visible));
            if (!this.virtualScroller() || !this.visibleOptions()) {
                this.#options.clear();
                this.makeOptions(this.multiSelectOptionsContent.toArray());
            }
        });
        this.visibleOptions.set(this.multiSelectOptionsContent?.length > 0 || this.multiSelectOptionsView?.length > 0);
        if (this.visibleOptions()) {
            this.multiSelectOptionsContent.notifyOnChanges();
        }
        this.multiselectSubscribe();
        this.setFocusMonitor(!this.disabled());
        this.setListKeyManager();
        this.inputElementSize();
        this.multiSelectOptionsContent.notifyOnChanges();
        this.multiSelectService.multiSelectFocus$
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((option) => {
            if (this.activeOption && this.activeOption.value !== option.value) {
                this.focusKeyManager.setActiveItem(option);
            }
        });
    }
    tabTarget;
    multiselectSubscribe(subscribe = true) {
        this.multiSelectService.multiSelectVisible$
            .pipe(debounceTime(100), distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
            .subscribe((visible) => {
            if (this.isDropdownVisible() !== visible) {
                this.isDropdownVisible.set(visible);
                this.visibleChange.emit(visible);
                if (!visible) {
                    /* empty */
                }
                else {
                    if (this.virtualScroller()) {
                        {
                            const dropdownMinWidth = parseInt(this.dropdownMenu.nativeElement.style.minWidth, 10);
                            const minWidth = this.itemMinWidth();
                            this.minWidth.set(minWidth > dropdownMinWidth ? minWidth : dropdownMinWidth);
                        }
                        this.scrollViewport?.setRenderedRange({ start: 0, end: this.visibleItems() });
                        this.scrollViewport?.scrollToIndex(0);
                    }
                }
                this.multiSelectOptionsContent.notifyOnChanges();
            }
            this.updateActiveItem(0);
        });
    }
    clearAllOptions($event) {
        const enabledOptions = Array.from(this.optionsSelected().values())
            .filter((option) => !option.disabled)
            .map((option) => option.value);
        this.multiSelectService.selectionModel?.deselect(...enabledOptions);
        this.removeAllUserOptions();
        setTimeout(() => {
            this.searchValue = '';
            if ($event) {
                this.setFocus('program', 'clearAllOptions');
            }
        });
    }
    selectAllOptions() {
        const enabledOptions = Array.from(this.#options.values())
            .filter((option) => option.visible && !option.disabled)
            .map((option) => option.value);
        this.multiSelectService.selectionModel.select(...enabledOptions);
        this.inputElementSize();
    }
    handleTagRemove(value) {
        this.multiSelectService.selectionModel.deselect(value);
        this.setFocus('program', 'handleTagRemove');
    }
    setFocus(origin = 'program', methodName) {
        if (this.disabled() || (origin === 'program' && !this.subtreeFocused)) {
            return;
        }
        const element = (this.inputElement?.nativeElement ?? this.elementRef.nativeElement);
        const preventScroll = origin === 'program';
        this.focusMonitor?.focusVia(element, origin, { preventScroll: preventScroll });
        // console.log('setFocus', origin, methodName, 'preventScroll:', preventScroll, element.tagName, this.value);
    }
    filterOptions(value) {
        const searchString = value.toLowerCase() ?? '';
        this.#options.forEach((item, index) => {
            if (searchString === '') {
                item.visible = true;
                return;
            }
            if (typeof this.search() === 'function') {
                const searchFn = this.search();
                item.visible = searchFn(item, searchString);
                return;
            }
            if ((this.search() === 'external' && !item.custom) || !searchString) {
                // item.visible = true;
                return;
            }
            item.visible = item.label?.toLowerCase().includes(searchString) ?? true;
        });
        this.multiSelectService.updateOptionsArray(Array.from(this.#options.values()).filter((item) => item.visible));
        this.userOptionsArray$.next(Array.from(this.#options.values()).filter((item) => item.custom));
    }
    inputElementSize() {
        if (this.inputElement) {
            if (!this.inputElement?.nativeElement?.disabled) {
                this.renderer.setAttribute(this.inputElement?.nativeElement, 'size', (this.searchValue.length + 2).toString());
            }
            else {
                // this.renderer.removeAttribute(this.inputElement?.nativeElement, 'size');
                this.renderer.setAttribute(this.inputElement?.nativeElement, 'size', '2');
            }
        }
    }
    handleSearchKeyDown($event) {
        if (['Enter'].includes($event.key) && this.searchValue.length && this.allowCreateOptions()) {
            $event.preventDefault();
            $event.stopPropagation();
            $event.stopImmediatePropagation();
            this.addUserOption();
            this.changeDetectorRef.markForCheck();
            return;
        }
        if (this.searchValue.length || this.optionsSelected().size === 0) {
            return;
        }
        if (['Backspace', 'Delete'].includes($event.key)) {
            $event.stopPropagation();
            const last = this.selectedOptions()
                .filter((option) => !option.disabled)
                .pop();
            if (last) {
                this.multiSelectService.selectionModel.deselect(last.value);
            }
        }
    }
    setFocusMonitor(monitor) {
        if (monitor && !this.disabled()) {
            // todo focus on input when tab in
            // this.focusMonitor?.monitor(this.elementRef, false)
            //   .pipe(
            //     tap(origin => {
            //       if (origin === 'keyboard' && this.search() && this.selectedOptions.length) {
            //         this.inputElement.nativeElement.focus();
            //       }
            //     }),
            //     takeUntilDestroyed(this.#destroyRef)
            //   )
            //   .subscribe();
            this.focusMonitorSubscription = this.focusMonitor
                ?.monitor(this.elementRef, true)
                .pipe(
            // to avoid focus lost on speed scrolling with virtualScroll
            debounceTime(100), takeUntilDestroyed(this.#destroyRef))
                .subscribe((origin) => {
                this.ngZone.run(() => {
                    this.subtreeFocused = this.focusOrigin(origin);
                    if (!this.subtreeFocused) {
                        this.onBlur();
                    }
                    this.changeDetectorRef?.markForCheck();
                });
            });
        }
        else {
            this.focusMonitor?.stopMonitoring(this.elementRef);
            this.focusMonitorSubscription?.unsubscribe();
        }
    }
    activeOption = null;
    handleScrolledIndexChange(scrolledIndex, scrollViewport) {
        if (!this.scrollViewport) {
            this.scrollViewport = scrollViewport;
        }
        const availableOptions = this.options.filter((option) => option.visible);
        const firstVisibleOption = availableOptions[scrolledIndex];
        const activeOptionIndex = availableOptions.findIndex((option) => option.value === this.activeOption?.value);
        const optionsArray = this.multiSelectOptionsContent.toArray();
        if (scrolledIndex > activeOptionIndex) {
            const firstVisibleItem = optionsArray.find((option) => option.value === firstVisibleOption?.value);
            if (firstVisibleItem && scrolledIndex) {
                this.focusKeyManager.setActiveItem(firstVisibleItem);
            }
            return;
        }
        const visibleItems = this.visibleItems() - 2;
        if (scrolledIndex + visibleItems <= activeOptionIndex) {
            const lastVisibleItem = optionsArray.findIndex((option) => option.value === firstVisibleOption.value);
            if (lastVisibleItem > -1) {
                this.focusKeyManager.setActiveItem(lastVisibleItem + visibleItems);
            }
            return;
        }
    }
    trackByFn(index, option) {
        return option.value;
    }
    handleSearchValueChange(searchValue) {
        this.searchValue = searchValue;
        if (!searchValue) {
            this.scrollViewport?.scrollToIndex(0);
            this.multiSelectOptionsContent.notifyOnChanges();
            if (this.subtreeFocused) {
                this.setFocus('program', 'handleSearchValueChange');
            }
        }
    }
    setVirtualScroller() {
        if (!this.virtualScroller()) {
            return;
        }
        // multiSelectOptionTemplate for virtualScroller
        this.contentTemplates.forEach((child) => {
            this.templates[child.id] = child.templateRef;
        });
        // if no template found, get defaultMultiSelectOptionTemplate from view
        if (!this.templates['multiSelectOptionTemplate']) {
            this.multiSelectOptionsContent = this.multiSelectOptionsView;
            this.multiSelectOptionsContent.notifyOnChanges();
        }
        // multiSelectOptionsContent gets out of order on changes
        // do sort() and reset()
        const indexOfOption = (option) => {
            return [...option.elementRef.nativeElement.parentElement.children]?.indexOf(option.elementRef.nativeElement);
        };
        const optionsChange$ = this.multiSelectOptionsContent.changes;
        const sortedOptions$ = optionsChange$.pipe(map((options) => options.toArray()), filter((options) => options.length > 0), map((options) => options.sort((a, b) => indexOfOption(a) - indexOfOption(b))), takeUntilDestroyed(this.#destroyRef));
        sortedOptions$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((sortedOptions) => {
            return this.multiSelectOptionsContent.reset(sortedOptions);
        });
        this.scrollViewportView.changes
            .pipe(take(1), map((changes) => changes.toArray()[0]), tap((scrollViewport) => {
            if (!this.scrollViewport) {
                this.scrollViewport = scrollViewport;
                const cdkVirtualScrollContentWrapper = this.scrollViewport.getElementRef().nativeElement?.firstElementChild;
                if (cdkVirtualScrollContentWrapper) {
                    const style = this.#document.defaultView?.getComputedStyle(cdkVirtualScrollContentWrapper, null) ?? undefined;
                    const padding = parseInt(style?.getPropertyValue('padding-left') ?? '12px') * 3;
                    const observer = new MutationObserver((mutationRecords) => {
                        mutationRecords.forEach((child) => {
                            child.addedNodes.forEach((node) => {
                                if (node.nodeName === 'C-MULTI-SELECT-OPTION') {
                                    const width = node.offsetWidth;
                                    if (width > this.itemMinWidth()) {
                                        this.itemMinWidth.set(width + padding);
                                    }
                                }
                            });
                        });
                    });
                    observer?.observe(cdkVirtualScrollContentWrapper, { childList: true });
                    this.#destroy$.subscribe(() => {
                        observer?.disconnect();
                    });
                }
            }
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    setListKeyManager() {
        // run once on multiSelectOptionsContent first set
        this.multiSelectOptionsContent.changes
            .pipe(filter((changes) => this.virtualScroller()), take(1), tap(() => {
            this.setFocusKeyManager(this.multiSelectOptionsContent);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    setFocusKeyManager(focusableOptions) {
        // this.focusKeyManager?.destroy();
        this.focusKeyManager = new FocusKeyManager(focusableOptions)
            .withHomeAndEnd()
            .withPageUpDown()
            .skipPredicate((option) => option.disabled || !option.visible);
        if (!this.virtualScroller()) {
            this.focusKeyManager.withTypeAhead(300);
        }
        this.focusKeyManager.tabOut
            .pipe(takeUntilDestroyed(this.#destroyRef), tap(() => {
            /* empty */
        }))
            .subscribe();
        this.focusKeyManager.change.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((change) => {
            if (change < 0) {
                this.activeOption ? this.focusKeyManager.setActiveItem(this.activeOption) : this.setFirstItemActive();
            }
            if (this.focusKeyManager.activeItem) {
                this.focusKeyManager.activeItem.active = true;
            }
            this.activeOption = this.focusKeyManager.activeItem;
            const option = this.activeOption;
            this.multiSelectService.focusChange(option);
            this.multiSelectOptionsContent.notifyOnChanges();
            this.multiSelectOptionsView.notifyOnChanges();
        });
        // if (!this.focusKeyManager.activeItem) {
        //   this.setFirstItemActive();
        // }
    }
    get firstFocusableItem() {
        return (this.multiSelectOptionsContent?.find((option) => option.visible && !option.disabled) ??
            this.multiSelectOptionsView?.find((option) => option.visible && !option.disabled));
    }
    setFirstItemActive() {
        const firstFocusableItem = this.firstFocusableItem;
        firstFocusableItem
            ? this.focusKeyManager?.setActiveItem(firstFocusableItem)
            : this.focusKeyManager?.setFirstItemActive();
        this.activeOption = this.focusKeyManager?.activeItem ?? null;
    }
    updateActiveItem(item) {
        const firstFocusableItem = this.firstFocusableItem ?? item;
        firstFocusableItem
            ? this.focusKeyManager?.updateActiveItem(firstFocusableItem)
            : this.focusKeyManager?.updateActiveItem(0);
        if (this.focusKeyManager?.activeItem) {
            this.multiSelectOptionsContent?.forEach((option) => {
                option.active = false;
            });
            this.multiSelectOptionsView?.forEach((option) => {
                option.active = false;
            });
            this.focusKeyManager.activeItem.active = true;
        }
        this.activeOption = this.focusKeyManager?.activeItem ?? null;
    }
    handleIndicatorClick($event) {
        $event.preventDefault();
        $event.stopPropagation();
        this.visible.update((visible) => !visible);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: MultiSelectComponent, isStandalone: true, selector: "c-multi-select", inputs: { allowCreateOptions: { classPropertyName: "allowCreateOptions", publicName: "allowCreateOptions", isSignal: true, isRequired: false, transformFunction: null }, cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: true, isRequired: false, transformFunction: null }, clearSearchOnSelect: { classPropertyName: "clearSearchOnSelect", publicName: "clearSearchOnSelect", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, multiple: { classPropertyName: "multiple", publicName: "multiple", isSignal: true, isRequired: false, transformFunction: null }, options: { classPropertyName: "options", publicName: "options", isSignal: false, isRequired: false, transformFunction: null }, optionsMaxHeightInput: { classPropertyName: "optionsMaxHeightInput", publicName: "optionsMaxHeight", isSignal: true, isRequired: false, transformFunction: null }, optionsStyle: { classPropertyName: "optionsStyle", publicName: "optionsStyle", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, resetSelectionOnOptionsChange: { classPropertyName: "resetSelectionOnOptionsChange", publicName: "resetSelectionOnOptionsChange", isSignal: true, isRequired: false, transformFunction: null }, search: { classPropertyName: "search", publicName: "search", isSignal: true, isRequired: false, transformFunction: null }, searchNoResultsLabel: { classPropertyName: "searchNoResultsLabel", publicName: "searchNoResultsLabel", isSignal: true, isRequired: false, transformFunction: null }, searchValue: { classPropertyName: "searchValue", publicName: "searchValue", isSignal: false, isRequired: false, transformFunction: null }, selectAll: { classPropertyName: "selectAll", publicName: "selectAll", isSignal: true, isRequired: false, transformFunction: null }, selectAllLabel: { classPropertyName: "selectAllLabel", publicName: "selectAllLabel", isSignal: true, isRequired: false, transformFunction: null }, selectionType: { classPropertyName: "selectionType", publicName: "selectionType", isSignal: true, isRequired: false, transformFunction: null }, selectionTypeCounterText: { classPropertyName: "selectionTypeCounterText", publicName: "selectionTypeCounterText", isSignal: true, isRequired: false, transformFunction: null }, selectionTypeCounterTextPluralMap: { classPropertyName: "selectionTypeCounterTextPluralMap", publicName: "selectionTypeCounterTextPluralMap", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: false, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, virtualScroller: { classPropertyName: "virtualScroller", publicName: "virtualScroller", isSignal: true, isRequired: false, transformFunction: null }, visibleItemsInput: { classPropertyName: "visibleItemsInput", publicName: "visibleItems", isSignal: true, isRequired: false, transformFunction: null }, itemSize: { classPropertyName: "itemSize", publicName: "itemSize", isSignal: true, isRequired: false, transformFunction: null }, itemMinWidthInput: { classPropertyName: "itemMinWidthInput", publicName: "itemMinWidth", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, popperOptionsInput: { classPropertyName: "popperOptionsInput", publicName: "popperOptions", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { searchValueChange: "searchValueChange", valueChange: "valueChange", visibleChange: "visibleChange" }, host: { listeners: { "click": "onClick($event)", "keyup": "onKeyUp($event)", "keydown": "onKeyDown($event)" }, properties: { "class": "hostClasses()", "attr.aria-multiselectable": "multiple()", "attr.aria-expanded": "visible()", "attr.aria-disabled": "disabled()", "attr.tabindex": "tabIndex" } }, providers: [
            I18nPluralPipe,
            MultiSelectService,
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => MultiSelectComponent),
                multi: true
            }
        ], queries: [{ propertyName: "multiSelectOptionsContent", predicate: MultiSelectOptionComponent, descendants: true }, { propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true }], viewQueries: [{ propertyName: "optionsElementRef", first: true, predicate: ["options"], descendants: true }, { propertyName: "inputElement", first: true, predicate: ["inputElement"], descendants: true }, { propertyName: "dropdownMenu", first: true, predicate: DropdownMenuDirective, descendants: true, read: ElementRef }, { propertyName: "dropdown", first: true, predicate: DropdownComponent, descendants: true, read: ElementRef }, { propertyName: "dropdownToggle", first: true, predicate: DropdownToggleDirective, descendants: true, read: ElementRef }, { propertyName: "multiSelectOptionsView", predicate: MultiSelectOptionComponent, descendants: true }, { propertyName: "scrollViewportView", predicate: ["scrollViewport"], descendants: true }], exportAs: ["cMultiSelect"], usesOnChanges: true, ngImport: i0, template: "@let isDisabled = disabled();\n@let placeholderText = placeholder();\n@let searchOn = search();\n@let cleanerBtn = cleaner();\n@let vScroller = virtualScroller();\n@let selType = selectionType();\n@let selectedSize = optionsSelected().size;\n@let selectedOpts = selectedOptions();\n@let counterPlaceholderTxt = counterPlaceholderText();\n<c-dropdown #dropdown=\"cDropdown\"\n            [(visible)]=\"visible\"\n            [autoClose]=\"'outside'\"\n            [class]=\"multiselectClasses()\"\n            [popperOptions]=\"popperOptions()\">\n  <div #dropdownToggle=\"cDropdownToggle\" [caret]=\"false\" [disabled]=\"isDisabled\" cDropdownToggle role=\"button\"\n       class=\"form-multi-select-input-group\">\n    <span [class]=\"multiselectSelectionClasses()\" [class.form-multi-select-selection-tags]=\"selType === 'tags' && selectedOpts.length\">\n      @if (selType === 'tags') {\n        @for (option of optionTags(); track option.value; let i = $index) {\n          <c-multi-select-tag\n            (remove)=\"handleTagRemove($event)\"\n            [disabled]=\"isDisabled || (option.disabled ?? false)\"\n            [label]=\"option.label ?? option.value?.toString()\"\n            [option]=\"option\"\n            [value]=\"option.value\"\n            class=\"form-multi-select-tag text-truncate\"\n            tabindex=\"-1\"\n          />\n        }\n      } @else if (selType === 'text' && !!selectedOpts.length) {\n        @for (option of selectedOpts; track option.value; let i = $index, last = $last) {\n          <span class=\"form-multi-select-text text-truncate\">{{ option.label }}{{ last ? '&nbsp;' : ',&nbsp;' }}</span>\n        }\n      }\n      @if (!searchOn) {\n        <span class=\"text-placeholder text-truncate\">\n          {{ selectedSize === 0 ? placeholderText : counterPlaceholderTxt }}\n        </span>\n      } @else {\n        <input\n          #inputElement\n          (keydown)=\"handleSearchKeyDown($event)\"\n          (valueChange)=\"handleSearchValueChange($event)\"\n          [attr.placeholder]=\"selectedOpts.length === 0 ? placeholderText : counterPlaceholderTxt\"\n          [disabled]=\"isDisabled || !searchOn\"\n          [class]=\"{ 'form-multi-select-search': true, disabled: (isDisabled || !searchOn), 'text-truncate': true }\"\n          [ngModel]=\"searchValue\"\n          [value]=\"searchValue\"\n          autocomplete=\"off\"\n          cMultiSelectSearch\n          size=\"2\"\n          tabindex=\"{{ isDisabled ? -1 : 0 }}\"\n          type=\"text\"\n        >\n      }\n    </span>\n    <div class=\"form-multi-select-buttons\">\n      @if (!!cleanerBtn && selectedSize > 0 && !isDisabled) {\n        <button\n          (click)=\"clearAllOptions($event)\"\n          [disabled]=\"isDisabled || (!isDropdownVisible() && cleanerBtn !== 'active')\"\n          aria-label=\"Clear all\"\n          class=\"form-multi-select-cleaner\"\n          type=\"button\"\n        ></button>\n      }\n      <button (click)=\"handleIndicatorClick($event)\"\n              [disabled]=\"isDisabled\"\n              class=\"form-multi-select-indicator\"\n              type=\"button\">\n      </button>\n    </div>\n  </div>\n  @if (!isDisabled) {\n    <div #dropdownMenu=\"cDropdownMenu\" [visible]=\"dropdown.visible()\" cDropdownMenu\n         class=\"form-multi-select-dropdown\" role=\"menu\">\n      @if (visibleOptions() && options.length > 0) {\n        @if (selectAll() && multiple() && !vScroller) {\n          <button\n            (click)=\"selectAllOptions()\"\n            class=\"form-multi-select-all\"\n            type=\"button\"\n            tabindex=\"0\"\n          >\n            {{ selectAllLabel() }}\n          </button>\n        }\n      }\n      @if ((visibleOptions() && options.length > 0) || loading()) {\n        <ng-container *ngTemplateOutlet=\"vScroller ? multiselectVirtualScroller : multiselectOptionsDiv\" />\n      } @else {\n        <div class=\"form-multi-select-options-empty\">{{ searchNoResultsLabel() }}</div>\n      }\n      @if (loading()) {\n        <c-element-cover [style]=\"{'border-radius': '6px', 'z-index': 2}\" />\n      }\n    </div>\n  }\n</c-dropdown>\n\n<ng-template #multiselectVirtualScroller>\n  @let itemSizePx = itemSize();\n  @let bufferPx = itemSizePx * visibleItems();\n  @defer (on immediate) {\n    @let optionsArray = multiSelectService?.optionsArray$ | async;\n    @if (optionsArray) {\n      @let optMaxHeight = optionsMaxHeight();\n      @let optMinWidth = minWidth();\n      <cdk-virtual-scroll-viewport\n        #scrollViewport\n        (scrolledIndexChange)=\"handleScrolledIndexChange($event, scrollViewport)\"\n        [itemSize]=\"itemSizePx\"\n        [ngStyle]=\"optMaxHeight !== 'auto' ? { 'height.px': optMaxHeight, 'maxHeight.px': optMaxHeight, 'minWidth.px': optMinWidth, overflowX: 'hidden' } : { 'minWidth.px': optMinWidth, overflowX: 'hidden'}\"\n        maxBufferPx=\"{{bufferPx}}\"\n        minBufferPx=\"{{bufferPx}}\"\n        class=\"form-multi-select-options\"\n        tabindex=\"-1\"\n      >\n        <ng-container\n          #cdkVirtualFor\n          *cdkVirtualFor=\"let option of optionsArray; trackBy: trackByFn; templateCacheSize: 0; even as even; odd as odd; index as index; count as count; first as first; last as last;\"\n          [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"\n          [ngTemplateOutlet]=\"templates['multiSelectOptionTemplate'] || defaultMultiSelectOptionTemplate\"\n        />\n      </cdk-virtual-scroll-viewport>\n    }\n  }\n</ng-template>\n\n<ng-template #multiselectOptionsDiv>\n  @let optMaxHeight = optionsMaxHeight();\n  <div [class.form-multi-select-options]=\"visibleOptions()\" [ngStyle]=\"optMaxHeight !== 'auto' ? { 'maxHeight.px': optMaxHeight, overflowY: 'scroll' } : {}\">\n    <ng-content />\n    <ng-container [ngTemplateOutlet]=\"userOptionsTemplate\" />\n  </div>\n</ng-template>\n\n<ng-template #defaultMultiSelectOptionTemplate let-option>\n  <c-multi-select-option\n    [disabled]=\"option.disabled\"\n    [label]=\"option.label\"\n    [text]=\"option.text\"\n    [value]=\"option.value\"\n    [visible]=\"option.visible\"\n  >\n    {{ option.text }}\n  </c-multi-select-option>\n</ng-template>\n\n<ng-template #userOptionsTemplate>\n  @for (option of userOptionsArray$ | async; track option; let even = $even, odd = $odd, index = $index, count = $count, first = $first, last = $last) {\n    <ng-container\n      [ngTemplateOutlet]=\"templates['multiSelectOptionTemplate'] || defaultMultiSelectOptionTemplate\"\n      [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"\n    />\n  }\n</ng-template>\n", styles: [":host .disabled{pointer-events:none}:host{display:block}:host:focus-visible{outline:none}:host.cdk-focused:not(.disabled) .form-multi-select{outline:none;--cui-form-multi-select-focus-box-shadow: 0 0 0 var(--cui-focus-ring-width) rgb(from var(--cui-form-multi-select-focus-border-color) r g b / var(--cui-focus-ring-opacity))}:host.cdk-focused:not(.disabled) .form-multi-select.is-valid{--cui-form-multi-select-focus-border-color: var(--cui-form-valid-border-color)}:host.cdk-focused:not(.disabled) .form-multi-select.is-invalid{--cui-form-multi-select-focus-border-color: var(--cui-form-invalid-border-color)}:host .form-multi-select .form-multi-select-selection{display:flex;flex:1 1 auto;flex-wrap:wrap}:host .form-multi-select .form-multi-select-selection .text-placeholder,:host .form-multi-select .form-multi-select-selection .form-multi-select-search::placeholder{color:var(--cui-form-multi-select-color);opacity:.8}:host .form-multi-select .form-multi-select-selection.form-control{border:none}:host .form-multi-select .form-multi-select-search[size]{display:flex}:host ::ng-deep .cdk-virtual-scroll-content-wrapper{padding:var(--cui-form-multi-select-options-padding-y) var(--cui-form-multi-select-options-padding-x);font-size:1rem;color:var(--cui-form-multi-select-options-color)}:host .cdk-virtual-scroll-viewport{width:var(--cui-dropdown-min-width)}:host .dropdown-menu{--cui-dropdown-padding-y: 0}:host-context(.input-group) :host:not(:first-child) .form-multi-select-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .form-multi-select-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .form-multi-select-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .form-multi-select-input-group{border-start-end-radius:var(--cui-form-multi-select-border-radius);border-end-end-radius:var(--cui-form-multi-select-border-radius)}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: MultiSelectTagComponent, selector: "c-multi-select-tag", inputs: ["option", "disabled", "label", "value"], outputs: ["remove"] }, { kind: "component", type: MultiSelectOptionComponent, selector: "c-multi-select-option", inputs: ["optionsStyle", "label", "text", "visible", "disabled", "selected", "value", "active", "role"], outputs: ["selectedChange", "focusChange"], exportAs: ["cMultiSelectOption"] }, { kind: "directive", type: MultiSelectSearchDirective, selector: "[cMultiSelectSearch]", inputs: ["delay", "value"], outputs: ["valueChange"], exportAs: ["cMultiSelectSearch"] }, { kind: "component", type: DropdownComponent, selector: "c-dropdown", inputs: ["alignment", "autoClose", "direction", "placement", "popper", "popperOptions", "variant", "visible"], outputs: ["visibleChange"], exportAs: ["cDropdown"] }, { kind: "directive", type: DropdownMenuDirective, selector: "[cDropdownMenu]", inputs: ["alignment", "visible"], exportAs: ["cDropdownMenu"] }, { kind: "directive", type: DropdownToggleDirective, selector: "[cDropdownToggle]", inputs: ["dropdownComponent", "disabled", "caret", "split"], exportAs: ["cDropdownToggle"] }, { kind: "component", type: ElementCoverComponent, selector: "c-element-cover, [cElementCover]", inputs: ["boundaries", "opacity"] }, { kind: "directive", type: NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: AsyncPipe, name: "async" }], deferBlockDependencies: [() => [NgTemplateOutlet, import('@angular/cdk/scrolling').then(m => m.CdkFixedSizeVirtualScroll), import('@angular/cdk/scrolling').then(m => m.CdkVirtualScrollViewport), import('@angular/cdk/scrolling').then(m => m.CdkVirtualForOf), NgStyle,
                AsyncPipe]] });
}
i0.ɵɵngDeclareClassMetadataAsync({ minVersion: "18.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectComponent, resolveDeferredDeps: () => [import('@angular/cdk/scrolling').then(m => m.CdkFixedSizeVirtualScroll), import('@angular/cdk/scrolling').then(m => m.CdkVirtualScrollViewport), import('@angular/cdk/scrolling').then(m => m.CdkVirtualForOf)], resolveMetadata: (CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport, CdkVirtualForOf) => ({ decorators: [{
                type: Component,
                args: [{ selector: 'c-multi-select', exportAs: 'cMultiSelect', providers: [
                            I18nPluralPipe,
                            MultiSelectService,
                            {
                                provide: NG_VALUE_ACCESSOR,
                                useExisting: forwardRef(() => MultiSelectComponent),
                                multi: true
                            }
                        ], imports: [
                            AsyncPipe,
                            NgTemplateOutlet,
                            CdkFixedSizeVirtualScroll,
                            CdkVirtualScrollViewport,
                            CdkVirtualForOf,
                            FormsModule,
                            MultiSelectTagComponent,
                            MultiSelectOptionComponent,
                            MultiSelectSearchDirective,
                            DropdownComponent,
                            DropdownMenuDirective,
                            DropdownToggleDirective,
                            ElementCoverComponent,
                            NgStyle
                        ], host: {
                            '[class]': 'hostClasses()',
                            '(click)': 'onClick($event)',
                            '(keyup)': 'onKeyUp($event)',
                            '(keydown)': 'onKeyDown($event)',
                            '[attr.aria-multiselectable]': 'multiple()',
                            '[attr.aria-expanded]': 'visible()',
                            '[attr.aria-disabled]': 'disabled()',
                            '[attr.tabindex]': 'tabIndex'
                        }, template: "@let isDisabled = disabled();\n@let placeholderText = placeholder();\n@let searchOn = search();\n@let cleanerBtn = cleaner();\n@let vScroller = virtualScroller();\n@let selType = selectionType();\n@let selectedSize = optionsSelected().size;\n@let selectedOpts = selectedOptions();\n@let counterPlaceholderTxt = counterPlaceholderText();\n<c-dropdown #dropdown=\"cDropdown\"\n            [(visible)]=\"visible\"\n            [autoClose]=\"'outside'\"\n            [class]=\"multiselectClasses()\"\n            [popperOptions]=\"popperOptions()\">\n  <div #dropdownToggle=\"cDropdownToggle\" [caret]=\"false\" [disabled]=\"isDisabled\" cDropdownToggle role=\"button\"\n       class=\"form-multi-select-input-group\">\n    <span [class]=\"multiselectSelectionClasses()\" [class.form-multi-select-selection-tags]=\"selType === 'tags' && selectedOpts.length\">\n      @if (selType === 'tags') {\n        @for (option of optionTags(); track option.value; let i = $index) {\n          <c-multi-select-tag\n            (remove)=\"handleTagRemove($event)\"\n            [disabled]=\"isDisabled || (option.disabled ?? false)\"\n            [label]=\"option.label ?? option.value?.toString()\"\n            [option]=\"option\"\n            [value]=\"option.value\"\n            class=\"form-multi-select-tag text-truncate\"\n            tabindex=\"-1\"\n          />\n        }\n      } @else if (selType === 'text' && !!selectedOpts.length) {\n        @for (option of selectedOpts; track option.value; let i = $index, last = $last) {\n          <span class=\"form-multi-select-text text-truncate\">{{ option.label }}{{ last ? '&nbsp;' : ',&nbsp;' }}</span>\n        }\n      }\n      @if (!searchOn) {\n        <span class=\"text-placeholder text-truncate\">\n          {{ selectedSize === 0 ? placeholderText : counterPlaceholderTxt }}\n        </span>\n      } @else {\n        <input\n          #inputElement\n          (keydown)=\"handleSearchKeyDown($event)\"\n          (valueChange)=\"handleSearchValueChange($event)\"\n          [attr.placeholder]=\"selectedOpts.length === 0 ? placeholderText : counterPlaceholderTxt\"\n          [disabled]=\"isDisabled || !searchOn\"\n          [class]=\"{ 'form-multi-select-search': true, disabled: (isDisabled || !searchOn), 'text-truncate': true }\"\n          [ngModel]=\"searchValue\"\n          [value]=\"searchValue\"\n          autocomplete=\"off\"\n          cMultiSelectSearch\n          size=\"2\"\n          tabindex=\"{{ isDisabled ? -1 : 0 }}\"\n          type=\"text\"\n        >\n      }\n    </span>\n    <div class=\"form-multi-select-buttons\">\n      @if (!!cleanerBtn && selectedSize > 0 && !isDisabled) {\n        <button\n          (click)=\"clearAllOptions($event)\"\n          [disabled]=\"isDisabled || (!isDropdownVisible() && cleanerBtn !== 'active')\"\n          aria-label=\"Clear all\"\n          class=\"form-multi-select-cleaner\"\n          type=\"button\"\n        ></button>\n      }\n      <button (click)=\"handleIndicatorClick($event)\"\n              [disabled]=\"isDisabled\"\n              class=\"form-multi-select-indicator\"\n              type=\"button\">\n      </button>\n    </div>\n  </div>\n  @if (!isDisabled) {\n    <div #dropdownMenu=\"cDropdownMenu\" [visible]=\"dropdown.visible()\" cDropdownMenu\n         class=\"form-multi-select-dropdown\" role=\"menu\">\n      @if (visibleOptions() && options.length > 0) {\n        @if (selectAll() && multiple() && !vScroller) {\n          <button\n            (click)=\"selectAllOptions()\"\n            class=\"form-multi-select-all\"\n            type=\"button\"\n            tabindex=\"0\"\n          >\n            {{ selectAllLabel() }}\n          </button>\n        }\n      }\n      @if ((visibleOptions() && options.length > 0) || loading()) {\n        <ng-container *ngTemplateOutlet=\"vScroller ? multiselectVirtualScroller : multiselectOptionsDiv\" />\n      } @else {\n        <div class=\"form-multi-select-options-empty\">{{ searchNoResultsLabel() }}</div>\n      }\n      @if (loading()) {\n        <c-element-cover [style]=\"{'border-radius': '6px', 'z-index': 2}\" />\n      }\n    </div>\n  }\n</c-dropdown>\n\n<ng-template #multiselectVirtualScroller>\n  @let itemSizePx = itemSize();\n  @let bufferPx = itemSizePx * visibleItems();\n  @defer (on immediate) {\n    @let optionsArray = multiSelectService?.optionsArray$ | async;\n    @if (optionsArray) {\n      @let optMaxHeight = optionsMaxHeight();\n      @let optMinWidth = minWidth();\n      <cdk-virtual-scroll-viewport\n        #scrollViewport\n        (scrolledIndexChange)=\"handleScrolledIndexChange($event, scrollViewport)\"\n        [itemSize]=\"itemSizePx\"\n        [ngStyle]=\"optMaxHeight !== 'auto' ? { 'height.px': optMaxHeight, 'maxHeight.px': optMaxHeight, 'minWidth.px': optMinWidth, overflowX: 'hidden' } : { 'minWidth.px': optMinWidth, overflowX: 'hidden'}\"\n        maxBufferPx=\"{{bufferPx}}\"\n        minBufferPx=\"{{bufferPx}}\"\n        class=\"form-multi-select-options\"\n        tabindex=\"-1\"\n      >\n        <ng-container\n          #cdkVirtualFor\n          *cdkVirtualFor=\"let option of optionsArray; trackBy: trackByFn; templateCacheSize: 0; even as even; odd as odd; index as index; count as count; first as first; last as last;\"\n          [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"\n          [ngTemplateOutlet]=\"templates['multiSelectOptionTemplate'] || defaultMultiSelectOptionTemplate\"\n        />\n      </cdk-virtual-scroll-viewport>\n    }\n  }\n</ng-template>\n\n<ng-template #multiselectOptionsDiv>\n  @let optMaxHeight = optionsMaxHeight();\n  <div [class.form-multi-select-options]=\"visibleOptions()\" [ngStyle]=\"optMaxHeight !== 'auto' ? { 'maxHeight.px': optMaxHeight, overflowY: 'scroll' } : {}\">\n    <ng-content />\n    <ng-container [ngTemplateOutlet]=\"userOptionsTemplate\" />\n  </div>\n</ng-template>\n\n<ng-template #defaultMultiSelectOptionTemplate let-option>\n  <c-multi-select-option\n    [disabled]=\"option.disabled\"\n    [label]=\"option.label\"\n    [text]=\"option.text\"\n    [value]=\"option.value\"\n    [visible]=\"option.visible\"\n  >\n    {{ option.text }}\n  </c-multi-select-option>\n</ng-template>\n\n<ng-template #userOptionsTemplate>\n  @for (option of userOptionsArray$ | async; track option; let even = $even, odd = $odd, index = $index, count = $count, first = $first, last = $last) {\n    <ng-container\n      [ngTemplateOutlet]=\"templates['multiSelectOptionTemplate'] || defaultMultiSelectOptionTemplate\"\n      [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"\n    />\n  }\n</ng-template>\n", styles: [":host .disabled{pointer-events:none}:host{display:block}:host:focus-visible{outline:none}:host.cdk-focused:not(.disabled) .form-multi-select{outline:none;--cui-form-multi-select-focus-box-shadow: 0 0 0 var(--cui-focus-ring-width) rgb(from var(--cui-form-multi-select-focus-border-color) r g b / var(--cui-focus-ring-opacity))}:host.cdk-focused:not(.disabled) .form-multi-select.is-valid{--cui-form-multi-select-focus-border-color: var(--cui-form-valid-border-color)}:host.cdk-focused:not(.disabled) .form-multi-select.is-invalid{--cui-form-multi-select-focus-border-color: var(--cui-form-invalid-border-color)}:host .form-multi-select .form-multi-select-selection{display:flex;flex:1 1 auto;flex-wrap:wrap}:host .form-multi-select .form-multi-select-selection .text-placeholder,:host .form-multi-select .form-multi-select-selection .form-multi-select-search::placeholder{color:var(--cui-form-multi-select-color);opacity:.8}:host .form-multi-select .form-multi-select-selection.form-control{border:none}:host .form-multi-select .form-multi-select-search[size]{display:flex}:host ::ng-deep .cdk-virtual-scroll-content-wrapper{padding:var(--cui-form-multi-select-options-padding-y) var(--cui-form-multi-select-options-padding-x);font-size:1rem;color:var(--cui-form-multi-select-options-color)}:host .cdk-virtual-scroll-viewport{width:var(--cui-dropdown-min-width)}:host .dropdown-menu{--cui-dropdown-padding-y: 0}:host-context(.input-group) :host:not(:first-child) .form-multi-select-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .form-multi-select-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .form-multi-select-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .form-multi-select-input-group{border-start-end-radius:var(--cui-form-multi-select-border-radius);border-end-end-radius:var(--cui-form-multi-select-border-radius)}\n"] }]
            }], ctorParameters: () => [], propDecorators: { allowCreateOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "allowCreateOptions", required: false }] }], cleaner: [{ type: i0.Input, args: [{ isSignal: true, alias: "cleaner", required: false }] }], clearSearchOnSelect: [{ type: i0.Input, args: [{ isSignal: true, alias: "clearSearchOnSelect", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], loading: [{ type: i0.Input, args: [{ isSignal: true, alias: "loading", required: false }] }], multiple: [{ type: i0.Input, args: [{ isSignal: true, alias: "multiple", required: false }] }], options: [{
                    type: Input
                }], optionsMaxHeightInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsMaxHeight", required: false }] }], optionsStyle: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsStyle", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], resetSelectionOnOptionsChange: [{ type: i0.Input, args: [{ isSignal: true, alias: "resetSelectionOnOptionsChange", required: false }] }], search: [{ type: i0.Input, args: [{ isSignal: true, alias: "search", required: false }] }], searchNoResultsLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "searchNoResultsLabel", required: false }] }], searchValue: [{
                    type: Input
                }], searchValueChange: [{ type: i0.Output, args: ["searchValueChange"] }], selectAll: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectAll", required: false }] }], selectAllLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectAllLabel", required: false }] }], selectionType: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectionType", required: false }] }], selectionTypeCounterText: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectionTypeCounterText", required: false }] }], selectionTypeCounterTextPluralMap: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectionTypeCounterTextPluralMap", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], value: [{
                    type: Input
                }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], virtualScroller: [{ type: i0.Input, args: [{ isSignal: true, alias: "virtualScroller", required: false }] }], visibleItemsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visibleItems", required: false }] }], itemSize: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemSize", required: false }] }], itemMinWidthInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemMinWidth", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], popperOptionsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "popperOptions", required: false }] }], multiSelectOptionsContent: [{
                    type: ContentChildren,
                    args: [MultiSelectOptionComponent, { descendants: true }]
                }], multiSelectOptionsView: [{
                    type: ViewChildren,
                    args: [MultiSelectOptionComponent]
                }], scrollViewportView: [{
                    type: ViewChildren,
                    args: ['scrollViewport']
                }], optionsElementRef: [{
                    type: ViewChild,
                    args: ['options']
                }], contentTemplates: [{
                    type: ContentChildren,
                    args: [TemplateIdDirective, { descendants: true }]
                }], inputElement: [{
                    type: ViewChild,
                    args: ['inputElement']
                }], dropdownMenu: [{
                    type: ViewChild,
                    args: [DropdownMenuDirective, { read: ElementRef }]
                }], dropdown: [{
                    type: ViewChild,
                    args: [DropdownComponent, { read: ElementRef }]
                }], dropdownToggle: [{
                    type: ViewChild,
                    args: [DropdownToggleDirective, { read: ElementRef }]
                }] } }) });

class IntersectionService {
    platformId = inject(PLATFORM_ID);
    #intersecting = new BehaviorSubject({ isIntersecting: false });
    intersecting$ = this.#intersecting.asObservable();
    defaultObserverOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.2
    };
    hostElementRefs = new Map();
    createIntersectionObserver(hostElement, observerOptions = this.defaultObserverOptions) {
        if (isPlatformServer(this.platformId)) {
            this.#intersecting.next({ isIntersecting: true, hostElement });
            return;
        }
        const options = { ...this.defaultObserverOptions, ...observerOptions };
        const handleIntersect = (entries, observer) => {
            entries.forEach((entry) => {
                this.#intersecting.next({ isIntersecting: entry.isIntersecting, hostElement });
            });
        };
        this.hostElementRefs.set(hostElement, new IntersectionObserver(handleIntersect, options));
        this.hostElementRefs.get(hostElement)?.observe(hostElement.nativeElement);
    }
    unobserve(elementRef) {
        this.hostElementRefs.get(elementRef)?.unobserve(elementRef.nativeElement);
        this.hostElementRefs.set(elementRef, null);
        this.hostElementRefs.delete(elementRef);
    }
    ngOnDestroy() {
        this.hostElementRefs.forEach((observer, elementRef) => {
            observer?.unobserve(elementRef.nativeElement);
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: IntersectionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: IntersectionService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: IntersectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ListenersService {
    renderer = inject(Renderer2);
    listeners = new Map();
    setListeners({ hostElement, trigger, callbackOn, callbackOff, callbackToggle, }) {
        const host = hostElement.nativeElement;
        const triggers = Array.isArray(trigger) ? trigger : trigger?.split(' ') ?? [];
        if (triggers?.includes('click')) {
            typeof callbackToggle === 'function' &&
                this.listeners.set('click', this.renderer.listen(host, 'click', callbackToggle));
        }
        if (triggers?.includes('focus')) {
            typeof callbackOn === 'function' &&
                this.listeners.set('focus', this.renderer.listen(host, 'focus', callbackOn));
        }
        if (triggers?.includes('focusin')) {
            typeof callbackOff === 'function' &&
                this.listeners.set('focusout', this.renderer.listen(host, 'focusout', callbackOff));
            typeof callbackOn === 'function' &&
                this.listeners.set('focusin', this.renderer.listen(host, 'focusin', callbackOn));
        }
        if (triggers?.includes('click') || triggers?.includes('focus')) {
            typeof callbackOff === 'function' &&
                this.listeners.set('blur', this.renderer.listen(host, 'blur', callbackOff));
        }
        if (triggers?.includes('hover')) {
            typeof callbackOn === 'function' &&
                this.listeners.set('mouseenter', this.renderer.listen(host, 'mouseenter', callbackOn));
            typeof callbackOff === 'function' &&
                this.listeners.set('mouseleave', this.renderer.listen(host, 'mouseleave', callbackOff));
        }
    }
    clearListeners() {
        this.listeners.forEach((unListen, key) => {
            unListen();
        });
        this.listeners.forEach((unListen, key) => {
            // @ts-ignore
            this.listeners.set(key, null);
        });
        this.listeners.clear();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListenersService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListenersService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListenersService, decorators: [{
            type: Injectable
        }] });

class ClassToggleService {
    #document = inject(DOCUMENT);
    #rendererFactory = inject(RendererFactory2);
    #renderer;
    constructor() {
        this.#renderer = this.#rendererFactory.createRenderer(null, null);
    }
    toggle(selector, className) {
        const element = this.#document.querySelector(selector);
        if (element) {
            element.classList.contains(className)
                ? this.#renderer.removeClass(element, className)
                : this.#renderer.addClass(element, className);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ClassToggleService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ClassToggleService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ClassToggleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class InMemoryStorageService {
    #storage = new Map();
    setItem(key, data) {
        this.#storage.set(key, JSON.stringify(data));
    }
    getItem(key) {
        return this.#storage.has(key) ? JSON.parse(this.#storage.get(key) ?? 'null') : undefined;
    }
    removeItem(key) {
        this.#storage.delete(key);
    }
    clear() {
        this.#storage.clear();
    }
    get length() {
        return this.#storage.size;
    }
    key(index) {
        return Array.from(this.#storage.keys())[index];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InMemoryStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InMemoryStorageService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InMemoryStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class LocalStorageService {
    platformId = inject(PLATFORM_ID);
    document = inject(DOCUMENT);
    constructor() {
        this.#localStorage =
            isPlatformBrowser(this.platformId) && this.document.defaultView
                ? this.document.defaultView?.localStorage
                : new InMemoryStorageService();
    }
    #localStorage;
    #data$ = new BehaviorSubject(null);
    data$ = this.#data$.asObservable();
    setItem(key, data) {
        this.#localStorage.setItem(key, JSON.stringify(data));
        this.#data$.next({ key, data });
    }
    getItem(key) {
        const data = JSON.parse(this.#localStorage.getItem(key) || 'null');
        this.#data$.next({ key, data });
        return data;
    }
    removeItem(key) {
        this.#localStorage.removeItem(key);
        this.#data$.next({ key, data: null });
    }
    clear() {
        this.#localStorage.clear();
        this.#data$.next(null);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LocalStorageService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LocalStorageService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class ColorModeService {
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #localStorage = inject(LocalStorageService);
    eventName = signal('ColorSchemeChange', ...(ngDevMode ? [{ debugName: "eventName" }] : []));
    localStorageItemName = signal(undefined, ...(ngDevMode ? [{ debugName: "localStorageItemName" }] : []));
    localStorageItemName$ = toObservable(this.localStorageItemName);
    colorMode = signal(undefined, ...(ngDevMode ? [{ debugName: "colorMode" }] : []));
    #colorModeEffect = effect(() => {
        const colorMode = this.colorMode();
        if (colorMode) {
            const localStorageItemName = this.localStorageItemName();
            localStorageItemName && this.setStoredTheme(localStorageItemName, colorMode);
            this.#setTheme(colorMode);
        }
    }, ...(ngDevMode ? [{ debugName: "#colorModeEffect" }] : []));
    constructor() {
        afterNextRender({
            read: () => {
                this.localStorageItemName$
                    .pipe(tap((params) => {
                    this.colorMode.set(this.getDefaultScheme(params));
                }), takeUntilDestroyed(this.#destroyRef))
                    .subscribe();
            }
        });
    }
    getStoredTheme(localStorageItemName) {
        return this.#localStorage.getItem(localStorageItemName);
    }
    setStoredTheme(localStorageItemName, colorMode) {
        return this.#localStorage.setItem(localStorageItemName, colorMode);
    }
    removeStoredTheme(localStorageItemName) {
        this.#localStorage.removeItem(localStorageItemName);
    }
    getDefaultScheme(localStorageItemName) {
        if (this.#document.defaultView === undefined) {
            return this.getDatasetTheme();
        }
        const storedTheme = localStorageItemName && this.getStoredTheme(localStorageItemName);
        return storedTheme ?? this.getDatasetTheme();
    }
    getPrefersColorScheme() {
        return this.#document.defaultView?.matchMedia('(prefers-color-scheme: dark)').matches
            ? 'dark'
            : this.#document.defaultView?.matchMedia('(prefers-color-scheme: light)').matches
                ? 'light'
                : undefined;
    }
    getDatasetTheme() {
        return this.#document.documentElement.dataset['coreuiTheme'];
    }
    #setTheme(colorMode) {
        this.#document.documentElement.dataset['coreuiTheme'] =
            colorMode === 'auto' ? this.getPrefersColorScheme() : colorMode;
        const event = new Event(this.eventName());
        this.#document.documentElement.dispatchEvent(event);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColorModeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColorModeService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColorModeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class UIDService {
    #document = inject(DOCUMENT);
    getUID(prefix = 'random-id') {
        let uid = prefix;
        do {
            uid = `${prefix}-${Math.floor(Math.random() * 1000000).toString(10)}`;
        } while (this.#document.getElementById(uid));
        return uid;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UIDService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UIDService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UIDService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class RtlService {
    #document = inject(DOCUMENT);
    isRTL(element) {
        if (element) {
            return (element.closest('[dir="rtl"]') !== null ||
                this.#document.defaultView?.getComputedStyle(element).direction === 'rtl');
        }
        return [this.#document?.documentElement?.dir, this.#document?.body?.dir].includes('rtl');
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RtlService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RtlService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RtlService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class AutocompleteOptionComponent {
    static ngAcceptInputType_disabled;
    #document = inject(DOCUMENT);
    #elementRef = inject(ElementRef);
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), alias: 'disabled', transform: booleanAttribute });
    #disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "#disabled" }] : []));
    #disabledEffect = effect(() => {
        this.#elementRef.nativeElement.disabled = this.#disabled();
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    option = input.required(...(ngDevMode ? [{ debugName: "option" }] : []));
    selected = input(false, ...(ngDevMode ? [{ debugName: "selected" }] : []));
    focusable = signal(false, ...(ngDevMode ? [{ debugName: "focusable" }] : []));
    #focusableEffect = effect(() => {
        this.#elementRef.nativeElement.tabIndex = this.focusable() ? 0 : -1;
    }, ...(ngDevMode ? [{ debugName: "#focusableEffect" }] : []));
    hostClasses = computed(() => {
        const option = this.option();
        const selected = this.selected();
        return {
            'autocomplete-option': true,
            disabled: isOptionDisabled(option),
            selected: selected
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    set disabled(value) {
        this.#disabled.set(value);
    }
    get disabled() {
        return this.#elementRef.nativeElement.disabled;
    }
    focus(origin) {
        if (!this.disabled && this.#document.activeElement !== this.#elementRef.nativeElement) {
            this.#elementRef.nativeElement.focus();
        }
    }
    getLabel() {
        return this.#elementRef.nativeElement.innerText ?? this.option()?.label ?? '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteOptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: AutocompleteOptionComponent, isStandalone: true, selector: "c-autocomplete-option", inputs: { disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, option: { classPropertyName: "option", publicName: "option", isSignal: true, isRequired: true, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null } }, host: { attributes: { "tabindex": "-1" }, properties: { "class": "hostClasses()", "attr.role": "\"option\"" }, classAttribute: "autocomplete-option" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block;-webkit-user-select:none;user-select:none;white-space:nowrap;overflow:hidden}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteOptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-autocomplete-option', imports: [], template: '<ng-content />', host: {
                        class: 'autocomplete-option',
                        '[class]': 'hostClasses()',
                        '[attr.role]': '"option"',
                        tabindex: '-1'
                    }, styles: [":host{display:block;-webkit-user-select:none;user-select:none;white-space:nowrap;overflow:hidden}\n"] }]
        }], propDecorators: { disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], option: [{ type: i0.Input, args: [{ isSignal: true, alias: "option", required: true }] }], selected: [{ type: i0.Input, args: [{ isSignal: true, alias: "selected", required: false }] }] } });

class AutocompleteService {
    autocompleteDirective = signal(undefined, ...(ngDevMode ? [{ debugName: "autocompleteDirective" }] : []));
    hint = signal(undefined, ...(ngDevMode ? [{ debugName: "hint" }] : []));
    searchValue = signal(undefined, ...(ngDevMode ? [{ debugName: "searchValue" }] : []));
    selected = signal(undefined, ...(ngDevMode ? [{ debugName: "selected" }] : []));
    visible = signal(false, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    subtreeFocused = signal(undefined, ...(ngDevMode ? [{ debugName: "subtreeFocused" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteService, decorators: [{
            type: Injectable
        }] });

class AutocompleteOptionsComponent {
    static ngAcceptInputType_highlightOptionsOnSearch;
    static ngAcceptInputType_loading;
    static ngAcceptInputType_visibleItems;
    isOptionSelected = isOptionSelected;
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    #focusMonitor = inject(FocusMonitor);
    #sanitizer = inject(DomSanitizer);
    #autocompleteService = inject(AutocompleteService);
    delay = input(300, ...(ngDevMode ? [{ debugName: "delay" }] : []));
    highlightOptionsOnSearch = input(...(ngDevMode ? [undefined, { debugName: "highlightOptionsOnSearch" }] : []));
    loading = input(false, { ...(ngDevMode ? { debugName: "loading" } : {}), transform: booleanAttribute });
    itemSize = input(40, { ...(ngDevMode ? { debugName: "itemSize" } : {}), transform: numberAttribute });
    options = input.required(...(ngDevMode ? [{ debugName: "options" }] : []));
    optionsMaxHeight = input(...(ngDevMode ? [undefined, { debugName: "optionsMaxHeight" }] : []));
    optionTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionTemplate" }] : []));
    optionGroupTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionGroupTemplate" }] : []));
    search = input(...(ngDevMode ? [undefined, { debugName: "search" }] : []));
    searchNoResultsLabel = input(...(ngDevMode ? [undefined, { debugName: "searchNoResultsLabel" }] : []));
    searchValue = input(...(ngDevMode ? [undefined, { debugName: "searchValue" }] : []));
    selected = input(null, ...(ngDevMode ? [{ debugName: "selected" }] : []));
    virtualScroller = input(...(ngDevMode ? [undefined, { debugName: "virtualScroller" }] : []));
    visibleItems = input(10, { ...(ngDevMode ? { debugName: "visibleItems" } : {}), transform: numberAttribute });
    optionClick = output();
    hostStyles = computed(() => {
        const maxHeight = this.optionsMaxHeight();
        const suffix = typeof maxHeight === 'number' ? 'px' : '';
        return maxHeight !== 'auto' ? { maxHeight: `${maxHeight}${suffix}`, overflowY: 'scroll' } : {};
    }, ...(ngDevMode ? [{ debugName: "hostStyles" }] : []));
    ngOnInit() {
        this.#focusMonitor
            .monitor(this.#elementRef, false)
            .pipe(debounceTime(this.delay()), takeUntilDestroyed(this.#destroyRef))
            .subscribe((value) => {
            if (value === 'keyboard') {
                this.focusKeyManager?.setFirstItemActive();
            }
        });
        this.#destroyRef.onDestroy(() => {
            this.#focusMonitor.stopMonitoring(this.#elementRef);
            this.#focusKeyChange$?.unsubscribe();
            this.focusKeyManager?.destroy();
        });
    }
    focusKeyManager;
    #focusKeyChange$;
    autocompleteOptions = viewChildren(AutocompleteOptionComponent, { ...(ngDevMode ? { debugName: "autocompleteOptions" } : {}), read: AutocompleteOptionComponent });
    #optionsEffect = effect(() => {
        const options = this.autocompleteOptions();
        if (this.focusKeyManager) {
            this.#focusKeyChange$.unsubscribe();
            this.focusKeyManager?.destroy();
        }
        this.focusKeyManager = new FocusKeyManager(options)
            .withPageUpDown()
            .withHomeAndEnd()
            .skipPredicate((option) => option.disabled)
            .withVerticalOrientation(true)
            .withHorizontalOrientation(null);
        untracked(() => {
            if (!this.virtualScroller() && !isGlobalSearch(this.search())) {
                this.focusKeyManager.withTypeAhead(this.delay());
            }
        });
        this.#focusKeyChange$ = this.focusKeyManager.change
            .pipe(distinctUntilChanged(), tap((optionIdx) => {
            if (!this.searchValue()) {
                const option = options[optionIdx];
                this.#autocompleteService.hint.set(option.option());
            }
            this.updateFocusableItems();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }, ...(ngDevMode ? [{ debugName: "#optionsEffect" }] : []));
    updateFocusableItems() {
        const options = this.autocompleteOptions();
        options
            .filter((option) => option.focusable())
            .forEach((option) => {
            option.focusable.set(false);
        });
        options.find((option) => this.focusKeyManager.activeItem === option)?.focusable.set(true);
    }
    handleOptionClick($event, option, idx) {
        this.#handleSelect(option, idx);
    }
    handleOptionKeyUp($event, option, idx) {
        if (['Enter', 'Space'].includes($event.code)) {
            $event.stopImmediatePropagation();
            $event.preventDefault();
            this.#handleSelect(option, idx);
        }
    }
    #handleSelect = (option, idx) => {
        this.#autocompleteService.selected.set(option);
        this.focusKeyManager?.setActiveItem(idx);
        this.#autocompleteService.visible.set(false);
    };
    handleOptionKeyDown($event, option) {
        if (['Space'].includes($event.code)) {
            $event.preventDefault();
        }
        if (['ArrowUp', 'ArrowDown', 'Home', 'End'].includes($event.key)) {
            this.focusKeyManager?.onKeydown($event);
            return;
        }
        if (['Tab'].includes($event.key)) {
            $event.preventDefault();
            this.#autocompleteService.visible.set(false);
            this.focusKeyManager?.tabOut.next();
        }
        this.focusKeyManager?.onKeydown($event);
    }
    // protected handleFocusOption($event: FocusEvent, option: IAutocompleteOption, idx: number) {
    //   this.focusKeyManager?.updateActiveItem(idx);
    //   this.focusKeyManager?.setActiveItem(idx);
    // }
    // protected handleScrolledIndexChange(scrolledIndex: number, scrollViewport: CdkVirtualScrollViewport) {
    //   console.log('scrolledIndex', scrolledIndex);
    // }
    // protected trackByFn = (index: number, option: IAutocompleteOption) => option.label;
    optionLabel(option) {
        const searchValue = this.searchValue() || '';
        if (this.highlightOptionsOnSearch() && searchValue) {
            const label = highlightSubstring(option.label, searchValue);
            return this.#sanitizer.bypassSecurityTrustHtml(label);
        }
        return option.label;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteOptionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: AutocompleteOptionsComponent, isStandalone: true, selector: "c-autocomplete-options", inputs: { delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, highlightOptionsOnSearch: { classPropertyName: "highlightOptionsOnSearch", publicName: "highlightOptionsOnSearch", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, itemSize: { classPropertyName: "itemSize", publicName: "itemSize", isSignal: true, isRequired: false, transformFunction: null }, options: { classPropertyName: "options", publicName: "options", isSignal: true, isRequired: true, transformFunction: null }, optionsMaxHeight: { classPropertyName: "optionsMaxHeight", publicName: "optionsMaxHeight", isSignal: true, isRequired: false, transformFunction: null }, optionTemplate: { classPropertyName: "optionTemplate", publicName: "optionTemplate", isSignal: true, isRequired: false, transformFunction: null }, optionGroupTemplate: { classPropertyName: "optionGroupTemplate", publicName: "optionGroupTemplate", isSignal: true, isRequired: false, transformFunction: null }, search: { classPropertyName: "search", publicName: "search", isSignal: true, isRequired: false, transformFunction: null }, searchNoResultsLabel: { classPropertyName: "searchNoResultsLabel", publicName: "searchNoResultsLabel", isSignal: true, isRequired: false, transformFunction: null }, searchValue: { classPropertyName: "searchValue", publicName: "searchValue", isSignal: true, isRequired: false, transformFunction: null }, selected: { classPropertyName: "selected", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null }, virtualScroller: { classPropertyName: "virtualScroller", publicName: "virtualScroller", isSignal: true, isRequired: false, transformFunction: null }, visibleItems: { classPropertyName: "visibleItems", publicName: "visibleItems", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { optionClick: "optionClick" }, host: { properties: { "style": "hostStyles()", "attr.role": "\"listbox\"" }, classAttribute: "autocomplete-options" }, viewQueries: [{ propertyName: "autocompleteOptions", predicate: AutocompleteOptionComponent, descendants: true, read: AutocompleteOptionComponent, isSignal: true }], ngImport: i0, template: "<!--@let vScroller = virtualScroller();-->\n@let optionsArray = options() || [];\n@let selectedOption = selected();\n\n<!--<ng-container *ngTemplateOutlet=\"vScroller ? autocompleteVirtualScroller : autocompleteOptionsTemplate\" />-->\n<ng-container *ngTemplateOutlet=\"autocompleteOptionsTemplate\" />\n\n<ng-template #autocompleteOptionsTemplate>\n  @for (option of optionsArray; track idx; let idx = $index) {\n    @if ('options' in option) {\n      <div class=\"autocomplete-optgroup-label\">\n        <ng-container *ngTemplateOutlet=\"optionGroupTemplate() || defaultGroupTemplate; context: { $implicit: option, idx: idx }\" />\n      </div>\n      @for (opt of option.options; track oIdx; let oIdx = $index) {\n        <ng-container *ngTemplateOutlet=\"defaultAutocompleteOptionTemplate; context: { $implicit: opt, idx: oIdx }\" />\n      }\n    } @else {\n      <ng-container *ngTemplateOutlet=\"defaultAutocompleteOptionTemplate; context: { $implicit: option, idx }\" />\n    }\n  }\n</ng-template>\n\n<ng-template #defaultAutocompleteOptionTemplate let-idx=\"idx\" let-option>\n  @let isSelected = isOptionSelected(option, selectedOption);\n  <c-autocomplete-option\n    #optionRef\n    (click)=\"handleOptionClick($event, option, idx)\"\n    (keydown)=\"handleOptionKeyDown($event, option)\"\n    (keyup)=\"handleOptionKeyUp($event, option, idx)\"\n    [disabled]=\"option.disabled || null\"\n    [option]=\"option\"\n    [selected]=\"isSelected\"\n  >\n    <ng-container *ngTemplateOutlet=\"optionTemplate() || defaultOptionTemplate; context: { $implicit: option, selected: isSelected, idx: idx }\" />\n  </c-autocomplete-option>\n  <!--  [tabIndex]=\"isSelected || optionRef.focusable() ? '0' : '-1'\"-->\n  <!--  (focus)=\"handleFocusOption($event, option, idx)\"-->\n\n</ng-template>\n\n<ng-template #defaultGroupTemplate let-idx=\"idx\" let-option>\n  {{ option.label }}\n</ng-template>\n\n<ng-template #defaultOptionTemplate let-idx=\"idx\" let-option>\n  <div [innerHTML]=\"optionLabel(option)\"></div>\n</ng-template>\n\n@if (false) {\n  <!--  todo: virtual scroll-->\n    <!--<ng-template #autocompleteVirtualScroller>-->\n    <!--  @let itemSizePx = itemSize();-->\n    <!--  @let bufferPx = itemSizePx * visibleItems();-->\n    <!--  @defer (on immediate) {-->\n    <!--    @if (optionsArray) {-->\n    <!--      @let optMaxHeight = optionsMaxHeight();-->\n    <!--      @let optMinWidth = 196;-->\n    <!--&lt;!&ndash;      @let optMinWidth = minWidth();&ndash;&gt;-->\n\n    <!--      <cdk-virtual-scroll-viewport-->\n    <!--        #scrollViewport-->\n    <!--            (scrolledIndexChange)=\"handleScrolledIndexChange($event, scrollViewport)\"-->\n    <!--        [itemSize]=\"itemSizePx\"-->\n    <!--        [style]=\"optMaxHeight !== 'auto' ? { 'height.px': 400, 'maxHeight.px': optMaxHeight, 'minWidth.px': optMinWidth, overflowX: 'hidden' } : { 'minWidth.px': optMinWidth, overflowX: 'hidden'}\"-->\n    <!--        maxBufferPx=\"{{bufferPx}}\"-->\n    <!--        minBufferPx=\"{{bufferPx}}\"-->\n    <!--        class=\"autocomplete-options\"-->\n    <!--        tabindex=\"-1\"-->\n    <!--      >-->\n    <!--        <ng-container-->\n    <!--          #cdkVirtualFor-->\n    <!--          *cdkVirtualFor=\"let option of optionsArray; trackBy: trackByFn; templateCacheSize: 0; even as even; odd as odd; index as index; count as count; first as first; last as last;\"-->\n    <!--          [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"-->\n    <!--          [ngTemplateOutlet]=\"defaultAutocompleteOptionTemplate\"-->\n    <!--        />-->\n    <!--      </cdk-virtual-scroll-viewport>-->\n    <!--    }-->\n    <!--  }-->\n    <!--</ng-template>-->\n}\n", styles: [":host{display:block}\n"], dependencies: [{ kind: "component", type: AutocompleteOptionComponent, selector: "c-autocomplete-option", inputs: ["disabled", "option", "selected"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteOptionsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-autocomplete-options', imports: [AutocompleteOptionComponent, NgTemplateOutlet], host: {
                        class: 'autocomplete-options',
                        '[style]': 'hostStyles()',
                        '[attr.role]': '"listbox"'
                    }, template: "<!--@let vScroller = virtualScroller();-->\n@let optionsArray = options() || [];\n@let selectedOption = selected();\n\n<!--<ng-container *ngTemplateOutlet=\"vScroller ? autocompleteVirtualScroller : autocompleteOptionsTemplate\" />-->\n<ng-container *ngTemplateOutlet=\"autocompleteOptionsTemplate\" />\n\n<ng-template #autocompleteOptionsTemplate>\n  @for (option of optionsArray; track idx; let idx = $index) {\n    @if ('options' in option) {\n      <div class=\"autocomplete-optgroup-label\">\n        <ng-container *ngTemplateOutlet=\"optionGroupTemplate() || defaultGroupTemplate; context: { $implicit: option, idx: idx }\" />\n      </div>\n      @for (opt of option.options; track oIdx; let oIdx = $index) {\n        <ng-container *ngTemplateOutlet=\"defaultAutocompleteOptionTemplate; context: { $implicit: opt, idx: oIdx }\" />\n      }\n    } @else {\n      <ng-container *ngTemplateOutlet=\"defaultAutocompleteOptionTemplate; context: { $implicit: option, idx }\" />\n    }\n  }\n</ng-template>\n\n<ng-template #defaultAutocompleteOptionTemplate let-idx=\"idx\" let-option>\n  @let isSelected = isOptionSelected(option, selectedOption);\n  <c-autocomplete-option\n    #optionRef\n    (click)=\"handleOptionClick($event, option, idx)\"\n    (keydown)=\"handleOptionKeyDown($event, option)\"\n    (keyup)=\"handleOptionKeyUp($event, option, idx)\"\n    [disabled]=\"option.disabled || null\"\n    [option]=\"option\"\n    [selected]=\"isSelected\"\n  >\n    <ng-container *ngTemplateOutlet=\"optionTemplate() || defaultOptionTemplate; context: { $implicit: option, selected: isSelected, idx: idx }\" />\n  </c-autocomplete-option>\n  <!--  [tabIndex]=\"isSelected || optionRef.focusable() ? '0' : '-1'\"-->\n  <!--  (focus)=\"handleFocusOption($event, option, idx)\"-->\n\n</ng-template>\n\n<ng-template #defaultGroupTemplate let-idx=\"idx\" let-option>\n  {{ option.label }}\n</ng-template>\n\n<ng-template #defaultOptionTemplate let-idx=\"idx\" let-option>\n  <div [innerHTML]=\"optionLabel(option)\"></div>\n</ng-template>\n\n@if (false) {\n  <!--  todo: virtual scroll-->\n    <!--<ng-template #autocompleteVirtualScroller>-->\n    <!--  @let itemSizePx = itemSize();-->\n    <!--  @let bufferPx = itemSizePx * visibleItems();-->\n    <!--  @defer (on immediate) {-->\n    <!--    @if (optionsArray) {-->\n    <!--      @let optMaxHeight = optionsMaxHeight();-->\n    <!--      @let optMinWidth = 196;-->\n    <!--&lt;!&ndash;      @let optMinWidth = minWidth();&ndash;&gt;-->\n\n    <!--      <cdk-virtual-scroll-viewport-->\n    <!--        #scrollViewport-->\n    <!--            (scrolledIndexChange)=\"handleScrolledIndexChange($event, scrollViewport)\"-->\n    <!--        [itemSize]=\"itemSizePx\"-->\n    <!--        [style]=\"optMaxHeight !== 'auto' ? { 'height.px': 400, 'maxHeight.px': optMaxHeight, 'minWidth.px': optMinWidth, overflowX: 'hidden' } : { 'minWidth.px': optMinWidth, overflowX: 'hidden'}\"-->\n    <!--        maxBufferPx=\"{{bufferPx}}\"-->\n    <!--        minBufferPx=\"{{bufferPx}}\"-->\n    <!--        class=\"autocomplete-options\"-->\n    <!--        tabindex=\"-1\"-->\n    <!--      >-->\n    <!--        <ng-container-->\n    <!--          #cdkVirtualFor-->\n    <!--          *cdkVirtualFor=\"let option of optionsArray; trackBy: trackByFn; templateCacheSize: 0; even as even; odd as odd; index as index; count as count; first as first; last as last;\"-->\n    <!--          [ngTemplateOutletContext]=\"{$implicit: option, even, odd, index, count, first, last}\"-->\n    <!--          [ngTemplateOutlet]=\"defaultAutocompleteOptionTemplate\"-->\n    <!--        />-->\n    <!--      </cdk-virtual-scroll-viewport>-->\n    <!--    }-->\n    <!--  }-->\n    <!--</ng-template>-->\n}\n", styles: [":host{display:block}\n"] }]
        }], propDecorators: { delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], highlightOptionsOnSearch: [{ type: i0.Input, args: [{ isSignal: true, alias: "highlightOptionsOnSearch", required: false }] }], loading: [{ type: i0.Input, args: [{ isSignal: true, alias: "loading", required: false }] }], itemSize: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemSize", required: false }] }], options: [{ type: i0.Input, args: [{ isSignal: true, alias: "options", required: true }] }], optionsMaxHeight: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsMaxHeight", required: false }] }], optionTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionTemplate", required: false }] }], optionGroupTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionGroupTemplate", required: false }] }], search: [{ type: i0.Input, args: [{ isSignal: true, alias: "search", required: false }] }], searchNoResultsLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "searchNoResultsLabel", required: false }] }], searchValue: [{ type: i0.Input, args: [{ isSignal: true, alias: "searchValue", required: false }] }], selected: [{ type: i0.Input, args: [{ isSignal: true, alias: "selected", required: false }] }], virtualScroller: [{ type: i0.Input, args: [{ isSignal: true, alias: "virtualScroller", required: false }] }], visibleItems: [{ type: i0.Input, args: [{ isSignal: true, alias: "visibleItems", required: false }] }], optionClick: [{ type: i0.Output, args: ["optionClick"] }], autocompleteOptions: [{ type: i0.ViewChildren, args: [i0.forwardRef(() => AutocompleteOptionComponent), { ...{ read: AutocompleteOptionComponent }, isSignal: true }] }] } });

class AutocompleteComponent {
    static ngAcceptInputType_allowOnlyDefinedOptions;
    static ngAcceptInputType_cleaner;
    static ngAcceptInputType_clearSearchOnSelect;
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_highlightOptionsOnSearch;
    static ngAcceptInputType_indicator;
    static ngAcceptInputType_loading;
    static ngAcceptInputType_readOnly;
    // static ngAcceptInputType_resetSelectionOnOptionsChange: BooleanInput;
    static ngAcceptInputType_showHints;
    static ngAcceptInputType_virtualScroller;
    static ngAcceptInputType_visible;
    static ngAcceptInputType_visibleItems;
    isExternalSearch = isExternalSearch;
    #document = inject(DOCUMENT);
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    #focusMonitor = inject(FocusMonitor);
    autocompleteService = inject(AutocompleteService);
    dropdownMenuRef = viewChild('dropdownMenuRef', { ...(ngDevMode ? { debugName: "dropdownMenuRef" } : {}), read: ElementRef });
    togglerRef = viewChild('dropdownToggleRef', { ...(ngDevMode ? { debugName: "togglerRef" } : {}), read: ElementRef });
    cleanerRef = viewChild('cleanerRef', { ...(ngDevMode ? { debugName: "cleanerRef" } : {}), read: ElementRef });
    indicatorRef = viewChild('indicatorRef', { ...(ngDevMode ? { debugName: "indicatorRef" } : {}), read: ElementRef });
    optionsRef = viewChild(AutocompleteOptionsComponent, ...(ngDevMode ? [{ debugName: "optionsRef" }] : []));
    #optionsRefEffect = effect(() => {
        const optionsRef = this.optionsRef();
        if (!optionsRef && this.#elementRef.nativeElement.contains(this.#document.activeElement)) {
            this.inputPortalContent().nativeElement.focus();
        }
    }, ...(ngDevMode ? [{ debugName: "#optionsRefEffect" }] : []));
    domTogglerPortal = signal(null, ...(ngDevMode ? [{ debugName: "domTogglerPortal" }] : []));
    domDropdownMenuPortal = signal(null, ...(ngDevMode ? [{ debugName: "domDropdownMenuPortal" }] : []));
    domInputPortal = signal(null, ...(ngDevMode ? [{ debugName: "domInputPortal" }] : []));
    inputPortalContent = input.required(...(ngDevMode ? [{ debugName: "inputPortalContent" }] : []));
    ngOnDestroy() {
        this.setFocusMonitor(false);
    }
    ngAfterViewInit() {
        const dropdownMenuElement = this.dropdownMenuRef()?.nativeElement;
        this.domDropdownMenuPortal.set(new DomPortal(dropdownMenuElement));
        const togglerElement = this.togglerRef()?.nativeElement;
        this.domTogglerPortal.set(new DomPortal(togglerElement));
        const inputPortalContent = this.inputPortalContent().nativeElement;
        this.domInputPortal.set(new DomPortal(inputPortalContent));
        this.setFocusMonitor(true);
        this.#destroyRef.onDestroy(() => {
            this.#focusMonitor?.stopMonitoring(this.#elementRef);
        });
    }
    setFocusMonitor(monitor) {
        if (monitor && !this.disabled()) {
            this.#focusMonitor
                ?.monitor(this.#elementRef, true)
                .pipe(tap((origin) => {
                this.indicatorRef()?.nativeElement.setAttribute('tabindex', origin ? '0' : '-1');
                this.cleanerRef()?.nativeElement.setAttribute('tabindex', origin ? '0' : '-1');
            }), 
            // to avoid focus lost on speed scrolling with virtualScroll
            debounceTime(100), tap((origin) => {
                this.autocompleteService.subtreeFocused.set(origin);
            }), takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        }
        else {
            this.#focusMonitor?.stopMonitoring(this.#elementRef);
        }
    }
    /**
     * Only allow selection of predefined options.
     * When `true`, users cannot enter custom values that are not in the options list.
     * When `false`, users can enter and select custom values.
     *
     * @default false
     */
    allowOnlyDefinedOptions = input(false, { ...(ngDevMode ? { debugName: "allowOnlyDefinedOptions" } : {}), transform: booleanAttribute });
    /**
     * Enables selection cleaner element.
     * When `true`, displays a clear button that allows users to reset the selection.
     * The cleaner button is only shown when there is a selection and the component is not disabled or read-only.
     *
     * @default false
     */
    cleaner = input(false, { ...(ngDevMode ? { debugName: "cleaner" } : {}), transform: booleanAttribute });
    /**
     * Whether to clear the internal search state after selecting an option.
     *
     * When set to `true`, the internal search value used for filtering options is cleared
     * after a selection is made. This affects only the component's internal logic.
     *
     * Note: This does **not** clear the visible input field if the component is using external search
     * or is controlled via the `searchValue` prop. In such cases, clearing must be handled externally.
     *
     * @default true
     */
    clearSearchOnSelect = input(false, { ...(ngDevMode ? { debugName: "clearSearchOnSelect" } : {}), transform: booleanAttribute });
    /**
     * Debounce delay in milliseconds for filtering options based on search input.
     * Controls how quickly the options list updates as the user types.
     * Higher values reduce update frequency for better performance with large datasets.
     * @default 150
     */
    delay = input(150, { ...(ngDevMode ? { debugName: "delay" } : {}), transform: numberAttribute });
    /**
     * Toggle the disabled state for the component.
     * When `true`, the Angular autocomplete is non-interactive and appears visually disabled.
     * Users cannot type, select options, or trigger the dropdown.
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    disabled = linkedSignal({ ...(ngDevMode ? { debugName: "disabled" } : {}), source: this.disabledInput,
        computation: (isDisabled) => {
            this.#elementRef.nativeElement.disabled = isDisabled;
            return isDisabled || null;
        } });
    /**
     * Highlight options that match the search criteria.
     * When `true`, matching portions of option labels are visually highlighted
     * based on the current search input value.
     *
     * @default false
     */
    highlightOptionsOnSearch = input(false, { ...(ngDevMode ? { debugName: "highlightOptionsOnSearch" } : {}), transform: booleanAttribute });
    /**
     * Show dropdown indicator/arrow button.
     * When `true`, displays a dropdown arrow button that can be clicked
     * to manually show or hide options dropdown.
     */
    indicator = input(false, { ...(ngDevMode ? { debugName: "indicator" } : {}), transform: booleanAttribute });
    /**
     * The size of the option item in the list (in pixels).
     * @return number
     * @default 40
     */
    itemSize = input(40, { ...(ngDevMode ? { debugName: "itemSize" } : {}), transform: numberAttribute });
    /**
     * When set, the options list will have a loading style: loading spinner and reduced opacity.
     * Use this to indicate that options are being fetched asynchronously.
     * The dropdown remains functional but shows visual loading indicators.
     */
    loading = input(false, { ...(ngDevMode ? { debugName: "loading" } : {}), transform: booleanAttribute });
    /**
     * List of option elements.
     * Can contain Option objects, OptionsGroup objects, or plain strings.
     * Plain strings are converted to simple Option objects internally.
     * This is a required prop - the Angular autocomplete needs options to function.
     */
    options = input.required(...(ngDevMode ? [{ debugName: "options" }] : []));
    /**
     * Sets maxHeight of options list.
     * Controls the maximum height of the dropdown options container.
     * Can be a number (pixels) or a CSS length string (e.g., '200px', '10rem').
     * When content exceeds this height, a scrollbar will appear.
     * @default 'auto'
     */
    optionsMaxHeight = input('auto', ...(ngDevMode ? [{ debugName: "optionsMaxHeight" }] : []));
    /**
     * Custom template for rendering individual options.
     * Allows complete customization of how each option appears in the dropdown.
     */
    optionTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionTemplate" }] : []));
    /**
     * Custom template for rendering option groups.
     * Allows customization of how option group headers appear in the dropdown.
     */
    optionGroupTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionGroupTemplate" }] : []));
    popperOptions = input.required(...(ngDevMode ? [{ debugName: "popperOptions" }] : []));
    /**
     * Specifies a short hint that is visible in the search input.
     * Displayed when the input is empty to guide user interaction.
     * Standard HTML input placeholder behavior.
     */
    // readonly placeholder = input<string>();
    /**
     * Toggle the readonly state for the component.
     * When `true`, users can view and interact with the dropdown but cannot
     * type in the search input or modify the selection through typing.
     * Selection via clicking options may still be possible.
     */
    readOnly = input(false, { ...(ngDevMode ? { debugName: "readOnly" } : {}), transform: booleanAttribute });
    /**
     * Determines whether the selected options should be cleared when the options list is updated.
     * When `true`, any previously selected options will be reset whenever the options
     * list undergoes a change. This ensures that outdated selections are not retained
     * when new options are provided.
     *
     * @default false
     */
    // readonly resetSelectionOnOptionsChange = input(false, { transform: booleanAttribute });
    /**
     * Enables and configures search functionality.
     * - `'external'`: Search is handled externally, filtering is not applied internally
     * - `'global'`: Enables global keyboard search when dropdown is closed
     * - Object with `external` and `global` boolean properties for fine-grained control
     */
    search = input(...(ngDevMode ? [undefined, { debugName: "search" }] : []));
    /**
     * Sets the label for no results when filtering.
     * - `false`: Don't show any message when no results found
     * - `true`: Show default "No results found" message
     * - `string`: Show custom text message
     * - `TemplateRef`: Show custom component/element
     * @default false
     */
    searchNoResultsLabel = input(false, ...(ngDevMode ? [{ debugName: "searchNoResultsLabel" }] : []));
    /**
     * Show hint options based on the current input value.
     * When `true`, displays a preview/hint of the first matching option
     * as semi-transparent text in the input field, similar to browser autocomplete.
     *
     * @default false
     */
    showHints = input(false, { ...(ngDevMode ? { debugName: "showHints" } : {}), transform: booleanAttribute });
    /**
     * Size the component small or large.
     * - `'sm'`: Small size variant
     * - `'lg'`: Large size variant
     * - `undefined`: Default/medium size
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Set form input validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Sets the initially selected value for the Angular autocomplete component.
     * Can be a string (matched against option labels) or number (matched against option values).
     * The component will attempt to find and select the matching option on mount.
     */
    // readonly valueInput = input<number | string>(undefined, { alias: 'value' });
    // readonly value = linkedSignal(this.valueInput);
    /**
     * Enable virtual scroller for the options list.
     * When `true`, only visible options are rendered in the DOM for better performance
     * with large option lists. Works in conjunction with `visibleItems` prop.
     *
     * @default false
     */
    virtualScroller = input(false, { ...(ngDevMode ? { debugName: "virtualScroller" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of autocomplete dropdown.
     * Controls whether the dropdown is initially visible.
     * The dropdown visibility can still be toggled through user interaction.
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    #visibleEffect = effect(() => {
        this.visible.set(this.autocompleteService.visible());
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    show = computed(() => {
        return this.visible() && !!this.options().length;
    }, ...(ngDevMode ? [{ debugName: "show" }] : []));
    /**
     * Amount of visible items when virtualScroller is enabled.
     * Determines how many option items are rendered at once when virtual scrolling is active.
     * Higher values show more items but use more memory. Lower values improve performance.
     * @default 8
     */
    visibleItems = input(8, { ...(ngDevMode ? { debugName: "visibleItems" } : {}), transform: numberAttribute });
    /**
     * Emits an event when the filter/search value changes.
     * Called whenever the user types in the search input.
     * Useful for implementing external search functionality or analytics.
     *
     * @param value - The current search input value
     */
    inputChange = output();
    /**
     * Emits an event when a user changes the selected option.
     * Called with the selected option object or `undefined` when cleared.
     * This is the primary callback for handling selection changes.
     *
     * @param option - The selected option object, or `undefined` if cleared
     */
    optionChange = output();
    valueChange = output();
    /**
     * The callback is fired when the dropdown requests to be hidden.
     * Called when the dropdown closes due to user interaction, clicks outside,
     * escape key, or programmatic changes.
     */
    visibleChange = output();
    #visibleEffect2 = effect(() => {
        const visible = this.visible();
        this.autocompleteService.visible.set(visible);
        if (!visible && !this.searchValue()) {
            this.autocompleteService.hint.set(undefined);
        }
        this.visibleChange.emit(visible);
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect2" }] : []));
    searchValue = computed(() => {
        return this.autocompleteService.searchValue() ?? '';
    }, ...(ngDevMode ? [{ debugName: "searchValue" }] : []));
    selected = this.autocompleteService.selected;
    handleClear($event) {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        $event.stopPropagation();
        // this.selected.set(null);
        this.autocompleteService.autocompleteDirective()?.handleClear();
        this.inputPortalContent().nativeElement.focus();
    }
    handleIndicatorClick($event) {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        $event.stopPropagation();
        // todo
        if (this.disabled() || this.readOnly()) {
            return;
        }
        this.visible.update((value) => !value);
        this.inputPortalContent().nativeElement.focus();
    }
    hostClasses = computed(() => {
        const sizing = this.sizing();
        const valid = this.valid();
        return {
            autocomplete: true,
            [`autocomplete-${sizing}`]: !!sizing,
            disabled: this.disabled(),
            'is-valid': valid === true,
            'is-invalid': valid === false,
            show: this.show()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    handleGlobalSearch($event) {
        if (this.visible() && $event.key === 'Tab' && !$event.shiftKey) {
            $event.preventDefault();
            this.optionsRef()?.focusKeyManager?.setFirstItemActive();
            return;
        }
        const global = isGlobalSearch(this.search());
        if (global && ['Space'].includes($event.code)) {
            return;
        }
        const inputRef = this.inputPortalContent().nativeElement;
        if (global && inputRef && ($event.key.length === 1 || ['Backspace', 'Delete'].includes($event.key))) {
            inputRef.focus();
        }
    }
    dropdownMenuClasses = computed(() => {
        return { 'autocomplete-dropdown': true, 'dropdown-menu': false };
    }, ...(ngDevMode ? [{ debugName: "dropdownMenuClasses" }] : []));
    hintValue = computed(() => {
        const hintOption = this.autocompleteService.hint();
        const searchValue = this.searchValue();
        const optionLabel = hintOption ? getOptionLabel(hintOption) : '';
        if (optionLabel && !searchValue) {
            return optionLabel;
        }
        return optionLabel ? `${searchValue}${optionLabel.slice(searchValue.length)}` : '';
    }, ...(ngDevMode ? [{ debugName: "hintValue" }] : []));
    #hintsEffect = effect(() => {
        if (!this.showHints()) {
            return;
        }
        const findOption = findHintOption(this.searchValue(), this.options());
        this.autocompleteService.hint.set(findOption);
    }, ...(ngDevMode ? [{ debugName: "#hintsEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: AutocompleteComponent, isStandalone: true, selector: "c-autocomplete", inputs: { inputPortalContent: { classPropertyName: "inputPortalContent", publicName: "inputPortalContent", isSignal: true, isRequired: true, transformFunction: null }, allowOnlyDefinedOptions: { classPropertyName: "allowOnlyDefinedOptions", publicName: "allowOnlyDefinedOptions", isSignal: true, isRequired: false, transformFunction: null }, cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: true, isRequired: false, transformFunction: null }, clearSearchOnSelect: { classPropertyName: "clearSearchOnSelect", publicName: "clearSearchOnSelect", isSignal: true, isRequired: false, transformFunction: null }, delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, highlightOptionsOnSearch: { classPropertyName: "highlightOptionsOnSearch", publicName: "highlightOptionsOnSearch", isSignal: true, isRequired: false, transformFunction: null }, indicator: { classPropertyName: "indicator", publicName: "indicator", isSignal: true, isRequired: false, transformFunction: null }, itemSize: { classPropertyName: "itemSize", publicName: "itemSize", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, options: { classPropertyName: "options", publicName: "options", isSignal: true, isRequired: true, transformFunction: null }, optionsMaxHeight: { classPropertyName: "optionsMaxHeight", publicName: "optionsMaxHeight", isSignal: true, isRequired: false, transformFunction: null }, optionTemplate: { classPropertyName: "optionTemplate", publicName: "optionTemplate", isSignal: true, isRequired: false, transformFunction: null }, optionGroupTemplate: { classPropertyName: "optionGroupTemplate", publicName: "optionGroupTemplate", isSignal: true, isRequired: false, transformFunction: null }, popperOptions: { classPropertyName: "popperOptions", publicName: "popperOptions", isSignal: true, isRequired: true, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, search: { classPropertyName: "search", publicName: "search", isSignal: true, isRequired: false, transformFunction: null }, searchNoResultsLabel: { classPropertyName: "searchNoResultsLabel", publicName: "searchNoResultsLabel", isSignal: true, isRequired: false, transformFunction: null }, showHints: { classPropertyName: "showHints", publicName: "showHints", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, virtualScroller: { classPropertyName: "virtualScroller", publicName: "virtualScroller", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, visibleItems: { classPropertyName: "visibleItems", publicName: "visibleItems", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { inputChange: "inputChange", optionChange: "optionChange", valueChange: "valueChange", visibleChange: "visibleChange" }, host: { attributes: { "ngSkipHydration": "true" }, listeners: { "keydown": "handleGlobalSearch($event)" }, properties: { "class": "hostClasses()" }, classAttribute: "autocomplete" }, providers: [AutocompleteService], viewQueries: [{ propertyName: "dropdownMenuRef", first: true, predicate: ["dropdownMenuRef"], descendants: true, read: ElementRef, isSignal: true }, { propertyName: "togglerRef", first: true, predicate: ["dropdownToggleRef"], descendants: true, read: ElementRef, isSignal: true }, { propertyName: "cleanerRef", first: true, predicate: ["cleanerRef"], descendants: true, read: ElementRef, isSignal: true }, { propertyName: "indicatorRef", first: true, predicate: ["indicatorRef"], descendants: true, read: ElementRef, isSignal: true }, { propertyName: "optionsRef", first: true, predicate: AutocompleteOptionsComponent, descendants: true, isSignal: true }], exportAs: ["cAutocompleteWrapper"], ngImport: i0, template: "@let inputElementId = inputPortalContent().nativeElement.getAttribute('id');\n@let allOptions = options();\n@let isDisabled = disabled();\n@let searchVal = searchValue();\n@let selectedVal = selected() ?? null;\n@let hint = hintValue();\n@let hasCleaner = cleaner();\n@let hasIndicator = indicator();\n\n<ng-template [cdkPortalOutlet]=\"domTogglerPortal()\" />\n<ng-template [cdkPortalOutlet]=\"domDropdownMenuPortal()\" />\n\n<c-dropdown\n  #dropdownRef=\"cDropdown\"\n  [(visible)]=\"visible\"\n  [autoClose]=\"'outside'\"\n  [popperOptions]=\"popperOptions()\"\n  class=\"d-flex\"\n>\n  <div\n    #dropdownToggleRef=\"cDropdownToggle\"\n    [caret]=\"false\"\n    [disabled]=\"isDisabled\"\n    cDropdownToggle\n    class=\"autocomplete-input-group\"\n    role=\"button\"\n  >\n    @if (showHints() && (!!searchVal || (hint && !searchVal && !selectedVal))) {\n      <input\n        #inputHintRef\n        [id]=\"`${inputElementId}-hint`\"\n        [value]=\"hint\"\n        aria-hidden=\"true\"\n        autoComplete=\"off\"\n        class=\"autocomplete-input autocomplete-input-hint\"\n        readOnly\n        tabIndex=\"-1\"\n      />\n    }\n\n    <span style=\"display: contents\">\n      <ng-template [cdkPortalOutlet]=\"domInputPortal()\" />\n    </span>\n\n    @if (hasCleaner || hasIndicator) {\n      <div class=\"autocomplete-buttons\">\n        @if (!isDisabled && !readOnly() && hasCleaner && selectedVal) {\n          <button\n            #cleanerRef\n            (click)=\"handleClear($event)\"\n            class=\"autocomplete-cleaner\"\n            tabindex=\"-1\"\n            type=\"button\"\n          ></button>\n        }\n        @if (hasIndicator) {\n          <button\n            #indicatorRef\n            (click)=\"handleIndicatorClick($event)\"\n            [disabled]=\"isDisabled || !(searchNoResultsLabel() || allOptions.length > 0) && isExternalSearch(search())\"\n            class=\"autocomplete-indicator\"\n            tabindex=\"-1\"\n            type=\"button\"\n          ></button>\n        }\n      </div>\n    }\n  </div>\n\n  <div #dropdownMenuRef>\n    @let showDropdown = show();\n    @let noResultsLabel = searchNoResultsLabel();\n    @let isLoading = loading();\n    @let isVisible = dropdownRef.visible();\n    @let showOptions = allOptions.length && !isLoading;\n    @let showLoadingWithResults = isLoading && searchVal && allOptions.length;\n    @let showNoResults = !isLoading && noResultsLabel && searchVal && !allOptions.length;\n    @if (!isDisabled) {\n      <div\n        #dropdownMenu=\"cDropdownMenu\"\n        [attr.aria-labelledby]=\"inputElementId\"\n        [class]=\"dropdownMenuClasses()\"\n        [id]=\"`${inputElementId}-listbox`\"\n        [class.show]=\"isVisible && (showOptions || showLoadingWithResults || showNoResults)\"\n        cDropdownMenu\n        role=\"listbox\"\n      >\n        @if (showDropdown) {\n          <c-autocomplete-options\n            #optionsRef\n            [delay]=\"delay()\"\n            [highlightOptionsOnSearch]=\"highlightOptionsOnSearch()\"\n            [itemSize]=\"itemSize()\"\n            [loading]=\"isLoading\"\n            [optionGroupTemplate]=\"optionGroupTemplate()\"\n            [optionTemplate]=\"optionTemplate()\"\n            [optionsMaxHeight]=\"optionsMaxHeight()\"\n            [options]=\"allOptions\"\n            [searchNoResultsLabel]=\"searchNoResultsLabel()\"\n            [searchValue]=\"searchVal\"\n            [search]=\"search()\"\n            [selected]=\"selectedVal\"\n            [virtualScroller]=\"virtualScroller()\"\n            [visibleItems]=\"visibleItems()\"\n            tabindex=\"-1\"\n          />\n        } @else if (showNoResults) {\n          <div class=\"autocomplete-options-empty\">{{ noResultsLabel }}</div>\n        }\n        @if (showLoadingWithResults) {\n          <c-element-cover [style]=\"{'border-radius': '6px', 'z-index': 2}\" />\n        }\n      </div>\n    }\n  </div>\n</c-dropdown>\n", styles: [":host{display:block}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .autocomplete-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .autocomplete-input-group{border-start-end-radius:var(--cui-autocomplete-border-radius);border-end-end-radius:var(--cui-autocomplete-border-radius)}\n"], dependencies: [{ kind: "directive", type: CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "component", type: AutocompleteOptionsComponent, selector: "c-autocomplete-options", inputs: ["delay", "highlightOptionsOnSearch", "loading", "itemSize", "options", "optionsMaxHeight", "optionTemplate", "optionGroupTemplate", "search", "searchNoResultsLabel", "searchValue", "selected", "virtualScroller", "visibleItems"], outputs: ["optionClick"] }, { kind: "component", type: DropdownComponent, selector: "c-dropdown", inputs: ["alignment", "autoClose", "direction", "placement", "popper", "popperOptions", "variant", "visible"], outputs: ["visibleChange"], exportAs: ["cDropdown"] }, { kind: "directive", type: DropdownToggleDirective, selector: "[cDropdownToggle]", inputs: ["dropdownComponent", "disabled", "caret", "split"], exportAs: ["cDropdownToggle"] }, { kind: "directive", type: DropdownMenuDirective, selector: "[cDropdownMenu]", inputs: ["alignment", "visible"], exportAs: ["cDropdownMenu"] }, { kind: "component", type: ElementCoverComponent, selector: "c-element-cover, [cElementCover]", inputs: ["boundaries", "opacity"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-autocomplete', exportAs: 'cAutocompleteWrapper', imports: [
                        CdkPortalOutlet,
                        AutocompleteOptionsComponent,
                        DropdownComponent,
                        DropdownToggleDirective,
                        DropdownMenuDirective,
                        ElementCoverComponent
                    ], host: {
                        class: 'autocomplete',
                        '[class]': 'hostClasses()',
                        '(keydown)': 'handleGlobalSearch($event)',
                        ngSkipHydration: 'true'
                    }, providers: [AutocompleteService], template: "@let inputElementId = inputPortalContent().nativeElement.getAttribute('id');\n@let allOptions = options();\n@let isDisabled = disabled();\n@let searchVal = searchValue();\n@let selectedVal = selected() ?? null;\n@let hint = hintValue();\n@let hasCleaner = cleaner();\n@let hasIndicator = indicator();\n\n<ng-template [cdkPortalOutlet]=\"domTogglerPortal()\" />\n<ng-template [cdkPortalOutlet]=\"domDropdownMenuPortal()\" />\n\n<c-dropdown\n  #dropdownRef=\"cDropdown\"\n  [(visible)]=\"visible\"\n  [autoClose]=\"'outside'\"\n  [popperOptions]=\"popperOptions()\"\n  class=\"d-flex\"\n>\n  <div\n    #dropdownToggleRef=\"cDropdownToggle\"\n    [caret]=\"false\"\n    [disabled]=\"isDisabled\"\n    cDropdownToggle\n    class=\"autocomplete-input-group\"\n    role=\"button\"\n  >\n    @if (showHints() && (!!searchVal || (hint && !searchVal && !selectedVal))) {\n      <input\n        #inputHintRef\n        [id]=\"`${inputElementId}-hint`\"\n        [value]=\"hint\"\n        aria-hidden=\"true\"\n        autoComplete=\"off\"\n        class=\"autocomplete-input autocomplete-input-hint\"\n        readOnly\n        tabIndex=\"-1\"\n      />\n    }\n\n    <span style=\"display: contents\">\n      <ng-template [cdkPortalOutlet]=\"domInputPortal()\" />\n    </span>\n\n    @if (hasCleaner || hasIndicator) {\n      <div class=\"autocomplete-buttons\">\n        @if (!isDisabled && !readOnly() && hasCleaner && selectedVal) {\n          <button\n            #cleanerRef\n            (click)=\"handleClear($event)\"\n            class=\"autocomplete-cleaner\"\n            tabindex=\"-1\"\n            type=\"button\"\n          ></button>\n        }\n        @if (hasIndicator) {\n          <button\n            #indicatorRef\n            (click)=\"handleIndicatorClick($event)\"\n            [disabled]=\"isDisabled || !(searchNoResultsLabel() || allOptions.length > 0) && isExternalSearch(search())\"\n            class=\"autocomplete-indicator\"\n            tabindex=\"-1\"\n            type=\"button\"\n          ></button>\n        }\n      </div>\n    }\n  </div>\n\n  <div #dropdownMenuRef>\n    @let showDropdown = show();\n    @let noResultsLabel = searchNoResultsLabel();\n    @let isLoading = loading();\n    @let isVisible = dropdownRef.visible();\n    @let showOptions = allOptions.length && !isLoading;\n    @let showLoadingWithResults = isLoading && searchVal && allOptions.length;\n    @let showNoResults = !isLoading && noResultsLabel && searchVal && !allOptions.length;\n    @if (!isDisabled) {\n      <div\n        #dropdownMenu=\"cDropdownMenu\"\n        [attr.aria-labelledby]=\"inputElementId\"\n        [class]=\"dropdownMenuClasses()\"\n        [id]=\"`${inputElementId}-listbox`\"\n        [class.show]=\"isVisible && (showOptions || showLoadingWithResults || showNoResults)\"\n        cDropdownMenu\n        role=\"listbox\"\n      >\n        @if (showDropdown) {\n          <c-autocomplete-options\n            #optionsRef\n            [delay]=\"delay()\"\n            [highlightOptionsOnSearch]=\"highlightOptionsOnSearch()\"\n            [itemSize]=\"itemSize()\"\n            [loading]=\"isLoading\"\n            [optionGroupTemplate]=\"optionGroupTemplate()\"\n            [optionTemplate]=\"optionTemplate()\"\n            [optionsMaxHeight]=\"optionsMaxHeight()\"\n            [options]=\"allOptions\"\n            [searchNoResultsLabel]=\"searchNoResultsLabel()\"\n            [searchValue]=\"searchVal\"\n            [search]=\"search()\"\n            [selected]=\"selectedVal\"\n            [virtualScroller]=\"virtualScroller()\"\n            [visibleItems]=\"visibleItems()\"\n            tabindex=\"-1\"\n          />\n        } @else if (showNoResults) {\n          <div class=\"autocomplete-options-empty\">{{ noResultsLabel }}</div>\n        }\n        @if (showLoadingWithResults) {\n          <c-element-cover [style]=\"{'border-radius': '6px', 'z-index': 2}\" />\n        }\n      </div>\n    }\n  </div>\n</c-dropdown>\n", styles: [":host{display:block}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .autocomplete-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .autocomplete-input-group{border-start-end-radius:var(--cui-autocomplete-border-radius);border-end-end-radius:var(--cui-autocomplete-border-radius)}\n"] }]
        }], propDecorators: { dropdownMenuRef: [{ type: i0.ViewChild, args: ['dropdownMenuRef', { ...{ read: ElementRef }, isSignal: true }] }], togglerRef: [{ type: i0.ViewChild, args: ['dropdownToggleRef', { ...{ read: ElementRef }, isSignal: true }] }], cleanerRef: [{ type: i0.ViewChild, args: ['cleanerRef', { ...{ read: ElementRef }, isSignal: true }] }], indicatorRef: [{ type: i0.ViewChild, args: ['indicatorRef', { ...{ read: ElementRef }, isSignal: true }] }], optionsRef: [{ type: i0.ViewChild, args: [i0.forwardRef(() => AutocompleteOptionsComponent), { isSignal: true }] }], inputPortalContent: [{ type: i0.Input, args: [{ isSignal: true, alias: "inputPortalContent", required: true }] }], allowOnlyDefinedOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "allowOnlyDefinedOptions", required: false }] }], cleaner: [{ type: i0.Input, args: [{ isSignal: true, alias: "cleaner", required: false }] }], clearSearchOnSelect: [{ type: i0.Input, args: [{ isSignal: true, alias: "clearSearchOnSelect", required: false }] }], delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], highlightOptionsOnSearch: [{ type: i0.Input, args: [{ isSignal: true, alias: "highlightOptionsOnSearch", required: false }] }], indicator: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicator", required: false }] }], itemSize: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemSize", required: false }] }], loading: [{ type: i0.Input, args: [{ isSignal: true, alias: "loading", required: false }] }], options: [{ type: i0.Input, args: [{ isSignal: true, alias: "options", required: true }] }], optionsMaxHeight: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsMaxHeight", required: false }] }], optionTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionTemplate", required: false }] }], optionGroupTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionGroupTemplate", required: false }] }], popperOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "popperOptions", required: true }] }], readOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "readOnly", required: false }] }], search: [{ type: i0.Input, args: [{ isSignal: true, alias: "search", required: false }] }], searchNoResultsLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "searchNoResultsLabel", required: false }] }], showHints: [{ type: i0.Input, args: [{ isSignal: true, alias: "showHints", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], virtualScroller: [{ type: i0.Input, args: [{ isSignal: true, alias: "virtualScroller", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleItems: [{ type: i0.Input, args: [{ isSignal: true, alias: "visibleItems", required: false }] }], inputChange: [{ type: i0.Output, args: ["inputChange"] }], optionChange: [{ type: i0.Output, args: ["optionChange"] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }] } });

class AutocompleteDirective {
    writeValue(value) {
        if (this.#initialValue) {
            return;
        }
        if (value === null || value === undefined || value === '') {
            if (this.value() !== undefined) {
                this.handleClear();
            }
        }
        this.value.set(value);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabledState.set(isDisabled);
    }
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    static ngAcceptInputType_allowOnlyDefinedOptions;
    static ngAcceptInputType_cleaner;
    static ngAcceptInputType_clearSearchOnSelect;
    static ngAcceptInputType_delay;
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_highlightOptionsOnSearch;
    static ngAcceptInputType_indicator;
    static ngAcceptInputType_loading;
    static ngAcceptInputType_readOnly;
    static ngAcceptInputType_resetSelectionOnOptionsChange;
    static ngAcceptInputType_showHints;
    static ngAcceptInputType_virtualScroller;
    static ngAcceptInputType_visible;
    static ngAcceptInputType_visibleItems;
    #destroyRef = inject(DestroyRef);
    #hostElement = inject(ElementRef);
    #viewContainerRef = inject(ViewContainerRef);
    #RtlService = inject(RtlService);
    #UIDService = inject(UIDService);
    #autocompleteService;
    #autocompleteComponentRef;
    /**
     * Optional popper Options object
     * @return Partial<Options>
     */
    popperOptionsInput = input(defaultPopperOptions, { ...(ngDevMode ? { debugName: "popperOptionsInput" } : {}), alias: 'popperOptions' });
    #popperOptionsEffect = effect(() => {
        const options = this.popperOptionsInput();
        const isRTL = this.#RtlService.isRTL(this.#hostElement.nativeElement);
        options.placement = (isRTL ? 'bottom-end' : 'bottom-start');
        const prevOptions = untracked(this.#popperOptions);
        const { modifiers: prevModifiers = [] } = prevOptions;
        const { modifiers: currModifiers = [] } = options;
        const currOptions = {
            ...options,
            modifiers: [
                ...prevModifiers.filter((prevModifier) => !currModifiers.find((currModifier) => currModifier.name === prevModifier.name)),
                ...currModifiers
            ]
        };
        this.#popperOptions.set({ ...prevOptions, ...currOptions });
    }, ...(ngDevMode ? [{ debugName: "#popperOptionsEffect" }] : []));
    popperOptions = computed(() => {
        return this.#popperOptions();
    }, ...(ngDevMode ? [{ debugName: "popperOptions" }] : []));
    #popperOptions = signal(defaultPopperOptions, ...(ngDevMode ? [{ debugName: "#popperOptions" }] : []));
    /**
     * Only allow selection of predefined options.
     * When `true`, users cannot enter custom values that are not in the options list.
     * When `false`, users can enter and select custom values.
     * @default false
     */
    allowOnlyDefinedOptions = input(false, { ...(ngDevMode ? { debugName: "allowOnlyDefinedOptions" } : {}), transform: booleanAttribute });
    /**
     * Enables selection cleaner element.
     * When `true`, displays a clear button that allows users to reset the selection.
     * The cleaner button is only shown when there is a selection and the component is not disabled or read-only.
     * @default false
     */
    cleaner = input(false, { ...(ngDevMode ? { debugName: "cleaner" } : {}), transform: booleanAttribute });
    /**
     * Whether to clear the internal search state after selecting an option.
     *
     * When set to `true`, the internal search value used for filtering options is cleared
     * after a selection is made. This affects only the component's internal logic.
     *
     * Note: This does **not** clear the visible input field if the component is using external search
     * or is controlled via the `searchValue` prop. In such cases, clearing must be handled externally.
     * @default true
     */
    clearSearchOnSelect = input(true, { ...(ngDevMode ? { debugName: "clearSearchOnSelect" } : {}), transform: booleanAttribute });
    /**
     * Debounce delay in milliseconds for filtering options based on search input.
     * Controls how quickly the options list updates as the user types.
     * Higher values reduce update frequency for better performance with large datasets.
     * @default 150
     */
    delay = input(150, { ...(ngDevMode ? { debugName: "delay" } : {}), transform: numberAttribute });
    /**
     * Toggle the disabled state for the component.
     * When `true`, the Angular autocomplete is non-interactive and appears visually disabled.
     * Users cannot type, select options, or trigger the dropdown.
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    disabledState = linkedSignal({ ...(ngDevMode ? { debugName: "disabledState" } : {}), source: this.disabled,
        computation: (isDisabled) => {
            this.#hostElement.nativeElement.disabled = isDisabled;
            return isDisabled || null;
        } });
    /**
     * Highlight options that match the search criteria.
     * When `true`, matching portions of option labels are visually highlighted
     * based on the current search input value.
     *
     * @default false
     */
    highlightOptionsOnSearch = input(false, { ...(ngDevMode ? { debugName: "highlightOptionsOnSearch" } : {}), transform: booleanAttribute });
    /**
     * Unique identifier for the Autocomplete component.
     * If not provided, a default ID will be generated.
     * @return string
     * @default 'autocomplete-<nextId>'
     */
    idInput = input(undefined, { ...(ngDevMode ? { debugName: "idInput" } : {}), alias: 'id' });
    /**
     * Unique identifier for the Autocomplete component.
     * If `idInput` is not provided, a default ID will be generated.
     * @return string
     */
    id = computed(() => this.idInput() ?? this.#UIDService.getUID('autocomplete'), ...(ngDevMode ? [{ debugName: "id" }] : []));
    /**
     * Show dropdown indicator/arrow button.
     * When `true`, displays a dropdown arrow button that can be clicked
     * to manually show or hide options dropdown.
     */
    indicator = input(false, { ...(ngDevMode ? { debugName: "indicator" } : {}), transform: booleanAttribute });
    /**
     * The size of the option item in the list (in pixels).
     * @return number
     * @default 40
     */
    itemSize = input(40, { ...(ngDevMode ? { debugName: "itemSize" } : {}), transform: numberAttribute });
    /**
     * When set, the options list will have a loading style: loading spinner and reduced opacity.
     * Use this to indicate that options are being fetched asynchronously.
     * The dropdown remains functional but shows visual loading indicators.
     */
    loading = input(false, { ...(ngDevMode ? { debugName: "loading" } : {}), transform: booleanAttribute });
    /**
     * List of option elements.
     * Can contain Option objects, OptionsGroup objects, or plain strings.
     * Plain strings are converted to simple Option objects internally.
     * This is a required prop - the Angular autocomplete needs options to function.
     */
    optionsInput = input.required({ ...(ngDevMode ? { debugName: "optionsInput" } : {}), alias: 'options' });
    options = computed(() => {
        const options = this.optionsInput();
        return normalizeOptionsArray(options);
    }, ...(ngDevMode ? [{ debugName: "options" }] : []));
    #optionsEffect = effect(() => {
        const options = this.options();
        untracked(() => {
            if (this.resetSelectionOnOptionsChange() && options.length) {
                this.handleClear();
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#optionsEffect" }] : []));
    /**
     * Sets maxHeight of options list.
     * Controls the maximum height of the dropdown options container.
     * Can be a number (pixels) or a CSS length string (e.g., '200px', '10rem').
     * When content exceeds this height, a scrollbar will appear.
     * @default 'auto'
     */
    optionsMaxHeightInput = input('auto', { ...(ngDevMode ? { debugName: "optionsMaxHeightInput" } : {}), alias: 'optionsMaxHeight' });
    optionsMaxHeight = linkedSignal(this.optionsMaxHeightInput, ...(ngDevMode ? [{ debugName: "optionsMaxHeight" }] : []));
    /**
     * Custom template for rendering individual options.
     * Allows complete customization of how each option appears in the dropdown.
     */
    optionTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionTemplate" }] : []));
    /**
     * Custom template for rendering option groups.
     * Allows customization of how option group headers appear in the dropdown.
     */
    optionGroupTemplate = input(...(ngDevMode ? [undefined, { debugName: "optionGroupTemplate" }] : []));
    /**
     * Specifies a short hint that is visible in the search input.
     * Displayed when the input is empty to guide user interaction.
     * Standard HTML input placeholder behavior.
     */
    placeholder = input(...(ngDevMode ? [undefined, { debugName: "placeholder" }] : []));
    /**
     * Toggle the readonly state for the component.
     * When `true`, users can view and interact with the dropdown but cannot
     * type in the search input or modify the selection through typing.
     * Selection via clicking options may still be possible.
     */
    readOnly = input(false, { ...(ngDevMode ? { debugName: "readOnly" } : {}), transform: booleanAttribute });
    /**
     * Determines whether the selected options should be cleared when the options list is updated.
     * When `true`, any previously selected options will be reset whenever the options
     * list undergoes a change. This ensures that outdated selections are not retained
     * when new options are provided.
     *
     * @default false
     */
    resetSelectionOnOptionsChange = input(false, { ...(ngDevMode ? { debugName: "resetSelectionOnOptionsChange" } : {}), transform: booleanAttribute });
    /**
     * Enables and configures search functionality.
     * - `'external'`: Search is handled externally, filtering is not applied internally
     * - `'global'`: Enables global keyboard search when dropdown is closed
     * - Object with `external` and `global` boolean properties for fine-grained control
     */
    search = input(...(ngDevMode ? [undefined, { debugName: "search" }] : []));
    /**
     * Sets the label for no results when filtering.
     * - `false`: Don't show any message when no results found
     * - `true`: Show default "No results found" message
     * - `string`: Show custom text message
     * - `TemplateRef`: Show custom component/element
     *
     * @default false
     */
    searchNoResultsLabel = input(false, ...(ngDevMode ? [{ debugName: "searchNoResultsLabel" }] : []));
    /**
     * Show hint options based on the current input value.
     * When `true`, displays a preview/hint of the first matching option
     * as semi-transparent text in the input field, similar to browser autocomplete.
     *
     * @default false
     */
    showHints = input(false, { ...(ngDevMode ? { debugName: "showHints" } : {}), transform: booleanAttribute });
    /**
     * Size the component small or large.
     * - `'sm'`: Small size variant
     * - `'lg'`: Large size variant
     * - `undefined`: Default/medium size
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Set form input validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Sets the initially selected value for the Angular autocomplete component.
     * Can be a string (matched against option labels) or number (matched against option values).
     * The component will attempt to find and select the matching option on mount.
     */
    valueInput = input(undefined, { ...(ngDevMode ? { debugName: "valueInput" } : {}), alias: 'value' });
    value = linkedSignal(this.valueInput, ...(ngDevMode ? [{ debugName: "value" }] : []));
    #initialValue = undefined;
    #valueEffect = effect(() => {
        this.#initialValue ??= this.value();
    }, ...(ngDevMode ? [{ debugName: "#valueEffect" }] : []));
    #optionsValueEffect = effect(() => {
        const value = this.value();
        const options = this.options();
        untracked(() => {
            if (typeof value === 'string' && value) {
                const foundOption = getFirstOptionByLabel(value, options) ?? { label: value };
                this.handleSelect(foundOption);
                return;
            }
            if (typeof value === 'number') {
                const foundOption = getFirstOptionByValue(value, options);
                if (foundOption) {
                    this.handleSelect(foundOption);
                }
                return;
            }
            const filteredOptions = untracked(this.filteredOptions);
            const selected = flattenOptionsArray(filteredOptions).find((option) => option.selected === true);
            if (selected) {
                this.handleSelect(selected);
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#optionsValueEffect" }] : []));
    /**
     * Enable virtual scroller for the options list.
     * When `true`, only visible options are rendered in the DOM for better performance
     * with large option lists. Works in conjunction with `visibleItems` prop.
     * @default false
     */
    virtualScroller = input(false, { ...(ngDevMode ? { debugName: "virtualScroller" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of autocomplete dropdown.
     * Controls whether the dropdown is initially visible.
     * The dropdown visibility can still be toggled through user interaction.
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    /**
     * Amount of visible items when virtualScroller is enabled.
     * Determines how many option items are rendered at once when virtual scrolling is active.
     * Higher values show more items but use more memory. Lower values improve performance.
     * @default 8
     */
    visibleItemsInput = input(8, { ...(ngDevMode ? { debugName: "visibleItemsInput" } : {}), alias: 'visibleItems', transform: numberAttribute });
    visibleItems = computed(() => {
        const visibleItems = this.visibleItemsInput();
        return visibleItems > 0 ? visibleItems : 8;
    }, ...(ngDevMode ? [{ debugName: "visibleItems" }] : []));
    #visibleItemsEffect = effect(() => {
        const optionsMaxHeight = this.optionsMaxHeight();
        if (optionsMaxHeight === 'auto') {
            this.optionsMaxHeight.set((this.itemSize() * this.visibleItems() + 16).toString() + 'px');
        }
    }, ...(ngDevMode ? [{ debugName: "#visibleItemsEffect" }] : []));
    /**
     * Emits an event when the filter/search value changes.
     * Called whenever the user types in the search input.
     * Useful for implementing external search functionality or analytics.
     * @param value - The current search input value
     */
    inputChange = output();
    /**
     * Emits an event when a user changes the selected option.
     * Called with the selected option object or `undefined` when cleared.
     * This is the primary callback for handling selection changes.
     * @param option - The selected option object, or `undefined` if cleared
     */
    optionChange = output();
    valueChange = output();
    /**
     * The callback is fired when the dropdown requests to be hidden.
     * Called when the dropdown closes due to user interaction, clicks outside,
     * escape key, or programmatic changes.
     */
    visibleChange = output();
    #inputEvent$ = fromEvent(this.#hostElement.nativeElement, 'input')
        .pipe(map((event) => {
        return event.target?.value ?? '';
    }), 
    // debounceTime(this.delay()),
    tap((value) => {
        if (this.showHints()) {
            const findOption = findHintOption(value, this.options());
            this.#autocompleteService.hint.set(findOption);
        }
    }), switchMap((event) => {
        // this.#autocompleteService.hint.set(undefined);
        const delay = this.delay();
        return timer(delay).pipe(
        // Keep the original event data
        switchMap(() => [event]));
    }), tap((value) => {
        this.handleSelect(null);
        this.handleSearch(value);
        this.#inputValue.set({ value, origin: 'event' });
    }), takeUntilDestroyed(this.#destroyRef))
        .subscribe();
    ngOnInit() {
        this.#initialValue ??= this.value();
        this.#autocompleteComponentRef = this.#viewContainerRef.createComponent(AutocompleteComponent, {
            bindings: [
                inputBinding('inputPortalContent', () => this.#hostElement),
                inputBinding('allowOnlyDefinedOptions', this.allowOnlyDefinedOptions),
                inputBinding('cleaner', this.cleaner),
                inputBinding('clearSearchOnSelect', this.clearSearchOnSelect),
                inputBinding('delay', this.delay),
                inputBinding('disabled', this.disabledState),
                inputBinding('highlightOptionsOnSearch', this.highlightOptionsOnSearch),
                inputBinding('itemSize', this.itemSize),
                inputBinding('indicator', this.indicator),
                inputBinding('loading', this.loading),
                inputBinding('options', this.filteredOptions),
                inputBinding('optionsMaxHeight', this.optionsMaxHeight),
                inputBinding('optionTemplate', this.optionTemplate),
                inputBinding('optionGroupTemplate', this.optionGroupTemplate),
                // inputBinding('placeholder', this.placeholder),
                inputBinding('readOnly', this.readOnly),
                // inputBinding('resetSelectionOnOptionsChange', this.resetSelectionOnOptionsChange),
                inputBinding('search', this.search),
                // inputBinding('searchValue', this.searchValue),
                inputBinding('searchNoResultsLabel', this.searchNoResultsLabel),
                inputBinding('showHints', this.showHints),
                inputBinding('sizing', this.sizing),
                inputBinding('valid', this.valid),
                // inputBinding('value', this.value),
                inputBinding('virtualScroller', this.virtualScroller),
                inputBinding('visible', this.visible),
                inputBinding('visibleItems', this.visibleItems),
                inputBinding('popperOptions', this.popperOptions),
                outputBinding('visibleChange', (visible) => this.visibleChange.emit(visible))
            ]
        });
        this.#autocompleteService = this.#autocompleteComponentRef.instance.autocompleteService;
        this.#autocompleteService.autocompleteDirective.set(this);
    }
    #placeholderEffect = effect(() => {
        this.#hostElement.nativeElement.placeholder =
            this.showHints() && this.#autocompleteService.hint() ? '' : (this.placeholder() ?? '');
    }, ...(ngDevMode ? [{ debugName: "#placeholderEffect" }] : []));
    #inputKeydown$ = fromEvent(this.#hostElement.nativeElement, 'keydown')
        .pipe(filter(($event) => ['Tab', 'Enter', 'Backspace', 'Delete', 'Escape', 'Down', 'ArrowDown'].includes($event.key)), tap(($event) => {
        if ($event.key === 'Escape') {
            this.handleDropdownHide();
            return;
        }
        if (['Down', 'ArrowDown'].includes($event.key) &&
            this.#hostElement.nativeElement.value.length === this.#hostElement.nativeElement.selectionStart) {
            $event.preventDefault();
            this.handleDropdownShow();
            this.#autocompleteComponentRef?.instance.optionsRef()?.focusKeyManager?.setFirstItemActive();
            return;
        }
        if ($event.key === 'Tab') {
            $event.stopImmediatePropagation();
            const hint = this.#autocompleteService?.hint();
            if (this.showHints() && hint && !this.#autocompleteService.selected()) {
                $event.preventDefault();
                this.handleSelect(hint);
                this.handleDropdownHide();
            }
            return;
        }
        if ($event.key === 'Enter') {
            const input = $event.target;
            const foundOptions = getFirstOptionByLabel(input.value, this.filteredOptions());
            if (foundOptions) {
                this.handleSelect(foundOptions);
            }
            else {
                if (!this.allowOnlyDefinedOptions()) {
                    const value = input.value;
                    const select = !!value ? { label: value } : null;
                    this.handleSelect(select);
                }
            }
            this.handleDropdownHide();
            return;
        }
        if (['Backspace', 'Delete'].includes($event.key)
        // && (this.#hostElement.nativeElement as HTMLInputElement).value !== ''
        ) {
            if (this.#autocompleteService.selected() !== null) {
                // this.handleSelect(null);
            }
            return;
        }
    }), takeUntilDestroyed(this.#destroyRef))
        .subscribe();
    onBlur($event) {
        // $event.preventDefault();
        // $event.stopPropagation();
        const selected = this.#autocompleteService.selected();
        if (this.allowOnlyDefinedOptions() && selected === null && this.filteredOptions().length === 0) {
            this.handleClear();
        }
        this.handleSelect(selected);
        this.handleSearch('');
        this.#autocompleteService.hint.set(undefined);
        this.onTouched();
    }
    #visibleEffect = effect(() => {
        const visible = this.#autocompleteService.visible();
        untracked(() => {
            this.visible.set(visible);
        });
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    #label = '';
    #selectedEffect = effect(() => {
        const selected = this.#autocompleteService.selected();
        const label = selected ? getOptionLabel(selected) : '';
        untracked(() => {
            if (selected !== undefined && label !== this.#label) {
                // this.handleSelect(selected);
                // if (this.#initialValue !== label)
                this.#label = label;
                this.valueChange.emit(label);
                this.optionChange.emit(selected);
                this.onChange(label);
                // }
                this.#initialValue = undefined;
                if (this.value() !== label) {
                    this.value.set(label);
                }
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#selectedEffect" }] : []));
    #searchValue = computed(() => {
        return this.#autocompleteService.searchValue() ?? '';
    }, ...(ngDevMode ? [{ debugName: "#searchValue" }] : []));
    #subtreeFocusedEffect = effect(() => {
        const subtreeFocused = this.#autocompleteService.subtreeFocused();
        if (subtreeFocused === null) {
            this.onBlur();
        }
        else {
            // this.onTouched();
        }
    }, ...(ngDevMode ? [{ debugName: "#subtreeFocusedEffect" }] : []));
    filteredOptions = computed(() => {
        const search = this.search();
        const options = this.options();
        const searchValue = this.#searchValue();
        return isExternalSearch(search) ? options : filterOptions(options, searchValue);
    }, ...(ngDevMode ? [{ debugName: "filteredOptions" }] : []));
    #filteredOptionsEffect = effect(() => {
        const filteredOptions = this.filteredOptions();
        untracked(() => {
            const searchValue = this.#searchValue();
            const visible = this.visible();
            const searchNoResultsLabel = this.searchNoResultsLabel();
            if (!searchNoResultsLabel && searchValue.length > 0 && filteredOptions.length === 0 && visible) {
                this.handleDropdownHide();
                return;
            }
            if (searchValue.length > 0 && filteredOptions.length > 0 && !visible) {
                this.handleDropdownShow();
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#filteredOptionsEffect" }] : []));
    handleSearch(value) {
        this.#autocompleteService.searchValue.set(value);
    }
    handleClear() {
        this.dispatchInputEvent('');
    }
    handleSelect(option) {
        if (option?.disabled) {
            return;
        }
        if (this.clearSearchOnSelect() && option !== null) {
            this.handleSearch('');
        }
        else {
            // this.#autocompleteService.hint.set(undefined);
        }
        this.#inputValue.set({
            value: option ? getOptionLabel(option) : (this.#autocompleteService.searchValue() ?? ''),
            origin: 'program'
        });
        this.#autocompleteService.selected.set(option ?? null);
    }
    #inputValue = signal({ value: '', origin: 'init' }, ...(ngDevMode ? [{ debugName: "#inputValue" }] : []));
    #inputValuePrev = '';
    #inputValueEffect = effect(() => {
        const { value = '', origin } = { ...this.#inputValue() };
        if (this.#inputValuePrev !== value) {
            this.#inputValuePrev = value;
            this.#hostElement.nativeElement.value = value;
            if (origin === 'event' || value === '') {
                this.inputChange.emit(value);
            }
        }
    }, ...(ngDevMode ? [{ debugName: "#inputValueEffect" }] : []));
    dispatchInputEvent(value) {
        const inputEvent = new InputEvent('input', {
            inputType: 'insertReplacementText',
            data: value
        });
        this.#hostElement.nativeElement.value = value;
        this.#hostElement.nativeElement.dispatchEvent(inputEvent);
    }
    handleDropdownHide() {
        this.#autocompleteService.visible.set(false);
        this.#hostElement.nativeElement.focus();
    }
    handleDropdownShow() {
        if (this.disabledState() || this.readOnly() || this.visible()) {
            return;
        }
        if (!isExternalSearch(this.search()) &&
            this.filteredOptions().length === 0 &&
            this.searchNoResultsLabel() === false) {
            return;
        }
        this.#autocompleteService.visible.set(true);
        this.#hostElement.nativeElement.focus();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: AutocompleteDirective, isStandalone: true, selector: "input[cAutocomplete]", inputs: { popperOptionsInput: { classPropertyName: "popperOptionsInput", publicName: "popperOptions", isSignal: true, isRequired: false, transformFunction: null }, allowOnlyDefinedOptions: { classPropertyName: "allowOnlyDefinedOptions", publicName: "allowOnlyDefinedOptions", isSignal: true, isRequired: false, transformFunction: null }, cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: true, isRequired: false, transformFunction: null }, clearSearchOnSelect: { classPropertyName: "clearSearchOnSelect", publicName: "clearSearchOnSelect", isSignal: true, isRequired: false, transformFunction: null }, delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, highlightOptionsOnSearch: { classPropertyName: "highlightOptionsOnSearch", publicName: "highlightOptionsOnSearch", isSignal: true, isRequired: false, transformFunction: null }, idInput: { classPropertyName: "idInput", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, indicator: { classPropertyName: "indicator", publicName: "indicator", isSignal: true, isRequired: false, transformFunction: null }, itemSize: { classPropertyName: "itemSize", publicName: "itemSize", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, optionsInput: { classPropertyName: "optionsInput", publicName: "options", isSignal: true, isRequired: true, transformFunction: null }, optionsMaxHeightInput: { classPropertyName: "optionsMaxHeightInput", publicName: "optionsMaxHeight", isSignal: true, isRequired: false, transformFunction: null }, optionTemplate: { classPropertyName: "optionTemplate", publicName: "optionTemplate", isSignal: true, isRequired: false, transformFunction: null }, optionGroupTemplate: { classPropertyName: "optionGroupTemplate", publicName: "optionGroupTemplate", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, resetSelectionOnOptionsChange: { classPropertyName: "resetSelectionOnOptionsChange", publicName: "resetSelectionOnOptionsChange", isSignal: true, isRequired: false, transformFunction: null }, search: { classPropertyName: "search", publicName: "search", isSignal: true, isRequired: false, transformFunction: null }, searchNoResultsLabel: { classPropertyName: "searchNoResultsLabel", publicName: "searchNoResultsLabel", isSignal: true, isRequired: false, transformFunction: null }, showHints: { classPropertyName: "showHints", publicName: "showHints", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, valueInput: { classPropertyName: "valueInput", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, virtualScroller: { classPropertyName: "virtualScroller", publicName: "virtualScroller", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, visibleItemsInput: { classPropertyName: "visibleItemsInput", publicName: "visibleItems", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { inputChange: "inputChange", optionChange: "optionChange", valueChange: "valueChange", visibleChange: "visibleChange" }, host: { attributes: { "type": "text" }, properties: { "attr.autocomplete": "\"off\"", "attr.aria-autocomplete": "\"list\"", "attr.aria-haspopup": "\"listbox\"", "attr.aria-expanded": "visible()", "attr.aria-controls": "`${id()}-listbox`", "attr.aria-activedescendant": "null", "attr.aria-owns": "`${id()}-listbox`", "attr.disabled": "disabledState() || null", "attr.id": "id()", "attr.readonly": "readOnly() || null", "attr.role": "\"combobox\"" }, classAttribute: "autocomplete-input" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => AutocompleteDirective),
                multi: true
            }
        ], exportAs: ["cAutocomplete"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cAutocomplete]',
                    exportAs: 'cAutocomplete',
                    host: {
                        class: 'autocomplete-input',
                        type: 'text',
                        '[attr.autocomplete]': '"off"',
                        '[attr.aria-autocomplete]': '"list"',
                        '[attr.aria-haspopup]': '"listbox"',
                        '[attr.aria-expanded]': 'visible()',
                        '[attr.aria-controls]': '`${id()}-listbox`',
                        '[attr.aria-activedescendant]': 'null',
                        '[attr.aria-owns]': '`${id()}-listbox`',
                        '[attr.disabled]': 'disabledState() || null',
                        '[attr.id]': 'id()',
                        '[attr.readonly]': 'readOnly() || null',
                        '[attr.role]': '"combobox"'
                        // '(blur)': 'onBlur($event)'
                    },
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => AutocompleteDirective),
                            multi: true
                        }
                    ]
                }]
        }], propDecorators: { popperOptionsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "popperOptions", required: false }] }], allowOnlyDefinedOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "allowOnlyDefinedOptions", required: false }] }], cleaner: [{ type: i0.Input, args: [{ isSignal: true, alias: "cleaner", required: false }] }], clearSearchOnSelect: [{ type: i0.Input, args: [{ isSignal: true, alias: "clearSearchOnSelect", required: false }] }], delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], highlightOptionsOnSearch: [{ type: i0.Input, args: [{ isSignal: true, alias: "highlightOptionsOnSearch", required: false }] }], idInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], indicator: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicator", required: false }] }], itemSize: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemSize", required: false }] }], loading: [{ type: i0.Input, args: [{ isSignal: true, alias: "loading", required: false }] }], optionsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "options", required: true }] }], optionsMaxHeightInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionsMaxHeight", required: false }] }], optionTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionTemplate", required: false }] }], optionGroupTemplate: [{ type: i0.Input, args: [{ isSignal: true, alias: "optionGroupTemplate", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], readOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "readOnly", required: false }] }], resetSelectionOnOptionsChange: [{ type: i0.Input, args: [{ isSignal: true, alias: "resetSelectionOnOptionsChange", required: false }] }], search: [{ type: i0.Input, args: [{ isSignal: true, alias: "search", required: false }] }], searchNoResultsLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "searchNoResultsLabel", required: false }] }], showHints: [{ type: i0.Input, args: [{ isSignal: true, alias: "showHints", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], valueInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], virtualScroller: [{ type: i0.Input, args: [{ isSignal: true, alias: "virtualScroller", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleItemsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visibleItems", required: false }] }], inputChange: [{ type: i0.Output, args: ["inputChange"] }], optionChange: [{ type: i0.Output, args: ["optionChange"] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }] } });

class AutocompleteModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteModule, imports: [AutocompleteDirective], exports: [AutocompleteDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [AutocompleteDirective],
                    exports: [AutocompleteDirective]
                }]
        }] });

class AlertHeadingDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertHeadingDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: AlertHeadingDirective, isStandalone: true, selector: "[cAlertHeading]", host: { classAttribute: "alert-heading" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertHeadingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cAlertHeading]',
                    host: { class: 'alert-heading' }
                }]
        }] });

class AlertLinkDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: AlertLinkDirective, isStandalone: true, selector: "[cAlertLink]", host: { classAttribute: "alert-link" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cAlertLink]',
                    host: { class: 'alert-link' }
                }]
        }] });

class ButtonDirective {
    static ngAcceptInputType_active;
    static ngAcceptInputType_disabled;
    /**
     * Toggle the active state for the component. [docs]
     * @type InputSignalWithTransform<boolean, unknown>
     */
    active = input(false, { ...(ngDevMode ? { debugName: "active" } : {}), transform: booleanAttribute });
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors. [docs]
     * @type InputSignal<Colors>
     */
    color = input('primary', ...(ngDevMode ? [{ debugName: "color" }] : []));
    /**
     * Toggle the disabled state for the component.
     * @type InputSignalWithTransform<boolean, unknown>
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    /**
     * Select the shape of the component.
     * @return Shapes
     */
    shape = input(...(ngDevMode ? [undefined, { debugName: "shape" }] : []));
    /**
     * Size the component small or large.
     * @return  sm' | 'lg' | ''
     */
    size = input('', ...(ngDevMode ? [{ debugName: "size" }] : []));
    /**
     * The tabindex attribute specifies the tab order of an element (when the "tab" button is used for navigating).
     */
    tabindex = input(undefined, { ...(ngDevMode ? { debugName: "tabindex" } : {}), transform: numberAttribute });
    /**
     * Specifies the type of button. Always specify the type attribute for the `<button>` element.
     * Different browsers may use different default types for the `<button>` element.
     * @type InputSignal<ButtonType>
     * @default 'button'
     */
    type = input('button', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Set the button variant to an outlined button or a ghost button.
     * @type InputSignal<'ghost' | 'outline' | undefined>
     */
    variant = input(...(ngDevMode ? [undefined, { debugName: "variant" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        const variant = this.variant();
        const size = this.size();
        const shape = this.shape();
        return {
            btn: true,
            [`btn-${color}`]: !!color && !variant,
            [`btn-${variant}`]: !!variant && !color,
            [`btn-${variant}-${color}`]: !!variant && !!color,
            [`btn-${size}`]: !!size,
            [`${shape}`]: !!shape,
            active: this.active(),
            disabled: this._disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    _disabled = computed(() => this.disabled(), ...(ngDevMode ? [{ debugName: "_disabled" }] : []));
    ariaDisabled = computed(() => {
        return this._disabled() ? true : null;
    }, ...(ngDevMode ? [{ debugName: "ariaDisabled" }] : []));
    attrDisabled = computed(() => {
        return this._disabled() ? '' : null;
    }, ...(ngDevMode ? [{ debugName: "attrDisabled" }] : []));
    tabIndex = computed(() => {
        return this._disabled() ? '-1' : (this.tabindex() ?? null);
    }, ...(ngDevMode ? [{ debugName: "tabIndex" }] : []));
    isActive = computed(() => {
        return this.active() || null;
    }, ...(ngDevMode ? [{ debugName: "isActive" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ButtonDirective, isStandalone: true, selector: "[cButton]", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, shape: { classPropertyName: "shape", publicName: "shape", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, tabindex: { classPropertyName: "tabindex", publicName: "tabindex", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-disabled": "ariaDisabled()", "attr.aria-pressed": "isActive()", "attr.disabled": "attrDisabled()", "attr.tabindex": "tabIndex()", "attr.type": "type()" }, classAttribute: "btn" }, exportAs: ["cButton"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cButton]',
                    exportAs: 'cButton',
                    host: {
                        class: 'btn',
                        '[class]': 'hostClasses()',
                        '[attr.aria-disabled]': 'ariaDisabled()',
                        '[attr.aria-pressed]': 'isActive()',
                        '[attr.disabled]': 'attrDisabled()',
                        '[attr.tabindex]': 'tabIndex()',
                        '[attr.type]': 'type()'
                    }
                }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], shape: [{ type: i0.Input, args: [{ isSignal: true, alias: "shape", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], tabindex: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabindex", required: false }] }], type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }] } });

class ButtonCloseDirective extends ButtonDirective {
    /**
     * Change the default color to white.
     * @type boolean
     * @deprecated 5.0.0. Use `cButtonClose.dark` instead.
     */
    white = input(false, { ...(ngDevMode ? { debugName: "white" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        const size = this.size();
        return {
            btn: true,
            'btn-close': true,
            'btn-close-white': this.white(),
            [`btn-${size}`]: !!size,
            active: this.active(),
            disabled: this._disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonCloseDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ButtonCloseDirective, isStandalone: true, selector: "[cButtonClose]", inputs: { white: { classPropertyName: "white", publicName: "white", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-disabled": "ariaDisabled()", "attr.aria-pressed": "isActive()", "attr.disabled": "attrDisabled()", "attr.tabindex": "tabIndex()", "attr.type": "type()" }, classAttribute: "btn btn-close" }, usesInheritance: true, hostDirectives: [{ directive: ThemeDirective, inputs: ["dark", "dark"] }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cButtonClose]',
                    hostDirectives: [{ directive: ThemeDirective, inputs: ['dark'] }],
                    host: {
                        class: 'btn btn-close',
                        '[class]': 'hostClasses()',
                        '[attr.aria-disabled]': 'ariaDisabled()',
                        '[attr.aria-pressed]': 'isActive()',
                        '[attr.disabled]': 'attrDisabled()',
                        '[attr.tabindex]': 'tabIndex()',
                        '[attr.type]': 'type()'
                    }
                }]
        }], propDecorators: { white: [{ type: i0.Input, args: [{ isSignal: true, alias: "white", required: false }] }] } });

class ButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ButtonModule, imports: [ButtonDirective,
            ButtonCloseDirective], exports: [ButtonDirective,
            ButtonCloseDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        ButtonDirective,
                        ButtonCloseDirective
                    ],
                    exports: [
                        ButtonDirective,
                        ButtonCloseDirective
                    ]
                }]
        }] });

class AlertComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @return Colors
     * @default 'primary'
     */
    color = input('primary', ...(ngDevMode ? [{ debugName: "color" }] : []));
    /**
     * Default role for alert. [docs]
     * @return string
     * @default 'alert'
     */
    role = input('alert', ...(ngDevMode ? [{ debugName: "role" }] : []));
    /**
     * Set the alert variant to a solid.
     * @return string
     */
    variant = input(...(ngDevMode ? [undefined, { debugName: "variant" }] : []));
    /**
     * Optionally adds a close button to alert and allow it to self dismiss.
     * @return boolean
     * @default false
     */
    dismissibleInput = input(false, { ...(ngDevMode ? { debugName: "dismissibleInput" } : {}), transform: booleanAttribute, alias: 'dismissible' });
    #dismissible = linkedSignal({ ...(ngDevMode ? { debugName: "#dismissible" } : {}), source: this.dismissibleInput,
        computation: (value) => {
            return value;
        } });
    set dismissible(value) {
        this.#dismissible.set(value);
    }
    get dismissible() {
        return this.#dismissible();
    }
    /**
     * Adds animation for dismissible alert.
     * @return boolean
     */
    fade = input(false, { ...(ngDevMode ? { debugName: "fade" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of alert component.
     * @return boolean
     */
    visibleInput = input(true, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    #visible = linkedSignal({ ...(ngDevMode ? { debugName: "#visible" } : {}), source: this.visibleInput,
        computation: (value) => {
            return value;
        } });
    set visible(value) {
        if (this.#visible() !== value) {
            this.#visible.set(value);
            this.visibleChange?.emit(value);
        }
    }
    get visible() {
        return this.#visible();
    }
    hide = signal(false, ...(ngDevMode ? [{ debugName: "hide" }] : []));
    /**
     * Event triggered on the alert dismiss.
     */
    visibleChange = output();
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    templates = computed(() => {
        return this.contentTemplates().reduce((acc, child) => {
            acc[child.id] = child.templateRef;
            return acc;
        }, {});
    }, ...(ngDevMode ? [{ debugName: "templates" }] : []));
    get animateType() {
        return this.visible ? 'show' : 'hide';
    }
    hostClasses = computed(() => {
        const color = this.color();
        const variant = this.variant();
        return {
            alert: true,
            'alert-dismissible': this.dismissible,
            fade: this.fade(),
            show: !this.hide(),
            [`alert-${color}`]: !!color && variant !== 'solid',
            [`bg-${color}`]: !!color && variant === 'solid',
            'text-white': !!color && variant === 'solid'
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    onAnimationStart($event) {
        this.onAnimationEvent($event);
    }
    onAnimationDone($event) {
        this.onAnimationEvent($event);
    }
    onAnimationEvent(event) {
        this.hide.set(event.phaseName === 'start' && event.toState === 'show');
        if (event.phaseName === 'done') {
            this.hide.set(event.toState === 'hide' || event.toState === 'void');
            if (event.toState === 'show') {
                this.hide.set(false);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: AlertComponent, isStandalone: true, selector: "c-alert", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, dismissibleInput: { classPropertyName: "dismissibleInput", publicName: "dismissible", isSignal: true, isRequired: false, transformFunction: null }, fade: { classPropertyName: "fade", publicName: "fade", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { listeners: { "@fadeInOut.start": "onAnimationStart($event)", "@fadeInOut.done": "onAnimationDone($event)" }, properties: { "@.disabled": "!fade()", "@fadeInOut": "animateType", "attr.role": "role()", "class": "hostClasses()" } }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cAlert"], ngImport: i0, template: "@if (visible || !hide()) {\n  @if (dismissible) {\n    <ng-container *ngTemplateOutlet=\"templates()?.['alertButtonCloseTemplate'] || defaultAlertButtonCloseTemplate\" />\n  }\n  <ng-content />\n}\n<ng-template #defaultAlertButtonCloseTemplate>\n  <button (click)=\"visible=false\" aria-label=\"Close\" cButtonClose></button>\n</ng-template>\n", styles: [":host{display:block;overflow:hidden}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: ButtonCloseDirective, selector: "[cButtonClose]", inputs: ["white"] }], animations: [
            trigger('fadeInOut', [
                state('show', style({ opacity: 1, height: '*', padding: '*', border: '*', margin: '*' })),
                state('hide', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                state('void', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                transition('show => hide', [animate('.3s ease-out')]),
                transition('hide => show', [animate('.3s ease-in')]),
                transition('show => void', [animate('.3s ease-out')]),
                transition('void => show', [animate('.3s ease-in')])
            ])
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-alert', exportAs: 'cAlert', imports: [NgTemplateOutlet, ButtonCloseDirective], animations: [
                        trigger('fadeInOut', [
                            state('show', style({ opacity: 1, height: '*', padding: '*', border: '*', margin: '*' })),
                            state('hide', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                            state('void', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                            transition('show => hide', [animate('.3s ease-out')]),
                            transition('hide => show', [animate('.3s ease-in')]),
                            transition('show => void', [animate('.3s ease-out')]),
                            transition('void => show', [animate('.3s ease-in')])
                        ])
                    ], host: {
                        '[@.disabled]': '!fade()',
                        '[@fadeInOut]': 'animateType',
                        '[attr.role]': 'role()',
                        '[class]': 'hostClasses()',
                        '(@fadeInOut.start)': 'onAnimationStart($event)',
                        '(@fadeInOut.done)': 'onAnimationDone($event)'
                    }, template: "@if (visible || !hide()) {\n  @if (dismissible) {\n    <ng-container *ngTemplateOutlet=\"templates()?.['alertButtonCloseTemplate'] || defaultAlertButtonCloseTemplate\" />\n  }\n  <ng-content />\n}\n<ng-template #defaultAlertButtonCloseTemplate>\n  <button (click)=\"visible=false\" aria-label=\"Close\" cButtonClose></button>\n</ng-template>\n", styles: [":host{display:block;overflow:hidden}\n"] }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], dismissibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "dismissible", required: false }] }], fade: [{ type: i0.Input, args: [{ isSignal: true, alias: "fade", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class AlertModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: AlertModule, imports: [AlertComponent,
            AlertHeadingDirective,
            AlertLinkDirective], exports: [AlertComponent,
            AlertHeadingDirective,
            AlertLinkDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        AlertComponent,
                        AlertHeadingDirective,
                        AlertLinkDirective
                    ],
                    exports: [
                        AlertComponent,
                        AlertHeadingDirective,
                        AlertLinkDirective
                    ]
                }]
        }] });

class AlignDirective {
    /**
     * Set vertical alignment of inline, inline-block, inline-table, and table cell elements
     * @return Alignment
     */
    align = input(undefined, { ...(ngDevMode ? { debugName: "align" } : {}), alias: 'cAlign' });
    hostClasses = computed(() => {
        const align = this.align();
        return {
            [`align-${align}`]: !!align
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlignDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: AlignDirective, isStandalone: true, selector: "[cAlign]", inputs: { align: { classPropertyName: "align", publicName: "cAlign", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["cAlign"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AlignDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cAlign]',
                    exportAs: 'cAlign',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { align: [{ type: i0.Input, args: [{ isSignal: true, alias: "cAlign", required: false }] }] } });

class BgColorDirective {
    /**
     * Set the background of an element to any contextual class
     */
    cBgColor = input('', ...(ngDevMode ? [{ debugName: "cBgColor" }] : []));
    /**
     * Add linear gradient as background image to the backgrounds.
     * @return boolean
     */
    gradient = input(...(ngDevMode ? [undefined, { debugName: "gradient" }] : []));
    hostClasses = computed(() => {
        const color = this.cBgColor();
        return {
            [`bg-${color}`]: !!color,
            'bg-gradient': this.gradient()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BgColorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: BgColorDirective, isStandalone: true, selector: "[cBgColor]", inputs: { cBgColor: { classPropertyName: "cBgColor", publicName: "cBgColor", isSignal: true, isRequired: false, transformFunction: null }, gradient: { classPropertyName: "gradient", publicName: "gradient", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["cBgColor"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BgColorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cBgColor]',
                    exportAs: 'cBgColor',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { cBgColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "cBgColor", required: false }] }], gradient: [{ type: i0.Input, args: [{ isSignal: true, alias: "gradient", required: false }] }] } });

class BorderDirective {
    /**
     * Add or remove an element’s borders
     * @return Border
     */
    cBorder = input(true, ...(ngDevMode ? [{ debugName: "cBorder" }] : []));
    hostClasses = computed(() => {
        const border = this.cBorder();
        if (typeof border === 'boolean') {
            return { border: border };
        }
        if (typeof border === 'number' || typeof border === 'string') {
            return {
                border: true,
                [`border-${border}`]: true
            };
        }
        if (typeof border === 'object') {
            const borderObj = {
                top: undefined,
                end: undefined,
                bottom: undefined,
                start: undefined,
                color: undefined,
                ...border
            };
            // @ts-ignore
            const keys = Object.keys(borderObj).filter((key) => borderObj[key] !== undefined);
            const classes = {};
            keys.forEach((key) => {
                // @ts-ignore
                const val = borderObj[key];
                if (typeof val === 'boolean') {
                    // @ts-ignore
                    classes[`border-${key}`] = true;
                }
                else if (typeof val === 'number' || typeof val === 'string') {
                    // @ts-ignore
                    classes[`border-${key}-${val}`] = true;
                }
                else if (typeof val === 'object') {
                    if ('color' in val) {
                        // @ts-ignore
                        classes[`border-${key}-${val.color}`] = true;
                    }
                    if ('width' in val) {
                        // @ts-ignore
                        classes[`border-${key}-${val.width}`] = true;
                    }
                }
            });
            return Object.entries(classes).length === 0 ? { border: false } : classes;
        }
        return { border: false };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BorderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: BorderDirective, isStandalone: true, selector: "[cBorder]", inputs: { cBorder: { classPropertyName: "cBorder", publicName: "cBorder", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["cBorder"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BorderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cBorder]',
                    exportAs: 'cBorder',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { cBorder: [{ type: i0.Input, args: [{ isSignal: true, alias: "cBorder", required: false }] }] } });

class RoundedDirective {
    /**
     * Set border radius variant and radius size
     * @type Rounded
     */
    cRounded = input(true, ...(ngDevMode ? [{ debugName: "cRounded" }] : []));
    hostClasses = computed(() => {
        const rounded = this.cRounded();
        if (typeof rounded === 'boolean') {
            return { rounded: rounded };
        }
        if (typeof rounded === 'number' || typeof rounded === 'string') {
            return {
                [`rounded-${rounded}`]: true
            };
        }
        if (typeof rounded === 'object') {
            const roundedObj = {
                top: undefined,
                end: undefined,
                bottom: undefined,
                start: undefined,
                circle: undefined,
                pill: undefined,
                size: undefined,
                ...rounded
            };
            // @ts-ignore
            const keys = Object.keys(roundedObj).filter((key) => roundedObj[key] !== undefined);
            const classes = {};
            keys.forEach((key) => {
                // @ts-ignore
                const val = roundedObj[key];
                if (typeof val === 'boolean') {
                    // @ts-ignore
                    classes[`rounded-${key}`] = val;
                }
                else {
                    // @ts-ignore
                    classes[`rounded-${val}`] = true;
                }
            });
            // console.log('rounded keys', keys, classes);
            return Object.entries(classes).length === 0 ? { rounded: false } : classes;
        }
        return { rounded: false };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RoundedDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: RoundedDirective, isStandalone: true, selector: "[cRounded]", inputs: { cRounded: { classPropertyName: "cRounded", publicName: "cRounded", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["cRounded"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RoundedDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cRounded]',
                    exportAs: 'cRounded',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { cRounded: [{ type: i0.Input, args: [{ isSignal: true, alias: "cRounded", required: false }] }] } });

class ShadowOnScrollDirective {
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #elementRef = inject(ElementRef);
    #scrolled = signal(false, ...(ngDevMode ? [{ debugName: "#scrolled" }] : []));
    #scrollEffect = effect(() => {
        this.#elementRef.nativeElement.classList.toggle(this.#shadowClass, this.#scrolled());
    }, ...(ngDevMode ? [{ debugName: "#scrollEffect" }] : []));
    #observable;
    #shadowClass = 'shadow-sm';
    constructor() {
        this.#destroyRef.onDestroy(() => {
            this.#scrollEffect?.destroy();
        });
    }
    cShadowOnScroll = input(true, ...(ngDevMode ? [{ debugName: "cShadowOnScroll" }] : []));
    #shadowOnScrollEffect = effect(() => {
        const value = this.cShadowOnScroll();
        untracked(() => {
            this.#scrolled.set(false);
            if (value) {
                this.#shadowClass = value === true ? 'shadow' : `shadow-${value}`;
                this.#observable = fromEvent(this.#document, 'scroll')
                    .pipe(takeUntilDestroyed(this.#destroyRef))
                    .subscribe((scrolled) => {
                    this.#scrolled.set(this.#document.documentElement.scrollTop > 0);
                });
            }
            else {
                this.#observable?.unsubscribe();
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#shadowOnScrollEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ShadowOnScrollDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ShadowOnScrollDirective, isStandalone: true, selector: "[cShadowOnScroll]", inputs: { cShadowOnScroll: { classPropertyName: "cShadowOnScroll", publicName: "cShadowOnScroll", isSignal: true, isRequired: false, transformFunction: null } }, exportAs: ["cShadowOnScroll"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ShadowOnScrollDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cShadowOnScroll]',
                    exportAs: 'cShadowOnScroll'
                }]
        }], ctorParameters: () => [], propDecorators: { cShadowOnScroll: [{ type: i0.Input, args: [{ isSignal: true, alias: "cShadowOnScroll", required: false }] }] } });

class TextColorDirective {
    /**
     * Set text-color of element
     * @type TextColors
     */
    color = input('', { ...(ngDevMode ? { debugName: "color" } : {}), alias: 'cTextColor' });
    hostClasses = computed(() => {
        const color = this.color();
        return {
            [`text-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TextColorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TextColorDirective, isStandalone: true, selector: "[cTextColor]", inputs: { color: { classPropertyName: "color", publicName: "cTextColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TextColorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTextColor]',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTextColor", required: false }] }] } });

class TextBgColorDirective {
    /**
     * Set text-bg-color of element
     * @type Colors
     */
    textBgColor = input('', { ...(ngDevMode ? { debugName: "textBgColor" } : {}), alias: 'cTextBgColor' });
    hostClasses = computed(() => {
        const color = this.textBgColor();
        return {
            [`text-bg-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TextBgColorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TextBgColorDirective, isStandalone: true, selector: "[cTextBgColor]", inputs: { textBgColor: { classPropertyName: "textBgColor", publicName: "cTextBgColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TextBgColorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTextBgColor]',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { textBgColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTextBgColor", required: false }] }] } });

const UTILITY_DIRECTIVES = [
    AlignDirective,
    BgColorDirective,
    BorderDirective,
    RoundedDirective,
    ShadowOnScrollDirective,
    TextColorDirective,
    TextBgColorDirective
];
class UtilitiesModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UtilitiesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: UtilitiesModule, imports: [AlignDirective,
            BgColorDirective,
            BorderDirective,
            RoundedDirective,
            ShadowOnScrollDirective,
            TextColorDirective,
            TextBgColorDirective], exports: [AlignDirective,
            BgColorDirective,
            BorderDirective,
            RoundedDirective,
            ShadowOnScrollDirective,
            TextColorDirective,
            TextBgColorDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UtilitiesModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: UtilitiesModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...UTILITY_DIRECTIVES],
                    exports: [...UTILITY_DIRECTIVES]
                }]
        }] });

class AvatarComponent {
    /**
     * Sets the background color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Select the shape of the component.
     * @type Shapes
     */
    shape = input(...(ngDevMode ? [undefined, { debugName: "shape" }] : []));
    /**
     * Size the component small, large, or extra large.
     * @default 'md'
     */
    size = input('', ...(ngDevMode ? [{ debugName: "size" }] : []));
    /**
     * The alt attribute for the img element alternate text.
     * @type string
     */
    alt = input('', ...(ngDevMode ? [{ debugName: "alt" }] : []));
    /**
     * The src attribute for the img element.
     * @type string
     */
    src = input(...(ngDevMode ? [undefined, { debugName: "src" }] : []));
    /**
     * Sets the color context of the status indicator to one of CoreUI’s themed colors.
     * @type Colors
     */
    status = input(...(ngDevMode ? [undefined, { debugName: "status" }] : []));
    /**
     * Sets the text color of the component to one of CoreUI’s themed colors.
     * via TextColorDirective
     * @type TextColors
     */
    textColor = input(...(ngDevMode ? [undefined, { debugName: "textColor" }] : []));
    statusClass = computed(() => {
        return {
            'avatar-status': true,
            [`bg-${this.status()}`]: !!this.status()
        };
    }, ...(ngDevMode ? [{ debugName: "statusClass" }] : []));
    hostClasses = computed(() => {
        const size = this.size();
        const color = this.color();
        const shape = this.shape();
        return {
            avatar: true,
            [`avatar-${size}`]: !!size,
            [`bg-${color}`]: !!color,
            [`${shape}`]: !!shape
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AvatarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: AvatarComponent, isStandalone: true, selector: "c-avatar", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, shape: { classPropertyName: "shape", publicName: "shape", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, alt: { classPropertyName: "alt", publicName: "alt", isSignal: true, isRequired: false, transformFunction: null }, src: { classPropertyName: "src", publicName: "src", isSignal: true, isRequired: false, transformFunction: null }, status: { classPropertyName: "status", publicName: "status", isSignal: true, isRequired: false, transformFunction: null }, textColor: { classPropertyName: "textColor", publicName: "textColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "avatar" }, hostDirectives: [{ directive: TextColorDirective, inputs: ["cTextColor", "textColor"] }], ngImport: i0, template: "<ng-content>\n  @if (src()) {\n    @defer (prefetch on idle) {\n      <img src=\"{{src() ?? ''}}\" class=\"avatar-img\" alt=\"{{alt()}}\" />\n    } @placeholder () {\n      <svg aria-label=\"Avatar placeholder\"\n           focusable=\"false\"\n           preserveAspectRatio=\"xMidYMid slice\"\n           role=\"img\"\n           xmlns=\"http://www.w3.org/2000/svg\"\n           class=\"avatar-img\"\n           style=\"position: absolute; width: 100%; height: 100%; inset: 0;\">\n        <rect fill=\"#868e96\" height=\"100%\" width=\"100%\"></rect>\n      </svg>\n    }\n  }\n</ng-content>\n@if (!!status()) {\n  <span [class]=\"statusClass()\"></span>\n}\n\n", styles: [":host .avatar-img{object-fit:cover}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AvatarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-avatar', hostDirectives: [
                        {
                            directive: TextColorDirective,
                            inputs: ['cTextColor: textColor']
                        }
                    ], host: { class: 'avatar', '[class]': 'hostClasses()' }, template: "<ng-content>\n  @if (src()) {\n    @defer (prefetch on idle) {\n      <img src=\"{{src() ?? ''}}\" class=\"avatar-img\" alt=\"{{alt()}}\" />\n    } @placeholder () {\n      <svg aria-label=\"Avatar placeholder\"\n           focusable=\"false\"\n           preserveAspectRatio=\"xMidYMid slice\"\n           role=\"img\"\n           xmlns=\"http://www.w3.org/2000/svg\"\n           class=\"avatar-img\"\n           style=\"position: absolute; width: 100%; height: 100%; inset: 0;\">\n        <rect fill=\"#868e96\" height=\"100%\" width=\"100%\"></rect>\n      </svg>\n    }\n  }\n</ng-content>\n@if (!!status()) {\n  <span [class]=\"statusClass()\"></span>\n}\n\n", styles: [":host .avatar-img{object-fit:cover}\n"] }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], shape: [{ type: i0.Input, args: [{ isSignal: true, alias: "shape", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], alt: [{ type: i0.Input, args: [{ isSignal: true, alias: "alt", required: false }] }], src: [{ type: i0.Input, args: [{ isSignal: true, alias: "src", required: false }] }], status: [{ type: i0.Input, args: [{ isSignal: true, alias: "status", required: false }] }], textColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "textColor", required: false }] }] } });

class AvatarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AvatarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: AvatarModule, imports: [AvatarComponent], exports: [AvatarComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AvatarModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: AvatarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        AvatarComponent
                    ],
                    exports: [
                        AvatarComponent
                    ]
                }]
        }] });

class BadgeComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Position badge in one of the corners of a link or button.
     * @type BadgePositions
     */
    position = input(...(ngDevMode ? [undefined, { debugName: "position" }] : []));
    /**
     * Select the shape of the component.
     * @type Shapes
     */
    shape = input(...(ngDevMode ? [undefined, { debugName: "shape" }] : []));
    /**
     * Size the component small.
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Sets the text color of the component to one of CoreUI’s themed colors.
     * via TextColorDirective
     * @type TextColors
     */
    textColor = input(...(ngDevMode ? [undefined, { debugName: "textColor" }] : []));
    /**
     * Sets the component's color scheme to one of CoreUI's themed colors, ensuring the text color contrast adheres to the WCAG 4.5:1 contrast ratio standard for accessibility.
     * via TextBgColorDirective
     * @type Colors
     * @since 5.0.0
     */
    textBgColor = input(...(ngDevMode ? [undefined, { debugName: "textBgColor" }] : []));
    hostClasses = computed(() => {
        const position = this.position();
        const positionClasses = {
            'position-absolute': !!position,
            'translate-middle': !!position,
            'top-0': position?.includes('top'),
            'top-100': position?.includes('bottom'),
            'start-100': position?.includes('end'),
            'start-0': position?.includes('start')
        };
        const color = this.color();
        const size = this.size();
        const shape = this.shape();
        return Object.assign({
            badge: true,
            [`bg-${color}`]: !!color,
            [`badge-${size}`]: !!size,
            [`${shape}`]: !!shape
        }, !!position ? positionClasses : {});
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BadgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: BadgeComponent, isStandalone: true, selector: "c-badge", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, position: { classPropertyName: "position", publicName: "position", isSignal: true, isRequired: false, transformFunction: null }, shape: { classPropertyName: "shape", publicName: "shape", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, textColor: { classPropertyName: "textColor", publicName: "textColor", isSignal: true, isRequired: false, transformFunction: null }, textBgColor: { classPropertyName: "textBgColor", publicName: "textBgColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "badge" }, hostDirectives: [{ directive: TextColorDirective, inputs: ["cTextColor", "textColor"] }, { directive: TextBgColorDirective, inputs: ["cTextBgColor", "textBgColor"] }], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BadgeComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-badge',
                    template: '<ng-content />',
                    hostDirectives: [
                        { directive: TextColorDirective, inputs: ['cTextColor: textColor'] },
                        { directive: TextBgColorDirective, inputs: ['cTextBgColor: textBgColor'] }
                    ],
                    host: {
                        class: 'badge',
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], position: [{ type: i0.Input, args: [{ isSignal: true, alias: "position", required: false }] }], shape: [{ type: i0.Input, args: [{ isSignal: true, alias: "shape", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], textColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "textColor", required: false }] }], textBgColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "textBgColor", required: false }] }] } });

class BadgeModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: BadgeModule, imports: [BadgeComponent], exports: [BadgeComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BadgeModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        BadgeComponent
                    ],
                    exports: [
                        BadgeComponent
                    ]
                }]
        }] });

class BackdropService {
    #backdropClick = new Subject();
    backdropClick$ = this.#backdropClick.asObservable();
    #document = inject(DOCUMENT);
    activeBackdrop;
    get #scrollbarWidth() {
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth#usage_notes
        const documentWidth = this.#document.documentElement.clientWidth;
        const scrollbarWidth = Math.abs((this.#document.defaultView?.innerWidth ?? documentWidth) - documentWidth);
        return `${scrollbarWidth}px`;
    }
    scrollbarWidth = this.#scrollbarWidth;
    setBackdrop(type = 'modal') {
        const backdropElement = this.#document.createElement('div');
        backdropElement.classList.add(`${type}-backdrop`);
        backdropElement.classList.add('fade');
        this.#document.body.appendChild(backdropElement);
        const clickHandler = () => {
            this.onClickHandler();
        };
        backdropElement.__backdropClickHandler = clickHandler;
        backdropElement.addEventListener('click', clickHandler);
        this.scrollbarWidth = this.#scrollbarWidth;
        setTimeout(() => {
            backdropElement.classList.add('show');
            // this.hideScrollbar();
        });
        this.activeBackdrop = backdropElement;
        return backdropElement;
    }
    clearBackdrop(backdropElement) {
        if (backdropElement) {
            const storedHandler = backdropElement.__backdropClickHandler;
            if (storedHandler) {
                backdropElement.removeEventListener('click', storedHandler);
                delete backdropElement.__backdropClickHandler;
            }
            backdropElement.classList.remove('show');
            setTimeout(() => {
                if (this.activeBackdrop === backdropElement) {
                    this.resetScrollbar();
                }
                if (backdropElement.parentElement === this.#document.body) {
                    this.#document.body.removeChild(backdropElement);
                }
            }, 300);
        }
        return undefined;
    }
    get #isRTL() {
        return [this.#document.documentElement.dir, this.#document.body.dir].includes('rtl');
    }
    get #padding() {
        return `padding-${this.#isRTL ? 'left' : 'right'}`;
    }
    #scrollBarVisible = true;
    hideScrollbar() {
        if (this.#scrollBarVisible) {
            this.#document.body.style.setProperty('overflow', 'hidden');
            this.#document.body.style.setProperty(this.#padding, this.scrollbarWidth);
            this.#scrollBarVisible = false;
        }
    }
    resetScrollbar() {
        this.#document.body.style.removeProperty('overflow');
        this.#document.body.style.removeProperty(this.#padding);
        this.#scrollBarVisible = true;
    }
    onClickHandler() {
        this.#backdropClick.next(true);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BackdropService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BackdropService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BackdropService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class BreadcrumbItemComponent {
    /**
     * Toggle the active state for the component. [docs]
     * @return boolean
     */
    active = input(undefined, { ...(ngDevMode ? { debugName: "active" } : {}), transform: booleanAttribute });
    /**
     * The `url` prop for the inner `[routerLink]` directive. [docs]
     * @return string
     */
    url = input(...(ngDevMode ? [undefined, { debugName: "url" }] : []));
    /**
     * Additional html attributes for link. [docs]
     * @return INavAttributes
     */
    attribs = input(...(ngDevMode ? [undefined, { debugName: "attribs" }] : []));
    _attributes = input(undefined, { ...(ngDevMode ? { debugName: "_attributes" } : {}), alias: 'attributes' });
    #attributesEffect = effect(() => {
        if (this._attributes()) {
            console.error('c-breadcrumb-item: [attributes] prop is removed, use [attribs] instead:', this._attributes());
        }
    }, ...(ngDevMode ? [{ debugName: "#attributesEffect" }] : []));
    /**
     * Some `NavigationExtras` props for the inner `[routerLink]` directive and `routerLinkActiveOptions`. [docs]
     * @return INavLinkProps
     */
    linkProps = input(...(ngDevMode ? [undefined, { debugName: "linkProps" }] : []));
    ariaCurrent = computed(() => {
        return this.active() ? 'page' : null;
    }, ...(ngDevMode ? [{ debugName: "ariaCurrent" }] : []));
    hostClasses = computed(() => {
        return {
            'breadcrumb-item': true,
            active: this.active()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: BreadcrumbItemComponent, isStandalone: true, selector: "c-breadcrumb-item", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, url: { classPropertyName: "url", publicName: "url", isSignal: true, isRequired: false, transformFunction: null }, attribs: { classPropertyName: "attribs", publicName: "attribs", isSignal: true, isRequired: false, transformFunction: null }, _attributes: { classPropertyName: "_attributes", publicName: "attributes", isSignal: true, isRequired: false, transformFunction: null }, linkProps: { classPropertyName: "linkProps", publicName: "linkProps", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.aria-current": "ariaCurrent()", "class": "hostClasses()" } }, exportAs: ["breadcrumbItem"], ngImport: i0, template: "@if (!active()) {\n  <a [routerLink]=\"url()\"\n     [cHtmlAttr]=\"attribs() ?? {}\"\n     [target]=\"attribs()?.['target']\"\n     [queryParams]=\"linkProps()?.queryParams ?? null\"\n     [fragment]=\"linkProps()?.fragment\"\n     [queryParamsHandling]=\"linkProps()?.queryParamsHandling ?? null\"\n     [preserveFragment]=\"linkProps()?.preserveFragment ?? false\"\n     [skipLocationChange]=\"linkProps()?.skipLocationChange ?? false\"\n     [replaceUrl]=\"linkProps()?.replaceUrl ?? false\"\n     [state]=\"linkProps()?.state ?? {}\"\n  >\n    <ng-container *ngTemplateOutlet=\"defaultBreadcrumbItemContentTemplate\" />\n  </a>\n} @else {\n  <span [cHtmlAttr]=\"attribs() ?? {}\">\n      <ng-container *ngTemplateOutlet=\"defaultBreadcrumbItemContentTemplate\" />\n  </span>\n}\n\n<ng-template #defaultBreadcrumbItemContentTemplate>\n  <ng-content />\n</ng-template>\n", styles: [":host{display:list-item;text-align:-webkit-match-parent;text-align:match-parent}\n"], dependencies: [{ kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-breadcrumb-item', imports: [RouterModule, NgTemplateOutlet, HtmlAttributesDirective], exportAs: 'breadcrumbItem', host: {
                        '[attr.aria-current]': 'ariaCurrent()',
                        '[class]': 'hostClasses()'
                    }, template: "@if (!active()) {\n  <a [routerLink]=\"url()\"\n     [cHtmlAttr]=\"attribs() ?? {}\"\n     [target]=\"attribs()?.['target']\"\n     [queryParams]=\"linkProps()?.queryParams ?? null\"\n     [fragment]=\"linkProps()?.fragment\"\n     [queryParamsHandling]=\"linkProps()?.queryParamsHandling ?? null\"\n     [preserveFragment]=\"linkProps()?.preserveFragment ?? false\"\n     [skipLocationChange]=\"linkProps()?.skipLocationChange ?? false\"\n     [replaceUrl]=\"linkProps()?.replaceUrl ?? false\"\n     [state]=\"linkProps()?.state ?? {}\"\n  >\n    <ng-container *ngTemplateOutlet=\"defaultBreadcrumbItemContentTemplate\" />\n  </a>\n} @else {\n  <span [cHtmlAttr]=\"attribs() ?? {}\">\n      <ng-container *ngTemplateOutlet=\"defaultBreadcrumbItemContentTemplate\" />\n  </span>\n}\n\n<ng-template #defaultBreadcrumbItemContentTemplate>\n  <ng-content />\n</ng-template>\n", styles: [":host{display:list-item;text-align:-webkit-match-parent;text-align:match-parent}\n"] }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], url: [{ type: i0.Input, args: [{ isSignal: true, alias: "url", required: false }] }], attribs: [{ type: i0.Input, args: [{ isSignal: true, alias: "attribs", required: false }] }], _attributes: [{ type: i0.Input, args: [{ isSignal: true, alias: "attributes", required: false }] }], linkProps: [{ type: i0.Input, args: [{ isSignal: true, alias: "linkProps", required: false }] }] } });

class BreadcrumbComponent {
    /**
     * Default aria-label for breadcrumb. [docs]
     * @return string
     * @default 'breadcrumb'
     */
    ariaLabel = input('breadcrumb', ...(ngDevMode ? [{ debugName: "ariaLabel" }] : []));
    /**
     * Default role for breadcrumb. [docs]
     * @return string
     * @default 'navigation'
     */
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: BreadcrumbComponent, isStandalone: true, selector: "c-breadcrumb", inputs: { ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.aria-label": "ariaLabel()", "attr.role": "role()" }, classAttribute: "breadcrumb" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-breadcrumb',
                    template: '<ng-content />',
                    host: {
                        class: 'breadcrumb',
                        '[attr.aria-label]': 'ariaLabel()',
                        '[attr.role]': 'role()'
                    }
                }]
        }], propDecorators: { ariaLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabel", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class BreadcrumbRouterService {
    #router = inject(Router);
    #activatedRoute = inject(ActivatedRoute);
    outlet = 'primary';
    #breadcrumbsBehaviorSubject = new BehaviorSubject(new Array());
    breadcrumbs$ = this.#breadcrumbsBehaviorSubject.asObservable();
    constructor() {
        this.#router.events
            .pipe(takeUntilDestroyed(), filter((event) => event instanceof NavigationEnd))
            .subscribe((event) => {
            const breadcrumbs = [];
            let currentRoute = this.#activatedRoute.root;
            let url = '';
            do {
                const childrenRoutes = currentRoute.children;
                currentRoute = null;
                childrenRoutes.forEach((childRoute) => {
                    // console.log('breadcrumb event', event, 'route', route);
                    if (childRoute.outlet === this.outlet) {
                        const routeSnapshot = childRoute.snapshot;
                        url += '/' + routeSnapshot.url.map((segment) => segment.path).join('/');
                        breadcrumbs.push({
                            label: routeSnapshot.data['title'] ?? routeSnapshot.title ?? '',
                            url,
                            queryParams: routeSnapshot.queryParams
                        });
                        currentRoute = childRoute;
                    }
                });
            } while (currentRoute);
            this.#breadcrumbsBehaviorSubject.next(Object.assign([], breadcrumbs));
            return breadcrumbs;
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbRouterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbRouterService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbRouterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class BreadcrumbRouterComponent {
    #breadcrumbRouterService = inject(BreadcrumbRouterService);
    /**
     * Optional array of IBreadcrumbItem to override default BreadcrumbRouter behavior. [docs]
     * @return IBreadcrumbItem[]
     */
    items = input(...(ngDevMode ? [undefined, { debugName: "items" }] : []));
    #breadcrumbs = toSignal(this.#breadcrumbRouterService.breadcrumbs$);
    breadcrumbs = computed(() => {
        return this.items() ?? this.#breadcrumbs() ?? [];
    }, ...(ngDevMode ? [{ debugName: "breadcrumbs" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbRouterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: BreadcrumbRouterComponent, isStandalone: true, selector: "c-breadcrumb-router, [cBreadcrumbRouter]", inputs: { items: { classPropertyName: "items", publicName: "items", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<c-breadcrumb class=\"m-0\">\n  @for (breadcrumb of breadcrumbs(); track breadcrumb; let last = $last) {\n    @if (breadcrumb?.label && (breadcrumb?.url?.slice(-1) === '/' || last)) {\n      <c-breadcrumb-item\n        [active]=\"last\"\n        [url]=\"breadcrumb?.url\"\n        [attribs]=\"breadcrumb?.attributes\"\n        [linkProps]=\"breadcrumb?.linkProps\"\n      >\n        {{ breadcrumb?.label }}\n      </c-breadcrumb-item>\n    }\n  }\n</c-breadcrumb>\n", dependencies: [{ kind: "component", type: BreadcrumbComponent, selector: "c-breadcrumb", inputs: ["ariaLabel", "role"] }, { kind: "component", type: BreadcrumbItemComponent, selector: "c-breadcrumb-item", inputs: ["active", "url", "attribs", "attributes", "linkProps"], exportAs: ["breadcrumbItem"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbRouterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-breadcrumb-router, [cBreadcrumbRouter]', imports: [BreadcrumbComponent, BreadcrumbItemComponent], template: "<c-breadcrumb class=\"m-0\">\n  @for (breadcrumb of breadcrumbs(); track breadcrumb; let last = $last) {\n    @if (breadcrumb?.label && (breadcrumb?.url?.slice(-1) === '/' || last)) {\n      <c-breadcrumb-item\n        [active]=\"last\"\n        [url]=\"breadcrumb?.url\"\n        [attribs]=\"breadcrumb?.attributes\"\n        [linkProps]=\"breadcrumb?.linkProps\"\n      >\n        {{ breadcrumb?.label }}\n      </c-breadcrumb-item>\n    }\n  }\n</c-breadcrumb>\n" }]
        }], propDecorators: { items: [{ type: i0.Input, args: [{ isSignal: true, alias: "items", required: false }] }] } });

class BreadcrumbModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbModule, imports: [BreadcrumbComponent,
            BreadcrumbItemComponent,
            BreadcrumbRouterComponent], exports: [BreadcrumbComponent,
            BreadcrumbItemComponent,
            BreadcrumbRouterComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbModule, providers: [BreadcrumbRouterService], imports: [BreadcrumbItemComponent,
            BreadcrumbRouterComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: BreadcrumbModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        BreadcrumbComponent,
                        BreadcrumbItemComponent,
                        BreadcrumbRouterComponent
                    ],
                    exports: [
                        BreadcrumbComponent,
                        BreadcrumbItemComponent,
                        BreadcrumbRouterComponent
                    ],
                    providers: [BreadcrumbRouterService]
                }]
        }] });

class ButtonGroupComponent {
    /**
     * Size the component small or large.
     * @return { 'sm' | 'lg' }
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Create a set of buttons that appear vertically stacked rather than horizontally. Split button dropdowns are not supported here.
     * @type boolean
     */
    vertical = input(false, { ...(ngDevMode ? { debugName: "vertical" } : {}), transform: booleanAttribute });
    /**
     * Default role attr for ButtonGroup. [docs]
     * @return string
     * @default 'group'
     */
    role = input('group', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        const size = this.size();
        const vertical = this.vertical();
        return {
            'btn-group': !vertical,
            'btn-group-vertical': vertical,
            [`btn-group-${size}`]: !!size
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ButtonGroupComponent, isStandalone: true, selector: "c-button-group", inputs: { size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, vertical: { classPropertyName: "vertical", publicName: "vertical", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()", "class": "hostClasses()" } }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-button-group',
                    template: '<ng-content />',
                    host: { '[attr.role]': 'role()', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], vertical: [{ type: i0.Input, args: [{ isSignal: true, alias: "vertical", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class ButtonToolbarComponent {
    /**
     * Default role attr for ButtonToolbar. [docs]
     * @type InputSignal<string>
     * @default 'toolbar'
     */
    role = input('toolbar', ...(ngDevMode ? [{ debugName: "role" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonToolbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ButtonToolbarComponent, isStandalone: true, selector: "c-button-toolbar", inputs: { role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()" }, classAttribute: "btn-toolbar" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonToolbarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-button-toolbar',
                    template: '<ng-content />',
                    host: { class: 'btn-toolbar', '[attr.role]': 'role()' }
                }]
        }], propDecorators: { role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class ButtonGroupModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupModule, imports: [ButtonGroupComponent, ButtonToolbarComponent], exports: [ButtonGroupComponent, ButtonToolbarComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ButtonGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ButtonGroupComponent, ButtonToolbarComponent],
                    exports: [ButtonGroupComponent, ButtonToolbarComponent]
                }]
        }] });

function dateTransform(value) {
    return tryDate(value, 'dateTransform');
}
const convertIsoWeekToDate = (isoWeek) => {
    const [year, week] = isoWeek.split(/w/i);
    // Get date for 4th of January for year
    const date = new Date(parseInt(year), 0, 4);
    // Get previous Monday, add 7 days for each week after first
    date.setDate(date.getDate() - (date.getDay() || 7) + 1 + (parseInt(week ?? 1) - 1) * 7);
    return date;
};
const convertToDateObject = (date, selectionType, propName) => {
    if (date instanceof Date) {
        return new Date(date);
    }
    if (isValidDate(date)) {
        return new Date(date);
    }
    if (selectionType === 'week') {
        return convertIsoWeekToDate(date);
    }
    return tryDate(date, propName ?? 'convertToDateObject');
    // return new Date(Date.parse(date));
};
const convertToLocalDate = (d, locale, options = {}) => d.toLocaleDateString(locale, options);
const convertToLocalTime = (d, locale, options = {}) => d.toLocaleTimeString(locale, options);
const createGroupsInArray = (arr, numberOfGroups) => {
    const perGroup = Math.ceil(arr.length / numberOfGroups);
    return Array.from({ length: numberOfGroups })
        .fill('')
        .map((_, i) => arr.slice(i * perGroup, (i + 1) * perGroup));
};
const getCalendarDate = (calendarDate, order, view) => {
    if (order !== 0 && view === 'days') {
        return new Date(calendarDate.getFullYear(), calendarDate.getMonth() + order, 1);
    }
    if (order !== 0 && view === 'months') {
        return new Date(calendarDate.getFullYear() + order, calendarDate.getMonth(), 1);
    }
    if (order !== 0 && view === 'years') {
        return new Date(calendarDate.getFullYear() + 12 * order, calendarDate.getMonth(), 1);
    }
    return calendarDate;
};
const getCurrentYear = () => new Date().getFullYear();
const getCurrentMonth = () => new Date().getMonth();
const getDateBySelectionType = (date, selectionType) => {
    if (date === null) {
        return null;
    }
    if (selectionType === 'week') {
        return `${date.getFullYear()}W${getWeekNumber(date)}`;
    }
    if (selectionType === 'month') {
        const monthNumber = `0${date.getMonth() + 1}`.slice(-2);
        return `${date.getFullYear()}-${monthNumber}`;
    }
    if (selectionType === 'year') {
        return `${date.getFullYear()}`;
    }
    return date;
};
//
const getLocalDateFromString = (string, locale, time) => {
    const date = new Date(2013, 11, 31, 17, 19, 22);
    let regex = time ? date.toLocaleString(locale) : date.toLocaleDateString(locale);
    regex = regex
        .replace('2013', '(?<year>[0-9]{2,4})')
        .replace('12', '(?<month>[0-9]{1,2})')
        .replace('31', '(?<day>[0-9]{1,2})');
    if (time) {
        regex = regex
            .replace('5', '(?<hour>[0-9]{1,2})')
            .replace('17', '(?<hour>[0-9]{1,2})')
            .replace('19', '(?<minute>[0-9]{1,2})')
            .replace('22', '(?<second>[0-9]{1,2})')
            .replace('PM', '(?<ampm>[A-Z]{2})');
    }
    const rgx = RegExp(`${regex}`);
    const partials = string.match(rgx);
    if (partials === null) {
        return;
    }
    const newDate = partials.groups &&
        (time
            ? new Date(Number(partials.groups['year']), Number(partials.groups['month']) - 1, Number(partials.groups['day']), partials.groups['ampm']
                ? partials.groups['ampm'] === 'PM'
                    ? Number(partials.groups['hour']) + 12
                    : Number(partials.groups['hour'])
                : Number(partials.groups['hour']), Number(partials.groups['minute']), Number(partials.groups['second']))
            : new Date(Number(partials.groups['year']), Number(partials.groups['month']) - 1, Number(partials.groups['day'])));
    return newDate;
};
const getMonthName = (month, locale) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(month);
    return d.toLocaleString(locale, { month: 'long' });
};
const getMonthsNames = (locale, year) => {
    const months = [];
    const d = new Date(year);
    d.setDate(1);
    for (let i = 0; i < 12; i++) {
        d.setMonth(i);
        months.push({ year, month: d.toLocaleDateString(locale, { month: 'short' }) });
    }
    return months;
};
const getYears = (year) => {
    const years = [];
    for (let _year = year - 6; _year < year + 6; _year++) {
        years.push(_year);
    }
    return years;
};
const getLeadingDays = (year, month, firstDayOfWeek) => {
    // 0: sunday
    // 1: monday
    const dates = [];
    const d = new Date(year, month);
    const y = d.getFullYear();
    const m = d.getMonth();
    const firstWeekday = new Date(y, m, 1).getDay();
    let leadingDays = 6 - (6 - firstWeekday) - firstDayOfWeek;
    if (firstDayOfWeek) {
        leadingDays = leadingDays < 0 ? 7 + leadingDays : leadingDays;
    }
    for (let i = leadingDays * -1; i < 0; i++) {
        dates.push({
            date: new Date(y, m, i + 1),
            month: 'previous'
        });
    }
    return dates;
};
const getMonthDays = (year, month) => {
    const dates = [];
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
        dates.push({
            date: new Date(year, month, i),
            month: 'current'
        });
    }
    return dates;
};
const getTrailingDays = (year, month, leadingDays, monthDays) => {
    const dates = [];
    const days = 42 - (leadingDays.length + monthDays.length);
    for (let i = 1; i <= days; i++) {
        dates.push({
            date: new Date(year, month + 1, i),
            month: 'next'
        });
    }
    return dates;
};
const getDayNumber = (date) => Math.ceil((Number(date) - Number(new Date(date.getFullYear(), 0, 0))) / 1000 / 60 / 60 / 24);
const getWeekNumber = (date) => {
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
};
const getMonthDetails = (year, month, firstDayOfWeek) => {
    const daysPrevMonth = getLeadingDays(year, month, firstDayOfWeek);
    const daysThisMonth = getMonthDays(year, month);
    const daysNextMonth = getTrailingDays(year, month, daysPrevMonth, daysThisMonth);
    const days = [...daysPrevMonth, ...daysThisMonth, ...daysNextMonth];
    const weeks = [];
    days.forEach((day, index) => {
        if (index % 7 === 0 || weeks.length === 0) {
            weeks.push({ weekNumber: -1, days: [] });
        }
        if ((index + 1) % 7 === 0) {
            weeks[weeks.length - 1].weekNumber = getWeekNumber(day.date);
        }
        weeks[weeks.length - 1].days.push(day);
    });
    return [...weeks];
};
const isDateDisabled = (date, min, max, dates) => {
    if (!date) {
        return false;
    }
    if (!min && !max && (!dates || !dates.length)) {
        return false;
    }
    if (min && date < min) {
        return true;
    }
    if (max && date > max) {
        return true;
    }
    let disabled = false;
    if (dates) {
        for (const _date of dates) {
            if (Array.isArray(_date)) {
                if (isDateInRange(date, _date[0], _date[1])) {
                    disabled = true;
                    break;
                }
            }
            if (_date instanceof Date) {
                if (isSameDateAs(date, _date)) {
                    disabled = true;
                    break;
                }
            }
        }
    }
    return disabled;
};
const isDateInRange = (date, start, end) => {
    return date && start && end && start <= date && date <= end;
};
const isYearInRange = (date, start, end) => {
    return date && start && end && start.getFullYear() <= date.getFullYear() && date.getFullYear() <= end.getFullYear();
};
const isDateBetweenMinMax = (date, min, max) => {
    if (date && min && max) {
        return min <= date && date <= max;
    }
    if (date && min) {
        return min <= date;
    }
    if (date && max) {
        return date <= max;
    }
    return true;
};
const isDateSelected = (date, start, end) => {
    return date && ((start && isSameDateAs(start, date)) || (end && isSameDateAs(end, date)));
};
const isMonthSelected = (date, start, end) => {
    return date && ((start && isSameMonthAs(start, date)) || (end && isSameMonthAs(end, date)));
};
const isYearSelected = (date, start, end) => {
    return date && ((start && isSameYearAs(start, date)) || (end && isSameYearAs(end, date)));
};
const isEndDate = (date, start, end) => {
    return date && start && end && isSameDateAs(end, date) && start < end;
};
const isLastDayOfMonth = (date) => {
    const test = new Date(date.getTime());
    const month = test.getMonth();
    test.setDate(test.getDate() + 1);
    return test.getMonth() !== month;
};
const equalDates = (a, b) => {
    return (a && b && a.getTime() === b.getTime()) || (a === null && b === null);
};
const isSameDateAs = (date, date2) => {
    return (date.getDate() == date2.getDate() &&
        date.getMonth() == date2.getMonth() &&
        date.getFullYear() == date2.getFullYear());
};
const isSameMonthAs = (date, date2) => {
    return date.getMonth() == date2.getMonth() && date.getFullYear() == date2.getFullYear();
};
const isSameYearAs = (date, date2) => {
    return date.getFullYear() == date2.getFullYear();
};
const isStartDate = (date, start, end) => {
    return date && start && end && isSameDateAs(start, date) && start < end;
};
const isToday = (date) => {
    const today = new Date();
    return (date.getDate() == today.getDate() &&
        date.getMonth() == today.getMonth() &&
        date.getFullYear() == today.getFullYear());
};
const isThisMonth = (date, index) => {
    const today = new Date();
    return index == today.getMonth() && date.getFullYear() == today.getFullYear();
};
const isThisYear = (year) => {
    const today = new Date();
    return year == today.getFullYear();
};
const isValidDate = (date) => {
    if (date instanceof Date || typeof date === 'number' || typeof date === 'string') {
        // Try to create a Date object from Date or timestamp or string
        const dateObj = new Date(date);
        return !isNaN(dateObj.getTime());
    }
    // If none of the above conditions are met, it's not a valid date
    return false;
};
const tryDate = (value, propName = 'date') => {
    let _value;
    try {
        if (!isValidDate(value)) {
            throw value;
        }
        _value = new Date(value);
    }
    catch (err) {
        console.warn(`Invalid ${propName}`, err);
        _value = new Date();
        _value.setHours(0, 0, 0, 0);
    }
    return _value;
};
const isDateInRangeDisabled = (startDate, endDate, dates, dateFilter) => {
    if ((!dates || !dates.length) && !dateFilter) {
        return false;
    }
    if (startDate && endDate) {
        const date = new Date(startDate);
        let disabled = false;
        while (date <= endDate) {
            disabled = isDateDisabled(date, null, null, dates) || (dateFilter ? !dateFilter(date) : false);
            if (disabled) {
                break;
            }
            date.setDate(date.getDate() + 1);
        }
        return disabled;
    }
    return false;
};
const getUtcTime = (value) => {
    const hours = value?.getUTCHours() ?? 0;
    const minutes = value?.getUTCMinutes() ?? 0;
    const seconds = value?.getUTCSeconds() ?? 0;
    const ms = value?.getUTCMilliseconds() ?? 0;
    const offset = value?.getTimezoneOffset() ?? 0;
    const utcTimeObj = { hours, minutes, seconds, ms, offset };
    Object.entries(utcTimeObj).forEach(([key, value]) => {
        if (value && Number.isNaN(value)) {
            utcTimeObj[key] = 0;
        }
    });
    Object.freeze(utcTimeObj);
    return utcTimeObj;
};
const setUtcTime = (value, time, withTime) => {
    if (!value || Number.isNaN(value?.getTime() ?? NaN) || !withTime) {
        return value;
    }
    const date = new Date(value);
    date.setUTCHours(time['hours'], time['minutes'], time['seconds'], time['ms']);
    return new Date(date);
};
// type DateParts = {
//   [key in DateTimeFormatPartTypes]?: string;
// };
// type DateParts = {
//   year: number, month: number, day: number, literal: string
// };
//
// export const cvtDate = (dateAsString: string, locale = 'pl-PL') => {
//   const dateString = dateAsString ?? new Date().toLocaleDateString(locale);
//   const dateSplit = dateString.split(/[.\-\/\s\D]/gi).filter(item => !!item.length);
//   const formatter = new Intl.DateTimeFormat(locale);
//   const parts = formatter.formatToParts().filter(part => ['year', 'month', 'day'].includes(part.type));
//   const dateShim = {
//     day: undefined, month: undefined, year: undefined
//   };
//   parts.forEach((part, index) => {
//     const { type, value } = { ...part };
//     // @ts-ignore
//     dateShim[type] = dateSplit[index];
//   });
//
//   return dateShim;
//
//   // const dateParts: DateParts = {
//   //   year: 0,
//   //   month: 0,
//   //   day: 0,
//   //   literal: '/'
//   // };
//   //
//   // const literal = parts.find(part => part.type === 'literal')?.value ?? '/';
//   // // const year = parseInt(parts.find(part => part.type === 'year')?.value ?? '0');
//   // // const month = parseInt(parts.find(part => part.type === 'year')?.value ?? '0');
//   // // const day = parseInt(parts.find(part => part.type === 'year')?.value ?? '0');
//   //
//   // const dateArray = dateString.split(literal);
//   //
//   // parts.forEach(part => {
//   //   const {type, value} = {...part};
//   //   if (type === 'day') {
//   //
//   //   }
//   // })
//   //
//   // parts.filter(part => part.type in dateParts).map(part => {
//   //   const {type, value} = {...part};
//   //   // @ts-ignore
//   //   dateParts[type] = parseInt(part.value);
//   //   //
//   //   // switch (type) {
//   //   //   case 'day':
//   //   //     day = parseInt(value);
//   //   //     break;
//   //   //   case 'month':
//   //   //     month = parseInt(value);
//   //   //     break;
//   //   //   case 'year':
//   //   //     year = parseInt(value);
//   //   //     break;
//   //   // }
//   // })
//   // // const { year, month, day } = {...dateParts}
//   // return new Date(year, month - 1, day);
// };
//
// // function getDateX(dateAsString) {
// //   const [day, month, year] = dateAsString.split('/');
// //   return new Date(year, month - 1, day);
// // }
//
// // getDate('12/07/1997');

class CalendarService {
    #keyValueDiffers = inject(KeyValueDiffers);
    #differ;
    constructor() {
        this.#differ = this.#keyValueDiffers.find(this.calendarStateObject).create();
    }
    calendarStateObject = {
        calendarDate: new Date(),
        hoverDate: null,
        startDate: null,
        endDate: null,
        maxDate: null,
        minDate: null,
        disabledDates: [],
        range: false,
        view: 'days',
        locale: 'default',
        dateFilter: undefined,
        weekdayFormat: 'short',
        dayFormat: 'numeric',
        navYearFirst: false,
        showAdjacentDays: true,
        selectAdjacentDays: false,
        selectionType: 'day',
        showWeekNumber: false,
        weekNumbersLabel: undefined,
        firstDayOfWeek: 1
    };
    #calendarState = new BehaviorSubject(this.calendarStateObject);
    calendarState$ = this.#calendarState.asObservable();
    update(state) {
        const keys = Object.keys(state);
        for (const key of keys) {
            const entry = this.calendarStateObject[key];
            if (entry === state[key]) {
                continue;
            }
            if (isValidDate(state[key]) && entry ? entry.getTime() === state[key]?.getTime() : false) {
                continue;
            }
            this.calendarStateObject[key] = state[key];
        }
        if (this.#differ) {
            const changes = this.#differ.diff(this.calendarStateObject);
            if (changes) {
                const newState = { ...this.calendarStateObject };
                changes.forEachChangedItem((item) => {
                    // console.log('changes', item.key, item);
                    newState[item.key] = item.currentValue;
                });
                this.#calendarState.next(newState);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

class CalendarNavigationComponent {
    #destroyRef = inject(DestroyRef);
    #calendarService = inject(CalendarService);
    constructor() {
        this.calendarStateSubscribe();
    }
    pageIndex = input(0, { ...(ngDevMode ? { debugName: "pageIndex" } : {}), transform: numberAttribute });
    addMonths = computed(() => {
        const view = this.view();
        const pageIndex = this.pageIndex();
        return pageIndex * (view === 'years' ? 144 : view === 'months' ? 12 : 1);
    }, ...(ngDevMode ? [{ debugName: "addMonths" }] : []));
    navigation = input(true, ...(ngDevMode ? [{ debugName: "navigation" }] : []));
    maxDate = signal(null, ...(ngDevMode ? [{ debugName: "maxDate" }] : []));
    minDate = signal(null, ...(ngDevMode ? [{ debugName: "minDate" }] : []));
    calendarDate = signal(new Date(), ...(ngDevMode ? [{ debugName: "calendarDate" }] : []));
    navYearFirst = signal(false, ...(ngDevMode ? [{ debugName: "navYearFirst" }] : []));
    locale = signal('default', ...(ngDevMode ? [{ debugName: "locale" }] : []));
    view = signal('days', ...(ngDevMode ? [{ debugName: "view" }] : []));
    selectionType = signal('day', ...(ngDevMode ? [{ debugName: "selectionType" }] : []));
    ariaNextMonthLabel = input('Next month', ...(ngDevMode ? [{ debugName: "ariaNextMonthLabel" }] : []));
    ariaNextYearLabel = input('Next year', ...(ngDevMode ? [{ debugName: "ariaNextYearLabel" }] : []));
    ariaPrevMonthLabel = input('Previous month', ...(ngDevMode ? [{ debugName: "ariaPrevMonthLabel" }] : []));
    ariaPrevYearLabel = input('Previous year', ...(ngDevMode ? [{ debugName: "ariaPrevYearLabel" }] : []));
    navigationClick = output();
    date = computed(() => {
        return new Date(this.calendarDate().getFullYear(), this.calendarDate().getMonth() + this.addMonths());
    }, ...(ngDevMode ? [{ debugName: "date" }] : []));
    disableNextMonth = computed(() => {
        const maxDate = this.maxDate();
        return maxDate ? this.date() >= maxDate : false;
    }, ...(ngDevMode ? [{ debugName: "disableNextMonth" }] : []));
    disablePrevMonth = computed(() => {
        const minDate = this.minDate();
        return minDate ? this.date() <= minDate : false;
    }, ...(ngDevMode ? [{ debugName: "disablePrevMonth" }] : []));
    disableNextYear = computed(() => {
        const maxDate = this.maxDate();
        return maxDate ? this.date().getFullYear() + 1 >= maxDate.getFullYear() : false;
    }, ...(ngDevMode ? [{ debugName: "disableNextYear" }] : []));
    disablePrevYear = computed(() => {
        const minDate = this.minDate();
        return minDate ? this.date().getFullYear() - 1 <= minDate.getFullYear() : false;
    }, ...(ngDevMode ? [{ debugName: "disablePrevYear" }] : []));
    calendarStateSubscribe() {
        this.#calendarService.calendarState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((state) => {
            const keys = Object.keys(state);
            for (const key of keys) {
                if (key in this) {
                    // @ts-ignore
                    typeof this[key] === 'function' ? this[key].set(state[key]) : (this[key] = state[key]);
                    // this[key] = state[key];
                }
            }
        });
    }
    setView(view) {
        this.view.set(view);
        this.#calendarService.update({ view: view });
    }
    handleNavigationClick(direction, years = false) {
        this.navigationClick.emit({ direction, years: years });
    }
    handleNavigationKeyUp($event, direction, years) {
        if (['Space'].includes($event.code)) {
            $event.preventDefault();
            this.handleNavigationClick(direction, years);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarNavigationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: CalendarNavigationComponent, isStandalone: true, selector: "c-calendar-navigation", inputs: { pageIndex: { classPropertyName: "pageIndex", publicName: "pageIndex", isSignal: true, isRequired: false, transformFunction: null }, navigation: { classPropertyName: "navigation", publicName: "navigation", isSignal: true, isRequired: false, transformFunction: null }, ariaNextMonthLabel: { classPropertyName: "ariaNextMonthLabel", publicName: "ariaNextMonthLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaNextYearLabel: { classPropertyName: "ariaNextYearLabel", publicName: "ariaNextYearLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaPrevMonthLabel: { classPropertyName: "ariaPrevMonthLabel", publicName: "ariaPrevMonthLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaPrevYearLabel: { classPropertyName: "ariaPrevYearLabel", publicName: "ariaPrevYearLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { navigationClick: "navigationClick" }, host: { classAttribute: "calendar-nav" }, ngImport: i0, template: "@if (navigation()) {\n  <div class=\"calendar-nav-prev\">\n    <button\n      (click)=\"handleNavigationClick('prev', true)\"\n      (keyup)=\"handleNavigationKeyUp($event, 'prev', true)\"\n      cButton\n      [disabled]=\"disablePrevYear()\"\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n      [attr.aria-label]=\"ariaPrevYearLabel()\"\n    >\n      <span class=\"calendar-nav-icon calendar-nav-icon-double-prev\"></span>\n    </button>\n    @if (view() === 'days') {\n      <button\n        (click)=\"handleNavigationClick('prev')\"\n        (keyup)=\"handleNavigationKeyUp($event, 'prev')\"\n        cButton\n        [disabled]=\"disablePrevMonth()\"\n        color=\"transparent\"\n        size=\"sm\"\n        tabindex=\"0\"\n        [attr.aria-label]=\"ariaPrevMonthLabel()\"\n      >\n        <span class=\"calendar-nav-icon calendar-nav-icon-prev\"></span>\n      </button>\n    }\n  </div>\n}\n<div [style]=\"navYearFirst() ? { display: 'flex', justifyContent: 'center' } : {}\" aria-live=\"polite\" class=\"calendar-nav-date\">\n  @if (view() === 'days') {\n    <button\n      (click)=\"setView('months')\"\n      cButton\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n    >\n      {{ date().toLocaleDateString(locale(), { month: 'long' }) }}\n    </button>\n  }\n  <button\n    (click)=\"setView('years')\"\n    [style]=\"navYearFirst() ? { order: '-1' } : {}\"\n    cButton\n    color=\"transparent\"\n    size=\"sm\"\n    tabindex=\"0\"\n  >\n    {{ date().toLocaleDateString(locale(), { year: 'numeric' }) }}\n  </button>\n</div>\n@if (navigation()) {\n  <div class=\"calendar-nav-next\">\n    @if (view() === 'days') {\n      <button\n        (click)=\"handleNavigationClick('next')\"\n        (keyup)=\"handleNavigationKeyUp($event, 'next')\"\n        [disabled]=\"disableNextMonth()\"\n        cButton\n        color=\"transparent\"\n        size=\"sm\"\n        tabindex=\"0\"\n        [attr.aria-label]=\"ariaNextMonthLabel()\"\n      >\n        <span class=\"calendar-nav-icon calendar-nav-icon-next\"></span>\n      </button>\n    }\n    <button\n      (click)=\"handleNavigationClick('next', true)\"\n      (keyup)=\"handleNavigationKeyUp($event, 'next', true)\"\n      cButton\n      [disabled]=\"disableNextYear()\"\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n      [attr.aria-label]=\"ariaNextYearLabel()\"\n    >\n      <span class=\"calendar-nav-icon calendar-nav-icon-double-next\"></span>\n    </button>\n  </div>\n}\n", styles: [""], dependencies: [{ kind: "directive", type: ButtonDirective, selector: "[cButton]", inputs: ["active", "color", "disabled", "shape", "size", "tabindex", "type", "variant"], exportAs: ["cButton"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarNavigationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-calendar-navigation', imports: [ButtonDirective], host: {
                        class: 'calendar-nav'
                    }, template: "@if (navigation()) {\n  <div class=\"calendar-nav-prev\">\n    <button\n      (click)=\"handleNavigationClick('prev', true)\"\n      (keyup)=\"handleNavigationKeyUp($event, 'prev', true)\"\n      cButton\n      [disabled]=\"disablePrevYear()\"\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n      [attr.aria-label]=\"ariaPrevYearLabel()\"\n    >\n      <span class=\"calendar-nav-icon calendar-nav-icon-double-prev\"></span>\n    </button>\n    @if (view() === 'days') {\n      <button\n        (click)=\"handleNavigationClick('prev')\"\n        (keyup)=\"handleNavigationKeyUp($event, 'prev')\"\n        cButton\n        [disabled]=\"disablePrevMonth()\"\n        color=\"transparent\"\n        size=\"sm\"\n        tabindex=\"0\"\n        [attr.aria-label]=\"ariaPrevMonthLabel()\"\n      >\n        <span class=\"calendar-nav-icon calendar-nav-icon-prev\"></span>\n      </button>\n    }\n  </div>\n}\n<div [style]=\"navYearFirst() ? { display: 'flex', justifyContent: 'center' } : {}\" aria-live=\"polite\" class=\"calendar-nav-date\">\n  @if (view() === 'days') {\n    <button\n      (click)=\"setView('months')\"\n      cButton\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n    >\n      {{ date().toLocaleDateString(locale(), { month: 'long' }) }}\n    </button>\n  }\n  <button\n    (click)=\"setView('years')\"\n    [style]=\"navYearFirst() ? { order: '-1' } : {}\"\n    cButton\n    color=\"transparent\"\n    size=\"sm\"\n    tabindex=\"0\"\n  >\n    {{ date().toLocaleDateString(locale(), { year: 'numeric' }) }}\n  </button>\n</div>\n@if (navigation()) {\n  <div class=\"calendar-nav-next\">\n    @if (view() === 'days') {\n      <button\n        (click)=\"handleNavigationClick('next')\"\n        (keyup)=\"handleNavigationKeyUp($event, 'next')\"\n        [disabled]=\"disableNextMonth()\"\n        cButton\n        color=\"transparent\"\n        size=\"sm\"\n        tabindex=\"0\"\n        [attr.aria-label]=\"ariaNextMonthLabel()\"\n      >\n        <span class=\"calendar-nav-icon calendar-nav-icon-next\"></span>\n      </button>\n    }\n    <button\n      (click)=\"handleNavigationClick('next', true)\"\n      (keyup)=\"handleNavigationKeyUp($event, 'next', true)\"\n      cButton\n      [disabled]=\"disableNextYear()\"\n      color=\"transparent\"\n      size=\"sm\"\n      tabindex=\"0\"\n      [attr.aria-label]=\"ariaNextYearLabel()\"\n    >\n      <span class=\"calendar-nav-icon calendar-nav-icon-double-next\"></span>\n    </button>\n  </div>\n}\n" }]
        }], ctorParameters: () => [], propDecorators: { pageIndex: [{ type: i0.Input, args: [{ isSignal: true, alias: "pageIndex", required: false }] }], navigation: [{ type: i0.Input, args: [{ isSignal: true, alias: "navigation", required: false }] }], ariaNextMonthLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaNextMonthLabel", required: false }] }], ariaNextYearLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaNextYearLabel", required: false }] }], ariaPrevMonthLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaPrevMonthLabel", required: false }] }], ariaPrevYearLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaPrevYearLabel", required: false }] }], navigationClick: [{ type: i0.Output, args: ["navigationClick"] }] } });

class CalendarWeekdayPipe {
    transform(date, weekdayFormat = 'short', locale = 'default') {
        return typeof weekdayFormat === 'function'
            ? weekdayFormat(date)
            : typeof weekdayFormat === 'string'
                ? date.toLocaleDateString(locale, { weekday: weekdayFormat })
                : date.toLocaleDateString(locale, { weekday: 'long' }).slice(0, weekdayFormat);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarWeekdayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarWeekdayPipe, isStandalone: true, name: "calendarWeekday" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarWeekdayPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarWeekday',
                    standalone: true
                }]
        }] });

class CalendarClassYearPipe {
    transform(year, args) {
        return {
            'calendar-cell': true,
            year: true,
            today: isThisYear(year)
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassYearPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassYearPipe, isStandalone: true, name: "calendarClassYear" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassYearPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarClassYear',
                    standalone: true
                }]
        }] });

class CalendarClassMonthPipe {
    transform(calendarDate, monthIndex) {
        const year = new Date(calendarDate).getFullYear();
        const date = new Date(year, monthIndex, 1);
        return {
            'calendar-cell': true,
            month: true,
            today: isThisMonth(date, monthIndex)
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassMonthPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassMonthPipe, isStandalone: true, name: "calendarClassMonth" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassMonthPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarClassMonth',
                    standalone: true
                }]
        }] });

class CalendarDayPipe {
    transform(date, dayFormat = 'numeric', locale = 'default') {
        return typeof dayFormat === 'function' ?
            dayFormat(date) :
            date.toLocaleDateString(locale, { day: dayFormat });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayPipe, isStandalone: true, name: "calendarDay" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarDay',
                    standalone: true
                }]
        }] });

class CalendarDayDirective {
    #destroyRef = inject(DestroyRef);
    #calendarService = inject(CalendarService);
    elementRef = inject(ElementRef);
    focusMonitor = inject(FocusMonitor);
    #calendarState = signal({ ...this.#calendarService.calendarStateObject }, ...(ngDevMode ? [{ debugName: "#calendarState" }] : []));
    constructor() {
        this.#calendarService.calendarState$
            .pipe(tap((state) => {
            this.#calendarState.set(state);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    date = input(...(ngDevMode ? [undefined, { debugName: "date" }] : []));
    month = input('current', ...(ngDevMode ? [{ debugName: "month" }] : []));
    disabled = false;
    // @Input() calendarDate = new Date();
    #selectEndDate = computed(() => {
        const state = this.#calendarState();
        return (state.range && !!state.startDate && !state.endDate);
    }, ...(ngDevMode ? [{ debugName: "#selectEndDate" }] : []));
    disabled$ = computed(() => {
        const state = { ...this.#calendarState() };
        const date = this.date();
        if (!date) {
            return true;
        }
        const dateYear = date.getFullYear();
        switch (state.view) {
            case 'months': {
                const dateMonth = dateYear * 100 + date.getMonth();
                const minDate = state.minDate ? state.minDate.getFullYear() * 100 + state.minDate?.getMonth() : undefined;
                const maxDate = state.maxDate ? state.maxDate.getFullYear() * 100 + state.maxDate?.getMonth() : undefined;
                this.disabled = (minDate ? dateMonth < minDate : false) || (maxDate ? dateMonth > maxDate : false);
                break;
            }
            case 'years': {
                const minYear = state.minDate?.getFullYear();
                const maxYear = state.maxDate?.getFullYear();
                this.disabled = (minYear ? dateYear < minYear : false) || (maxYear ? dateYear > maxYear : false);
                break;
            }
        }
        return this.disabled;
    }, ...(ngDevMode ? [{ debugName: "disabled$" }] : []));
    hostClasses = computed(() => {
        const date = this.date();
        if (!date) {
            return;
        }
        const selectEndDate = this.#selectEndDate();
        const month = this.month();
        const state = this.#calendarState();
        state.startDate = state.startDate ?? null;
        state.hoverDate = state.hoverDate ?? null;
        state.endDate = state.endDate ?? null;
        const start = isStartDate(date, state.startDate, state.endDate);
        const end = isEndDate(date, state.startDate, state.endDate);
        let selected = false;
        let clickable = false;
        let rangeHover = false;
        let inRange = false;
        let disabledRange = false;
        let disabledDate = false;
        let disabledByFilter = false;
        switch (state.view) {
            case 'months':
                if (state.selectionType === 'month') {
                    selected = !!isMonthSelected(date, state.startDate, state.endDate);
                    inRange = !!isDateInRange(date, state.startDate, state.endDate);
                    rangeHover = selectEndDate && !!isDateInRange(date, state.startDate, state.hoverDate);
                }
                break;
            case 'years':
                if (state.selectionType === 'year') {
                    selected = !!isYearSelected(date, state.startDate, state.endDate);
                    inRange = !!isDateInRange(date, state.startDate, state.endDate);
                    rangeHover = selectEndDate && !!isYearInRange(date, state.startDate, state.hoverDate);
                }
                break;
            default:
                selected = state.selectionType === 'day' && !!isDateSelected(date, state.startDate, state.endDate);
                clickable = month !== 'current' && !!state.selectAdjacentDays;
                rangeHover =
                    state.selectionType === 'day' &&
                        month === 'current' &&
                        selectEndDate &&
                        !!isDateInRange(date, state.startDate, state.hoverDate);
                inRange = month === 'current' && (isDateInRange(date, state.startDate, state.endDate) || !!start || !!end);
                disabledRange = isDateInRangeDisabled(state.startDate, date, state.disabledDates, state.dateFilter);
                disabledDate = isDateDisabled(date, state.minDate, state.maxDate, state.disabledDates);
                disabledByFilter = typeof state.dateFilter === 'function' && date ? !state.dateFilter(date) : false;
                this.disabled = disabledDate || disabledByFilter;
        }
        return {
            'range-hover': rangeHover && (!disabledRange || !disabledByFilter),
            selected: selected,
            range: inRange,
            start: !!start,
            end: !!end,
            disabled: this.disabled,
            clickable: clickable
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ariaSelected = computed(() => {
        return this.hostClasses()?.['selected'] ? true : null;
    }, ...(ngDevMode ? [{ debugName: "ariaSelected" }] : []));
    focus(origin) {
        this.focusMonitor.focusVia(this.elementRef.nativeElement, origin ?? 'keyboard');
    }
    getLabel() {
        return this.date()?.toLocaleDateString(this.#calendarState().locale) ?? '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: CalendarDayDirective, isStandalone: true, selector: "[cCalendarDay]", inputs: { date: { classPropertyName: "date", publicName: "date", isSignal: true, isRequired: false, transformFunction: null }, month: { classPropertyName: "month", publicName: "month", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-selected": "ariaSelected()" } }, exportAs: ["cCalendarDay"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCalendarDay]',
                    standalone: true,
                    exportAs: 'cCalendarDay',
                    host: { '[class]': 'hostClasses()', '[attr.aria-selected]': 'ariaSelected()' }
                }]
        }], ctorParameters: () => [], propDecorators: { date: [{ type: i0.Input, args: [{ isSignal: true, alias: "date", required: false }] }], month: [{ type: i0.Input, args: [{ isSignal: true, alias: "month", required: false }] }] } });

class CalendarDayTitlePipe {
    transform(date, locale = 'default') {
        return date.toLocaleDateString(locale);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayTitlePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayTitlePipe, isStandalone: true, name: "calendarDayTitle" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarDayTitlePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarDayTitle',
                    standalone: true
                }]
        }] });

class CalendarClassDayPipe {
    transform(dateObj, datesConfig) {
        const { date, month } = { ...dateObj };
        const { minDate, maxDate, disabledDates, dateFilter } = { ...datesConfig };
        // @ts-ignore
        const disabled = isDateDisabled(date, minDate, maxDate, disabledDates) || (dateFilter ? !dateFilter(date) : false);
        return {
            'calendar-cell': true,
            today: isToday(date),
            disabled: disabled,
            next: month === 'next',
            previous: month === 'previous',
            current: month === 'current',
            last: isLastDayOfMonth(date)
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassDayPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassDayPipe, isStandalone: true, name: "calendarClassDay" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassDayPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarClassDay',
                    standalone: true
                }]
        }] });

class CalendarRowDirective {
    #destroyRef = inject(DestroyRef);
    #calendarService = inject(CalendarService);
    elementRef = inject(ElementRef);
    focusMonitor = inject(FocusMonitor);
    constructor() {
        this.#calendarService.calendarState$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
            .subscribe((state) => {
            const { startDate = this.startDate, endDate = this.endDate, hoverDate = this.hoverDate, maxDate = this.maxDate, minDate = this.minDate, range = this.range, disabledDates = this.disabledDates, dateFilter = this.dateFilter, selectionType = this.selectionType, calendarDate = this.calendarDate, locale = this.locale } = { ...state };
            this.startDate = startDate;
            this.endDate = endDate;
            this.hoverDate = hoverDate;
            this.maxDate = maxDate;
            this.minDate = minDate;
            this.range = range;
            this.disabledDates = disabledDates;
            this.dateFilter = dateFilter;
            this.selectionType = selectionType;
            this.calendarDate = calendarDate ?? new Date();
            this.locale = locale ?? 'default';
            // this.#tabindex.set(this.selectionType === 'week' && this.current && !this.disabled ? 0 : -1);
        });
    }
    set week(value) {
        this.current = value.days.some((day) => day.month === 'current');
        this.tabindex.set(this.selectionType === 'week' && this.current && !this.disabled ? 0 : -1);
        this.#week = value;
    }
    get week() {
        return this.#week;
    }
    #week;
    date = null;
    startDate = null;
    endDate = null;
    hoverDate = null;
    maxDate = null;
    minDate = null;
    range = false;
    selectionType = 'week';
    disabledDates;
    dateFilter;
    calendarDate = new Date();
    locale = 'default';
    disabled;
    current;
    tabindex = signal(-1, ...(ngDevMode ? [{ debugName: "tabindex" }] : []));
    get selectEndDate() {
        return this.range && !!this.startDate && !this.endDate;
    }
    get hostClasses() {
        if (!this.week) {
            return {
                'calendar-row': true
            };
        }
        const week = this.week;
        const calendarDate = this.calendarDate;
        const selectionType = this.selectionType;
        this.current = week.days.some((day) => day.month === 'current');
        if (this.selectionType === 'week') {
            this.date = convertToDateObject(week.weekNumber === 0 ? `${calendarDate.getFullYear()}W53` : `${calendarDate.getFullYear()}W${week.weekNumber}`, 'week');
            const inRange = isDateInRange(this.date, this.startDate, this.endDate);
            const rangeHover = this.hoverDate &&
                (this.selectEndDate
                    ? isDateInRange(this.date, this.startDate, this.hoverDate)
                    : isDateInRange(this.date, this.hoverDate, this.endDate));
            const selected = isDateSelected(this.date, this.startDate, this.endDate);
            const disabledDate = isDateDisabled(this.date, this.minDate, this.maxDate, this.disabledDates);
            const disabledByFilter = Object.prototype.toString.call(this.dateFilter) === '[object Function]' && this.date
                ? !this.dateFilter(this.date)
                : false;
            this.disabled = disabledDate || disabledByFilter;
            return {
                'calendar-row': true,
                disabled: this.disabled,
                range: inRange,
                'range-hover': rangeHover,
                selected: selected,
                current: this.current
            };
        }
        else {
            return {
                'calendar-row': true,
                current: this.current
            };
        }
    }
    get ariaSelected() {
        return this.hostClasses?.['selected'] ? true : null;
    }
    focus(origin) {
        this.focusMonitor.focusVia(this.elementRef.nativeElement, origin ?? 'keyboard');
    }
    getLabel() {
        return this.date?.toLocaleDateString(this.locale) ?? '';
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarRowDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: CalendarRowDirective, isStandalone: true, selector: "[cCalendarRow]", inputs: { week: ["cCalendarRow", "week"] }, host: { properties: { "attr.aria-selected": "ariaSelected", "attr.tabindex": "tabindex()", "class": "this.hostClasses" } }, exportAs: ["cCalendarRow"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCalendarRow]',
                    standalone: true,
                    exportAs: 'cCalendarRow',
                    host: { '[attr.aria-selected]': 'ariaSelected', '[attr.tabindex]': 'tabindex()' }
                }]
        }], ctorParameters: () => [], propDecorators: { week: [{
                type: Input,
                args: [{ alias: 'cCalendarRow', required: true }]
            }], hostClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CalendarMonthComponent {
    #destroyRef = inject(DestroyRef);
    #calendarService = inject(CalendarService);
    constructor() {
        this.calendarStateSubscribe();
    }
    pageIndex = input(0, { ...(ngDevMode ? { debugName: "pageIndex" } : {}), transform: numberAttribute });
    addMonths = computed(() => {
        const view = this.view();
        const pageIndex = this.pageIndex();
        return pageIndex * (view === 'years' ? 144 : view === 'months' ? 12 : 1);
    }, ...(ngDevMode ? [{ debugName: "addMonths" }] : []));
    /**
     * Initial selected date.
     * @type  (Date | null)
     */
    startDate = signal(null, { ...(ngDevMode ? { debugName: "startDate" } : {}), equal: equalDates });
    /**
     * Initial selected to date (range).
     * @type  Date | null
     */
    endDate = signal(null, { ...(ngDevMode ? { debugName: "endDate" } : {}), equal: equalDates });
    datesEffect = effect(() => {
        const startDate = this.startDate();
        const endDate = startDate && this.range() ? this.endDate() : null;
        this.#calendarService.update({ startDate: startDate, endDate: endDate });
    }, ...(ngDevMode ? [{ debugName: "datesEffect" }] : []));
    /**
     * Specify the list of dates that cannot be selected.
     * @type  Date[] | Date[][]
     */
    disabledDates = signal([], ...(ngDevMode ? [{ debugName: "disabledDates" }] : []));
    /**
     * Sets the day of start week.
     * - 0 - Sunday,
     * - 1 - Monday,
     * - 2 - Tuesday,
     * - 3 - Wednesday,
     * - 4 - Thursday,
     * - 5 - Friday,
     * - 6 - Saturday,
     */
    firstDayOfWeek = signal(1, ...(ngDevMode ? [{ debugName: "firstDayOfWeek" }] : []));
    /**
     * Sets the default locale for components. If not set, it is inherited from the browser.
     * @default 'default'
     */
    locale = signal('default', ...(ngDevMode ? [{ debugName: "locale" }] : []));
    /**
     * Allow range selection.
     * @type boolean
     * @default false
     */
    range = signal(false, ...(ngDevMode ? [{ debugName: "range" }] : []));
    /**
     * Set length or format of day name.
     * @type number | 'long' | 'narrow' | 'short'
     * @default 'short'
     */
    weekdayFormat = signal('short', ...(ngDevMode ? [{ debugName: "weekdayFormat" }] : []));
    /**
     * Set calendar view.
     * @type 'days' | 'months' | 'years'
     * @default 'days'
     */
    view = signal('days', ...(ngDevMode ? [{ debugName: "view" }] : []));
    /**
     * Max selectable date.
     */
    maxDate = signal(null, { ...(ngDevMode ? { debugName: "maxDate" } : {}), equal: equalDates });
    /**
     * Min selectable date.
     */
    minDate = signal(null, { ...(ngDevMode ? { debugName: "minDate" } : {}), equal: equalDates });
    listOfMonths = computed(() => {
        return createGroupsInArray(getMonthsNames(this.locale(), this.date().getFullYear()), 4);
    }, ...(ngDevMode ? [{ debugName: "listOfMonths" }] : []));
    listOfYears = computed(() => createGroupsInArray(getYears(this.date().getFullYear()), 4), ...(ngDevMode ? [{ debugName: "listOfYears" }] : []));
    /**
     * Default date of the component
     * @type Date
     */
    calendarDate = signal(new Date(), { ...(ngDevMode ? { debugName: "calendarDate" } : {}), equal: equalDates });
    date = computed(() => {
        const date = this.calendarDate();
        return new Date(date.getFullYear(), date.getMonth() + this.addMonths(), 1, 0, 0, 0, 0);
    }, { ...(ngDevMode ? { debugName: "date" } : {}), equal: (a, b) => a.getTime() === b.getTime() });
    calendarDateEffect = effect(() => {
        this.#calendarService.update({ calendarDate: this.calendarDate() });
    }, ...(ngDevMode ? [{ debugName: "calendarDateEffect" }] : []));
    // readonly calendarDays = viewChildren(CalendarDayDirective);
    // readonly calendarRows = viewChildren(CalendarRowDirective);
    //
    // readonly calendarDays$ = toObservable(this.calendarDays);
    // readonly calendarRows$ = toObservable(this.calendarRows);
    calendarDays;
    calendarRows;
    dateFilter = signal(undefined, ...(ngDevMode ? [{ debugName: "dateFilter" }] : []));
    dayFormat = signal('numeric', ...(ngDevMode ? [{ debugName: "dayFormat" }] : []));
    selectAdjacentDays = signal(false, ...(ngDevMode ? [{ debugName: "selectAdjacentDays" }] : []));
    showAdjacentDays = signal(true, ...(ngDevMode ? [{ debugName: "showAdjacentDays" }] : []));
    selectionType = signal('day', ...(ngDevMode ? [{ debugName: "selectionType" }] : []));
    showWeekNumber = signal(false, ...(ngDevMode ? [{ debugName: "showWeekNumber" }] : []));
    weekNumbersLabel = signal('', ...(ngDevMode ? [{ debugName: "weekNumbersLabel" }] : []));
    monthDetails = computed(() => {
        const date = this.date();
        return getMonthDetails(date.getFullYear(), date.getMonth(), this.firstDayOfWeek());
    }, ...(ngDevMode ? [{ debugName: "monthDetails" }] : []));
    weekDays = computed(() => {
        const monthDetails = this.monthDetails();
        return monthDetails[0].days;
    }, ...(ngDevMode ? [{ debugName: "weekDays" }] : []));
    yearNumber(year) {
        return new Date(year, 0).toLocaleDateString(this.locale(), { year: 'numeric' });
    }
    isDateDisabled(date) {
        const selectionType = this.selectionType();
        const disabledDates = this.disabledDates();
        const dateFilterFn = this.dateFilter();
        const maxDate = this.maxDate();
        const minDate = this.minDate();
        if (selectionType === 'year') {
            const min = minDate ? new Date(minDate.getFullYear(), 0, 1, 0, 0, 0, 0) : null;
            const max = maxDate ? new Date(maxDate.getFullYear(), 11, 31, 0, 0, 0, 0) : null;
            return isDateDisabled(date, min, max, disabledDates) || (dateFilterFn ? !dateFilterFn(date) : false);
        }
        if (selectionType === 'month') {
            const min = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), 1, 0, 0, 0, 0) : null;
            const max = maxDate ? new Date(maxDate.getFullYear(), maxDate.getMonth() + 1, 1, 0, 0, -1, 0) : null;
            return isDateDisabled(date, min, max, disabledDates) || (dateFilterFn ? !dateFilterFn(date) : false);
        }
        return isDateDisabled(date, minDate, maxDate, disabledDates) || (dateFilterFn ? !dateFilterFn(date) : false);
    }
    calendarCellTitle(date) {
        return date.toLocaleDateString(this.locale());
    }
    handleCellMouseEnter(hoverDate) {
        if (!hoverDate) {
            return;
        }
        const selectionType = this.selectionType();
        const updateHoverValue = (['day', 'week'].includes(selectionType) && this.view() === 'days') ||
            (['month'].includes(selectionType) && this.view() === 'months') ||
            (['year'].includes(selectionType) && this.view() === 'years');
        // if (updateHoverValue) {
        const hoverValue = this.isDateDisabled(hoverDate) || !updateHoverValue ? null : hoverDate;
        this.#calendarService.update({ hoverDate: hoverValue });
        // }
    }
    handleYearCellClick(year) {
        if (this.selectionType() === 'year') {
            let date = new Date(year, 0, 1, 0, 0, 0, 0);
            if (this.range() && this.startDate() && !this.endDate()) {
                date = new Date(date.getFullYear(), 12, 1, 0, 0, -1, 0);
            }
            this.handleDayCellClick(date);
            return;
        }
        const calendarDate = new Date(year, 0, 1, 0, 0, 0, 0);
        this.view.set('months');
        this.#calendarService.update({ view: this.view(), calendarDate: calendarDate });
    }
    handleYearCellKeyUp($event, date) {
        if (!date) {
            return;
        }
        if (['Space', 'Enter'].includes($event.code)) {
            this.handleYearCellClick(date.getFullYear());
            return;
        }
        if (['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes($event.code)) {
            this.handleCellMouseEnter(new Date(date));
            return;
        }
    }
    handleMonthCellKeyUp($event, date) {
        if (!date) {
            return;
        }
        if (['Space', 'Enter'].includes($event.code)) {
            this.handleMonthCellClick(date);
            return;
        }
        if (['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes($event.code)) {
            this.handleCellMouseEnter(date);
            return;
        }
    }
    handleMonthCellClick(value) {
        if (!value) {
            return;
        }
        if (this.selectionType() === 'month') {
            let date = new Date(value.getFullYear(), value.getMonth(), 1, 0, 0, 0, 0);
            if (this.range() && this.startDate() && !this.endDate()) {
                date = new Date(date.getFullYear(), date.getMonth() + 1, 1, 0, 0, -1, 0);
            }
            this.handleDayCellClick(date);
            return;
        }
        this.view.set('days');
        this.#calendarService.update({ view: this.view() });
        // this.setCalendarPage(0, 0, value.getMonth() - this.addMonths());
        const calendarDate = new Date(value.getFullYear(), value.getMonth() - this.addMonths(), 1, 0, 0, 0, 0);
        this.#calendarService.update({ calendarDate: calendarDate });
    }
    setCalendarPage(years, months = 0, setMonth) {
        const year = this.date().getFullYear();
        const month = this.date().getMonth();
        const d = new Date(year, month, 1, 0, 0, 0, 0);
        years && d.setFullYear(d.getFullYear() + years);
        months && d.setMonth(d.getMonth() + months);
        typeof setMonth === 'number' && d.setMonth(setMonth);
        this.#calendarService.update({ calendarDate: d });
    }
    handleDayCellKeyDown($event, date) {
        if (!date) {
            return;
        }
        if (!['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes($event.code)) {
            return;
        }
        $event.preventDefault();
        this.handleCellMouseEnter(date);
        const eventKey = $event.key === 'Tab' ? ($event.shiftKey ? 'ArrowLeft' : 'ArrowRight') : $event.key;
        const selectionType = this.selectionType();
        const view = this.view();
        const maxDate = this.maxDate();
        if (maxDate &&
            date >= convertToDateObject(maxDate, selectionType) &&
            ['ArrowRight', 'ArrowDown'].includes(eventKey)) {
            return;
        }
        const minDate = this.minDate();
        if (minDate && date <= convertToDateObject(minDate, selectionType) && ['ArrowLeft', 'ArrowUp'].includes(eventKey)) {
            return;
        }
        let element = $event.target;
        if (selectionType === 'week' && view === 'days' && element.tabIndex === -1) {
            element = element.closest('tr[tabindex="0"]');
        }
        const elements = ['week'].includes(selectionType) && view === 'days' ? this.calendarRows : this.calendarDays;
        const list = elements
            .toArray()
            .map((calendarCell) => calendarCell.elementRef.nativeElement)
            // .filter((element) => this.selectionType === 'week' ? element?.tabIndex === 0 : element?.classList.contains('current'))
            .filter((element) => element?.tabIndex === 0);
        const index = list.indexOf(element);
        const first = index === 0;
        const last = index === list.length - 1;
        const toBoundary = {
            start: index,
            end: list.length - (index + 1)
        };
        const gap = {
            ArrowRight: 1,
            ArrowLeft: -1,
            ArrowUp: selectionType === 'week' && view === 'days' ? -1 : view === 'days' ? -7 : -3,
            ArrowDown: selectionType === 'week' && view === 'days' ? 1 : view === 'days' ? 7 : 3
        };
        if ((eventKey === 'ArrowRight' && last) ||
            (eventKey === 'ArrowDown' && toBoundary.end < gap.ArrowDown) ||
            (eventKey === 'ArrowLeft' && first) ||
            (eventKey === 'ArrowUp' && toBoundary.start < Math.abs(gap.ArrowUp))) {
            elements.changes
                .pipe(take(1), takeUntilDestroyed(this.#destroyRef))
                .subscribe((change) => {
                const _list = change
                    .toArray()
                    // .filter(element => this.selectionType === 'week' ? element?.elementRef.nativeElement.tabIndex === 0 : element?.elementRef.nativeElement.classList.contains('current'));
                    .filter((directive) => directive?.elementRef.nativeElement.tabIndex === 0);
                if (_list.length) {
                    let dayToFocus;
                    switch (eventKey) {
                        case 'ArrowRight': {
                            dayToFocus = _list[0];
                            break;
                        }
                        case 'ArrowLeft': {
                            dayToFocus = _list[_list.length - 1];
                            break;
                        }
                        case 'ArrowDown': {
                            dayToFocus = _list[gap.ArrowDown - (list.length - index)];
                            break;
                        }
                        case 'ArrowUp': {
                            dayToFocus = _list[_list.length - (Math.abs(gap.ArrowUp) + 1 - (index + 1))];
                            break;
                        }
                    }
                    if (!dayToFocus?.disabled) {
                        // console.log('dayToFocus', dayToFocus?.date);
                        dayToFocus?.focus('keyboard');
                    }
                }
            });
            switch (view) {
                case 'days':
                    this.setCalendarPage(0, ['ArrowRight', 'ArrowDown'].includes(eventKey) ? 1 : -1);
                    break;
                case 'months':
                    this.setCalendarPage(['ArrowRight', 'ArrowDown'].includes(eventKey) ? 1 : -1);
                    break;
                case 'years':
                    this.setCalendarPage(['ArrowRight', 'ArrowDown'].includes(eventKey) ? 12 : -12);
                    break;
            }
            return;
        }
        // @ts-ignore
        const nextElement = list[index + gap[eventKey]];
        if (nextElement?.tabIndex === 0) {
            nextElement.focus();
            return;
        }
        for (let i = index; i < list.length; list.length && ['ArrowRight', 'ArrowDown'].includes(eventKey) ? i++ : i--) {
            // @ts-ignore
            const nextElement = list[i + gap[eventKey]];
            if (nextElement?.tabIndex === 0) {
                // @ts-ignore
                nextElement?.focus();
                break;
            }
            // @ts-ignore
            if (!nextElement) {
                break;
            }
        }
    }
    handleCellMouseLeave() {
        this.#calendarService.update({ hoverDate: null });
    }
    handleDayCellKeyUp($event, date) {
        if (['Space', 'Enter'].includes($event.code)) {
            this.handleDayCellClick(date);
            return;
        }
        if (['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'].includes($event.code)) {
            this.handleCellMouseEnter(date);
            return;
        }
    }
    handleDayCellClick(date) {
        if (this.selectionType() === 'day' && this.isDateDisabled(date)) {
            return;
        }
        if (!this.range()) {
            this.endDate.set(null);
            this.startDate.set(date);
            return;
        }
        const startDate = this.startDate();
        const endDate = this.endDate();
        if (startDate && endDate) {
            this.startDate.set(null);
            this.endDate.set(null);
            return;
        }
        if (!!startDate && !endDate) {
            if (startDate && date < startDate) {
                this.endDate.set(null);
                this.startDate.set(date);
            }
            else {
                const addDays = this.selectionType() !== 'week' ? 1 : 7;
                this.endDate.set(new Date(date.getFullYear(), date.getMonth(), date.getDate() + addDays, 0, 0, -1, 0));
            }
        }
        else {
            this.endDate.set(null);
            this.startDate.set(date);
        }
        {
            const startDate = this.startDate();
            const endDate = this.endDate();
            if (startDate && endDate) {
                const dateFilterFn = this.dateFilter();
                if (isDateInRangeDisabled(startDate, endDate, this.disabledDates(), dateFilterFn)) {
                    this.startDate.set(null);
                    this.endDate.set(null);
                    return;
                }
            }
        }
    }
    calendarStateSubscribe() {
        this.#calendarService.calendarState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((state) => {
            const keys = Object.keys(state);
            for (const key of keys) {
                if (key in this) {
                    // @ts-ignore
                    const isWritableSignal = typeof this[key] === 'function' && typeof this[key].set === 'function';
                    // @ts-ignore
                    isWritableSignal ? this[key].set(state[key]) : (this[key] = state[key]);
                }
            }
        });
    }
    getMonthStart(index) {
        return new Date(this.calendarDate().getFullYear(), index + this.addMonths(), 1, 0, 0, 0, 0);
    }
    getMonthEnd(index) {
        return new Date(this.calendarDate().getFullYear(), index + 1 + this.addMonths(), 1, 0, 0, -1, 0);
    }
    getYearStart(year) {
        return new Date(year, 0, 1);
    }
    getYearEnd(year) {
        return new Date(year, 11, 1, 0, 0, -1, 0);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarMonthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: CalendarMonthComponent, isStandalone: true, selector: "c-calendar-month", inputs: { pageIndex: { classPropertyName: "pageIndex", publicName: "pageIndex", isSignal: true, isRequired: false, transformFunction: null } }, viewQueries: [{ propertyName: "calendarDays", predicate: CalendarDayDirective, descendants: true }, { propertyName: "calendarRows", predicate: CalendarRowDirective, descendants: true }], exportAs: ["cCalendarMonth"], ngImport: i0, template: "<table>\n  @if (view() === 'days') {\n    <thead>\n    <tr>\n      @if (showWeekNumber()) {\n        <th class=\"calendar-cell\">\n          <div class=\"calendar-header-cell-inner\">{{ weekNumbersLabel() }}</div>\n        </th>\n      }\n      @for (weekDay of weekDays(); track weekDay.date; let i = $index) {\n        @let date = weekDay.date ;\n        <th class=\"calendar-cell\" abbr=\"{{date | calendarWeekday:'long':locale()}}\">\n          <div class=\"calendar-header-cell-inner\">\n            {{ date | calendarWeekday:weekdayFormat():locale() }}\n          </div>\n        </th>\n      }\n    </tr>\n    </thead>\n  }\n  <tbody (mouseleave)=\"handleCellMouseLeave()\">\n    @switch (view()) {\n      @case ('years') {\n        @for (yearsRow of listOfYears(); track yearsRow[0]; let rowIdx = $index) {\n          <tr>\n            @for (year of yearsRow; track year; let idx = $index) {\n              <td\n                #calendarYear=\"cCalendarDay\"\n                (click)=\"!calendarYear.disabled$() && handleYearCellClick(year)\"\n                (keydown)=\"calendarYear.date() && handleDayCellKeyDown($event, calendarYear.date())\"\n                (keyup)=\"handleYearCellKeyUp($event, calendarYear.date())\"\n                (mouseenter)=\"calendarYear.date() && handleCellMouseEnter(calendarYear.date())\"\n                [attr.aria-label]=\"calendarYear.date()?.getFullYear()\"\n                [date]=\"getYearStart(year)\"\n                [class]=\"year | calendarClassYear\"\n                [tabindex]=\"calendarYear.disabled$() ? -1 : 0\"\n                cCalendarDay\n              >\n                <div class=\"calendar-cell-inner\">\n                  {{ yearNumber(year) }}\n                </div>\n              </td>\n            }\n          </tr>\n        }\n      }\n      @case ('months') {\n        @for (monthsRow of listOfMonths(); track monthsRow[0]; let rowIdx = $index) {\n          <tr>\n            @for (month of monthsRow; track month; let idx = $index) {\n              @let monthStart = getMonthStart(rowIdx*3+idx);\n              <td\n                #calendarMonth=\"cCalendarDay\"\n                (click)=\"!calendarMonth.disabled$() && handleMonthCellClick(calendarMonth.date())\"\n                (keydown)=\"calendarMonth.date() && handleDayCellKeyDown($event, calendarMonth.date())\"\n                (keyup)=\"handleMonthCellKeyUp($event, calendarMonth.date())\"\n                (mouseenter)=\"calendarMonth.date() && handleCellMouseEnter(calendarMonth.date())\"\n                [attr.aria-label]=\"calendarMonth.date()?.getFullYear()+' '+month.month\"\n                [date]=\"monthStart\"\n                [class]=\"monthStart | calendarClassMonth:(rowIdx*3+idx)\"\n                [tabindex]=\"calendarMonth.disabled$() ? -1 : 0\"\n                cCalendarDay\n              >\n                <div class=\"calendar-cell-inner\">\n                  {{ month.month }}\n                </div>\n              </td>\n            }\n          </tr>\n        }\n\n      }\n      @default {\n        @let weekSelection = selectionType() === 'week';\n        @let daySelection = selectionType() === 'day';\n        @for (week of monthDetails(); track week.days[0]; let i = $index) {\n          <tr #calendarRow=\"cCalendarRow\"\n              (click)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellClick(calendarRow.date)\"\n              (keydown)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellKeyDown($event, calendarRow.date)\"\n              (keyup)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellKeyUp($event, calendarRow.date)\"\n              (mouseenter)=\"weekSelection && calendarRow.date && handleCellMouseEnter(calendarRow.date)\"\n              [cCalendarRow]=\"week\"\n          >\n            @if (showWeekNumber()) {\n              <th class=\"calendar-cell-week-number\">\n                {{ week.weekNumber === 0 ? 53 : week.weekNumber }}\n              </th>\n            }\n            @for (dateObj of week.days; track dateObj.date; let idx = $index) {\n              @let currentMonth = dateObj.month === 'current';\n              @if (currentMonth || showAdjacentDays()) {\n                @let selectable = daySelection && (currentMonth || selectAdjacentDays());\n                <td #calendarCell=\"cCalendarDay\"\n                    (click)=\"selectable && handleDayCellClick(dateObj.date)\"\n                    (keydown)=\"selectable && handleDayCellKeyDown($event, dateObj.date)\"\n                    (keyup)=\"selectable && handleDayCellKeyUp($event, dateObj.date)\"\n                    (mouseenter)=\"selectable && handleCellMouseEnter(dateObj.date)\"\n                    [attr.aria-label]=\"dateObj.date | calendarDayTitle:locale()\"\n                    [date]=\"dateObj.date\"\n                    [month]=\"dateObj.month\"\n                    [class]=\"dateObj | calendarClassDay:{minDate: minDate(), maxDate: maxDate(), disabledDates: disabledDates(), dateFilter: dateFilter()}\"\n                    [tabindex]=\"selectable && !isDateDisabled(dateObj.date) ? 0 : -1\"\n                    cCalendarDay\n                >\n                  <div class=\"calendar-cell-inner\">\n                    {{ dateObj.date | calendarDay:dayFormat():locale() }}\n                  </div>\n                </td>\n              } @else {\n                <td class=\"calendar-cell\"></td>\n              }\n            }\n          </tr>\n        }\n      }\n    }\n  </tbody>\n</table>\n\n", styles: [".btn-transparent:focus-visible{outline:-webkit-focus-ring-color auto 1px}.calendar-row:focus-visible{box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem}.calendar-row:focus-within:not([tabindex=\"-1\"]){outline:0;box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem;outline-style:auto!important}.calendar-row:focus:not([tabindex=\"-1\"]){outline:0;box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem;outline-style:auto!important}.calendar-cell:focus:not(:focus-visible):not([tabindex=\"-1\"]){box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem}.calendar-cell-inner{-webkit-user-select:none;user-select:none}\n"], dependencies: [{ kind: "directive", type: CalendarDayDirective, selector: "[cCalendarDay]", inputs: ["date", "month"], exportAs: ["cCalendarDay"] }, { kind: "directive", type: CalendarRowDirective, selector: "[cCalendarRow]", inputs: ["cCalendarRow"], exportAs: ["cCalendarRow"] }, { kind: "pipe", type: CalendarDayPipe, name: "calendarDay" }, { kind: "pipe", type: CalendarDayTitlePipe, name: "calendarDayTitle" }, { kind: "pipe", type: CalendarClassDayPipe, name: "calendarClassDay" }, { kind: "pipe", type: CalendarClassMonthPipe, name: "calendarClassMonth" }, { kind: "pipe", type: CalendarClassYearPipe, name: "calendarClassYear" }, { kind: "pipe", type: CalendarWeekdayPipe, name: "calendarWeekday" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarMonthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-calendar-month', exportAs: 'cCalendarMonth', imports: [
                        CalendarDayDirective,
                        CalendarDayPipe,
                        CalendarDayTitlePipe,
                        CalendarRowDirective,
                        CalendarClassDayPipe,
                        CalendarClassMonthPipe,
                        CalendarClassYearPipe,
                        CalendarWeekdayPipe
                    ], template: "<table>\n  @if (view() === 'days') {\n    <thead>\n    <tr>\n      @if (showWeekNumber()) {\n        <th class=\"calendar-cell\">\n          <div class=\"calendar-header-cell-inner\">{{ weekNumbersLabel() }}</div>\n        </th>\n      }\n      @for (weekDay of weekDays(); track weekDay.date; let i = $index) {\n        @let date = weekDay.date ;\n        <th class=\"calendar-cell\" abbr=\"{{date | calendarWeekday:'long':locale()}}\">\n          <div class=\"calendar-header-cell-inner\">\n            {{ date | calendarWeekday:weekdayFormat():locale() }}\n          </div>\n        </th>\n      }\n    </tr>\n    </thead>\n  }\n  <tbody (mouseleave)=\"handleCellMouseLeave()\">\n    @switch (view()) {\n      @case ('years') {\n        @for (yearsRow of listOfYears(); track yearsRow[0]; let rowIdx = $index) {\n          <tr>\n            @for (year of yearsRow; track year; let idx = $index) {\n              <td\n                #calendarYear=\"cCalendarDay\"\n                (click)=\"!calendarYear.disabled$() && handleYearCellClick(year)\"\n                (keydown)=\"calendarYear.date() && handleDayCellKeyDown($event, calendarYear.date())\"\n                (keyup)=\"handleYearCellKeyUp($event, calendarYear.date())\"\n                (mouseenter)=\"calendarYear.date() && handleCellMouseEnter(calendarYear.date())\"\n                [attr.aria-label]=\"calendarYear.date()?.getFullYear()\"\n                [date]=\"getYearStart(year)\"\n                [class]=\"year | calendarClassYear\"\n                [tabindex]=\"calendarYear.disabled$() ? -1 : 0\"\n                cCalendarDay\n              >\n                <div class=\"calendar-cell-inner\">\n                  {{ yearNumber(year) }}\n                </div>\n              </td>\n            }\n          </tr>\n        }\n      }\n      @case ('months') {\n        @for (monthsRow of listOfMonths(); track monthsRow[0]; let rowIdx = $index) {\n          <tr>\n            @for (month of monthsRow; track month; let idx = $index) {\n              @let monthStart = getMonthStart(rowIdx*3+idx);\n              <td\n                #calendarMonth=\"cCalendarDay\"\n                (click)=\"!calendarMonth.disabled$() && handleMonthCellClick(calendarMonth.date())\"\n                (keydown)=\"calendarMonth.date() && handleDayCellKeyDown($event, calendarMonth.date())\"\n                (keyup)=\"handleMonthCellKeyUp($event, calendarMonth.date())\"\n                (mouseenter)=\"calendarMonth.date() && handleCellMouseEnter(calendarMonth.date())\"\n                [attr.aria-label]=\"calendarMonth.date()?.getFullYear()+' '+month.month\"\n                [date]=\"monthStart\"\n                [class]=\"monthStart | calendarClassMonth:(rowIdx*3+idx)\"\n                [tabindex]=\"calendarMonth.disabled$() ? -1 : 0\"\n                cCalendarDay\n              >\n                <div class=\"calendar-cell-inner\">\n                  {{ month.month }}\n                </div>\n              </td>\n            }\n          </tr>\n        }\n\n      }\n      @default {\n        @let weekSelection = selectionType() === 'week';\n        @let daySelection = selectionType() === 'day';\n        @for (week of monthDetails(); track week.days[0]; let i = $index) {\n          <tr #calendarRow=\"cCalendarRow\"\n              (click)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellClick(calendarRow.date)\"\n              (keydown)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellKeyDown($event, calendarRow.date)\"\n              (keyup)=\"weekSelection && (calendarRow.current) && calendarRow.date && handleDayCellKeyUp($event, calendarRow.date)\"\n              (mouseenter)=\"weekSelection && calendarRow.date && handleCellMouseEnter(calendarRow.date)\"\n              [cCalendarRow]=\"week\"\n          >\n            @if (showWeekNumber()) {\n              <th class=\"calendar-cell-week-number\">\n                {{ week.weekNumber === 0 ? 53 : week.weekNumber }}\n              </th>\n            }\n            @for (dateObj of week.days; track dateObj.date; let idx = $index) {\n              @let currentMonth = dateObj.month === 'current';\n              @if (currentMonth || showAdjacentDays()) {\n                @let selectable = daySelection && (currentMonth || selectAdjacentDays());\n                <td #calendarCell=\"cCalendarDay\"\n                    (click)=\"selectable && handleDayCellClick(dateObj.date)\"\n                    (keydown)=\"selectable && handleDayCellKeyDown($event, dateObj.date)\"\n                    (keyup)=\"selectable && handleDayCellKeyUp($event, dateObj.date)\"\n                    (mouseenter)=\"selectable && handleCellMouseEnter(dateObj.date)\"\n                    [attr.aria-label]=\"dateObj.date | calendarDayTitle:locale()\"\n                    [date]=\"dateObj.date\"\n                    [month]=\"dateObj.month\"\n                    [class]=\"dateObj | calendarClassDay:{minDate: minDate(), maxDate: maxDate(), disabledDates: disabledDates(), dateFilter: dateFilter()}\"\n                    [tabindex]=\"selectable && !isDateDisabled(dateObj.date) ? 0 : -1\"\n                    cCalendarDay\n                >\n                  <div class=\"calendar-cell-inner\">\n                    {{ dateObj.date | calendarDay:dayFormat():locale() }}\n                  </div>\n                </td>\n              } @else {\n                <td class=\"calendar-cell\"></td>\n              }\n            }\n          </tr>\n        }\n      }\n    }\n  </tbody>\n</table>\n\n", styles: [".btn-transparent:focus-visible{outline:-webkit-focus-ring-color auto 1px}.calendar-row:focus-visible{box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem}.calendar-row:focus-within:not([tabindex=\"-1\"]){outline:0;box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem;outline-style:auto!important}.calendar-row:focus:not([tabindex=\"-1\"]){outline:0;box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem;outline-style:auto!important}.calendar-cell:focus:not(:focus-visible):not([tabindex=\"-1\"]){box-shadow:var(--cui-calendar-cell-focus-box-shadow);border-radius:.375rem}.calendar-cell-inner{-webkit-user-select:none;user-select:none}\n"] }]
        }], ctorParameters: () => [], propDecorators: { pageIndex: [{ type: i0.Input, args: [{ isSignal: true, alias: "pageIndex", required: false }] }], calendarDays: [{
                type: ViewChildren,
                args: [CalendarDayDirective]
            }], calendarRows: [{
                type: ViewChildren,
                args: [CalendarRowDirective]
            }] } });

class CalendarClassViewPipe {
    transform(view) {
        return {
            calendar: true,
            [`${view}`]: !!view
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassViewPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassViewPipe, isStandalone: true, name: "calendarClassView" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassViewPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarClassView',
                    standalone: true
                }]
        }] });

class CalendarComponent {
    #destroyRef = inject(DestroyRef);
    #calendarService = inject(CalendarService);
    calendarDateChange = output();
    /**
     * Default date of the component
     * @type  Date | string
     */
    calendarDateModel = model(new Date(new Date().setDate(1)).setHours(0, 0, 0, 0), { ...(ngDevMode ? { debugName: "calendarDateModel" } : {}), alias: 'calendarDate' });
    calendarDate = computed(() => {
        const date = convertToDateObject(this.calendarDateModel() ?? this.startDate() ?? new Date(), this.selectionType(), 'calendarDate') ?? new Date();
        const calendarDate = new Date(new Date(date.setDate(1)).setHours(0, 0, 0, 0));
        return convertToDateObject(calendarDate, this.selectionType(), 'calendarDate');
    }, { ...(ngDevMode ? { debugName: "calendarDate" } : {}), equal: equalDates });
    calendarDateEffect = effect(() => {
        this.#calendarService.update({ calendarDate: this.calendarDate() });
        this.calendarDateChange.emit(this.calendarDate());
    }, ...(ngDevMode ? [{ debugName: "calendarDateEffect" }] : []));
    /**
     * The number of calendars that render on desktop devices.
     * @type number
     * default 1
     */
    calendars = input(1, { ...(ngDevMode ? { debugName: "calendars" } : {}), transform: numberAttribute });
    calendarsArray = computed(() => {
        const calendars = ['month', 'year'].includes(this.selectionType()) ? 1 : this.calendars();
        return Array.from({ length: calendars }, (v, i) => i);
    }, ...(ngDevMode ? [{ debugName: "calendarsArray" }] : []));
    /**
     * Set the format of day name.
     * @default 'numeric'
     * @type DayFormatType
     */
    dayFormat = input('numeric', ...(ngDevMode ? [{ debugName: "dayFormat" }] : []));
    dayFormatEffect = effect(() => {
        this.#calendarService.update({ dayFormat: this.dayFormat() });
    }, ...(ngDevMode ? [{ debugName: "dayFormatEffect" }] : []));
    /**
     * Specify the list of dates that cannot be selected.
     * @type (Date | Date[])[]
     */
    disabledDates = input([], ...(ngDevMode ? [{ debugName: "disabledDates" }] : []));
    disabledDatesEffect = effect(() => {
        this.#calendarService.update({ disabledDates: this.disabledDates() });
    }, ...(ngDevMode ? [{ debugName: "disabledDatesEffect" }] : []));
    endDateChange = output();
    startDateChange = output();
    /**
     * Initial selected date.
     * @type  (Date | null)
     */
    startDateModel = model(null, { ...(ngDevMode ? { debugName: "startDateModel" } : {}), alias: 'startDate' });
    startDate = computed(() => {
        const date = this.startDateModel() ?? null;
        return !!date ? convertToDateObject(date, this.selectionType(), 'startDate') : null;
    }, { ...(ngDevMode ? { debugName: "startDate" } : {}), equal: equalDates });
    /**
     * Initial selected to date (range).
     * @type Date | null
     */
    endDateModel = model(null, { ...(ngDevMode ? { debugName: "endDateModel" } : {}), alias: 'endDate' });
    endDate = computed(() => {
        const date = this.endDateModel() ?? null;
        return !!date && this.range() ? convertToDateObject(date, this.selectionType(), 'endDate') : null;
    }, { ...(ngDevMode ? { debugName: "endDate" } : {}), equal: equalDates });
    #datesEffect = effect(() => {
        const startDate = this.startDate();
        const endDate = this.endDate();
        this.#calendarService.update({ startDate: startDate, endDate: endDate });
        this.startDateChange.emit(startDate);
        this.endDateChange.emit(endDate);
    }, ...(ngDevMode ? [{ debugName: "#datesEffect" }] : []));
    /**
     * Sets the day of start week.
     * - 0 - Sunday,
     * - 1 - Monday,
     * - 2 - Tuesday,
     * - 3 - Wednesday,
     * - 4 - Thursday,
     * - 5 - Friday,
     * - 6 - Saturday,
     *
     * @type number
     * @default 1
     */
    firstDayOfWeek = input(1, ...(ngDevMode ? [{ debugName: "firstDayOfWeek" }] : []));
    firstDayOfWeekEffect = effect(() => {
        this.#calendarService.update({ firstDayOfWeek: this.firstDayOfWeek() });
    }, ...(ngDevMode ? [{ debugName: "firstDayOfWeekEffect" }] : []));
    /**
     * Sets the default locale for components. If not set, it is inherited from the browser.
     * @default 'default'
     */
    locale = input('default', ...(ngDevMode ? [{ debugName: "locale" }] : []));
    localeEffect = effect(() => {
        this.#calendarService.update({ locale: this.locale() });
    }, ...(ngDevMode ? [{ debugName: "localeEffect" }] : []));
    /**
     * Max selectable date.
     */
    maxDateInput = input(null, { ...(ngDevMode ? { debugName: "maxDateInput" } : {}), alias: 'maxDate' });
    maxDateEffect = effect(() => {
        const date = this.maxDateInput();
        const maxDate = !!date ? convertToDateObject(date, undefined, 'maxDate') : null;
        this.#calendarService.update({ maxDate: maxDate });
    }, ...(ngDevMode ? [{ debugName: "maxDateEffect" }] : []));
    /**
     * Min selectable date.
     */
    minDateInput = input(null, { ...(ngDevMode ? { debugName: "minDateInput" } : {}), alias: 'minDate' });
    minDateEffect = effect(() => {
        const date = this.minDateInput();
        const minDate = !!date ? convertToDateObject(date, undefined, 'minDate') : null;
        this.#calendarService.update({ minDate: minDate });
    }, ...(ngDevMode ? [{ debugName: "minDateEffect" }] : []));
    /**
     * Show navigation.
     * @type boolean
     */
    navigation = input(true, { ...(ngDevMode ? { debugName: "navigation" } : {}), transform: booleanAttribute });
    /**
     * Reorder year-month navigation, and render year first.
     * @type boolean
     * @default false
     */
    navYearFirst = input(false, { ...(ngDevMode ? { debugName: "navYearFirst" } : {}), transform: booleanAttribute });
    navYearFirstEffect = effect(() => {
        this.#calendarService.update({ navYearFirst: this.navYearFirst() });
    }, ...(ngDevMode ? [{ debugName: "navYearFirstEffect" }] : []));
    /**
     * Allow range selection.
     * @type boolean
     * @default false
     */
    range = input(false, { ...(ngDevMode ? { debugName: "range" } : {}), transform: booleanAttribute });
    rangeEffect = effect(() => {
        this.#calendarService.update({ range: this.range() });
    }, ...(ngDevMode ? [{ debugName: "rangeEffect" }] : []));
    /**
     * Set calendar view.
     * @type 'days' | 'months' | 'years'
     * @default 'days'
     */
    view = model('days', ...(ngDevMode ? [{ debugName: "view" }] : []));
    #viewEffect = effect(() => {
        this.#calendarService.update({ view: this.view() });
    }, ...(ngDevMode ? [{ debugName: "#viewEffect" }] : []));
    /**
     * Set length or format of day name.
     * @type number | 'long' | 'narrow' | 'short'
     * @default 'short'
     */
    weekdayFormat = input('short', ...(ngDevMode ? [{ debugName: "weekdayFormat" }] : []));
    weekdayFormatEffect = effect(() => {
        this.#calendarService.update({ weekdayFormat: this.weekdayFormat() });
    }, ...(ngDevMode ? [{ debugName: "weekdayFormatEffect" }] : []));
    dateFilter = input(...(ngDevMode ? [undefined, { debugName: "dateFilter" }] : []));
    dateFilterEffect = effect(() => {
        this.#calendarService.update({ dateFilter: this.dateFilter() });
    }, ...(ngDevMode ? [{ debugName: "dateFilterEffect" }] : []));
    /**
     * Set whether days in adjacent months shown before or after the current month are selectable. This only applies if the `showAdjacentDays` option is set to true.
     * @type boolean
     * @default false
     * @since 4.4.10
     */
    selectAdjacentDays = input(false, { ...(ngDevMode ? { debugName: "selectAdjacentDays" } : {}), transform: booleanAttribute });
    selectAdjacentDaysEffect = effect(() => {
        this.#calendarService.update({ selectAdjacentDays: this.selectAdjacentDays() });
    }, ...(ngDevMode ? [{ debugName: "selectAdjacentDaysEffect" }] : []));
    /**
     * Set whether to display dates in adjacent months (non-selectable) at the start and end of the current month.
     * @type boolean
     * @default true
     * @since 4.4.10
     */
    showAdjacentDays = input(true, { ...(ngDevMode ? { debugName: "showAdjacentDays" } : {}), transform: booleanAttribute });
    showAdjacentDaysEffect = effect(() => {
        this.#calendarService.update({ showAdjacentDays: this.showAdjacentDays() });
    }, ...(ngDevMode ? [{ debugName: "showAdjacentDaysEffect" }] : []));
    /**
     * Specify the type of date selection as day, week, month, or year.
     * @type 'day' | 'week' | 'month' | 'year'
     * @default 'day'
     * @since 5.0.0
     */
    selectionType = input('day', ...(ngDevMode ? [{ debugName: "selectionType" }] : []));
    selectionTypeEffect = effect(() => {
        const selectionType = this.selectionType();
        const view = ['day', 'week'].includes(selectionType) ? 'days' : selectionType === 'month' ? 'months' : 'years';
        this.#calendarService.update({ selectionType: selectionType, view: view });
    }, ...(ngDevMode ? [{ debugName: "selectionTypeEffect" }] : []));
    /**
     * Set whether to display week numbers in the calendar.
     * @type boolean
     * @default false
     * @since 5.0.0
     */
    showWeekNumber = input(false, { ...(ngDevMode ? { debugName: "showWeekNumber" } : {}), transform: booleanAttribute });
    showWeekNumberEffect = effect(() => {
        this.#calendarService.update({ showWeekNumber: this.showWeekNumber() });
    }, ...(ngDevMode ? [{ debugName: "showWeekNumberEffect" }] : []));
    /**
     * Label displayed over week numbers in the calendar.
     * @type string
     * @default undefined
     * @since 5.0.0
     */
    weekNumbersLabel = input(...(ngDevMode ? [undefined, { debugName: "weekNumbersLabel" }] : []));
    weekNumbersLabelEffect = effect(() => {
        this.#calendarService.update({ weekNumbersLabel: this.weekNumbersLabel() });
    }, ...(ngDevMode ? [{ debugName: "weekNumbersLabelEffect" }] : []));
    ariaNextMonthLabel = input('Next month', ...(ngDevMode ? [{ debugName: "ariaNextMonthLabel" }] : []));
    ariaNextYearLabel = input('Next year', ...(ngDevMode ? [{ debugName: "ariaNextYearLabel" }] : []));
    ariaPrevMonthLabel = input('Previous month', ...(ngDevMode ? [{ debugName: "ariaPrevMonthLabel" }] : []));
    ariaPrevYearLabel = input('Previous year', ...(ngDevMode ? [{ debugName: "ariaPrevYearLabel" }] : []));
    calendarCellHover = output();
    // used with calendarService
    hoverDate = signal(null, ...(ngDevMode ? [{ debugName: "hoverDate" }] : []));
    hoverDateEffect = effect(() => {
        this.calendarCellHover.emit(this.hoverDate());
    }, ...(ngDevMode ? [{ debugName: "hoverDateEffect" }] : []));
    hostClasses = computed(() => {
        const selectionType = this.selectionType();
        const view = this.view();
        return {
            calendars: true,
            [`select-${selectionType}`]: selectionType && view === 'days',
            'show-week-numbers': this.showWeekNumber()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    // @ViewChildren(CalendarMonthComponent) calendarMonths!: QueryList<CalendarMonthComponent>;
    ngOnInit() {
        this.#calendarService.update({
            locale: this.locale() ?? 'default',
            view: this.view(),
            range: this.range(),
            selectionType: this.selectionType()
            // showAdjacentDays: this.showAdjacentDays,
            // selectAdjacentDays: this.selectAdjacentDays
        });
    }
    ngAfterViewInit() {
        // setTimeout(() => {
        this.calendarStateSubscribe();
        // });
    }
    calendarStateSubscribe() {
        const inputs = [
            'dateFilter',
            'dayFormat',
            'disabledDates',
            'firstDayOfWeek',
            'locale',
            'maxDate',
            'minDate',
            'navYearFirst',
            'range',
            'selectAdjacentDays',
            'selectionType',
            'showAdjacentDays',
            'showWeekNumber',
            'weekdayFormat',
            'weekNumbersLabel'
        ];
        // const models = ['startDate', 'endDate', 'calendarDate'];
        this.#calendarService?.calendarState$
            .pipe(debounceTime(1), takeUntilDestroyed(this.#destroyRef))
            .subscribe((state) => {
            const keys = Object.keys(state);
            for (const key of keys) {
                if (key in this) {
                    if (inputs.includes(key)) {
                        continue;
                    }
                    // if (models.includes(key) && state[key] !== undefined) {
                    //   const model = `${key}Model`;
                    //   // @ts-ignore
                    //   this[model].set(state[key]);
                    // }
                    if (key === 'startDate') {
                        this.startDateModel.set(state[key]);
                        continue;
                    }
                    if (key === 'endDate') {
                        this.endDateModel.set(state[key]);
                        continue;
                    }
                    if (key === 'calendarDate') {
                        this.calendarDateModel.set(state[key]);
                        continue;
                    }
                    if (key === 'view') {
                        this.view.set(state[key]);
                        continue;
                    }
                    // @ts-ignore
                    const isWritableSignal = typeof this[key] === 'function' && typeof this[key].set === 'function';
                    // @ts-ignore
                    const isReadonlySignal = typeof this[key] === 'function' && typeof this[key].set === 'undefined';
                    // @ts-ignore
                    if (isWritableSignal && !isReadonlySignal ? this[key]() !== state[key] : this[key] !== state[key]) {
                        // @ts-ignore
                        isWritableSignal ? this[key].set(state[key]) : (this[key] = state[key]);
                        // this[key] = state[key];
                    }
                }
            }
        });
    }
    setCalendarPage(years, months = 0, setMonth) {
        const calendarDate = this.calendarDate();
        const year = calendarDate.getFullYear();
        const month = calendarDate.getMonth();
        const d = new Date(year, month, 1);
        years && d.setFullYear(d.getFullYear() + years);
        months && d.setMonth(d.getMonth() + months);
        typeof setMonth === 'number' && d.setMonth(setMonth);
        this.calendarDateModel.set(d);
    }
    handleNavigationClick(direction, years = false) {
        const view = this.view();
        if (direction === 'prev') {
            if (years) {
                this.setCalendarPage(view === 'years' ? -10 : -1);
                return;
            }
            if (view !== 'days') {
                this.setCalendarPage(-1);
                return;
            }
            this.setCalendarPage(0, -1);
            return;
        }
        if (direction === 'next') {
            if (years) {
                this.setCalendarPage(view === 'years' ? 10 : 1);
                return;
            }
            if (view !== 'days') {
                this.setCalendarPage(1);
                return;
            }
            this.setCalendarPage(0, 1);
            return;
        }
    }
    /**
     * Clear startDate and endDate
     * @type void
     */
    clearDates() {
        this.#calendarService.update({ startDate: null, endDate: null });
    }
    handleFocusChange($event) {
        if ($event === null) {
            this.#calendarService.update({ hoverDate: null });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: CalendarComponent, isStandalone: true, selector: "c-calendar", inputs: { calendarDateModel: { classPropertyName: "calendarDateModel", publicName: "calendarDate", isSignal: true, isRequired: false, transformFunction: null }, calendars: { classPropertyName: "calendars", publicName: "calendars", isSignal: true, isRequired: false, transformFunction: null }, dayFormat: { classPropertyName: "dayFormat", publicName: "dayFormat", isSignal: true, isRequired: false, transformFunction: null }, disabledDates: { classPropertyName: "disabledDates", publicName: "disabledDates", isSignal: true, isRequired: false, transformFunction: null }, startDateModel: { classPropertyName: "startDateModel", publicName: "startDate", isSignal: true, isRequired: false, transformFunction: null }, endDateModel: { classPropertyName: "endDateModel", publicName: "endDate", isSignal: true, isRequired: false, transformFunction: null }, firstDayOfWeek: { classPropertyName: "firstDayOfWeek", publicName: "firstDayOfWeek", isSignal: true, isRequired: false, transformFunction: null }, locale: { classPropertyName: "locale", publicName: "locale", isSignal: true, isRequired: false, transformFunction: null }, maxDateInput: { classPropertyName: "maxDateInput", publicName: "maxDate", isSignal: true, isRequired: false, transformFunction: null }, minDateInput: { classPropertyName: "minDateInput", publicName: "minDate", isSignal: true, isRequired: false, transformFunction: null }, navigation: { classPropertyName: "navigation", publicName: "navigation", isSignal: true, isRequired: false, transformFunction: null }, navYearFirst: { classPropertyName: "navYearFirst", publicName: "navYearFirst", isSignal: true, isRequired: false, transformFunction: null }, range: { classPropertyName: "range", publicName: "range", isSignal: true, isRequired: false, transformFunction: null }, view: { classPropertyName: "view", publicName: "view", isSignal: true, isRequired: false, transformFunction: null }, weekdayFormat: { classPropertyName: "weekdayFormat", publicName: "weekdayFormat", isSignal: true, isRequired: false, transformFunction: null }, dateFilter: { classPropertyName: "dateFilter", publicName: "dateFilter", isSignal: true, isRequired: false, transformFunction: null }, selectAdjacentDays: { classPropertyName: "selectAdjacentDays", publicName: "selectAdjacentDays", isSignal: true, isRequired: false, transformFunction: null }, showAdjacentDays: { classPropertyName: "showAdjacentDays", publicName: "showAdjacentDays", isSignal: true, isRequired: false, transformFunction: null }, selectionType: { classPropertyName: "selectionType", publicName: "selectionType", isSignal: true, isRequired: false, transformFunction: null }, showWeekNumber: { classPropertyName: "showWeekNumber", publicName: "showWeekNumber", isSignal: true, isRequired: false, transformFunction: null }, weekNumbersLabel: { classPropertyName: "weekNumbersLabel", publicName: "weekNumbersLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaNextMonthLabel: { classPropertyName: "ariaNextMonthLabel", publicName: "ariaNextMonthLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaNextYearLabel: { classPropertyName: "ariaNextYearLabel", publicName: "ariaNextYearLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaPrevMonthLabel: { classPropertyName: "ariaPrevMonthLabel", publicName: "ariaPrevMonthLabel", isSignal: true, isRequired: false, transformFunction: null }, ariaPrevYearLabel: { classPropertyName: "ariaPrevYearLabel", publicName: "ariaPrevYearLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { calendarDateChange: "calendarDateChange", calendarDateModel: "calendarDateChange", endDateChange: "endDateChange", startDateChange: "startDateChange", startDateModel: "startDateChange", endDateModel: "endDateChange", view: "viewChange", calendarCellHover: "calendarCellHover" }, host: { properties: { "class": "hostClasses()" }, classAttribute: "calendar" }, providers: [CalendarService], exportAs: ["cCalendar"], ngImport: i0, template: "@for (calendar of calendarsArray(); track calendar; let i = $index) {\n  <div [class]=\"view() | calendarClassView\">\n    <c-calendar-navigation\n      (navigationClick)=\"handleNavigationClick($event.direction, $event.years)\"\n      [pageIndex]=\"i\"\n      [navigation]=\"navigation()\"\n      [ariaNextMonthLabel]=\"ariaNextMonthLabel()\"\n      [ariaNextYearLabel]=\"ariaNextYearLabel()\"\n      [ariaPrevMonthLabel]=\"ariaPrevMonthLabel()\"\n      [ariaPrevYearLabel]=\"ariaPrevYearLabel()\"\n    />\n\n    <c-calendar-month\n      [pageIndex]=\"i\"\n      cdkMonitorSubtreeFocus\n      (cdkFocusChange)=\"handleFocusChange($event)\"\n    />\n  </div>\n}\n", styles: [".btn-transparent:focus-visible{outline:-webkit-focus-ring-color auto 1px}\n"], dependencies: [{ kind: "component", type: CalendarNavigationComponent, selector: "c-calendar-navigation", inputs: ["pageIndex", "navigation", "ariaNextMonthLabel", "ariaNextYearLabel", "ariaPrevMonthLabel", "ariaPrevYearLabel"], outputs: ["navigationClick"] }, { kind: "component", type: CalendarMonthComponent, selector: "c-calendar-month", inputs: ["pageIndex"], exportAs: ["cCalendarMonth"] }, { kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i1$2.CdkMonitorFocus, selector: "[cdkMonitorElementFocus], [cdkMonitorSubtreeFocus]", outputs: ["cdkFocusChange"], exportAs: ["cdkMonitorFocus"] }, { kind: "pipe", type: CalendarClassViewPipe, name: "calendarClassView" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-calendar', exportAs: 'cCalendar', providers: [CalendarService], imports: [CalendarNavigationComponent, CalendarClassViewPipe, CalendarMonthComponent, A11yModule], host: { class: 'calendar', '[class]': 'hostClasses()' }, template: "@for (calendar of calendarsArray(); track calendar; let i = $index) {\n  <div [class]=\"view() | calendarClassView\">\n    <c-calendar-navigation\n      (navigationClick)=\"handleNavigationClick($event.direction, $event.years)\"\n      [pageIndex]=\"i\"\n      [navigation]=\"navigation()\"\n      [ariaNextMonthLabel]=\"ariaNextMonthLabel()\"\n      [ariaNextYearLabel]=\"ariaNextYearLabel()\"\n      [ariaPrevMonthLabel]=\"ariaPrevMonthLabel()\"\n      [ariaPrevYearLabel]=\"ariaPrevYearLabel()\"\n    />\n\n    <c-calendar-month\n      [pageIndex]=\"i\"\n      cdkMonitorSubtreeFocus\n      (cdkFocusChange)=\"handleFocusChange($event)\"\n    />\n  </div>\n}\n", styles: [".btn-transparent:focus-visible{outline:-webkit-focus-ring-color auto 1px}\n"] }]
        }], propDecorators: { calendarDateChange: [{ type: i0.Output, args: ["calendarDateChange"] }], calendarDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "calendarDate", required: false }] }, { type: i0.Output, args: ["calendarDateChange"] }], calendars: [{ type: i0.Input, args: [{ isSignal: true, alias: "calendars", required: false }] }], dayFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "dayFormat", required: false }] }], disabledDates: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabledDates", required: false }] }], endDateChange: [{ type: i0.Output, args: ["endDateChange"] }], startDateChange: [{ type: i0.Output, args: ["startDateChange"] }], startDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "startDate", required: false }] }, { type: i0.Output, args: ["startDateChange"] }], endDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "endDate", required: false }] }, { type: i0.Output, args: ["endDateChange"] }], firstDayOfWeek: [{ type: i0.Input, args: [{ isSignal: true, alias: "firstDayOfWeek", required: false }] }], locale: [{ type: i0.Input, args: [{ isSignal: true, alias: "locale", required: false }] }], maxDateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "maxDate", required: false }] }], minDateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "minDate", required: false }] }], navigation: [{ type: i0.Input, args: [{ isSignal: true, alias: "navigation", required: false }] }], navYearFirst: [{ type: i0.Input, args: [{ isSignal: true, alias: "navYearFirst", required: false }] }], range: [{ type: i0.Input, args: [{ isSignal: true, alias: "range", required: false }] }], view: [{ type: i0.Input, args: [{ isSignal: true, alias: "view", required: false }] }, { type: i0.Output, args: ["viewChange"] }], weekdayFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "weekdayFormat", required: false }] }], dateFilter: [{ type: i0.Input, args: [{ isSignal: true, alias: "dateFilter", required: false }] }], selectAdjacentDays: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectAdjacentDays", required: false }] }], showAdjacentDays: [{ type: i0.Input, args: [{ isSignal: true, alias: "showAdjacentDays", required: false }] }], selectionType: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectionType", required: false }] }], showWeekNumber: [{ type: i0.Input, args: [{ isSignal: true, alias: "showWeekNumber", required: false }] }], weekNumbersLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "weekNumbersLabel", required: false }] }], ariaNextMonthLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaNextMonthLabel", required: false }] }], ariaNextYearLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaNextYearLabel", required: false }] }], ariaPrevMonthLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaPrevMonthLabel", required: false }] }], ariaPrevYearLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaPrevYearLabel", required: false }] }], calendarCellHover: [{ type: i0.Output, args: ["calendarCellHover"] }] } });

class CalendarClassRowPipe {
    transform(week, datesConfig) {
        const { minDate, maxDate, disabledDates, dateFilter, calendarDate, selectionType } = { ...datesConfig };
        const date = convertToDateObject(week.weekNumber === 0 ? `${calendarDate.getFullYear()}W53` : `${calendarDate.getFullYear()}W${week.weekNumber}`, selectionType);
        const isDisabled = isDateDisabled(date, minDate, maxDate, disabledDates) || (dateFilter ? !dateFilter(date) : false);
        return {
            'calendar-row': true,
            disabled: isDisabled
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassRowPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassRowPipe, isStandalone: true, name: "calendarClassRow" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarClassRowPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'calendarClassRow',
                    standalone: true
                }]
        }] });

class CalendarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalendarModule, imports: [CalendarComponent,
            CalendarClassYearPipe,
            CalendarClassDayPipe,
            CalendarClassMonthPipe,
            CalendarDayDirective,
            CalendarClassViewPipe,
            CalendarNavigationComponent,
            CalendarMonthComponent,
            CalendarDayPipe,
            CalendarWeekdayPipe,
            CalendarDayTitlePipe,
            CalendarClassRowPipe], exports: [CalendarComponent,
            CalendarMonthComponent,
            CalendarNavigationComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarModule, imports: [CalendarComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalendarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CalendarComponent,
                        CalendarClassYearPipe,
                        CalendarClassDayPipe,
                        CalendarClassMonthPipe,
                        CalendarDayDirective,
                        CalendarClassViewPipe,
                        CalendarNavigationComponent,
                        CalendarMonthComponent,
                        CalendarDayPipe,
                        CalendarWeekdayPipe,
                        CalendarDayTitlePipe,
                        CalendarClassRowPipe
                    ],
                    exports: [
                        CalendarComponent,
                        CalendarMonthComponent,
                        CalendarNavigationComponent
                    ]
                }]
        }] });

class CalloutComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        return {
            callout: true,
            [`callout-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalloutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: CalloutComponent, isStandalone: true, selector: "c-callout, [cCallout]", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "callout" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalloutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-callout, [cCallout]', template: '<ng-content />', host: { class: 'callout', '[class]': 'hostClasses()' }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }] } });

class CalloutModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalloutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CalloutModule, imports: [CalloutComponent], exports: [CalloutComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalloutModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CalloutModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [CalloutComponent],
                    imports: [CalloutComponent]
                }]
        }] });

class CardComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Sets the text color context of the component to one of CoreUI’s themed colors.
     * via TextColorDirective
     * @type TextColors
     */
    textColor = input(...(ngDevMode ? [undefined, { debugName: "textColor" }] : []));
    /**
     * Sets the component's color scheme to one of CoreUI themed colors, ensuring the text color contrast adheres to the WCAG 4.5:1 contrast ratio standard for accessibility.
     * via TextBgColorDirective
     * @type Colors
     * @since 5.0.0
     */
    textBgColor = input(...(ngDevMode ? [undefined, { debugName: "textBgColor" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        return {
            card: true,
            [`bg-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: CardComponent, isStandalone: true, selector: "c-card, [c-card]", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, textColor: { classPropertyName: "textColor", publicName: "textColor", isSignal: true, isRequired: false, transformFunction: null }, textBgColor: { classPropertyName: "textBgColor", publicName: "textBgColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "card" }, hostDirectives: [{ directive: TextColorDirective, inputs: ["cTextColor", "textColor"] }, { directive: TextBgColorDirective, inputs: ["cTextBgColor", "textBgColor"] }], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card, [c-card]',
                    template: '<ng-content />',
                    hostDirectives: [
                        { directive: TextColorDirective, inputs: ['cTextColor: textColor'] },
                        { directive: TextBgColorDirective, inputs: ['cTextBgColor: textBgColor'] }
                    ],
                    host: { class: 'card', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], textColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "textColor", required: false }] }], textBgColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "textBgColor", required: false }] }] } });

class CardBodyComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardBodyComponent, isStandalone: true, selector: "c-card-body, [c-card-body]", host: { classAttribute: "card-body" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card-body, [c-card-body]',
                    template: '<ng-content />',
                    host: { class: 'card-body' }
                }]
        }] });

class CardFooterComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardFooterComponent, isStandalone: true, selector: "c-card-footer, [c-card-footer]", host: { classAttribute: "card-footer" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card-footer, [c-card-footer]',
                    template: '<ng-content />',
                    host: { class: 'card-footer' }
                }]
        }] });

class CardGroupComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardGroupComponent, isStandalone: true, selector: "c-card-group, [c-card-group]", host: { classAttribute: "card-group" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card-group, [c-card-group]',
                    template: '<ng-content />',
                    host: { class: 'card-group' }
                }]
        }] });

class CardHeaderComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardHeaderComponent, isStandalone: true, selector: "c-card-header, [c-card-header]", host: { classAttribute: "card-header" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card-header, [c-card-header]',
                    template: '<ng-content />',
                    host: { class: 'card-header' }
                }]
        }] });

class CardHeaderActionsComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardHeaderActionsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardHeaderActionsComponent, isStandalone: true, selector: "c-card-header-actions, [c-card-header-actions]", host: { classAttribute: "card-header-actions" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardHeaderActionsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-card-header-actions, [c-card-header-actions]',
                    template: '<ng-content />',
                    host: { class: 'card-header-actions' }
                }]
        }] });

class CardImgDirective {
    /**
     * Optionally orientate the image to the top, bottom, or make it overlaid across the card.
     * @type {'top | 'bottom'}
     */
    orientation = input(undefined, { ...(ngDevMode ? { debugName: "orientation" } : {}), alias: 'cCardImg' });
    hostClasses = computed(() => {
        const orientation = this.orientation();
        const suffix = !!orientation ? `-${orientation}` : '';
        const horizontal = ['start', 'end'].includes(orientation ?? '-') ? orientation : undefined;
        return {
            [`card-img${suffix}`]: !horizontal,
            'img-fluid': !!horizontal,
            [`rounded-${horizontal}`]: !!horizontal
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardImgDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: CardImgDirective, isStandalone: true, selector: "[cCardImg]", inputs: { orientation: { classPropertyName: "orientation", publicName: "cCardImg", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardImgDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCardImg]',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { orientation: [{ type: i0.Input, args: [{ isSignal: true, alias: "cCardImg", required: false }] }] } });

class CardImgOverlayComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardImgOverlayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CardImgOverlayComponent, isStandalone: true, selector: "c-card-img-overlay", host: { classAttribute: "card-img-overlay" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardImgOverlayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-card-img-overlay', template: '<ng-content />', host: { class: 'card-img-overlay' }, styles: [":host{display:block}\n"] }]
        }] });

class CardLinkDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: CardLinkDirective, isStandalone: true, selector: "[cCardLink]", host: { classAttribute: "card-link" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCardLink]',
                    host: { class: 'card-link' }
                }]
        }] });

class CardSubtitleDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardSubtitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: CardSubtitleDirective, isStandalone: true, selector: "[cCardSubtitle]", host: { classAttribute: "card-subtitle" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardSubtitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCardSubtitle]',
                    host: { class: 'card-subtitle' }
                }]
        }] });

class CardTextDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardTextDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: CardTextDirective, isStandalone: true, selector: "[cCardText]", host: { classAttribute: "card-text" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardTextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCardText]',
                    host: { class: 'card-text' }
                }]
        }] });

class CardTitleDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: CardTitleDirective, isStandalone: true, selector: "[cCardTitle]", host: { classAttribute: "card-title" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCardTitle]',
                    host: { class: 'card-title' }
                }]
        }] });

class CardModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CardModule, imports: [CardComponent,
            CardBodyComponent,
            CardFooterComponent,
            CardGroupComponent,
            CardHeaderComponent,
            CardHeaderActionsComponent,
            CardImgDirective,
            CardImgOverlayComponent,
            CardLinkDirective,
            CardSubtitleDirective,
            CardTextDirective,
            CardTitleDirective], exports: [CardComponent,
            CardBodyComponent,
            CardFooterComponent,
            CardGroupComponent,
            CardHeaderComponent,
            CardHeaderActionsComponent,
            CardImgDirective,
            CardImgOverlayComponent,
            CardLinkDirective,
            CardSubtitleDirective,
            CardTextDirective,
            CardTitleDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CardModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CardComponent,
                        CardBodyComponent,
                        CardFooterComponent,
                        CardGroupComponent,
                        CardHeaderComponent,
                        CardHeaderActionsComponent,
                        CardImgDirective,
                        CardImgOverlayComponent,
                        CardLinkDirective,
                        CardSubtitleDirective,
                        CardTextDirective,
                        CardTitleDirective
                    ],
                    exports: [
                        CardComponent,
                        CardBodyComponent,
                        CardFooterComponent,
                        CardGroupComponent,
                        CardHeaderComponent,
                        CardHeaderActionsComponent,
                        CardImgDirective,
                        CardImgOverlayComponent,
                        CardLinkDirective,
                        CardSubtitleDirective,
                        CardTextDirective,
                        CardTitleDirective
                    ]
                }]
        }] });

class CarouselService {
    #carouselIndex = new BehaviorSubject({});
    carouselIndex$ = this.#carouselIndex.asObservable();
    setIndex(index) {
        this.#carouselIndex.next(index);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselService, decorators: [{
            type: Injectable
        }] });

class CarouselState {
    #carouselService = inject(CarouselService);
    #state = {
        activeItemIndex: -1,
        animate: true,
        items: [],
        direction: 'next',
        transition: 'slide',
        interval: 0
    };
    get state() {
        return this.#state;
    }
    set state(state) {
        const prevState = { ...this.#state };
        const nextState = { ...this.#state, ...state };
        this.#state = nextState;
        if (prevState.activeItemIndex !== nextState.activeItemIndex) {
            const activeItemIndex = this.state.activeItemIndex || 0;
            const itemInterval = (this.state.items && this.state.items[activeItemIndex]?.interval()) || -1;
            this.#carouselService.setIndex({
                active: nextState.activeItemIndex,
                interval: itemInterval,
                lastItemIndex: (nextState.items?.length ?? 0) - 1
            });
        }
    }
    setItems(newItems) {
        if (newItems.length) {
            const itemsArray = newItems;
            itemsArray.forEach((item, i) => {
                item.index = i;
            });
            this.state = {
                items: [...itemsArray]
            };
        }
        else {
            this.reset();
        }
    }
    setNextIndex(nextIndex) {
        this.#carouselService.setIndex(nextIndex);
    }
    direction(direction = 'next') {
        this.state = { direction };
        const { activeItemIndex = -1, items } = this.state;
        const itemsCount = items?.length ?? 0;
        if (itemsCount > 0) {
            return direction === 'next'
                ? activeItemIndex === itemsCount - 1
                    ? 0
                    : activeItemIndex + 1
                : activeItemIndex === 0
                    ? itemsCount - 1
                    : activeItemIndex - 1;
        }
        else {
            return 0;
        }
    }
    reset() {
        this.state = {
            activeItemIndex: -1,
            animate: true,
            items: [],
            direction: 'next',
            transition: 'slide',
            interval: 0
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselState, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselState });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselState, decorators: [{
            type: Injectable
        }] });

class CarouselConfig {
    /* Animate transition of slides */
    activeIndex = 0;
    /* Animate transition of slides */
    animate = true;
    /* Default direction of auto changing of slides */
    direction = 'next';
    /* Default interval of auto changing of slides */
    interval;
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselConfig, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselConfig, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselConfig, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class CarouselComponent {
    config = inject(CarouselConfig);
    #hostElement = inject(ElementRef);
    #carouselService = inject(CarouselService);
    #carouselState = inject(CarouselState);
    #intersectionService = inject(IntersectionService);
    #listenersService = inject(ListenersService);
    constructor() {
        this.loadConfig();
    }
    loadConfig() {
        this.activeIndex.update((activeIndex) => this.config?.activeIndex ?? activeIndex);
        this.animate.update((animate) => this.config?.animate ?? animate);
        this.direction.update((direction) => this.config?.direction ?? direction);
        this.interval.update((interval) => this.config?.interval ?? interval);
    }
    /**
     * Index of the active item.
     * @return number
     */
    activeIndexInput = input(0, { ...(ngDevMode ? { debugName: "activeIndexInput" } : {}), alias: 'activeIndex', transform: numberAttribute });
    activeIndex = linkedSignal({ ...(ngDevMode ? { debugName: "activeIndex" } : {}), source: this.activeIndexInput,
        computation: (value) => value });
    /**
     * Carousel automatically starts cycle items.
     * @return boolean
     */
    animateInput = input(true, { ...(ngDevMode ? { debugName: "animateInput" } : {}), alias: 'animate' });
    animate = linkedSignal({ ...(ngDevMode ? { debugName: "animate" } : {}), source: this.animateInput,
        computation: (value) => value });
    /**
     * Carousel direction. [docs]
     * @return {'next' | 'prev'}
     */
    directionInput = input('next', { ...(ngDevMode ? { debugName: "directionInput" } : {}), alias: 'direction' });
    direction = linkedSignal({ ...(ngDevMode ? { debugName: "direction" } : {}), source: this.directionInput,
        computation: (value) => value });
    /**
     * The amount of time to delay between automatically cycling an item. If false, carousel will not automatically cycle.
     * @return number
     * @default 0
     */
    intervalInput = input(-1, { ...(ngDevMode ? { debugName: "intervalInput" } : {}), alias: 'interval', transform: numberAttribute });
    interval = linkedSignal({ ...(ngDevMode ? { debugName: "interval" } : {}), source: this.intervalInput,
        computation: (value) => value });
    #intervalEffect = effect(() => {
        const interval = this.interval();
        this.#carouselState.state = { interval: interval };
        interval ? this.setTimer() : this.resetTimer();
    }, ...(ngDevMode ? [{ debugName: "#intervalEffect" }] : []));
    /**
     * Sets which event handlers you’d like provided to your pause prop. You can specify one trigger or an array of them.
     * @return {'hover' | 'focus' | 'click'}
     */
    pause = input('hover', ...(ngDevMode ? [{ debugName: "pause" }] : []));
    /**
     * Support left/right swipe interactions on touchscreen devices.
     * @return boolean
     * @default true
     */
    touch = input(true, ...(ngDevMode ? [{ debugName: "touch" }] : []));
    /**
     * Set type of the transition.
     * @return {'slide' | 'crossfade'}
     * @default 'slide'
     */
    transition = input('slide', ...(ngDevMode ? [{ debugName: "transition" }] : []));
    /**
     * Set whether the carousel should cycle continuously or have hard stops.
     * @return boolean
     * @default true
     */
    wrap = input(true, ...(ngDevMode ? [{ debugName: "wrap" }] : []));
    /**
     * Event emitted on carousel item change. [docs]
     * @return number
     */
    itemChange = output();
    timerId;
    activeItemInterval = 0;
    swipeSubscription;
    #destroyRef = inject(DestroyRef);
    ngOnInit() {
        this.carouselStateSubscribe();
    }
    ngOnDestroy() {
        this.resetTimer();
        this.clearListeners();
        this.swipeSubscribe(false);
    }
    ngAfterContentInit() {
        this.intersectionServiceSubscribe();
        this.#carouselState.state = {
            activeItemIndex: this.activeIndex(),
            animate: this.animate(),
            interval: this.interval(),
            transition: this.transition()
        };
        this.setListeners();
        this.swipeSubscribe();
    }
    setListeners() {
        const config = {
            hostElement: this.#hostElement,
            trigger: this.pause() || [],
            callbackOff: () => {
                this.setTimer();
            },
            callbackOn: () => {
                this.resetTimer();
            }
        };
        this.#listenersService.setListeners(config);
    }
    clearListeners() {
        this.#listenersService.clearListeners();
    }
    set visible(value) {
        this.#visible = value;
    }
    get visible() {
        return this.#visible;
    }
    #visible = true;
    setTimer() {
        const interval = this.activeItemInterval || this.interval();
        const direction = this.direction();
        this.resetTimer();
        if (interval > 0) {
            this.timerId = setTimeout(() => {
                const nextIndex = this.#carouselState.direction(direction);
                this.#carouselState.state = { activeItemIndex: nextIndex };
            }, interval);
        }
    }
    resetTimer() {
        clearTimeout(this.timerId);
        this.timerId = undefined;
    }
    carouselStateSubscribe() {
        this.#carouselService.carouselIndex$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((nextItem) => {
            if ('active' in nextItem && typeof nextItem.active === 'number') {
                this.itemChange?.emit(nextItem.active);
            }
            this.activeItemInterval =
                typeof nextItem.interval === 'number' && nextItem.interval > -1 ? nextItem.interval : this.interval();
            const direction = this.direction();
            const isLastItem = (nextItem.active === nextItem.lastItemIndex && direction === 'next') ||
                (nextItem.active === 0 && direction === 'prev');
            !this.wrap() && isLastItem ? this.resetTimer() : this.setTimer();
        });
    }
    intersectionServiceSubscribe() {
        this.#intersectionService.createIntersectionObserver(this.#hostElement);
        this.#intersectionService.intersecting$
            .pipe(filter((next) => next.hostElement === this.#hostElement), finalize(() => {
            this.#intersectionService.unobserve(this.#hostElement);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((next) => {
            this.visible = next.isIntersecting;
            next.isIntersecting ? this.setTimer() : this.resetTimer();
        });
    }
    swipeSubscribe(subscribe = true) {
        if (this.touch() && subscribe) {
            const carouselElement = this.#hostElement.nativeElement;
            const touchStart$ = fromEvent(carouselElement, 'touchstart');
            const touchEnd$ = fromEvent(carouselElement, 'touchend');
            const touchMove$ = fromEvent(carouselElement, 'touchmove');
            this.swipeSubscription = touchStart$
                .pipe(zipWith(touchEnd$.pipe(withLatestFrom(touchMove$))), takeUntilDestroyed(this.#destroyRef))
                .subscribe(([touchstart, [touchend, touchmove]]) => {
                touchstart.stopPropagation();
                touchmove.stopPropagation();
                const distanceX = touchstart.touches[0]?.clientX - touchmove.touches[0]?.clientX || 0;
                if (Math.abs(distanceX) > 0.3 * carouselElement.clientWidth && touchstart.timeStamp <= touchmove.timeStamp) {
                    const nextIndex = this.#carouselState.direction(distanceX > 0 ? 'next' : 'prev');
                    this.#carouselState.state = { activeItemIndex: nextIndex };
                }
            });
        }
        else {
            this.swipeSubscription?.unsubscribe();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: CarouselComponent, isStandalone: true, selector: "c-carousel", inputs: { activeIndexInput: { classPropertyName: "activeIndexInput", publicName: "activeIndex", isSignal: true, isRequired: false, transformFunction: null }, animateInput: { classPropertyName: "animateInput", publicName: "animate", isSignal: true, isRequired: false, transformFunction: null }, directionInput: { classPropertyName: "directionInput", publicName: "direction", isSignal: true, isRequired: false, transformFunction: null }, intervalInput: { classPropertyName: "intervalInput", publicName: "interval", isSignal: true, isRequired: false, transformFunction: null }, pause: { classPropertyName: "pause", publicName: "pause", isSignal: true, isRequired: false, transformFunction: null }, touch: { classPropertyName: "touch", publicName: "touch", isSignal: true, isRequired: false, transformFunction: null }, transition: { classPropertyName: "transition", publicName: "transition", isSignal: true, isRequired: false, transformFunction: null }, wrap: { classPropertyName: "wrap", publicName: "wrap", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { itemChange: "itemChange" }, host: { properties: { "class.carousel-fade": "transition() === \"crossfade\" && animate()" }, classAttribute: "carousel slide" }, providers: [CarouselService, CarouselState, ListenersService], exportAs: ["cCarousel"], hostDirectives: [{ directive: ThemeDirective, inputs: ["dark", "dark"] }], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel', template: '<ng-content />', providers: [CarouselService, CarouselState, ListenersService], hostDirectives: [{ directive: ThemeDirective, inputs: ['dark'] }], exportAs: 'cCarousel', host: {
                        class: 'carousel slide',
                        '[class.carousel-fade]': 'transition() === "crossfade" && animate()'
                    }, styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { activeIndexInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "activeIndex", required: false }] }], animateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "animate", required: false }] }], directionInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "direction", required: false }] }], intervalInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "interval", required: false }] }], pause: [{ type: i0.Input, args: [{ isSignal: true, alias: "pause", required: false }] }], touch: [{ type: i0.Input, args: [{ isSignal: true, alias: "touch", required: false }] }], transition: [{ type: i0.Input, args: [{ isSignal: true, alias: "transition", required: false }] }], wrap: [{ type: i0.Input, args: [{ isSignal: true, alias: "wrap", required: false }] }], itemChange: [{ type: i0.Output, args: ["itemChange"] }] } });

class CarouselCaptionComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselCaptionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: CarouselCaptionComponent, isStandalone: true, selector: "c-carousel-caption", host: { properties: { "class.carousel-caption": "true" } }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselCaptionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel-caption', template: '<ng-content />', host: {
                        '[class.carousel-caption]': 'true'
                    }, styles: [":host{display:block}\n"] }]
        }] });

class CarouselControlComponent {
    #carouselState = inject(CarouselState);
    /**
     * Carousel control caption. [docs]
     * @return string
     */
    captionInput = input(undefined, { ...(ngDevMode ? { debugName: "captionInput" } : {}), alias: 'caption' });
    caption = linkedSignal({ ...(ngDevMode ? { debugName: "caption" } : {}), source: this.captionInput,
        computation: (value) => {
            return !!value ? value : this.direction() === 'prev' ? 'Previous' : 'Next';
        } });
    /**
     * Carousel control direction.
     * @return {'next' | 'prev'}
     */
    direction = input('next', ...(ngDevMode ? [{ debugName: "direction" }] : []));
    /**
     * Carousel control role.
     * @return string
     */
    role = input('button', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        return `carousel-control-${this.direction()}`;
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    carouselControlIconClass = computed(() => {
        return `carousel-control-${this.direction()}-icon`;
    }, ...(ngDevMode ? [{ debugName: "carouselControlIconClass" }] : []));
    onKeyUp($event) {
        if ($event.key === 'Enter') {
            this.#play();
        }
        if ($event.key === 'ArrowLeft') {
            this.#play('prev');
        }
        if ($event.key === 'ArrowRight') {
            this.#play('next');
        }
    }
    onClick($event) {
        this.#play();
    }
    #play(direction = this.direction()) {
        const nextIndex = this.#carouselState.direction(direction);
        this.#carouselState.state = { activeItemIndex: nextIndex };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselControlComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: CarouselControlComponent, isStandalone: true, selector: "c-carousel-control", inputs: { captionInput: { classPropertyName: "captionInput", publicName: "caption", isSignal: true, isRequired: false, transformFunction: null }, direction: { classPropertyName: "direction", publicName: "direction", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "keyup": "onKeyUp($event)", "click": "onClick($event)" }, properties: { "attr.role": "role()", "class": "hostClasses()" } }, exportAs: ["cCarouselControl"], ngImport: i0, template: "<ng-content>\n  <span [attr.aria-hidden]=\"true\" [class]=\"carouselControlIconClass()\"></span>\n  <span class=\"visually-hidden\">{{ caption() }}</span>\n</ng-content>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselControlComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel-control', exportAs: 'cCarouselControl', host: {
                        '[attr.role]': 'role()',
                        '[class]': 'hostClasses()',
                        '(keyup)': 'onKeyUp($event)',
                        '(click)': 'onClick($event)'
                    }, template: "<ng-content>\n  <span [attr.aria-hidden]=\"true\" [class]=\"carouselControlIconClass()\"></span>\n  <span class=\"visually-hidden\">{{ caption() }}</span>\n</ng-content>\n" }]
        }], propDecorators: { captionInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "caption", required: false }] }], direction: [{ type: i0.Input, args: [{ isSignal: true, alias: "direction", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class CarouselIndicatorsComponent {
    #destroyRef = inject(DestroyRef);
    #carouselService = inject(CarouselService);
    #carouselState = inject(CarouselState);
    items = [];
    active = 0;
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    templates = computed(() => {
        return this.contentTemplates().reduce((acc, child) => {
            acc[child.id] = child.templateRef;
            return acc;
        }, {});
    }, ...(ngDevMode ? [{ debugName: "templates" }] : []));
    ngOnInit() {
        this.#carouselService.carouselIndex$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((nextIndex) => {
            this.items = this.#carouselState?.state?.items?.map((item) => item.index) ?? [];
            if ('active' in nextIndex) {
                this.active = nextIndex.active ?? 0;
            }
        });
    }
    onClick(index) {
        if (index !== this.active) {
            const direction = index < this.active ? 'prev' : 'next';
            this.#carouselState.state = { direction, activeItemIndex: index };
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselIndicatorsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: CarouselIndicatorsComponent, isStandalone: true, selector: "c-carousel-indicators", host: { classAttribute: "carousel-indicators" }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cCarouselIndicators"], ngImport: i0, template: "@let tmpl = templates();\n<ng-container *ngTemplateOutlet=\"tmpl['carouselIndicatorsTemplate'] || defaultCarouselIndicatorsTemplate; context: {$implicit: items, active: active}\" />\n\n<ng-template #defaultCarouselIndicatorsTemplate>\n  @for (item of items; track item; let i = $index) {\n    <button\n      [attr.data-coreui-target]=\"i\"\n      type=\"button\"\n      (click)=\"onClick(i)\"\n      [class]=\"{ active: active === i }\"\n      [attr.aria-current]=\"active === i\">\n    </button>\n  }\n</ng-template>\n", dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselIndicatorsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel-indicators', exportAs: 'cCarouselIndicators', imports: [NgTemplateOutlet], host: { class: 'carousel-indicators' }, template: "@let tmpl = templates();\n<ng-container *ngTemplateOutlet=\"tmpl['carouselIndicatorsTemplate'] || defaultCarouselIndicatorsTemplate; context: {$implicit: items, active: active}\" />\n\n<ng-template #defaultCarouselIndicatorsTemplate>\n  @for (item of items; track item; let i = $index) {\n    <button\n      [attr.data-coreui-target]=\"i\"\n      type=\"button\"\n      (click)=\"onClick(i)\"\n      [class]=\"{ active: active === i }\"\n      [attr.aria-current]=\"active === i\">\n    </button>\n  }\n</ng-template>\n" }]
        }], propDecorators: { contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class CarouselItemComponent {
    #destroyRef = inject(DestroyRef);
    #carouselService = inject(CarouselService);
    index;
    /**
     * @ignore
     */
    activeInput = input(false, { ...(ngDevMode ? { debugName: "activeInput" } : {}), transform: booleanAttribute, alias: 'active' });
    active = linkedSignal({ ...(ngDevMode ? { debugName: "active" } : {}), source: this.activeInput,
        computation: (value) => {
            return value;
        } });
    /**
     * Time delay before cycling to next item. If -1, uses carousel interval value.
     * @return number
     * @default -1
     */
    interval = input(-1, ...(ngDevMode ? [{ debugName: "interval" }] : []));
    /**
     * Carousel item role.
     * @return string
     * @default 'group'
     */
    role = input('group', ...(ngDevMode ? [{ debugName: "role" }] : []));
    constructor() {
        this.#carouselService.carouselIndex$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((nextIndex) => {
            if ('active' in nextIndex) {
                this.active.set(nextIndex.active === this.index);
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: CarouselItemComponent, isStandalone: true, selector: "c-carousel-item", inputs: { activeInput: { classPropertyName: "activeInput", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, interval: { classPropertyName: "interval", publicName: "interval", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.active": "active()", "attr.role": "role()" }, classAttribute: "carousel-item" }, exportAs: ["cCarouselItem"], ngImport: i0, template: "@if (active()) {\n  <ng-content />\n}\n", styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel-item', exportAs: 'cCarouselItem', host: {
                        class: 'carousel-item',
                        '[class.active]': 'active()',
                        '[attr.role]': 'role()'
                    }, template: "@if (active()) {\n  <ng-content />\n}\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { activeInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], interval: [{ type: i0.Input, args: [{ isSignal: true, alias: "interval", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

function toSlideLeft(fromState, toState) {
    return toState.left === true && toState.type === 'slide';
}
function toSlideRight(fromState, toState) {
    return toState.left === false && toState.type === 'slide';
}
function toFadeLeft(fromState, toState) {
    return toState.left === true && toState.type !== 'slide';
}
function toFadeRight(fromState, toState) {
    return toState.left === false && toState.type !== 'slide';
}
const slideAnimationLeft = animation(group([
    query(':leave', [
        animate('0.6s ease-in-out', style({
            transform: 'translateX(-100%)'
        }))
    ], { optional: true }),
    query(':enter', [
        style({
            transform: 'translateX(100%)'
        }),
        animate('0.6s ease-in-out', style('*'))
    ], { optional: true })
]));
const slideAnimationRight = animation(group([
    query(':enter', [
        style({
            transform: 'translateX(-100%)'
        }),
        animate('0.6s ease-in-out', style('*'))
    ], { optional: true }),
    query(':leave', [
        animate('0.6s ease-in-out', style({
            transform: 'translateX(100%)'
        }))
    ], { optional: true })
]));
const fadeAnimationLeft = animation(group([
    query(':leave', [
        animate('0.9s ease-in-out', style({
            zIndex: 0,
            opacity: 0
        }))
    ], { optional: true }),
    query(':enter', [
        style({
            zIndex: 1,
            opacity: 1
        }),
        animate('0.6s ease-in-out', style('*'))
    ], { optional: true })
]));
const fadeAnimationRight = animation(group([
    query(':enter', [
        style({
            zIndex: 1,
            opacity: 1
        }),
        animate('0.6s ease-in-out', style('*'))
    ], { optional: true }),
    query(':leave', [
        animate('0.9s ease-in-out', style({
            zIndex: 0,
            opacity: 0
        }))
    ], { optional: true })
]));
const carouselPlay = trigger('carouselPlay', [
    state('*', style({ transform: 'translateX(0)', display: 'block', opacity: 1 })),
    transition(toFadeLeft, useAnimation(fadeAnimationLeft)),
    transition(toFadeRight, useAnimation(fadeAnimationRight)),
    transition(toSlideLeft, useAnimation(slideAnimationLeft)),
    transition(toSlideRight, useAnimation(slideAnimationRight))
]);

class CarouselInnerComponent {
    #carouselState = inject(CarouselState);
    activeIndex = signal(undefined, ...(ngDevMode ? [{ debugName: "activeIndex" }] : []));
    animate = signal(true, ...(ngDevMode ? [{ debugName: "animate" }] : []));
    interval = signal(0, ...(ngDevMode ? [{ debugName: "interval" }] : []));
    slide = signal({ left: true }, ...(ngDevMode ? [{ debugName: "slide" }] : []));
    transition = signal('crossfade', ...(ngDevMode ? [{ debugName: "transition" }] : []));
    slideType = computed(() => {
        return { left: this.slide().left, type: this.transition() };
    }, ...(ngDevMode ? [{ debugName: "slideType" }] : []));
    ariaLive = computed(() => {
        return this.interval() ? 'off' : 'polite';
    }, ...(ngDevMode ? [{ debugName: "ariaLive" }] : []));
    contentItems = contentChildren(CarouselItemComponent, ...(ngDevMode ? [{ debugName: "contentItems" }] : []));
    #prevContentItems = signal([], ...(ngDevMode ? [{ debugName: "#prevContentItems" }] : []));
    ngAfterContentInit() {
        this.setItems();
    }
    ngAfterContentChecked() {
        this.setItems();
        const state = this.#carouselState?.state;
        const nextIndex = state?.activeItemIndex;
        const nextDirection = state?.direction;
        if (this.activeIndex() !== nextIndex) {
            this.animate.set(state?.animate ?? false);
            this.activeIndex.set(state?.activeItemIndex);
            this.interval.set(state?.interval ?? 0);
            this.slide.set({ left: nextDirection === 'next' });
            this.transition.set(state?.transition ?? 'slide');
        }
    }
    setItems() {
        const contentItems = this.contentItems();
        if (this.#prevContentItems() !== contentItems) {
            this.#prevContentItems.set([...contentItems]);
            this.#carouselState.setItems(contentItems);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselInnerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: CarouselInnerComponent, isStandalone: true, selector: "c-carousel-inner", host: { properties: { "@carouselPlay": "slideType()", "@.disabled": "!animate()", "attr.aria-live": "ariaLive()" }, classAttribute: "carousel-inner" }, queries: [{ propertyName: "contentItems", predicate: CarouselItemComponent, isSignal: true }], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"], animations: [carouselPlay] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselInnerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-carousel-inner', animations: [carouselPlay], template: '<ng-content />', host: {
                        class: 'carousel-inner',
                        '[@carouselPlay]': 'slideType()',
                        '[@.disabled]': '!animate()',
                        '[attr.aria-live]': 'ariaLive()'
                    }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { contentItems: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => CarouselItemComponent), { isSignal: true }] }] } });

class CarouselModule {
    static forRoot() {
        return { ngModule: CarouselModule, providers: [] };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CarouselModule, imports: [CarouselComponent,
            CarouselCaptionComponent,
            CarouselControlComponent,
            CarouselIndicatorsComponent,
            CarouselInnerComponent,
            CarouselItemComponent], exports: [CarouselComponent,
            CarouselCaptionComponent,
            CarouselControlComponent,
            CarouselIndicatorsComponent,
            CarouselInnerComponent,
            CarouselItemComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselModule, providers: [CarouselService, CarouselState, CarouselConfig] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CarouselComponent,
                        CarouselCaptionComponent,
                        CarouselControlComponent,
                        CarouselIndicatorsComponent,
                        CarouselInnerComponent,
                        CarouselItemComponent
                    ],
                    providers: [CarouselService, CarouselState, CarouselConfig],
                    exports: [
                        CarouselComponent,
                        CarouselCaptionComponent,
                        CarouselControlComponent,
                        CarouselIndicatorsComponent,
                        CarouselInnerComponent,
                        CarouselItemComponent
                    ]
                }]
        }] });

class FormDirective {
    /**
     * Mark a form as validated. If you set it `true`, all validation styles will be applied to the form. [docs]
     * @return boolean
     * @default false
     */
    validated = input(false, { ...(ngDevMode ? { debugName: "validated" } : {}), transform: booleanAttribute });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormDirective, isStandalone: true, selector: "form[cForm]", inputs: { validated: { classPropertyName: "validated", publicName: "validated", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.was-validated": "validated()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'form[cForm]',
                    host: { '[class.was-validated]': 'validated()' }
                }]
        }], propDecorators: { validated: [{ type: i0.Input, args: [{ isSignal: true, alias: "validated", required: false }] }] } });

class FormCheckLabelDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: FormCheckLabelDirective, isStandalone: true, selector: "label[cFormCheckLabel]", host: { classAttribute: "form-check-label" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[cFormCheckLabel]',
                    host: { class: 'form-check-label' }
                }]
        }] });

class FormCheckComponent {
    static ngAcceptInputType_inline;
    static ngAcceptInputType_reverse;
    static ngAcceptInputType_switch;
    /**
     * Group checkboxes or radios on the same horizontal row.
     * @default false
     */
    inline = input(false, { ...(ngDevMode ? { debugName: "inline" } : {}), transform: booleanAttribute });
    /**
     * Put checkboxes or radios on the opposite side.
     * @default false
     * @since 4.4.7
     */
    reverse = input(false, { ...(ngDevMode ? { debugName: "reverse" } : {}), transform: booleanAttribute });
    /**
     * Size the component large or extra large. Works only with `[switch]="true"` [docs]
     * @default undefined
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Render a toggle switch on for checkbox.
     * @returns boolean
     * @default false
     */
    switch = input(false, { ...(ngDevMode ? { debugName: "switch" } : {}), transform: booleanAttribute });
    formCheckLabel = contentChild(FormCheckLabelDirective, ...(ngDevMode ? [{ debugName: "formCheckLabel" }] : []));
    hostClasses = computed(() => {
        const sizing = this.sizing();
        const isSwitch = this.switch();
        return {
            'form-check': !!this.formCheckLabel(),
            'form-switch': isSwitch,
            [`form-switch-${sizing}`]: isSwitch && !!sizing,
            'form-check-inline': this.inline(),
            'form-check-reverse': this.reverse()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: FormCheckComponent, isStandalone: true, selector: "c-form-check", inputs: { inline: { classPropertyName: "inline", publicName: "inline", isSignal: true, isRequired: false, transformFunction: null }, reverse: { classPropertyName: "reverse", publicName: "reverse", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, switch: { classPropertyName: "switch", publicName: "switch", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, queries: [{ propertyName: "formCheckLabel", first: true, predicate: FormCheckLabelDirective, descendants: true, isSignal: true }], exportAs: ["cFormCheck"], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-form-check',
                    template: '<ng-content />',
                    exportAs: 'cFormCheck',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { inline: [{ type: i0.Input, args: [{ isSignal: true, alias: "inline", required: false }] }], reverse: [{ type: i0.Input, args: [{ isSignal: true, alias: "reverse", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], switch: [{ type: i0.Input, args: [{ isSignal: true, alias: "switch", required: false }] }], formCheckLabel: [{ type: i0.ContentChild, args: [i0.forwardRef(() => FormCheckLabelDirective), { isSignal: true }] }] } });

class FormControlDirective {
    #hostElement = inject(ElementRef);
    /**
     * Size the component small or large.
     * @default undefined
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Set component validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Specifies the type of input element.
     */
    type = input('text', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Render the component styled as plain text. Removes the default form field styling and preserve the correct margin and padding. Recommend to use alongside `readonly`
     * @default false
     */
    plaintext = input(false, { ...(ngDevMode ? { debugName: "plaintext" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        const type = this.type();
        const isRange = type === 'range';
        const plaintext = this.plaintext();
        const sizing = this.sizing();
        const valid = this.valid();
        return {
            'form-control': !isRange && !plaintext,
            'form-control-plaintext': !isRange && plaintext,
            'form-control-color': type === 'color',
            'form-range': isRange,
            [`form-control-${sizing}`]: !!sizing && !isRange,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    get hostTag() {
        return this.#hostElement.nativeElement.tagName;
    }
    ngOnInit() {
        const hostTag = this.hostTag.toLowerCase();
        if (hostTag !== 'input' && hostTag !== 'textarea') {
            console.warn(`CoreUI [cFormControl] works with '<input>' and '<textarea>' - not with '<${hostTag}>'`);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormControlDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormControlDirective, isStandalone: true, selector: "input[cFormControl], textarea[cFormControl]", inputs: { sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, plaintext: { classPropertyName: "plaintext", publicName: "plaintext", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.type": "type()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormControlDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cFormControl], textarea[cFormControl]',
                    host: {
                        '[class]': 'hostClasses()',
                        '[attr.type]': 'type()'
                    }
                }]
        }], propDecorators: { sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], plaintext: [{ type: i0.Input, args: [{ isSignal: true, alias: "plaintext", required: false }] }] } });

class FormCheckInputDirective {
    static ngAcceptInputType_indeterminate;
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    /**
     * Specifies the type of component.
     * @default 'checkbox'
     */
    type = input('checkbox', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Set component indeterminate state.
     * @default false
     */
    indeterminateInput = input(false, { ...(ngDevMode ? { debugName: "indeterminateInput" } : {}), transform: booleanAttribute, alias: 'indeterminate' });
    #indeterminate = linkedSignal(this.indeterminateInput, ...(ngDevMode ? [{ debugName: "#indeterminate" }] : []));
    #indeterminateEffect = effect(() => {
        if (this.type() === 'checkbox') {
            const indeterminate = this.#indeterminate();
            const htmlInputElement = this.#hostElement.nativeElement;
            if (indeterminate) {
                this.#renderer.setProperty(htmlInputElement, 'checked', false);
            }
            this.#renderer.setProperty(htmlInputElement, 'indeterminate', indeterminate);
        }
    }, ...(ngDevMode ? [{ debugName: "#indeterminateEffect" }] : []));
    get indeterminate() {
        return this.#indeterminate();
    }
    /**
     * Set component validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    hostClasses = computed(() => {
        const valid = this.valid();
        return {
            'form-check-input': true,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    get checked() {
        return this.#hostElement?.nativeElement?.checked;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckInputDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormCheckInputDirective, isStandalone: true, selector: "input[cFormCheckInput]", inputs: { type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, indeterminateInput: { classPropertyName: "indeterminateInput", publicName: "indeterminate", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.type": "type()" }, classAttribute: "form-check-input" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormCheckInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cFormCheckInput]',
                    host: {
                        class: 'form-check-input',
                        '[class]': 'hostClasses()',
                        '[attr.type]': 'type()'
                    }
                }]
        }], propDecorators: { type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], indeterminateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "indeterminate", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }] } });

class FormFeedbackComponent {
    /**
     * If your form layout allows it, you can display validation feedback in a styled tooltip.
     * @default false
     */
    tooltip = input(false, { ...(ngDevMode ? { debugName: "tooltip" } : {}), transform: booleanAttribute });
    /**
     * Set component validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    hostClasses = computed(() => {
        const status = this.valid() === true ? 'valid' : 'invalid';
        const type = this.tooltip() ? 'tooltip' : 'feedback';
        return {
            [`${status}-${type}`]: true
            // 'valid-feedback': valid === true && !tooltip,
            // 'valid-tooltip': valid === true && tooltip,
            // 'invalid-feedback': valid !== true && !tooltip,
            // 'invalid-tooltip': valid !== true && tooltip
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormFeedbackComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: FormFeedbackComponent, isStandalone: true, selector: "c-form-feedback", inputs: { tooltip: { classPropertyName: "tooltip", publicName: "tooltip", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormFeedbackComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-form-feedback',
                    template: '<ng-content />',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { tooltip: [{ type: i0.Input, args: [{ isSignal: true, alias: "tooltip", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }] } });

class FormFloatingDirective {
    /**
     * Enable floating labels
     * @dafault boolean
     */
    floating = input(true, { ...(ngDevMode ? { debugName: "floating" } : {}), transform: booleanAttribute, alias: 'cFormFloating' });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormFloatingDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormFloatingDirective, isStandalone: true, selector: "[cFormFloating]", inputs: { floating: { classPropertyName: "floating", publicName: "cFormFloating", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.form-floating": "floating()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormFloatingDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cFormFloating]',
                    host: { '[class.form-floating]': 'floating()' }
                }]
        }], propDecorators: { floating: [{ type: i0.Input, args: [{ isSignal: true, alias: "cFormFloating", required: false }] }] } });

class FormLabelDirective {
    /**
     * For horizontal forms set labels to 'col' and make them vertically centered with their associated form controls.
     * @default ''
     */
    col = input('', { ...(ngDevMode ? { debugName: "col" } : {}), alias: 'cLabel' });
    /**
     * Size the label small or large.
     * @default ''
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    hostClasses = computed(() => {
        const col = this.col();
        const sizing = this.sizing();
        return {
            'form-label': true,
            'col-form-label': col === 'col',
            [`col-form-label-${sizing}`]: !!sizing && col === 'col'
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormLabelDirective, isStandalone: true, selector: "[cLabel]", inputs: { col: { classPropertyName: "col", publicName: "cLabel", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "form-label" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cLabel]',
                    host: { class: 'form-label', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { col: [{ type: i0.Input, args: [{ isSignal: true, alias: "cLabel", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }] } });

class FormPasswordActionComponent {
    /**
     * Sets the accessible label (`aria-label`) for the toggle password visibility button. This improves accessibility for screen readers and should describe the action, e.g. `"Show password"` or `"Hide password"`.
     * @default 'Toggle password visibility'
     */
    ariaLabelToggler = input('Toggle password visibility', ...(ngDevMode ? [{ debugName: "ariaLabelToggler" }] : []));
    /**
     * Sets disabled state of the toggle password visibility button. When `true`, the user cannot interact with the button.
     * @default false
     */
    disabled = input(false, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    handleClick($event) {
        // fix for Safari
        $event.currentTarget?.focus();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormPasswordActionComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: FormPasswordActionComponent, isStandalone: true, selector: "c-form-password-action", inputs: { ariaLabelToggler: { classPropertyName: "ariaLabelToggler", publicName: "ariaLabelToggler", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<button\n  [attr.aria-label]=\"ariaLabelToggler()\"\n  class=\"form-password-action\"\n  data-coreui-toggle=\"password\"\n  type=\"button\"\n  [disabled]=\"disabled()\"\n  (click)=\"handleClick($event)\"\n>\n  <span class=\"form-password-action-icon\"></span>\n</button>\n", styles: [":host{display:none}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormPasswordActionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-form-password-action', template: "<button\n  [attr.aria-label]=\"ariaLabelToggler()\"\n  class=\"form-password-action\"\n  data-coreui-toggle=\"password\"\n  type=\"button\"\n  [disabled]=\"disabled()\"\n  (click)=\"handleClick($event)\"\n>\n  <span class=\"form-password-action-icon\"></span>\n</button>\n", styles: [":host{display:none}\n"] }]
        }], propDecorators: { ariaLabelToggler: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabelToggler", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }] } });

class FormPasswordDirective {
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_readOnly;
    static ngAcceptInputType_showPassword;
    #hostElement = inject(ElementRef);
    #destroyRef = inject(DestroyRef);
    #renderer = inject(Renderer2);
    viewContainerRef = inject(ViewContainerRef);
    #focusMonitor = inject(FocusMonitor);
    #wrapper = this.#renderer.createElement('div');
    #actionButtonRef = this.viewContainerRef.createComponent(FormPasswordActionComponent);
    #unListen;
    #afterNextRender = afterNextRender({
        write: () => {
            // this fixes RuntimeError: NG0500: During hydration Angular expected <input> but found <div>.
            // Find more at https://angular.dev/errors/NG0500
            const nativeElement = this.#hostElement.nativeElement;
            const parentNode = this.#renderer.parentNode(nativeElement);
            const wrapper = this.#wrapper;
            const actionButton = this.#actionButtonRef.location.nativeElement.querySelector('button');
            this.#renderer.addClass(wrapper, 'form-password');
            this.#renderer.appendChild(parentNode, wrapper);
            this.#renderer.insertBefore(parentNode, wrapper, nativeElement);
            this.#renderer.appendChild(wrapper, nativeElement);
            this.#renderer.appendChild(wrapper, actionButton);
            this.#renderer.removeChild(wrapper, this.#actionButtonRef.location.nativeElement);
            this.#addFloatingLabel();
        }
    });
    ngOnInit() {
        const actionButton = this.#actionButtonRef.location.nativeElement.querySelector('button');
        this.#unListen = this.#renderer.listen(actionButton, 'click', () => {
            this.#passwordVisible.update((value) => !value);
        });
        this.#focusMonitor
            .monitor(this.#wrapper, true)
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef))
            .subscribe((origin) => {
            this.#passwordVisible.update((value) => (origin ? value : false));
            this.#renderer.setAttribute(actionButton, 'tabindex', origin ? '0' : '-1');
        });
    }
    /**
     * Sets the accessible label (`aria-label`) for the toggle password visibility button. This improves accessibility for screen readers and should describe the action, e.g. `"Show password"` or `"Hide password"`.
     * @default 'Toggle password visibility'
     */
    ariaLabelToggler = input('Toggle password visibility', ...(ngDevMode ? [{ debugName: "ariaLabelToggler" }] : []));
    #ariaLabelTogglerEffect = effect(() => {
        this.#actionButtonRef?.setInput('ariaLabelToggler', this.ariaLabelToggler() ?? this.#actionButtonRef.instance.ariaLabelToggler());
    }, ...(ngDevMode ? [{ debugName: "#ariaLabelTogglerEffect" }] : []));
    /**
     * Sets disabled state of the password input field. When `true`, the user cannot interact with the field.
     * @dafault false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    #disabledEffect = effect(() => {
        const disabled = this.disabled();
        this.#actionButtonRef.setInput('disabled', disabled);
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    /**
     * set form labels that float over the password input fields
     * @reurnd string | undefined
     */
    floatingLabel = input(...(ngDevMode ? [undefined, { debugName: "floatingLabel" }] : []));
    /**
     * Disables the password input field. When `true`, the user cannot interact with the field.
     * @default false
     */
    readOnly = input(false, { ...(ngDevMode ? { debugName: "readOnly" } : {}), transform: booleanAttribute });
    /**
     * Size the component small or large.
     * @default undefined
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Set component validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Controls the initial visibility of the password. When `true`, the input type is set to `"text"` instead of `"password"`. This allows **toggling password visibility** in Angular forms.
     * @default false
     */
    showPassword = input(false, { ...(ngDevMode ? { debugName: "showPassword" } : {}), transform: booleanAttribute });
    #passwordVisible = linkedSignal(this.showPassword, ...(ngDevMode ? [{ debugName: "#passwordVisible" }] : []));
    /**
     * Specifies the type of input element.
     */
    type = computed(() => {
        return this.#passwordVisible() ? 'text' : 'password';
    }, ...(ngDevMode ? [{ debugName: "type" }] : []));
    hostClasses = computed(() => {
        const sizing = this.sizing();
        const valid = this.valid();
        return {
            'form-control': true,
            [`form-control-${sizing}`]: !!sizing,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    #validEffect = effect(() => {
        const valid = this.valid();
        this.#renderer.removeClass(this.#wrapper, 'is-valid');
        this.#renderer.removeClass(this.#wrapper, 'is-invalid');
        if (valid === true) {
            this.#renderer.addClass(this.#wrapper, 'is-valid');
        }
        else if (valid === false) {
            this.#renderer.addClass(this.#wrapper, 'is-invalid');
        }
    }, ...(ngDevMode ? [{ debugName: "#validEffect" }] : []));
    ngOnDestroy() {
        this.#unListen?.();
        this.#focusMonitor.stopMonitoring(this.#wrapper);
    }
    #addFloatingLabel() {
        const floatingLabel = this.floatingLabel();
        const wrapper = this.#wrapper;
        if (floatingLabel && !wrapper.classList.contains('form-floating')) {
            const nativeElement = this.#hostElement.nativeElement;
            const placeholder = nativeElement.getAttribute('placeholder');
            if (!placeholder) {
                nativeElement.setAttribute('placeholder', floatingLabel);
            }
            const label = this.#renderer.createElement('label');
            const text = this.#renderer.createText(floatingLabel);
            this.#renderer.appendChild(label, text);
            this.#renderer.addClass(label, 'form-label');
            this.#renderer.appendChild(wrapper, label);
            this.#renderer.addClass(wrapper, 'form-floating');
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormPasswordDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormPasswordDirective, isStandalone: true, selector: "input[cFormPassword]", inputs: { ariaLabelToggler: { classPropertyName: "ariaLabelToggler", publicName: "ariaLabelToggler", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, floatingLabel: { classPropertyName: "floatingLabel", publicName: "floatingLabel", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, showPassword: { classPropertyName: "showPassword", publicName: "showPassword", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "type": "type()", "disabled": "disabled()", "readonly": "readOnly()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormPasswordDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cFormPassword]',
                    host: {
                        '[class]': 'hostClasses()',
                        '[type]': 'type()',
                        '[disabled]': 'disabled()',
                        '[readonly]': 'readOnly()'
                    }
                }]
        }], propDecorators: { ariaLabelToggler: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabelToggler", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], floatingLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "floatingLabel", required: false }] }], readOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "readOnly", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], showPassword: [{ type: i0.Input, args: [{ isSignal: true, alias: "showPassword", required: false }] }] } });

class FormSelectDirective {
    /**
     * Size the component small or large.
     * @default undefined
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Set component validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    hostClasses = computed(() => {
        const sizing = this.sizing();
        const valid = this.valid();
        return {
            'form-select': true,
            [`form-select-${sizing}`]: !!sizing,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormSelectDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FormSelectDirective, isStandalone: true, selector: "select[cSelect]", inputs: { sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "form-select" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormSelectDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'select[cSelect]',
                    host: { class: 'form-select', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }] } });

class FormTextDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormTextDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: FormTextDirective, isStandalone: true, selector: "[cFormText]", host: { classAttribute: "form-text" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormTextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cFormText]',
                    host: { class: 'form-text' }
                }]
        }] });

class InputGroupComponent {
    /**
     * Size the component small or large.
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    hostClasses = computed(() => {
        const sizing = this.sizing();
        return {
            'input-group': true,
            [`input-group-${sizing}`]: !!sizing
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InputGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: InputGroupComponent, isStandalone: true, selector: "c-input-group", inputs: { sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "input-group" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InputGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-input-group',
                    template: '<ng-content />',
                    host: { class: 'input-group', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }] } });

class InputGroupTextDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InputGroupTextDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: InputGroupTextDirective, isStandalone: true, selector: "[cInputGroupText]", host: { classAttribute: "input-group-text" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: InputGroupTextDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cInputGroupText]',
                    host: { class: 'input-group-text' }
                }]
        }] });

class FormModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: FormModule, imports: [FormDirective,
            FormCheckComponent,
            FormCheckLabelDirective,
            FormCheckInputDirective,
            FormControlDirective,
            FormFeedbackComponent,
            FormFloatingDirective,
            FormLabelDirective,
            FormPasswordDirective,
            FormSelectDirective,
            FormTextDirective,
            InputGroupComponent,
            InputGroupTextDirective], exports: [FormDirective,
            FormCheckComponent,
            FormCheckLabelDirective,
            FormCheckInputDirective,
            FormControlDirective,
            FormFeedbackComponent,
            FormFloatingDirective,
            FormLabelDirective,
            FormPasswordDirective,
            FormSelectDirective,
            FormTextDirective,
            InputGroupComponent,
            InputGroupTextDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FormModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        FormDirective,
                        FormCheckComponent,
                        FormCheckLabelDirective,
                        FormCheckInputDirective,
                        FormControlDirective,
                        FormFeedbackComponent,
                        FormFloatingDirective,
                        FormLabelDirective,
                        FormPasswordDirective,
                        FormSelectDirective,
                        FormTextDirective,
                        InputGroupComponent,
                        InputGroupTextDirective
                    ],
                    exports: [
                        FormDirective,
                        FormCheckComponent,
                        FormCheckLabelDirective,
                        FormCheckInputDirective,
                        FormControlDirective,
                        FormFeedbackComponent,
                        FormFloatingDirective,
                        FormLabelDirective,
                        FormPasswordDirective,
                        FormSelectDirective,
                        FormTextDirective,
                        InputGroupComponent,
                        InputGroupTextDirective
                    ]
                }]
        }] });

const getSupportedLocale = (locale = 'default') => {
    let supportedLocale;
    try {
        supportedLocale = Intl.DateTimeFormat.supportedLocalesOf(locale).length ? locale : 'default';
    }
    catch (e) {
        supportedLocale = 'default';
    }
    return supportedLocale;
};
const isHour12 = (locale = 'default') => {
    const supportedLocale = getSupportedLocale(locale);
    const hour12 = Intl.DateTimeFormat(supportedLocale, { timeStyle: 'short' }).resolvedOptions().hour12;
    return hour12 ?? false;
};
const convert12hTo24h = (abbr, hour) => {
    if (abbr === 'am' && hour === 12) {
        return 0;
    }
    if (abbr === 'am') {
        return hour;
    }
    if (abbr === 'pm' && hour === 12) {
        return 12;
    }
    return hour + 12;
};
const convert24hTo12h = (hour) => hour % 12 || 12;
const convertTimeToDate = (time) => time ? (time instanceof Date ? new Date(time) : new Date(`1970-01-01 ${time}`)) : null;
// export const getAmPm = (date: Date, locale: string) => {
//   if (date.toLocaleTimeString(locale).includes('AM')) {
//     return 'am'
//   }
//   if (date.toLocaleTimeString(locale).includes('PM')) {
//     return 'pm'
//   }
//   return date.getHours() >= 12 ? 'pm' : 'am'
// }
const getAmPm = (date, locale, options = {}) => {
    const dayPeriods = getDayPeriods('en-US', options);
    const time = new Intl.DateTimeFormat('en-US', { timeStyle: 'short', hour12: true }).format(date);
    const dayPeriod = dayPeriods.find(element => time.includes(element.label));
    return (dayPeriod?.value ?? (date.getHours() >= 12 ? 'pm' : 'am'));
};
const isAm = (date) => {
    return date.getHours() < 12;
};
const isPm = (date) => {
    return date.getHours() > 11;
};
const isValidTimeZone = (timeZone) => {
    try {
        if (timeZone === undefined) {
            return false;
        }
        if (!Intl || !Intl.DateTimeFormat().resolvedOptions().timeZone) {
            throw new Error('Time zones not supported.');
        }
        if (typeof timeZone !== 'string') {
            throw new Error(`Invalid timeZone type specified: ${timeZone}`);
        }
        try {
            // throws an error if timezone is not valid
            Intl.DateTimeFormat(undefined, { timeZone: timeZone });
        }
        catch (error) {
            throw new Error(`Invalid timeZone string specified: ${timeZone}`);
        }
        return true;
    }
    catch (error) {
        console.warn(error);
        return false;
    }
};
const getListOfHours = (locale, options = {}) => {
    const hour12 = isHour12(locale);
    const time = new Date(0);
    return Array.from({ length: 24 }, (_, i) => {
        time.setHours(i);
        const dayPeriod = getAmPm(time, locale);
        return {
            value: i,
            dayPeriod: dayPeriod,
            label: time.toLocaleTimeString(locale, {
                hour: '2-digit',
                hour12: hour12,
                minute: '2-digit',
                second: '2-digit'
            }).split(':')[0]
        };
    });
};
const getListOfMinutes = (locale, options = {}, valueAsString = false) => {
    const d = new Date(0);
    return Array.from({ length: 60 }, (_, i) => {
        d.setMinutes(i);
        return {
            value: i,
            // value: valueAsString ? i.toString() : i,
            label: d.toLocaleTimeString(locale, {
                // ...options,
                hour: '2-digit',
                hour12: false,
                minute: '2-digit',
                second: '2-digit'
            }).split(':')[1]
        };
    });
};
const getListOfSeconds = (locale, options = {}, valueAsString = false) => Array.from({ length: 60 }, (_, i) => {
    const d = new Date(0);
    d.setSeconds(i);
    return {
        value: i,
        // value: valueAsString ? i.toString() : i,
        label: d.toLocaleTimeString(locale, {
            // ...options,
            hour: '2-digit',
            hour12: false,
            minute: '2-digit',
            second: '2-digit'
        }).split(':')[2]
    };
});
const getSelectedHour = (date, locale) => date instanceof Date ? (isHour12(locale) ? convert24hTo12h(date.getHours()) : date.getHours()) : '';
const getSelectedMinutes = (date) => (date ? date.getMinutes() : '');
const getSelectedSeconds = (date) => (date ? date.getSeconds() : '');
// export const isAmPm = (locale: string) =>
//   ['am', 'AM', 'pm', 'PM'].some((el) => new Date().toLocaleString(locale).includes(el))
const isAmPm = (locale = 'default') => {
    const supportedLocale = getSupportedLocale(locale);
    const date = new Date();
    const time = new Intl.DateTimeFormat(supportedLocale, { timeStyle: 'short' }).format(date);
    const isAmPm = time.match(/(?:\p{L}\p{M}\p{P}*)+|(?:\p{L}\p{P}*)+/gu) ?? [];
    return isAmPm.length > 0;
};
const isValidTime = (time) => {
    if (typeof time === 'string') {
        return isValidDate(time) || isValidDate(new Date(`1970-01-01 ${time}`));
    }
    return isValidDate(time);
    // const d = new Date(`1970-01-01 ${time}`)
    // return d instanceof Date && d.getTime()
};
// export const _getDayPeriods = (locale: string = 'default', options: DateTimeFormatOptions = {}) => {
//   const supportedLocale = getSupportedLocale(locale);
//
//   const dayPeriods = { am: 'am', pm: 'pm' };
//
//   const hour12 = isHour12(supportedLocale);
//   if (!hour12) {
//     return dayPeriods;
//   }
//
//   const am = new Date(Date.UTC(1970, 0, 1, 0, 0, 0));
//   const pm = new Date(Date.UTC(1970, 0, 1, 13, 0, 0));
//
//   const formatter = new Intl.DateTimeFormat(supportedLocale, { hour12: hour12, hour: 'numeric', timeZone: 'UTC' });
//   dayPeriods.am = formatter.formatToParts(am).find(part => part.type === 'dayPeriod')?.value ?? 'am';
//   dayPeriods.pm = formatter.formatToParts(pm).find(part => part.type === 'dayPeriod')?.value ?? 'pm'
//   return dayPeriods;
// };
const getDayPeriods = (locale = 'default', options = {}) => {
    const supportedLocale = getSupportedLocale(locale);
    const dayPeriods = [
        { value: 'am', label: 'am' },
        { value: 'pm', label: 'pm' }
    ];
    const hour12 = isHour12(supportedLocale);
    if (!hour12) {
        return dayPeriods;
    }
    const am = new Date(Date.UTC(1970, 0, 1, 0, 0, 0));
    const pm = new Date(Date.UTC(1970, 0, 1, 13, 0, 0));
    const formatter = new Intl.DateTimeFormat(supportedLocale, { hour12: hour12, hour: 'numeric', timeZone: 'UTC' });
    const formattedAm = formatter.formatToParts(am).find(part => part.type === 'dayPeriod')?.value ?? 'am';
    const formattedPm = formatter.formatToParts(pm).find(part => part.type === 'dayPeriod')?.value ?? 'pm';
    dayPeriods.forEach((element, index, dayPeriods) => {
        dayPeriods[index].label = element.value === 'am' ? (formattedAm ?? 'am') : (formattedPm ?? 'pm');
    });
    return dayPeriods;
};
// testing
// const langs = [
//   ['Abkhazian', 'ab'],
//   ['Afar', 'aa'],
//   ['Afrikaans', 'af'],
//   ['Akan', 'ak'],
//   ['Albanian', 'sq'],
//   ['Amharic', 'am'],
//   ['Arabic', 'ar'],
//   ['Aragonese', 'an'],
//   ['Argentina', 'es-ar'],
//   ['Armenian', 'hy'],
//   ['Assamese', 'as'],
//   ['Avaric', 'av'],
//   ['Avestan', 'ae'],
//   ['Aymara', 'ay'],
//   ['Azerbaijani', 'az'],
//   ['Bambara', 'bm'],
//   ['Bashkir', 'ba'],
//   ['Basque', 'eu'],
//   ['Belarusian', 'be'],
//   ['Bengali (Bangla)', 'bn'],
//   ['Bihari', 'bh'],
//   ['Bislama', 'bi'],
//   ['Bosnian', 'bs'],
//   ['Breton', 'br'],
//   ['Bulgarian', 'bg'],
//   ['Burmese', 'my'],
//   ['Catalan', 'ca'],
//   ['Chamorro', 'ch'],
//   ['Chechen', 'ce'],
//   ['Chichewa, Chewa, Nyanja', 'ny'],
//   ['Chinese', 'zh'],
//   ['Chinese (Simplified)', 'zh-Hans'],
//   ['Chinese (Traditional)', 'zh-Hant'],
//   ['Chuvash', 'cv'],
//   ['Cornish', 'kw'],
//   ['Corsican', 'co'],
//   ['Cree', 'cr'],
//   ['Croatian', 'hr'],
//   ['Czech', 'cs'],
//   ['Danish', 'da'],
//   ['Divehi, Dhivehi, Maldivian', 'dv'],
//   ['Dutch', 'nl'],
//   ['Dzongkha', 'dz'],
//   ['English', 'en'],
//   ['Esperanto', 'eo'],
//   ['Estonian', 'et'],
//   ['Ewe', 'ee'],
//   ['Faroese', 'fo'],
//   ['Fijian', 'fj'],
//   ['Finnish', 'fi'],
//   ['French', 'fr'],
//   ['Fula, Fulah, Pulaar, Pular', 'ff'],
//   ['Galician', 'gl'],
//   ['Gaelic (Scottish)', 'gd'],
//   ['Gaelic (Manx)', 'gv'],
//   ['Georgian', 'ka'],
//   ['German', 'de'],
//   ['Greek', 'el'],
//   ['Greenlandic', 'kl'],
//   ['Guarani', 'gn'],
//   ['Gujarati', 'gu'],
//   ['Haitian Creole', 'ht'],
//   ['Hausa', 'ha'],
//   ['Hebrew', 'he'],
//   ['Herero', 'hz'],
//   ['Hindi', 'hi'],
//   ['Hiri Motu', 'ho'],
//   ['Hungarian', 'hu'],
//   ['Icelandic', 'is'],
//   ['Ido', 'io'],
//   ['Igbo', 'ig'],
//   ['Indonesian', 'id', 'in'],
//   ['Interlingua', 'ia'],
//   ['Interlingue', 'ie'],
//   ['Inuktitut', 'iu'],
//   ['Inupiak', 'ik'],
//   ['Irish', 'ga'],
//   ['Italian', 'it'],
//   ['Japanese', 'ja'],
//   ['Javanese', 'jv'],
//   ['Kalaallisut, Greenlandic', 'kl'],
//   ['Kannada', 'kn'],
//   ['Kanuri', 'kr'],
//   ['Kashmiri', 'ks'],
//   ['Kazakh', 'kk'],
//   ['Khmer', 'km'],
//   ['Kikuyu', 'ki'],
//   ['Kinyarwanda (Rwanda)', 'rw'],
//   ['Kirundi', 'rn'],
//   ['Kyrgyz', 'ky'],
//   ['Komi', 'kv'],
//   ['Kongo', 'kg'],
//   ['Korean', 'ko'],
//   ['Kurdish', 'ku'],
//   ['Kwanyama', 'kj'],
//   ['Lao', 'lo'],
//   ['Latin', 'la'],
//   ['Latvian (Lettish)', 'lv'],
//   ['Limburgish (Limburger)', 'li'],
//   ['Lingala', 'ln'],
//   ['Lithuanian', 'lt'],
//   ['Luga-Katanga', 'lu'],
//   ['Luganda, Ganda', 'lg'],
//   ['Luxembourgish', 'lb'],
//   ['Manx', 'gv'],
//   ['Macedonian', 'mk'],
//   ['Malagasy', 'mg'],
//   ['Malay', 'ms'],
//   ['Malayalam', 'ml'],
//   ['Maltese', 'mt'],
//   ['Maori', 'mi'],
//   ['Marathi', 'mr'],
//   ['Marshallese', 'mh'],
//   ['Moldavian', 'mo'],
//   ['Mongolian', 'mn'],
//   ['Nauru', 'na'],
//   ['Navajo', 'nv'],
//   ['Ndonga', 'ng'],
//   ['Northern Ndebele', 'nd'],
//   ['Nepali', 'ne'],
//   ['Norwegian', 'no'],
//   ['Norwegian bokmål', 'nb'],
//   ['Norwegian nynorsk', 'nn'],
//   ['Nuosu', 'ii'],
//   ['Occitan', 'oc'],
//   ['Ojibwe', 'oj'],
//   ['Old Church Slavonic, Old Bulgarian', 'cu'],
//   ['Oriya', 'or'],
//   ['Oromo (Afaan Oromo)', 'om'],
//   ['Ossetian', 'os'],
//   ['Pāli', 'pi'],
//   ['Pashto, Pushto', 'ps'],
//   ['Persian (Farsi)', 'fa'],
//   ['Polish', 'pl'],
//   ['Portuguese', 'pt'],
//   ['Punjabi (Eastern)', 'pa'],
//   ['Quechua', 'qu'],
//   ['Romansh', 'rm'],
//   ['Romanian', 'ro'],
//   ['Russian', 'ru'],
//   ['Sami', 'se'],
//   ['Samoan', 'sm'],
//   ['Sango', 'sg'],
//   ['Sanskrit', 'sa'],
//   ['Serbian', 'sr'],
//   ['Serbo-Croatian', 'sh'],
//   ['Sesotho', 'st'],
//   ['Setswana', 'tn'],
//   ['Shona', 'sn'],
//   ['Sichuan Yi', 'ii'],
//   ['Sindhi', 'sd'],
//   ['Sinhalese', 'si'],
//   ['Siswati', 'ss'],
//   ['Slovak', 'sk'],
//   ['Slovenian', 'sl'],
//   ['Somali', 'so'],
//   ['Southern Ndebele', 'nr'],
//   ['Spanish', 'es'],
//   ['Sundanese', 'su'],
//   ['Swahili (Kiswahili)', 'sw'],
//   ['Swati', 'ss'],
//   ['Swedish', 'sv'],
//   ['Tagalog', 'tl'],
//   ['Tahitian', 'ty'],
//   ['Tajik', 'tg'],
//   ['Tamil', 'ta'],
//   ['Tatar', 'tt'],
//   ['Telugu', 'te'],
//   ['Thai', 'th'],
//   ['Tibetan', 'bo'],
//   ['Tigrinya', 'ti'],
//   ['Tonga', 'to'],
//   ['Tsonga', 'ts'],
//   ['Turkish', 'tr'],
//   ['Turkmen', 'tk'],
//   ['Twi', 'tw'],
//   ['Uyghur', 'ug'],
//   ['Ukrainian', 'uk'],
//   ['Urdu', 'ur'],
//   ['Uzbek', 'uz'],
//   ['Venda', 've'],
//   ['Vietnamese', 'vi'],
//   ['Volapük', 'vo'],
//   ['Wallon', 'wa'],
//   ['Welsh', 'cy'],
//   ['Wolof', 'wo'],
//   ['Western Frisian', 'fy'],
//   ['Xhosa', 'xh'],
//   ['Yiddish', 'yi', 'ji'],
//   ['Yoruba', 'yo'],
//   ['Zhuang, Chuang', 'za'],
//   ['Zulu', 'zu'],
// ]
//
// langs.forEach(lang => {const period = getDayPeriods(lang[1]); if (period.am === 'AM') return; console.log(lang[1], period )})
// Chrome / Opera
// Amharic am {am: 'ጥዋት', pm: 'ከሰዓት'}
// Arabic ar {am: 'ص', pm: 'م'}
// Bulgarian bg {am: 'пр.об.', pm: 'сл.об.'}
// Chinese (Traditional) zh-Hant {am: '上午', pm: '下午'}
// Greek el {am: 'π.μ.', pm: 'μ.μ.'}
// Hindi hi {am: 'am', pm: 'pm'}
// Kannada kn {am: 'ಪೂರ್ವಾಹ್ನ', pm: 'ಅಪರಾಹ್ನ'}
// Korean ko {am: '오전', pm: '오후'}
// Malay ms {am: 'PG', pm: 'PTG'}
// Tamil ta {am: 'முற்பகல்', pm: 'பிற்பகல்'}
// Edge Chromium
// Albanian sq {am: 'e paradites', pm: 'e pasdites'}
// Amharic am {am: 'ጥዋት', pm: 'ከሰዓት'}
// Arabic ar {am: 'ص', pm: 'م'}
// Bulgarian bg {am: 'пр.об.', pm: 'сл.об.'}
// Chinese (Traditional) zh-Hant {am: '上午', pm: '下午'}
// Greek el {am: 'π.μ.', pm: 'μ.μ.'}
// Hindi hi {am: 'am', pm: 'pm'}
// Kannada kn {am: 'ಪೂರ್ವಾಹ್ನ', pm: 'ಅಪರಾಹ್ನ'}
// Korean ko {am: '오전', pm: '오후'}
// Malay ms {am: 'PG', pm: 'PTG'}
// Punjabi (Eastern) pa {am: 'ਪੂ.ਦੁ.', pm: 'ਬਾ.ਦੁ.'}
// Sindhi sd {am: 'صبح، منجهند', pm: 'منجهند، شام'}
// Tamil ta {am: 'முற்பகல்', pm: 'பிற்பகல்'}
// Firefox
// Akan ak { am: "AN", pm: "EW" }
// Albanian sq { am: "e paradites", pm: "e pasdites" }
// Amharic am { am: "ጥዋት", pm: "ከሰዓት" }
// Arabic ar { am: "ص", pm: "م" }
// Assamese as { am: "পূৰ্বাহ্ন", pm: "অপৰাহ্ন" }
// Bulgarian bg { am: "пр.об.", pm: "сл.об." }
// Chinese (Traditional) zh-Hant { am: "上午", pm: "下午" }
// Dzongkha dz { am: "སྔ་ཆ་", pm: "ཕྱི་ཆ་" }
// Ewe ee { am: "ŋdi", pm: "ɣetrɔ" }
// Greek el { am: "π.μ.", pm: "μ.μ." }
// Hindi hi { am: "am", pm: "pm" }
// Kannada kn { am: "ಪೂರ್ವಾಹ್ನ", pm: "ಅಪರಾಹ್ನ" }
// Korean ko { am: "오전", pm: "오후" }
// Malay ms { am: "PG", pm: "PTG" }
// Oromo (Afaan Oromo) om { am: "WD", pm: "WB" }
// Punjabi (Eastern) pa { am: "ਪੂ.ਦੁ.", pm: "ਬਾ.ਦੁ." }
// Sanskrit sa { am: "पूर्वाह्न", pm: "अपराह्न" }
// Sindhi sd { am: "صبح، منجهند", pm: "منجهند، شام" }
// Somali so { am: "GH", pm: "GD" }
// Tamil ta { am: "முற்பகல்", pm: "பிற்பகல்" }
// Tigrinya ti { am: "ቅ.ቀ.", pm: "ድ.ቀ." }
// Tonga to { am: "hengihengi", pm: "efiafi" }
// Twi tw { am: "AN", pm: "EW" }
// const formats = {
//   "ar-SA" : "dd/MM/yy",
//   "bg-BG" : "dd.M.yyyy",
//   "ca-ES" : "dd/MM/yyyy",
//   "zh-TW" : "yyyy/M/d",
//   "cs-CZ" : "d.M.yyyy",
//   "da-DK" : "dd-MM-yyyy",
//   "de-DE" : "dd.MM.yyyy",
//   "el-GR" : "d/M/yyyy",
//   "en-US" : "M/d/yyyy",
//   "fi-FI" : "d.M.yyyy",
//   "fr-FR" : "dd/MM/yyyy",
//   "he-IL" : "dd/MM/yyyy",
//   "hu-HU" : "yyyy. MM. dd.",
//   "is-IS" : "d.M.yyyy",
//   "it-IT" : "dd/MM/yyyy",
//   "ja-JP" : "yyyy/MM/dd",
//   "ko-KR" : "yyyy-MM-dd",
//   "nl-NL" : "d-M-yyyy",
//   "nb-NO" : "dd.MM.yyyy",
//   "pl-PL" : "yyyy-MM-dd",
//   "pt-BR" : "d/M/yyyy",
//   "ro-RO" : "dd.MM.yyyy",
//   "ru-RU" : "dd.MM.yyyy",
//   "hr-HR" : "d.M.yyyy",
//   "sk-SK" : "d. M. yyyy",
//   "sq-AL" : "yyyy-MM-dd",
//   "sv-SE" : "yyyy-MM-dd",
//   "th-TH" : "d/M/yyyy",
//   "tr-TR" : "dd.MM.yyyy",
//   "ur-PK" : "dd/MM/yyyy",
//   "id-ID" : "dd/MM/yyyy",
//   "uk-UA" : "dd.MM.yyyy",
//   "be-BY" : "dd.MM.yyyy",
//   "sl-SI" : "d.M.yyyy",
//   "et-EE" : "d.MM.yyyy",
//   "lv-LV" : "yyyy.MM.dd.",
//   "lt-LT" : "yyyy.MM.dd",
//   "fa-IR" : "MM/dd/yyyy",
//   "vi-VN" : "dd/MM/yyyy",
//   "hy-AM" : "dd.MM.yyyy",
//   "az-Latn-AZ" : "dd.MM.yyyy",
//   "eu-ES" : "yyyy/MM/dd",
//   "mk-MK" : "dd.MM.yyyy",
//   "af-ZA" : "yyyy/MM/dd",
//   "ka-GE" : "dd.MM.yyyy",
//   "fo-FO" : "dd-MM-yyyy",
//   "hi-IN" : "dd-MM-yyyy",
//   "ms-MY" : "dd/MM/yyyy",
//   "kk-KZ" : "dd.MM.yyyy",
//   "ky-KG" : "dd.MM.yy",
//   "sw-KE" : "M/d/yyyy",
//   "uz-Latn-UZ" : "dd/MM yyyy",
//   "tt-RU" : "dd.MM.yyyy",
//   "pa-IN" : "dd-MM-yy",
//   "gu-IN" : "dd-MM-yy",
//   "ta-IN" : "dd-MM-yyyy",
//   "te-IN" : "dd-MM-yy",
//   "kn-IN" : "dd-MM-yy",
//   "mr-IN" : "dd-MM-yyyy",
//   "sa-IN" : "dd-MM-yyyy",
//   "mn-MN" : "yy.MM.dd",
//   "gl-ES" : "dd/MM/yy",
//   "kok-IN" : "dd-MM-yyyy",
//   "syr-SY" : "dd/MM/yyyy",
//   "dv-MV" : "dd/MM/yy",
//   "ar-IQ" : "dd/MM/yyyy",
//   "zh-CN" : "yyyy/M/d",
//   "de-CH" : "dd.MM.yyyy",
//   "en-GB" : "dd/MM/yyyy",
//   "es-MX" : "dd/MM/yyyy",
//   "fr-BE" : "d/MM/yyyy",
//   "it-CH" : "dd.MM.yyyy",
//   "nl-BE" : "d/MM/yyyy",
//   "nn-NO" : "dd.MM.yyyy",
//   "pt-PT" : "dd-MM-yyyy",
//   "sr-Latn-CS" : "d.M.yyyy",
//   "sv-FI" : "d.M.yyyy",
//   "az-Cyrl-AZ" : "dd.MM.yyyy",
//   "ms-BN" : "dd/MM/yyyy",
//   "uz-Cyrl-UZ" : "dd.MM.yyyy",
//   "ar-EG" : "dd/MM/yyyy",
//   "zh-HK" : "d/M/yyyy",
//   "de-AT" : "dd.MM.yyyy",
//   "en-AU" : "d/MM/yyyy",
//   "es-ES" : "dd/MM/yyyy",
//   "fr-CA" : "yyyy-MM-dd",
//   "sr-Cyrl-CS" : "d.M.yyyy",
//   "ar-LY" : "dd/MM/yyyy",
//   "zh-SG" : "d/M/yyyy",
//   "de-LU" : "dd.MM.yyyy",
//   "en-CA" : "dd/MM/yyyy",
//   "es-GT" : "dd/MM/yyyy",
//   "fr-CH" : "dd.MM.yyyy",
//   "ar-DZ" : "dd-MM-yyyy",
//   "zh-MO" : "d/M/yyyy",
//   "de-LI" : "dd.MM.yyyy",
//   "en-NZ" : "d/MM/yyyy",
//   "es-CR" : "dd/MM/yyyy",
//   "fr-LU" : "dd/MM/yyyy",
//   "ar-MA" : "dd-MM-yyyy",
//   "en-IE" : "dd/MM/yyyy",
//   "es-PA" : "MM/dd/yyyy",
//   "fr-MC" : "dd/MM/yyyy",
//   "ar-TN" : "dd-MM-yyyy",
//   "en-ZA" : "yyyy/MM/dd",
//   "es-DO" : "dd/MM/yyyy",
//   "ar-OM" : "dd/MM/yyyy",
//   "en-JM" : "dd/MM/yyyy",
//   "es-VE" : "dd/MM/yyyy",
//   "ar-YE" : "dd/MM/yyyy",
//   "en-029" : "MM/dd/yyyy",
//   "es-CO" : "dd/MM/yyyy",
//   "ar-SY" : "dd/MM/yyyy",
//   "en-BZ" : "dd/MM/yyyy",
//   "es-PE" : "dd/MM/yyyy",
//   "ar-JO" : "dd/MM/yyyy",
//   "en-TT" : "dd/MM/yyyy",
//   "es-AR" : "dd/MM/yyyy",
//   "ar-LB" : "dd/MM/yyyy",
//   "en-ZW" : "M/d/yyyy",
//   "es-EC" : "dd/MM/yyyy",
//   "ar-KW" : "dd/MM/yyyy",
//   "en-PH" : "M/d/yyyy",
//   "es-CL" : "dd-MM-yyyy",
//   "ar-AE" : "dd/MM/yyyy",
//   "es-UY" : "dd/MM/yyyy",
//   "ar-BH" : "dd/MM/yyyy",
//   "es-PY" : "dd/MM/yyyy",
//   "ar-QA" : "dd/MM/yyyy",
//   "es-BO" : "dd/MM/yyyy",
//   "es-SV" : "dd/MM/yyyy",
//   "es-HN" : "dd/MM/yyyy",
//   "es-NI" : "dd/MM/yyyy",
//   "es-PR" : "dd/MM/yyyy",
//   "am-ET" : "d/M/yyyy",
//   "tzm-Latn-DZ" : "dd-MM-yyyy",
//   "iu-Latn-CA" : "d/MM/yyyy",
//   "sma-NO" : "dd.MM.yyyy",
//   "mn-Mong-CN" : "yyyy/M/d",
//   "gd-GB" : "dd/MM/yyyy",
//   "en-MY" : "d/M/yyyy",
//   "prs-AF" : "dd/MM/yy",
//   "bn-BD" : "dd-MM-yy",
//   "wo-SN" : "dd/MM/yyyy",
//   "rw-RW" : "M/d/yyyy",
//   "qut-GT" : "dd/MM/yyyy",
//   "sah-RU" : "MM.dd.yyyy",
//   "gsw-FR" : "dd/MM/yyyy",
//   "co-FR" : "dd/MM/yyyy",
//   "oc-FR" : "dd/MM/yyyy",
//   "mi-NZ" : "dd/MM/yyyy",
//   "ga-IE" : "dd/MM/yyyy",
//   "se-SE" : "yyyy-MM-dd",
//   "br-FR" : "dd/MM/yyyy",
//   "smn-FI" : "d.M.yyyy",
//   "moh-CA" : "M/d/yyyy",
//   "arn-CL" : "dd-MM-yyyy",
//   "ii-CN" : "yyyy/M/d",
//   "dsb-DE" : "d. M. yyyy",
//   "ig-NG" : "d/M/yyyy",
//   "kl-GL" : "dd-MM-yyyy",
//   "lb-LU" : "dd/MM/yyyy",
//   "ba-RU" : "dd.MM.yy",
//   "nso-ZA" : "yyyy/MM/dd",
//   "quz-BO" : "dd/MM/yyyy",
//   "yo-NG" : "d/M/yyyy",
//   "ha-Latn-NG" : "d/M/yyyy",
//   "fil-PH" : "M/d/yyyy",
//   "ps-AF" : "dd/MM/yy",
//   "fy-NL" : "d-M-yyyy",
//   "ne-NP" : "M/d/yyyy",
//   "se-NO" : "dd.MM.yyyy",
//   "iu-Cans-CA" : "d/M/yyyy",
//   "sr-Latn-RS" : "d.M.yyyy",
//   "si-LK" : "yyyy-MM-dd",
//   "sr-Cyrl-RS" : "d.M.yyyy",
//   "lo-LA" : "dd/MM/yyyy",
//   "km-KH" : "yyyy-MM-dd",
//   "cy-GB" : "dd/MM/yyyy",
//   "bo-CN" : "yyyy/M/d",
//   "sms-FI" : "d.M.yyyy",
//   "as-IN" : "dd-MM-yyyy",
//   "ml-IN" : "dd-MM-yy",
//   "en-IN" : "dd-MM-yyyy",
//   "or-IN" : "dd-MM-yy",
//   "bn-IN" : "dd-MM-yy",
//   "tk-TM" : "dd.MM.yy",
//   "bs-Latn-BA" : "d.M.yyyy",
//   "mt-MT" : "dd/MM/yyyy",
//   "sr-Cyrl-ME" : "d.M.yyyy",
//   "se-FI" : "d.M.yyyy",
//   "zu-ZA" : "yyyy/MM/dd",
//   "xh-ZA" : "yyyy/MM/dd",
//   "tn-ZA" : "yyyy/MM/dd",
//   "hsb-DE" : "d. M. yyyy",
//   "bs-Cyrl-BA" : "d.M.yyyy",
//   "tg-Cyrl-TJ" : "dd.MM.yy",
//   "sr-Latn-BA" : "d.M.yyyy",
//   "smj-NO" : "dd.MM.yyyy",
//   "rm-CH" : "dd/MM/yyyy",
//   "smj-SE" : "yyyy-MM-dd",
//   "quz-EC" : "dd/MM/yyyy",
//   "quz-PE" : "dd/MM/yyyy",
//   "hr-BA" : "d.M.yyyy.",
//   "sr-Latn-ME" : "d.M.yyyy",
//   "sma-SE" : "yyyy-MM-dd",
//   "en-SG" : "d/M/yyyy",
//   "ug-CN" : "yyyy-M-d",
//   "sr-Cyrl-BA" : "d.M.yyyy",
//   "es-US" : "M/d/yyyy"
// };
//
// Object.keys(formats).forEach(lang => {const period = getDayPeriods(lang); if (period.am === 'AM') return; console.log(lang, period )})

class TimeElementDirective {
    elementRef = inject(ElementRef);
    cTimeElement = input.required(...(ngDevMode ? [{ debugName: "cTimeElement" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimeElementDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TimeElementDirective, isStandalone: true, selector: "[cTimeElement]", inputs: { cTimeElement: { classPropertyName: "cTimeElement", publicName: "cTimeElement", isSignal: true, isRequired: true, transformFunction: null } }, exportAs: ["cTimeElement"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimeElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTimeElement]',
                    exportAs: 'cTimeElement',
                    standalone: true
                }]
        }], propDecorators: { cTimeElement: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTimeElement", required: true }] }] } });
class TimePickerRollColComponent {
    elementRef = inject(ElementRef);
    #init = true;
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    elements = input([], ...(ngDevMode ? [{ debugName: "elements" }] : []));
    onClick = input(...(ngDevMode ? [undefined, { debugName: "onClick" }] : []));
    refresh = input(undefined, { ...(ngDevMode ? { debugName: "refresh" } : {}), transform: booleanAttribute });
    selectedInput = input(null, { ...(ngDevMode ? { debugName: "selectedInput" } : {}), alias: 'selected' });
    selected = linkedSignal(this.selectedInput, ...(ngDevMode ? [{ debugName: "selected" }] : []));
    selectedChange = output();
    timeElements;
    ngAfterViewInit() {
        this.timeElements?.changes.subscribe((state) => {
            const selectedElement = this.timeElements?.find((element) => {
                return element.cTimeElement()?.value === this.selected();
            });
            this.scrollToElement(selectedElement?.elementRef.nativeElement);
        });
        setTimeout(() => {
            const selectedElement = this.elementRef.nativeElement.querySelector('.selected');
            this.scrollToElement(selectedElement);
        }, 1000);
    }
    ngOnChanges(changes) {
        if ((changes['refresh'] && changes['refresh'].currentValue) ||
            (changes['selectedInput'] && !changes['selectedInput'].firstChange)) {
            const selectedElement = this.timeElements?.find((element) => {
                return element.cTimeElement()?.value === this.selected();
            });
            this.#init = !selectedElement;
            this.scrollToElement(selectedElement?.elementRef.nativeElement);
        }
    }
    scrollToElement(element) {
        setTimeout(() => {
            const elementSelected = element ??
                this.elementRef.nativeElement.querySelector('.selected') ??
                this.elementRef.nativeElement.firstElementChild;
            if (elementSelected) {
                this.elementRef.nativeElement.scrollTo({
                    top: elementSelected?.offsetTop,
                    behavior: this.#init ? 'auto' : 'smooth'
                });
                this.#init = false;
            }
        });
    }
    handleElementClick($event, element) {
        if (element.disabled) {
            return;
        }
        this.selected.set(element.value);
        this.scrollToElement($event.target);
        const onClick = this.onClick();
        onClick && onClick(element.value);
        this.selectedChange.emit(element.value);
    }
    handleElementKeyDown($event, element) {
        if ($event.code === 'Space' || $event.code === 'Enter') {
            $event.preventDefault();
            this.handleElementClick($event, element);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerRollColComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: TimePickerRollColComponent, isStandalone: true, selector: "c-time-picker-roll-col", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, elements: { classPropertyName: "elements", publicName: "elements", isSignal: true, isRequired: false, transformFunction: null }, onClick: { classPropertyName: "onClick", publicName: "onClick", isSignal: true, isRequired: false, transformFunction: null }, refresh: { classPropertyName: "refresh", publicName: "refresh", isSignal: true, isRequired: false, transformFunction: null }, selectedInput: { classPropertyName: "selectedInput", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { selectedChange: "selectedChange" }, host: { properties: { "class.disabled": "disabled()" }, classAttribute: "time-picker-roll-col" }, viewQueries: [{ propertyName: "timeElements", predicate: TimeElementDirective, descendants: true }], exportAs: ["cTimePickerRollCol"], usesOnChanges: true, ngImport: i0, template: "@for (element of elements(); track element.value; let i = $index) {\n  @let cellWidth = element.label.length > 2 ? {   '--cui-timepicker-roll-cell-width' : '80px' } : {};\n  <div (click)=\"handleElementClick($event, element)\"\n       (keydown)=\"handleElementKeyDown($event, element)\"\n       [attr.disabled]=\"element.disabled || null\"\n       [cTimeElement]=\"element\"\n       [class]=\"{'time-picker-roll-cell': true, selected: element.value === selected(), disabled: element.disabled}\"\n       [tabindex]=\"element.disabled ? -1 : 0\"\n       role=\"button\"\n       [style]=\"cellWidth\"\n  >\n    {{ element.label }}\n  </div>\n}\n\n", styles: [":host{display:block}:host .disabled{pointer-events:none}:host .time-picker-roll-cell{--cui-timepicker-roll-cell-width: 50px;width:var(--cui-timepicker-roll-cell-width)}\n"], dependencies: [{ kind: "directive", type: TimeElementDirective, selector: "[cTimeElement]", inputs: ["cTimeElement"], exportAs: ["cTimeElement"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerRollColComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-time-picker-roll-col', exportAs: 'cTimePickerRollCol', imports: [TimeElementDirective], host: {
                        class: 'time-picker-roll-col',
                        '[class.disabled]': 'disabled()'
                    }, template: "@for (element of elements(); track element.value; let i = $index) {\n  @let cellWidth = element.label.length > 2 ? {   '--cui-timepicker-roll-cell-width' : '80px' } : {};\n  <div (click)=\"handleElementClick($event, element)\"\n       (keydown)=\"handleElementKeyDown($event, element)\"\n       [attr.disabled]=\"element.disabled || null\"\n       [cTimeElement]=\"element\"\n       [class]=\"{'time-picker-roll-cell': true, selected: element.value === selected(), disabled: element.disabled}\"\n       [tabindex]=\"element.disabled ? -1 : 0\"\n       role=\"button\"\n       [style]=\"cellWidth\"\n  >\n    {{ element.label }}\n  </div>\n}\n\n", styles: [":host{display:block}:host .disabled{pointer-events:none}:host .time-picker-roll-cell{--cui-timepicker-roll-cell-width: 50px;width:var(--cui-timepicker-roll-cell-width)}\n"] }]
        }], propDecorators: { disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], elements: [{ type: i0.Input, args: [{ isSignal: true, alias: "elements", required: false }] }], onClick: [{ type: i0.Input, args: [{ isSignal: true, alias: "onClick", required: false }] }], refresh: [{ type: i0.Input, args: [{ isSignal: true, alias: "refresh", required: false }] }], selectedInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "selected", required: false }] }], selectedChange: [{ type: i0.Output, args: ["selectedChange"] }], timeElements: [{
                type: ViewChildren,
                args: [TimeElementDirective]
            }] } });

class DayPeriodElementDirective {
    elementRef = inject(ElementRef);
    cDayPeriodElement = input.required(...(ngDevMode ? [{ debugName: "cDayPeriodElement" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DayPeriodElementDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: DayPeriodElementDirective, isStandalone: true, selector: "[cDayPeriodElement]", inputs: { cDayPeriodElement: { classPropertyName: "cDayPeriodElement", publicName: "cDayPeriodElement", isSignal: true, isRequired: true, transformFunction: null } }, exportAs: ["cDayPeriodElement"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DayPeriodElementDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cDayPeriodElement]',
                    exportAs: 'cDayPeriodElement',
                    standalone: true
                }]
        }], propDecorators: { cDayPeriodElement: [{ type: i0.Input, args: [{ isSignal: true, alias: "cDayPeriodElement", required: true }] }] } });
class TimePickerRollAmPmComponent {
    elementRef = inject(ElementRef);
    #init = true;
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    elements = input([], ...(ngDevMode ? [{ debugName: "elements" }] : []));
    onClick = input(...(ngDevMode ? [undefined, { debugName: "onClick" }] : []));
    refresh = input(undefined, { ...(ngDevMode ? { debugName: "refresh" } : {}), transform: booleanAttribute });
    selectedInput = input(null, { ...(ngDevMode ? { debugName: "selectedInput" } : {}), alias: 'selected' });
    selected = linkedSignal(this.selectedInput, ...(ngDevMode ? [{ debugName: "selected" }] : []));
    selectedChange = output();
    dayPeriodElements;
    ngAfterViewInit() {
        this.dayPeriodElements?.changes.subscribe((state) => {
            const selectedElement = this.dayPeriodElements?.find((element) => {
                return element.cDayPeriodElement()?.value === this.selected();
            });
            this.scrollToElement(selectedElement?.elementRef.nativeElement);
        });
        const selectedElement = this.elementRef.nativeElement.querySelector('.selected');
        this.scrollToElement(selectedElement);
    }
    ngOnChanges(changes) {
        if ((changes['refresh'] && changes['refresh'].currentValue) ||
            (changes['selectedInput'] && !changes['selectedInput'].firstChange)) {
            const selectedElement = this.dayPeriodElements?.find((element) => {
                return element.cDayPeriodElement()?.value === this.selected();
            });
            this.#init = !selectedElement;
            this.scrollToElement(selectedElement?.elementRef.nativeElement);
        }
    }
    scrollToElement(element) {
        setTimeout(() => {
            const elementSelected = element || this.elementRef.nativeElement.querySelector('.selected');
            if (elementSelected) {
                this.elementRef.nativeElement.scrollTo({
                    top: elementSelected?.offsetTop,
                    behavior: this.#init ? 'auto' : 'smooth'
                });
                this.#init = false;
            }
        });
    }
    handleElementClick($event, element) {
        if (element.disabled) {
            return;
        }
        this.selected.set(element.value);
        this.scrollToElement($event.target);
        const onClick = this.onClick();
        onClick && onClick(element.value);
        this.selectedChange.emit(element.value);
    }
    handleElementKeyDown($event, element) {
        if ($event.code === 'Space' || $event.code === 'Enter') {
            $event.preventDefault();
            this.handleElementClick($event, element);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerRollAmPmComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: TimePickerRollAmPmComponent, isStandalone: true, selector: "c-time-picker-roll-am-pm", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, elements: { classPropertyName: "elements", publicName: "elements", isSignal: true, isRequired: false, transformFunction: null }, onClick: { classPropertyName: "onClick", publicName: "onClick", isSignal: true, isRequired: false, transformFunction: null }, refresh: { classPropertyName: "refresh", publicName: "refresh", isSignal: true, isRequired: false, transformFunction: null }, selectedInput: { classPropertyName: "selectedInput", publicName: "selected", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { selectedChange: "selectedChange" }, host: { properties: { "class.disabled": "disabled()" }, classAttribute: "time-picker-roll-col" }, viewQueries: [{ propertyName: "dayPeriodElements", predicate: DayPeriodElementDirective, descendants: true }], usesOnChanges: true, ngImport: i0, template: "@for (element of elements(); track element.value; let i = $index) {\n  <div (click)=\"handleElementClick($event, element)\"\n       (keydown)=\"handleElementKeyDown($event, element)\"\n       [attr.disabled]=\"element.disabled || null\"\n       [cDayPeriodElement]=\"element\"\n       [class]=\"{'time-picker-roll-cell': true, selected: element.value === selected(), disabled: element.disabled}\"\n       [tabindex]=\"element.disabled ? -1 : 0\"\n       role=\"button\"\n  >\n    {{ element.label }}\n  </div>\n}\n", styles: [":host{display:block}:host .disabled{pointer-events:none}.time-picker-roll-cell{--cui-timepicker-roll-cell-width: 53px;width:var(--cui-timepicker-roll-cell-width)}\n"], dependencies: [{ kind: "directive", type: DayPeriodElementDirective, selector: "[cDayPeriodElement]", inputs: ["cDayPeriodElement"], exportAs: ["cDayPeriodElement"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerRollAmPmComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-time-picker-roll-am-pm', imports: [DayPeriodElementDirective], host: {
                        class: 'time-picker-roll-col',
                        '[class.disabled]': 'disabled()'
                    }, template: "@for (element of elements(); track element.value; let i = $index) {\n  <div (click)=\"handleElementClick($event, element)\"\n       (keydown)=\"handleElementKeyDown($event, element)\"\n       [attr.disabled]=\"element.disabled || null\"\n       [cDayPeriodElement]=\"element\"\n       [class]=\"{'time-picker-roll-cell': true, selected: element.value === selected(), disabled: element.disabled}\"\n       [tabindex]=\"element.disabled ? -1 : 0\"\n       role=\"button\"\n  >\n    {{ element.label }}\n  </div>\n}\n", styles: [":host{display:block}:host .disabled{pointer-events:none}.time-picker-roll-cell{--cui-timepicker-roll-cell-width: 53px;width:var(--cui-timepicker-roll-cell-width)}\n"] }]
        }], propDecorators: { disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], elements: [{ type: i0.Input, args: [{ isSignal: true, alias: "elements", required: false }] }], onClick: [{ type: i0.Input, args: [{ isSignal: true, alias: "onClick", required: false }] }], refresh: [{ type: i0.Input, args: [{ isSignal: true, alias: "refresh", required: false }] }], selectedInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "selected", required: false }] }], selectedChange: [{ type: i0.Output, args: ["selectedChange"] }], dayPeriodElements: [{
                type: ViewChildren,
                args: [DayPeriodElementDirective]
            }] } });

// todo: Focus management
class TimePickerComponent {
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_inputReadOnly;
    static ngAcceptInputType_seconds;
    static ngAcceptInputType_visible;
    #destroyRef = inject(DestroyRef);
    /**
     * Toggle visibility or set the content of the cleaner button.
     * @return boolean
     * @default true
     */
    cleaner = input(true, { ...(ngDevMode ? { debugName: "cleaner" } : {}), transform: booleanAttribute });
    /**
     * Sets DateTime format options including timeZone
     * @return DateTimeFormatOptions
     * @default {}
     * @note The date object doesn't store timeZone, it keeps the local timezone of the browser, regardless if you parsed UTC dates.
     */
    dateTimeFormatOptions = input({}, ...(ngDevMode ? [{ debugName: "dateTimeFormatOptions" }] : []));
    /**
     * Toggle the disabled state for the component.
     * @return boolean
     * @default false
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    disabledEffect = effect(() => {
        if (this.disabled()) {
            this.timeInputCtrl.disable({ emitEvent: false });
        }
        else {
            this.timeInputCtrl.enable({ emitEvent: false });
        }
    }, ...(ngDevMode ? [{ debugName: "disabledEffect" }] : []));
    /**
     * Filter available hours to pick.
     * @return (value: number) => boolean;
     * @default undefined
     */
    filterHours = input(...(ngDevMode ? [undefined, { debugName: "filterHours" }] : []));
    /**
     * Filter available minutes to pick.
     * @return (value: number) => boolean;
     * @default undefined
     */
    filterMinutes = input(...(ngDevMode ? [undefined, { debugName: "filterMinutes" }] : []));
    /**
     * Filter available seconds to pick.
     * @return (value: number) => boolean;
     * @default undefined
     */
    filterSeconds = input(...(ngDevMode ? [undefined, { debugName: "filterSeconds" }] : []));
    /**
     * Toggle visibility or set the content of the input indicator.
     * @return boolean
     * @default true
     */
    indicator = input(true, { ...(ngDevMode ? { debugName: "indicator" } : {}), transform: booleanAttribute });
    /**
     * Toggle the readonly state for the component.
     * @return boolean
     * @default false
     */
    inputReadOnly = input(false, { ...(ngDevMode ? { debugName: "inputReadOnly" } : {}), transform: booleanAttribute });
    /**
     * Sets the default locale for components. If not set, it is inherited from the browser.
     * @return string
     * @default default
     */
    locale = input('default', ...(ngDevMode ? [{ debugName: "locale" }] : []));
    /**
     * Specifies short hint visible in time input.
     * @return string
     * @default 'Select time'
     */
    placeholder = input('Select time', ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    /**
     * Toggle seconds visibility.
     * @return boolean
     * @default false
     */
    seconds = input(false, { ...(ngDevMode ? { debugName: "seconds" } : {}), transform: booleanAttribute });
    /**
     * Size the component small or large.
     * @return 'sm' | 'lg' | undefined
     * @default undefined
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Initial selected time.
     * @return Date | string | null | number
     * @default undefined
     */
    set time(value) {
        if (value && isValidTime(value)) {
            const time = convertTimeToDate(value);
            if (!time) {
                this.clearTime();
                return;
            }
            this.dayPeriod = getAmPm(time, this.locale());
            if (this._time?.getTime() !== time.getTime()) {
                this._time = new Date(time.getTime());
            }
        }
        else {
            this.clearTime();
        }
    }
    get time() {
        return this._time;
    }
    _time;
    _timeInternal;
    /**
     * Set the time picker variant to a roll or select.
     * @return 'roll' | 'select'
     * @default 'roll'
     */
    variant = input('roll', ...(ngDevMode ? [{ debugName: "variant" }] : []));
    /**
     * Toggle visual validation feedback.
     * @return boolean | undefined
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Toggle the visibility of dropdown timepicker menu component.
     * @return boolean
     * @default false
     */
    visible = input(false, { ...(ngDevMode ? { debugName: "visible" } : {}), transform: booleanAttribute });
    timeChange = output();
    onBlur() {
        this.onTouched();
    }
    dropdownRef = viewChild(DropdownComponent, { ...(ngDevMode ? { debugName: "dropdownRef" } : {}), read: ElementRef });
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    templates = computed(() => {
        return this.contentTemplates().reduce((acc, child) => {
            acc[child.id] = child.templateRef;
            return acc;
        }, {});
    }, ...(ngDevMode ? [{ debugName: "templates" }] : []));
    listOfHours;
    listOfHours12;
    listOfMinutes;
    listOfSeconds;
    hour12 = isHour12(this.locale());
    dayPeriods = [
        { value: 'am', label: 'am' },
        { value: 'pm', label: 'pm' }
    ];
    getListOfHours12() {
        const listOfHours12 = this.hour12
            ? this.listOfHours?.filter((item) => (item.dayPeriod && item.dayPeriod === this.dayPeriod) || !item.dayPeriod)
            : this.listOfHours;
        if (this.hour12 && [0, 12].includes(listOfHours12[0].value)) {
            listOfHours12.push(listOfHours12.shift());
        }
        return listOfHours12;
    }
    set timeInputValue(value) {
        this.timeInputCtrl.setValue(value);
        this._timeInputValue = value;
    }
    get timeInputValue() {
        return this._timeInputValue;
    }
    _timeInputValue = this.time?.toLocaleTimeString(this.locale(), { timeStyle: this.seconds() ? 'medium' : 'short' }) ?? '';
    timePickerClasses = computed(() => {
        const disabled = this.disabled();
        const size = this.size();
        const valid = this.valid();
        const variant = this.variant();
        return {
            'time-picker': true,
            [`time-picker-${size}`]: !!size,
            'form-control': variant === 'select' && valid !== undefined,
            'is-valid': valid === true,
            'is-invalid': valid === false,
            disabled: disabled,
            dropdown: false
        };
    }, ...(ngDevMode ? [{ debugName: "timePickerClasses" }] : []));
    hostClasses = computed(() => {
        const valid = this.valid();
        return {
            'time-picker': true,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    selectTime = new FormGroup({
        selectHours: new FormControl({ value: this.hour, disabled: this.disabled() }, { validators: [Validators.min(0), Validators.max(this.hour12 ? 11 : 23)] }),
        selectMinutes: new FormControl({ value: this.minute, disabled: this.disabled() }, { validators: [Validators.min(0), Validators.max(59)] }),
        selectSeconds: new FormControl({ value: this.second, disabled: this.disabled() }, { validators: [Validators.min(0), Validators.max(59)] }),
        selectDayPeriod: new FormControl({ value: this.dayPeriod, disabled: this.disabled() }, Validators.required)
    }, { validators: [Validators.required] });
    timeInputCtrl = new FormControl({
        value: this.timeInputValue,
        disabled: this.disabled()
    }, { validators: [Validators.required] });
    set hour(value) {
        if (this._hour !== value) {
            if (value < 0) {
                this.clearTime();
                return;
            }
            this._hour = value;
        }
    }
    get hour() {
        return this._hour;
    }
    _hour = -1;
    set minute(value) {
        if (this._minute !== value) {
            if (value < 0) {
                this.clearTime();
                return;
            }
            this._minute = value;
        }
    }
    get minute() {
        return this._minute;
    }
    _minute = -1;
    set second(value) {
        if (this._second !== value) {
            if (value < 0) {
                this.clearTime();
                return;
            }
            this._second = value;
        }
    }
    get second() {
        return this._second;
    }
    _second = -1;
    set dayPeriod(value) {
        if (this._dayPeriod !== value) {
            this._dayPeriod = value;
            if (this.listOfHours) {
                this.listOfHours12 = this.getListOfHours12();
            }
            this.patchSelectTimeValues();
        }
        // this.listOfHours12 = this.hour12
        //                      ? this.listOfHours?.filter(
        //     (item) => (item.dayPeriod && item.dayPeriod === value) || !item.dayPeriod
        //   )
        //                      : this.listOfHours;
        // this.patchSelectTimeValues();
    }
    get dayPeriod() {
        return this._dayPeriod;
    }
    _dayPeriod = 'am';
    setTime() {
        this._hour = this._hour < 0 ? 0 : this._hour;
        this._minute = this._minute < 0 ? 0 : this._minute;
        this._second = this._second < 0 ? 0 : this._second;
        const time = new Date(this._time?.getTime() || new Date());
        time.setHours(this.hour, this.minute, this.second, 0);
        this.time = time;
        this.emitTime(time);
    }
    clearTime() {
        if (this.time) {
            this._time = undefined;
            this.emitTime(undefined);
        }
        this._hour = -1;
        this._minute = -1;
        this._second = -1;
        this.dayPeriod = 'am';
        this.patchSelectTimeValues();
        this.timeInputValue = '';
    }
    emitTime(time) {
        const timeValue = !!time && isValidTime(time) ? new Date(time.getTime()) : undefined;
        if (this._timeInternal?.getTime() !== timeValue?.getTime()) {
            this.timeChange.emit(timeValue);
            this.onChange(timeValue);
            this.onTouched();
            this._timeInternal = timeValue;
        }
        this.timeInputValue =
            this.time?.toLocaleTimeString(this.locale(), {
                timeStyle: this.seconds() ? 'medium' : 'short'
            }) ?? '';
    }
    ngOnInit() {
        this.setTimeLists();
        this.updateSelectTime();
        this.setChangeHandlers();
    }
    ngOnChanges(changes) {
        if (changes['time']) {
            if (!changes['time'].firstChange) {
                if (this._timeInternal?.getTime() !== changes['time'].currentValue?.getTime()) {
                    this.updateSelectTime();
                }
            }
        }
    }
    setTimeLists() {
        this.dayPeriods = getDayPeriods(this.locale(), this.dateTimeFormatOptions());
        this.hour12 = isHour12(this.locale());
        this.listOfHours = this.listOfTimeSegments(getListOfHours, this.filterHours());
        this.listOfMinutes = this.listOfTimeSegments(getListOfMinutes, this.filterMinutes());
        this.listOfSeconds = this.listOfTimeSegments(getListOfSeconds, this.filterSeconds());
        if (this.variant() === 'select') {
            const undefSegment = { value: -1, label: ' --', disabled: true };
            this.listOfHours.unshift({ ...undefSegment });
            this.listOfMinutes.unshift({ ...undefSegment });
            this.listOfSeconds.unshift({ ...undefSegment });
        }
        this.listOfHours12 = this.getListOfHours12();
    }
    updateSelectTime(time = this.time) {
        if (!!time && isValidTime(time)) {
            const hour = time.getHours() ?? 0;
            const minute = time.getMinutes() ?? 0;
            const second = this.seconds() ? (time.getSeconds() ?? 0) : 0;
            const closestSecond = this.closestTime(this.listOfSeconds, second);
            const closestMinute = this.closestTime(this.listOfMinutes, minute);
            const closestHour = this.closestTime(this.listOfHours, hour);
            this.second = this.seconds() ? closestSecond.value : 0;
            this.minute = closestMinute.value;
            this.hour = closestHour.value;
            this.setTime();
            this.patchSelectTimeValues();
        }
    }
    closestTime(timeArray, timeSegment) {
        let array = timeArray ?? [{ value: 0, label: '00' }];
        if (this.time) {
            array = timeArray.filter((el) => el.value >= 0);
            array = array.length ? array : timeArray;
        }
        return array.reduce((a, b) => {
            return Math.abs(b.value - timeSegment) < Math.abs(a.value - timeSegment) ? b : a;
        });
    }
    listOfTimeSegments(getListOfTimeSegments, filterTimeSegments) {
        const list = getListOfTimeSegments(this.locale(), this.dateTimeFormatOptions()).filter((segment) => {
            return typeof filterTimeSegments === 'function' ? filterTimeSegments(segment.value) : true;
        });
        return list.length ? list : getListOfTimeSegments(this.locale(), this.dateTimeFormatOptions()).slice(0, 1);
    }
    handleSelectDayPeriodChange(dayPeriod) {
        if (dayPeriod !== this.dayPeriod) {
            const hrs = this.time?.getHours() ?? (this.hour < 0 ? 0 : this.hour);
            const hour = hrs > 12 ? convert24hTo12h(hrs) : convert12hTo24h(dayPeriod, hrs);
            this.hour = this.closestTime(this.listOfHours, hour).value;
            if (hour >= 0) {
                this.setTime();
            }
            this.updateSelectTime();
        }
    }
    handleSelectTimeChange(value, segment) {
        this[segment] = value;
        if (value >= 0) {
            this.setTime();
        }
        this.updateSelectTime();
    }
    patchSelectTimeValues() {
        const dayPeriod = this.selectTime.get('selectDayPeriod')?.value;
        if (this.dayPeriod !== dayPeriod) {
            this.selectTime.patchValue({
                selectDayPeriod: this.dayPeriod ?? 'am'
            });
        }
        const hours = this.selectTime.get('selectHours')?.value;
        const minutes = this.selectTime.get('selectMinutes')?.value;
        const seconds = this.selectTime.get('selectSeconds')?.value;
        const goUpdate = (this.second < 0 && this.minute < 0) || (this.hour >= 0 && this.minute >= 0 && this.second >= 0);
        const timeChanged = this.hour !== hours || this.minute !== minutes || this.second !== seconds;
        if (goUpdate && timeChanged) {
            if (this.hour !== hours) {
                this.selectTime.patchValue({
                    selectHours: this.hour ?? 0
                });
            }
            if (this.minute !== minutes) {
                this.selectTime.patchValue({
                    selectMinutes: this.minute ?? 0
                });
            }
            if (this.second !== seconds) {
                this.selectTime.patchValue({
                    selectSeconds: this.second ?? 0
                });
            }
        }
    }
    trackByHour(index, hour) {
        return hour;
    }
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    writeValue(value) {
        this.time = value;
        this.updateSelectTime();
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled.set(isDisabled);
    }
    handleTimeInputChange($event) {
        const value = $event.target?.value;
        if (!value || !isValidTime(value)) {
            this.clearTime();
            return;
        }
        const time = convertTimeToDate(value);
        this.updateSelectTime(time ?? new Date(Date.now()));
    }
    handleClear($event) {
        this.clearTime();
    }
    handleBlur($event) {
        if (this.disabled()) {
            return;
        }
        this.dropdownRef()?.nativeElement?.classList.remove('show');
        setTimeout(() => {
            this.onBlur();
        }, 100);
    }
    handleFocus($event) {
        if (this.disabled()) {
            return;
        }
        this.dropdownRef()?.nativeElement?.classList.add('show');
    }
    setChangeHandlers() {
        if (this.variant() !== 'select') {
            return;
        }
        this.selectTime
            .get('selectHours')
            ?.valueChanges.pipe(tap((value) => {
            this.handleSelectTimeChange(value, 'hour');
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.selectTime
            .get('selectMinutes')
            ?.valueChanges.pipe(tap((value) => {
            this.handleSelectTimeChange(value, 'minute');
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.selectTime
            .get('selectSeconds')
            ?.valueChanges.pipe(filter(() => this.seconds()), tap((value) => {
            this.handleSelectTimeChange(value, 'second');
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.selectTime
            .get('selectDayPeriod')
            ?.valueChanges.pipe(filter(() => this.hour12), tap((value) => {
            this.handleSelectDayPeriodChange(value);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: TimePickerComponent, isStandalone: true, selector: "c-time-picker", inputs: { cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: true, isRequired: false, transformFunction: null }, dateTimeFormatOptions: { classPropertyName: "dateTimeFormatOptions", publicName: "dateTimeFormatOptions", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, filterHours: { classPropertyName: "filterHours", publicName: "filterHours", isSignal: true, isRequired: false, transformFunction: null }, filterMinutes: { classPropertyName: "filterMinutes", publicName: "filterMinutes", isSignal: true, isRequired: false, transformFunction: null }, filterSeconds: { classPropertyName: "filterSeconds", publicName: "filterSeconds", isSignal: true, isRequired: false, transformFunction: null }, indicator: { classPropertyName: "indicator", publicName: "indicator", isSignal: true, isRequired: false, transformFunction: null }, inputReadOnly: { classPropertyName: "inputReadOnly", publicName: "inputReadOnly", isSignal: true, isRequired: false, transformFunction: null }, locale: { classPropertyName: "locale", publicName: "locale", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, seconds: { classPropertyName: "seconds", publicName: "seconds", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, time: { classPropertyName: "time", publicName: "time", isSignal: false, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { timeChange: "timeChange" }, host: { listeners: { "focusout": "onBlur()" }, properties: { "class": "hostClasses()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => TimePickerComponent),
                multi: true
            }
        ], queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], viewQueries: [{ propertyName: "dropdownRef", first: true, predicate: DropdownComponent, descendants: true, read: ElementRef, isSignal: true }], exportAs: ["cTimePicker"], usesOnChanges: true, ngImport: i0, template: "@if (variant() === 'select') {\n  <div class=\"date-picker-timepickers\">\n    <div class=\"picker time-picker\">\n      <div [formGroup]=\"selectTime\" [class]=\"timePickerClasses()\" class=\"time-picker-body\">\n        <span class=\"time-picker-inline-icon\"></span>\n        <select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectHours\">\n          @for (hour of listOfHours12; track hour.value; let i = $index) {\n            <option [attr.disabled]=\"hour.disabled || null\" [ngValue]=\"hour.value\">\n              {{ hour.label }}\n            </option>\n          }\n        </select>\n        {{ ' :' }}<select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                          cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectMinutes\">\n        @for (minute of listOfMinutes; track minute.value; let i = $index) {\n          <option [attr.disabled]=\"minute.disabled || null\" [ngValue]=\"minute.value\">\n            {{ minute.label }}\n          </option>\n        }\n      </select>\n        @if (seconds()) {\n          {{ ' :' }}<select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                            cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectSeconds\">\n            @for (second of listOfSeconds; track second.value; let i = $index) {\n              <option [attr.disabled]=\"second.disabled || null\" [ngValue]=\"second.value\">\n                {{ second.label }}\n              </option>\n            }\n          </select>\n        }\n        @if (hour12) {\n          <select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                  cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectDayPeriod\">\n            @for (dayPeriod of dayPeriods; track dayPeriod.value) {\n              <option [ngValue]=\"dayPeriod.value\" class=\"time-picker-roll-cell\">\n                {{ dayPeriod.label }}\n              </option>\n            }\n          </select>\n        }\n      </div>\n    </div>\n  </div>\n}\n@if (variant() === 'roll') {\n  <c-dropdown #dropdown=\"cDropdown\" [autoClose]=\"'outside'\" [class]=\"timePickerClasses()\" [visible]=\"visible()\">\n    <div [caret]=\"false\" cDropdownToggle class=\"time-picker-input-group\">\n      <input (blur)=\"handleBlur($event);\"\n             (change)=\"handleTimeInputChange($event)\"\n             (focus)=\"handleFocus($event)\"\n             [attr.pattern]=\"hour12 ? '(((0[1-9])|(1[0-2])):([0-5])([0-9])\\\\s(A|P)M)' : '([01]?[0-9]|2[0-3]):[0-5][0-9]'\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [formControl]=\"timeInputCtrl\"\n             [placeholder]=\"placeholder()\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             class=\"time-picker-input\"\n      >\n      @if (indicator()) {\n        <div class=\"time-picker-indicator\"></div>\n      }\n      @if (cleaner() && time && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"time-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div #dropdownMenu=\"cDropdownMenu\" cDropdownMenu class=\"time-picker-dropdown py-0\">\n      <div class=\"time-picker-body time-picker-roll\" style=\"position: relative;\">\n        <c-time-picker-roll-col\n          (selectedChange)=\"handleSelectTimeChange($event, 'hour')\"\n          [disabled]=\"disabled()\"\n          [elements]=\"listOfHours12\"\n          [refresh]=\"dropdownMenu.visible()\"\n          [selected]=\"hour\"\n          role=\"listbox\"\n        />\n        <c-time-picker-roll-col\n          (selectedChange)=\"handleSelectTimeChange($event, 'minute')\"\n          [disabled]=\"disabled()\"\n          [elements]=\"listOfMinutes\"\n          [refresh]=\"dropdownMenu.visible()\"\n          [selected]=\"minute\"\n          role=\"listbox\"\n        />\n        @if (seconds()) {\n          <c-time-picker-roll-col\n            (selectedChange)=\"handleSelectTimeChange($event, 'second')\"\n            [disabled]=\"disabled()\"\n            [elements]=\"listOfSeconds\"\n            [refresh]=\"dropdownMenu.visible()\"\n            [selected]=\"second\"\n            role=\"listbox\"\n          />\n        }\n        @if (hour12) {\n          <c-time-picker-roll-am-pm\n            (selectedChange)=\"handleSelectDayPeriodChange($event)\"\n            [disabled]=\"disabled()\"\n            [elements]=\"dayPeriods\"\n            [refresh]=\"dropdownMenu.visible()\"\n            [selected]=\"dayPeriod\"\n            role=\"listbox\"\n          />\n        }\n      </div>\n      @let tmpl = templates();\n      @if (tmpl?.['timePickerFooter']) {\n        <div class=\"time-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"tmpl?.['timePickerFooter']; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n  </c-dropdown>\n}\n", styles: [".disabled{pointer-events:none}:host-context(.input-group) :host:not(:first-child) .time-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .time-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .time-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .time-picker-input-group{border-start-end-radius:var(--cui-time-picker-border-radius);border-end-end-radius:var(--cui-time-picker-border-radius)}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],[formArray],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: DropdownComponent, selector: "c-dropdown", inputs: ["alignment", "autoClose", "direction", "placement", "popper", "popperOptions", "variant", "visible"], outputs: ["visibleChange"], exportAs: ["cDropdown"] }, { kind: "directive", type: DropdownToggleDirective, selector: "[cDropdownToggle]", inputs: ["dropdownComponent", "disabled", "caret", "split"], exportAs: ["cDropdownToggle"] }, { kind: "directive", type: DropdownMenuDirective, selector: "[cDropdownMenu]", inputs: ["alignment", "visible"], exportAs: ["cDropdownMenu"] }, { kind: "directive", type: FormSelectDirective, selector: "select[cSelect]", inputs: ["sizing", "valid"] }, { kind: "component", type: TimePickerRollColComponent, selector: "c-time-picker-roll-col", inputs: ["disabled", "elements", "onClick", "refresh", "selected"], outputs: ["selectedChange"], exportAs: ["cTimePickerRollCol"] }, { kind: "component", type: TimePickerRollAmPmComponent, selector: "c-time-picker-roll-am-pm", inputs: ["disabled", "elements", "onClick", "refresh", "selected"], outputs: ["selectedChange"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-time-picker', exportAs: 'cTimePicker', imports: [
                        NgTemplateOutlet,
                        ReactiveFormsModule,
                        DropdownComponent,
                        DropdownToggleDirective,
                        DropdownMenuDirective,
                        FormSelectDirective,
                        TimePickerRollColComponent,
                        TimePickerRollAmPmComponent
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => TimePickerComponent),
                            multi: true
                        }
                    ], host: {
                        '(focusout)': 'onBlur()',
                        '[class]': 'hostClasses()'
                    }, template: "@if (variant() === 'select') {\n  <div class=\"date-picker-timepickers\">\n    <div class=\"picker time-picker\">\n      <div [formGroup]=\"selectTime\" [class]=\"timePickerClasses()\" class=\"time-picker-body\">\n        <span class=\"time-picker-inline-icon\"></span>\n        <select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectHours\">\n          @for (hour of listOfHours12; track hour.value; let i = $index) {\n            <option [attr.disabled]=\"hour.disabled || null\" [ngValue]=\"hour.value\">\n              {{ hour.label }}\n            </option>\n          }\n        </select>\n        {{ ' :' }}<select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                          cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectMinutes\">\n        @for (minute of listOfMinutes; track minute.value; let i = $index) {\n          <option [attr.disabled]=\"minute.disabled || null\" [ngValue]=\"minute.value\">\n            {{ minute.label }}\n          </option>\n        }\n      </select>\n        @if (seconds()) {\n          {{ ' :' }}<select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                            cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectSeconds\">\n            @for (second of listOfSeconds; track second.value; let i = $index) {\n              <option [attr.disabled]=\"second.disabled || null\" [ngValue]=\"second.value\">\n                {{ second.label }}\n              </option>\n            }\n          </select>\n        }\n        @if (hour12) {\n          <select (blur)=\"onBlur()\" [attr.disabled]=\"disabled() || null\" [sizing]=\"size() ?? ''\"\n                  cSelect class=\"time-picker-inline-select ms-0\" formControlName=\"selectDayPeriod\">\n            @for (dayPeriod of dayPeriods; track dayPeriod.value) {\n              <option [ngValue]=\"dayPeriod.value\" class=\"time-picker-roll-cell\">\n                {{ dayPeriod.label }}\n              </option>\n            }\n          </select>\n        }\n      </div>\n    </div>\n  </div>\n}\n@if (variant() === 'roll') {\n  <c-dropdown #dropdown=\"cDropdown\" [autoClose]=\"'outside'\" [class]=\"timePickerClasses()\" [visible]=\"visible()\">\n    <div [caret]=\"false\" cDropdownToggle class=\"time-picker-input-group\">\n      <input (blur)=\"handleBlur($event);\"\n             (change)=\"handleTimeInputChange($event)\"\n             (focus)=\"handleFocus($event)\"\n             [attr.pattern]=\"hour12 ? '(((0[1-9])|(1[0-2])):([0-5])([0-9])\\\\s(A|P)M)' : '([01]?[0-9]|2[0-3]):[0-5][0-9]'\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [formControl]=\"timeInputCtrl\"\n             [placeholder]=\"placeholder()\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             class=\"time-picker-input\"\n      >\n      @if (indicator()) {\n        <div class=\"time-picker-indicator\"></div>\n      }\n      @if (cleaner() && time && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"time-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div #dropdownMenu=\"cDropdownMenu\" cDropdownMenu class=\"time-picker-dropdown py-0\">\n      <div class=\"time-picker-body time-picker-roll\" style=\"position: relative;\">\n        <c-time-picker-roll-col\n          (selectedChange)=\"handleSelectTimeChange($event, 'hour')\"\n          [disabled]=\"disabled()\"\n          [elements]=\"listOfHours12\"\n          [refresh]=\"dropdownMenu.visible()\"\n          [selected]=\"hour\"\n          role=\"listbox\"\n        />\n        <c-time-picker-roll-col\n          (selectedChange)=\"handleSelectTimeChange($event, 'minute')\"\n          [disabled]=\"disabled()\"\n          [elements]=\"listOfMinutes\"\n          [refresh]=\"dropdownMenu.visible()\"\n          [selected]=\"minute\"\n          role=\"listbox\"\n        />\n        @if (seconds()) {\n          <c-time-picker-roll-col\n            (selectedChange)=\"handleSelectTimeChange($event, 'second')\"\n            [disabled]=\"disabled()\"\n            [elements]=\"listOfSeconds\"\n            [refresh]=\"dropdownMenu.visible()\"\n            [selected]=\"second\"\n            role=\"listbox\"\n          />\n        }\n        @if (hour12) {\n          <c-time-picker-roll-am-pm\n            (selectedChange)=\"handleSelectDayPeriodChange($event)\"\n            [disabled]=\"disabled()\"\n            [elements]=\"dayPeriods\"\n            [refresh]=\"dropdownMenu.visible()\"\n            [selected]=\"dayPeriod\"\n            role=\"listbox\"\n          />\n        }\n      </div>\n      @let tmpl = templates();\n      @if (tmpl?.['timePickerFooter']) {\n        <div class=\"time-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"tmpl?.['timePickerFooter']; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n  </c-dropdown>\n}\n", styles: [".disabled{pointer-events:none}:host-context(.input-group) :host:not(:first-child) .time-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .time-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .time-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .time-picker-input-group{border-start-end-radius:var(--cui-time-picker-border-radius);border-end-end-radius:var(--cui-time-picker-border-radius)}\n"] }]
        }], propDecorators: { cleaner: [{ type: i0.Input, args: [{ isSignal: true, alias: "cleaner", required: false }] }], dateTimeFormatOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "dateTimeFormatOptions", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], filterHours: [{ type: i0.Input, args: [{ isSignal: true, alias: "filterHours", required: false }] }], filterMinutes: [{ type: i0.Input, args: [{ isSignal: true, alias: "filterMinutes", required: false }] }], filterSeconds: [{ type: i0.Input, args: [{ isSignal: true, alias: "filterSeconds", required: false }] }], indicator: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicator", required: false }] }], inputReadOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "inputReadOnly", required: false }] }], locale: [{ type: i0.Input, args: [{ isSignal: true, alias: "locale", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], seconds: [{ type: i0.Input, args: [{ isSignal: true, alias: "seconds", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], time: [{
                type: Input
            }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], timeChange: [{ type: i0.Output, args: ["timeChange"] }], dropdownRef: [{ type: i0.ViewChild, args: [i0.forwardRef(() => DropdownComponent), { ...{ read: ElementRef }, isSignal: true }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class TimePickerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: TimePickerModule, imports: [TimePickerComponent], exports: [TimePickerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerModule, imports: [TimePickerComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TimePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TimePickerComponent],
                    exports: [TimePickerComponent]
                }]
        }] });

class CustomRangeKeyPipe {
    transform(customRange, ...args) {
        return customRange[0];
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CustomRangeKeyPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: CustomRangeKeyPipe, isStandalone: true, name: "customRangeKey" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: CustomRangeKeyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'customRangeKey',
                    standalone: true
                }]
        }] });

/** Must be a valid date string */
// todo: validation
// export function invalidDateStringValidator(locale: string): ValidatorFn {
//   return (control: AbstractControl): ValidationErrors | null => {
//     // const validDate = !isNaN(Date.parse(control.value));
//     stringToDateConvert(control.value, locale);
//     const dateShim = cvtDate(control.value, locale);
//     // @ts-ignore
//     // control.setValue(new Date(dateShim.year, dateShim.month, dateShim.day))
//     console.log('dateShim', dateShim, locale);
//     const validDate = isValidDate(control.value);
//     console.log('invalidDateStringValidator', control, control.value, validDate);
//     return validDate ? { invalidDateStringValidator: { value: control.value } } : null;
//   };
// }
class DateRangePickerComponent {
    // Treats input as boolean value, even if provided as string or other types. Uses angular coercion mechanism
    static ngAcceptInputType_closeOnSelect;
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_inputReadOnly;
    static ngAcceptInputType_navYearFirst;
    static ngAcceptInputType_withTime;
    static ngAcceptInputType_selectAdjacentDays;
    static ngAcceptInputType_showWeekNumber;
    static ngAcceptInputType_timepicker;
    static ngAcceptInputType_visible;
    #cdr = inject(ChangeDetectorRef);
    #breakpointObserver = inject(BreakpointObserver);
    #destroyRef = inject(DestroyRef);
    /**
     * Set the format of day name.
     * @default 'numeric'
     * @return DayFormatType
     */
    dayFormat = input('numeric', ...(ngDevMode ? [{ debugName: "dayFormat" }] : []));
    /**
     * The number of calendars that render on desktop devices.
     * @return number
     * @default 2
     */
    calendars = input(2, { ...(ngDevMode ? { debugName: "calendars" } : {}), transform: numberAttribute });
    /**
     * Toggle visibility or set the content of the cleaner button.
     * @return boolean
     * @default true
     */
    cleaner = input(true, { ...(ngDevMode ? { debugName: "cleaner" } : {}), transform: booleanAttribute });
    /**
     * Determine if the dropdown should be closed after value setting.
     * @return boolean
     * @default false
     */
    closeOnSelect = input(false, { ...(ngDevMode ? { debugName: "closeOnSelect" } : {}), transform: booleanAttribute });
    /**
     * Set date format.
     * We use Angular formatDate() function, see:
     * - https://angular.io/api/common/formatDate
     * - https://angular.io/api/common/DatePipe#pre-defined-format-options
     */
    format = input(...(ngDevMode ? [undefined, { debugName: "format" }] : []));
    /**
     * Toggle visibility or set the content of the input indicator.
     * @return boolean
     * @default true
     */
    indicator = input(true, ...(ngDevMode ? [{ debugName: "indicator" }] : []));
    /**
     * Custom function to format the selected date into a string according to a custom format.
     * @since v5.0.0
     */
    inputDateFormat = input(...(ngDevMode ? [undefined, { debugName: "inputDateFormat" }] : []));
    /**
     * Custom function to parse the input value into a valid Date object.
     * @since v5.0.0
     */
    inputDateParse = input(...(ngDevMode ? [undefined, { debugName: "inputDateParse" }] : []));
    /**
     * Toggle the readonly state for the component.
     * @return boolean
     * @default false
     */
    inputReadOnlyInput = input(false, { ...(ngDevMode ? { debugName: "inputReadOnlyInput" } : {}), transform: booleanAttribute, alias: 'inputReadOnly' });
    inputReadOnly = computed(() => {
        return this.inputReadOnlyInput() || typeof this.format() === 'string';
    }, ...(ngDevMode ? [{ debugName: "inputReadOnly" }] : []));
    /**
     * Reorder year-month navigation, and render year first.
     * @return boolean
     * @default false
     */
    navYearFirst = input(false, { ...(ngDevMode ? { debugName: "navYearFirst" } : {}), transform: booleanAttribute });
    /**
     * Specifies short hints that are visible in start date and end date inputs.
     * type string
     */
    placeholder = input(['Start date', 'End date'], ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    /**
     * Predefined date ranges the user can select from.
     * @return ICalendarRanges
     */
    ranges = input(...(ngDevMode ? [undefined, { debugName: "ranges" }] : []));
    /**
     * Sets the color context of the cancel button to one of CoreUI’s themed colors.
     * @return 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light' | string
     * @default 'secondary'
     */
    rangesButtonsColor = input('secondary', ...(ngDevMode ? [{ debugName: "rangesButtonsColor" }] : []));
    /**
     * Size the ranges button small or large.
     * @return 'sm' | 'lg' | ''
     */
    rangesButtonsSize = input('', ...(ngDevMode ? [{ debugName: "rangesButtonsSize" }] : []));
    /**
     * Set the ranges button variant to an outlined button or a ghost button.
     * @return 'outline' | 'ghost' | undefined
     */
    rangesButtonsVariant = input('ghost', ...(ngDevMode ? [{ debugName: "rangesButtonsVariant" }] : []));
    /**
     * Default icon or character that separates two dates.
     * @return boolean
     * @default true
     */
    separator = input(true, { ...(ngDevMode ? { debugName: "separator" } : {}), transform: booleanAttribute });
    /**
     * Size the component small or large.
     * @return 'sm' | 'lg' | undefined
     * @default undefined
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Keep track of the time with the date value.
     * @return boolean
     * @default false
     */
    withTime = input(false, { ...(ngDevMode ? { debugName: "withTime" } : {}), transform: booleanAttribute });
    /**
     * Provide an additional time selection by adding select boxes to choose time.
     * @return boolean
     * @default false
     */
    timepickerInput = input(false, { ...(ngDevMode ? { debugName: "timepickerInput" } : {}), transform: booleanAttribute, alias: 'timepicker' });
    get timepicker() {
        return false;
        // return this.timepickerInput();
    }
    /**
     * Toggle visual validation feedback.
     * @return boolean | undefined
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Toggle the visibility of the dropdown date-picker component.
     * @return boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    #visibleInputEffect = effect(() => {
        this.visible.set(this.visibleInput());
    }, ...(ngDevMode ? [{ debugName: "#visibleInputEffect" }] : []));
    visible = signal(false, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    startDateModel = model(null, { ...(ngDevMode ? { debugName: "startDateModel" } : {}), alias: 'startDate' });
    #dateModelEffect = effect(() => {
        this.dateRangeGroup.patchValue({ startDateInput: this.startDateValue(), endDateInput: this.endDateValue() });
        const startDate = this.startDate();
        const endDate = this.endDate();
        this.valueModel.set({ startDate: startDate, endDate: endDate });
        this.startDateModel.set(startDate);
        this.endDateModel.set(endDate);
    }, ...(ngDevMode ? [{ debugName: "#dateModelEffect" }] : []));
    startDate = computed(() => {
        const startDate = this.startDateModel();
        return startDate ? new Date(startDate) : null;
    }, { ...(ngDevMode ? { debugName: "startDate" } : {}), equal: equalDates });
    startTime = computed(() => {
        return getUtcTime(this.startDate());
    }, ...(ngDevMode ? [{ debugName: "startTime" }] : []));
    endTime = computed(() => {
        return getUtcTime(this.endDate());
    }, ...(ngDevMode ? [{ debugName: "endTime" }] : []));
    startDateChange = toObservable(this.startDate);
    endDateModel = model(null, { ...(ngDevMode ? { debugName: "endDateModel" } : {}), alias: 'endDate' });
    endDate = computed(() => {
        const endDate = this.endDateModel();
        return endDate ? new Date(endDate) : null;
    }, { ...(ngDevMode ? { debugName: "endDate" } : {}), equal: equalDates });
    endDateChange = toObservable(this.endDate);
    calendarDateInput = input(new Date(), { ...(ngDevMode ? { debugName: "calendarDateInput" } : {}), transform: dateTransform, alias: 'calendarDate' });
    calendarDate = linkedSignal(this.calendarDateInput, ...(ngDevMode ? [{ debugName: "calendarDate" }] : []));
    disabledDates = input([], ...(ngDevMode ? [{ debugName: "disabledDates" }] : []));
    /**
     * Set first day of week.
     * @return DaysOfWeek
     * @default 1 (Monday)
     */
    firstDayOfWeek = input(1, ...(ngDevMode ? [{ debugName: "firstDayOfWeek" }] : []));
    locale = input('default', ...(ngDevMode ? [{ debugName: "locale" }] : []));
    maxDate = input(null, ...(ngDevMode ? [{ debugName: "maxDate" }] : []));
    minDate = input(null, ...(ngDevMode ? [{ debugName: "minDate" }] : []));
    /**
     * Show calendar navigation.
     * @return boolean
     * @default true
     */
    navigation = input(true, { ...(ngDevMode ? { debugName: "navigation" } : {}), transform: booleanAttribute });
    /**
     * Allow range selection.
     * @return boolean
     * @default true
     */
    rangeInput = input(true, { ...(ngDevMode ? { debugName: "rangeInput" } : {}), transform: booleanAttribute, alias: 'range' });
    range = computed(() => {
        return this.rangeInput();
    }, ...(ngDevMode ? [{ debugName: "range" }] : []));
    dateFilter = input(...(ngDevMode ? [undefined, { debugName: "dateFilter" }] : []));
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    #disabledEffect = effect(() => {
        this.disabled() ? this.dateRangeGroup.disable() : this.dateRangeGroup.enable();
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    disabled = computed(() => {
        return this.#disabled() || this.disabledInput();
    }, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    #disabled = signal(false, ...(ngDevMode ? [{ debugName: "#disabled" }] : []));
    valueModel = model(null, { ...(ngDevMode ? { debugName: "valueModel" } : {}), alias: 'value' });
    #valueInputEffect = effect(() => {
        const value = this.valueModel() ?? { startDate: null, endDate: null };
        const range = this.range();
        untracked(() => {
            this.value =
                value instanceof Date
                    ? { startDate: value, endDate: null }
                    : { ...value };
        });
    }, ...(ngDevMode ? [{ debugName: "#valueInputEffect" }] : []));
    set value(value) {
        const newValue = value;
        if (JSON.stringify(this.#value()) !== JSON.stringify(newValue)) {
            this.#value.set(newValue);
            this.valueChange.emit(this.value);
            this.onChange(this.value);
            this.#cdr.markForCheck();
            if (this.closeOnSelect() && this.visible()) {
                const hasValue = this.range() ? !!(newValue.startDate && newValue.endDate) : !!newValue;
                this.visible.set(!hasValue);
            }
        }
    }
    get value() {
        return this.range() ? this.#value() : this.#value().startDate;
    }
    #value = signal({ startDate: null, endDate: null }, ...(ngDevMode ? [{ debugName: "#value" }] : []));
    /**
     * Set length or format of day name.
     * @return WeekdayFormatType
     * @default 'short'
     */
    weekdayFormat = input('short', ...(ngDevMode ? [{ debugName: "weekdayFormat" }] : []));
    /**
     * Optional popper Options object
     * @return Partial<Options>
     */
    popperjsOptions = input({ strategy: 'absolute' }, { ...(ngDevMode ? { debugName: "popperjsOptions" } : {}), alias: 'popperOptions' });
    /**
     * Set whether days in adjacent months shown before or after the current month are selectable. This only applies if the `showAdjacentDays` option is set to true.
     * @return boolean
     * @default false
     * @since 4.4.10
     */
    selectAdjacentDays = input(false, { ...(ngDevMode ? { debugName: "selectAdjacentDays" } : {}), transform: booleanAttribute });
    /**
     * Set whether to display dates in adjacent months (non-selectable) at the start and end of the current month.
     * @return boolean
     * @default true
     * @since 4.4.10
     */
    showAdjacentDays = input(true, { ...(ngDevMode ? { debugName: "showAdjacentDays" } : {}), transform: booleanAttribute });
    /**
     * Specify the type of date selection as day, week, month, or year.
     * @return 'day' | 'week' | 'month' | 'year'
     * @default 'day'
     * @since 5.0.0
     */
    selectionType = input('day', ...(ngDevMode ? [{ debugName: "selectionType" }] : []));
    /**
     * Set whether to display week numbers in the calendar.
     * @return boolean
     * @default false
     * @since 5.0.0
     */
    showWeekNumber = input(false, { ...(ngDevMode ? { debugName: "showWeekNumber" } : {}), transform: booleanAttribute });
    /**
     * Label displayed over week numbers in the calendar.
     * @return string
     * @default undefined
     * @since 5.0.0
     */
    weekNumbersLabel = input(...(ngDevMode ? [undefined, { debugName: "weekNumbersLabel" }] : []));
    valueChange = output();
    calendarCellHover = output();
    calendarDateChange = output();
    startDateElementRef = viewChild('startDateElementRef', ...(ngDevMode ? [{ debugName: "startDateElementRef" }] : []));
    endDateElementRef = viewChild('endDateElementRef', ...(ngDevMode ? [{ debugName: "endDateElementRef" }] : []));
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    templates = computed(() => {
        return this.contentTemplates().reduce((acc, child) => {
            acc[child.id] = child.templateRef;
            return acc;
        }, {});
    }, ...(ngDevMode ? [{ debugName: "templates" }] : []));
    dateChangeSubscriptions = [];
    subscribeDateChange(subscribe = true) {
        if (subscribe) {
            // todo
            // this.dateChangeSubscriptions.push(
            //   this.startDateChange.subscribe((next) => {
            //     // console.log('startDateChange', next);
            //     // this.startDate = next;
            //     // this.onChange(next);
            //   })
            // );
            // this.dateChangeSubscriptions.push(
            //   this.endDateChange.subscribe((next) => {
            //     // console.log('endDateChange', next);
            //     // this.endDate = next;
            //     // this.onChange(next);
            //   })
            // );
            return;
        }
        this.dateChangeSubscriptions.forEach((subscription) => {
            subscription?.unsubscribe();
        });
    }
    onBlur() {
        this.onTouched();
    }
    isMobile = true;
    customRanges;
    showRanges = false;
    startDateValue = computed(() => {
        const locale = this.locale() ?? 'default';
        return this.formatDate(this.startDate());
    }, ...(ngDevMode ? [{ debugName: "startDateValue" }] : []));
    endDateValue = computed(() => {
        const locale = this.locale() ?? 'default';
        return this.formatDate(this.endDate());
    }, ...(ngDevMode ? [{ debugName: "endDateValue" }] : []));
    get startDatePlaceholder() {
        const placeholder = this.placeholder();
        return Array.isArray(placeholder) ? placeholder[0] : placeholder;
    }
    get endDatePlaceholder() {
        const placeholder = this.placeholder();
        return Array.isArray(placeholder) ? placeholder[1] : placeholder;
    }
    // todo: validation
    // startDateInput = new FormControl({ value: this.startDateValue, disabled: this.disabled }, [
    //   Validators.required
    //   // Validators.minLength(8),
    //   // Validators.maxLength(10),
    //   // invalidDateStringValidator(this.locale)
    // ]);
    // endDateInput = new FormControl({ value: this.endDateValue, disabled: this.disabled }, [
    //   Validators.required
    //   // Validators.minLength(8),
    //   // Validators.maxLength(10),
    //   // invalidDateStringValidator(this.locale)
    // ]);
    dateRangeGroup = new FormGroup({
        startDateInput: new FormControl({ value: '', disabled: this.disabled() }, [Validators.required]),
        endDateInput: new FormControl({ value: '', disabled: this.disabled() }, [Validators.required])
    });
    inputStartHoverValue = signal(null, ...(ngDevMode ? [{ debugName: "inputStartHoverValue" }] : []));
    inputEndHoverValue = signal(null, ...(ngDevMode ? [{ debugName: "inputEndHoverValue" }] : []));
    datePickerClasses = computed(() => {
        const size = this.size();
        const valid = this.valid();
        const disabled = this.disabled();
        return {
            'date-picker': true,
            [`date-picker-${size}`]: size,
            disabled: disabled,
            'is-valid': valid === true,
            'is-invalid': valid === false,
            dropdown: false
        };
    }, ...(ngDevMode ? [{ debugName: "datePickerClasses" }] : []));
    formatDate(date) {
        const format = this.format();
        const inputDateFormat = this.inputDateFormat();
        const locale = this.locale();
        return !date || !isValidDate(date)
            ? ''
            : typeof inputDateFormat === 'function'
                ? inputDateFormat(date)
                : !!format && !!locale && locale !== 'default'
                    ? formatDate(date, format, locale)
                    : this.timepicker
                        ? date.toLocaleString(locale)
                        : date.toLocaleDateString(locale);
    }
    #breakpoints = {
        OneCalendarRanges: '(min-width: 480px)',
        TwoCalendars: '(max-width: 650px)',
        TwoCalendarsRanges: '(max-width: 810px)'
    };
    ngOnInit() {
        const ranges = this.ranges();
        this.customRanges = ranges ? Object.entries(ranges) : [];
        const breakpoints = this.#breakpoints;
        this.#breakpointObserver
            .observe([breakpoints.OneCalendarRanges, breakpoints.TwoCalendars, breakpoints.TwoCalendarsRanges])
            .pipe(takeUntilDestroyed(this.#destroyRef))
            .subscribe((result) => {
            if (result.matches) {
                this.isMobile =
                    (this.customRanges?.length > 0 && result.breakpoints[breakpoints.TwoCalendarsRanges]) ||
                        result.breakpoints[breakpoints.TwoCalendars];
                if (this.customRanges?.length > 0) {
                    this.showRanges = !result.breakpoints[breakpoints.OneCalendarRanges];
                }
            }
        });
        this.subscribeDateChange();
    }
    ngOnDestroy() {
        this.subscribeDateChange(false);
    }
    convertValueToDate(value) {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            return this.inputParse(value) ?? null;
        }
        return new Date(value);
    }
    inputParse(date) {
        const inputDateParse = this.inputDateParse();
        if (typeof inputDateParse === 'function') {
            const parsedDate = inputDateParse(date);
            if (isValidDate(parsedDate)) {
                return parsedDate;
            }
        }
        return getLocalDateFromString(date, this.locale(), this.timepicker);
    }
    getCustomRangeKey(customRange) {
        return customRange[0];
    }
    setCustomRange(customRange) {
        const key = this.getCustomRangeKey(customRange);
        this.handleStartDateChange(customRange[1][0]);
        this.handleEndDateChange(customRange[1][1]);
    }
    handleCalendarCellHover($event) {
        this.calendarCellHover.emit($event);
        if (!this.startDate()) {
            this.setInputValue(this.startDateElementRef(), $event);
            this.inputStartHoverValue.set($event);
            this.setInputValue(this.endDateElementRef(), null);
            this.inputEndHoverValue.set(null);
            return;
        }
        if (!this.endDate()) {
            this.setInputValue(this.endDateElementRef(), $event);
            this.inputEndHoverValue.set($event);
            return;
        }
    }
    handleCalendarDateChange($event) {
        if (this.calendarDate().getTime() !== $event.getTime()) {
            this.calendarDateChange.emit($event);
        }
    }
    handleStartDateChange(date) {
        const newDate = setUtcTime(date, this.startTime(), this.withTime());
        this.startDateModel.set(newDate ?? null);
        this.inputStartHoverValue.set(null);
    }
    handleEndDateChange(date) {
        if (!this.range()) {
            return;
        }
        const newDate = setUtcTime(date, this.endTime(), this.withTime());
        this.endDateModel.set(newDate ?? null);
        this.inputEndHoverValue.set(null);
    }
    handleClear($event) {
        this.handleStartDateChange(null);
        this.handleEndDateChange(null);
    }
    handleStartDateInputChange($event) {
        const inputValue = $event.target.value;
        const date = this.inputParse(inputValue);
        if (!date) {
            this.handleStartDateChange(null);
            this.handleEndDateChange(null);
            return;
        }
        if (date instanceof Date && date.getTime()) {
            const endDate = this.endDate();
            if (!(this.forbiddenDate(date) || (endDate && endDate < date))) {
                this.calendarDate.set(date);
                this.handleStartDateChange(date);
                return;
            }
        }
        this.handleStartDateChange(this.startDate() ?? null);
    }
    handleEndDateInputChange($event) {
        const inputValue = $event.target.value;
        const date = this.inputParse(inputValue);
        if (!date) {
            this.handleEndDateChange(null);
            return;
        }
        if (date instanceof Date && date.getTime()) {
            const startDate = this.startDate();
            if (!(this.forbiddenDate(date) || (startDate && startDate > date))) {
                this.calendarDate.set(date);
                this.handleEndDateChange(date);
            }
            else {
                this.handleEndDateChange(null);
            }
        }
        this.handleEndDateChange(this.endDate() ?? null);
    }
    forbiddenDate(date) {
        const dateFilter = this.dateFilter();
        return (isDateDisabled(date, this.minDate(), this.maxDate(), this.disabledDates()) ||
            (dateFilter ? !dateFilter(date) : false));
    }
    setInputValue = (elementRef, date) => {
        if (!elementRef) {
            return;
        }
        elementRef.nativeElement.value = this.formatDate(date);
        // elementRef.nativeElement.valueAsDate = date;
        return;
    };
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.#disabled.set(isDisabled);
    }
    writeValue(value) {
        if (!!value) {
            this.handleStartDateChange(this.range() ? value.startDate : value);
            this.handleEndDateChange(this.range() ? value.endDate : null);
        }
    }
    handleStartTimeChange(time) {
        this.startDateModel.set(time ?? this.startDate());
    }
    handleEndTimeChange(time) {
        if (!this.range()) {
            return;
        }
        this.endDateModel.set(time ?? this.endDate());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: DateRangePickerComponent, isStandalone: true, selector: "c-date-range-picker", inputs: { dayFormat: { classPropertyName: "dayFormat", publicName: "dayFormat", isSignal: true, isRequired: false, transformFunction: null }, calendars: { classPropertyName: "calendars", publicName: "calendars", isSignal: true, isRequired: false, transformFunction: null }, cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: true, isRequired: false, transformFunction: null }, closeOnSelect: { classPropertyName: "closeOnSelect", publicName: "closeOnSelect", isSignal: true, isRequired: false, transformFunction: null }, format: { classPropertyName: "format", publicName: "format", isSignal: true, isRequired: false, transformFunction: null }, indicator: { classPropertyName: "indicator", publicName: "indicator", isSignal: true, isRequired: false, transformFunction: null }, inputDateFormat: { classPropertyName: "inputDateFormat", publicName: "inputDateFormat", isSignal: true, isRequired: false, transformFunction: null }, inputDateParse: { classPropertyName: "inputDateParse", publicName: "inputDateParse", isSignal: true, isRequired: false, transformFunction: null }, inputReadOnlyInput: { classPropertyName: "inputReadOnlyInput", publicName: "inputReadOnly", isSignal: true, isRequired: false, transformFunction: null }, navYearFirst: { classPropertyName: "navYearFirst", publicName: "navYearFirst", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, ranges: { classPropertyName: "ranges", publicName: "ranges", isSignal: true, isRequired: false, transformFunction: null }, rangesButtonsColor: { classPropertyName: "rangesButtonsColor", publicName: "rangesButtonsColor", isSignal: true, isRequired: false, transformFunction: null }, rangesButtonsSize: { classPropertyName: "rangesButtonsSize", publicName: "rangesButtonsSize", isSignal: true, isRequired: false, transformFunction: null }, rangesButtonsVariant: { classPropertyName: "rangesButtonsVariant", publicName: "rangesButtonsVariant", isSignal: true, isRequired: false, transformFunction: null }, separator: { classPropertyName: "separator", publicName: "separator", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, withTime: { classPropertyName: "withTime", publicName: "withTime", isSignal: true, isRequired: false, transformFunction: null }, timepickerInput: { classPropertyName: "timepickerInput", publicName: "timepicker", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, startDateModel: { classPropertyName: "startDateModel", publicName: "startDate", isSignal: true, isRequired: false, transformFunction: null }, endDateModel: { classPropertyName: "endDateModel", publicName: "endDate", isSignal: true, isRequired: false, transformFunction: null }, calendarDateInput: { classPropertyName: "calendarDateInput", publicName: "calendarDate", isSignal: true, isRequired: false, transformFunction: null }, disabledDates: { classPropertyName: "disabledDates", publicName: "disabledDates", isSignal: true, isRequired: false, transformFunction: null }, firstDayOfWeek: { classPropertyName: "firstDayOfWeek", publicName: "firstDayOfWeek", isSignal: true, isRequired: false, transformFunction: null }, locale: { classPropertyName: "locale", publicName: "locale", isSignal: true, isRequired: false, transformFunction: null }, maxDate: { classPropertyName: "maxDate", publicName: "maxDate", isSignal: true, isRequired: false, transformFunction: null }, minDate: { classPropertyName: "minDate", publicName: "minDate", isSignal: true, isRequired: false, transformFunction: null }, navigation: { classPropertyName: "navigation", publicName: "navigation", isSignal: true, isRequired: false, transformFunction: null }, rangeInput: { classPropertyName: "rangeInput", publicName: "range", isSignal: true, isRequired: false, transformFunction: null }, dateFilter: { classPropertyName: "dateFilter", publicName: "dateFilter", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, valueModel: { classPropertyName: "valueModel", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, weekdayFormat: { classPropertyName: "weekdayFormat", publicName: "weekdayFormat", isSignal: true, isRequired: false, transformFunction: null }, popperjsOptions: { classPropertyName: "popperjsOptions", publicName: "popperOptions", isSignal: true, isRequired: false, transformFunction: null }, selectAdjacentDays: { classPropertyName: "selectAdjacentDays", publicName: "selectAdjacentDays", isSignal: true, isRequired: false, transformFunction: null }, showAdjacentDays: { classPropertyName: "showAdjacentDays", publicName: "showAdjacentDays", isSignal: true, isRequired: false, transformFunction: null }, selectionType: { classPropertyName: "selectionType", publicName: "selectionType", isSignal: true, isRequired: false, transformFunction: null }, showWeekNumber: { classPropertyName: "showWeekNumber", publicName: "showWeekNumber", isSignal: true, isRequired: false, transformFunction: null }, weekNumbersLabel: { classPropertyName: "weekNumbersLabel", publicName: "weekNumbersLabel", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { startDateModel: "startDateChange", endDateModel: "endDateChange", valueModel: "valueChange", valueChange: "valueChange", calendarCellHover: "calendarCellHover", calendarDateChange: "calendarDateChange" }, host: { listeners: { "focusout": "onBlur()" }, properties: { "class": "datePickerClasses()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => DateRangePickerComponent),
                multi: true
            }
        ], queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], viewQueries: [{ propertyName: "startDateElementRef", first: true, predicate: ["startDateElementRef"], descendants: true, isSignal: true }, { propertyName: "endDateElementRef", first: true, predicate: ["endDateElementRef"], descendants: true, isSignal: true }], exportAs: ["cDateRangePicker"], ngImport: i0, template: "<fieldset [formGroup]=\"dateRangeGroup\">\n  <c-dropdown #dropdown=\"cDropdown\" [(visible)]=\"visible\" [autoClose]=\"'outside'\" [class]=\"datePickerClasses()\"\n              [popperOptions]=\"popperjsOptions()\" class=\"date-picker\">\n    <div [caret]=\"false\"\n         [disabled]=\"disabled() ?? dropdown.visible\"\n         cDropdownToggle\n         class=\"date-picker-input-group\"\n    >\n      <input #startDateElementRef\n             (change)=\"handleStartDateInputChange($event)\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [class.hover]=\"!!inputStartHoverValue()\"\n             [placeholder]=\"startDatePlaceholder\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             [valid]=\"range() ? undefined : valid()\"\n             cFormControl\n             class=\"date-picker-input\"\n             formControlName=\"startDateInput\"\n             pattern=\"[1-9]*\"\n      >\n      @if (range() && separator()) {\n        <div class=\"date-picker-separator\"></div>\n      }\n      @if (range()) {\n        <input #endDateElementRef\n               (change)=\"handleEndDateInputChange($event)\"\n               [attr.tabindex]=\"disabled() ? -1 : 0\"\n               [class.hover]=\"!!inputEndHoverValue()\"\n               [placeholder]=\"endDatePlaceholder\"\n               [readonly]=\"inputReadOnly() ?? null\"\n               [valid]=\"valid() ?? undefined\"\n               cFormControl\n               class=\"date-picker-input\"\n               formControlName=\"endDateInput\"\n               pattern=\"[1-9]*\"\n        >\n      }\n      @if (indicator()) {\n        <div class=\"date-picker-indicator\"></div>\n      }\n      @if (cleaner() && startDateElementRef.value && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"date-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div cDropdownMenu class=\"date-picker-dropdown py-0\">\n      <div class=\"date-picker-body\">\n        @if (!showRanges) {\n          @if (ranges() && customRanges.length > 0) {\n            <div class=\"date-picker-ranges\">\n              @for (customRange of customRanges; track customRange) {\n                <button (click)=\"setCustomRange(customRange)\"\n                        [color]=\"rangesButtonsColor()\"\n                        [size]=\"rangesButtonsSize()\"\n                        [variant]=\"rangesButtonsVariant()\"\n                        cButton>\n                  {{ customRange | customRangeKey }}\n                </button>\n              }\n            </div>\n          }\n        }\n        <c-calendar\n          (calendarCellHover)=\"handleCalendarCellHover($event)\"\n          (calendarDateChange)=\"handleCalendarDateChange($event)\"\n          (endDateChange)=\"handleEndDateChange($event)\"\n          (startDateChange)=\"handleStartDateChange($event)\"\n          [calendarDate]=\"calendarDate()\"\n          [calendars]=\"isMobile ? 1 : calendars()\"\n          [dateFilter]=\"dateFilter()\"\n          [dayFormat]=\"dayFormat()\"\n          [disabledDates]=\"disabledDates()\"\n          [endDate]=\"endDate()\"\n          [firstDayOfWeek]=\"firstDayOfWeek()\"\n          [locale]=\"locale()\"\n          [maxDate]=\"maxDate()\"\n          [minDate]=\"minDate()\"\n          [navYearFirst]=\"navYearFirst()\"\n          [navigation]=\"navigation()\"\n          [range]=\"range()\"\n          [selectAdjacentDays]=\"selectAdjacentDays()\"\n          [selectionType]=\"selectionType()\"\n          [showAdjacentDays]=\"showAdjacentDays()\"\n          [showWeekNumber]=\"showWeekNumber()\"\n          [startDate]=\"startDate()\"\n          [weekNumbersLabel]=\"weekNumbersLabel()\"\n          [weekdayFormat]=\"weekdayFormat()\"\n          class=\"date-picker-calendars\"\n        />\n        @if (false && timepicker) {\n          <div class=\"date-picker-timepickers\">\n            <!-- todo-->\n            <c-time-picker\n              variant=\"select\"\n              [locale]=\"locale()\"\n              [disabled]=\"!startDate()\"\n              [time]=\"startDate() ?? undefined\"\n              (timeChange)=\"handleStartTimeChange($event)\"\n            />\n            @if (range()) {\n              <c-time-picker\n                variant=\"select\"\n                [locale]=\"locale()\"\n                [disabled]=\"!endDate()\"\n                [time]=\"endDate() ?? undefined\"\n                (timeChange)=\"handleEndTimeChange($event)\"\n              />\n            }\n          </div>\n        }\n      </div>\n      @let footer = templates()?.['datePickerFooter'];\n      @if (footer) {\n        <div class=\"date-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"footer; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n    <ng-content />\n  </c-dropdown>\n</fieldset>\n", styles: [".form-control.date-picker-input:focus{box-shadow:0 0}:host-context(.input-group) :host:not(:first-child) .date-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .date-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .date-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .date-picker-input-group{border-start-end-radius:var(--cui-date-picker-border-radius);border-end-end-radius:var(--cui-date-picker-border-radius)}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],[formArray],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: CalendarComponent, selector: "c-calendar", inputs: ["calendarDate", "calendars", "dayFormat", "disabledDates", "startDate", "endDate", "firstDayOfWeek", "locale", "maxDate", "minDate", "navigation", "navYearFirst", "range", "view", "weekdayFormat", "dateFilter", "selectAdjacentDays", "showAdjacentDays", "selectionType", "showWeekNumber", "weekNumbersLabel", "ariaNextMonthLabel", "ariaNextYearLabel", "ariaPrevMonthLabel", "ariaPrevYearLabel"], outputs: ["calendarDateChange", "endDateChange", "startDateChange", "viewChange", "calendarCellHover"], exportAs: ["cCalendar"] }, { kind: "component", type: TimePickerComponent, selector: "c-time-picker", inputs: ["cleaner", "dateTimeFormatOptions", "disabled", "filterHours", "filterMinutes", "filterSeconds", "indicator", "inputReadOnly", "locale", "placeholder", "seconds", "size", "time", "variant", "valid", "visible"], outputs: ["timeChange"], exportAs: ["cTimePicker"] }, { kind: "directive", type: ButtonDirective, selector: "[cButton]", inputs: ["active", "color", "disabled", "shape", "size", "tabindex", "type", "variant"], exportAs: ["cButton"] }, { kind: "component", type: DropdownComponent, selector: "c-dropdown", inputs: ["alignment", "autoClose", "direction", "placement", "popper", "popperOptions", "variant", "visible"], outputs: ["visibleChange"], exportAs: ["cDropdown"] }, { kind: "directive", type: DropdownToggleDirective, selector: "[cDropdownToggle]", inputs: ["dropdownComponent", "disabled", "caret", "split"], exportAs: ["cDropdownToggle"] }, { kind: "directive", type: DropdownMenuDirective, selector: "[cDropdownMenu]", inputs: ["alignment", "visible"], exportAs: ["cDropdownMenu"] }, { kind: "directive", type: FormControlDirective, selector: "input[cFormControl], textarea[cFormControl]", inputs: ["sizing", "valid", "type", "plaintext"] }, { kind: "pipe", type: CustomRangeKeyPipe, name: "customRangeKey" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-date-range-picker', exportAs: 'cDateRangePicker', imports: [
                        NgTemplateOutlet,
                        ReactiveFormsModule,
                        CalendarComponent,
                        TimePickerComponent,
                        ButtonDirective,
                        DropdownComponent,
                        DropdownToggleDirective,
                        DropdownMenuDirective,
                        FormControlDirective,
                        CustomRangeKeyPipe
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DateRangePickerComponent),
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        '[class]': 'datePickerClasses()',
                        '(focusout)': 'onBlur()'
                    }, template: "<fieldset [formGroup]=\"dateRangeGroup\">\n  <c-dropdown #dropdown=\"cDropdown\" [(visible)]=\"visible\" [autoClose]=\"'outside'\" [class]=\"datePickerClasses()\"\n              [popperOptions]=\"popperjsOptions()\" class=\"date-picker\">\n    <div [caret]=\"false\"\n         [disabled]=\"disabled() ?? dropdown.visible\"\n         cDropdownToggle\n         class=\"date-picker-input-group\"\n    >\n      <input #startDateElementRef\n             (change)=\"handleStartDateInputChange($event)\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [class.hover]=\"!!inputStartHoverValue()\"\n             [placeholder]=\"startDatePlaceholder\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             [valid]=\"range() ? undefined : valid()\"\n             cFormControl\n             class=\"date-picker-input\"\n             formControlName=\"startDateInput\"\n             pattern=\"[1-9]*\"\n      >\n      @if (range() && separator()) {\n        <div class=\"date-picker-separator\"></div>\n      }\n      @if (range()) {\n        <input #endDateElementRef\n               (change)=\"handleEndDateInputChange($event)\"\n               [attr.tabindex]=\"disabled() ? -1 : 0\"\n               [class.hover]=\"!!inputEndHoverValue()\"\n               [placeholder]=\"endDatePlaceholder\"\n               [readonly]=\"inputReadOnly() ?? null\"\n               [valid]=\"valid() ?? undefined\"\n               cFormControl\n               class=\"date-picker-input\"\n               formControlName=\"endDateInput\"\n               pattern=\"[1-9]*\"\n        >\n      }\n      @if (indicator()) {\n        <div class=\"date-picker-indicator\"></div>\n      }\n      @if (cleaner() && startDateElementRef.value && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"date-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div cDropdownMenu class=\"date-picker-dropdown py-0\">\n      <div class=\"date-picker-body\">\n        @if (!showRanges) {\n          @if (ranges() && customRanges.length > 0) {\n            <div class=\"date-picker-ranges\">\n              @for (customRange of customRanges; track customRange) {\n                <button (click)=\"setCustomRange(customRange)\"\n                        [color]=\"rangesButtonsColor()\"\n                        [size]=\"rangesButtonsSize()\"\n                        [variant]=\"rangesButtonsVariant()\"\n                        cButton>\n                  {{ customRange | customRangeKey }}\n                </button>\n              }\n            </div>\n          }\n        }\n        <c-calendar\n          (calendarCellHover)=\"handleCalendarCellHover($event)\"\n          (calendarDateChange)=\"handleCalendarDateChange($event)\"\n          (endDateChange)=\"handleEndDateChange($event)\"\n          (startDateChange)=\"handleStartDateChange($event)\"\n          [calendarDate]=\"calendarDate()\"\n          [calendars]=\"isMobile ? 1 : calendars()\"\n          [dateFilter]=\"dateFilter()\"\n          [dayFormat]=\"dayFormat()\"\n          [disabledDates]=\"disabledDates()\"\n          [endDate]=\"endDate()\"\n          [firstDayOfWeek]=\"firstDayOfWeek()\"\n          [locale]=\"locale()\"\n          [maxDate]=\"maxDate()\"\n          [minDate]=\"minDate()\"\n          [navYearFirst]=\"navYearFirst()\"\n          [navigation]=\"navigation()\"\n          [range]=\"range()\"\n          [selectAdjacentDays]=\"selectAdjacentDays()\"\n          [selectionType]=\"selectionType()\"\n          [showAdjacentDays]=\"showAdjacentDays()\"\n          [showWeekNumber]=\"showWeekNumber()\"\n          [startDate]=\"startDate()\"\n          [weekNumbersLabel]=\"weekNumbersLabel()\"\n          [weekdayFormat]=\"weekdayFormat()\"\n          class=\"date-picker-calendars\"\n        />\n        @if (false && timepicker) {\n          <div class=\"date-picker-timepickers\">\n            <!-- todo-->\n            <c-time-picker\n              variant=\"select\"\n              [locale]=\"locale()\"\n              [disabled]=\"!startDate()\"\n              [time]=\"startDate() ?? undefined\"\n              (timeChange)=\"handleStartTimeChange($event)\"\n            />\n            @if (range()) {\n              <c-time-picker\n                variant=\"select\"\n                [locale]=\"locale()\"\n                [disabled]=\"!endDate()\"\n                [time]=\"endDate() ?? undefined\"\n                (timeChange)=\"handleEndTimeChange($event)\"\n              />\n            }\n          </div>\n        }\n      </div>\n      @let footer = templates()?.['datePickerFooter'];\n      @if (footer) {\n        <div class=\"date-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"footer; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n    <ng-content />\n  </c-dropdown>\n</fieldset>\n", styles: [".form-control.date-picker-input:focus{box-shadow:0 0}:host-context(.input-group) :host:not(:first-child) .date-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .date-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .date-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .date-picker-input-group{border-start-end-radius:var(--cui-date-picker-border-radius);border-end-end-radius:var(--cui-date-picker-border-radius)}\n"] }]
        }], propDecorators: { dayFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "dayFormat", required: false }] }], calendars: [{ type: i0.Input, args: [{ isSignal: true, alias: "calendars", required: false }] }], cleaner: [{ type: i0.Input, args: [{ isSignal: true, alias: "cleaner", required: false }] }], closeOnSelect: [{ type: i0.Input, args: [{ isSignal: true, alias: "closeOnSelect", required: false }] }], format: [{ type: i0.Input, args: [{ isSignal: true, alias: "format", required: false }] }], indicator: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicator", required: false }] }], inputDateFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "inputDateFormat", required: false }] }], inputDateParse: [{ type: i0.Input, args: [{ isSignal: true, alias: "inputDateParse", required: false }] }], inputReadOnlyInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "inputReadOnly", required: false }] }], navYearFirst: [{ type: i0.Input, args: [{ isSignal: true, alias: "navYearFirst", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], ranges: [{ type: i0.Input, args: [{ isSignal: true, alias: "ranges", required: false }] }], rangesButtonsColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "rangesButtonsColor", required: false }] }], rangesButtonsSize: [{ type: i0.Input, args: [{ isSignal: true, alias: "rangesButtonsSize", required: false }] }], rangesButtonsVariant: [{ type: i0.Input, args: [{ isSignal: true, alias: "rangesButtonsVariant", required: false }] }], separator: [{ type: i0.Input, args: [{ isSignal: true, alias: "separator", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], withTime: [{ type: i0.Input, args: [{ isSignal: true, alias: "withTime", required: false }] }], timepickerInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "timepicker", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], startDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "startDate", required: false }] }, { type: i0.Output, args: ["startDateChange"] }], endDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "endDate", required: false }] }, { type: i0.Output, args: ["endDateChange"] }], calendarDateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "calendarDate", required: false }] }], disabledDates: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabledDates", required: false }] }], firstDayOfWeek: [{ type: i0.Input, args: [{ isSignal: true, alias: "firstDayOfWeek", required: false }] }], locale: [{ type: i0.Input, args: [{ isSignal: true, alias: "locale", required: false }] }], maxDate: [{ type: i0.Input, args: [{ isSignal: true, alias: "maxDate", required: false }] }], minDate: [{ type: i0.Input, args: [{ isSignal: true, alias: "minDate", required: false }] }], navigation: [{ type: i0.Input, args: [{ isSignal: true, alias: "navigation", required: false }] }], rangeInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "range", required: false }] }], dateFilter: [{ type: i0.Input, args: [{ isSignal: true, alias: "dateFilter", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], valueModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }, { type: i0.Output, args: ["valueChange"] }], weekdayFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "weekdayFormat", required: false }] }], popperjsOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "popperOptions", required: false }] }], selectAdjacentDays: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectAdjacentDays", required: false }] }], showAdjacentDays: [{ type: i0.Input, args: [{ isSignal: true, alias: "showAdjacentDays", required: false }] }], selectionType: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectionType", required: false }] }], showWeekNumber: [{ type: i0.Input, args: [{ isSignal: true, alias: "showWeekNumber", required: false }] }], weekNumbersLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "weekNumbersLabel", required: false }] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], calendarCellHover: [{ type: i0.Output, args: ["calendarCellHover"] }], calendarDateChange: [{ type: i0.Output, args: ["calendarDateChange"] }], startDateElementRef: [{ type: i0.ViewChild, args: ['startDateElementRef', { isSignal: true }] }], endDateElementRef: [{ type: i0.ViewChild, args: ['endDateElementRef', { isSignal: true }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class DateRangePickerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerModule, imports: [DateRangePickerComponent], exports: [DateRangePickerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerModule, imports: [DateRangePickerComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DateRangePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DateRangePickerComponent],
                    exports: [DateRangePickerComponent]
                }]
        }] });

class DatePickerComponent extends DateRangePickerComponent {
    dateChange = output();
    calendars = input(1, { ...(ngDevMode ? { debugName: "calendars" } : {}), transform: numberAttribute });
    placeholder = input('Select date', ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    range = signal(false, ...(ngDevMode ? [{ debugName: "range" }] : []));
    endDate = signal(null, ...(ngDevMode ? [{ debugName: "endDate" }] : []));
    startDateModel = model(null, { ...(ngDevMode ? { debugName: "startDateModel" } : {}), alias: 'date' });
    get date() {
        return this.startDate();
    }
    writeValue(value) {
        this.startDateModel.set(value);
        super.writeValue(value);
    }
    subscribeDateChange(subscribe = true) {
        if (subscribe) {
            this.dateChangeSubscriptions.push(this.startDateChange.subscribe((next) => {
                if (this.startDateModel()?.getTime() !== next?.getTime()) {
                    this.startDateModel.set(next);
                    this.onChange(next);
                    this.dateChange.emit(next);
                }
            }));
            return;
        }
        this.dateChangeSubscriptions.forEach((subscription) => {
            subscription?.unsubscribe();
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DatePickerComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: DatePickerComponent, isStandalone: true, selector: "c-date-picker", inputs: { calendars: { classPropertyName: "calendars", publicName: "calendars", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, startDateModel: { classPropertyName: "startDateModel", publicName: "date", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { dateChange: "dateChange", startDateModel: "dateChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => DatePickerComponent),
                multi: true
            }
        ], exportAs: ["cDatePicker"], usesInheritance: true, ngImport: i0, template: "<fieldset [formGroup]=\"dateRangeGroup\">\n  <c-dropdown #dropdown=\"cDropdown\" [(visible)]=\"visible\" [autoClose]=\"'outside'\" [class]=\"datePickerClasses()\"\n              [popperOptions]=\"popperjsOptions()\" class=\"date-picker\">\n    <div [caret]=\"false\"\n         [disabled]=\"disabled() ?? dropdown.visible\"\n         cDropdownToggle\n         class=\"date-picker-input-group\"\n    >\n      <input #startDateElementRef\n             (change)=\"handleStartDateInputChange($event)\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [class.hover]=\"!!inputStartHoverValue()\"\n             [placeholder]=\"startDatePlaceholder\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             [valid]=\"range() ? undefined : valid()\"\n             cFormControl\n             class=\"date-picker-input\"\n             formControlName=\"startDateInput\"\n             pattern=\"[1-9]*\"\n      >\n      @if (range() && separator()) {\n        <div class=\"date-picker-separator\"></div>\n      }\n      @if (range()) {\n        <input #endDateElementRef\n               (change)=\"handleEndDateInputChange($event)\"\n               [attr.tabindex]=\"disabled() ? -1 : 0\"\n               [class.hover]=\"!!inputEndHoverValue()\"\n               [placeholder]=\"endDatePlaceholder\"\n               [readonly]=\"inputReadOnly() ?? null\"\n               [valid]=\"valid() ?? undefined\"\n               cFormControl\n               class=\"date-picker-input\"\n               formControlName=\"endDateInput\"\n               pattern=\"[1-9]*\"\n        >\n      }\n      @if (indicator()) {\n        <div class=\"date-picker-indicator\"></div>\n      }\n      @if (cleaner() && startDateElementRef.value && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"date-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div cDropdownMenu class=\"date-picker-dropdown py-0\">\n      <div class=\"date-picker-body\">\n        @if (!showRanges) {\n          @if (ranges() && customRanges.length > 0) {\n            <div class=\"date-picker-ranges\">\n              @for (customRange of customRanges; track customRange) {\n                <button (click)=\"setCustomRange(customRange)\"\n                        [color]=\"rangesButtonsColor()\"\n                        [size]=\"rangesButtonsSize()\"\n                        [variant]=\"rangesButtonsVariant()\"\n                        cButton>\n                  {{ customRange | customRangeKey }}\n                </button>\n              }\n            </div>\n          }\n        }\n        <c-calendar\n          (calendarCellHover)=\"handleCalendarCellHover($event)\"\n          (calendarDateChange)=\"handleCalendarDateChange($event)\"\n          (endDateChange)=\"handleEndDateChange($event)\"\n          (startDateChange)=\"handleStartDateChange($event)\"\n          [calendarDate]=\"calendarDate()\"\n          [calendars]=\"isMobile ? 1 : calendars()\"\n          [dateFilter]=\"dateFilter()\"\n          [dayFormat]=\"dayFormat()\"\n          [disabledDates]=\"disabledDates()\"\n          [endDate]=\"endDate()\"\n          [firstDayOfWeek]=\"firstDayOfWeek()\"\n          [locale]=\"locale()\"\n          [maxDate]=\"maxDate()\"\n          [minDate]=\"minDate()\"\n          [navYearFirst]=\"navYearFirst()\"\n          [navigation]=\"navigation()\"\n          [range]=\"range()\"\n          [selectAdjacentDays]=\"selectAdjacentDays()\"\n          [selectionType]=\"selectionType()\"\n          [showAdjacentDays]=\"showAdjacentDays()\"\n          [showWeekNumber]=\"showWeekNumber()\"\n          [startDate]=\"startDate()\"\n          [weekNumbersLabel]=\"weekNumbersLabel()\"\n          [weekdayFormat]=\"weekdayFormat()\"\n          class=\"date-picker-calendars\"\n        />\n        @if (false && timepicker) {\n          <div class=\"date-picker-timepickers\">\n            <!-- todo-->\n            <c-time-picker\n              variant=\"select\"\n              [locale]=\"locale()\"\n              [disabled]=\"!startDate()\"\n              [time]=\"startDate() ?? undefined\"\n              (timeChange)=\"handleStartTimeChange($event)\"\n            />\n            @if (range()) {\n              <c-time-picker\n                variant=\"select\"\n                [locale]=\"locale()\"\n                [disabled]=\"!endDate()\"\n                [time]=\"endDate() ?? undefined\"\n                (timeChange)=\"handleEndTimeChange($event)\"\n              />\n            }\n          </div>\n        }\n      </div>\n      @let footer = templates()?.['datePickerFooter'];\n      @if (footer) {\n        <div class=\"date-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"footer; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n    <ng-content />\n  </c-dropdown>\n</fieldset>\n", styles: [".form-control.date-picker-input:focus{box-shadow:0 0}:host-context(.input-group) :host:not(:first-child) .date-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .date-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .date-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .date-picker-input-group{border-start-end-radius:var(--cui-date-picker-border-radius);border-end-end-radius:var(--cui-date-picker-border-radius)}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],[formArray],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i1.PatternValidator, selector: "[pattern][formControlName],[pattern][formControl],[pattern][ngModel]", inputs: ["pattern"] }, { kind: "directive", type: i1.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: CalendarComponent, selector: "c-calendar", inputs: ["calendarDate", "calendars", "dayFormat", "disabledDates", "startDate", "endDate", "firstDayOfWeek", "locale", "maxDate", "minDate", "navigation", "navYearFirst", "range", "view", "weekdayFormat", "dateFilter", "selectAdjacentDays", "showAdjacentDays", "selectionType", "showWeekNumber", "weekNumbersLabel", "ariaNextMonthLabel", "ariaNextYearLabel", "ariaPrevMonthLabel", "ariaPrevYearLabel"], outputs: ["calendarDateChange", "endDateChange", "startDateChange", "viewChange", "calendarCellHover"], exportAs: ["cCalendar"] }, { kind: "component", type: TimePickerComponent, selector: "c-time-picker", inputs: ["cleaner", "dateTimeFormatOptions", "disabled", "filterHours", "filterMinutes", "filterSeconds", "indicator", "inputReadOnly", "locale", "placeholder", "seconds", "size", "time", "variant", "valid", "visible"], outputs: ["timeChange"], exportAs: ["cTimePicker"] }, { kind: "directive", type: ButtonDirective, selector: "[cButton]", inputs: ["active", "color", "disabled", "shape", "size", "tabindex", "type", "variant"], exportAs: ["cButton"] }, { kind: "component", type: DropdownComponent, selector: "c-dropdown", inputs: ["alignment", "autoClose", "direction", "placement", "popper", "popperOptions", "variant", "visible"], outputs: ["visibleChange"], exportAs: ["cDropdown"] }, { kind: "directive", type: DropdownToggleDirective, selector: "[cDropdownToggle]", inputs: ["dropdownComponent", "disabled", "caret", "split"], exportAs: ["cDropdownToggle"] }, { kind: "directive", type: DropdownMenuDirective, selector: "[cDropdownMenu]", inputs: ["alignment", "visible"], exportAs: ["cDropdownMenu"] }, { kind: "directive", type: FormControlDirective, selector: "input[cFormControl], textarea[cFormControl]", inputs: ["sizing", "valid", "type", "plaintext"] }, { kind: "pipe", type: CustomRangeKeyPipe, name: "customRangeKey" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DatePickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-date-picker', exportAs: 'cDatePicker', imports: [
                        NgTemplateOutlet,
                        ReactiveFormsModule,
                        CalendarComponent,
                        TimePickerComponent,
                        ButtonDirective,
                        DropdownComponent,
                        DropdownToggleDirective,
                        DropdownMenuDirective,
                        FormControlDirective,
                        CustomRangeKeyPipe
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => DatePickerComponent),
                            multi: true
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<fieldset [formGroup]=\"dateRangeGroup\">\n  <c-dropdown #dropdown=\"cDropdown\" [(visible)]=\"visible\" [autoClose]=\"'outside'\" [class]=\"datePickerClasses()\"\n              [popperOptions]=\"popperjsOptions()\" class=\"date-picker\">\n    <div [caret]=\"false\"\n         [disabled]=\"disabled() ?? dropdown.visible\"\n         cDropdownToggle\n         class=\"date-picker-input-group\"\n    >\n      <input #startDateElementRef\n             (change)=\"handleStartDateInputChange($event)\"\n             [attr.tabindex]=\"disabled() ? -1 : 0\"\n             [class.hover]=\"!!inputStartHoverValue()\"\n             [placeholder]=\"startDatePlaceholder\"\n             [readonly]=\"inputReadOnly() ?? null\"\n             [valid]=\"range() ? undefined : valid()\"\n             cFormControl\n             class=\"date-picker-input\"\n             formControlName=\"startDateInput\"\n             pattern=\"[1-9]*\"\n      >\n      @if (range() && separator()) {\n        <div class=\"date-picker-separator\"></div>\n      }\n      @if (range()) {\n        <input #endDateElementRef\n               (change)=\"handleEndDateInputChange($event)\"\n               [attr.tabindex]=\"disabled() ? -1 : 0\"\n               [class.hover]=\"!!inputEndHoverValue()\"\n               [placeholder]=\"endDatePlaceholder\"\n               [readonly]=\"inputReadOnly() ?? null\"\n               [valid]=\"valid() ?? undefined\"\n               cFormControl\n               class=\"date-picker-input\"\n               formControlName=\"endDateInput\"\n               pattern=\"[1-9]*\"\n        >\n      }\n      @if (indicator()) {\n        <div class=\"date-picker-indicator\"></div>\n      }\n      @if (cleaner() && startDateElementRef.value && !disabled()) {\n        <div (click)=\"!disabled() && handleClear($event)\" class=\"date-picker-cleaner\" role=\"button\"></div>\n      }\n    </div>\n    <div cDropdownMenu class=\"date-picker-dropdown py-0\">\n      <div class=\"date-picker-body\">\n        @if (!showRanges) {\n          @if (ranges() && customRanges.length > 0) {\n            <div class=\"date-picker-ranges\">\n              @for (customRange of customRanges; track customRange) {\n                <button (click)=\"setCustomRange(customRange)\"\n                        [color]=\"rangesButtonsColor()\"\n                        [size]=\"rangesButtonsSize()\"\n                        [variant]=\"rangesButtonsVariant()\"\n                        cButton>\n                  {{ customRange | customRangeKey }}\n                </button>\n              }\n            </div>\n          }\n        }\n        <c-calendar\n          (calendarCellHover)=\"handleCalendarCellHover($event)\"\n          (calendarDateChange)=\"handleCalendarDateChange($event)\"\n          (endDateChange)=\"handleEndDateChange($event)\"\n          (startDateChange)=\"handleStartDateChange($event)\"\n          [calendarDate]=\"calendarDate()\"\n          [calendars]=\"isMobile ? 1 : calendars()\"\n          [dateFilter]=\"dateFilter()\"\n          [dayFormat]=\"dayFormat()\"\n          [disabledDates]=\"disabledDates()\"\n          [endDate]=\"endDate()\"\n          [firstDayOfWeek]=\"firstDayOfWeek()\"\n          [locale]=\"locale()\"\n          [maxDate]=\"maxDate()\"\n          [minDate]=\"minDate()\"\n          [navYearFirst]=\"navYearFirst()\"\n          [navigation]=\"navigation()\"\n          [range]=\"range()\"\n          [selectAdjacentDays]=\"selectAdjacentDays()\"\n          [selectionType]=\"selectionType()\"\n          [showAdjacentDays]=\"showAdjacentDays()\"\n          [showWeekNumber]=\"showWeekNumber()\"\n          [startDate]=\"startDate()\"\n          [weekNumbersLabel]=\"weekNumbersLabel()\"\n          [weekdayFormat]=\"weekdayFormat()\"\n          class=\"date-picker-calendars\"\n        />\n        @if (false && timepicker) {\n          <div class=\"date-picker-timepickers\">\n            <!-- todo-->\n            <c-time-picker\n              variant=\"select\"\n              [locale]=\"locale()\"\n              [disabled]=\"!startDate()\"\n              [time]=\"startDate() ?? undefined\"\n              (timeChange)=\"handleStartTimeChange($event)\"\n            />\n            @if (range()) {\n              <c-time-picker\n                variant=\"select\"\n                [locale]=\"locale()\"\n                [disabled]=\"!endDate()\"\n                [time]=\"endDate() ?? undefined\"\n                (timeChange)=\"handleEndTimeChange($event)\"\n              />\n            }\n          </div>\n        }\n      </div>\n      @let footer = templates()?.['datePickerFooter'];\n      @if (footer) {\n        <div class=\"date-picker-footer\">\n          <ng-container *ngTemplateOutlet=\"footer; context: {$implicit: dropdown}\" />\n        </div>\n      }\n    </div>\n    <ng-content />\n  </c-dropdown>\n</fieldset>\n", styles: [".form-control.date-picker-input:focus{box-shadow:0 0}:host-context(.input-group) :host:not(:first-child) .date-picker-input-group{border-start-start-radius:0;border-end-start-radius:0}:host-context(.input-group) :host:not(:last-child) .date-picker-input-group{border-start-end-radius:0;border-end-end-radius:0}:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:not(:last-child):has(+[class*=valid-],+[class*=invalid-]) .date-picker-input-group,:host-context(.input-group.has-validation,.input-group:not(.has-validation)) :host:last-child .date-picker-input-group{border-start-end-radius:var(--cui-date-picker-border-radius);border-end-end-radius:var(--cui-date-picker-border-radius)}\n"] }]
        }], propDecorators: { dateChange: [{ type: i0.Output, args: ["dateChange"] }], calendars: [{ type: i0.Input, args: [{ isSignal: true, alias: "calendars", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], startDateModel: [{ type: i0.Input, args: [{ isSignal: true, alias: "date", required: false }] }, { type: i0.Output, args: ["dateChange"] }] } });

class DatePickerModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DatePickerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: DatePickerModule, imports: [DatePickerComponent], exports: [DatePickerComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DatePickerModule, imports: [DatePickerComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: DatePickerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [DatePickerComponent],
                    exports: [DatePickerComponent]
                }]
        }] });

class FooterComponent {
    /**
     * Place footer in non-static positions. [docs]
     * @type Positions
     */
    position = input(...(ngDevMode ? [undefined, { debugName: "position" }] : []));
    /**
     * Default role for footer. [docs]
     * @return string
     * @default 'contentinfo'
     */
    role = input('contentinfo', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        const position = this.position();
        return {
            footer: true,
            [`footer-${position}`]: !!position
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: FooterComponent, isStandalone: true, selector: "c-footer, [cFooter]", inputs: { position: { classPropertyName: "position", publicName: "position", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.role": "role()" }, classAttribute: "footer" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-footer, [cFooter]',
                    template: '<ng-content />',
                    host: {
                        class: 'footer',
                        '[class]': 'hostClasses()',
                        '[attr.role]': 'role()'
                    }
                }]
        }], propDecorators: { position: [{ type: i0.Input, args: [{ isSignal: true, alias: "position", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class FooterModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FooterModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: FooterModule, imports: [FooterComponent], exports: [FooterComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FooterModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FooterModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [FooterComponent],
                    exports: [FooterComponent]
                }]
        }] });

class ContainerComponent {
    /**
     * Set container 100% wide until a breakpoint.
     */
    breakpoint = input('', ...(ngDevMode ? [{ debugName: "breakpoint" }] : []));
    /**
     * Set container 100% wide, spanning the entire width of the viewport.
     * @return boolean
     */
    fluid = input(false, { ...(ngDevMode ? { debugName: "fluid" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        const breakpoint = this.breakpoint();
        const fluid = this.fluid();
        return {
            container: !fluid && !breakpoint,
            'container-fluid': !!fluid,
            [`container-${breakpoint}`]: !!breakpoint
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ContainerComponent, isStandalone: true, selector: "c-container, [cContainer]", inputs: { breakpoint: { classPropertyName: "breakpoint", publicName: "breakpoint", isSignal: true, isRequired: false, transformFunction: null }, fluid: { classPropertyName: "fluid", publicName: "fluid", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-container, [cContainer]', template: '<ng-content />', host: { '[class]': 'hostClasses()' }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { breakpoint: [{ type: i0.Input, args: [{ isSignal: true, alias: "breakpoint", required: false }] }], fluid: [{ type: i0.Input, args: [{ isSignal: true, alias: "fluid", required: false }] }] } });

class ColDirective {
    static ngAcceptInputType_cCol;
    static ngAcceptInputType_xs;
    static ngAcceptInputType_sm;
    static ngAcceptInputType_md;
    static ngAcceptInputType_lg;
    static ngAcceptInputType_xl;
    static ngAcceptInputType_xxl;
    /**
     * The number of columns/offset/order on extra small devices (<576px).
     * @return { 'auto' | number |  boolean }
     */
    cCol = input(false, { ...(ngDevMode ? { debugName: "cCol" } : {}), transform: this.coerceInput });
    xs = input(false, { ...(ngDevMode ? { debugName: "xs" } : {}), transform: this.coerceInput });
    /**
     * The number of columns/offset/order on small devices (<768px).
     * @return { 'auto' | number |  boolean }
     */
    sm = input(false, { ...(ngDevMode ? { debugName: "sm" } : {}), transform: this.coerceInput });
    /**
     * The number of columns/offset/order on medium devices (<992px).
     * @return { 'auto' | number |  boolean }
     */
    md = input(false, { ...(ngDevMode ? { debugName: "md" } : {}), transform: this.coerceInput });
    /**
     * The number of columns/offset/order on large devices (<1200px).
     * @return { 'auto' | number |  boolean }
     */
    lg = input(false, { ...(ngDevMode ? { debugName: "lg" } : {}), transform: this.coerceInput });
    /**
     * The number of columns/offset/order on X-Large devices (<1400px).
     * @return { 'auto' | number |  boolean }
     */
    xl = input(false, { ...(ngDevMode ? { debugName: "xl" } : {}), transform: this.coerceInput });
    /**
     * The number of columns/offset/order on XX-Large devices (≥1400px).
     * @return { 'auto' | number |  boolean }
     */
    xxl = input(false, { ...(ngDevMode ? { debugName: "xxl" } : {}), transform: this.coerceInput });
    breakpoints = computed(() => {
        return {
            xs: this.xs() || this.cCol(),
            sm: this.sm(),
            md: this.md(),
            lg: this.lg(),
            xl: this.xl(),
            xxl: this.xxl()
        };
    }, ...(ngDevMode ? [{ debugName: "breakpoints" }] : []));
    offset = input(...(ngDevMode ? [undefined, { debugName: "offset" }] : []));
    order = input(...(ngDevMode ? [undefined, { debugName: "order" }] : []));
    hostClasses = computed(() => {
        const classes = {
            col: true
        };
        const breakpoints = this.breakpoints();
        const offsetInput = this.offset();
        const orderInput = this.order();
        Object.keys(BreakpointInfix).forEach((breakpoint) => {
            const value = breakpoints[breakpoint];
            const infix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
            classes[`col${infix}`] = value === true;
            classes[`col${infix}-${value}`] = typeof value === 'number' || typeof value === 'string';
        });
        if (typeof offsetInput === 'object') {
            const offset = { ...offsetInput };
            Object.entries(offset).forEach((entry) => {
                const [breakpoint, value] = [...entry];
                const infix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
                classes[`offset${infix}-${value}`] = value >= 0 && value <= 11;
            });
        }
        else {
            const offset = numberAttribute(offsetInput);
            classes[`offset-${offset}`] = typeof offset === 'number' && offset > 0 && offset <= 11;
        }
        if (typeof orderInput === 'object') {
            const order = { ...orderInput };
            Object.entries(order).forEach((entry) => {
                const [breakpoint, value] = [...entry];
                const infix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
                classes[`order${infix}-${value}`] = !!value;
            });
        }
        else {
            const order = orderInput;
            classes[`order-${order}`] = !!order;
        }
        // if there is no 'col' class, add one
        classes['col'] =
            !Object.entries(classes).filter((i) => i[0].startsWith('col-') && i[1]).length || breakpoints['xs'] === true;
        return classes;
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    coerceInput(value) {
        if (value === 'auto') {
            return value;
        }
        if (value === '' || value === undefined || value === null) {
            return booleanAttribute(value);
        }
        if (typeof value === 'boolean') {
            return value;
        }
        return numberAttribute(value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ColDirective, isStandalone: true, selector: "[cCol]", inputs: { cCol: { classPropertyName: "cCol", publicName: "cCol", isSignal: true, isRequired: false, transformFunction: null }, xs: { classPropertyName: "xs", publicName: "xs", isSignal: true, isRequired: false, transformFunction: null }, sm: { classPropertyName: "sm", publicName: "sm", isSignal: true, isRequired: false, transformFunction: null }, md: { classPropertyName: "md", publicName: "md", isSignal: true, isRequired: false, transformFunction: null }, lg: { classPropertyName: "lg", publicName: "lg", isSignal: true, isRequired: false, transformFunction: null }, xl: { classPropertyName: "xl", publicName: "xl", isSignal: true, isRequired: false, transformFunction: null }, xxl: { classPropertyName: "xxl", publicName: "xxl", isSignal: true, isRequired: false, transformFunction: null }, offset: { classPropertyName: "offset", publicName: "offset", isSignal: true, isRequired: false, transformFunction: null }, order: { classPropertyName: "order", publicName: "order", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cCol]',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { cCol: [{ type: i0.Input, args: [{ isSignal: true, alias: "cCol", required: false }] }], xs: [{ type: i0.Input, args: [{ isSignal: true, alias: "xs", required: false }] }], sm: [{ type: i0.Input, args: [{ isSignal: true, alias: "sm", required: false }] }], md: [{ type: i0.Input, args: [{ isSignal: true, alias: "md", required: false }] }], lg: [{ type: i0.Input, args: [{ isSignal: true, alias: "lg", required: false }] }], xl: [{ type: i0.Input, args: [{ isSignal: true, alias: "xl", required: false }] }], xxl: [{ type: i0.Input, args: [{ isSignal: true, alias: "xxl", required: false }] }], offset: [{ type: i0.Input, args: [{ isSignal: true, alias: "offset", required: false }] }], order: [{ type: i0.Input, args: [{ isSignal: true, alias: "order", required: false }] }] } });

class ColComponent extends ColDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ColComponent, isStandalone: true, selector: "c-col", usesInheritance: true, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ColComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-col', template: '<ng-content />', styles: [":host{display:block}\n"] }]
        }] });

class RowDirective {
    /**
     * The number of columns/offset/order on extra small devices (<576px).
     * @return { cols: 'auto' | number }
     */
    xs = input(...(ngDevMode ? [undefined, { debugName: "xs" }] : []));
    /**
     * The number of columns/offset/order on small devices (<768px).
     * @return { cols: 'auto' | number }
     */
    sm = input(...(ngDevMode ? [undefined, { debugName: "sm" }] : []));
    /**
     * The number of columns/offset/order on medium devices (<992px).
     * @return { cols: 'auto' | number }
     */
    md = input(...(ngDevMode ? [undefined, { debugName: "md" }] : []));
    /**
     * The number of columns/offset/order on large devices (<1200px).
     * @return { cols: 'auto' | number }
     */
    lg = input(...(ngDevMode ? [undefined, { debugName: "lg" }] : []));
    /**
     * The number of columns/offset/order on X-Large devices (<1400px).
     * @return { cols: 'auto' | number }
     */
    xl = input(...(ngDevMode ? [undefined, { debugName: "xl" }] : []));
    /**
     * The number of columns/offset/order on XX-Large devices (≥1400px).
     * @return { cols: 'auto' | number }
     */
    xxl = input(...(ngDevMode ? [undefined, { debugName: "xxl" }] : []));
    hostClasses = computed(() => {
        const cols = this.xs();
        const classes = {
            row: true,
            [`row-cols-${cols}`]: !!cols
        };
        Object.keys(BreakpointInfix).forEach((breakpoint) => {
            // @ts-ignore
            const value = this[breakpoint]();
            if (typeof value === 'number' || typeof value === 'string') {
                const infix = breakpoint === 'xs' ? '' : `-${breakpoint}`;
                classes[`row-cols${infix}-${value}`] = !!value;
            }
        });
        return classes;
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RowDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: RowDirective, isStandalone: true, selector: "[cRow]", inputs: { xs: { classPropertyName: "xs", publicName: "xs", isSignal: true, isRequired: false, transformFunction: null }, sm: { classPropertyName: "sm", publicName: "sm", isSignal: true, isRequired: false, transformFunction: null }, md: { classPropertyName: "md", publicName: "md", isSignal: true, isRequired: false, transformFunction: null }, lg: { classPropertyName: "lg", publicName: "lg", isSignal: true, isRequired: false, transformFunction: null }, xl: { classPropertyName: "xl", publicName: "xl", isSignal: true, isRequired: false, transformFunction: null }, xxl: { classPropertyName: "xxl", publicName: "xxl", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "row" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cRow]',
                    host: {
                        class: 'row',
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { xs: [{ type: i0.Input, args: [{ isSignal: true, alias: "xs", required: false }] }], sm: [{ type: i0.Input, args: [{ isSignal: true, alias: "sm", required: false }] }], md: [{ type: i0.Input, args: [{ isSignal: true, alias: "md", required: false }] }], lg: [{ type: i0.Input, args: [{ isSignal: true, alias: "lg", required: false }] }], xl: [{ type: i0.Input, args: [{ isSignal: true, alias: "xl", required: false }] }], xxl: [{ type: i0.Input, args: [{ isSignal: true, alias: "xxl", required: false }] }] } });

class RowComponent extends RowDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RowComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: RowComponent, isStandalone: true, selector: "c-row", usesInheritance: true, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-row',
                    template: '<ng-content />'
                }]
        }] });

class GutterDirective {
    /**
     * Define padding between columns to space and align content responsively in the Bootstrap grid system.
     */
    gutter = input({}, ...(ngDevMode ? [{ debugName: "gutter" }] : []));
    hostClasses = computed(() => {
        let gutterClass;
        const gutterInput = this.gutter();
        if (typeof gutterInput === 'number') {
            gutterClass = GutterDirective.getGutterClasses({ g: gutterInput });
            return gutterClass;
        }
        {
            const { g, gx, gy } = { ...gutterInput };
            gutterClass = GutterDirective.getGutterClasses({ g, gx, gy });
        }
        Object.keys(BreakpointInfix).forEach((key) => {
            // @ts-ignore
            const gutter = gutterInput[key] ? { ...gutterInput[key] } : undefined;
            if (gutter) {
                const classes = GutterDirective.getGutterClasses(gutter, key);
                gutterClass = { ...gutterClass, ...classes };
            }
        });
        return gutterClass;
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static getGutterClasses(gutter, breakpoint) {
        const { g, gx, gy } = { ...gutter };
        const infix = breakpoint ? `-${breakpoint}` : '';
        return {
            [`g${infix}-${g}`]: typeof g === 'number',
            [`gx${infix}-${gx}`]: typeof gx === 'number',
            [`gy${infix}-${gy}`]: typeof gy === 'number'
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: GutterDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: GutterDirective, isStandalone: true, selector: "[gutter]", inputs: { gutter: { classPropertyName: "gutter", publicName: "gutter", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["gutter"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: GutterDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[gutter]',
                    exportAs: 'gutter',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { gutter: [{ type: i0.Input, args: [{ isSignal: true, alias: "gutter", required: false }] }] } });

class GridModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: GridModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: GridModule, imports: [ColComponent,
            ColDirective,
            ContainerComponent,
            GutterDirective,
            RowComponent,
            RowDirective], exports: [ColComponent,
            ColDirective,
            ContainerComponent,
            GutterDirective,
            RowComponent,
            RowDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: GridModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: GridModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        ColComponent,
                        ColDirective,
                        ContainerComponent,
                        GutterDirective,
                        RowComponent,
                        RowDirective
                    ],
                    exports: [
                        ColComponent,
                        ColDirective,
                        ContainerComponent,
                        GutterDirective,
                        RowComponent,
                        RowDirective
                    ]
                }]
        }] });

class HeaderComponent {
    /**
     * Defines optional container wrapping children elements.
     */
    container = input(...(ngDevMode ? [undefined, { debugName: "container" }] : []));
    /**
     * Place header in non-static positions.
     */
    position = input(...(ngDevMode ? [undefined, { debugName: "position" }] : []));
    /**
     * Default role for header. [docs]
     * @type string
     * @default 'banner'
     */
    role = input('banner', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        return !!this.container() ? this.containerClasses() : this.headerClasses();
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    headerClasses = computed(() => {
        const position = this.position();
        return {
            header: true,
            [`header-${position}`]: !!position
        };
    }, ...(ngDevMode ? [{ debugName: "headerClasses" }] : []));
    containerClasses = computed(() => {
        const container = this.container();
        return {
            container: container === true,
            [`container-${container}`]: typeof container === 'string'
        };
    }, ...(ngDevMode ? [{ debugName: "containerClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: HeaderComponent, isStandalone: true, selector: "c-header, [c-header]", inputs: { container: { classPropertyName: "container", publicName: "container", isSignal: true, isRequired: false, transformFunction: null }, position: { classPropertyName: "position", publicName: "position", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()", "class": "hostClasses()" } }, exportAs: ["cHeader"], ngImport: i0, template: "@if (!!container()) {\n  <div [class]=\"headerClasses()\">\n    <ng-content />\n  </div>\n} @else {\n  <ng-content />\n}\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-header, [c-header]', exportAs: 'cHeader', host: { '[attr.role]': 'role()', '[class]': 'hostClasses()' }, template: "@if (!!container()) {\n  <div [class]=\"headerClasses()\">\n    <ng-content />\n  </div>\n} @else {\n  <ng-content />\n}\n" }]
        }], propDecorators: { container: [{ type: i0.Input, args: [{ isSignal: true, alias: "container", required: false }] }], position: [{ type: i0.Input, args: [{ isSignal: true, alias: "position", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class HeaderBrandComponent {
    /**
     * Default role for header-brand. [docs]
     * @return string
     * @default 'button'
     */
    role = input('button', ...(ngDevMode ? [{ debugName: "role" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderBrandComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: HeaderBrandComponent, isStandalone: true, selector: "c-header-brand", inputs: { role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()" }, classAttribute: "header-brand" }, exportAs: ["cHeaderBrand"], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderBrandComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-header-brand',
                    template: '<ng-content />',
                    exportAs: 'cHeaderBrand',
                    host: {
                        '[attr.role]': 'role()',
                        class: 'header-brand'
                    }
                }]
        }], propDecorators: { role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class HeaderDividerComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderDividerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: HeaderDividerComponent, isStandalone: true, selector: "c-header-divider, [cHeaderDivider]", host: { classAttribute: "header-divider" }, ngImport: i0, template: ``, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderDividerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-header-divider, [cHeaderDivider]',
                    template: ``,
                    host: {
                        class: 'header-divider'
                    }
                }]
        }] });

class HeaderNavComponent {
    /**
     * Default role for header-nav. [docs]
     * @return string
     * @default 'navigation'
     */
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: HeaderNavComponent, isStandalone: true, selector: "c-header-nav", inputs: { role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()" }, classAttribute: "header-nav" }, exportAs: ["cHeaderNav"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [""] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-header-nav', template: '<ng-content />', exportAs: 'cHeaderNav', host: {
                        '[attr.role]': 'role()',
                        class: 'header-nav'
                    } }]
        }], propDecorators: { role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class HeaderTextComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderTextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: HeaderTextComponent, isStandalone: true, selector: "c-header-text, [cHeaderText]", host: { classAttribute: "header-text" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderTextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-header-text, [cHeaderText]',
                    template: '<ng-content />',
                    host: {
                        class: 'header-text'
                    }
                }]
        }] });

class HeaderTogglerDirective {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    /**
     * Default type for header-toggler button. [docs]
     * @return string
     * @default 'button'
     */
    type = input('button', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Default aria-label attr for header-toggler. [docs]
     * @type string
     * @default 'Toggle navigation'
     */
    ariaLabel = input('Toggle navigation', ...(ngDevMode ? [{ debugName: "ariaLabel" }] : []));
    addDefaultIcon() {
        const span = this.#renderer.createElement('span');
        this.#renderer.addClass(span, 'header-toggler-icon');
        this.#renderer.appendChild(this.#hostElement.nativeElement, span);
    }
    ngAfterContentInit() {
        const hasContent = this.#hostElement.nativeElement.childNodes.length > 0;
        if (!hasContent) {
            this.addDefaultIcon();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderTogglerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: HeaderTogglerDirective, isStandalone: true, selector: "[cHeaderToggler]", inputs: { type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.type": "type()", "attr.aria-label": "ariaLabel()" }, classAttribute: "header-toggler" }, exportAs: ["cHeaderToggler"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderTogglerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cHeaderToggler]',
                    exportAs: 'cHeaderToggler',
                    host: {
                        '[attr.type]': 'type()',
                        '[attr.aria-label]': 'ariaLabel()',
                        class: 'header-toggler'
                    }
                }]
        }], propDecorators: { type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], ariaLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabel", required: false }] }] } });

class HeaderModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: HeaderModule, imports: [HeaderComponent,
            HeaderBrandComponent,
            HeaderDividerComponent,
            HeaderNavComponent,
            HeaderTextComponent,
            HeaderTogglerDirective], exports: [HeaderComponent,
            HeaderBrandComponent,
            HeaderDividerComponent,
            HeaderNavComponent,
            HeaderTextComponent,
            HeaderTogglerDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        HeaderComponent,
                        HeaderBrandComponent,
                        HeaderDividerComponent,
                        HeaderNavComponent,
                        HeaderTextComponent,
                        HeaderTogglerDirective
                    ],
                    exports: [
                        HeaderComponent,
                        HeaderBrandComponent,
                        HeaderDividerComponent,
                        HeaderNavComponent,
                        HeaderTextComponent,
                        HeaderTogglerDirective
                    ]
                }]
        }] });

class ImgDirective {
    /**
     * Set the horizontal aligment.
     * @type {'' | 'start' | 'end' | 'center'}
     */
    align = input('', ...(ngDevMode ? [{ debugName: "align" }] : []));
    /**
     * Make image responsive.
     * @type boolean
     */
    fluid = input(false, { ...(ngDevMode ? { debugName: "fluid" } : {}), transform: booleanAttribute });
    /**
     * Make image rounded.
     * @type boolean
     */
    rounded = input(false, { ...(ngDevMode ? { debugName: "rounded" } : {}), transform: booleanAttribute });
    /**
     * Give an image a rounded 1px border appearance.
     * @type boolean
     */
    thumbnail = input(false, { ...(ngDevMode ? { debugName: "thumbnail" } : {}), transform: booleanAttribute });
    /**
     * Color for image placeholder.
     */
    placeholderColor = input('transparent', ...(ngDevMode ? [{ debugName: "placeholderColor" }] : []));
    hostStyles = computed(() => {
        return { backgroundColor: this.placeholderColor() };
    }, ...(ngDevMode ? [{ debugName: "hostStyles" }] : []));
    hostClasses = computed(() => {
        const align = this.align();
        return {
            [`float-${align}`]: align === 'start' || align === 'end',
            'd-block': align === 'center',
            'mx-auto': align === 'center',
            'img-fluid': this.fluid(),
            rounded: this.rounded(),
            'img-thumbnail': this.thumbnail()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ImgDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ImgDirective, isStandalone: true, selector: "[cImg]", inputs: { align: { classPropertyName: "align", publicName: "align", isSignal: true, isRequired: false, transformFunction: null }, fluid: { classPropertyName: "fluid", publicName: "fluid", isSignal: true, isRequired: false, transformFunction: null }, rounded: { classPropertyName: "rounded", publicName: "rounded", isSignal: true, isRequired: false, transformFunction: null }, thumbnail: { classPropertyName: "thumbnail", publicName: "thumbnail", isSignal: true, isRequired: false, transformFunction: null }, placeholderColor: { classPropertyName: "placeholderColor", publicName: "placeholderColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "style": "hostStyles()" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ImgDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cImg]',
                    host: {
                        '[class]': 'hostClasses()',
                        '[style]': 'hostStyles()'
                    }
                }]
        }], propDecorators: { align: [{ type: i0.Input, args: [{ isSignal: true, alias: "align", required: false }] }], fluid: [{ type: i0.Input, args: [{ isSignal: true, alias: "fluid", required: false }] }], rounded: [{ type: i0.Input, args: [{ isSignal: true, alias: "rounded", required: false }] }], thumbnail: [{ type: i0.Input, args: [{ isSignal: true, alias: "thumbnail", required: false }] }], placeholderColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholderColor", required: false }] }] } });

class ImgModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ImgModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ImgModule, imports: [ImgDirective], exports: [ImgDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ImgModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ImgModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [ImgDirective],
                    exports: [ImgDirective]
                }]
        }] });

class ListGroupDirective {
    static ngAcceptInputType_flush;
    /**
     * Remove some borders and rounded corners to render list group items edge-to-edge in a parent component (e.g., `<CCard>`).
     * @type boolean
     */
    flush = input(false, { ...(ngDevMode ? { debugName: "flush" } : {}), transform: booleanAttribute });
    /**
     * Specify horizontal layout type.
     */
    horizontal = input(...(ngDevMode ? [undefined, { debugName: "horizontal" }] : []));
    hostClasses = computed(() => {
        const horizontal = this.horizontal();
        return {
            'list-group': true,
            'list-group-horizontal': horizontal === true || horizontal === '',
            [`list-group-horizontal-${horizontal}`]: !!horizontal && typeof horizontal !== 'boolean',
            'list-group-flush': this.flush()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ListGroupDirective, isStandalone: true, selector: "[cListGroup]", inputs: { flush: { classPropertyName: "flush", publicName: "flush", isSignal: true, isRequired: false, transformFunction: null }, horizontal: { classPropertyName: "horizontal", publicName: "horizontal", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "list-group" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cListGroup]',
                    host: {
                        class: 'list-group',
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { flush: [{ type: i0.Input, args: [{ isSignal: true, alias: "flush", required: false }] }], horizontal: [{ type: i0.Input, args: [{ isSignal: true, alias: "horizontal", required: false }] }] } });

class ListGroupItemDirective {
    static ngAcceptInputType_active;
    static ngAcceptInputType_disabled;
    hostElement = inject(ElementRef);
    /**
     * Toggle the active state for the component.
     * @type InputSignalWithTransform<boolean, unknown>
     */
    active = input(false, { ...(ngDevMode ? { debugName: "active" } : {}), transform: booleanAttribute });
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type InputSignal<boolean | undefined>
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Set disabled attr for the host element. [docs]
     * @type boolean
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    /**
     * The tabindex attribute specifies the tab order of an element (when the "tab" button is used for navigating).
     */
    tabindex = input(undefined, { ...(ngDevMode ? { debugName: "tabindex" } : {}), transform: numberAttribute });
    hostClasses = computed(() => {
        const host = this.hostElement.nativeElement;
        const color = this.color();
        return {
            'list-group-item': true,
            'list-group-item-action': host.nodeName === 'A' || host.nodeName === 'BUTTON',
            active: this.active(),
            disabled: this._disabled(),
            [`list-group-item-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    _disabled = computed(() => this.disabled(), ...(ngDevMode ? [{ debugName: "_disabled" }] : []));
    ariaDisabled = computed(() => {
        return this._disabled() ? true : null;
    }, ...(ngDevMode ? [{ debugName: "ariaDisabled" }] : []));
    attrDisabled = computed(() => {
        return this._disabled() ? '' : null;
    }, ...(ngDevMode ? [{ debugName: "attrDisabled" }] : []));
    tabIndex = computed(() => {
        return this._disabled() ? '-1' : (this.tabindex() ?? null);
    }, ...(ngDevMode ? [{ debugName: "tabIndex" }] : []));
    ariaCurrent = computed(() => {
        return this.active() || null;
    }, ...(ngDevMode ? [{ debugName: "ariaCurrent" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ListGroupItemDirective, isStandalone: true, selector: "[cListGroupItem], c-list-group-item", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, tabindex: { classPropertyName: "tabindex", publicName: "tabindex", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-disabled": "ariaDisabled()", "attr.aria-current": "ariaCurrent()", "attr.disabled": "attrDisabled()", "attr.tabindex": "tabIndex()" } }, exportAs: ["cListGroupItem"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cListGroupItem], c-list-group-item',
                    exportAs: 'cListGroupItem',
                    host: {
                        '[class]': 'hostClasses()',
                        '[attr.aria-disabled]': 'ariaDisabled()',
                        '[attr.aria-current]': 'ariaCurrent()',
                        '[attr.disabled]': 'attrDisabled()',
                        '[attr.tabindex]': 'tabIndex()'
                    }
                }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], tabindex: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabindex", required: false }] }] } });

class ListGroupModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ListGroupModule, imports: [ListGroupDirective, ListGroupItemDirective], exports: [ListGroupDirective, ListGroupItemDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ListGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [ListGroupDirective, ListGroupItemDirective],
                    imports: [ListGroupDirective, ListGroupItemDirective]
                }]
        }] });

const loadingButtonAnimation = trigger('loadingButton', [
    transition(':enter', [
        style({
            opacity: 0,
            marginRight: '1rem',
            marginLeft: '-2rem'
        }),
        animate('.15s ease-in-out', style({
            opacity: 1,
            marginRight: '1rem',
            marginLeft: '0rem'
        }))
    ]),
    transition(':leave', [
        style({
            opacity: 1
        }),
        animate('.15s ease-in-out', style({
            opacity: 0,
            marginRight: '1rem',
            marginLeft: '-2rem'
        }))
    ])
]);

class LoadingButtonComponent extends ButtonDirective {
    /**
     * Makes button disabled when loading.
     * @type boolean
     * @default false
     */
    disabledOnLoading = input(false, { ...(ngDevMode ? { debugName: "disabledOnLoading" } : {}), transform: booleanAttribute });
    /**
     * Loading state (set to true to start animation).
     * @type boolean
     */
    loading = input(false, { ...(ngDevMode ? { debugName: "loading" } : {}), transform: booleanAttribute });
    /**
     * Event emitted on loading change
     * @type boolean
     */
    loadingChange = output();
    loadingEffect = effect(() => {
        this.loadingChange.emit(this.loading());
    }, ...(ngDevMode ? [{ debugName: "loadingEffect" }] : []));
    /**
     * Sets type of spinner.
     * @type {'border' | 'grow'}
     */
    spinnerType = input('border', ...(ngDevMode ? [{ debugName: "spinnerType" }] : []));
    constructor() {
        super();
    }
    hostClasses = computed(() => {
        const color = this.color();
        const size = this.size();
        const variant = this.variant();
        return {
            btn: true,
            'btn-loading': true,
            'is-loading': this.loading(),
            [`btn-${color}`]: !!color && !variant,
            [`btn-${variant}`]: !!variant && !color,
            [`btn-${variant}-${color}`]: !!variant && !!color,
            [`btn-${size}`]: !!size,
            active: this.active(),
            disabled: this._disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    _disabled = computed(() => {
        return this.disabled() || (this.disabledOnLoading() && this.loading());
    }, ...(ngDevMode ? [{ debugName: "_disabled" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: LoadingButtonComponent, isStandalone: true, selector: "button[cLoadingButton], a[cLoadingButton]", inputs: { disabledOnLoading: { classPropertyName: "disabledOnLoading", publicName: "disabledOnLoading", isSignal: true, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: true, isRequired: false, transformFunction: null }, spinnerType: { classPropertyName: "spinnerType", publicName: "spinnerType", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { loadingChange: "loadingChange" }, host: { properties: { "class": "hostClasses()", "attr.aria-disabled": "ariaDisabled()", "attr.aria-pressed": "isActive()", "attr.disabled": "attrDisabled()", "attr.tabindex": "tabIndex()", "attr.type": "type()" }, classAttribute: "btn-loading" }, exportAs: ["cLoadingButton"], usesInheritance: true, ngImport: i0, template: "@if (loading()) {\n  <c-spinner [variant]=\"spinnerType()\" size=\"sm\" class=\"btn-loading-spinner\" @loadingButton />\n}\n<ng-content />\n", dependencies: [{ kind: "component", type: SpinnerComponent, selector: "c-spinner", inputs: ["color", "label", "size", "variant", "role"] }], animations: [loadingButtonAnimation] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'button[cLoadingButton], a[cLoadingButton]', exportAs: 'cLoadingButton', imports: [SpinnerComponent], animations: [loadingButtonAnimation], host: {
                        class: 'btn-loading',
                        '[class]': 'hostClasses()',
                        '[attr.aria-disabled]': 'ariaDisabled()',
                        '[attr.aria-pressed]': 'isActive()',
                        '[attr.disabled]': 'attrDisabled()',
                        '[attr.tabindex]': 'tabIndex()',
                        '[attr.type]': 'type()'
                    }, template: "@if (loading()) {\n  <c-spinner [variant]=\"spinnerType()\" size=\"sm\" class=\"btn-loading-spinner\" @loadingButton />\n}\n<ng-content />\n" }]
        }], ctorParameters: () => [], propDecorators: { disabledOnLoading: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabledOnLoading", required: false }] }], loading: [{ type: i0.Input, args: [{ isSignal: true, alias: "loading", required: false }] }], loadingChange: [{ type: i0.Output, args: ["loadingChange"] }], spinnerType: [{ type: i0.Input, args: [{ isSignal: true, alias: "spinnerType", required: false }] }] } });

class LoadingButtonModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonModule, imports: [LoadingButtonComponent], exports: [LoadingButtonComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: LoadingButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [LoadingButtonComponent],
                    imports: [LoadingButtonComponent]
                }]
        }] });

class MultiSelectOptgroupLabelComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptgroupLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: MultiSelectOptgroupLabelComponent, isStandalone: true, selector: "c-multi-select-optgroup-label", host: { classAttribute: "form-multi-select-optgroup-label" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptgroupLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-multi-select-optgroup-label', template: '<ng-content />', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, host: { class: 'form-multi-select-optgroup-label' }, styles: [":host{display:block}\n"] }]
        }] });

class MultiSelectOptgroupComponent {
    static ngAcceptInputType_disabled;
    #destroyRef = inject(DestroyRef);
    #multiSelectService = inject(MultiSelectService);
    label = input(...(ngDevMode ? [undefined, { debugName: "label" }] : []));
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    visible = signal(true, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    multiSelectOptions = contentChildren(MultiSelectOptionComponent, { ...(ngDevMode ? { debugName: "multiSelectOptions" } : {}), descendants: true });
    #optionsEffect = effect(() => {
        const disabled = this.disabled();
        const options = this.multiSelectOptions();
        options?.forEach((option) => {
            option.disabled = disabled || option.disabled;
        });
    }, ...(ngDevMode ? [{ debugName: "#optionsEffect" }] : []));
    hostClasses = computed(() => {
        return {
            'form-multi-select-options': true,
            'd-none': !this.visible()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngAfterContentInit() {
        this.watchOptGroupContent();
    }
    watchOptGroupContent() {
        this.#multiSelectService.optionsArray$
            .pipe(tap((x) => {
            this.visible.set(this.multiSelectOptions()?.some((option) => option.visible));
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptgroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: MultiSelectOptgroupComponent, isStandalone: true, selector: "c-multi-select-optgroup", inputs: { label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, queries: [{ propertyName: "multiSelectOptions", predicate: MultiSelectOptionComponent, descendants: true, isSignal: true }], ngImport: i0, template: `
    @let lbl = label();
    @if (lbl && visible()) {
      <c-multi-select-optgroup-label>{{ lbl }}</c-multi-select-optgroup-label>
    }
    <ng-content />
  `, isInline: true, styles: [":host{display:block}\n"], dependencies: [{ kind: "component", type: MultiSelectOptgroupLabelComponent, selector: "c-multi-select-optgroup-label" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectOptgroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-multi-select-optgroup', template: `
    @let lbl = label();
    @if (lbl && visible()) {
      <c-multi-select-optgroup-label>{{ lbl }}</c-multi-select-optgroup-label>
    }
    <ng-content />
  `, changeDetection: ChangeDetectionStrategy.OnPush, imports: [MultiSelectOptgroupLabelComponent], host: {
                        '[class]': 'hostClasses()'
                    }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { label: [{ type: i0.Input, args: [{ isSignal: true, alias: "label", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], multiSelectOptions: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => MultiSelectOptionComponent), { ...{ descendants: true }, isSignal: true }] }] } });

class MultiSelectModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectModule, imports: [MultiSelectComponent,
            MultiSelectOptionComponent,
            MultiSelectOptgroupComponent,
            MultiSelectOptgroupLabelComponent], exports: [MultiSelectComponent,
            MultiSelectOptionComponent,
            MultiSelectOptgroupComponent,
            MultiSelectOptgroupLabelComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectModule, providers: [MultiSelectService], imports: [MultiSelectComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: MultiSelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        MultiSelectComponent,
                        MultiSelectOptionComponent,
                        MultiSelectOptgroupComponent,
                        MultiSelectOptgroupLabelComponent
                    ],
                    exports: [
                        MultiSelectComponent,
                        MultiSelectOptionComponent,
                        MultiSelectOptgroupComponent,
                        MultiSelectOptgroupLabelComponent
                    ],
                    providers: [MultiSelectService]
                }]
        }] });

class NavLinkDirective {
    static ngAcceptInputType_disabled;
    /**
     * Sets .nav-link class to the host. [docs]
     * @default true
     */
    cNavLink = input(true, { ...(ngDevMode ? { debugName: "cNavLink" } : {}), transform: booleanAttribute });
    /**
     * Toggle the active state for the component. [docs]
     * @default undefined
     */
    active = input(...(ngDevMode ? [undefined, { debugName: "active" }] : []));
    /**
     * Set disabled attr for the host element. [docs]
     * @default false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    /**
     * The tabindex attribute specifies the tab order of an element (when the "tab" button is used for navigating).
     */
    tabindex = input(undefined, { ...(ngDevMode ? { debugName: "tabindex" } : {}), transform: numberAttribute });
    ariaCurrent = computed(() => {
        return this.active() ? 'page' : null;
    }, ...(ngDevMode ? [{ debugName: "ariaCurrent" }] : []));
    ariaDisabled = null;
    attrDisabled = null;
    attrTabindex = null;
    styleCursor = null;
    #disabledEffect = effect(() => {
        const disabled = this.disabled();
        this.ariaDisabled = disabled || null;
        this.attrDisabled = disabled ? '' : null;
        this.attrTabindex = disabled ? -1 : (this.tabindex() ?? null);
        this.styleCursor = disabled ? null : 'pointer';
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    hostClasses = computed(() => {
        return {
            'nav-link': this.cNavLink(),
            disabled: this.disabled(),
            active: this.active()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: NavLinkDirective, isStandalone: true, selector: "[cNavLink]", inputs: { cNavLink: { classPropertyName: "cNavLink", publicName: "cNavLink", isSignal: true, isRequired: false, transformFunction: null }, active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, tabindex: { classPropertyName: "tabindex", publicName: "tabindex", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-current": "ariaCurrent()", "attr.aria-disabled": "ariaDisabled", "attr.disabled": "attrDisabled", "attr.tabindex": "attrTabindex", "style.cursor": "styleCursor" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cNavLink]',
                    host: {
                        '[class]': 'hostClasses()',
                        '[attr.aria-current]': 'ariaCurrent()',
                        '[attr.aria-disabled]': 'ariaDisabled',
                        '[attr.disabled]': 'attrDisabled',
                        '[attr.tabindex]': 'attrTabindex',
                        '[style.cursor]': 'styleCursor'
                    }
                }]
        }], propDecorators: { cNavLink: [{ type: i0.Input, args: [{ isSignal: true, alias: "cNavLink", required: false }] }], active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], tabindex: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabindex", required: false }] }] } });

class NavItemComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: NavItemComponent, isStandalone: true, selector: "c-nav-item", host: { classAttribute: "nav-item" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:list-item;text-align:match-parent;text-align:-webkit-match-parent}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-nav-item', template: '<ng-content />', host: { class: 'nav-item' }, styles: [":host{display:list-item;text-align:match-parent;text-align:-webkit-match-parent}\n"] }]
        }] });

class NavComponent {
    /**
     * Specify a layout type for component.
     * @default undefined
     */
    layout = input(...(ngDevMode ? [undefined, { debugName: "layout" }] : []));
    /**
     * Set the nav variant to tabs or pills.
     * @default undefined
     */
    variant = input(...(ngDevMode ? [undefined, { debugName: "variant" }] : []));
    hostClasses = computed(() => {
        const layout = this.layout();
        const variant = this.variant();
        return {
            nav: true,
            [`nav-${layout}`]: !!layout,
            [`nav-${variant}`]: !!variant
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: NavComponent, isStandalone: true, selector: "c-nav", inputs: { layout: { classPropertyName: "layout", publicName: "layout", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "nav" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host .nav-link:focus{outline:0}:host.nav-underline-border{column-gap:0}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-nav', template: '<ng-content />', host: { class: 'nav', '[class]': 'hostClasses()' }, styles: [":host .nav-link:focus{outline:0}:host.nav-underline-border{column-gap:0}\n"] }]
        }], propDecorators: { layout: [{ type: i0.Input, args: [{ isSignal: true, alias: "layout", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }] } });

class NavModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: NavModule, imports: [NavComponent,
            NavItemComponent,
            NavLinkDirective], exports: [NavComponent,
            NavItemComponent,
            NavLinkDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        NavComponent,
                        NavItemComponent,
                        NavLinkDirective
                    ],
                    exports: [
                        NavComponent,
                        NavItemComponent,
                        NavLinkDirective
                    ]
                }]
        }] });

// todo: fix container prop issue not rendering children
// todo: workaround -  use <c-container> component directly in template
class NavbarComponent {
    #breakpointObserver = inject(BreakpointObserver);
    #document = inject(DOCUMENT);
    #hostElement = inject(ElementRef);
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Defines optional container wrapping children elements.
     */
    container = input(...(ngDevMode ? [undefined, { debugName: "container" }] : []));
    /**
     * Defines the responsive breakpoint to determine when content collapses.
     */
    expand = input(...(ngDevMode ? [undefined, { debugName: "expand" }] : []));
    /**
     * Place component in non-static positions.
     */
    placement = input(...(ngDevMode ? [undefined, { debugName: "placement" }] : []));
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    collapse = contentChild(CollapseDirective, ...(ngDevMode ? [{ debugName: "collapse" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        const expand = this.expand();
        const expandClassSuffix = expand === true ? '' : `-${expand}`;
        const placement = this.placement();
        return {
            navbar: true,
            [`navbar-expand${expandClassSuffix}`]: !!expand,
            [`bg-${color}`]: !!color,
            [`${placement}`]: !!placement
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    containerClass = computed(() => {
        const container = this.container();
        return `container${container !== true ? '-' + container : ''}`;
    }, ...(ngDevMode ? [{ debugName: "containerClass" }] : []));
    computedStyle = signal('', ...(ngDevMode ? [{ debugName: "computedStyle" }] : []));
    #afterEveryRenderFn = afterEveryRender({
        read: () => {
            const expand = this.expand();
            if (typeof expand === 'string') {
                const computedStyle = this.#document.defaultView
                    ?.getComputedStyle(this.#hostElement.nativeElement)
                    ?.getPropertyValue(`--cui-breakpoint-${expand}`) ?? false;
                computedStyle && this.computedStyle.set(computedStyle);
            }
        }
    });
    breakpoint = computed(() => {
        const expand = this.expand();
        if (typeof expand === 'string') {
            return this.computedStyle();
        }
        return false;
    }, ...(ngDevMode ? [{ debugName: "breakpoint" }] : []));
    #observer;
    ngAfterContentInit() {
        const breakpoint = this.breakpoint();
        if (breakpoint) {
            const onBreakpoint = `(min-width: ${breakpoint})`;
            this.#observer = this.#breakpointObserver
                .observe([onBreakpoint])
                .pipe()
                .subscribe((result) => {
                const collapse = this.collapse();
                if (collapse) {
                    const animate = collapse.animate();
                    collapse.animate.set(false);
                    collapse.toggle(false);
                    setTimeout(() => {
                        collapse.toggle(result.matches);
                        setTimeout(() => {
                            collapse.animate.set(animate);
                        });
                    });
                }
            });
        }
    }
    ngOnDestroy() {
        this.#observer?.unsubscribe();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: NavbarComponent, isStandalone: true, selector: "c-navbar", inputs: { color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, container: { classPropertyName: "container", publicName: "container", isSignal: true, isRequired: false, transformFunction: null }, expand: { classPropertyName: "expand", publicName: "expand", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.role": "role()" } }, queries: [{ propertyName: "collapse", first: true, predicate: CollapseDirective, descendants: true, isSignal: true }], hostDirectives: [{ directive: ThemeDirective, inputs: ["colorScheme", "colorScheme"] }], ngImport: i0, template: "<ng-container *ngTemplateOutlet=\"container() ? withContainerTemplate : noContainerTemplate\" />\n\n<ng-template #withContainerTemplate>\n  <div [class]=\"containerClass()\">\n    <ng-content />\n  </div>\n</ng-template>\n\n<ng-template #noContainerTemplate>\n  <ng-content />\n</ng-template>\n", dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-navbar', imports: [NgTemplateOutlet], hostDirectives: [{ directive: ThemeDirective, inputs: ['colorScheme'] }], host: { '[class]': 'hostClasses()', '[attr.role]': 'role()' }, template: "<ng-container *ngTemplateOutlet=\"container() ? withContainerTemplate : noContainerTemplate\" />\n\n<ng-template #withContainerTemplate>\n  <div [class]=\"containerClass()\">\n    <ng-content />\n  </div>\n</ng-template>\n\n<ng-template #noContainerTemplate>\n  <ng-content />\n</ng-template>\n" }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], container: [{ type: i0.Input, args: [{ isSignal: true, alias: "container", required: false }] }], expand: [{ type: i0.Input, args: [{ isSignal: true, alias: "expand", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], collapse: [{ type: i0.ContentChild, args: [i0.forwardRef(() => CollapseDirective), { isSignal: true }] }] } });

class NavbarBrandDirective {
    role = input('button', ...(ngDevMode ? [{ debugName: "role" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarBrandDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: NavbarBrandDirective, isStandalone: true, selector: "[cNavbarBrand]", inputs: { role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()" }, classAttribute: "navbar-brand" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarBrandDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cNavbarBrand]',
                    host: { class: 'navbar-brand', '[attr.role]': 'role()' }
                }]
        }], propDecorators: { role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class NavbarNavComponent {
    /**
     * Enable vertical scrolling of a collapsed navbar toggleable contents.
     * @type boolean
     */
    scroll = input(false, { ...(ngDevMode ? { debugName: "scroll" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            'navbar-nav': true,
            'navbar-nav-scroll': this.scroll()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: NavbarNavComponent, isStandalone: true, selector: "c-navbar-nav", inputs: { scroll: { classPropertyName: "scroll", publicName: "scroll", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarNavComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-navbar-nav',
                    template: '<ng-content />',
                    host: { '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { scroll: [{ type: i0.Input, args: [{ isSignal: true, alias: "scroll", required: false }] }] } });

class NavbarTextComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarTextComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: NavbarTextComponent, isStandalone: true, selector: "c-navbar-text", host: { classAttribute: "navbar-text" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarTextComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-navbar-text',
                    template: '<ng-content />',
                    host: { class: 'navbar-text' }
                }]
        }] });

class NavbarTogglerDirective {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    constructor() {
        afterNextRender({
            read: () => {
                const hasContent = this.#hostElement.nativeElement.childNodes.length;
                if (!hasContent) {
                    this.addDefaultIcon();
                }
            }
        });
    }
    /**
     * Reference to navbar collapse element (via # template variable) . [docs]
     * @type string
     * @default 'button'
     */
    collapseRef = input(undefined, { ...(ngDevMode ? { debugName: "collapseRef" } : {}), alias: 'cNavbarToggler' });
    /**
     * Default type for navbar-toggler. [docs]
     * @type string
     * @default 'button'
     */
    type = input('button', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Default aria-label attr for navbar-toggler. [docs]
     * @type string
     * @default 'Toggle navigation'
     */
    ariaLabel = input('Toggle navigation', ...(ngDevMode ? [{ debugName: "ariaLabel" }] : []));
    handleClick($event) {
        const collapseRef = this.collapseRef();
        collapseRef?.toggle(!collapseRef?.visible());
    }
    addDefaultIcon() {
        const span = this.#renderer.createElement('span');
        this.#renderer.addClass(span, 'navbar-toggler-icon');
        this.#renderer.appendChild(this.#hostElement.nativeElement, span);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarTogglerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: NavbarTogglerDirective, isStandalone: true, selector: "[cNavbarToggler]", inputs: { collapseRef: { classPropertyName: "collapseRef", publicName: "cNavbarToggler", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "handleClick($event)" }, properties: { "attr.aria-label": "ariaLabel()", "attr.type": "type()" }, classAttribute: "navbar-toggler" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarTogglerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cNavbarToggler]',
                    host: {
                        '[attr.aria-label]': 'ariaLabel()',
                        '[attr.type]': 'type()',
                        class: 'navbar-toggler',
                        '(click)': 'handleClick($event)'
                    }
                }]
        }], ctorParameters: () => [], propDecorators: { collapseRef: [{ type: i0.Input, args: [{ isSignal: true, alias: "cNavbarToggler", required: false }] }], type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], ariaLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabel", required: false }] }] } });

class NavbarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: NavbarModule, imports: [NavbarBrandDirective,
            NavbarComponent,
            NavbarNavComponent,
            NavbarTextComponent,
            NavbarTogglerDirective], exports: [NavbarBrandDirective,
            NavbarComponent,
            NavbarNavComponent,
            NavbarTextComponent,
            NavbarTogglerDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: NavbarModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        NavbarBrandDirective,
                        NavbarComponent,
                        NavbarNavComponent,
                        NavbarTextComponent,
                        NavbarTogglerDirective
                    ],
                    imports: [
                        NavbarBrandDirective,
                        NavbarComponent,
                        NavbarNavComponent,
                        NavbarTextComponent,
                        NavbarTogglerDirective
                    ]
                }]
        }] });

class ModalBodyComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ModalBodyComponent, isStandalone: true, selector: "c-modal-body", host: { classAttribute: "modal-body" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-modal-body', template: '<ng-content />', host: { class: 'modal-body' }, styles: [":host{display:block}\n"] }]
        }] });

class ModalContentComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ModalContentComponent, isStandalone: true, selector: "c-modal-content", host: { classAttribute: "modal-content" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-modal-content',
                    template: '<ng-content />',
                    host: { class: 'modal-content' }
                }]
        }] });

class ModalDialogComponent {
    /**
     * Align the modal in the center or top of the screen.
     * @default undefined
     */
    alignment = input(...(ngDevMode ? [undefined, { debugName: "alignment" }] : []));
    /**
     * Set modal to covers the entire user viewport.
     * @return {boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'}
     */
    fullscreen = input(...(ngDevMode ? [undefined, { debugName: "fullscreen" }] : []));
    /**
     * Does the modal dialog itself scroll, or does the whole dialog scroll within the window.
     * @default false
     * @return {boolean}
     */
    scrollable = input(false, { ...(ngDevMode ? { debugName: "scrollable" } : {}), transform: booleanAttribute });
    /**
     * Size the component small, large, or extra large.
     * @default undefined
     * @return {'sm' | 'lg' | 'xl'}
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    hostClasses = computed(() => {
        const fullscreen = this.fullscreen();
        const size = this.size();
        return {
            'modal-dialog': true,
            'modal-dialog-centered': this.alignment() === 'center',
            'modal-fullscreen': fullscreen === true,
            [`modal-fullscreen-${fullscreen}-down`]: typeof fullscreen === 'string',
            'modal-dialog-scrollable': this.scrollable(),
            [`modal-${size}`]: !!size
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalDialogComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ModalDialogComponent, isStandalone: true, selector: "c-modal-dialog", inputs: { alignment: { classPropertyName: "alignment", publicName: "alignment", isSignal: true, isRequired: false, transformFunction: null }, fullscreen: { classPropertyName: "fullscreen", publicName: "fullscreen", isSignal: true, isRequired: false, transformFunction: null }, scrollable: { classPropertyName: "scrollable", publicName: "scrollable", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "modal-dialog" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}:host.modal-dialog-centered{display:flex}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-modal-dialog', template: '<ng-content />', host: { class: 'modal-dialog', '[class]': 'hostClasses()' }, styles: [":host{display:block}:host.modal-dialog-centered{display:flex}\n"] }]
        }], propDecorators: { alignment: [{ type: i0.Input, args: [{ isSignal: true, alias: "alignment", required: false }] }], fullscreen: [{ type: i0.Input, args: [{ isSignal: true, alias: "fullscreen", required: false }] }], scrollable: [{ type: i0.Input, args: [{ isSignal: true, alias: "scrollable", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }] } });

class ModalService {
    #modalState = new Subject();
    modalState$ = this.#modalState.asObservable();
    toggle(action) {
        this.#modalState.next(action);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ModalToggleDirective {
    #modalService = inject(ModalService);
    /**
     * Html id attr of modal to dismiss.
     * @default undefined
     */
    toggle = input(undefined, { ...(ngDevMode ? { debugName: "toggle" } : {}), alias: 'cModalToggle' });
    dismiss($event) {
        $event.preventDefault();
        this.#modalService.toggle({ show: 'toggle', id: this.toggle() });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ModalToggleDirective, isStandalone: true, selector: "[cModalToggle]", inputs: { toggle: { classPropertyName: "toggle", publicName: "cModalToggle", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "dismiss($event)" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cModalToggle]',
                    host: {
                        '(click)': 'dismiss($event)'
                    }
                }]
        }], propDecorators: { toggle: [{ type: i0.Input, args: [{ isSignal: true, alias: "cModalToggle", required: false }] }] } });

class ModalFooterComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ModalFooterComponent, isStandalone: true, selector: "c-modal-footer", host: { classAttribute: "modal-footer" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-modal-footer',
                    template: '<ng-content />',
                    host: { class: 'modal-footer' }
                }]
        }] });

class ModalHeaderComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ModalHeaderComponent, isStandalone: true, selector: "c-modal-header", host: { classAttribute: "modal-header" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-modal-header',
                    template: '<ng-content />',
                    host: { class: 'modal-header' }
                }]
        }] });

class ModalTitleDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: ModalTitleDirective, isStandalone: true, selector: "[cModalTitle]", host: { classAttribute: "modal-title" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cModalTitle]',
                    host: { class: 'modal-title' }
                }]
        }] });

class ModalComponent {
    static ngAcceptInputType_ariaModalInput;
    static ngAcceptInputType_scrollable;
    static ngAcceptInputType_visibleInput;
    #document = inject(DOCUMENT);
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #modalService = inject(ModalService);
    #backdropService = inject(BackdropService);
    #destroyRef = inject(DestroyRef);
    #focusMonitor = inject(FocusMonitor);
    /**
     * Align the modal in the center or top of the screen.
     * @return {'top' | 'center'}
     * @default 'top'
     */
    alignment = input('top', ...(ngDevMode ? [{ debugName: "alignment" }] : []));
    /**
     * Apply a backdrop on body while modal is open.
     * @return boolean | 'static'
     * @default true
     */
    backdrop = input(true, ...(ngDevMode ? [{ debugName: "backdrop" }] : []));
    /**
     * Set modal to cover the entire user viewport.
     * @return {boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'}
     * @default undefined
     */
    fullscreen = input(...(ngDevMode ? [undefined, { debugName: "fullscreen" }] : []));
    /**
     * Closes the modal when escape key is pressed.
     * @return boolean
     * @default true
     */
    keyboard = input(true, { ...(ngDevMode ? { debugName: "keyboard" } : {}), transform: booleanAttribute });
    attrId = input(undefined, { ...(ngDevMode ? { debugName: "attrId" } : {}), alias: 'id' });
    get id() {
        return this.attrId();
    }
    /**
     * Size the component small, large, or extra large.
     * @return {'sm' | 'lg' | 'xl'}
     * @default undefined
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Remove animation to create modal that simply appear rather than fade in to view.
     */
    transition = input(true, { ...(ngDevMode ? { debugName: "transition" } : {}), transform: booleanAttribute });
    /**
     * Default role for modal
     * @return string
     * @default 'dialog'
     */
    role = input('dialog', ...(ngDevMode ? [{ debugName: "role" }] : []));
    /**
     * Set aria-modal html attr for modal
     * @type boolean
     * @default null
     */
    ariaModalInput = input(false, { ...(ngDevMode ? { debugName: "ariaModalInput" } : {}), transform: booleanAttribute, alias: 'ariaModal' });
    ariaModal = computed(() => {
        return this.visible() || this.ariaModalInput() ? true : null;
    }, ...(ngDevMode ? [{ debugName: "ariaModal" }] : []));
    /**
     * Create a scrollable modal that allows scrolling the modal body.
     * @return boolean
     * @default false
     */
    scrollable = input(false, { ...(ngDevMode ? { debugName: "scrollable" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of modal component.
     * @return boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    #visibleInputEffect = effect(() => {
        const visible = this.visible();
        untracked(() => {
            this.animateStart();
            this.setBodyStyles(visible);
            this.setBackdrop(this.backdrop() !== false && visible);
            this.visibleChange?.emit(visible);
        });
    }, ...(ngDevMode ? [{ debugName: "#visibleInputEffect" }] : []));
    #activeElement = signal(null, ...(ngDevMode ? [{ debugName: "#activeElement" }] : []));
    #visibleEffect = effect(() => {
        const visible = this.visible();
        const afterViewInit = this.#afterViewInit();
        untracked(() => {
            if (visible && afterViewInit) {
                this.#activeElement.set(this.#document.activeElement);
                // this.#activeElement()?.blur();
                setTimeout(() => {
                    const focusable = this.modalContentRef()?.nativeElement.querySelectorAll('[tabindex]:not([tabindex="-1"]), button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
                    if (focusable?.length) {
                        this.#focusMonitor.focusVia(focusable[0], 'keyboard');
                    }
                });
            }
            else {
                const activeElement = this.#activeElement();
                if (activeElement && this.#document.contains(activeElement)) {
                    this.#focusMonitor.focusVia(activeElement, 'keyboard');
                    setTimeout(() => {
                        // this.#activeElement()?.focus();
                        this.#activeElement.set(null);
                    });
                }
            }
        });
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    /**
     * Event triggered on modal dismiss.
     * @return boolean
     */
    visibleChange = output();
    // @ViewChild(ModalContentComponent, { read: ElementRef }) modalContent!: ElementRef;
    // @ViewChild('modalContentRef', { read: ElementRef }) modalContentRef!: ElementRef;
    // readonly modalContentRef = viewChild(ModalContentComponent, { read: ElementRef });
    modalContentRef = viewChild('modalContentRef', { ...(ngDevMode ? { debugName: "modalContentRef" } : {}), read: ElementRef });
    modalDialogRef = viewChild.required(ModalDialogComponent, { read: ElementRef });
    #modalDialogEffect = effect((OnCleanup) => {
        const modalDialogElement = this.modalDialogRef().nativeElement;
        const removeEventListeners = () => {
            modalDialogElement?.removeEventListener('transitionend', this.#handleTransitionEnd);
        };
        OnCleanup(removeEventListeners);
        modalDialogElement?.addEventListener('transitionend', this.#handleTransitionEnd);
    }, ...(ngDevMode ? [{ debugName: "#modalDialogEffect" }] : []));
    #handleTransitionEnd = (event) => {
        const modalDialogElement = this.modalDialogRef().nativeElement;
        if (event.target === modalDialogElement && event.propertyName === 'transform') {
            if (!this.visible()) {
                this.#renderer.setStyle(this.#hostElement.nativeElement, 'display', 'none');
            }
        }
    };
    #activeBackdrop;
    // private inBoundingClientRect!: boolean;
    hostClasses = computed(() => {
        return {
            modal: true,
            fade: this.transition(),
            show: this.show
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ariaHidden = computed(() => {
        return this.visible() ? null : true;
    }, ...(ngDevMode ? [{ debugName: "ariaHidden" }] : []));
    get show() {
        return this.visible() && this.#show();
    }
    set show(value) {
        this.#show.set(value);
    }
    #show = signal(false, ...(ngDevMode ? [{ debugName: "#show" }] : []));
    animateStart() {
        if (this.visible()) {
            this.#backdropService.hideScrollbar();
            this.#renderer.setStyle(this.#hostElement.nativeElement, 'display', 'block');
        }
        else {
            if (!this.transition()) {
                this.#renderer.setStyle(this.#hostElement.nativeElement, 'display', 'none');
            }
        }
        setTimeout(() => {
            this.show = this.visible();
        });
    }
    onKeyUpHandler(event) {
        if (event.key === 'Escape' && this.keyboard() && this.visible()) {
            if (this.backdrop() === 'static') {
                this.setStaticBackdrop();
            }
            else {
                this.#modalService.toggle({ show: false, modal: this });
            }
        }
    }
    mouseDownTarget = null;
    onMouseDownHandler($event) {
        this.mouseDownTarget = $event.target;
    }
    onClickHandler($event) {
        if (this.mouseDownTarget !== $event.target) {
            this.mouseDownTarget = null;
            return;
        }
        const targetElement = $event.target;
        if (targetElement === this.#hostElement.nativeElement) {
            if (this.backdrop() === 'static') {
                this.setStaticBackdrop();
                return;
            }
            this.#modalService.toggle({ show: false, modal: this });
        }
    }
    ngOnInit() {
        this.stateToggleSubscribe();
    }
    #afterViewInit = signal(false, ...(ngDevMode ? [{ debugName: "#afterViewInit" }] : []));
    ngAfterViewInit() {
        this.#afterViewInit.set(true);
    }
    ngOnDestroy() {
        this.#modalService.toggle({ show: false, modal: this });
        this.#afterViewInit.set(false);
        this.setBackdrop(false);
    }
    stateToggleSubscribe() {
        this.#modalService.modalState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((action) => {
            if (this === action.modal || this.id === action.id) {
                if ('show' in action) {
                    this.visible.update((visible) => (action?.show === 'toggle' ? !visible : action.show));
                }
            }
            else {
                if (this.visible()) {
                    this.visible.set(false);
                }
            }
        });
    }
    setBackdrop(setBackdrop) {
        this.#activeBackdrop = setBackdrop
            ? this.#backdropService.setBackdrop('modal')
            : this.#backdropService.clearBackdrop(this.#activeBackdrop);
    }
    setBodyStyles(open) {
        if (open) {
            if (!!this.backdrop()) {
                this.#renderer.addClass(this.#document.body, 'modal-open');
            }
        }
        else {
            this.#renderer.removeClass(this.#document.body, 'modal-open');
        }
    }
    setStaticBackdrop() {
        if (this.transition()) {
            this.#renderer.addClass(this.#hostElement.nativeElement, 'modal-static');
            this.#renderer.setStyle(this.#hostElement.nativeElement, 'overflow-y', 'hidden');
            setTimeout(() => {
                this.#renderer.removeClass(this.#hostElement.nativeElement, 'modal-static');
                this.#renderer.removeStyle(this.#hostElement.nativeElement, 'overflow-y');
            }, 300);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: ModalComponent, isStandalone: true, selector: "c-modal", inputs: { alignment: { classPropertyName: "alignment", publicName: "alignment", isSignal: true, isRequired: false, transformFunction: null }, backdrop: { classPropertyName: "backdrop", publicName: "backdrop", isSignal: true, isRequired: false, transformFunction: null }, fullscreen: { classPropertyName: "fullscreen", publicName: "fullscreen", isSignal: true, isRequired: false, transformFunction: null }, keyboard: { classPropertyName: "keyboard", publicName: "keyboard", isSignal: true, isRequired: false, transformFunction: null }, attrId: { classPropertyName: "attrId", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, transition: { classPropertyName: "transition", publicName: "transition", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, ariaModalInput: { classPropertyName: "ariaModalInput", publicName: "ariaModal", isSignal: true, isRequired: false, transformFunction: null }, scrollable: { classPropertyName: "scrollable", publicName: "scrollable", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { listeners: { "mousedown": "onMouseDownHandler($event)", "click": "onClickHandler($event)", "document:keyup": "onKeyUpHandler($event)" }, properties: { "class": "hostClasses()", "attr.role": "visible() ? role() : null", "attr.inert": "ariaHidden()", "attr.id": "id", "attr.aria-modal": "ariaModal()", "attr.aria-hidden": "ariaHidden()", "attr.tabindex": "-1" }, classAttribute: "modal" }, viewQueries: [{ propertyName: "modalContentRef", first: true, predicate: ["modalContentRef"], descendants: true, read: ElementRef, isSignal: true }, { propertyName: "modalDialogRef", first: true, predicate: ModalDialogComponent, descendants: true, read: ElementRef, isSignal: true }], exportAs: ["cModal"], ngImport: i0, template: "@let isVisible = visible();\n<c-modal-dialog\n  [alignment]=\"alignment()\"\n  [fullscreen]=\"fullscreen()\"\n  [scrollable]=\"scrollable()\"\n  [size]=\"size()\">\n  <c-modal-content>\n    <div #modalContentRef [cdkTrapFocusAutoCapture]=\"isVisible\" [cdkTrapFocus]=\"isVisible\" style=\"display: contents;\">\n      <ng-content />\n    </div>\n  </c-modal-content>\n</c-modal-dialog>\n", dependencies: [{ kind: "component", type: ModalDialogComponent, selector: "c-modal-dialog", inputs: ["alignment", "fullscreen", "scrollable", "size"] }, { kind: "component", type: ModalContentComponent, selector: "c-modal-content" }, { kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i1$2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-modal', exportAs: 'cModal', imports: [ModalDialogComponent, ModalContentComponent, A11yModule], host: {
                        class: 'modal',
                        '[class]': 'hostClasses()',
                        '[attr.role]': 'visible() ? role() : null',
                        '[attr.inert]': 'ariaHidden()',
                        '[attr.id]': 'id',
                        '[attr.aria-modal]': 'ariaModal()',
                        '[attr.aria-hidden]': 'ariaHidden()',
                        '[attr.tabindex]': '-1',
                        '(mousedown)': 'onMouseDownHandler($event)',
                        '(click)': 'onClickHandler($event)',
                        '(document:keyup)': 'onKeyUpHandler($event)'
                    }, template: "@let isVisible = visible();\n<c-modal-dialog\n  [alignment]=\"alignment()\"\n  [fullscreen]=\"fullscreen()\"\n  [scrollable]=\"scrollable()\"\n  [size]=\"size()\">\n  <c-modal-content>\n    <div #modalContentRef [cdkTrapFocusAutoCapture]=\"isVisible\" [cdkTrapFocus]=\"isVisible\" style=\"display: contents;\">\n      <ng-content />\n    </div>\n  </c-modal-content>\n</c-modal-dialog>\n" }]
        }], propDecorators: { alignment: [{ type: i0.Input, args: [{ isSignal: true, alias: "alignment", required: false }] }], backdrop: [{ type: i0.Input, args: [{ isSignal: true, alias: "backdrop", required: false }] }], fullscreen: [{ type: i0.Input, args: [{ isSignal: true, alias: "fullscreen", required: false }] }], keyboard: [{ type: i0.Input, args: [{ isSignal: true, alias: "keyboard", required: false }] }], attrId: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], transition: [{ type: i0.Input, args: [{ isSignal: true, alias: "transition", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], ariaModalInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaModal", required: false }] }], scrollable: [{ type: i0.Input, args: [{ isSignal: true, alias: "scrollable", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], modalContentRef: [{ type: i0.ViewChild, args: ['modalContentRef', { ...{ read: ElementRef }, isSignal: true }] }], modalDialogRef: [{ type: i0.ViewChild, args: [i0.forwardRef(() => ModalDialogComponent), { ...{ read: ElementRef }, isSignal: true }] }] } });

class ModalModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ModalModule, imports: [ModalBodyComponent,
            ModalComponent,
            ModalContentComponent,
            ModalDialogComponent,
            ModalFooterComponent,
            ModalHeaderComponent,
            ModalTitleDirective,
            ModalToggleDirective], exports: [ModalBodyComponent,
            ModalComponent,
            ModalContentComponent,
            ModalDialogComponent,
            ModalFooterComponent,
            ModalHeaderComponent,
            ModalTitleDirective,
            ModalToggleDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalModule, providers: [ModalService], imports: [ModalComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ModalModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        ModalBodyComponent,
                        ModalComponent,
                        ModalContentComponent,
                        ModalDialogComponent,
                        ModalFooterComponent,
                        ModalHeaderComponent,
                        ModalTitleDirective,
                        ModalToggleDirective
                    ],
                    imports: [
                        ModalBodyComponent,
                        ModalComponent,
                        ModalContentComponent,
                        ModalDialogComponent,
                        ModalFooterComponent,
                        ModalHeaderComponent,
                        ModalTitleDirective,
                        ModalToggleDirective
                    ],
                    providers: [ModalService]
                }]
        }] });

class OffcanvasService {
    offcanvasState = new Subject();
    offcanvasState$ = this.offcanvasState.asObservable();
    toggle(action) {
        this.offcanvasState.next(action);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

let nextId$2 = 0;
class OffcanvasComponent {
    #document = inject(DOCUMENT);
    #platformId = inject(PLATFORM_ID);
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #offcanvasService = inject(OffcanvasService);
    #backdropService = inject(BackdropService);
    #breakpointObserver = inject(BreakpointObserver);
    #destroyRef = inject(DestroyRef);
    /**
     * Apply a backdrop on body while offcanvas is open.
     * @return boolean | 'static'
     * @default true
     */
    backdrop = input(true, ...(ngDevMode ? [{ debugName: "backdrop" }] : []));
    /**
     * Closes the offcanvas when escape key is pressed [docs]
     * @return boolean
     * @default true
     */
    keyboard = input(true, { ...(ngDevMode ? { debugName: "keyboard" } : {}), transform: booleanAttribute });
    /**
     * Components placement, there’s no default placement.
     * @return {'start' | 'end' | 'top' | 'bottom'}
     * @default 'start'
     */
    placement = input('start', ...(ngDevMode ? [{ debugName: "placement" }] : []));
    /**
     * Responsive offcanvas property hides content outside the viewport from a specified breakpoint and down.
     * @return boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
     * @default true
     * @since 4.3.10
     */
    responsive = input(true, ...(ngDevMode ? [{ debugName: "responsive" }] : []));
    id = input(`offcanvas-${this.placement()}-${nextId$2++}`, ...(ngDevMode ? [{ debugName: "id" }] : []));
    /**
     * Default role for offcanvas. [docs]
     * @return string
     * @default 'dialog'
     */
    role = input('dialog', ...(ngDevMode ? [{ debugName: "role" }] : []));
    /**
     * Set aria-modal html attr for offcanvas. [docs]
     * @return boolean
     * @default true
     */
    ariaModal = input(true, { ...(ngDevMode ? { debugName: "ariaModal" } : {}), transform: booleanAttribute });
    #activeBackdrop;
    #backdropClickSubscription;
    #layoutChangeSubscription;
    /**
     * Allow body scrolling while offcanvas is visible.
     * @return boolean
     * @default false
     */
    scroll = input(false, { ...(ngDevMode ? { debugName: "scroll" } : {}), transform: booleanAttribute });
    /**
     * Toggle the visibility of offcanvas component.
     * @return boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    visible = linkedSignal({ ...(ngDevMode ? { debugName: "visible" } : {}), source: this.visibleInput,
        computation: (value) => value });
    visibleEffect = effect(() => {
        const visible = this.visible();
        this.animateStart(visible);
        if (visible) {
            this.setBackdrop(this.backdrop());
            this.setFocus();
        }
        else {
            this.setBackdrop(false);
        }
        this.layoutChangeSubscribe(visible);
        this.visibleChange?.emit(visible);
    }, ...(ngDevMode ? [{ debugName: "visibleEffect" }] : []));
    /**
     * Event triggered on visible change.
     * @return <boolean>
     */
    visibleChange = output();
    hostClasses = computed(() => {
        const responsive = this.responsive();
        const placement = this.placement();
        const visible = this.visible();
        return {
            offcanvas: typeof responsive === 'boolean',
            [`offcanvas-${responsive}`]: typeof responsive !== 'boolean',
            [`offcanvas-${placement}`]: !!placement,
            show: visible
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ariaHidden = computed(() => {
        return this.visible() ? null : true;
    }, ...(ngDevMode ? [{ debugName: "ariaHidden" }] : []));
    get tabIndex() {
        return '-1';
    }
    get show() {
        return this.visible();
    }
    set show(value) {
        this.visible.set(value);
    }
    get responsiveBreakpoint() {
        const responsive = this.responsive();
        if (typeof responsive !== 'string') {
            return false;
        }
        const element = this.#document.documentElement;
        const breakpointValue = this.#document.defaultView
            ?.getComputedStyle(element)
            ?.getPropertyValue(`--cui-breakpoint-${responsive.trim()}`) ?? false;
        return breakpointValue ? `${parseFloat(breakpointValue.trim()) - 0.02}px` : false;
    }
    animateStart(visible = this.visible()) {
        if (visible) {
            if (!this.scroll()) {
                this.#backdropService.hideScrollbar();
            }
            this.#renderer.removeClass(this.#hostElement.nativeElement, 'hiding');
            this.#renderer.addClass(this.#hostElement.nativeElement, 'showing');
        }
        else {
            this.#renderer.removeClass(this.#hostElement.nativeElement, 'showing');
            this.#renderer.addClass(this.#hostElement.nativeElement, 'hiding');
        }
    }
    onKeyDownHandler(event) {
        if (event.key === 'Escape' && this.keyboard() && this.visible() && this.backdrop() !== 'static') {
            this.#offcanvasService.toggle({ show: false, id: this.id() });
        }
    }
    ngOnInit() {
        this.stateToggleSubscribe();
        setTimeout(() => {
            // hotfix to avoid offcanvas flicker on the first render
            this.#renderer.setStyle(this.#hostElement.nativeElement, 'display', 'flex');
        });
        this.#hostElement.nativeElement.addEventListener('transitionend', this.#handleTransitionEnd);
    }
    ngOnDestroy() {
        this.#offcanvasService.toggle({ show: false, id: this.id() });
        this.#removeEventListeners();
    }
    #removeEventListeners = () => {
        this.#hostElement.nativeElement.removeEventListener('transitionend', this.#handleTransitionEnd);
    };
    #handleTransitionEnd = (event) => {
        const offcanvasElement = this.#hostElement.nativeElement;
        if (event.target === offcanvasElement && event.propertyName === 'transform') {
            if (this.visible()) {
                this.#renderer.removeClass(offcanvasElement, 'showing');
            }
            else {
                this.#renderer.removeClass(offcanvasElement, 'hiding');
                this.#renderer.removeStyle(this.#document.body, 'overflow');
                this.#renderer.removeStyle(this.#document.body, 'paddingRight');
            }
        }
    };
    setFocus() {
        if (isPlatformBrowser(this.#platformId)) {
            setTimeout(() => this.#hostElement.nativeElement.focus());
        }
    }
    stateToggleSubscribe() {
        this.#offcanvasService.offcanvasState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((action) => {
            if (this === action.offcanvas || this.id() === action.id) {
                if ('show' in action) {
                    this.visible.update((value) => (action?.show === 'toggle' ? !value : action.show));
                }
            }
        });
    }
    backdropClickSubscribe(subscribe = true) {
        if (subscribe) {
            this.#backdropClickSubscription = this.#backdropService.backdropClick$
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe((clicked) => {
                this.#offcanvasService.toggle({ show: !clicked, id: this.id() });
            });
        }
        else {
            this.#backdropClickSubscription?.unsubscribe();
        }
    }
    setBackdrop(setBackdrop) {
        this.#activeBackdrop = !!setBackdrop
            ? this.#backdropService.setBackdrop('offcanvas')
            : this.#backdropService.clearBackdrop(this.#activeBackdrop);
        setBackdrop === true ? this.backdropClickSubscribe() : this.backdropClickSubscribe(false);
    }
    layoutChangeSubscribe(subscribe = true) {
        if (subscribe) {
            if (!this.responsiveBreakpoint) {
                return;
            }
            const responsiveBreakpoint = `(max-width: ${this.responsiveBreakpoint})`;
            const layoutChanges = this.#breakpointObserver.observe([responsiveBreakpoint]);
            this.#layoutChangeSubscription = layoutChanges
                .pipe(filter((breakpointState) => !breakpointState.matches), takeUntilDestroyed(this.#destroyRef))
                .subscribe((breakpointState) => {
                this.visible.set(breakpointState.matches);
            });
        }
        else {
            this.#layoutChangeSubscription?.unsubscribe();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: OffcanvasComponent, isStandalone: true, selector: "c-offcanvas", inputs: { backdrop: { classPropertyName: "backdrop", publicName: "backdrop", isSignal: true, isRequired: false, transformFunction: null }, keyboard: { classPropertyName: "keyboard", publicName: "keyboard", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, responsive: { classPropertyName: "responsive", publicName: "responsive", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, ariaModal: { classPropertyName: "ariaModal", publicName: "ariaModal", isSignal: true, isRequired: false, transformFunction: null }, scroll: { classPropertyName: "scroll", publicName: "scroll", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { attributes: { "ngSkipHydration": "true" }, listeners: { "document:keydown": "onKeyDownHandler($event)" }, properties: { "attr.id": "id()", "attr.inert": "ariaHidden() || null", "attr.role": "role()", "attr.aria-modal": "ariaModal()", "attr.tabindex": "tabIndex", "class": "hostClasses()" } }, exportAs: ["cOffcanvas"], hostDirectives: [{ directive: ThemeDirective, inputs: ["dark", "dark"] }], ngImport: i0, template: "<div cdkTrapFocus cdkTrapFocusAutoCapture>\n  <ng-content />\n</div>\n\n", styles: [":host{display:none}\n"], dependencies: [{ kind: "ngmodule", type: A11yModule }, { kind: "directive", type: i1$2.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-offcanvas', exportAs: 'cOffcanvas', imports: [A11yModule], hostDirectives: [{ directive: ThemeDirective, inputs: ['dark'] }], host: {
                        ngSkipHydration: 'true',
                        '[attr.id]': 'id()',
                        '[attr.inert]': 'ariaHidden() || null',
                        '[attr.role]': 'role()',
                        '[attr.aria-modal]': 'ariaModal()',
                        '[attr.tabindex]': 'tabIndex',
                        '[class]': 'hostClasses()',
                        '(document:keydown)': 'onKeyDownHandler($event)'
                    }, template: "<div cdkTrapFocus cdkTrapFocusAutoCapture>\n  <ng-content />\n</div>\n\n", styles: [":host{display:none}\n"] }]
        }], propDecorators: { backdrop: [{ type: i0.Input, args: [{ isSignal: true, alias: "backdrop", required: false }] }], keyboard: [{ type: i0.Input, args: [{ isSignal: true, alias: "keyboard", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], responsive: [{ type: i0.Input, args: [{ isSignal: true, alias: "responsive", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], ariaModal: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaModal", required: false }] }], scroll: [{ type: i0.Input, args: [{ isSignal: true, alias: "scroll", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }] } });

class OffcanvasBodyComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: OffcanvasBodyComponent, isStandalone: true, selector: "c-offcanvas-body", host: { classAttribute: "offcanvas-body" }, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-offcanvas-body', template: '<ng-content />', host: { class: 'offcanvas-body' }, styles: [":host{display:block}\n"] }]
        }] });

class OffcanvasHeaderComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: OffcanvasHeaderComponent, isStandalone: true, selector: "c-offcanvas-header", host: { classAttribute: "offcanvas-header" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-offcanvas-header',
                    template: '<ng-content />',
                    host: { class: 'offcanvas-header' }
                }]
        }] });

class OffcanvasTitleDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: OffcanvasTitleDirective, isStandalone: true, selector: "[cOffcanvasTitle]", host: { classAttribute: "offcanvas-title" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cOffcanvasTitle]',
                    host: { class: 'offcanvas-title' }
                }]
        }] });

class OffcanvasToggleDirective {
    #offcanvasService = inject(OffcanvasService);
    /**
     * Html id attr of offcanvas to toggle.
     * @return string
     */
    id = input(undefined, { ...(ngDevMode ? { debugName: "id" } : {}), alias: 'cOffcanvasToggle' });
    toggleOpen($event) {
        $event.preventDefault();
        this.#offcanvasService.toggle({ show: 'toggle', id: this.id() });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: OffcanvasToggleDirective, isStandalone: true, selector: "[cOffcanvasToggle]", inputs: { id: { classPropertyName: "id", publicName: "cOffcanvasToggle", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "toggleOpen($event)" } }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cOffcanvasToggle]',
                    host: {
                        '(click)': 'toggleOpen($event)'
                    }
                }]
        }], propDecorators: { id: [{ type: i0.Input, args: [{ isSignal: true, alias: "cOffcanvasToggle", required: false }] }] } });

class OffcanvasModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasModule, imports: [OffcanvasBodyComponent,
            OffcanvasComponent,
            OffcanvasHeaderComponent,
            OffcanvasTitleDirective,
            OffcanvasToggleDirective], exports: [OffcanvasBodyComponent,
            OffcanvasComponent,
            OffcanvasHeaderComponent,
            OffcanvasTitleDirective,
            OffcanvasToggleDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasModule, providers: [
            OffcanvasService,
            BackdropService
        ], imports: [OffcanvasComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OffcanvasModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        OffcanvasBodyComponent,
                        OffcanvasComponent,
                        OffcanvasHeaderComponent,
                        OffcanvasTitleDirective,
                        OffcanvasToggleDirective
                    ],
                    imports: [
                        OffcanvasBodyComponent,
                        OffcanvasComponent,
                        OffcanvasHeaderComponent,
                        OffcanvasTitleDirective,
                        OffcanvasToggleDirective
                    ],
                    providers: [
                        OffcanvasService,
                        BackdropService
                    ]
                }]
        }] });

class OtpService {
    disabled = signal(false, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    masked = signal(false, ...(ngDevMode ? [{ debugName: "masked" }] : []));
    placeholder = signal('', ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    readOnly = signal(false, ...(ngDevMode ? [{ debugName: "readOnly" }] : []));
    type = signal('number', ...(ngDevMode ? [{ debugName: "type" }] : []));
    valid = signal(undefined, ...(ngDevMode ? [{ debugName: "valid" }] : []));
    hostClasses = computed(() => {
        const valid = this.valid();
        return {
            'form-otp-control': true,
            'is-valid': valid === true,
            'is-invalid': valid === false,
            disabled: this.disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    inputMode = computed(() => (this.type() === 'number' ? 'numeric' : 'text'), ...(ngDevMode ? [{ debugName: "inputMode" }] : []));
    inputType = computed(() => (this.masked() ? 'password' : 'text'), ...(ngDevMode ? [{ debugName: "inputType" }] : []));
    pattern = computed(() => (this.type() === 'text' ? '.*' : '[0-9]*'), ...(ngDevMode ? [{ debugName: "pattern" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OtpService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OtpService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OtpService, decorators: [{
            type: Injectable
        }] });

class OtpDirective {
    hostElement = inject((ElementRef));
    #document = inject(DOCUMENT);
    #otpService = inject(OtpService);
    focusable = signal(false, ...(ngDevMode ? [{ debugName: "focusable" }] : []));
    placeholderInput = input('', { ...(ngDevMode ? { debugName: "placeholderInput" } : {}), alias: 'placeholder' });
    placeholder = computed(() => {
        const placeholder = this.placeholderInput() || this.#otpService.placeholder();
        const index = this.index() ?? 0;
        return placeholder.length > 1 ? placeholder?.charAt(index) : placeholder;
    }, ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    valueInput = input('', { ...(ngDevMode ? { debugName: "valueInput" } : {}), alias: 'value' });
    value = linkedSignal(this.valueInput, ...(ngDevMode ? [{ debugName: "value" }] : []));
    #valueEffect = effect(() => {
        const value = this.value() ?? '';
        this.hostElement.nativeElement.value = String(value);
    }, ...(ngDevMode ? [{ debugName: "#valueEffect" }] : []));
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    #disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "#disabled" }] : []));
    #disabledEffect = effect(() => {
        this.hostElement.nativeElement.disabled = this.#disabled();
    }, ...(ngDevMode ? [{ debugName: "#disabledEffect" }] : []));
    set disabled(disabled) {
        this.#disabled.set(disabled);
    }
    get disabled() {
        return this.hostElement.nativeElement.disabled;
    }
    tabIndex = signal(0, ...(ngDevMode ? [{ debugName: "tabIndex" }] : []));
    index = signal(undefined, ...(ngDevMode ? [{ debugName: "index" }] : []));
    focus(origin) {
        if (!this.disabled) {
            this.hostElement.nativeElement.focus();
            this.focusable.set(true);
        }
        // console.log('focus called with origin:', origin, this.index());
    }
    #otpServiceEffect = effect(() => {
        this.#disabled.set(this.#otpService.disabled());
    }, ...(ngDevMode ? [{ debugName: "#otpServiceEffect" }] : []));
    hostClasses = this.#otpService.hostClasses;
    inputMode = this.#otpService.inputMode;
    inputType = this.#otpService.inputType;
    pattern = this.#otpService.pattern;
    readOnly = this.#otpService.readOnly;
    #focusableEffect = effect(() => {
        this.tabIndex.set(this.focusable() && !this.disabled ? 0 : -1);
    }, ...(ngDevMode ? [{ debugName: "#focusableEffect" }] : []));
    handleSelectionChange($event) {
        const inputElement = this.hostElement.nativeElement;
        if (inputElement.value !== '' && this.#document.activeElement === inputElement) {
            if (inputElement.selectionStart === inputElement.selectionEnd) {
                setTimeout(() => {
                    inputElement.select();
                });
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OtpDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: OtpDirective, isStandalone: true, selector: "input[cOtp]", inputs: { placeholderInput: { classPropertyName: "placeholderInput", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, valueInput: { classPropertyName: "valueInput", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "selectionchange": "handleSelectionChange($event)" }, properties: { "class": "hostClasses()", "attr.inputMode": "inputMode()", "attr.maxLength": "1", "autocomplete": "\"one-time-code\"", "attr.pattern": "pattern()", "placeholder": "placeholder()", "readOnly": "readOnly()", "tabIndex": "tabIndex()", "attr.type": "inputType()" }, classAttribute: "form-otp-control" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OtpDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cOtp]',
                    host: {
                        '(selectionchange)': 'handleSelectionChange($event)',
                        class: 'form-otp-control',
                        '[class]': 'hostClasses()',
                        '[attr.inputMode]': 'inputMode()',
                        '[attr.maxLength]': '1',
                        '[autocomplete]': '"one-time-code"',
                        '[attr.pattern]': 'pattern()',
                        '[placeholder]': 'placeholder()',
                        '[readOnly]': 'readOnly()',
                        '[tabIndex]': 'tabIndex()',
                        '[attr.type]': 'inputType()'
                        // '[value]': 'value()'
                    }
                }]
        }], propDecorators: { placeholderInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], valueInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }] } });

const isValidInput = (inputValue, type) => {
    if (inputValue.length !== 1)
        return false;
    switch (type) {
        case 'number': {
            return /^\d$/.test(inputValue);
        }
        default: {
            return /^.$/.test(inputValue);
        }
    }
};
const extractValidChars = (text, type) => {
    switch (type) {
        case 'number': {
            return text.replaceAll(/\D/g, '');
        }
        default: {
            return text;
        }
    }
};

class OneTimePasswordComponent {
    static ngAcceptInputType_autoSubmit;
    static ngAcceptInputType_disabledInput;
    static ngAcceptInputType_masked;
    static ngAcceptInputType_readOnly;
    writeValue(value) {
        const valueStr = value?.toString() || '';
        this.#inputValues.set(valueStr.split('').slice(0, this.inputCount() || valueStr.length));
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled.set(isDisabled);
    }
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    #destroyRef = inject(DestroyRef);
    #otpService = inject(OtpService);
    #hostElement = inject(ElementRef);
    #rtlService = inject(RtlService);
    #isRtl = signal(false, ...(ngDevMode ? [{ debugName: "#isRtl" }] : []));
    constructor() {
        afterEveryRender({
            read: () => {
                this.#isRtl.set(this.#rtlService.isRTL(this.#hostElement.nativeElement));
            }
        });
    }
    /**
     * Function to generate aria-label for each input field. Receives current index (0-based) and total number of inputs.
     * @default: (index, total) => `Digit ${index + 1} of ${total}`
     */
    ariaLabel = input((index, total) => `Digit ${index + 1} of ${total}`, ...(ngDevMode ? [{ debugName: "ariaLabel" }] : []));
    /**
     * Automatically submit the form when all one time password fields are filled.
     * @default: false
     */
    autoSubmit = input(false, { ...(ngDevMode ? { debugName: "autoSubmit" } : {}), transform: booleanAttribute });
    // readonly children = input();
    /**
     * Initial value for Angular one time password input.
     * @default: undefined
     */
    defaultValue = input(...(ngDevMode ? [undefined, { debugName: "defaultValue" }] : []));
    /**
     * Disable all one time password (OTP) input fields.
     * @default: false
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "disabled" }] : []));
    /**
     * Enforce sequential input (users must fill fields in order).
     * @default: true
     */
    linear = input(true, { ...(ngDevMode ? { debugName: "linear" } : {}), transform: booleanAttribute });
    /**
     * Show input as password (masked characters).
     * @default: false
     */
    masked = input(false, { ...(ngDevMode ? { debugName: "masked" } : {}), transform: booleanAttribute });
    /**
     * Placeholder text for input fields. Single character applies to all fields, longer strings apply character-by-character.
     * @default: undefined
     */
    placeholder = input('', ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    /**
     * Make Angular OTP input component read-only.
     * @default: false
     */
    readOnly = input(false, { ...(ngDevMode ? { debugName: "readOnly" } : {}), transform: booleanAttribute });
    /**
     * Sets the visual size of the React.js one time password (OTP) input. Use 'sm' for small or 'lg' for large input fields.
     * @default: undefined
     */
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    /**
     * Input validation type: 'number' for digits only, or 'text' for free text.
     * @default: 'number'
     */
    type = input('number', ...(ngDevMode ? [{ debugName: "type" }] : []));
    /**
     * Set form input validation state to valid.
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Current value for OTP input.
     * @default: undefined
     */
    valueInput = input(undefined, { ...(ngDevMode ? { debugName: "valueInput" } : {}), alias: 'value' });
    value = linkedSignal(() => this.valueInput()?.toString() ?? '', ...(ngDevMode ? [{ debugName: "value" }] : []));
    #valueEffect = effect(() => {
        const value = this.#inputValues().join(''); // non-breaking space to empty
        untracked(() => {
            setTimeout(() => {
                if (!this.#destroyRef.destroyed) {
                    this.valueChange?.emit(value);
                }
                this.onChange(value);
                if (value.length === this.inputCount()) {
                    // this.value.set(value);
                    if (!this.#destroyRef.destroyed) {
                        this.complete?.emit(value);
                    }
                    if (this.autoSubmit()) {
                        const form = this.#hostElement.nativeElement.closest('form');
                        if (typeof form?.requestSubmit === 'function') {
                            form.requestSubmit();
                        }
                    }
                }
            });
        });
    }, ...(ngDevMode ? [{ debugName: "#valueEffect" }] : []));
    /**
     * Event triggered when the Angular one time password (OTP) value changes.
     */
    valueChange = output();
    /**
     * Callback triggered when all Angular one time password (OTP) fields are filled.
     */
    complete = output();
    #otpServiceEffect = effect(() => {
        this.#otpService.disabled.set(this.disabled());
        this.#otpService.masked.set(this.masked());
        this.#otpService.placeholder.set(this.placeholder());
        this.#otpService.readOnly.set(this.readOnly());
        this.#otpService.type.set(this.type());
        this.#otpService.valid.set(this.valid());
    }, ...(ngDevMode ? [{ debugName: "#otpServiceEffect" }] : []));
    hostClasses = computed(() => {
        const sizing = this.sizing();
        const valid = this.valid();
        return {
            'form-otp': true,
            [`form-otp-${sizing}`]: !!sizing,
            'is-valid': valid === true,
            'is-invalid': valid === false
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    // protected readonly inputElementRefs = contentChildren(OtpDirective, { read: ElementRef<HTMLInputElement>, descendants: true });
    inputRefs = contentChildren(OtpDirective, { ...(ngDevMode ? { debugName: "inputRefs" } : {}), descendants: true });
    inputCount = computed(() => this.inputRefs().length, ...(ngDevMode ? [{ debugName: "inputCount" }] : []));
    #events$ = [];
    #inputRefEffect = effect(() => {
        this.#events$.forEach((subscription) => {
            subscription?.unsubscribe();
        });
        const inputRefs = this.inputRefs();
        inputRefs.forEach((inputRef, index) => {
            inputRef.index.set(index);
            this.#events$.push(fromEvent(inputRef.hostElement.nativeElement, 'focus')
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe(($event) => {
                this.handleInputFocus($event, index);
            }));
            this.#events$.push(fromEvent(inputRef.hostElement.nativeElement, 'input')
                .pipe(takeUntilDestroyed(this.#destroyRef))
                .subscribe(($event) => {
                this.handleInputChange($event, index);
            }));
            this.#events$.push(fromEvent(inputRef.hostElement.nativeElement, 'paste')
                .pipe(takeUntilDestroyed(this.#destroyRef), tap(($event) => $event.preventDefault()))
                .subscribe(($event) => {
                this.handlePaste($event, index);
            }));
            this.#events$.push(fromEvent(inputRef.hostElement.nativeElement, 'keydown')
                .pipe(takeUntilDestroyed(this.#destroyRef), filter((keyEvent) => ['Backspace', 'ArrowRight', 'ArrowLeft', 'Tab'].includes(keyEvent.key)))
                .subscribe(($event) => {
                this.handleKeyDown($event, index);
            }));
        });
    }, ...(ngDevMode ? [{ debugName: "#inputRefEffect" }] : []));
    #focusKeyManager;
    #inputRefsEffect = effect(() => {
        if (untracked(this.disabled)) {
            return;
        }
        const isRtl = this.#isRtl();
        const inputRefs = this.inputRefs();
        this.#focusKeyManager?.destroy();
        this.#focusKeyManager = new FocusKeyManager(inputRefs)
            .withHorizontalOrientation(isRtl ? 'rtl' : 'ltr')
            .withVerticalOrientation(false)
            .skipPredicate((inputElement) => inputElement.disabled);
        this.#focusKeyManager?.change.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((changeIdx) => {
            inputRefs.forEach((inputRef, index) => {
                inputRef.focusable.set(index === changeIdx);
            });
        });
        this.#focusKeyManager?.tabOut.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
            if (!this.linear())
                return;
            const firstEmptyInput = this.inputRefs().find((input) => !input?.hostElement.nativeElement.value);
            if (firstEmptyInput) {
                this.#focusKeyManager.updateActiveItem(firstEmptyInput);
                this.#focusKeyManager?.change.next(firstEmptyInput.index() ?? 0);
            }
        });
        this.#focusKeyManager?.updateActiveItem(0);
        this.#focusKeyManager?.change.next(0);
    }, ...(ngDevMode ? [{ debugName: "#inputRefsEffect" }] : []));
    #inputValues = linkedSignal(() => {
        const initialValue = String(this.value() || untracked(this.defaultValue) || '');
        return Array.from({ length: this.inputCount() }, (_, i) => initialValue[i] || '');
    }, ...(ngDevMode ? [{ debugName: "#inputValues" }] : []));
    #inputsArrayEffect = effect(() => {
        const inputsArray = this.#inputValues();
        const inputRefs = this.inputRefs();
        inputRefs.forEach((inputRef, index) => {
            inputRef.index.set(index);
            inputRef.value.set(inputsArray[index]?.trim() || '');
        });
    }, ...(ngDevMode ? [{ debugName: "#inputsArrayEffect" }] : []));
    handleInputChange($event, index) {
        this.#focusKeyManager.updateActiveItem(index);
        const inputValue = $event.target.value;
        if (inputValue.length === 1 && !isValidInput(inputValue, this.type())) {
            $event.target.value = '';
            this.#inputValues.update((value) => {
                const chars = [...value];
                chars[index] = '';
                return chars;
            });
            return;
        }
        const newValues = [...this.#inputValues()];
        newValues[index] = inputValue.length === 1 ? inputValue : '';
        untracked(() => {
            this.#inputValues.set(newValues);
        });
        // handle focus to next input if needed
        if (inputValue.length === 1) {
            this.#focusKeyManager.setNextItemActive();
        }
    }
    handleInputFocus($event, index) {
        this.#focusKeyManager.updateActiveItem(index);
        const target = $event.target;
        if (target.value) {
            setTimeout(() => {
                target.select();
            }, 0);
            return;
        }
        if (this.linear()) {
            const firstEmptyInput = this.inputRefs().find((input) => !input?.hostElement.nativeElement.value);
            if (firstEmptyInput && firstEmptyInput.hostElement.nativeElement !== target) {
                this.#focusKeyManager.setActiveItem(firstEmptyInput);
            }
            return;
        }
        this.#focusKeyManager.setActiveItem(index);
    }
    handleKeyDown($event, index) {
        const { key, target } = $event;
        if (key === 'Tab') {
            this.#focusKeyManager.tabOut.next();
            return;
        }
        if (key === 'Backspace' && target.value === '') {
            const newValues = [...this.#inputValues()];
            if (index > 0) {
                newValues[index - 1] = '';
                this.#inputValues.set(newValues);
                setTimeout(() => {
                    this.#focusKeyManager.setPreviousItemActive();
                });
            }
            return;
        }
        if (key === 'ArrowRight') {
            if (!this.#isRtl() && this.linear() && target.value === '') {
                return;
            }
            !this.#isRtl() ? this.#focusKeyManager.setNextItemActive() : this.#focusKeyManager.setPreviousItemActive();
            return;
        }
        if (key === 'ArrowLeft') {
            if (this.#isRtl() && this.linear() && target.value === '') {
                return;
            }
            this.#isRtl() ? this.#focusKeyManager.setNextItemActive() : this.#focusKeyManager.setPreviousItemActive();
            return;
        }
    }
    handlePaste($event, index) {
        $event.preventDefault();
        const inputCount = this.inputCount();
        const pastedData = $event.clipboardData?.getData('text') ?? '';
        const validChars = extractValidChars(pastedData, this.type());
        if (!validChars)
            return;
        const newValues = [...this.#inputValues()];
        const startIndex = index;
        for (let i = 0; i < validChars.length && startIndex + i < inputCount; i++) {
            newValues[startIndex + i] = validChars[i];
        }
        // untracked(() => {
        this.#inputValues.set(newValues);
        // });
        setTimeout(() => {
            // Focus the next empty input or the last filled input
            const nextEmptyIndex = startIndex + validChars.length;
            if (nextEmptyIndex < inputCount) {
                this.#focusKeyManager.setActiveItem(nextEmptyIndex);
                // this.#focusKeyManager.setNextItemActive();
            }
            else {
                this.#focusKeyManager.setLastItemActive();
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: OneTimePasswordComponent, isStandalone: true, selector: "c-one-time-password", inputs: { ariaLabel: { classPropertyName: "ariaLabel", publicName: "ariaLabel", isSignal: true, isRequired: false, transformFunction: null }, autoSubmit: { classPropertyName: "autoSubmit", publicName: "autoSubmit", isSignal: true, isRequired: false, transformFunction: null }, defaultValue: { classPropertyName: "defaultValue", publicName: "defaultValue", isSignal: true, isRequired: false, transformFunction: null }, disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, linear: { classPropertyName: "linear", publicName: "linear", isSignal: true, isRequired: false, transformFunction: null }, masked: { classPropertyName: "masked", publicName: "masked", isSignal: true, isRequired: false, transformFunction: null }, placeholder: { classPropertyName: "placeholder", publicName: "placeholder", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null }, type: { classPropertyName: "type", publicName: "type", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, valueInput: { classPropertyName: "valueInput", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { valueChange: "valueChange", complete: "complete" }, host: { attributes: { "role": "group" }, properties: { "class": "hostClasses()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => OneTimePasswordComponent),
                multi: true
            },
            OtpService
        ], queries: [{ propertyName: "inputRefs", predicate: OtpDirective, descendants: true, isSignal: true }], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:flex}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-one-time-password', template: '<ng-content />', imports: [], host: {
                        '[class]': 'hostClasses()',
                        role: 'group'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => OneTimePasswordComponent),
                            multi: true
                        },
                        OtpService
                    ], styles: [":host{display:flex}\n"] }]
        }], ctorParameters: () => [], propDecorators: { ariaLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "ariaLabel", required: false }] }], autoSubmit: [{ type: i0.Input, args: [{ isSignal: true, alias: "autoSubmit", required: false }] }], defaultValue: [{ type: i0.Input, args: [{ isSignal: true, alias: "defaultValue", required: false }] }], disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], linear: [{ type: i0.Input, args: [{ isSignal: true, alias: "linear", required: false }] }], masked: [{ type: i0.Input, args: [{ isSignal: true, alias: "masked", required: false }] }], placeholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "placeholder", required: false }] }], readOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "readOnly", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }], type: [{ type: i0.Input, args: [{ isSignal: true, alias: "type", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], valueInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], complete: [{ type: i0.Output, args: ["complete"] }], inputRefs: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => OtpDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class OneTimePasswordModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordModule, imports: [OneTimePasswordComponent, OtpDirective], exports: [OneTimePasswordComponent, OtpDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: OneTimePasswordModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [OneTimePasswordComponent, OtpDirective],
                    exports: [OneTimePasswordComponent, OtpDirective]
                }]
        }] });

// export * from './one-time-password.types';

class PageLinkDirective {
    static ngAcceptInputType_disabled;
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            'page-link': true,
            disabled: this.disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageLinkDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: PageLinkDirective, isStandalone: true, selector: "[cPageLink]", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "page-link" }, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cPageLink]',
                    host: { class: 'page-link', '[class]': 'hostClasses()' }
                }]
        }], propDecorators: { disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }] } });

class PageItemDirective {
    static ngAcceptInputType_disabled;
    #renderer = inject(Renderer2);
    /**
     * Toggle the active state for the component.
     * @return boolean
     */
    active = input(...(ngDevMode ? [undefined, { debugName: "active" }] : []));
    /**
     * Toggle the disabled state for the component.
     * @return boolean
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    ariaCurrent = computed(() => {
        return this.active() ? 'page' : null;
    }, ...(ngDevMode ? [{ debugName: "ariaCurrent" }] : []));
    hostClasses = computed(() => {
        return {
            'page-item': true,
            disabled: this.disabled(),
            active: this.active()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    pageLinkElementRef = contentChild(PageLinkDirective, { ...(ngDevMode ? { debugName: "pageLinkElementRef" } : {}), read: ElementRef });
    pageLinkElementRefEffect = effect(() => {
        const pageLinkElementRef = this.pageLinkElementRef();
        const disabled = this.disabled();
        if (!pageLinkElementRef) {
            return;
        }
        const pageLinkElement = pageLinkElementRef.nativeElement;
        if (disabled) {
            this.#renderer.setAttribute(pageLinkElement, 'aria-disabled', 'true');
            this.#renderer.setAttribute(pageLinkElement, 'tabindex', '-1');
        }
        else {
            this.#renderer.removeAttribute(pageLinkElement, 'aria-disabled');
            this.#renderer.removeAttribute(pageLinkElement, 'tabindex');
        }
    }, ...(ngDevMode ? [{ debugName: "pageLinkElementRefEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageItemDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.2.0", version: "21.1.4", type: PageItemDirective, isStandalone: true, selector: "[cPageItem]", inputs: { active: { classPropertyName: "active", publicName: "active", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-current": "ariaCurrent()" }, classAttribute: "page-item" }, queries: [{ propertyName: "pageLinkElementRef", first: true, predicate: PageLinkDirective, descendants: true, read: ElementRef, isSignal: true }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cPageItem]',
                    host: {
                        class: 'page-item',
                        '[class]': 'hostClasses()',
                        '[attr.aria-current]': 'ariaCurrent()'
                    }
                }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "active", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], pageLinkElementRef: [{ type: i0.ContentChild, args: [i0.forwardRef(() => PageLinkDirective), { ...{ read: ElementRef }, isSignal: true }] }] } });

class PageItemComponent extends PageItemDirective {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageItemComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: PageItemComponent, isStandalone: true, selector: "c-page-item", usesInheritance: true, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:list-item;text-align:-webkit-match-parent;text-align:match-parent}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PageItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-page-item', template: '<ng-content />', styles: [":host{display:list-item;text-align:-webkit-match-parent;text-align:match-parent}\n"] }]
        }] });

class PaginationComponent {
    /**
     * Set the alignment of pagination components.
     * @values 'start', 'center', 'end'
     */
    align = input('', ...(ngDevMode ? [{ debugName: "align" }] : []));
    /**
     * Size the component small or large.
     * @values 'sm', 'lg'
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Default role for pagination. [docs]
     * @return string
     * @default 'navigation'
     */
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    paginationClass = computed(() => {
        const size = this.size();
        const align = this.align();
        return {
            pagination: true,
            [`pagination-${size}`]: !!size,
            [`justify-content-${align}`]: !!align
        };
    }, ...(ngDevMode ? [{ debugName: "paginationClass" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: PaginationComponent, isStandalone: true, selector: "c-pagination", inputs: { align: { classPropertyName: "align", publicName: "align", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()" } }, ngImport: i0, template: "<ul [class]=\"paginationClass()\">\n  <ng-content />\n</ul>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-pagination', host: {
                        '[attr.role]': 'role()'
                    }, template: "<ul [class]=\"paginationClass()\">\n  <ng-content />\n</ul>\n" }]
        }], propDecorators: { align: [{ type: i0.Input, args: [{ isSignal: true, alias: "align", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class PaginationModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: PaginationModule, imports: [PageItemComponent,
            PageItemDirective,
            PageLinkDirective,
            PaginationComponent], exports: [PageItemComponent,
            PageItemDirective,
            PageLinkDirective,
            PaginationComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PaginationModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        PageItemComponent,
                        PageItemDirective,
                        PageLinkDirective,
                        PaginationComponent
                    ],
                    imports: [
                        PageItemComponent,
                        PageItemDirective,
                        PageLinkDirective,
                        PaginationComponent
                    ]
                }]
        }] });

class PlaceholderDirective {
    /**
     * placeholder toggler
     * @type boolean
     * @default false
     */
    visible = input(false, { ...(ngDevMode ? { debugName: "visible" } : {}), transform: booleanAttribute,
        alias: 'cPlaceholder' });
    /**
     * Size the placeholder xs, small, large.
     */
    size = input(undefined, { ...(ngDevMode ? { debugName: "size" } : {}), alias: 'cPlaceholderSize' });
    ariaHidden = computed(() => {
        return this.visible() ? null : true;
    }, ...(ngDevMode ? [{ debugName: "ariaHidden" }] : []));
    hostClasses = computed(() => {
        const size = this.size();
        return {
            placeholder: this.visible(),
            [`placeholder-${size}`]: !!size
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: PlaceholderDirective, isStandalone: true, selector: "[cPlaceholder]", inputs: { visible: { classPropertyName: "visible", publicName: "cPlaceholder", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "cPlaceholderSize", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.aria-hidden": "ariaHidden()" } }, exportAs: ["cPlaceholder"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cPlaceholder]',
                    exportAs: 'cPlaceholder',
                    host: {
                        '[class]': 'hostClasses()',
                        '[attr.aria-hidden]': 'ariaHidden()'
                    }
                }]
        }], propDecorators: { visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPlaceholder", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPlaceholderSize", required: false }] }] } });

class PlaceholderAnimationDirective {
    /**
     * Animation type for placeholder
     * @type 'glow' | 'wave'
     * @default undefined
     */
    animation = input(undefined, { ...(ngDevMode ? { debugName: "animation" } : {}), alias: 'cPlaceholderAnimation' });
    placeholder = contentChild(PlaceholderDirective, ...(ngDevMode ? [{ debugName: "placeholder" }] : []));
    hostClasses = computed(() => {
        const animation = this.animation();
        return {
            [`placeholder-${animation}`]: this.placeholder()?.visible() && !!animation
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderAnimationDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.2.0", version: "21.1.4", type: PlaceholderAnimationDirective, isStandalone: true, selector: "[cPlaceholderAnimation]", inputs: { animation: { classPropertyName: "animation", publicName: "cPlaceholderAnimation", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, queries: [{ propertyName: "placeholder", first: true, predicate: PlaceholderDirective, descendants: true, isSignal: true }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderAnimationDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cPlaceholderAnimation]',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { animation: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPlaceholderAnimation", required: false }] }], placeholder: [{ type: i0.ContentChild, args: [i0.forwardRef(() => PlaceholderDirective), { isSignal: true }] }] } });

class PlaceholderModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderModule, imports: [PlaceholderDirective,
            PlaceholderAnimationDirective], exports: [PlaceholderDirective,
            PlaceholderAnimationDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PlaceholderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        PlaceholderDirective,
                        PlaceholderAnimationDirective
                    ],
                    exports: [
                        PlaceholderDirective,
                        PlaceholderAnimationDirective
                    ]
                }]
        }] });

class PopoverComponent {
    renderer = inject(Renderer2);
    /**
     * Content of popover
     * @return {string | TemplateRef}
     */
    content = input('', ...(ngDevMode ? [{ debugName: "content" }] : []));
    #contentEffect = effect(() => {
        this.updateView(this.content());
    }, ...(ngDevMode ? [{ debugName: "#contentEffect" }] : []));
    /**
     * Toggle the visibility of popover component.
     * @return boolean
     */
    visible = input(false, { ...(ngDevMode ? { debugName: "visible" } : {}), transform: booleanAttribute });
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    role = input('tooltip', ...(ngDevMode ? [{ debugName: "role" }] : []));
    viewContainerRef = viewChild('popoverTemplate', { ...(ngDevMode ? { debugName: "viewContainerRef" } : {}), read: ViewContainerRef });
    textNode;
    hostClasses = computed(() => {
        return {
            popover: true,
            fade: true,
            show: this.visible(),
            'bs-popover-auto': true
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnDestroy() {
        this.clear();
    }
    clear() {
        this.viewContainerRef()?.clear();
        if (!!this.textNode) {
            this.renderer.removeChild(this.textNode.parentNode, this.textNode);
        }
    }
    updateView(content) {
        this.clear();
        if (!content) {
            return;
        }
        if (content instanceof TemplateRef) {
            this.viewContainerRef()?.createEmbeddedView(content);
        }
        else {
            const textNodeContent = this.renderer.createText(content);
            this.textNode = this.renderer.createElement('div');
            this.renderer.addClass(this.textNode, 'popover-body');
            this.renderer.appendChild(this.textNode, textNodeContent);
            const element = this.viewContainerRef()?.element.nativeElement;
            this.renderer.appendChild(element.parentNode, this.textNode);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: PopoverComponent, isStandalone: true, selector: "c-popover", inputs: { content: { classPropertyName: "content", publicName: "content", isSignal: true, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.role": "role()", "attr.id": "id()" }, classAttribute: "popover fade bs-popover-auto" }, viewQueries: [{ propertyName: "viewContainerRef", first: true, predicate: ["popoverTemplate"], descendants: true, read: ViewContainerRef, isSignal: true }], ngImport: i0, template: "<ng-container>\n  <div [class.popover-arrow]=\"!!content()\" data-popper-arrow></div>\n  <ng-container #popoverTemplate />\n</ng-container>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-popover', host: {
                        class: 'popover fade bs-popover-auto',
                        '[class]': 'hostClasses()',
                        '[attr.role]': 'role()',
                        '[attr.id]': 'id()'
                    }, template: "<ng-container>\n  <div [class.popover-arrow]=\"!!content()\" data-popper-arrow></div>\n  <ng-container #popoverTemplate />\n</ng-container>\n" }]
        }], propDecorators: { content: [{ type: i0.Input, args: [{ isSignal: true, alias: "content", required: false }] }], visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], viewContainerRef: [{ type: i0.ViewChild, args: ['popoverTemplate', { ...{ read: ViewContainerRef }, isSignal: true }] }] } });

class PopoverDirective {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #viewContainerRef = inject(ViewContainerRef);
    #listenersService = inject(ListenersService);
    #changeDetectorRef = inject(ChangeDetectorRef);
    #intersectionService = inject(IntersectionService);
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #timerId1;
    #timerId2;
    /**
     * Content of popover
     * @return {string | TemplateRef}
     */
    content = input(undefined, { ...(ngDevMode ? { debugName: "content" } : {}), alias: 'cPopover' });
    #contentEffect = effect(() => {
        if (this.content()) {
            this.destroyTooltipElement();
        }
    }, ...(ngDevMode ? [{ debugName: "#contentEffect" }] : []));
    /**
     * Optional popper Options object, takes precedence over cPopoverPlacement prop
     * @return Partial<Options>
     */
    popperOptions = input({}, { ...(ngDevMode ? { debugName: "popperOptions" } : {}), alias: 'cPopoverOptions' });
    #popperOptionsEffect = effect(() => {
        this._popperOptions = {
            ...this._popperOptions,
            placement: this.placement(),
            ...this.popperOptions()
        };
    }, ...(ngDevMode ? [{ debugName: "#popperOptionsEffect" }] : []));
    popperOptionsComputed = computed(() => {
        return { placement: this.placement(), ...this._popperOptions };
    }, ...(ngDevMode ? [{ debugName: "popperOptionsComputed" }] : []));
    /**
     * Describes the placement of your component after Popper.js has applied all the modifiers that may have flipped or altered the originally provided placement property.
     * @return: 'top' | 'bottom' | 'left' | 'right'
     * @default: 'top'
     */
    placement = input('top', { ...(ngDevMode ? { debugName: "placement" } : {}), alias: 'cPopoverPlacement' });
    /**
     * ElementRefDirective for positioning the tooltip on reference element
     * @return: ElementRefDirective
     * @default: undefined
     */
    reference = input(undefined, { ...(ngDevMode ? { debugName: "reference" } : {}), alias: 'cTooltipRef' });
    referenceRef = computed(() => this.reference()?.elementRef ?? this.#hostElement, ...(ngDevMode ? [{ debugName: "referenceRef" }] : []));
    /**
     * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
     * @return: Triggers | Triggers[]
     */
    trigger = input('hover', { ...(ngDevMode ? { debugName: "trigger" } : {}), alias: 'cPopoverTrigger' });
    /**
     * Toggle the visibility of popover component.
     * @return boolean
     */
    visible = model(false, { ...(ngDevMode ? { debugName: "visible" } : {}), alias: 'cPopoverVisible' });
    #visibleEffect = afterRenderEffect({
        // this fixes RuntimeError: NG0500: During hydration Angular expected <abc> but found <xyz>.
        // Find more at https://angular.dev/errors/NG0500
        write: () => {
            this.visible() ? this.addTooltipElement() : this.removeTooltipElement();
        }
    });
    get ariaDescribedBy() {
        return this.tooltipId ? this.tooltipId : null;
    }
    tooltip;
    tooltipId;
    tooltipRef;
    popperInstance;
    _popperOptions = {
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 9]
                }
            }
        ]
    };
    ngAfterViewInit() {
        this.intersectionServiceSubscribe();
    }
    ngOnDestroy() {
        this.clearListeners();
        this.destroyTooltipElement();
    }
    ngOnInit() {
        this.setListeners();
    }
    setListeners() {
        const config = {
            hostElement: this.#hostElement,
            trigger: this.trigger(),
            callbackToggle: () => {
                this.visible.update((visible) => !visible);
            },
            callbackOff: () => {
                this.visible.set(false);
            },
            callbackOn: () => {
                this.visible.set(true);
            }
        };
        this.#listenersService.setListeners(config);
    }
    clearListeners() {
        this.#listenersService.clearListeners();
    }
    intersectionServiceSubscribe() {
        this.#intersectionService.createIntersectionObserver(this.referenceRef());
        this.#intersectionService.intersecting$
            .pipe(filter((next) => next.hostElement === this.referenceRef()), debounceTime(100), finalize(() => {
            this.#intersectionService.unobserve(this.referenceRef());
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((next) => {
            this.visible.set(next.isIntersecting ? this.visible() : false);
        });
    }
    getUID(prefix) {
        let uid = prefix ?? 'random-id';
        do {
            uid = `${prefix}-${Math.floor(Math.random() * 1000000).toString(10)}`;
        } while (this.#document.getElementById(uid));
        return uid;
    }
    createTooltipElement() {
        if (!this.tooltipRef) {
            this.tooltipRef = this.#viewContainerRef.createComponent(PopoverComponent);
            // this.viewContainerRef.detach();
        }
    }
    destroyTooltipElement() {
        this.tooltip?.remove();
        this.tooltipRef?.destroy();
        // @ts-ignore
        this.tooltipRef = undefined;
        this.popperInstance?.destroy();
        this.#viewContainerRef?.detach();
        this.#viewContainerRef?.clear();
    }
    addTooltipElement() {
        if (!this.content()) {
            this.destroyTooltipElement();
            return;
        }
        if (!this.tooltipRef) {
            this.createTooltipElement();
        }
        this.tooltipRef?.setInput('content', this.content() ?? '');
        this.tooltip = this.tooltipRef?.location.nativeElement;
        this.#renderer.addClass(this.tooltip, 'd-none');
        this.#renderer.addClass(this.tooltip, 'fade');
        this.popperInstance?.destroy();
        this.#viewContainerRef.insert(this.tooltipRef.hostView);
        this.#renderer.appendChild(this.#document.body, this.tooltip);
        this.popperInstance = createPopper(this.referenceRef().nativeElement, this.tooltip, {
            ...this.popperOptionsComputed()
        });
        if (!this.visible()) {
            this.removeTooltipElement();
            return;
        }
        clearTimeout(this.#timerId2);
        clearTimeout(this.#timerId1);
        this.#timerId1 = setTimeout(() => {
            this.tooltipId = this.getUID('popover');
            this.tooltipRef?.setInput('id', this.tooltipId);
            this.#renderer.removeClass(this.tooltip, 'd-none');
            this.tooltipRef?.setInput('visible', this.visible());
            this.popperInstance?.forceUpdate();
            this.#changeDetectorRef?.markForCheck();
            this.#timerId1 = undefined;
        }, 150);
    }
    removeTooltipElement() {
        this.tooltipId = '';
        if (!this.tooltipRef) {
            return;
        }
        clearTimeout(this.#timerId2);
        this.#timerId2 = setTimeout(() => {
            this.#viewContainerRef?.detach();
            this.#timerId2 = undefined;
        }, 300);
        this.tooltipRef.setInput('visible', false);
        this.tooltipRef.setInput('id', undefined);
        this.#changeDetectorRef.markForCheck();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: PopoverDirective, isStandalone: true, selector: "[cPopover]", inputs: { content: { classPropertyName: "content", publicName: "cPopover", isSignal: true, isRequired: false, transformFunction: null }, popperOptions: { classPropertyName: "popperOptions", publicName: "cPopoverOptions", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "cPopoverPlacement", isSignal: true, isRequired: false, transformFunction: null }, reference: { classPropertyName: "reference", publicName: "cTooltipRef", isSignal: true, isRequired: false, transformFunction: null }, trigger: { classPropertyName: "trigger", publicName: "cPopoverTrigger", isSignal: true, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "cPopoverVisible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visible: "cPopoverVisibleChange" }, host: { properties: { "attr.aria-describedby": "ariaDescribedBy" } }, providers: [ListenersService, IntersectionService], exportAs: ["cPopover"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cPopover]',
                    exportAs: 'cPopover',
                    providers: [ListenersService, IntersectionService],
                    host: { '[attr.aria-describedby]': 'ariaDescribedBy' }
                }]
        }], propDecorators: { content: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPopover", required: false }] }], popperOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPopoverOptions", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPopoverPlacement", required: false }] }], reference: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipRef", required: false }] }], trigger: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPopoverTrigger", required: false }] }], visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "cPopoverVisible", required: false }] }, { type: i0.Output, args: ["cPopoverVisibleChange"] }] } });

class PopoverModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: PopoverModule, imports: [PopoverDirective,
            PopoverComponent], exports: [PopoverDirective,
            PopoverComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: PopoverModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        PopoverDirective,
                        PopoverComponent
                    ],
                    imports: [
                        PopoverDirective,
                        PopoverComponent
                    ]
                }]
        }] });

class ProgressService {
    stacked = signal(false, ...(ngDevMode ? [{ debugName: "stacked" }] : []));
    value = signal(undefined, ...(ngDevMode ? [{ debugName: "value" }] : []));
    precision = signal(0, ...(ngDevMode ? [{ debugName: "precision" }] : []));
    min = signal(0, ...(ngDevMode ? [{ debugName: "min" }] : []));
    max = signal(100, ...(ngDevMode ? [{ debugName: "max" }] : []));
    percent = computed(() => {
        return +((((this.value() ?? 0) - this.min()) / (this.max() - this.min())) * 100).toFixed(this.precision());
    }, ...(ngDevMode ? [{ debugName: "percent" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressService, decorators: [{
            type: Injectable
        }] });

class ProgressBarDirective {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #progressService = inject(ProgressService);
    #valuesEffect = effect(() => {
        const host = this.#hostElement.nativeElement;
        const value = this.#progressService.value();
        const percent = this.#progressService.percent();
        const stacked = this.#progressService.stacked();
        if (value === undefined) {
            for (const name of ['aria-valuenow', 'aria-valuemax', 'aria-valuemin', 'role']) {
                this.#renderer.removeAttribute(host, name);
            }
        }
        else {
            const { min, max } = this.#progressService;
            this.#renderer.setAttribute(host, 'aria-valuenow', String(value));
            this.#renderer.setAttribute(host, 'aria-valuemin', String(min()));
            this.#renderer.setAttribute(host, 'aria-valuemax', String(max()));
            this.#renderer.setAttribute(host, 'role', this.role());
        }
        const tagName = host.tagName;
        if (percent >= 0 && ((stacked && tagName === 'C-PROGRESS') || (!stacked && tagName !== 'C-PROGRESS'))) {
            this.#renderer.setStyle(host, 'width', `${percent}%`);
        }
        else {
            this.#renderer.removeStyle(host, 'width');
        }
    }, ...(ngDevMode ? [{ debugName: "#valuesEffect" }] : []));
    /**
     * Use to animate the stripes right to left via CSS3 animations.
     * @return boolean
     */
    animated = input(undefined, { ...(ngDevMode ? { debugName: "animated" } : {}), transform: booleanAttribute });
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @values 'primary', 'secondary', 'success', 'danger', 'warning', 'info', 'dark', 'light'
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    precision = input(0, { ...(ngDevMode ? { debugName: "precision" } : {}), transform: numberAttribute });
    /**
     * The percent value the ProgressBar.
     * @return number
     * @default 0
     */
    value = input(0, { ...(ngDevMode ? { debugName: "value" } : {}), transform: numberAttribute });
    /**
     * Set the progress bar variant to optional striped.
     * @values 'striped'
     * @default undefined
     */
    variant = input(...(ngDevMode ? [undefined, { debugName: "variant" }] : []));
    /**
     * The max value of the ProgressBar.
     * @return number
     * @default 100
     */
    max = input(100, { ...(ngDevMode ? { debugName: "max" } : {}), transform: numberAttribute });
    /**
     * Set default html role attribute.
     * @return string
     */
    role = input('progressbar', ...(ngDevMode ? [{ debugName: "role" }] : []));
    #serviceEffect = effect(() => {
        this.#progressService.precision.set(this.precision());
        const max = this.max();
        this.#progressService.max.set(isNaN(max) || max <= 0 ? 100 : max);
        const value = this.value();
        this.#progressService.value.set(value && !isNaN(value) ? value : undefined);
    }, ...(ngDevMode ? [{ debugName: "#serviceEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressBarDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ProgressBarDirective, isStandalone: true, selector: "[cProgressBar]", inputs: { animated: { classPropertyName: "animated", publicName: "animated", isSignal: true, isRequired: false, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, precision: { classPropertyName: "precision", publicName: "precision", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, max: { classPropertyName: "max", publicName: "max", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, exportAs: ["cProgressBar"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressBarDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cProgressBar]',
                    exportAs: 'cProgressBar'
                }]
        }], propDecorators: { animated: [{ type: i0.Input, args: [{ isSignal: true, alias: "animated", required: false }] }], color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], precision: [{ type: i0.Input, args: [{ isSignal: true, alias: "precision", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], max: [{ type: i0.Input, args: [{ isSignal: true, alias: "max", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class ProgressBarComponent {
    #progressBarDirective = inject(ProgressBarDirective, { optional: true });
    hostClasses = computed(() => {
        const animated = this.#progressBarDirective?.animated();
        const color = this.#progressBarDirective?.color();
        const variant = this.#progressBarDirective?.variant();
        return {
            'progress-bar': true,
            'progress-bar-animated': !!animated,
            [`progress-bar-${variant}`]: !!variant,
            [`bg-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ProgressBarComponent, isStandalone: true, selector: "c-progress-bar", host: { properties: { "class": "hostClasses()" }, classAttribute: "progress-bar" }, hostDirectives: [{ directive: ProgressBarDirective, inputs: ["animated", "animated", "color", "color", "max", "max", "role", "role", "value", "value", "variant", "variant"] }], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-progress-bar',
                    template: '<ng-content />',
                    hostDirectives: [
                        {
                            directive: ProgressBarDirective,
                            inputs: ['animated', 'color', 'max', 'role', 'value', 'variant']
                        }
                    ],
                    host: { class: 'progress-bar', '[class]': 'hostClasses()' }
                }]
        }] });

class ProgressStackedComponent {
    stacked = input(true, ...(ngDevMode ? [{ debugName: "stacked" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressStackedComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ProgressStackedComponent, isStandalone: true, selector: "c-progress-stacked", inputs: { stacked: { classPropertyName: "stacked", publicName: "stacked", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.progress-stacked": "stacked()" } }, exportAs: ["cProgressStacked"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:flex}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressStackedComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-progress-stacked', exportAs: 'cProgressStacked', template: '<ng-content />', host: { '[class.progress-stacked]': 'stacked()' }, styles: [":host{display:flex}\n"] }]
        }], propDecorators: { stacked: [{ type: i0.Input, args: [{ isSignal: true, alias: "stacked", required: false }] }] } });

class ProgressComponent {
    #hostElement = inject(ElementRef);
    progressBarDirective = inject(ProgressBarDirective, {
        optional: true
    });
    #stacked = inject(ProgressStackedComponent, { optional: true })?.stacked() ?? false;
    #progressService = inject(ProgressService);
    constructor() {
        this.#progressService.stacked.set(this.#stacked);
    }
    stacked = this.#progressService.stacked;
    percent = this.#progressService.percent;
    barValue = this.#progressService.value;
    contentProgressBars = contentChildren(ProgressBarComponent, ...(ngDevMode ? [{ debugName: "contentProgressBars" }] : []));
    /**
     * Sets the height of the component. If you set that value the inner `<CProgressBar>` will automatically resize accordingly.
     * @return number
     */
    height = input(0, { ...(ngDevMode ? { debugName: "height" } : {}), transform: numberAttribute });
    /**
     * Displays thin progress.
     * @return boolean
     */
    thin = input(false, { ...(ngDevMode ? { debugName: "thin" } : {}), transform: booleanAttribute });
    /**
     * Change the default color to white.
     * @return boolean
     */
    white = input(false, { ...(ngDevMode ? { debugName: "white" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            progress: true,
            'progress-thin': this.thin(),
            'progress-white': this.white()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    hostStyle = computed(() => {
        const height = this.height();
        return !!height ? `${height}px` : (this.#hostElement?.nativeElement?.style?.height ?? undefined);
    }, ...(ngDevMode ? [{ debugName: "hostStyle" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: ProgressComponent, isStandalone: true, selector: "c-progress", inputs: { height: { classPropertyName: "height", publicName: "height", isSignal: true, isRequired: false, transformFunction: null }, thin: { classPropertyName: "thin", publicName: "thin", isSignal: true, isRequired: false, transformFunction: null }, white: { classPropertyName: "white", publicName: "white", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "style.height": "hostStyle()" }, classAttribute: "progress" }, providers: [ProgressService], queries: [{ propertyName: "contentProgressBars", predicate: ProgressBarComponent, isSignal: true }], exportAs: ["cProgress"], hostDirectives: [{ directive: ProgressBarDirective, inputs: ["animated", "animated", "color", "color", "max", "max", "role", "role", "value", "value", "variant", "variant"] }], ngImport: i0, template: "@if (contentProgressBars()?.length) {\n  <ng-container *ngTemplateOutlet=\"defaultContent\" />\n} @else {\n  @let pbd = progressBarDirective;\n  <c-progress-bar [animated]=\"pbd?.animated()\" [variant]=\"pbd?.variant()\" [color]=\"pbd?.color()\" [value]=\"barValue()\">\n    <ng-container *ngTemplateOutlet=\"defaultContent\" />\n  </c-progress-bar>\n}\n\n<ng-template #defaultContent>\n  <ng-content />\n</ng-template>\n\n", styles: [":host-context(.progress-stacked).progress{transition:var(--cui-progress-bar-transition)}\n"], dependencies: [{ kind: "component", type: ProgressBarComponent, selector: "c-progress-bar" }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-progress', exportAs: 'cProgress', imports: [ProgressBarComponent, NgTemplateOutlet], hostDirectives: [
                        {
                            directive: ProgressBarDirective,
                            inputs: ['animated', 'color', 'max', 'role', 'value', 'variant']
                        }
                    ], host: {
                        class: 'progress',
                        '[class]': 'hostClasses()',
                        '[style.height]': 'hostStyle()'
                    }, providers: [ProgressService], template: "@if (contentProgressBars()?.length) {\n  <ng-container *ngTemplateOutlet=\"defaultContent\" />\n} @else {\n  @let pbd = progressBarDirective;\n  <c-progress-bar [animated]=\"pbd?.animated()\" [variant]=\"pbd?.variant()\" [color]=\"pbd?.color()\" [value]=\"barValue()\">\n    <ng-container *ngTemplateOutlet=\"defaultContent\" />\n  </c-progress-bar>\n}\n\n<ng-template #defaultContent>\n  <ng-content />\n</ng-template>\n\n", styles: [":host-context(.progress-stacked).progress{transition:var(--cui-progress-bar-transition)}\n"] }]
        }], ctorParameters: () => [], propDecorators: { contentProgressBars: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => ProgressBarComponent), { isSignal: true }] }], height: [{ type: i0.Input, args: [{ isSignal: true, alias: "height", required: false }] }], thin: [{ type: i0.Input, args: [{ isSignal: true, alias: "thin", required: false }] }], white: [{ type: i0.Input, args: [{ isSignal: true, alias: "white", required: false }] }] } });

class ProgressModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ProgressModule, imports: [ProgressComponent,
            ProgressBarComponent,
            ProgressBarDirective,
            ProgressStackedComponent], exports: [ProgressComponent,
            ProgressBarComponent,
            ProgressBarDirective,
            ProgressStackedComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ProgressModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        ProgressComponent,
                        ProgressBarComponent,
                        ProgressBarDirective,
                        ProgressStackedComponent
                    ],
                    imports: [
                        ProgressComponent,
                        ProgressBarComponent,
                        ProgressBarDirective,
                        ProgressStackedComponent
                    ]
                }]
        }] });

class TooltipComponent {
    renderer = inject(Renderer2);
    /**
     * Content of tooltip
     * @type {string | TemplateRef}
     */
    content = input('', ...(ngDevMode ? [{ debugName: "content" }] : []));
    #contentEffect = effect(() => {
        this.updateView(this.content());
    }, ...(ngDevMode ? [{ debugName: "#contentEffect" }] : []));
    /**
     * Toggle the visibility of popover component.
     * @type boolean
     */
    visible = input(false, { ...(ngDevMode ? { debugName: "visible" } : {}), transform: booleanAttribute });
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    role = input('tooltip', ...(ngDevMode ? [{ debugName: "role" }] : []));
    viewContainerRef = viewChild('tooltipTemplate', { ...(ngDevMode ? { debugName: "viewContainerRef" } : {}), read: ViewContainerRef });
    textNode;
    hostClasses = computed(() => {
        return {
            tooltip: true,
            fade: true,
            show: this.visible(),
            'bs-tooltip-auto': true
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnDestroy() {
        this.clear();
    }
    clear() {
        this.viewContainerRef()?.clear();
        if (!!this.textNode) {
            this.renderer.removeChild(this.textNode.parentNode, this.textNode);
        }
    }
    updateView(content) {
        this.clear();
        if (!content) {
            return;
        }
        if (content instanceof TemplateRef) {
            this.viewContainerRef()?.createEmbeddedView(content);
        }
        else {
            this.textNode = this.renderer.createText(content);
            const element = this.viewContainerRef()?.element.nativeElement;
            this.renderer.appendChild(element.parentNode, this.textNode);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: TooltipComponent, isStandalone: true, selector: "c-tooltip", inputs: { content: { classPropertyName: "content", publicName: "content", isSignal: true, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.role": "role()", "attr.id": "id()" }, classAttribute: "tooltip fade bs-tooltip-auto" }, viewQueries: [{ propertyName: "viewContainerRef", first: true, predicate: ["tooltipTemplate"], descendants: true, read: ViewContainerRef, isSignal: true }], ngImport: i0, template: "<ng-container>\n  <div class=\"tooltip-arrow\" data-popper-arrow></div>\n  <div class=\"tooltip-inner\">\n    <ng-container #tooltipTemplate />\n  </div>\n</ng-container>\n" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-tooltip', host: {
                        class: 'tooltip fade bs-tooltip-auto',
                        '[class]': 'hostClasses()',
                        '[attr.role]': 'role()',
                        '[attr.id]': 'id()'
                    }, template: "<ng-container>\n  <div class=\"tooltip-arrow\" data-popper-arrow></div>\n  <div class=\"tooltip-inner\">\n    <ng-container #tooltipTemplate />\n  </div>\n</ng-container>\n" }]
        }], propDecorators: { content: [{ type: i0.Input, args: [{ isSignal: true, alias: "content", required: false }] }], visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], viewContainerRef: [{ type: i0.ViewChild, args: ['tooltipTemplate', { ...{ read: ViewContainerRef }, isSignal: true }] }] } });

class TooltipDirective {
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #viewContainerRef = inject(ViewContainerRef);
    #listenersService = inject(ListenersService);
    #changeDetectorRef = inject(ChangeDetectorRef);
    #intersectionService = inject(IntersectionService);
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #timerId1;
    #timerId2;
    /**
     * Content of tooltip
     * @return {string | TemplateRef}
     */
    content = input(undefined, { ...(ngDevMode ? { debugName: "content" } : {}), alias: 'cTooltip' });
    #contentEffect = effect(() => {
        if (this.content()) {
            this.destroyTooltipElement();
        }
    }, ...(ngDevMode ? [{ debugName: "#contentEffect" }] : []));
    /**
     * Optional popper Options object, takes precedence over cPopoverPlacement prop
     * @return Partial<Options>
     */
    popperOptions = input({}, { ...(ngDevMode ? { debugName: "popperOptions" } : {}), alias: 'cTooltipOptions' });
    #popperOptionsEffect = effect(() => {
        this._popperOptions = {
            ...this._popperOptions,
            placement: this.placement(),
            ...this.popperOptions()
        };
    }, ...(ngDevMode ? [{ debugName: "#popperOptionsEffect" }] : []));
    popperOptionsComputed = computed(() => {
        return { placement: this.placement(), ...this._popperOptions };
    }, ...(ngDevMode ? [{ debugName: "popperOptionsComputed" }] : []));
    /**
     * Describes the placement of your component after Popper.js has applied all the modifiers that may have flipped or altered the originally provided placement property.
     * @return: 'top' | 'bottom' | 'left' | 'right'
     * @default: 'top'
     */
    placement = input('top', { ...(ngDevMode ? { debugName: "placement" } : {}), alias: 'cTooltipPlacement' });
    /**
     * ElementRefDirective for positioning the tooltip on reference element
     * @return: ElementRefDirective
     * @default: undefined
     */
    reference = input(undefined, { ...(ngDevMode ? { debugName: "reference" } : {}), alias: 'cTooltipRef' });
    referenceRef = computed(() => this.reference()?.elementRef ?? this.#hostElement, ...(ngDevMode ? [{ debugName: "referenceRef" }] : []));
    /**
     * Sets which event handlers you’d like provided to your toggle prop. You can specify one trigger or an array of them.
     * @return: 'Triggers | Triggers[]
     */
    trigger = input('hover', { ...(ngDevMode ? { debugName: "trigger" } : {}), alias: 'cTooltipTrigger' });
    /**
     * Toggle the visibility of tooltip component.
     * @return boolean
     */
    visible = model(false, { ...(ngDevMode ? { debugName: "visible" } : {}), alias: 'cTooltipVisible' });
    #visibleEffect = afterRenderEffect({
        // this fixes RuntimeError: NG0500: During hydration Angular expected <abc> but found <xyz>.
        // Find more at https://angular.dev/errors/NG0500
        write: () => {
            this.visible() ? this.addTooltipElement() : this.removeTooltipElement();
        }
    });
    get ariaDescribedBy() {
        return this.tooltipId ? this.tooltipId : null;
    }
    tooltip;
    tooltipId;
    tooltipRef;
    popperInstance;
    _popperOptions = {
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 5]
                }
            }
        ]
    };
    ngAfterViewInit() {
        this.intersectionServiceSubscribe();
    }
    ngOnDestroy() {
        this.clearListeners();
        this.destroyTooltipElement();
    }
    ngOnInit() {
        this.setListeners();
    }
    setListeners() {
        const config = {
            hostElement: this.#hostElement,
            trigger: this.trigger(),
            callbackToggle: () => {
                this.visible.update((value) => !value);
            },
            callbackOff: () => {
                this.visible.set(false);
            },
            callbackOn: () => {
                this.visible.set(true);
            }
        };
        this.#listenersService.setListeners(config);
    }
    clearListeners() {
        this.#listenersService.clearListeners();
    }
    intersectionServiceSubscribe() {
        this.#intersectionService.createIntersectionObserver(this.referenceRef());
        this.#intersectionService.intersecting$
            .pipe(filter((next) => next.hostElement === this.referenceRef()), debounceTime(100), finalize(() => {
            this.#intersectionService.unobserve(this.referenceRef());
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe((next) => {
            this.visible.set(next.isIntersecting ? this.visible() : false);
        });
    }
    getUID(prefix) {
        let uid = prefix ?? 'random-id';
        do {
            uid = `${prefix}-${Math.floor(Math.random() * 1000000).toString(10)}`;
        } while (this.#document.getElementById(uid));
        return uid;
    }
    createTooltipElement() {
        if (!this.tooltipRef) {
            this.tooltipRef = this.#viewContainerRef.createComponent(TooltipComponent);
            // this.viewContainerRef.detach();
        }
    }
    destroyTooltipElement() {
        this.tooltip?.remove();
        this.tooltipRef?.destroy();
        // @ts-ignore
        this.tooltipRef = undefined;
        this.popperInstance?.destroy();
        this.#viewContainerRef?.detach();
        this.#viewContainerRef?.clear();
    }
    addTooltipElement() {
        if (!this.content()) {
            this.destroyTooltipElement();
            return;
        }
        if (!this.tooltipRef) {
            this.createTooltipElement();
        }
        this.tooltipRef?.setInput('content', this.content() ?? '');
        this.tooltip = this.tooltipRef?.location.nativeElement;
        this.#renderer.addClass(this.tooltip, 'd-none');
        this.#renderer.addClass(this.tooltip, 'fade');
        this.popperInstance?.destroy();
        this.#viewContainerRef.insert(this.tooltipRef.hostView);
        this.#renderer.appendChild(this.#document.body, this.tooltip);
        this.popperInstance = createPopper(this.referenceRef().nativeElement, this.tooltip, {
            ...this.popperOptionsComputed()
        });
        if (!this.visible()) {
            this.removeTooltipElement();
            return;
        }
        clearTimeout(this.#timerId2);
        clearTimeout(this.#timerId1);
        this.#timerId1 = setTimeout(() => {
            this.tooltipId = this.getUID('tooltip');
            this.tooltipRef?.setInput('id', this.tooltipId);
            this.#renderer.removeClass(this.tooltip, 'd-none');
            this.tooltipRef?.setInput('visible', this.visible());
            this.popperInstance?.forceUpdate();
            this.#changeDetectorRef?.markForCheck();
            this.#timerId1 = undefined;
        }, 150);
    }
    removeTooltipElement() {
        this.tooltipId = '';
        if (!this.tooltipRef) {
            return;
        }
        clearTimeout(this.#timerId2);
        this.#timerId2 = setTimeout(() => {
            this.#viewContainerRef?.detach();
            this.#timerId2 = undefined;
        }, 300);
        this.tooltipRef.setInput('visible', false);
        this.tooltipRef.setInput('id', undefined);
        this.#changeDetectorRef.markForCheck();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TooltipDirective, isStandalone: true, selector: "[cTooltip]", inputs: { content: { classPropertyName: "content", publicName: "cTooltip", isSignal: true, isRequired: false, transformFunction: null }, popperOptions: { classPropertyName: "popperOptions", publicName: "cTooltipOptions", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "cTooltipPlacement", isSignal: true, isRequired: false, transformFunction: null }, reference: { classPropertyName: "reference", publicName: "cTooltipRef", isSignal: true, isRequired: false, transformFunction: null }, trigger: { classPropertyName: "trigger", publicName: "cTooltipTrigger", isSignal: true, isRequired: false, transformFunction: null }, visible: { classPropertyName: "visible", publicName: "cTooltipVisible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visible: "cTooltipVisibleChange" }, host: { properties: { "attr.aria-describedby": "ariaDescribedBy" } }, providers: [ListenersService, IntersectionService], exportAs: ["cTooltip"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTooltip]',
                    exportAs: 'cTooltip',
                    providers: [ListenersService, IntersectionService],
                    host: { '[attr.aria-describedby]': 'ariaDescribedBy' }
                }]
        }], propDecorators: { content: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltip", required: false }] }], popperOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipOptions", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipPlacement", required: false }] }], reference: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipRef", required: false }] }], trigger: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipTrigger", required: false }] }], visible: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTooltipVisible", required: false }] }, { type: i0.Output, args: ["cTooltipVisibleChange"] }] } });

class TooltipModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: TooltipModule, imports: [TooltipComponent,
            TooltipDirective], exports: [TooltipComponent,
            TooltipDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        TooltipComponent,
                        TooltipDirective
                    ],
                    imports: [
                        TooltipComponent,
                        TooltipDirective
                    ]
                }]
        }] });

class RatingItemLabelComponent {
    #elementRef = inject(ElementRef);
    #renderer = inject(Renderer2);
    label = input('', ...(ngDevMode ? [{ debugName: "label" }] : []));
    value = input.required(...(ngDevMode ? [{ debugName: "value" }] : []));
    focusable = signal(false, ...(ngDevMode ? [{ debugName: "focusable" }] : []));
    focusableEffect = effect(() => {
        this.#renderer.setAttribute(this.#elementRef.nativeElement, 'tabindex', this.focusable() ? '0' : '-1');
    }, ...(ngDevMode ? [{ debugName: "focusableEffect" }] : []));
    // @HostBinding('attr.tabindex')
    // get tabIndex() {
    //   // console.log('active', this.active(), this.active() ? '0' : '-1')
    //   console.log('focusable', this.value(), this.focusable(), this.focusable() ? '0' : '-1');
    //   return this.focusable() ? '0' : '-1';
    // }
    focus(origin) {
        this.#elementRef.nativeElement.focus();
    }
    getLabel() {
        return this.label() || this.value().toString();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingItemLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: RatingItemLabelComponent, isStandalone: true, selector: "c-rating-item-label", inputs: { label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: true, transformFunction: null } }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingItemLabelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-rating-item-label',
                    standalone: true,
                    template: '<ng-content />'
                }]
        }], propDecorators: { label: [{ type: i0.Input, args: [{ isSignal: true, alias: "label", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: true }] }] } });

const coerceStringArray = (value) => (Array.isArray(value) ? [...value] : [value]);
const coerceRatingPrecision = (value) => [1, 0.5, 0.25].includes(numberAttribute(value)) ? numberAttribute(value) : 1;
const coerceNumber = (value) => Number(value ?? 0);
class RatingComponent {
    static ngAcceptInputType_allowClear;
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_highlightOnlySelected;
    static ngAcceptInputType_readOnly;
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    #rtlService = inject(RtlService);
    #isRtl = signal(false, ...(ngDevMode ? [{ debugName: "#isRtl" }] : []));
    constructor() {
        afterEveryRender({
            read: () => {
                this.#isRtl.set(this.#rtlService.isRTL(this.#elementRef.nativeElement));
            }
        });
    }
    /**
     * The default icon to display when the item is selected.
     * @returns: string | string[]
     */
    activeIcon = input([], { ...(ngDevMode ? { debugName: "activeIcon" } : {}), transform: coerceStringArray });
    /**
     * Enables the clearing upon clicking the selected item again.
     * @returns: boolean
     * @default: false
     */
    allowClear = input(false, { ...(ngDevMode ? { debugName: "allowClear" } : {}), transform: booleanAttribute });
    /**
     * Toggle the disabled state for the component.
     * @returns: boolean
     * @default: false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    #disabledState = linkedSignal(this.disabled, ...(ngDevMode ? [{ debugName: "#disabledState" }] : []));
    /**
     * If enabled, only the currently selected icon will be visibly highlighted.
     * @returns: boolean
     * @default: false
     */
    highlightOnlySelected = input(false, { ...(ngDevMode ? { debugName: "highlightOnlySelected" } : {}), transform: booleanAttribute });
    /**
     * The default icon to display when the item is not selected.
     * @returns: string | string[]
     */
    icon = input([], { ...(ngDevMode ? { debugName: "icon" } : {}), transform: coerceStringArray });
    /**
     * Specifies the total number of stars to be displayed in the star rating component. This property determines the scale of the rating,
     * such as out of 5 stars, 10 stars, etc.
     * @returns: number
     * @default: 5
     */
    itemCount = input(5, { ...(ngDevMode ? { debugName: "itemCount" } : {}), transform: numberAttribute });
    /**
     * Minimum increment value change allowed.
     * @returns: number
     * @default: 1
     */
    precision = input(1, { ...(ngDevMode ? { debugName: "precision" } : {}), transform: numberAttribute });
    /**
     * Allows to click fractions
     * @returns: boolean
     * @default: false
     */
    // readonly precisionClick = input(false, { transform: booleanAttribute });
    /**
     * Toggle the readonly state for the component.
     * @returns: boolean
     * @default: false
     */
    readOnly = input(false, { ...(ngDevMode ? { debugName: "readOnly" } : {}), transform: booleanAttribute });
    /**
     * Size the component small, large, or custom if you define custom icons with custom height.
     * @returns: 'sm' | 'lg' | 'custom' | undefined
     * @default: undefined
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Enable tooltips with default values or set specific labels for each icon.
     * @returns: boolean | string[] | undefined
     * @default: undefined
     */
    tooltips = input(undefined, ...(ngDevMode ? [{ debugName: "tooltips" }] : []));
    /**
     * The `value` attribute of component.
     * @returns: number | null
     * @default: null
     * Emits `valueChange` on value change
     */
    value = model(null, ...(ngDevMode ? [{ debugName: "value" }] : []));
    /**
     * Event emitted on hoverValue change.
     * @returns: number | null
     */
    hoverValue = signal(null, ...(ngDevMode ? [{ debugName: "hoverValue" }] : []));
    hoverValueChange = output();
    hoverValueEffect = effect(() => {
        this.hoverValueChange.emit(this.hoverValue());
        if (!this.hoverValue()) {
            this.hoveredItem.set(undefined);
        }
    }, ...(ngDevMode ? [{ debugName: "hoverValueEffect" }] : []));
    #cleared = signal(false, ...(ngDevMode ? [{ debugName: "#cleared" }] : []));
    hoveredItem = signal(undefined, ...(ngDevMode ? [{ debugName: "hoveredItem" }] : []));
    tooltipValue = signal(null, ...(ngDevMode ? [{ debugName: "tooltipValue" }] : []));
    ratingDisabled = computed(() => this.disabled() || this.readOnly() || this.#disabledState(), ...(ngDevMode ? [{ debugName: "ratingDisabled" }] : []));
    ratingDisabledEffect = effect(() => {
        if (!this.ratingDisabled()) {
            const focusIn$ = fromEvent(this.#elementRef.nativeElement, 'focusin');
            const focusOut$ = fromEvent(this.#elementRef.nativeElement, 'focusout');
            const mouseLeave$ = fromEvent(this.#elementRef.nativeElement, 'mouseleave');
            merge(focusIn$, focusOut$, mouseLeave$)
                .pipe(filter((event) => !this.ratingDisabled()), tap(($event) => {
                switch ($event.type) {
                    case 'focusin':
                        this.onTouched();
                        break;
                    case 'focusout':
                    case 'mouseleave':
                        this.hoverValue.set(null);
                        break;
                }
            }), takeWhile(() => !this.ratingDisabled()), takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        }
    }, ...(ngDevMode ? [{ debugName: "ratingDisabledEffect" }] : []));
    contentTemplates = contentChildren(TemplateIdDirective, ...(ngDevMode ? [{ debugName: "contentTemplates" }] : []));
    iconTemplates = computed(() => {
        const templateArray = this.contentTemplates().map((child) => {
            return [[child.id], child.templateRef];
        });
        return Object.fromEntries(templateArray);
    }, ...(ngDevMode ? [{ debugName: "iconTemplates" }] : []));
    numberOfSubItems = computed(() => {
        return parseInt((1 / this.precision()).toFixed());
    }, ...(ngDevMode ? [{ debugName: "numberOfSubItems" }] : []));
    ratingItems = computed(() => {
        return Array.from({ length: this.itemCount() }, (_, itemIndex) => {
            if (this.numberOfSubItems() === 1) {
                return [itemIndex + 1];
            }
            return Array.from({ length: this.numberOfSubItems() }, (_, subIndex) => itemIndex + (subIndex + 1) * this.precision());
        });
    }, ...(ngDevMode ? [{ debugName: "ratingItems" }] : []));
    ratingItemLabels = viewChildren(RatingItemLabelComponent, ...(ngDevMode ? [{ debugName: "ratingItemLabels" }] : []));
    #focusKeyManager;
    ratingItemLabelsEffect = effect(() => {
        if (untracked(this.ratingDisabled)) {
            return;
        }
        const isRtl = this.#isRtl();
        this.#focusKeyManager = new FocusKeyManager(this.ratingItemLabels())
            .withHorizontalOrientation(isRtl ? 'rtl' : 'ltr')
            .withVerticalOrientation(false)
            .withWrap();
        const activeItemIndex = this.ratingItems()
            .flat()
            .findIndex((value) => value === coerceNumber(this.value() ?? 0));
        this.#focusKeyManager?.updateActiveItem(activeItemIndex < 0 ? 0 : activeItemIndex);
        this.#focusKeyManager.tabOut
            .pipe(tap((x) => {
            const activeItemIndex = untracked(this.ratingItems)
                .flat()
                .findIndex((value) => value === coerceNumber(untracked(this.value) ?? 0));
            this.#focusKeyManager.updateActiveItem(activeItemIndex === -1 ? 0 : activeItemIndex);
            this.updateFocusableItems();
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        this.#focusKeyManager.change
            .pipe(tap((value) => {
            this.ratingItemLabels().forEach((label, index) => {
                label.focusable.set(index === this.#focusKeyManager.activeItemIndex);
                if (index === this.#focusKeyManager.activeItemIndex) {
                    const value = untracked(label.value);
                    this.handleClick(value);
                }
            });
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
    }, ...(ngDevMode ? [{ debugName: "ratingItemLabelsEffect" }] : []));
    hostClasses = computed(() => {
        const size = this.size();
        return {
            rating: true,
            [`rating-${size}`]: size,
            disabled: this.disabled() || this.#disabledState(),
            readonly: this.readOnly()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    onKeyDown($event) {
        if (this.ratingDisabled()) {
            return;
        }
        if (['Space', 'Enter'].includes($event.code)) {
            $event.preventDefault();
            const item = this.ratingItemLabels().find((label, index) => index === this.#focusKeyManager?.activeItemIndex);
            this.handleClick(item?.value() ?? -1);
            return;
        }
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes($event.key)) {
            this.#focusKeyManager.onKeydown($event);
            return;
        }
        if (['Tab'].includes($event.key)) {
            this.#focusKeyManager?.tabOut.next();
        }
        return;
    }
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.#disabledState.set(isDisabled);
    }
    writeValue(value) {
        this.value.set(value);
    }
    isItemActive(value) {
        const localValue = this.hoverValue() ?? this.value() ?? -1;
        return this.highlightOnlySelected() ? localValue === value : localValue >= value;
    }
    notLastItemStyle(value) {
        return {
            zIndex: this.numberOfSubItems() - value,
            position: 'absolute',
            width: `${this.precision() * (value + 1) * 100}%`,
            overflow: 'hidden',
            opacity: 0.1
        };
    }
    handleFocusIn($event, itemValue) {
        this.handleMouseEnter($event, itemValue);
    }
    handleClick(value) {
        if (this.ratingDisabled()) {
            return;
        }
        if (this.allowClear() && value === this.value()) {
            this.#cleared.set(true);
            this.value.set(null);
            this.hoverValue.set(null);
        }
        else {
            this.value.set(value);
        }
    }
    handleMouseEnter($event, value) {
        if (this.ratingDisabled()) {
            return;
        }
        this.hoverValue.set(null);
        this.hoverValue.set(value);
        this.tooltipValue.set(value);
    }
    getTooltipValue(index) {
        if (!this.hoverValue() || this.hoveredItem() !== index) {
            return undefined;
        }
        if (Array.isArray(this.tooltips())) {
            const tooltips = this.tooltips();
            return tooltips[index];
        }
        return this.precision()
            ? (this.tooltipValue()?.toString() ?? '')
            : this.ratingItems()
                ? (this.ratingItems()[index].at(-1) ?? '').toString()
                : '';
    }
    updateFocusableItems() {
        this.ratingItemLabels().forEach((label, index) => {
            label.focusable.set(index === this.#focusKeyManager?.activeItemIndex);
        });
    }
    // keep it at the bottom of the code
    valueEffect = effect(() => {
        const ratingValue = this.value();
        this.onChange(ratingValue);
        if (untracked(this.ratingDisabled)) {
            return;
        }
        if (untracked(this.#cleared)) {
            this.#cleared.set(false);
            return;
        }
        const activeItemIndex = this.ratingItems()
            .flat()
            .findIndex((value) => value === coerceNumber(ratingValue ?? 0));
        this.#focusKeyManager?.updateActiveItem(activeItemIndex < 0 ? 0 : activeItemIndex);
        this.updateFocusableItems();
    }, ...(ngDevMode ? [{ debugName: "valueEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: RatingComponent, isStandalone: true, selector: "c-rating", inputs: { activeIcon: { classPropertyName: "activeIcon", publicName: "activeIcon", isSignal: true, isRequired: false, transformFunction: null }, allowClear: { classPropertyName: "allowClear", publicName: "allowClear", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, highlightOnlySelected: { classPropertyName: "highlightOnlySelected", publicName: "highlightOnlySelected", isSignal: true, isRequired: false, transformFunction: null }, icon: { classPropertyName: "icon", publicName: "icon", isSignal: true, isRequired: false, transformFunction: null }, itemCount: { classPropertyName: "itemCount", publicName: "itemCount", isSignal: true, isRequired: false, transformFunction: null }, precision: { classPropertyName: "precision", publicName: "precision", isSignal: true, isRequired: false, transformFunction: null }, readOnly: { classPropertyName: "readOnly", publicName: "readOnly", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, tooltips: { classPropertyName: "tooltips", publicName: "tooltips", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { value: "valueChange", hoverValueChange: "hoverValueChange" }, host: { attributes: { "role": "radiogroup" }, listeners: { "keydown": "onKeyDown($event)" }, properties: { "attr.aria-disabled": "ratingDisabled()", "attr.aria-valueNow": "value()", "attr.aria-valuemax": "itemCount()", "class": "hostClasses()" } }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RatingComponent),
                multi: true
            }
        ], queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, isSignal: true }], viewQueries: [{ propertyName: "ratingItemLabels", predicate: RatingItemLabelComponent, descendants: true, isSignal: true }], exportAs: ["cRating"], ngImport: i0, template: "@for (item of ratingItems(); track item; let itemIndex = $index, last = $last) {\n  @if (tooltips()) {\n    <div\n      class=\"rating-item\"\n      [cTooltip]=\"getTooltipValue(itemIndex)\"\n      [cTooltipTrigger]=\"['hover', 'focusin']\"\n      (mouseenter)=\"hoveredItem.set(itemIndex)\"\n      (focusin)=\"hoveredItem.set(itemIndex)\"\n    >\n      <ng-container *ngTemplateOutlet=\"ratingItemLabel; context: { $implicit: item, itemIndex }\" />\n    </div>\n  } @else {\n    <div class=\"rating-item\">\n      <ng-container *ngTemplateOutlet=\"ratingItemLabel; context: { $implicit: item, itemIndex }\" />\n    </div>\n  }\n}\n\n<ng-template let-item let-itemIndex=\"itemIndex\" #ratingItemLabel>\n  @for (itemValue of item; track radioIndex; let radioIndex = $index, last = $last) {\n    <c-rating-item-label\n      class=\"rating-item-label d-inline-block\"\n      role=\"radio\"\n      (click)=\"handleClick(itemValue)\"\n      (focus)=\"handleFocusIn($event, itemValue)\"\n      [class.active]=\"isItemActive(itemValue)\"\n      (mouseenter)=\"handleMouseEnter($event, itemValue)\"\n      [style]=\"radioIndex + 1 < numberOfSubItems() ? notLastItemStyle(radioIndex) : {}\"\n      [value]=\"itemValue\"\n      [attr.aria-label]=\"itemValue\"\n      [attr.aria-checked]=\"itemValue === value()\"\n    >\n      @if (iconTemplates()[icon()[itemIndex] ?? icon()]) {\n        <ng-container *ngTemplateOutlet=\"customIconTemplate; context: { $implicit: icon()?.[itemIndex] ?? icon() }\" />\n      } @else {\n        <ng-container *ngTemplateOutlet=\"defaultIconTemplate\" />\n      }\n      @if (iconTemplates()[activeIcon()[itemIndex] ?? activeIcon()]) {\n        <ng-container *ngTemplateOutlet=\"customIconActiveTemplate; context: { $implicit: activeIcon()?.[itemIndex] ?? activeIcon() }\" />\n      }\n    </c-rating-item-label>\n  }\n</ng-template>\n\n<ng-template #tooltipContent>\n  <span>{{ hoverValue() }}</span>\n</ng-template>\n\n<ng-template #defaultIconTemplate>\n  <div class=\"rating-item-icon\"></div>\n</ng-template>\n\n<ng-template #customIconTemplate let-icon>\n  <div class=\"rating-item-custom-icon\">\n    <ng-container *ngTemplateOutlet=\"iconTemplates()?.[icon] || defaultIconTemplate\" />\n  </div>\n</ng-template>\n\n<ng-template #customIconActiveTemplate let-icon>\n  <div class=\"rating-item-custom-icon-active\">\n    <ng-container *ngTemplateOutlet=\"iconTemplates()?.[icon] || customIconTemplate\" />\n  </div>\n</ng-template>\n", styles: [":host{display:inline-flex}:is(:host.disabled,:host.readonly) .rating-item-label{cursor:default}\n"], dependencies: [{ kind: "directive", type: i0.forwardRef(() => NgTemplateOutlet), selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i0.forwardRef(() => TooltipDirective), selector: "[cTooltip]", inputs: ["cTooltip", "cTooltipOptions", "cTooltipPlacement", "cTooltipRef", "cTooltipTrigger", "cTooltipVisible"], outputs: ["cTooltipVisibleChange"], exportAs: ["cTooltip"] }, { kind: "component", type: i0.forwardRef(() => RatingItemLabelComponent), selector: "c-rating-item-label", inputs: ["label", "value"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-rating', exportAs: 'cRating', imports: [NgTemplateOutlet, forwardRef(() => TooltipDirective), RatingItemLabelComponent], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RatingComponent),
                            multi: true
                        }
                    ], host: {
                        '[attr.aria-disabled]': 'ratingDisabled()',
                        '[attr.aria-valueNow]': 'value()',
                        '[attr.aria-valuemax]': 'itemCount()',
                        '[class]': 'hostClasses()',
                        '(keydown)': 'onKeyDown($event)',
                        role: 'radiogroup'
                    }, template: "@for (item of ratingItems(); track item; let itemIndex = $index, last = $last) {\n  @if (tooltips()) {\n    <div\n      class=\"rating-item\"\n      [cTooltip]=\"getTooltipValue(itemIndex)\"\n      [cTooltipTrigger]=\"['hover', 'focusin']\"\n      (mouseenter)=\"hoveredItem.set(itemIndex)\"\n      (focusin)=\"hoveredItem.set(itemIndex)\"\n    >\n      <ng-container *ngTemplateOutlet=\"ratingItemLabel; context: { $implicit: item, itemIndex }\" />\n    </div>\n  } @else {\n    <div class=\"rating-item\">\n      <ng-container *ngTemplateOutlet=\"ratingItemLabel; context: { $implicit: item, itemIndex }\" />\n    </div>\n  }\n}\n\n<ng-template let-item let-itemIndex=\"itemIndex\" #ratingItemLabel>\n  @for (itemValue of item; track radioIndex; let radioIndex = $index, last = $last) {\n    <c-rating-item-label\n      class=\"rating-item-label d-inline-block\"\n      role=\"radio\"\n      (click)=\"handleClick(itemValue)\"\n      (focus)=\"handleFocusIn($event, itemValue)\"\n      [class.active]=\"isItemActive(itemValue)\"\n      (mouseenter)=\"handleMouseEnter($event, itemValue)\"\n      [style]=\"radioIndex + 1 < numberOfSubItems() ? notLastItemStyle(radioIndex) : {}\"\n      [value]=\"itemValue\"\n      [attr.aria-label]=\"itemValue\"\n      [attr.aria-checked]=\"itemValue === value()\"\n    >\n      @if (iconTemplates()[icon()[itemIndex] ?? icon()]) {\n        <ng-container *ngTemplateOutlet=\"customIconTemplate; context: { $implicit: icon()?.[itemIndex] ?? icon() }\" />\n      } @else {\n        <ng-container *ngTemplateOutlet=\"defaultIconTemplate\" />\n      }\n      @if (iconTemplates()[activeIcon()[itemIndex] ?? activeIcon()]) {\n        <ng-container *ngTemplateOutlet=\"customIconActiveTemplate; context: { $implicit: activeIcon()?.[itemIndex] ?? activeIcon() }\" />\n      }\n    </c-rating-item-label>\n  }\n</ng-template>\n\n<ng-template #tooltipContent>\n  <span>{{ hoverValue() }}</span>\n</ng-template>\n\n<ng-template #defaultIconTemplate>\n  <div class=\"rating-item-icon\"></div>\n</ng-template>\n\n<ng-template #customIconTemplate let-icon>\n  <div class=\"rating-item-custom-icon\">\n    <ng-container *ngTemplateOutlet=\"iconTemplates()?.[icon] || defaultIconTemplate\" />\n  </div>\n</ng-template>\n\n<ng-template #customIconActiveTemplate let-icon>\n  <div class=\"rating-item-custom-icon-active\">\n    <ng-container *ngTemplateOutlet=\"iconTemplates()?.[icon] || customIconTemplate\" />\n  </div>\n</ng-template>\n", styles: [":host{display:inline-flex}:is(:host.disabled,:host.readonly) .rating-item-label{cursor:default}\n"] }]
        }], ctorParameters: () => [], propDecorators: { activeIcon: [{ type: i0.Input, args: [{ isSignal: true, alias: "activeIcon", required: false }] }], allowClear: [{ type: i0.Input, args: [{ isSignal: true, alias: "allowClear", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], highlightOnlySelected: [{ type: i0.Input, args: [{ isSignal: true, alias: "highlightOnlySelected", required: false }] }], icon: [{ type: i0.Input, args: [{ isSignal: true, alias: "icon", required: false }] }], itemCount: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemCount", required: false }] }], precision: [{ type: i0.Input, args: [{ isSignal: true, alias: "precision", required: false }] }], readOnly: [{ type: i0.Input, args: [{ isSignal: true, alias: "readOnly", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], tooltips: [{ type: i0.Input, args: [{ isSignal: true, alias: "tooltips", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }, { type: i0.Output, args: ["valueChange"] }], hoverValueChange: [{ type: i0.Output, args: ["hoverValueChange"] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { isSignal: true }] }], ratingItemLabels: [{ type: i0.ViewChildren, args: [i0.forwardRef(() => RatingItemLabelComponent), { isSignal: true }] }] } });

class RatingModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RatingModule, imports: [RatingComponent], exports: [RatingComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RatingModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [RatingComponent],
                    exports: [RatingComponent]
                }]
        }] });

const calculateClickValue = (event, container, min, max, step, vertical, rtl) => {
    const clickPosition = getClickPosition(event, container, vertical, rtl);
    const value = min + clickPosition * (max - min);
    return roundToStep(value, step);
};
const calculateMoveValue = (event, container, min, max, step, vertical, rtl) => {
    const rect = container.getBoundingClientRect();
    const position = vertical
        ? calculateVerticalPosition(event.clientY, rect)
        : calculateHorizontalPosition(event.clientX, rect, rtl);
    if (typeof position === 'string') {
        return position === 'max' ? max : min;
    }
    const value = min + position * (max - min);
    return roundToStep(value, step);
};
const calculateVerticalPosition = (mouseY, rect) => {
    if (mouseY < rect.top) {
        return 'max';
    }
    if (mouseY > rect.bottom) {
        return 'min';
    }
    return Math.min(Math.max((rect.bottom - mouseY) / rect.height, 0), 1);
};
const calculateHorizontalPosition = (mouseX, rect, rtl) => {
    if (mouseX < rect.left) {
        return rtl ? 'max' : 'min';
    }
    if (mouseX > rect.right) {
        return rtl ? 'min' : 'max';
    }
    const relativeX = rtl ? rect.right - mouseX : mouseX - rect.left;
    return Math.min(Math.max(relativeX / rect.width, 0), 1);
};
const calculateLabelPosition = (min, max, labels, label, index) => {
    if (typeof label === 'object' && 'value' in label) {
        return `${((label.value - min) / (max - min)) * 100}%`;
    }
    return `${(index / (labels.length - 1)) * 100}%`;
};
const calculateTooltipPosition = (min, max, value, thumbSize, vertical, rtl) => {
    const percent = (value - min) / (max - min);
    const margin = percent > 0.5
        ? `-${(percent - 0.5) * thumbSize.value}${thumbSize.unit}`
        : `${(0.5 - percent) * thumbSize.value}${thumbSize.unit}`;
    if (vertical) {
        return {
            bottom: `${percent * 100}%`,
            marginBottom: margin
        };
    }
    return rtl ? { right: `${percent * 100}%`, marginRight: margin } : { left: `${percent * 100}%`, marginLeft: margin };
};
const getClickPosition = (event, container, vertical, rtl) => {
    const { offsetX, offsetY } = event;
    const { offsetWidth, offsetHeight } = container;
    if (vertical) {
        return 1 - offsetY / offsetHeight;
    }
    return rtl ? 1 - offsetX / offsetWidth : offsetX / offsetWidth;
};
const getLabelValue = (min, max, labels, label, index) => typeof label === 'object' && 'value' in label ? label.value : min + (index / (labels.length - 1)) * (max - min);
const getNearestValueIndex = (value, values) => {
    const valuesLength = values.length;
    if (value < values[0]) {
        return 0;
    }
    if (value > values[valuesLength - 1]) {
        return valuesLength - 1;
    }
    const distances = values.map((v) => Math.abs(v - value));
    const min = Math.min(...distances);
    const firstIndex = distances.indexOf(min);
    return value < values[firstIndex] ? firstIndex : distances.lastIndexOf(min);
};
const roundToStep = (number, step) => {
    const _step = step === 0 ? 1 : step;
    return Math.round(number / _step) * _step;
};
const updateGradient = (min, max, values, vertical, rtl) => {
    const minVal = Math.min(...values);
    const maxVal = Math.max(...values);
    const from = ((minVal - min) / (max - min)) * 100;
    const to = ((maxVal - min) / (max - min)) * 100;
    const direction = vertical ? 'to top' : rtl ? 'to left' : 'to right';
    return {
        backgroundImage: values.length === 1
            ? `linear-gradient(
    ${direction},
    var(--cui-range-slider-track-in-range-bg) 0%,
    var(--cui-range-slider-track-in-range-bg) ${to}%,
    transparent ${to}%,
    transparent 100%
  )`
            : `linear-gradient(
    ${direction},
    transparent 0%,
    transparent ${from}%,
    var(--cui-range-slider-track-in-range-bg) ${from}%,
    var(--cui-range-slider-track-in-range-bg) ${to}%,
    transparent ${to}%,
    transparent 100%
  )`
    };
};
const updateValue = (value, values, distance, index) => {
    const newValue = [...values];
    newValue[index] = validateValue(value, values, distance, index);
    return newValue;
};
const validateValue = (value, values, distance, index) => {
    // If there's only one value, return it as is
    if (values.length === 1) {
        return value;
    }
    // Determine previous and next values if they exist
    const prevValue = index > 0 ? values[index - 1] : undefined;
    const nextValue = index < values.length - 1 ? values[index + 1] : undefined;
    // If it's the first element, ensure it's not too close to the next value
    if (index === 0 && nextValue !== undefined) {
        return Math.min(value, nextValue - distance);
    }
    // If it's the last element, ensure it's not too close to the previous value
    if (index === values.length - 1 && prevValue !== undefined) {
        return Math.max(value, prevValue + distance);
    }
    // For middle elements, ensure the value is within the allowed distance from both neighbors
    if (prevValue !== undefined && nextValue !== undefined) {
        const minVal = prevValue + distance;
        const maxVal = nextValue - distance;
        return Math.min(Math.max(value, minVal), maxVal);
    }
    // Fallback: If for some reason prevValue or nextValue is undefined, return the original value
    return value;
};
const equalArrays = (a, b) => a.length === b.length && a.every((element, index) => element === b[index]);

class RangeSliderTrackPipe {
    transform(value, min, max, vertical, rtl = false) {
        return updateGradient(min, max, value, vertical, rtl);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTrackPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTrackPipe, isStandalone: true, name: "rangeSliderTrack" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTrackPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rangeSliderTrack'
                }]
        }] });

class RangeSliderLabelStylePipe {
    transform(label, min, max, vertical, labels, index, isRTL = false) {
        const labelPosition = calculateLabelPosition(min, max, labels, label, index);
        return Object.assign(vertical ? { bottom: labelPosition } : isRTL ? { right: labelPosition } : { left: labelPosition }, typeof label === 'object' && 'style' in label && label.style);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelStylePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelStylePipe, isStandalone: true, name: "rangeSliderLabelStyle" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelStylePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rangeSliderLabelStyle'
                }]
        }] });

class RangeSliderLabelClassPipe {
    transform(label, clickableLabels) {
        const classes = {
            'range-slider-label': true,
            clickable: clickableLabels
        };
        const labelClasses = typeof label === 'object' && 'class' in label && label.class;
        let classObj = {};
        if (typeof labelClasses === 'string') {
            classObj = { [labelClasses]: true };
        }
        else if (Array.isArray(labelClasses)) {
            classObj = labelClasses.reduce((acc, labelClass) => ({ ...acc, [labelClass]: true }), {});
        }
        else if (labelClasses instanceof Set) {
            classObj = Array.from(labelClasses).reduce((acc, labelClass) => ({ ...acc, [labelClass]: true }), {});
        }
        else if (typeof labelClasses === 'object') {
            classObj = labelClasses;
        }
        return { ...classes, ...classObj };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelClassPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelClassPipe, isStandalone: true, name: "rangeSliderLabelClass" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderLabelClassPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rangeSliderLabelClass'
                }]
        }] });

class RangeSliderTooltipPositionStylePipe {
    transform(value, min, max, vertical, thumbSize, rtl = false) {
        return thumbSize ? calculateTooltipPosition(min, max, value, thumbSize, vertical, rtl) : null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTooltipPositionStylePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTooltipPositionStylePipe, isStandalone: true, name: "rangeSliderTooltipPositionStyle" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderTooltipPositionStylePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'rangeSliderTooltipPositionStyle'
                }]
        }] });

class RangeSliderComponent {
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_vertical;
    #destroyRef = inject(DestroyRef);
    #document = inject(DOCUMENT);
    #elementRef = inject(ElementRef);
    #UIDService = inject(UIDService);
    #RtlService = inject(RtlService);
    /**
     * Enable or disable clickable labels in the Angular Range Slider.
     * When set to `true`, users can click on labels to adjust the slider's value directly, enhancing interactivity and user experience.
     * @default true
     */
    clickableLabels = input(true, { ...(ngDevMode ? { debugName: "clickableLabels" } : {}), transform: booleanAttribute });
    /**
     * Control the interactive state of the Angular Range Slider with the `disabled` prop.
     * Setting it to `true` will disable all slider functionalities, preventing user interaction and visually indicating a non-interactive state.
     * @default false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    disabledState = linkedSignal({ ...(ngDevMode ? { debugName: "disabledState" } : {}), source: this.disabled,
        computation: (isDisabled) => {
            this.#elementRef.nativeElement.disabled = isDisabled;
            return isDisabled;
            // return isDisabled ? isDisabled : null;
        } });
    /**
     * Define the minimum distance between slider handles using the `distance` prop in the Angular Range Slider.
     * This ensures that the handles maintain a specified separation, preventing overlap and maintaining clear value distinctions.
     * @default 0
     */
    distance = input(0, { ...(ngDevMode ? { debugName: "distance" } : {}), transform: numberAttribute });
    /**
     * Add descriptive labels to your Angular Range Slider by providing an array of `labels`.
     * These labels enhance the slider's usability by clearly indicating key values and providing contextual information to users.
     */
    labels = input([], ...(ngDevMode ? [{ debugName: "labels" }] : []));
    /**
     * Specify the maximum value for the Angular Range Slider with the `max` prop.
     * This determines the upper limit of the slider's range, enabling precise control over the highest selectable value.
     * @default 100
     */
    max = input(100, { ...(ngDevMode ? { debugName: "max" } : {}), transform: numberAttribute });
    /**
     * Set the minimum value for the Angular Range Slider using the `min` prop.
     * This defines the lower bound of the slider's range, allowing you to control the starting point of user selection.
     * @default 0
     */
    min = input(0, { ...(ngDevMode ? { debugName: "min" } : {}), transform: numberAttribute });
    /**
     * Default role for c-range-slider.
     * @default 'slider'
     */
    role = input('slider', ...(ngDevMode ? [{ debugName: "role" }] : []));
    /**
     * Control the granularity of the Angular Range Slider by setting the `step` prop.
     * This defines the increment intervals between selectable values, allowing for precise adjustments based on your application's requirements.
     * @default 1
     */
    step = input(1, { ...(ngDevMode ? { debugName: "step" } : {}), transform: numberAttribute });
    /**
     * Toggle the visibility of tooltips in the Angular Range Slider with the `tooltips` prop.
     * When enabled, tooltips display the current value of the slider handles, providing real-time feedback to users.
     * @default true
     */
    tooltips = input(true, { ...(ngDevMode ? { debugName: "tooltips" } : {}), transform: booleanAttribute });
    /**
     * Customize the display format of tooltips in the Angular Range Slider using the `tooltipsFormat` function.
     * This allows you to format the tooltip values according to your specific requirements, enhancing the clarity and presentation of information.
     */
    tooltipsFormat = input(...(ngDevMode ? [undefined, { debugName: "tooltipsFormat" }] : []));
    /**
     * Controls the visual representation of the slider's track. When set to `'fill'`, the track is dynamically filled based on the slider's value(s).
     * Setting it to `false` disables the filled track.
     * @default 'fill'
     */
    track = input('fill', ...(ngDevMode ? [{ debugName: "track" }] : []));
    /**
     * Set the current value(s) of the Angular Range Slider using the `value` prop.
     * Whether you're using a single value or an array for multi-handle sliders, this prop controls the slider's position and ensures it reflects the desired state.
     */
    value = model([], ...(ngDevMode ? [{ debugName: "value" }] : []));
    valueChange = output();
    currentValue = computed(() => {
        const value = this.value();
        return Array.isArray(value) ? [...value] : [value];
    }
    // { equal: equalArrays }
    , ...(ngDevMode ? [{ debugName: "currentValue" }] : []));
    valueEffect = effect(() => {
        const currentValue = this.currentValue();
        const inputRefs = this.inputRefs();
        if (inputRefs.length) {
            inputRefs.forEach((inputRef, index) => {
                inputRef.nativeElement.value = currentValue[index].toString();
            });
        }
        this.#value$.next(currentValue);
    }, ...(ngDevMode ? [{ debugName: "valueEffect" }] : []));
    #value$ = new BehaviorSubject(this.currentValue());
    valueNow = computed(() => {
        const value = this.currentValue();
        return value.length === 1 ? value[0] : [...value];
    }, ...(ngDevMode ? [{ debugName: "valueNow" }] : []));
    inputIds = signal([], ...(ngDevMode ? [{ debugName: "inputIds" }] : []));
    inputIdsEffect = effect(() => {
        const sliders = this.currentValue().length;
        const inputIds = untracked(this.inputIds);
        if (sliders > 0) {
            const ids = new Array(sliders).fill(undefined);
            for (let i = 0; i < sliders; i++) {
                ids[i] = inputIds[i] ?? this.#UIDService.getUID(`range-slider-input-${i}`);
            }
            this.inputIds.set([...ids]);
        }
    }, ...(ngDevMode ? [{ debugName: "inputIdsEffect" }] : []));
    /**
     * Orient the Angular Range Slider vertically by setting the `vertical` prop to `true`.
     * This changes the slider's layout from horizontal to vertical, providing a different aesthetic and fitting various UI designs.
     */
    vertical = input(false, { ...(ngDevMode ? { debugName: "vertical" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            'range-slider': true,
            'range-slider-vertical': this.vertical(),
            disabled: this.disabled() || this.disabledState()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    inputRefs = viewChildren('inputRef', ...(ngDevMode ? [{ debugName: "inputRefs" }] : []));
    trackRef = viewChild('trackRef', ...(ngDevMode ? [{ debugName: "trackRef" }] : []));
    isDragging = signal(false, ...(ngDevMode ? [{ debugName: "isDragging" }] : []));
    dragIndex = signal(0, ...(ngDevMode ? [{ debugName: "dragIndex" }] : []));
    thumbSize = signal(null, ...(ngDevMode ? [{ debugName: "thumbSize" }] : []));
    isDraggingEffect = effect((onCleanup) => {
        const removeEventListeners = () => {
            this.#document.removeEventListener('mousemove', this.handleMouseMove);
            this.#document.removeEventListener('mouseup', this.handleMouseUp);
        };
        onCleanup(removeEventListeners);
        if (this.isDragging()) {
            this.#document.addEventListener('mousemove', this.handleMouseMove);
            this.#document.addEventListener('mouseup', this.handleMouseUp);
        }
        else {
            removeEventListeners();
        }
    }, ...(ngDevMode ? [{ debugName: "isDraggingEffect" }] : []));
    #getThumbSize = (element, vertical) => {
        const computedStyles = this.#document.defaultView?.getComputedStyle(element);
        const value = computedStyles?.getPropertyValue(vertical ? '--cui-range-slider-thumb-height' : '--cui-range-slider-thumb-width') ?? '2rem';
        const regex = /^(\d+\.?\d*)([%a-z]*)$/i;
        const match = value?.match(regex);
        if (match) {
            return {
                value: Number.parseFloat(match[1]),
                unit: match[2] || null
            };
        }
        return null;
    };
    constructor() {
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
        afterNextRender({
            read: () => {
                setTimeout(() => {
                    const thumbSize = this.#getThumbSize(this.#elementRef.nativeElement, this.vertical());
                    this.thumbSize.set(thumbSize);
                }, 1000);
            }
        });
    }
    ngOnInit() {
        this.#value$
            .pipe(debounceTime(300), distinctUntilChanged(equalArrays), takeUntilDestroyed(this.#destroyRef))
            .subscribe((value) => {
            const returnValue = value.length === 1 ? value[0] : [...value];
            this.onChange(returnValue);
            this.onTouched();
            this.valueChange.emit(returnValue);
        });
    }
    onChange = (value) => {
        /* empty */
    };
    onTouched = () => {
        /* empty */
    };
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabledState.set(isDisabled);
        // this.disabledState.set(isDisabled ? isDisabled : null);
    }
    writeValue(value) {
        this.value.set(Array.isArray(value) ? [...value] : [value]);
    }
    get isRtl() {
        return this.#RtlService.isRTL(this.#elementRef.nativeElement);
    }
    updateNearestValue = (value) => {
        const nearestIndex = getNearestValueIndex(value, this.currentValue());
        const newCurrentValue = [...this.currentValue()];
        newCurrentValue[nearestIndex] = validateValue(value, this.currentValue(), this.distance(), nearestIndex);
        setTimeout(() => {
            this.inputRefs()[nearestIndex].nativeElement.focus();
        });
        this.value.set(newCurrentValue);
    };
    handleInputChange(value, index) {
        this.isDragging.set(false);
        const newCurrentValue = updateValue(value, this.currentValue(), this.distance(), index);
        this.value.set([...newCurrentValue]);
    }
    handleInputsContainerMouseDown($event) {
        $event.preventDefault();
        if (!this.trackRef() || $event.button !== 0 || this.disabledState()) {
            return;
        }
        const validTarget = [...this.inputRefs(), this.trackRef()];
        if (!validTarget.find((target) => target?.nativeElement === $event.target)) {
            return;
        }
        const clickValue = calculateClickValue($event, this.trackRef()?.nativeElement, this.min(), this.max(), this.step(), this.vertical(), this.isRtl);
        const index = getNearestValueIndex(clickValue, this.currentValue());
        this.isDragging.set(true);
        this.dragIndex.set(index);
        this.updateNearestValue(clickValue);
    }
    handleLabelClick($event, label) {
        if (!this.clickableLabels() || this.disabledState() || $event.button !== 0) {
            return;
        }
        const labelValue = getLabelValue(this.min(), this.max(), this.labels(), label, this.labels().indexOf(label));
        this.updateNearestValue(labelValue);
    }
    getLabel(label) {
        return typeof label === 'object' && 'label' in label ? label.label : label;
    }
    handleMouseMove($event) {
        if (!this.isDragging() || !this.trackRef() || this.disabledState()) {
            return;
        }
        const moveValue = calculateMoveValue($event, this.trackRef()?.nativeElement, this.min(), this.max(), this.step(), this.vertical(), this.isRtl);
        const newCurrentValue = updateValue(moveValue, this.currentValue(), this.distance(), this.dragIndex());
        this.value.set(newCurrentValue);
    }
    handleMouseUp() {
        this.isDragging.set(false);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: RangeSliderComponent, isStandalone: true, selector: "c-range-slider", inputs: { clickableLabels: { classPropertyName: "clickableLabels", publicName: "clickableLabels", isSignal: true, isRequired: false, transformFunction: null }, disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, distance: { classPropertyName: "distance", publicName: "distance", isSignal: true, isRequired: false, transformFunction: null }, labels: { classPropertyName: "labels", publicName: "labels", isSignal: true, isRequired: false, transformFunction: null }, max: { classPropertyName: "max", publicName: "max", isSignal: true, isRequired: false, transformFunction: null }, min: { classPropertyName: "min", publicName: "min", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, step: { classPropertyName: "step", publicName: "step", isSignal: true, isRequired: false, transformFunction: null }, tooltips: { classPropertyName: "tooltips", publicName: "tooltips", isSignal: true, isRequired: false, transformFunction: null }, tooltipsFormat: { classPropertyName: "tooltipsFormat", publicName: "tooltipsFormat", isSignal: true, isRequired: false, transformFunction: null }, track: { classPropertyName: "track", publicName: "track", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, vertical: { classPropertyName: "vertical", publicName: "vertical", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { value: "valueChange", valueChange: "valueChange" }, host: { properties: { "class": "hostClasses()", "attr.disabled": "disabledState() || null", "attr.aria-disabled": "disabledState() || null", "attr.aria-orientation": "vertical() ? \"vertical\" : \"horizontal\"", "attr.aria-valuemax": "max()", "attr.aria-valuemin": "min()", "attr.aria-valuenow": "valueNow()", "attr.role": "role()" }, classAttribute: "range-slider" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => RangeSliderComponent),
                multi: true
            }
        ], viewQueries: [{ propertyName: "inputRefs", predicate: ["inputRef"], descendants: true, isSignal: true }, { propertyName: "trackRef", first: true, predicate: ["trackRef"], descendants: true, isSignal: true }], ngImport: i0, template: "<div (mousedown)=\"handleInputsContainerMouseDown($event)\" class=\"range-slider-inputs-container\">\n  @for (value of currentValue(); track index; let index = $index) {\n    <input\n      class=\"range-slider-input\"\n      type=\"range\"\n      [min]=\"min()\"\n      [max]=\"max()\"\n      [step]=\"step()\"\n      [value]=\"value\"\n      [attr.value]=\"value\"\n      [attr.aria-valuemin]=\"min()\"\n      [attr.aria-valuemax]=\"max()\"\n      [attr.aria-valuenow]=\"value\"\n      [attr.aria-orientation]=\"vertical() ? 'vertical' : 'horizontal'\"\n      [disabled]=\"disabledState()\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"handleInputChange($event, index)\"\n      [attr.id]=\"inputIds()[index]\"\n      role=\"slider\"\n      #inputRef\n    />\n    @if (tooltips()) {\n      <div\n        class=\"range-slider-tooltip\"\n        [style]=\"value | rangeSliderTooltipPositionStyle: min() : max() : vertical() : thumbSize() : isRtl\"\n      >\n        <div class=\"range-slider-tooltip-inner\">\n          @let tooltipsFn = tooltipsFormat();\n          <label for=\"{{inputIds()[index]}}\">\n            {{ tooltipsFn?.(value) ?? value }}\n          </label>\n        </div>\n        <div class=\"range-slider-tooltip-arrow\"></div>\n      </div>\n    }\n  }\n  @if (track()) {\n    <div\n      class=\"range-slider-track\"\n      [style]=\"currentValue() | rangeSliderTrack: min() : max() : vertical() : isRtl\"\n      #trackRef\n    ></div>\n  } @else {\n    <div class=\"range-slider-track\" #trackRef></div>\n  }\n</div>\n\n@if (labels()) {\n  <div class=\"range-slider-labels-container\" #labelsContainerRef>\n    @for (label of labels(); track index; let index = $index) {\n      @let labelValue = getLabel(label);\n      <div\n        #labelRef\n        [class]=\"label | rangeSliderLabelClass: clickableLabels()\"\n        [style]=\"label | rangeSliderLabelStyle: min() : max() : vertical() : labels() : index: isRtl\"\n        (mousedown)=\"handleLabelClick($event, label)\"\n      >\n        {{ labelValue }}\n      </div>\n    }\n  </div>\n}\n", styles: [""], dependencies: [{ kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i1.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1.RangeValueAccessor, selector: "input[type=range][formControlName],input[type=range][formControl],input[type=range][ngModel]" }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "pipe", type: RangeSliderTrackPipe, name: "rangeSliderTrack" }, { kind: "pipe", type: RangeSliderLabelStylePipe, name: "rangeSliderLabelStyle" }, { kind: "pipe", type: RangeSliderLabelClassPipe, name: "rangeSliderLabelClass" }, { kind: "pipe", type: RangeSliderTooltipPositionStylePipe, name: "rangeSliderTooltipPositionStyle" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-range-slider', imports: [
                        RangeSliderTrackPipe,
                        RangeSliderLabelStylePipe,
                        RangeSliderLabelClassPipe,
                        RangeSliderTooltipPositionStylePipe,
                        ReactiveFormsModule,
                        FormsModule
                    ], providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => RangeSliderComponent),
                            multi: true
                        }
                    ], host: {
                        class: 'range-slider',
                        '[class]': 'hostClasses()',
                        '[attr.disabled]': 'disabledState() || null',
                        '[attr.aria-disabled]': 'disabledState() || null',
                        '[attr.aria-orientation]': 'vertical() ? "vertical" : "horizontal"',
                        '[attr.aria-valuemax]': 'max()',
                        '[attr.aria-valuemin]': 'min()',
                        '[attr.aria-valuenow]': 'valueNow()',
                        '[attr.role]': 'role()'
                    }, template: "<div (mousedown)=\"handleInputsContainerMouseDown($event)\" class=\"range-slider-inputs-container\">\n  @for (value of currentValue(); track index; let index = $index) {\n    <input\n      class=\"range-slider-input\"\n      type=\"range\"\n      [min]=\"min()\"\n      [max]=\"max()\"\n      [step]=\"step()\"\n      [value]=\"value\"\n      [attr.value]=\"value\"\n      [attr.aria-valuemin]=\"min()\"\n      [attr.aria-valuemax]=\"max()\"\n      [attr.aria-valuenow]=\"value\"\n      [attr.aria-orientation]=\"vertical() ? 'vertical' : 'horizontal'\"\n      [disabled]=\"disabledState()\"\n      [ngModel]=\"value\"\n      (ngModelChange)=\"handleInputChange($event, index)\"\n      [attr.id]=\"inputIds()[index]\"\n      role=\"slider\"\n      #inputRef\n    />\n    @if (tooltips()) {\n      <div\n        class=\"range-slider-tooltip\"\n        [style]=\"value | rangeSliderTooltipPositionStyle: min() : max() : vertical() : thumbSize() : isRtl\"\n      >\n        <div class=\"range-slider-tooltip-inner\">\n          @let tooltipsFn = tooltipsFormat();\n          <label for=\"{{inputIds()[index]}}\">\n            {{ tooltipsFn?.(value) ?? value }}\n          </label>\n        </div>\n        <div class=\"range-slider-tooltip-arrow\"></div>\n      </div>\n    }\n  }\n  @if (track()) {\n    <div\n      class=\"range-slider-track\"\n      [style]=\"currentValue() | rangeSliderTrack: min() : max() : vertical() : isRtl\"\n      #trackRef\n    ></div>\n  } @else {\n    <div class=\"range-slider-track\" #trackRef></div>\n  }\n</div>\n\n@if (labels()) {\n  <div class=\"range-slider-labels-container\" #labelsContainerRef>\n    @for (label of labels(); track index; let index = $index) {\n      @let labelValue = getLabel(label);\n      <div\n        #labelRef\n        [class]=\"label | rangeSliderLabelClass: clickableLabels()\"\n        [style]=\"label | rangeSliderLabelStyle: min() : max() : vertical() : labels() : index: isRtl\"\n        (mousedown)=\"handleLabelClick($event, label)\"\n      >\n        {{ labelValue }}\n      </div>\n    }\n  </div>\n}\n" }]
        }], ctorParameters: () => [], propDecorators: { clickableLabels: [{ type: i0.Input, args: [{ isSignal: true, alias: "clickableLabels", required: false }] }], disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], distance: [{ type: i0.Input, args: [{ isSignal: true, alias: "distance", required: false }] }], labels: [{ type: i0.Input, args: [{ isSignal: true, alias: "labels", required: false }] }], max: [{ type: i0.Input, args: [{ isSignal: true, alias: "max", required: false }] }], min: [{ type: i0.Input, args: [{ isSignal: true, alias: "min", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], step: [{ type: i0.Input, args: [{ isSignal: true, alias: "step", required: false }] }], tooltips: [{ type: i0.Input, args: [{ isSignal: true, alias: "tooltips", required: false }] }], tooltipsFormat: [{ type: i0.Input, args: [{ isSignal: true, alias: "tooltipsFormat", required: false }] }], track: [{ type: i0.Input, args: [{ isSignal: true, alias: "track", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }, { type: i0.Output, args: ["valueChange"] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }], vertical: [{ type: i0.Input, args: [{ isSignal: true, alias: "vertical", required: false }] }], inputRefs: [{ type: i0.ViewChildren, args: ['inputRef', { isSignal: true }] }], trackRef: [{ type: i0.ViewChild, args: ['trackRef', { isSignal: true }] }] } });

class RangeSliderModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderModule, imports: [RangeSliderComponent], exports: [RangeSliderComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderModule, imports: [RangeSliderComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: RangeSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [RangeSliderComponent],
                    exports: [RangeSliderComponent]
                }]
        }] });

class SidebarService {
    #sidebarState = new BehaviorSubject({});
    sidebarState$ = this.#sidebarState.asObservable();
    toggle(action) {
        this.#sidebarState.next(action);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SidebarBackdropService {
    #document = inject(DOCUMENT);
    #sidebarService = inject(SidebarService);
    #backdrop;
    renderer;
    #clickListener = () => {
        /* empty */
    };
    setBackdrop(sidebar) {
        const backdrop = this.#document.getElementsByClassName('sidebar-backdrop');
        // console.log(`sidebar-${this.id}`, ' setBackdrop', backdrop);
        if (backdrop.length === 0) {
            this.#backdrop = this.renderer.createElement('div');
            this.renderer.addClass(this.#backdrop, 'sidebar-backdrop');
            this.renderer.appendChild(this.#document.body, this.#backdrop);
            this.#clickListener = this.renderer.listen(this.#backdrop, 'click', (e) => {
                // console.log(`sidebar-${this.id}`, ' backdrop click', e);
                this.#sidebarService.toggle({ toggle: 'visible', sidebar });
            });
        }
        // console.log(this.backdrop, sidebar.sidebarState.mobile, sidebar.sidebarState.show);
        if (this.#backdrop && sidebar.sidebarState.mobile && sidebar.sidebarState.visible) {
            this.renderer.addClass(this.#backdrop, 'fade');
            this.renderer.addClass(this.#backdrop, 'show');
            // this.renderer.removeClass(this.backdrop, 'd-none');
        }
        else {
            this.renderer.removeClass(this.#backdrop, 'show');
            this.renderer.removeClass(this.#backdrop, 'fade');
            // this.renderer.addClass(this.backdrop, 'd-none');
        }
    }
    clearBackdrop() {
        if (this.#backdrop) {
            // clear backdrop click Listener
            this.#clickListener();
            // this.renderer.listen(this.backdrop, 'click', (e): void => {} );
            this.renderer.removeChild(this.#document.body, this.#backdrop);
            // @ts-ignore
            this.#backdrop = undefined;
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarBackdropService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarBackdropService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarBackdropService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SidebarComponent {
    #document = inject(DOCUMENT);
    #renderer = inject(Renderer2);
    #breakpointObserver = inject(BreakpointObserver);
    #sidebarService = inject(SidebarService);
    #backdropService = inject(SidebarBackdropService);
    #onMobile = false;
    #layoutChangeSubscription;
    #stateToggleSubscription;
    state = signal({
        sidebar: this
    }, ...(ngDevMode ? [{ debugName: "state" }] : []));
    #stateInitial = {
        narrow: false,
        visible: false,
        unfoldable: false
    };
    /**
     * Sets if the color of text should be colored for a light or dark background.
     * @return 'dark' | 'light'
     */
    colorScheme = input(...(ngDevMode ? [undefined, { debugName: "colorScheme" }] : []));
    /**
     * Sets html attribute id.
     * @return string
     */
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    /**
     * Make sidebar narrow.
     * @return boolean
     * @default false
     */
    narrowInput = input(false, { ...(ngDevMode ? { debugName: "narrowInput" } : {}), transform: booleanAttribute, alias: 'narrow' });
    #narrow = linkedSignal(this.narrowInput, ...(ngDevMode ? [{ debugName: "#narrow" }] : []));
    set narrow(value) {
        this.#narrow.set(value);
    }
    get narrow() {
        return this.#narrow();
    }
    /**
     * Set sidebar to overlaid variant.
     * @return boolean
     * @default false
     */
    overlaid = input(false, { ...(ngDevMode ? { debugName: "overlaid" } : {}), transform: booleanAttribute });
    /**
     * Components placement, there’s no default placement.
     * @return 'start' | 'end'
     */
    placement = input(...(ngDevMode ? [undefined, { debugName: "placement" }] : []));
    /**
     * Place sidebar in non-static positions.
     * @return 'fixed' | 'sticky'
     * @default 'fixed'
     */
    position = input('fixed', ...(ngDevMode ? [{ debugName: "position" }] : []));
    /**
     * Size the component small, large, or extra large.
     * @return 'sm' | 'lg' | 'xl'
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Expand narrowed sidebar on hover.
     * @type boolean
     * @default false
     */
    unfoldableInput = input(false, { ...(ngDevMode ? { debugName: "unfoldableInput" } : {}), transform: booleanAttribute, alias: 'unfoldable' });
    unfoldable = linkedSignal({ ...(ngDevMode ? { debugName: "unfoldable" } : {}), source: this.unfoldableInput,
        computation: (value) => value });
    /**
     * Toggle the visibility of sidebar component.
     * @type boolean
     * @default false
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    #visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "#visible" }] : []));
    #visibleEffect = effect(() => {
        this.visibleChange?.emit(this.#visible());
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    set visible(value) {
        this.#visible.set(value);
    }
    get visible() {
        return this.#visible();
    }
    /**
     * Event emitted on visibility change.
     * @return boolean
     */
    visibleChange = output();
    set sidebarState(value) {
        const newState = value;
        if ('toggle' in newState) {
            if (newState.toggle === 'visible') {
                newState.visible = !this.state().visible;
                this.#visible.set(newState.visible);
            }
            else if (newState.toggle === 'unfoldable') {
                newState.unfoldable = !this.state().unfoldable;
                this.unfoldable.set(newState.unfoldable);
            }
        }
        else {
            this.#visible.update((visible) => (newState.visible ?? visible) && !this.overlaid());
        }
        this.state.update((state) => ({ ...state, ...newState }));
        this.state().mobile && this.state().visible
            ? this.#backdropService.setBackdrop(this)
            : this.#backdropService.clearBackdrop();
    }
    get sidebarState() {
        return { ...this.state() };
    }
    get getMobileBreakpoint() {
        const element = this.#document.documentElement;
        const mobileBreakpoint = this.#document.defaultView?.getComputedStyle(element)?.getPropertyValue('--cui-mobile-breakpoint') ?? 'md';
        const breakpointValue = this.#document.defaultView
            ?.getComputedStyle(element)
            ?.getPropertyValue(`--cui-breakpoint-${mobileBreakpoint.trim()}`) ?? '768px';
        return `${parseFloat(breakpointValue.trim()) - 0.02}px`;
    }
    constructor() {
        this.#backdropService.renderer = this.#renderer;
    }
    hostClasses = computed(() => {
        const { mobile, visible } = { ...this.sidebarState };
        const unfoldable = this.unfoldable();
        const placement = this.placement();
        const colorScheme = this.colorScheme();
        const size = this.size();
        return {
            sidebar: true,
            'sidebar-fixed': this.position() === 'fixed' && !mobile,
            'sidebar-narrow': this.#narrow() && !unfoldable,
            'sidebar-narrow-unfoldable': unfoldable,
            'sidebar-overlaid': this.overlaid(),
            [`sidebar-${placement}`]: !!placement,
            [`sidebar-${colorScheme}`]: !!colorScheme,
            [`sidebar-${size}`]: !!size,
            show: visible,
            // show: visible && this.#onMobile, //todo: check
            hide: !visible
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnInit() {
        this.setInitialState();
        this.layoutChangeSubscribe();
        this.stateToggleSubscribe();
    }
    ngOnDestroy() {
        this.stateToggleSubscribe(false);
        this.layoutChangeSubscribe(false);
    }
    ngOnChanges(changes) {
        const oldStateMap = new Map(Object.entries(this.state()));
        const newStateMap = new Map();
        newStateMap.set('sidebar', this);
        const propList = ['visible', 'unfoldable', 'narrow'];
        for (const propName in changes) {
            if (propList.includes(propName)) {
                if (changes[propName] && !changes[propName].firstChange) {
                    const value = booleanAttribute(changes[propName].currentValue);
                    if (oldStateMap.get(propName) !== value) {
                        newStateMap.set(propName, value);
                    }
                }
            }
        }
        if (newStateMap.size > 1) {
            const state = Object.fromEntries(newStateMap.entries());
            this.#sidebarService.toggle(state);
        }
    }
    setInitialState() {
        this.#stateInitial = {
            narrow: this.#narrow(),
            visible: this.#visible(),
            unfoldable: this.unfoldable()
        };
        this.#sidebarService.toggle({
            ...this.#stateInitial,
            sidebar: this
        });
    }
    stateToggleSubscribe(subscribe = true) {
        if (subscribe) {
            this.#stateToggleSubscription = this.#sidebarService.sidebarState$.subscribe((state) => {
                if (this === state.sidebar || this.id() === state.id) {
                    this.sidebarState = { ...state };
                }
            });
        }
        else {
            this.#stateToggleSubscription?.unsubscribe();
        }
    }
    layoutChangeSubscribe(subscribe = true) {
        const onMobile = `(max-width: ${this.getMobileBreakpoint})`;
        if (subscribe) {
            const layoutChanges = this.#breakpointObserver.observe([onMobile]);
            this.#layoutChangeSubscription = layoutChanges.subscribe((result) => {
                const isOnMobile = result.breakpoints[onMobile];
                const isUnfoldable = isOnMobile ? false : this.unfoldable();
                if (this.#onMobile !== isOnMobile) {
                    this.#onMobile = isOnMobile;
                    this.#sidebarService.toggle({
                        mobile: isOnMobile,
                        unfoldable: isUnfoldable,
                        visible: isOnMobile ? !isOnMobile : this.#stateInitial.visible,
                        sidebar: this
                    });
                }
            });
        }
        else {
            this.#layoutChangeSubscription?.unsubscribe();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: SidebarComponent, isStandalone: true, selector: "c-sidebar", inputs: { colorScheme: { classPropertyName: "colorScheme", publicName: "colorScheme", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, narrowInput: { classPropertyName: "narrowInput", publicName: "narrow", isSignal: true, isRequired: false, transformFunction: null }, overlaid: { classPropertyName: "overlaid", publicName: "overlaid", isSignal: true, isRequired: false, transformFunction: null }, placement: { classPropertyName: "placement", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, position: { classPropertyName: "position", publicName: "position", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, unfoldableInput: { classPropertyName: "unfoldableInput", publicName: "unfoldable", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { properties: { "class": "hostClasses()", "attr.inert": "!this.sidebarState.visible || null" }, classAttribute: "sidebar" }, exportAs: ["cSidebar"], usesOnChanges: true, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar',
                    exportAs: 'cSidebar',
                    template: '<ng-content />',
                    host: {
                        class: 'sidebar',
                        '[class]': 'hostClasses()',
                        '[attr.inert]': '!this.sidebarState.visible || null'
                    }
                }]
        }], ctorParameters: () => [], propDecorators: { colorScheme: [{ type: i0.Input, args: [{ isSignal: true, alias: "colorScheme", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], narrowInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "narrow", required: false }] }], overlaid: [{ type: i0.Input, args: [{ isSignal: true, alias: "overlaid", required: false }] }], placement: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], position: [{ type: i0.Input, args: [{ isSignal: true, alias: "position", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], unfoldableInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "unfoldable", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }] } });

class SidebarBrandComponent {
    brandFull = input(...(ngDevMode ? [undefined, { debugName: "brandFull" }] : []));
    brandNarrow = input(...(ngDevMode ? [undefined, { debugName: "brandNarrow" }] : []));
    routerLink = input(...(ngDevMode ? [undefined, { debugName: "routerLink" }] : []));
    brandImg = computed(() => Boolean(this.brandFull() || this.brandNarrow()), ...(ngDevMode ? [{ debugName: "brandImg" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarBrandComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarBrandComponent, isStandalone: true, selector: "c-sidebar-brand", inputs: { brandFull: { classPropertyName: "brandFull", publicName: "brandFull", isSignal: true, isRequired: false, transformFunction: null }, brandNarrow: { classPropertyName: "brandNarrow", publicName: "brandNarrow", isSignal: true, isRequired: false, transformFunction: null }, routerLink: { classPropertyName: "routerLink", publicName: "routerLink", isSignal: true, isRequired: false, transformFunction: null } }, host: { classAttribute: "sidebar-brand" }, ngImport: i0, template: "@if (brandImg()) {\n  <a [routerLink]=\"routerLink()\">\n    @if (brandFull()) {\n      <img [cHtmlAttr]=\"brandFull()\" [class]=\"'sidebar-brand-full'\">\n    }\n    @if (brandNarrow()) {\n      <img [cHtmlAttr]=\"brandNarrow()\" [class]=\"'sidebar-brand-narrow'\">\n    }\n  </a>\n} @else {\n  <ng-content />\n}\n", dependencies: [{ kind: "directive", type: RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarBrandComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-sidebar-brand', imports: [RouterLink, HtmlAttributesDirective], host: { class: 'sidebar-brand' }, template: "@if (brandImg()) {\n  <a [routerLink]=\"routerLink()\">\n    @if (brandFull()) {\n      <img [cHtmlAttr]=\"brandFull()\" [class]=\"'sidebar-brand-full'\">\n    }\n    @if (brandNarrow()) {\n      <img [cHtmlAttr]=\"brandNarrow()\" [class]=\"'sidebar-brand-narrow'\">\n    }\n  </a>\n} @else {\n  <ng-content />\n}\n" }]
        }], propDecorators: { brandFull: [{ type: i0.Input, args: [{ isSignal: true, alias: "brandFull", required: false }] }], brandNarrow: [{ type: i0.Input, args: [{ isSignal: true, alias: "brandNarrow", required: false }] }], routerLink: [{ type: i0.Input, args: [{ isSignal: true, alias: "routerLink", required: false }] }] } });

/**
 * Allows the sidebar to be toggled/folded via click on host element.
 */
class SidebarToggleDirective {
    #sidebarService = inject(SidebarService);
    /**
     * Id of sidebar for toggle action.
     * @return string
     */
    id = input(undefined, { ...(ngDevMode ? { debugName: "id" } : {}), alias: 'cSidebarToggle' });
    /**
     * Sidebar property name for toggle action.
     *
     * @return 'visible' | 'unfoldable'
     * @default 'visible'
     */
    toggle = input('visible', ...(ngDevMode ? [{ debugName: "toggle" }] : []));
    toggleOpen($event) {
        $event.preventDefault();
        this.#sidebarService.toggle({ toggle: this.toggle(), id: this.id() });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: SidebarToggleDirective, isStandalone: true, selector: "[cSidebarToggle]", inputs: { id: { classPropertyName: "id", publicName: "cSidebarToggle", isSignal: true, isRequired: false, transformFunction: null }, toggle: { classPropertyName: "toggle", publicName: "toggle", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "toggleOpen($event)" } }, exportAs: ["cSidebarToggle"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cSidebarToggle]',
                    exportAs: 'cSidebarToggle',
                    host: {
                        '(click)': 'toggleOpen($event)'
                    }
                }]
        }], propDecorators: { id: [{ type: i0.Input, args: [{ isSignal: true, alias: "cSidebarToggle", required: false }] }], toggle: [{ type: i0.Input, args: [{ isSignal: true, alias: "toggle", required: false }] }] } });

class SidebarTogglerDirective {
    role = input('button', ...(ngDevMode ? [{ debugName: "role" }] : []));
    get getStyles() {
        return {
            appearance: 'button',
            'align-items': 'flex-start',
            cursor: 'pointer'
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarTogglerDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: SidebarTogglerDirective, isStandalone: true, selector: "[cSidebarToggler]", inputs: { role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "attr.role": "role()", "style": "getStyles" }, classAttribute: "sidebar-toggler" }, hostDirectives: [{ directive: SidebarToggleDirective, inputs: ["cSidebarToggle", "cSidebarToggler", "toggle", "toggle"] }], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarTogglerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cSidebarToggler]',
                    hostDirectives: [{ directive: SidebarToggleDirective, inputs: ['cSidebarToggle: cSidebarToggler', 'toggle'] }],
                    host: {
                        '[attr.role]': 'role()',
                        class: 'sidebar-toggler',
                        '[style]': 'getStyles'
                    }
                }]
        }], propDecorators: { role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class SidebarHeaderComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: SidebarHeaderComponent, isStandalone: true, selector: "c-sidebar-header", host: { classAttribute: "sidebar-header" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar-header',
                    template: '<ng-content />',
                    host: { class: 'sidebar-header' }
                }]
        }] });

class SidebarFooterComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarFooterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: SidebarFooterComponent, isStandalone: true, selector: "c-sidebar-footer", host: { classAttribute: "sidebar-footer" }, ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarFooterComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar-footer',
                    template: '<ng-content />',
                    host: { class: 'sidebar-footer' }
                }]
        }] });

class SidebarNavService {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavService, decorators: [{
            type: Injectable
        }] });
class SidebarNavHelper {
    itemType(item) {
        if (item.divider) {
            return 'divider';
        }
        else if (item.title) {
            return 'title';
        }
        else if (item.children && item.children.length > 0) {
            return 'group';
        }
        else if (item.label) {
            return 'label';
        }
        else if (!Object.keys(item).length) {
            return 'empty';
        }
        else {
            return 'link';
        }
    }
    isActive(router, item) {
        return router.isActive(item.url, false);
    }
    hasBadge = (item) => Boolean(item?.badge);
    hasIcon = (item) => Boolean(item?.icon) || item?.icon === '';
    hasIconComponent = (item) => Boolean(item?.iconComponent);
    getIconClass(item) {
        const classes = {
            'nav-icon': true
        };
        const icon = item.icon;
        // @ts-ignore
        classes[icon] = this.hasIcon(item);
        return classes;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavHelper, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavHelper, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavHelper, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class SidebarNavGroupService {
    sidebarNavGroupState = new BehaviorSubject({});
    sidebarNavGroupState$ = this.sidebarNavGroupState.asObservable();
    toggle(action) {
        this.sidebarNavGroupState.next(action);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavGroupService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavGroupService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavGroupService, decorators: [{
            type: Injectable
        }] });

class SidebarNavIconPipe {
    transform(item, args) {
        const icon = item.icon;
        return {
            'nav-icon': true,
            [`${icon}`]: !!icon
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavIconPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavIconPipe, isStandalone: true, name: "cSidebarNavIcon" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavIconPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cSidebarNavIcon'
                }]
        }] });

class SidebarNavBadgePipe {
    transform(item, args) {
        const badge = item.badge;
        return {
            badge: true,
            'ms-auto': true,
            'badge-sm': !badge.size,
            [`badge-${badge.size}`]: !!badge.size,
            [`bg-${badge.color}`]: !!badge.color,
            [`${badge.class}`]: !!badge.class
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavBadgePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavBadgePipe, isStandalone: true, name: "cSidebarNavBadge" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavBadgePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cSidebarNavBadge'
                }]
        }] });

class SidebarNavLinkPipe {
    transform(item) {
        const disabled = item?.attributes?.disabled;
        return {
            'nav-link': true,
            disabled,
            'btn-link': disabled,
            [`nav-link-${item.variant}`]: !!item.variant
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkPipe, isStandalone: true, name: "cSidebarNavLink" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cSidebarNavLink'
                }]
        }] });

class SidebarNavLinkContentComponent {
    helper = inject(SidebarNavHelper);
    item = input({}, ...(ngDevMode ? [{ debugName: "item" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarNavLinkContentComponent, isStandalone: true, selector: "c-sidebar-nav-link-content", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null } }, providers: [SidebarNavHelper], ngImport: i0, template: `
    @let itemLinkContent = item();
    @if (itemLinkContent) {
      <ng-container>{{ itemLinkContent?.name ?? '' }}</ng-container>
    }
  `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkContentComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar-nav-link-content',
                    template: `
    @let itemLinkContent = item();
    @if (itemLinkContent) {
      <ng-container>{{ itemLinkContent?.name ?? '' }}</ng-container>
    }
  `,
                    providers: [SidebarNavHelper]
                }]
        }], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }] } });
class SidebarNavLinkComponent {
    router = inject(Router);
    item = input(...(ngDevMode ? [undefined, { debugName: "item" }] : []));
    linkClick = output();
    linkType;
    href;
    linkActive;
    url;
    navigationEndObservable;
    navSubscription;
    constructor() {
        const router = this.router;
        this.navigationEndObservable = router.events.pipe(filter((event) => {
            return event instanceof NavigationEnd;
        }), takeUntilDestroyed());
    }
    ngOnInit() {
        const item = this.item() ?? {};
        this.url =
            typeof item.url === 'string'
                ? item.url
                : this.router.serializeUrl(this.router.createUrlTree(item.url ?? ['']));
        this.linkType = this.getLinkType();
        this.href = this.isDisabled() ? '' : item.href || this.url;
        this.linkActive = this.router.url.split(/[?#(;]/)[0] === this.href.split(/[?#(;]/)[0];
        this.navSubscription = this.navigationEndObservable.subscribe((event) => {
            const itemUrlArray = this.href.split(/[?#(;]/)[0].split('/');
            const urlArray = event.urlAfterRedirects.split(/[?#(;]/)[0].split('/');
            this.linkActive = itemUrlArray.every((value, index) => value === urlArray[index]);
        });
    }
    ngOnDestroy() {
        this.navSubscription?.unsubscribe();
    }
    getLinkType() {
        return this.isDisabled() ? 'disabled' : this.isExternalLink() ? 'external' : 'link';
    }
    isDisabled() {
        return this.item()?.attributes?.['disabled'];
    }
    isExternalLink() {
        const item = this.item() ?? {};
        const linkPath = Array.isArray(item.url) ? item.url[0] : item.url;
        return !!item.href || linkPath?.substring(0, 4) === 'http';
    }
    linkClicked() {
        this.linkClick?.emit();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarNavLinkComponent, isStandalone: true, selector: "c-sidebar-nav-link", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { linkClick: "linkClick" }, providers: [SidebarNavHelper], ngImport: i0, template: "@let linkItem = item() ?? {};\n\n@switch (linkType) {\n  @case ('disabled') {\n    <a [cHtmlAttr]=\"linkItem.attributes ?? {}\" [class]=\"linkItem | cSidebarNavLink\">\n      <ng-container *ngTemplateOutlet=\"iconTemplate; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n  @case ('external') {\n    <a (click)=\"linkClicked()\" [cHtmlAttr]=\"linkItem.attributes ?? {}\" [href]=\"href\" [class]=\"linkItem | cSidebarNavLink\">\n      <ng-container *ngTemplateOutlet=\"iconTemplate; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n  @default {\n    <a (click)=\"linkClicked()\"\n       [cHtmlAttr]=\"linkItem.attributes ?? {}\"\n       [fragment]=\"linkItem.linkProps?.fragment\"\n       [class]=\"linkItem | cSidebarNavLink\"\n       [preserveFragment]=\"linkItem.linkProps?.preserveFragment ?? false\"\n       [queryParamsHandling]=\"linkItem.linkProps?.queryParamsHandling\"\n       [queryParams]=\"linkItem.linkProps?.queryParams ?? null\"\n       [replaceUrl]=\"linkItem.linkProps?.replaceUrl ?? false\"\n       [routerLinkActiveOptions]=\"linkItem.linkProps?.routerLinkActiveOptions ?? { exact: false }\"\n       [routerLink]=\"linkItem.url\"\n       [skipLocationChange]=\"linkItem.linkProps?.skipLocationChange ?? false\"\n       [state]=\"linkItem.linkProps?.state ?? {}\"\n       [target]=\"linkItem.attributes?.['target']\"\n       routerLinkActive=\"active\"\n    >\n      <!--    [class.active]=\"linkActive\"-->\n      <ng-container *ngTemplateOutlet=\"iconTemplate ; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n}\n<ng-template #iconTemplate let-item>\n  <!--  <i *ngIf=\"item?.icon\" [class]=\"item | cSidebarNavIcon\"></i>-->\n  @if (item?.icon) {\n    <span class=\"nav-icon\">\n      <span [class]=\"item.icon ?? ''\"></span>\n    </span>\n  }\n  @if (item?.iconComponent) {\n    <svg\n      [cIcon]=\"item.iconComponent?.content\"\n      [customClasses]=\"item | cSidebarNavIcon\"\n      [name]=\"item.iconComponent?.name\"\n    ></svg>\n  }\n  @if (!item?.icon && !item?.iconComponent) {\n    <span [class]=\"item | cSidebarNavIcon\"></span>\n  }\n</ng-template>\n", dependencies: [{ kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i1$1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i1$1.RouterLinkActive, selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }, { kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "directive", type: IconDirective, selector: "svg[cIcon]", inputs: ["cIcon", "customClasses", "size", "title", "height", "width", "name", "viewBox", "xmlns", "pointer-events", "role"], exportAs: ["cIcon"] }, { kind: "component", type: SidebarNavLinkContentComponent, selector: "c-sidebar-nav-link-content", inputs: ["item"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: SidebarNavLinkPipe, name: "cSidebarNavLink" }, { kind: "pipe", type: SidebarNavBadgePipe, name: "cSidebarNavBadge" }, { kind: "pipe", type: SidebarNavIconPipe, name: "cSidebarNavIcon" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLinkComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-sidebar-nav-link', providers: [SidebarNavHelper], imports: [
                        RouterModule,
                        HtmlAttributesDirective,
                        IconDirective,
                        SidebarNavLinkContentComponent,
                        SidebarNavLinkPipe,
                        SidebarNavBadgePipe,
                        SidebarNavIconPipe,
                        NgTemplateOutlet
                    ], template: "@let linkItem = item() ?? {};\n\n@switch (linkType) {\n  @case ('disabled') {\n    <a [cHtmlAttr]=\"linkItem.attributes ?? {}\" [class]=\"linkItem | cSidebarNavLink\">\n      <ng-container *ngTemplateOutlet=\"iconTemplate; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n  @case ('external') {\n    <a (click)=\"linkClicked()\" [cHtmlAttr]=\"linkItem.attributes ?? {}\" [href]=\"href\" [class]=\"linkItem | cSidebarNavLink\">\n      <ng-container *ngTemplateOutlet=\"iconTemplate; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n  @default {\n    <a (click)=\"linkClicked()\"\n       [cHtmlAttr]=\"linkItem.attributes ?? {}\"\n       [fragment]=\"linkItem.linkProps?.fragment\"\n       [class]=\"linkItem | cSidebarNavLink\"\n       [preserveFragment]=\"linkItem.linkProps?.preserveFragment ?? false\"\n       [queryParamsHandling]=\"linkItem.linkProps?.queryParamsHandling\"\n       [queryParams]=\"linkItem.linkProps?.queryParams ?? null\"\n       [replaceUrl]=\"linkItem.linkProps?.replaceUrl ?? false\"\n       [routerLinkActiveOptions]=\"linkItem.linkProps?.routerLinkActiveOptions ?? { exact: false }\"\n       [routerLink]=\"linkItem.url\"\n       [skipLocationChange]=\"linkItem.linkProps?.skipLocationChange ?? false\"\n       [state]=\"linkItem.linkProps?.state ?? {}\"\n       [target]=\"linkItem.attributes?.['target']\"\n       routerLinkActive=\"active\"\n    >\n      <!--    [class.active]=\"linkActive\"-->\n      <ng-container *ngTemplateOutlet=\"iconTemplate ; context: {$implicit: linkItem}\" />\n      <c-sidebar-nav-link-content [item]=\"linkItem\" />\n      @if (linkItem.badge) {\n        <span [class]=\"linkItem | cSidebarNavBadge\">{{ linkItem.badge?.text }}</span>\n      }\n    </a>\n  }\n}\n<ng-template #iconTemplate let-item>\n  <!--  <i *ngIf=\"item?.icon\" [class]=\"item | cSidebarNavIcon\"></i>-->\n  @if (item?.icon) {\n    <span class=\"nav-icon\">\n      <span [class]=\"item.icon ?? ''\"></span>\n    </span>\n  }\n  @if (item?.iconComponent) {\n    <svg\n      [cIcon]=\"item.iconComponent?.content\"\n      [customClasses]=\"item | cSidebarNavIcon\"\n      [name]=\"item.iconComponent?.name\"\n    ></svg>\n  }\n  @if (!item?.icon && !item?.iconComponent) {\n    <span [class]=\"item | cSidebarNavIcon\"></span>\n  }\n</ng-template>\n" }]
        }], ctorParameters: () => [], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }], linkClick: [{ type: i0.Output, args: ["linkClick"] }] } });

class SidebarNavLabelComponent {
    helper = inject(SidebarNavHelper);
    item = input({}, ...(ngDevMode ? [{ debugName: "item" }] : []));
    itemClass = computed(() => {
        const classes = {
            'c-nav-label': true,
            'c-active': true
        };
        const itemClass = this.item().class;
        if (itemClass) {
            classes[itemClass] = !!itemClass;
        }
        return classes;
    }, ...(ngDevMode ? [{ debugName: "itemClass" }] : []));
    labelIconClass = computed(() => {
        const item = this.item();
        const iconClasses = this.helper.getIconClass(item);
        const variant = `text-${item.label?.variant}`;
        iconClasses[variant] = !!item.label?.variant;
        const labelClass = item.label?.class ?? '';
        iconClasses[labelClass] = !!labelClass;
        return iconClasses;
    }, ...(ngDevMode ? [{ debugName: "labelIconClass" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLabelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarNavLabelComponent, isStandalone: true, selector: "c-sidebar-nav-label", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "@let labelItem = item();\n<a [cHtmlAttr]=\"labelItem.attributes\"\n   [class]=\"itemClass()\"\n   href=\"{{labelItem.url}}\">\n  @if (helper.hasIcon(labelItem)) {\n    <i [class]=\"labelIconClass()\"></i>\n  }\n  <ng-container>{{ labelItem.name }}</ng-container>\n  @if (helper.hasBadge(labelItem)) {\n    <span [class]=\"labelItem | cSidebarNavBadge\">{{ labelItem.badge?.text ?? '' }}</span>\n  }\n</a>\n", dependencies: [{ kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "pipe", type: SidebarNavBadgePipe, name: "cSidebarNavBadge" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavLabelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-sidebar-nav-label', imports: [HtmlAttributesDirective, SidebarNavBadgePipe], template: "@let labelItem = item();\n<a [cHtmlAttr]=\"labelItem.attributes\"\n   [class]=\"itemClass()\"\n   href=\"{{labelItem.url}}\">\n  @if (helper.hasIcon(labelItem)) {\n    <i [class]=\"labelIconClass()\"></i>\n  }\n  <ng-container>{{ labelItem.name }}</ng-container>\n  @if (helper.hasBadge(labelItem)) {\n    <span [class]=\"labelItem | cSidebarNavBadge\">{{ labelItem.badge?.text ?? '' }}</span>\n  }\n</a>\n" }]
        }], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }] } });

class SidebarNavTitleComponent {
    #elementRef = inject(ElementRef);
    #renderer = inject(Renderer2);
    item = input(...(ngDevMode ? [undefined, { debugName: "item" }] : []));
    #itemEffect = effect(() => {
        const item = this.item();
        if (item?.name) {
            const nativeElement = this.#elementRef.nativeElement;
            const name = this.#renderer.createText(item.name);
            if (item?.class) {
                const classes = item.class;
                this.#renderer.addClass(nativeElement, classes);
            }
            if (item?.wrapper) {
                const wrapper = this.#renderer.createElement(item.wrapper.element);
                this.addAttribs(item.wrapper.attributes, wrapper);
                this.#renderer.appendChild(wrapper, name);
                this.#renderer.appendChild(nativeElement, wrapper);
            }
            else {
                this.#renderer.appendChild(nativeElement, name);
            }
        }
    }, ...(ngDevMode ? [{ debugName: "#itemEffect" }] : []));
    addAttribs(attribs, element) {
        if (attribs) {
            for (const attr in attribs) {
                if (attr === 'style' && typeof attribs[attr] === 'object') {
                    this.setStyle(attribs[attr], element);
                }
                else if (attr === 'class') {
                    this.addClass(attribs[attr], element);
                }
                else {
                    this.setAttrib(attr, attribs[attr], element);
                }
            }
        }
    }
    setStyle(styles, el) {
        for (const style in styles) {
            if (style) {
                this.#renderer.setStyle(el, style, styles[style]);
            }
        }
    }
    addClass(classes, el) {
        const classArray = Array.isArray(classes) ? classes : classes.split(' ');
        classArray
            .filter((element) => element.length > 0)
            .forEach((element) => {
            this.#renderer.addClass(el, element);
        });
    }
    setAttrib(key, value, el) {
        this.#renderer.setAttribute(el, key, value);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: SidebarNavTitleComponent, isStandalone: true, selector: "c-sidebar-nav-title", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: '', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavTitleComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar-nav-title',
                    template: ''
                }]
        }], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }] } });

class SidebarNavDividerComponent {
    item = input(...(ngDevMode ? [undefined, { debugName: "item" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavDividerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: SidebarNavDividerComponent, isStandalone: true, selector: "c-sidebar-nav-divider", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: ``, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavDividerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-sidebar-nav-divider',
                    template: ``
                }]
        }], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }] } });

class SidebarNavItemClassPipe {
    helper = inject(SidebarNavHelper);
    // transform(item: any, ...args: any[]): any {
    transform(item, args) {
        const itemType = this.helper.itemType(item);
        let itemClass;
        if (['divider', 'title'].includes(itemType)) {
            itemClass = `nav-${itemType}`;
        }
        else if (itemType === 'group') {
            // itemClass = 'c-sidebar-nav-group' ;
            itemClass = '';
        }
        else {
            itemClass = 'nav-item';
        }
        return item.class ? `${itemClass} ${item.class}` : itemClass;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavItemClassPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavItemClassPipe, isStandalone: true, name: "cSidebarNavItemClass" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavItemClassPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'cSidebarNavItemClass'
                }]
        }] });

class SidebarNavGroupComponent {
    #router = inject(Router);
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #sidebarNavGroupService = inject(SidebarNavGroupService);
    helper = inject(SidebarNavHelper);
    constructor() {
        const router = this.#router;
        this.navigationEndObservable = router.events.pipe(filter((event) => event instanceof NavigationEnd));
    }
    item = input(...(ngDevMode ? [undefined, { debugName: "item" }] : []));
    dropdownMode = input('path', ...(ngDevMode ? [{ debugName: "dropdownMode" }] : []));
    show = input(...(ngDevMode ? [undefined, { debugName: "show" }] : []));
    compact = input(undefined, { ...(ngDevMode ? { debugName: "compact" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        return {
            'nav-group': true,
            show: this.open()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    sidebarNav = viewChild.required(forwardRef(() => SidebarNavComponent), { read: ElementRef });
    navigationEndObservable;
    navSubscription;
    navGroupSubscription;
    open = signal(undefined, ...(ngDevMode ? [{ debugName: "open" }] : []));
    navItems = signal([], ...(ngDevMode ? [{ debugName: "navItems" }] : []));
    display = signal({ display: 'block' }, ...(ngDevMode ? [{ debugName: "display" }] : []));
    ngOnInit() {
        this.navItems.set([...(this.item()?.children ?? [])]);
        this.navSubscription = this.navigationEndObservable.subscribe((event) => {
            if (this.dropdownMode() !== 'none') {
                const samePath = this.samePath(event.url);
                this.openGroup(samePath);
            }
        });
        if (this.samePath(this.#router.routerState.snapshot.url)) {
            this.openGroup(true);
        }
        this.navGroupSubscription = this.#sidebarNavGroupService.sidebarNavGroupState$.subscribe((next) => {
            if (this.dropdownMode() === 'close' && next.sidebarNavGroup && next.sidebarNavGroup !== this) {
                const url = next.sidebarNavGroup.item()?.url ?? [];
                const normUrl = Array.isArray(url) ? this.#router.createUrlTree(url).toString() : url;
                const itemUrl = this.item()?.url ?? [];
                const normItemUrl = Array.isArray(normUrl) ? this.#router.createUrlTree(normUrl).toString() : normUrl;
                if (normUrl.startsWith(normItemUrl)) {
                    return;
                }
                if (this.samePath(this.#router.routerState.snapshot.url)) {
                    this.openGroup(true);
                    return;
                }
                this.openGroup(false);
            }
        });
    }
    samePath(url) {
        // console.log('item:', this.item.name, this.item.url, 'url:', url);
        const itemUrl = this.item()?.url ?? [];
        const itemArray = Array.isArray(itemUrl) ? itemUrl : itemUrl.split('/');
        const urlArray = url.split('/');
        return itemArray?.every((value, index) => {
            // console.log(value === urlArray[index], 'value:', value, 'index:', index, urlArray[index], url);
            return value === urlArray[index];
        });
    }
    openGroup(open) {
        this.open.set(open);
    }
    toggleGroup($event) {
        $event.preventDefault();
        this.openGroup(!this.open());
        if (this.open()) {
            this.#sidebarNavGroupService.toggle({ open: this.open(), sidebarNavGroup: this });
        }
    }
    ngOnDestroy() {
        this.navSubscription?.unsubscribe();
    }
    onAnimationStart($event) {
        this.display.set({ display: 'block' });
        setTimeout(() => {
            const host = this.sidebarNav()?.nativeElement;
            if ($event.toState === 'open' && host) {
                this.#renderer.setStyle(host, 'height', `${host['scrollHeight']}px`);
            }
        });
    }
    onAnimationDone($event) {
        setTimeout(() => {
            const host = this.sidebarNav()?.nativeElement;
            if ($event.toState === 'open' && host) {
                this.#renderer.setStyle(host, 'height', 'auto');
            }
            if ($event.toState === 'closed') {
                setTimeout(() => {
                    this.display.set(null);
                });
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavGroupComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarNavGroupComponent, isStandalone: true, selector: "c-sidebar-nav-group", inputs: { item: { classPropertyName: "item", publicName: "item", isSignal: true, isRequired: false, transformFunction: null }, dropdownMode: { classPropertyName: "dropdownMode", publicName: "dropdownMode", isSignal: true, isRequired: false, transformFunction: null }, show: { classPropertyName: "show", publicName: "show", isSignal: true, isRequired: false, transformFunction: null }, compact: { classPropertyName: "compact", publicName: "compact", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, providers: [SidebarNavHelper, SidebarNavGroupService], viewQueries: [{ propertyName: "sidebarNav", first: true, predicate: i0.forwardRef(() => SidebarNavComponent), descendants: true, read: ElementRef, isSignal: true }], ngImport: i0, template: "@let grpItem = item();\n<a (click)=\"toggleGroup($event)\"\n   [cHtmlAttr]=\"grpItem?.attributes\"\n   class=\"nav-link nav-group-toggle\"\n   href>\n  <ng-container *ngTemplateOutlet=\"iconTemplate ; context: {$implicit: grpItem}\" />\n  <ng-container>{{ grpItem?.name }}</ng-container>\n  @if (grpItem?.badge) {\n    <span [class]=\"grpItem | cSidebarNavBadge\">{{ grpItem?.badge?.text }}</span>\n  }\n</a>\n<c-sidebar-nav\n  (@openClose.done)=\"onAnimationDone($event)\"\n  (@openClose.start)=\"onAnimationStart($event)\"\n  [@openClose]=\"open() ? 'open' : 'closed'\"\n  [compact]=\"compact()\"\n  [dropdownMode]=\"dropdownMode()\"\n  [groupItems]=\"true\"\n  [navItems]=\"navItems()\"\n  [style]=\"display()\"\n/>\n\n<ng-template #iconTemplate let-item>\n  <!--  <i *ngIf=\"item?.icon\" [class]=\"item | cSidebarNavIcon\"></i>-->\n  @if (item?.icon) {\n    <span class=\"nav-icon\">\n    <span [class]=\"item.icon ?? ''\"></span>\n  </span>\n  }\n  @if (item?.iconComponent) {\n    <svg\n      [cIcon]=\"item.iconComponent?.content\"\n      [customClasses]=\"item | cSidebarNavIcon\"\n      [name]=\"item.iconComponent?.name\"\n    />\n  }\n  @if (!item?.icon && !item?.iconComponent) {\n    <span [class]=\"item | cSidebarNavIcon\"></span>\n  }\n</ng-template>\n", styles: [".nav-group-toggle{cursor:pointer}.nav-group-items{display:block}\n"], dependencies: [{ kind: "directive", type: i0.forwardRef(() => HtmlAttributesDirective), selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "directive", type: i0.forwardRef(() => IconDirective), selector: "svg[cIcon]", inputs: ["cIcon", "customClasses", "size", "title", "height", "width", "name", "viewBox", "xmlns", "pointer-events", "role"], exportAs: ["cIcon"] }, { kind: "directive", type: i0.forwardRef(() => NgTemplateOutlet), selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavComponent), selector: "c-sidebar-nav", inputs: ["navItems", "dropdownMode", "groupItems", "compact", "role"] }, { kind: "pipe", type: i0.forwardRef(() => SidebarNavIconPipe), name: "cSidebarNavIcon" }, { kind: "pipe", type: i0.forwardRef(() => SidebarNavBadgePipe), name: "cSidebarNavBadge" }], animations: [
            trigger('openClose', [
                state('open', style({
                    height: '*'
                })),
                state('closed', style({
                    height: '0px'
                })),
                transition('open <=> closed', [animate('.15s ease')])
            ])
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavGroupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-sidebar-nav-group', providers: [SidebarNavHelper, SidebarNavGroupService], imports: [
                        HtmlAttributesDirective,
                        IconDirective,
                        NgTemplateOutlet,
                        SidebarNavIconPipe,
                        SidebarNavBadgePipe,
                        forwardRef(() => SidebarNavComponent)
                    ], animations: [
                        trigger('openClose', [
                            state('open', style({
                                height: '*'
                            })),
                            state('closed', style({
                                height: '0px'
                            })),
                            transition('open <=> closed', [animate('.15s ease')])
                        ])
                    ], host: {
                        '[class]': 'hostClasses()'
                    }, template: "@let grpItem = item();\n<a (click)=\"toggleGroup($event)\"\n   [cHtmlAttr]=\"grpItem?.attributes\"\n   class=\"nav-link nav-group-toggle\"\n   href>\n  <ng-container *ngTemplateOutlet=\"iconTemplate ; context: {$implicit: grpItem}\" />\n  <ng-container>{{ grpItem?.name }}</ng-container>\n  @if (grpItem?.badge) {\n    <span [class]=\"grpItem | cSidebarNavBadge\">{{ grpItem?.badge?.text }}</span>\n  }\n</a>\n<c-sidebar-nav\n  (@openClose.done)=\"onAnimationDone($event)\"\n  (@openClose.start)=\"onAnimationStart($event)\"\n  [@openClose]=\"open() ? 'open' : 'closed'\"\n  [compact]=\"compact()\"\n  [dropdownMode]=\"dropdownMode()\"\n  [groupItems]=\"true\"\n  [navItems]=\"navItems()\"\n  [style]=\"display()\"\n/>\n\n<ng-template #iconTemplate let-item>\n  <!--  <i *ngIf=\"item?.icon\" [class]=\"item | cSidebarNavIcon\"></i>-->\n  @if (item?.icon) {\n    <span class=\"nav-icon\">\n    <span [class]=\"item.icon ?? ''\"></span>\n  </span>\n  }\n  @if (item?.iconComponent) {\n    <svg\n      [cIcon]=\"item.iconComponent?.content\"\n      [customClasses]=\"item | cSidebarNavIcon\"\n      [name]=\"item.iconComponent?.name\"\n    />\n  }\n  @if (!item?.icon && !item?.iconComponent) {\n    <span [class]=\"item | cSidebarNavIcon\"></span>\n  }\n</ng-template>\n", styles: [".nav-group-toggle{cursor:pointer}.nav-group-items{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { item: [{ type: i0.Input, args: [{ isSignal: true, alias: "item", required: false }] }], dropdownMode: [{ type: i0.Input, args: [{ isSignal: true, alias: "dropdownMode", required: false }] }], show: [{ type: i0.Input, args: [{ isSignal: true, alias: "show", required: false }] }], compact: [{ type: i0.Input, args: [{ isSignal: true, alias: "compact", required: false }] }], sidebarNav: [{ type: i0.ViewChild, args: [forwardRef(() => SidebarNavComponent), { ...{ read: ElementRef }, isSignal: true }] }] } });
class SidebarNavComponent {
    sidebar = inject(SidebarComponent, { optional: true });
    helper = inject(SidebarNavHelper);
    router = inject(Router);
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    #sidebarService = inject(SidebarService);
    navItems = input([], ...(ngDevMode ? [{ debugName: "navItems" }] : []));
    dropdownMode = input('path', ...(ngDevMode ? [{ debugName: "dropdownMode" }] : []));
    groupItems = input(undefined, { ...(ngDevMode ? { debugName: "groupItems" } : {}), transform: booleanAttribute });
    compact = input(undefined, { ...(ngDevMode ? { debugName: "compact" } : {}), transform: booleanAttribute });
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        const groupItems = this.groupItems();
        return {
            'sidebar-nav': !groupItems,
            'nav-group-items': groupItems,
            compact: groupItems && this.compact()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    // @HostBinding('class.nav-group-items')
    // get sidebarNavGroupItemsClass(): boolean {
    //   return !!this.groupItems;
    // }
    navItemsArray = signal([], ...(ngDevMode ? [{ debugName: "navItemsArray" }] : []));
    ngOnChanges(changes) {
        const navItems = this.navItems();
        this.navItemsArray.set(Array.isArray(navItems) ? navItems.slice() : []);
    }
    hideMobile() {
        // todo: proper scrollIntoView() after NavigationEnd
        if (this.sidebar && this.sidebar.sidebarState.mobile) {
            this.#sidebarService.toggle({ toggle: 'visible', sidebar: this.sidebar });
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SidebarNavComponent, isStandalone: true, selector: "c-sidebar-nav", inputs: { navItems: { classPropertyName: "navItems", publicName: "navItems", isSignal: true, isRequired: false, transformFunction: null }, dropdownMode: { classPropertyName: "dropdownMode", publicName: "dropdownMode", isSignal: true, isRequired: false, transformFunction: null }, groupItems: { classPropertyName: "groupItems", publicName: "groupItems", isSignal: true, isRequired: false, transformFunction: null }, compact: { classPropertyName: "compact", publicName: "compact", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()", "attr.role": "role()" } }, usesOnChanges: true, ngImport: i0, template: "@for (nItem of navItemsArray(); track nItem) {\n  @switch (helper.itemType(nItem)) {\n    @case ('group') {\n      <c-sidebar-nav-group\n        #rla=\"routerLinkActive\"\n        [dropdownMode]=\"dropdownMode()\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n        [routerLinkActiveOptions]=\"{exact: true}\"\n        routerLinkActive=\"show\"\n        [compact]=\"compact()\"\n      />\n    }\n    @case ('divider') {\n      <c-sidebar-nav-divider\n        [cHtmlAttr]=\"nItem.attributes ?? {}\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('title') {\n      <c-sidebar-nav-title\n        [cHtmlAttr]=\"nItem.attributes ?? {}\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('label') {\n      <c-sidebar-nav-label\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('empty') {\n      <ng-container />\n    }\n    @default {\n      <c-sidebar-nav-link\n        (linkClick)=\"hideMobile()\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n  }\n}\n<ng-content />\n", dependencies: [{ kind: "directive", type: i0.forwardRef(() => HtmlAttributesDirective), selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavLinkComponent), selector: "c-sidebar-nav-link", inputs: ["item"], outputs: ["linkClick"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavLabelComponent), selector: "c-sidebar-nav-label", inputs: ["item"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavTitleComponent), selector: "c-sidebar-nav-title", inputs: ["item"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavDividerComponent), selector: "c-sidebar-nav-divider", inputs: ["item"] }, { kind: "component", type: i0.forwardRef(() => SidebarNavGroupComponent), selector: "c-sidebar-nav-group", inputs: ["item", "dropdownMode", "show", "compact"] }, { kind: "ngmodule", type: i0.forwardRef(() => RouterModule) }, { kind: "directive", type: i0.forwardRef(() => i1$1.RouterLinkActive), selector: "[routerLinkActive]", inputs: ["routerLinkActiveOptions", "ariaCurrentWhenActive", "routerLinkActive"], outputs: ["isActiveChange"], exportAs: ["routerLinkActive"] }, { kind: "pipe", type: i0.forwardRef(() => SidebarNavItemClassPipe), name: "cSidebarNavItemClass" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-sidebar-nav', imports: [
                        HtmlAttributesDirective,
                        SidebarNavLinkComponent,
                        SidebarNavLabelComponent,
                        SidebarNavTitleComponent,
                        SidebarNavDividerComponent,
                        forwardRef(() => SidebarNavGroupComponent),
                        SidebarNavItemClassPipe,
                        RouterModule
                    ], host: {
                        '[class]': 'hostClasses()',
                        '[attr.role]': 'role()'
                    }, template: "@for (nItem of navItemsArray(); track nItem) {\n  @switch (helper.itemType(nItem)) {\n    @case ('group') {\n      <c-sidebar-nav-group\n        #rla=\"routerLinkActive\"\n        [dropdownMode]=\"dropdownMode()\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n        [routerLinkActiveOptions]=\"{exact: true}\"\n        routerLinkActive=\"show\"\n        [compact]=\"compact()\"\n      />\n    }\n    @case ('divider') {\n      <c-sidebar-nav-divider\n        [cHtmlAttr]=\"nItem.attributes ?? {}\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('title') {\n      <c-sidebar-nav-title\n        [cHtmlAttr]=\"nItem.attributes ?? {}\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('label') {\n      <c-sidebar-nav-label\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n    @case ('empty') {\n      <ng-container />\n    }\n    @default {\n      <c-sidebar-nav-link\n        (linkClick)=\"hideMobile()\"\n        [item]=\"nItem\"\n        [class]=\"nItem | cSidebarNavItemClass\"\n      />\n    }\n  }\n}\n<ng-content />\n" }]
        }], propDecorators: { navItems: [{ type: i0.Input, args: [{ isSignal: true, alias: "navItems", required: false }] }], dropdownMode: [{ type: i0.Input, args: [{ isSignal: true, alias: "dropdownMode", required: false }] }], groupItems: [{ type: i0.Input, args: [{ isSignal: true, alias: "groupItems", required: false }] }], compact: [{ type: i0.Input, args: [{ isSignal: true, alias: "compact", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class SidebarModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SidebarModule, imports: [SidebarComponent,
            SidebarBrandComponent,
            SidebarFooterComponent,
            SidebarHeaderComponent,
            SidebarNavComponent,
            SidebarNavBadgePipe,
            SidebarNavDividerComponent,
            SidebarNavGroupComponent,
            SidebarNavIconPipe,
            SidebarNavItemClassPipe,
            SidebarNavLabelComponent,
            SidebarNavLinkComponent,
            SidebarNavLinkContentComponent,
            SidebarNavLinkPipe,
            SidebarNavTitleComponent,
            SidebarToggleDirective,
            SidebarTogglerDirective], exports: [SidebarComponent,
            SidebarToggleDirective,
            SidebarTogglerDirective,
            SidebarBrandComponent,
            SidebarNavComponent,
            SidebarHeaderComponent,
            SidebarFooterComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarModule, providers: [
            SidebarService,
            SidebarNavHelper,
            SidebarNavGroupService
        ], imports: [SidebarNavComponent,
            SidebarNavGroupComponent,
            SidebarNavLinkComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SidebarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        SidebarComponent,
                        SidebarBrandComponent,
                        SidebarFooterComponent,
                        SidebarHeaderComponent,
                        SidebarNavComponent,
                        SidebarNavBadgePipe,
                        SidebarNavDividerComponent,
                        SidebarNavGroupComponent,
                        SidebarNavIconPipe,
                        SidebarNavItemClassPipe,
                        SidebarNavLabelComponent,
                        SidebarNavLinkComponent,
                        SidebarNavLinkContentComponent,
                        SidebarNavLinkPipe,
                        SidebarNavTitleComponent,
                        SidebarToggleDirective,
                        SidebarTogglerDirective
                    ],
                    exports: [
                        SidebarComponent,
                        SidebarToggleDirective,
                        SidebarTogglerDirective,
                        SidebarBrandComponent,
                        SidebarNavComponent,
                        SidebarHeaderComponent,
                        SidebarFooterComponent
                    ],
                    providers: [
                        SidebarService,
                        SidebarNavHelper,
                        SidebarNavGroupService
                    ]
                }]
        }] });

class SmartPaginationComponent {
    /**
     * Current page number.
     * @default 1
     * @returns number
     */
    activePageInput = input(1, { ...(ngDevMode ? { debugName: "activePageInput" } : {}), transform: numberAttribute, alias: 'activePage' });
    activePage = linkedSignal(this.activePageInput, ...(ngDevMode ? [{ debugName: "activePage" }] : []));
    /**
     * Event triggered on activePage change.
     * @return number
     */
    activePageChange = output();
    /**
     * Show/hide arrows
     * @default true
     * @returns boolean
     */
    arrows = input(true, ...(ngDevMode ? [{ debugName: "arrows" }] : []));
    /**
     * Show double arrows buttons
     * @default true
     * @returns boolean
     */
    doubleArrows = input(true, ...(ngDevMode ? [{ debugName: "doubleArrows" }] : []));
    /**
     * Show/hide dots.
     * @default true
     * returns boolean
     */
    dots = input(true, ...(ngDevMode ? [{ debugName: "dots" }] : []));
    /**
     * Maximum items number.
     * @default 5
     * @return number
     */
    limit = input(5, { ...(ngDevMode ? { debugName: "limit" } : {}), transform: numberAttribute });
    /**
     * Number of pages.
     * @default 1
     * @return number
     */
    pages = input(1, { ...(ngDevMode ? { debugName: "pages" } : {}), transform: numberAttribute });
    /**
     * The content of 'firstButton' button.
     * @default '&laquo'
     */
    firstButton = input('«', ...(ngDevMode ? [{ debugName: "firstButton" }] : []));
    firstBtnRef = computed(() => {
        const firstBtn = this.firstButton();
        return firstBtn instanceof TemplateRef ? firstBtn : undefined;
    }, ...(ngDevMode ? [{ debugName: "firstBtnRef" }] : []));
    /**
     * The content of 'lastButton' button.
     * @default '&raquo'
     */
    lastButton = input('»', ...(ngDevMode ? [{ debugName: "lastButton" }] : []));
    lastBtnRef = computed(() => {
        const lastBtn = this.lastButton();
        return lastBtn instanceof TemplateRef ? lastBtn : undefined;
    }, ...(ngDevMode ? [{ debugName: "lastBtnRef" }] : []));
    /**
     * The content of 'nextButton' button.
     * @default '&lsaquo'
     */
    nextButton = input('›', ...(ngDevMode ? [{ debugName: "nextButton" }] : []));
    nextBtnRef = computed(() => {
        const nextBtn = this.nextButton();
        return nextBtn instanceof TemplateRef ? nextBtn : undefined;
    }, ...(ngDevMode ? [{ debugName: "nextBtnRef" }] : []));
    /**
     * The content of 'previousButton' button.
     * @default '&rsaquo'
     */
    previousButton = input('‹', ...(ngDevMode ? [{ debugName: "previousButton" }] : []));
    prevBtnRef = computed(() => {
        const prevBtn = this.previousButton();
        return prevBtn instanceof TemplateRef ? prevBtn : undefined;
    }, ...(ngDevMode ? [{ debugName: "prevBtnRef" }] : []));
    /**
     * Set the alignment of pagination components.
     * @values 'start', 'center', 'end'
     */
    align = input('start', ...(ngDevMode ? [{ debugName: "align" }] : []));
    /**
     * Size the component small or large.
     * @values 'sm', 'lg'
     */
    size = input(...(ngDevMode ? [undefined, { debugName: "size" }] : []));
    /**
     * Default role for pagination.
     * @return string
     * @default 'navigation'
     */
    role = input('navigation', ...(ngDevMode ? [{ debugName: "role" }] : []));
    paginationClass = computed(() => {
        const align = this.align();
        const size = this.size();
        return {
            pagination: true,
            [`pagination-${size}`]: !!size,
            [`justify-content-${align}`]: !!align
        };
    }, ...(ngDevMode ? [{ debugName: "paginationClass" }] : []));
    maxPrevItems = computed(() => {
        return Math.floor((this.limit() - 1) / 2);
    }, ...(ngDevMode ? [{ debugName: "maxPrevItems" }] : []));
    maxNextItems = computed(() => {
        return Math.ceil((this.limit() - 1) / 2);
    }, ...(ngDevMode ? [{ debugName: "maxNextItems" }] : []));
    range = computed(() => {
        return this.activePage() + this.maxNextItems();
    }, ...(ngDevMode ? [{ debugName: "range" }] : []));
    lastItem = computed(() => {
        const range = this.range();
        const pages = this.pages();
        return range >= pages ? pages : range - (this.afterDots() ? 1 : 0);
    }, ...(ngDevMode ? [{ debugName: "lastItem" }] : []));
    computedLimit = computed(() => {
        return this.limit() - (this.afterDots() ? 1 : 0) - (this.beforeDots() ? 1 : 0);
    }, ...(ngDevMode ? [{ debugName: "computedLimit" }] : []));
    itemsLimit = computed(() => {
        const pages = this.pages();
        return pages < this.computedLimit() ? pages : this.computedLimit();
    }, ...(ngDevMode ? [{ debugName: "itemsLimit" }] : []));
    showDots = computed(() => {
        const limit = this.limit();
        return this.dots() && limit > 4 && limit < this.pages();
    }, ...(ngDevMode ? [{ debugName: "showDots" }] : []));
    beforeDots = computed(() => {
        return this.showDots() && this.activePage() > this.maxPrevItems() + 1;
    }, ...(ngDevMode ? [{ debugName: "beforeDots" }] : []));
    afterDots = computed(() => {
        return this.showDots() && this.activePage() < this.pages() - this.maxNextItems();
    }, ...(ngDevMode ? [{ debugName: "afterDots" }] : []));
    items = computed(() => {
        const itemsLimit = this.itemsLimit();
        if (this.activePage() - this.maxPrevItems() <= 1) {
            return Array.from({
                length: itemsLimit
            }, (_item, i) => i + 1);
        }
        else {
            return Array.from({
                length: itemsLimit
            }, (_item, i) => this.lastItem() - i).reverse();
        }
    }, ...(ngDevMode ? [{ debugName: "items" }] : []));
    setPage(page) {
        if (page !== this.activePage()) {
            this.activePage.set(page);
            this.activePageChange.emit(page);
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SmartPaginationComponent, isStandalone: true, selector: "c-smart-pagination", inputs: { activePageInput: { classPropertyName: "activePageInput", publicName: "activePage", isSignal: true, isRequired: false, transformFunction: null }, arrows: { classPropertyName: "arrows", publicName: "arrows", isSignal: true, isRequired: false, transformFunction: null }, doubleArrows: { classPropertyName: "doubleArrows", publicName: "doubleArrows", isSignal: true, isRequired: false, transformFunction: null }, dots: { classPropertyName: "dots", publicName: "dots", isSignal: true, isRequired: false, transformFunction: null }, limit: { classPropertyName: "limit", publicName: "limit", isSignal: true, isRequired: false, transformFunction: null }, pages: { classPropertyName: "pages", publicName: "pages", isSignal: true, isRequired: false, transformFunction: null }, firstButton: { classPropertyName: "firstButton", publicName: "firstButton", isSignal: true, isRequired: false, transformFunction: null }, lastButton: { classPropertyName: "lastButton", publicName: "lastButton", isSignal: true, isRequired: false, transformFunction: null }, nextButton: { classPropertyName: "nextButton", publicName: "nextButton", isSignal: true, isRequired: false, transformFunction: null }, previousButton: { classPropertyName: "previousButton", publicName: "previousButton", isSignal: true, isRequired: false, transformFunction: null }, align: { classPropertyName: "align", publicName: "align", isSignal: true, isRequired: false, transformFunction: null }, size: { classPropertyName: "size", publicName: "size", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { activePageChange: "activePageChange" }, host: { properties: { "attr.role": "role()" } }, exportAs: ["cSmartPagination"], ngImport: i0, template: "@let activePg = activePage();\n@let arrow = arrows();\n@let dblArrows = doubleArrows();\n@let pageCnt = pages();\n\n<ul [class]=\"paginationClass()\">\n  @if (dblArrows) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(1)\"\n        [disabled]=\"activePg === 1\"\n        [class.disabled]=\"activePg === 1\"\n        aria-label=\"First page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"firstBtnRef() || firstBtn\" />\n      </button>\n    </li>\n  }\n  @if (arrow) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(activePg - 1)\"\n        [disabled]=\"activePg === 1\"\n        [class.disabled]=\"activePg === 1\"\n        aria-label=\"Previous page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"prevBtnRef() || prevBtn\" />\n      </button>\n    </li>\n  }\n  @if (beforeDots()) {\n    <li cPageItem disabled>\n      <a cPageLink disabled role=\"separator\">\n        ...\n      </a>\n    </li>\n  }\n  @for (item of items(); track (item).toString(); let i = $index) {\n    <li [active]=\"activePg === item\" cPageItem>\n      <button\n        (click)=\"setPage(item)\"\n        [title]=\"(activePg === item ? 'Current page ' : 'Go to page ') + (item).toString()\"\n        cPageLink\n      >\n        {{ item }}\n      </button>\n    </li>\n  }\n  @if (afterDots()) {\n    <li cPageItem disabled>\n      <a cPageLink disabled role=\"separator\">\n        ...\n      </a>\n    </li>\n  }\n  @if (arrow) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(activePg + 1)\"\n        [disabled]=\"activePg === pageCnt\"\n        [class.disabled]=\"activePg === pageCnt\"\n        aria-label=\"Next page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"nextBtnRef() || nextBtn\" />\n      </button>\n    </li>\n  }\n  @if (dblArrows) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(pageCnt)\"\n        [disabled]=\"activePg === pageCnt\"\n        [class.disabled]=\"activePg === pageCnt\"\n        aria-label=\"Last page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"lastBtnRef() || lastBtn\" />\n      </button>\n    </li>\n  }\n</ul>\n\n<ng-template #firstBtn>\n  <span aria-hidden=\"true\">{{ firstButton() }}</span>\n</ng-template>\n\n<ng-template #prevBtn>\n  <span aria-hidden=\"true\">{{ previousButton() }}</span>\n</ng-template>\n\n<ng-template #nextBtn>\n  <span aria-hidden=\"true\">{{ nextButton() }}</span>\n</ng-template>\n\n<ng-template #lastBtn>\n  <span aria-hidden=\"true\">{{ lastButton() }}</span>\n</ng-template>\n", dependencies: [{ kind: "directive", type: PageItemDirective, selector: "[cPageItem]", inputs: ["active", "disabled"] }, { kind: "directive", type: PageLinkDirective, selector: "[cPageLink]", inputs: ["disabled"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-pagination', exportAs: 'cSmartPagination', imports: [PageItemDirective, PageLinkDirective, NgTemplateOutlet], host: {
                        '[attr.role]': 'role()'
                    }, template: "@let activePg = activePage();\n@let arrow = arrows();\n@let dblArrows = doubleArrows();\n@let pageCnt = pages();\n\n<ul [class]=\"paginationClass()\">\n  @if (dblArrows) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(1)\"\n        [disabled]=\"activePg === 1\"\n        [class.disabled]=\"activePg === 1\"\n        aria-label=\"First page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"firstBtnRef() || firstBtn\" />\n      </button>\n    </li>\n  }\n  @if (arrow) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(activePg - 1)\"\n        [disabled]=\"activePg === 1\"\n        [class.disabled]=\"activePg === 1\"\n        aria-label=\"Previous page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"prevBtnRef() || prevBtn\" />\n      </button>\n    </li>\n  }\n  @if (beforeDots()) {\n    <li cPageItem disabled>\n      <a cPageLink disabled role=\"separator\">\n        ...\n      </a>\n    </li>\n  }\n  @for (item of items(); track (item).toString(); let i = $index) {\n    <li [active]=\"activePg === item\" cPageItem>\n      <button\n        (click)=\"setPage(item)\"\n        [title]=\"(activePg === item ? 'Current page ' : 'Go to page ') + (item).toString()\"\n        cPageLink\n      >\n        {{ item }}\n      </button>\n    </li>\n  }\n  @if (afterDots()) {\n    <li cPageItem disabled>\n      <a cPageLink disabled role=\"separator\">\n        ...\n      </a>\n    </li>\n  }\n  @if (arrow) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(activePg + 1)\"\n        [disabled]=\"activePg === pageCnt\"\n        [class.disabled]=\"activePg === pageCnt\"\n        aria-label=\"Next page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"nextBtnRef() || nextBtn\" />\n      </button>\n    </li>\n  }\n  @if (dblArrows) {\n    <li cPageItem>\n      <button\n        (click)=\"setPage(pageCnt)\"\n        [disabled]=\"activePg === pageCnt\"\n        [class.disabled]=\"activePg === pageCnt\"\n        aria-label=\"Last page\"\n        cPageLink\n      >\n        <ng-container *ngTemplateOutlet=\"lastBtnRef() || lastBtn\" />\n      </button>\n    </li>\n  }\n</ul>\n\n<ng-template #firstBtn>\n  <span aria-hidden=\"true\">{{ firstButton() }}</span>\n</ng-template>\n\n<ng-template #prevBtn>\n  <span aria-hidden=\"true\">{{ previousButton() }}</span>\n</ng-template>\n\n<ng-template #nextBtn>\n  <span aria-hidden=\"true\">{{ nextButton() }}</span>\n</ng-template>\n\n<ng-template #lastBtn>\n  <span aria-hidden=\"true\">{{ lastButton() }}</span>\n</ng-template>\n" }]
        }], propDecorators: { activePageInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "activePage", required: false }] }], activePageChange: [{ type: i0.Output, args: ["activePageChange"] }], arrows: [{ type: i0.Input, args: [{ isSignal: true, alias: "arrows", required: false }] }], doubleArrows: [{ type: i0.Input, args: [{ isSignal: true, alias: "doubleArrows", required: false }] }], dots: [{ type: i0.Input, args: [{ isSignal: true, alias: "dots", required: false }] }], limit: [{ type: i0.Input, args: [{ isSignal: true, alias: "limit", required: false }] }], pages: [{ type: i0.Input, args: [{ isSignal: true, alias: "pages", required: false }] }], firstButton: [{ type: i0.Input, args: [{ isSignal: true, alias: "firstButton", required: false }] }], lastButton: [{ type: i0.Input, args: [{ isSignal: true, alias: "lastButton", required: false }] }], nextButton: [{ type: i0.Input, args: [{ isSignal: true, alias: "nextButton", required: false }] }], previousButton: [{ type: i0.Input, args: [{ isSignal: true, alias: "previousButton", required: false }] }], align: [{ type: i0.Input, args: [{ isSignal: true, alias: "align", required: false }] }], size: [{ type: i0.Input, args: [{ isSignal: true, alias: "size", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }] } });

class SmartPaginationModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationModule, imports: [SmartPaginationComponent], exports: [SmartPaginationComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartPaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [SmartPaginationComponent],
                    imports: [SmartPaginationComponent]
                }]
        }] });

class FilterInputDirective {
    #elementRef = inject(ElementRef);
    #destroyRef = inject(DestroyRef);
    #value = '';
    emitObject = signal({
        value: '',
        type: '',
        column: ''
    }, ...(ngDevMode ? [{ debugName: "emitObject" }] : []));
    delay = input(300, ...(ngDevMode ? [{ debugName: "delay" }] : []));
    onEvent = input('input', ...(ngDevMode ? [{ debugName: "onEvent" }] : []));
    valueInput = input('', { ...(ngDevMode ? { debugName: "valueInput" } : {}), alias: 'value' });
    value = linkedSignal(this.valueInput, ...(ngDevMode ? [{ debugName: "value" }] : []));
    #valueEffect = effect(() => {
        const value = this.value();
        if (this.#value !== value) {
            untracked(() => {
                this.#value = this.value();
                this.emitObject.update((emitObj) => ({ ...emitObj, value: value, type: this.onEvent() }));
                this.valueChange.emit({ ...this.emitObject() });
            });
        }
    }, ...(ngDevMode ? [{ debugName: "#valueEffect" }] : []));
    valueChange = output();
    ngAfterViewInit() {
        this.setSubscription();
    }
    setSubscription() {
        const dueTime = this.onEvent() === 'change' ? 0 : this.delay();
        fromEvent(this.#elementRef.nativeElement, this.onEvent())
            .pipe(map$1((event) => {
            return event.target.value;
        }), debounceTime$1(dueTime), takeUntilDestroyed(this.#destroyRef))
            .subscribe((value) => {
            if (value !== this.#value) {
                this.value.set(value);
            }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FilterInputDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: FilterInputDirective, isStandalone: true, selector: "[cFilterInput]", inputs: { delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, onEvent: { classPropertyName: "onEvent", publicName: "onEvent", isSignal: true, isRequired: false, transformFunction: null }, valueInput: { classPropertyName: "valueInput", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { valueChange: "valueChange" }, exportAs: ["cFilterInput"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: FilterInputDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cFilterInput]',
                    exportAs: 'cFilterInput',
                    standalone: true
                }]
        }], propDecorators: { delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], onEvent: [{ type: i0.Input, args: [{ isSignal: true, alias: "onEvent", required: false }] }], valueInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], valueChange: [{ type: i0.Output, args: ["valueChange"] }] } });

class SmartTableFilterComponent {
    #filterInputDirective = inject(FilterInputDirective);
    value = this.#filterInputDirective.value;
    filterLabel = input('Filter:', ...(ngDevMode ? [{ debugName: "filterLabel" }] : []));
    filterPlaceholder = input('Filter', ...(ngDevMode ? [{ debugName: "filterPlaceholder" }] : []));
    sizing = input(...(ngDevMode ? [undefined, { debugName: "sizing" }] : []));
    labelClasses = computed(() => {
        const sizing = this.sizing();
        return {
            'col-form-label': true,
            [`col-form-label-${sizing}`]: !!sizing
        };
    }, ...(ngDevMode ? [{ debugName: "labelClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableFilterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SmartTableFilterComponent, isStandalone: true, selector: "c-smart-table-filter", inputs: { filterLabel: { classPropertyName: "filterLabel", publicName: "filterLabel", isSignal: true, isRequired: false, transformFunction: null }, filterPlaceholder: { classPropertyName: "filterPlaceholder", publicName: "filterPlaceholder", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null } }, host: { classAttribute: "row mb-2" }, ngImport: i0, template: "@let filterLbl = filterLabel();\n@if (!!filterLbl) {\n  <label [class]=\"labelClasses()\" cLabel=\"col\" class=\"col-auto\">\n    {{ filterLbl }}\n  </label>\n}\n<div class=\"col-auto\">\n  <input\n    [placeholder]=\"filterPlaceholder()\"\n    [sizing]=\"sizing()\"\n    [value]=\"value()\"\n    autocomplete=\"off\"\n    cFormControl\n    size=\"10\"\n  />\n</div>\n", dependencies: [{ kind: "directive", type: FormControlDirective, selector: "input[cFormControl], textarea[cFormControl]", inputs: ["sizing", "valid", "type", "plaintext"] }, { kind: "directive", type: FormLabelDirective, selector: "[cLabel]", inputs: ["cLabel", "sizing"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-table-filter', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FormControlDirective, FormLabelDirective], host: {
                        class: 'row mb-2'
                    }, template: "@let filterLbl = filterLabel();\n@if (!!filterLbl) {\n  <label [class]=\"labelClasses()\" cLabel=\"col\" class=\"col-auto\">\n    {{ filterLbl }}\n  </label>\n}\n<div class=\"col-auto\">\n  <input\n    [placeholder]=\"filterPlaceholder()\"\n    [sizing]=\"sizing()\"\n    [value]=\"value()\"\n    autocomplete=\"off\"\n    cFormControl\n    size=\"10\"\n  />\n</div>\n" }]
        }], propDecorators: { filterLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "filterLabel", required: false }] }], filterPlaceholder: [{ type: i0.Input, args: [{ isSignal: true, alias: "filterPlaceholder", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }] } });

const getClickedColumnName = (target, columnNames, selectable) => {
    const closest = target.closest('tr');
    const children = closest ? Array.from(closest.children) : [];
    const clickedCell = children.filter((child) => child.contains(target))[0];
    return columnNames[children.indexOf(clickedCell) - (selectable ? 1 : 0)];
};
const getColumnKey = (column) => typeof column === 'object' ? (column.key ?? column.group) : column;
const getColumnLabel = (column) => typeof column === 'object' && column.label !== undefined ? column.label : prettifyName(getColumnKey(column));
const getColumnName = getColumnKey;
const getColumnNames = (columns, items) => {
    if (columns?.length) {
        const _columns = [];
        for (const column of columns) {
            if (typeof column === 'object' && column.children) {
                _columns.push(...getColumnNames(column.children, []));
                continue;
            }
            typeof column === 'object' ? _columns.push(column.key) : _columns.push(column);
        }
        return _columns;
    }
    return getColumnNamesFromItems(items);
};
const getColumns = (_columns) => {
    const columns = [];
    for (const column of _columns) {
        if (typeof column === 'object' && column.group && column.children) {
            columns.push(...getColumns(column.children));
            continue;
        }
        if (typeof column === 'object' && column.children) {
            columns.push(...getColumns(column.children));
        }
        columns.push(column);
    }
    return columns;
};
const countColumns = (columns, counter = 0) => {
    let _counter = counter;
    for (const column of columns) {
        if (!column.children) {
            _counter++;
        }
        if (column.children) {
            _counter = countColumns(column.children, _counter);
        }
    }
    return _counter;
};
const getColumnGroups = (columns) => {
    const groups = [];
    const traverseColumns = (column, deep = 0, colSpan = 0) => {
        const groups = [];
        if (column.children) {
            for (const _column of column.children) {
                if (!_column.group) {
                    colSpan++;
                }
                groups.push(...traverseColumns(_column, deep + 1, colSpan));
            }
        }
        if (typeof column === 'object' && column.group) {
            const { children, group, ...rest } = column;
            groups.push({
                deep: deep,
                label: group,
                ...(children && { colspan: countColumns(children) }),
                ...rest
            });
        }
        return groups;
    };
    if (columns) {
        for (const column of columns) {
            if (typeof column === 'object' && column.group) {
                const objects = traverseColumns(column);
                if (objects) {
                    for (const object of objects) {
                        const { deep, ...rest } = object;
                        if (deep === undefined) {
                            continue;
                        }
                        for (let i = 0; i < deep; i++) {
                            if (groups[i]) {
                                continue;
                            }
                            groups.push([]);
                        }
                        if (groups[deep]) {
                            groups[deep].push(rest);
                        }
                        else {
                            groups.push([rest]);
                        }
                    }
                }
            }
        }
    }
    return groups;
};
const getColumnNamesFromItems = (items) => Object.keys(items[0] || {}).filter((el) => el.charAt(0) !== '_');
const getColumnValues = (items, key) => {
    return items.map((item) => item[key]);
};
const convertNgClassToObj = (value) => {
    const WS_REGEXP = /\s+/;
    const rawClass = typeof value === 'string' ? value.trim().split(WS_REGEXP) : value;
    const objClass = {};
    if (Array.isArray(rawClass) || rawClass instanceof Set) {
        for (const klass of rawClass) {
            objClass[klass] = true;
        }
        return objClass;
    }
    return (rawClass ?? {});
};
const getTableDataCellClass = (column, item) => {
    const colClass = convertNgClassToObj(column?._colClass ?? {});
    const rowClass = convertNgClassToObj(item._class ?? {});
    const allClass = convertNgClassToObj(item._cellClass?._all ?? {});
    const cellClass = convertNgClassToObj(item._cellClass?.[column.key ?? column.group] ?? {});
    return { ...colClass, ...rowClass, ...allClass, ...cellClass };
};
const getTableDataCellProps = (column, item) => {
    const props = {};
    if (typeof column === 'object' && column._colProps) {
        Object.assign(props, column._colProps);
    }
    if (item._cellProps?._all) {
        Object.assign(props, item._cellProps._all);
    }
    if (item._cellProps?.[column.key ?? column.group]) {
        Object.assign(props, item._cellProps[column.key ?? column.group]);
    }
    return props;
};
const getTableDataCellStyles = (column, item) => {
    const styles = {};
    if (typeof column === 'object' && column._colStyle) {
        Object.assign(styles, column._colStyle);
    }
    if (item._cellStyle?._all) {
        Object.assign(styles, item._cellStyle._all);
    }
    // @ts-ignore
    if (item._cellStyle?.[column.key]) {
        // @ts-ignore
        Object.assign(styles, item._cellStyle[column.key]);
    }
    return { ...styles };
};
const getTableHeaderCellProps = (column) => {
    if (typeof column === 'object' && column._props) {
        const { color, ...props } = { ...column._props };
        return props;
    }
    return {};
};
const getTableHeaderCellClass = (column) => {
    const headerClass = convertNgClassToObj(column?._classes ?? {});
    return { ...headerClass };
};
const getTableHeaderCellStyles = (column, columnSorter) => {
    const style = {
        verticalAlign: 'middle',
        overflow: 'hidden',
        cursor: columnSorter ? 'pointer' : ''
    };
    if (typeof column === 'object' && column._style) {
        return { ...style, ...column._style };
    }
    return { ...style };
};
const prettifyName = (name) => {
    return name
        .replace(/[-_.]/g, ' ')
        .replace(/ +/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

class TableColorDirective {
    /**
     * Use contextual color for tables, table rows or individual cells.
     * @return Colors
     */
    color = input(undefined, { ...(ngDevMode ? { debugName: "color" } : {}), alias: 'cTableColor' });
    hostClasses = computed(() => {
        const color = this.color();
        return {
            [`table-${color}`]: !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableColorDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TableColorDirective, isStandalone: true, selector: "[cTableColor]", inputs: { color: { classPropertyName: "color", publicName: "cTableColor", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" } }, exportAs: ["cTableColor"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableColorDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTableColor]',
                    exportAs: 'cTableColor',
                    host: {
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { color: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTableColor", required: false }] }] } });

class TableActiveDirective {
    /**
     * Highlight a table row or cell
     * @return boolean
     */
    active = input(false, { ...(ngDevMode ? { debugName: "active" } : {}), alias: "cTableActive", transform: booleanAttribute });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableActiveDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TableActiveDirective, isStandalone: true, selector: "[cTableActive]", inputs: { active: { classPropertyName: "active", publicName: "cTableActive", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class.table-active": "active()" } }, exportAs: ["cTableActive"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableActiveDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTableActive]',
                    exportAs: 'cTableActive',
                    host: {
                        '[class.table-active]': 'active()'
                    }
                }]
        }], propDecorators: { active: [{ type: i0.Input, args: [{ isSignal: true, alias: "cTableActive", required: false }] }] } });

class TableDirective {
    static ngAcceptInputType_bordered;
    static ngAcceptInputType_borderless;
    static ngAcceptInputType_hover;
    static ngAcceptInputType_small;
    static ngAcceptInputType_striped;
    static ngAcceptInputType_stripedColumns;
    #renderer = inject(Renderer2);
    #hostElement = inject(ElementRef);
    /**
     * Set the vertical alignment.
     * @return string
     * @values 'bottom' | 'middle' | 'top'
     */
    align = input(...(ngDevMode ? [undefined, { debugName: "align" }] : []));
    /**
     * Sets the border color of the component to one of CoreUI’s themed colors.
     * @return Colors
     */
    borderColor = input(...(ngDevMode ? [undefined, { debugName: "borderColor" }] : []));
    /**
     * Add borders on all sides of the table and cells.
     * @return boolean
     */
    bordered = input(false, { ...(ngDevMode ? { debugName: "bordered" } : {}), transform: booleanAttribute });
    /**
     * Remove borders on all sides of the table and cells.
     * @return boolean
     */
    borderless = input(false, { ...(ngDevMode ? { debugName: "borderless" } : {}), transform: booleanAttribute });
    /**
     * Put the `<caption>` on the top of the table.
     * @return 'top'
     * @values 'top'
     */
    caption = input(...(ngDevMode ? [undefined, { debugName: "caption" }] : []));
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @return Colors
     */
    color = input(...(ngDevMode ? [undefined, { debugName: "color" }] : []));
    /**
     * Enable a hover state on table rows within table body.
     * @return boolean
     */
    hover = input(false, { ...(ngDevMode ? { debugName: "hover" } : {}), transform: booleanAttribute });
    /**
     * Make table responsive across all viewports or pick a maximum breakpoint with which to have a responsive table up to.
     * @values: {boolean | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'}
     */
    responsive = input(...(ngDevMode ? [undefined, { debugName: "responsive" }] : []));
    /**
     * Make table more compact by cutting all cell `padding` in half.
     * @return boolean
     */
    small = input(false, { ...(ngDevMode ? { debugName: "small" } : {}), transform: booleanAttribute });
    /**
     * Add zebra-striping to any table row within the table body.
     * @return boolean
     */
    striped = input(false, { ...(ngDevMode ? { debugName: "striped" } : {}), transform: booleanAttribute });
    /**
     * Add zebra-striping to any table column.
     * @return boolean
     * @since 4.2.4
     */
    stripedColumns = input(false, { ...(ngDevMode ? { debugName: "stripedColumns" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        const align = this.align();
        const caption = this.caption();
        const borderColor = this.borderColor();
        const bordered = this.bordered();
        const borderless = this.borderless();
        const color = this.color();
        const hover = this.hover();
        const small = this.small();
        const striped = this.striped();
        const stripedColumns = this.stripedColumns();
        return {
            table: true,
            [`align-${align}`]: !!align,
            [`caption-${caption}`]: !!caption,
            [`border-${borderColor}`]: !!borderColor,
            'table-bordered': bordered,
            'table-borderless': borderless,
            [`table-${color}`]: !!color,
            'table-hover': hover,
            'table-sm': small,
            'table-striped': striped,
            'table-striped-columns': stripedColumns
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    #responsiveWrapperEffect = afterRenderEffect({
        // this fixes RuntimeError: NG0500: During hydration Angular expected <abc> but found <xyz>.
        // Find more at https://angular.dev/errors/NG0500
        write: () => {
            const responsive = this.responsive();
            if (!!responsive) {
                const nativeElement = this.#hostElement.nativeElement;
                const wrapper = this.#renderer.createElement('div');
                const className = responsive === true ? 'table-responsive' : `table-responsive-${responsive}`;
                this.#renderer.addClass(wrapper, className);
                const parentNode = this.#renderer.parentNode(nativeElement);
                this.#renderer.appendChild(parentNode, wrapper);
                this.#renderer.insertBefore(parentNode, wrapper, nativeElement);
                this.#renderer.appendChild(wrapper, nativeElement);
            }
        }
    });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TableDirective, isStandalone: true, selector: "table[cTable]", inputs: { align: { classPropertyName: "align", publicName: "align", isSignal: true, isRequired: false, transformFunction: null }, borderColor: { classPropertyName: "borderColor", publicName: "borderColor", isSignal: true, isRequired: false, transformFunction: null }, bordered: { classPropertyName: "bordered", publicName: "bordered", isSignal: true, isRequired: false, transformFunction: null }, borderless: { classPropertyName: "borderless", publicName: "borderless", isSignal: true, isRequired: false, transformFunction: null }, caption: { classPropertyName: "caption", publicName: "caption", isSignal: true, isRequired: false, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, hover: { classPropertyName: "hover", publicName: "hover", isSignal: true, isRequired: false, transformFunction: null }, responsive: { classPropertyName: "responsive", publicName: "responsive", isSignal: true, isRequired: false, transformFunction: null }, small: { classPropertyName: "small", publicName: "small", isSignal: true, isRequired: false, transformFunction: null }, striped: { classPropertyName: "striped", publicName: "striped", isSignal: true, isRequired: false, transformFunction: null }, stripedColumns: { classPropertyName: "stripedColumns", publicName: "stripedColumns", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "table" }, exportAs: ["cTable"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'table[cTable]',
                    exportAs: 'cTable',
                    host: {
                        class: 'table',
                        '[class]': 'hostClasses()'
                    }
                }]
        }], propDecorators: { align: [{ type: i0.Input, args: [{ isSignal: true, alias: "align", required: false }] }], borderColor: [{ type: i0.Input, args: [{ isSignal: true, alias: "borderColor", required: false }] }], bordered: [{ type: i0.Input, args: [{ isSignal: true, alias: "bordered", required: false }] }], borderless: [{ type: i0.Input, args: [{ isSignal: true, alias: "borderless", required: false }] }], caption: [{ type: i0.Input, args: [{ isSignal: true, alias: "caption", required: false }] }], color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], hover: [{ type: i0.Input, args: [{ isSignal: true, alias: "hover", required: false }] }], responsive: [{ type: i0.Input, args: [{ isSignal: true, alias: "responsive", required: false }] }], small: [{ type: i0.Input, args: [{ isSignal: true, alias: "small", required: false }] }], striped: [{ type: i0.Input, args: [{ isSignal: true, alias: "striped", required: false }] }], stripedColumns: [{ type: i0.Input, args: [{ isSignal: true, alias: "stripedColumns", required: false }] }] } });

class TableModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: TableModule, imports: [TableDirective,
            TableColorDirective,
            TableActiveDirective], exports: [TableDirective,
            TableColorDirective,
            TableActiveDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TableModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [
                        TableDirective,
                        TableColorDirective,
                        TableActiveDirective
                    ],
                    imports: [
                        TableDirective,
                        TableColorDirective,
                        TableActiveDirective
                    ]
                }]
        }] });

class SmartTableColumnFilterComponent {
    #filterInputDirective = inject(FilterInputDirective);
    value = this.#filterInputDirective.value;
    column = input(...(ngDevMode ? [undefined, { debugName: "column" }] : []));
    #columnEffect = effect(() => {
        this.#filterInputDirective.emitObject.update((emitObj) => ({ ...emitObj, column: this.column() }));
    }, ...(ngDevMode ? [{ debugName: "#columnEffect" }] : []));
    sizing = input('sm', ...(ngDevMode ? [{ debugName: "sizing" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableColumnFilterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: SmartTableColumnFilterComponent, isStandalone: true, selector: "c-smart-table-column-filter", inputs: { column: { classPropertyName: "column", publicName: "column", isSignal: true, isRequired: false, transformFunction: null }, sizing: { classPropertyName: "sizing", publicName: "sizing", isSignal: true, isRequired: false, transformFunction: null } }, ngImport: i0, template: "<input\n  [sizing]=\"sizing()\"\n  [value]=\"value()\"\n  autocomplete=\"off\"\n  cFormControl\n/>\n", dependencies: [{ kind: "directive", type: FormControlDirective, selector: "input[cFormControl], textarea[cFormControl]", inputs: ["sizing", "valid", "type", "plaintext"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableColumnFilterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-table-column-filter', changeDetection: ChangeDetectionStrategy.OnPush, imports: [FormControlDirective], template: "<input\n  [sizing]=\"sizing()\"\n  [value]=\"value()\"\n  autocomplete=\"off\"\n  cFormControl\n/>\n" }]
        }], propDecorators: { column: [{ type: i0.Input, args: [{ isSignal: true, alias: "column", required: false }] }], sizing: [{ type: i0.Input, args: [{ isSignal: true, alias: "sizing", required: false }] }] } });

class SmartTableColumnLabelPipe {
    transform(column, ...args) {
        return getColumnLabel(column);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableColumnLabelPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SmartTableColumnLabelPipe, isStandalone: true, name: "columnLabel" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableColumnLabelPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'columnLabel',
                    standalone: true
                }]
        }] });

// todo - add support for column groups
class SmartTableHeadComponent {
    getTableHeaderCellStyles = getTableHeaderCellStyles;
    getTableHeaderCellProps = getTableHeaderCellProps;
    getTableHeaderCellClass = getTableHeaderCellClass;
    columnFilter = input(...(ngDevMode ? [undefined, { debugName: "columnFilter" }] : []));
    columnFilterStateInput = input({}, { ...(ngDevMode ? { debugName: "columnFilterStateInput" } : {}), alias: 'columnFilterState' });
    columnFilterState = linkedSignal(this.columnFilterStateInput, ...(ngDevMode ? [{ debugName: "columnFilterState" }] : []));
    columnSorter = input(true, ...(ngDevMode ? [{ debugName: "columnSorter" }] : []));
    component = input('thead', ...(ngDevMode ? [{ debugName: "component" }] : []));
    set columns(value) {
        const columns = value ? getColumns(value) : [];
        this.#columns.set(columns);
    }
    get columns() {
        return this.#columns();
    }
    #columns = signal(undefined, ...(ngDevMode ? [{ debugName: "#columns" }] : []));
    columnGroupsInput = input(undefined, { ...(ngDevMode ? { debugName: "columnGroupsInput" } : {}), alias: 'columnGroups' });
    columnGroups = linkedSignal(this.columnGroupsInput, ...(ngDevMode ? [{ debugName: "columnGroups" }] : []));
    selectable = input(undefined, { ...(ngDevMode ? { debugName: "selectable" } : {}), transform: booleanAttribute });
    selectAll = input(true, { ...(ngDevMode ? { debugName: "selectAll" } : {}), transform: booleanAttribute });
    #selectedAllPreviousValue;
    set selectedAll(value) {
        this.indeterminate$.next(value === 'indeterminate');
        this.#selectedAll = value === 'indeterminate' ? false : booleanAttribute(value);
        if (value !== 'indeterminate') {
            this.#selectedAllPreviousValue = this.#selectedAll;
        }
    }
    get selectedAll() {
        return this.#selectedAll;
    }
    #selectedAll = false;
    showGroups = input(false, { ...(ngDevMode ? { debugName: "showGroups" } : {}), transform: booleanAttribute });
    sorterValue = input(...(ngDevMode ? [undefined, { debugName: "sorterValue" }] : []));
    set columnFilterTemplates(value) {
        this.#columnFilterTemplates = { ...value };
    }
    get columnFilterTemplates() {
        return this.#columnFilterTemplates;
    }
    #columnFilterTemplates = {};
    set columnLabelTemplates(value) {
        this.#columnLabelTemplates = { ...value };
    }
    get columnLabelTemplates() {
        return this.#columnLabelTemplates;
    }
    #columnLabelTemplates = {};
    set summaryRowTemplate(value) {
        this.#summaryRowTemplate = value;
    }
    get summaryRowTemplate() {
        return this.#summaryRowTemplate;
    }
    #summaryRowTemplate = null;
    // todo
    // @Input() sortingIcon?: string | TemplateRef<any>;
    // @Input() sortingIconAscending?: string | TemplateRef<any>;
    // @Input() sortingIconDescending?: string | TemplateRef<any>;
    indeterminate$ = new BehaviorSubject(false);
    columnFilterStateChange = output();
    sorterStateChange = output();
    selectAllChange = output();
    hostClasses = computed(() => {
        const component = this.component();
        return {
            thead: component === 'thead',
            tfoot: component === 'tfoot'
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnInit() {
        this.columns?.forEach((column, index) => {
            const columnKey = this.columnKey(column);
            this.columnFilterState()[columnKey] = this.columnFilterState()[columnKey] ?? '';
        });
    }
    tableHeaderCellColor(column) {
        if (typeof column === 'object' && column._props) {
            const { color } = { ...column._props };
            return color;
        }
        return '';
    }
    columnKey(column) {
        return typeof column === 'object' ? (column.key ?? column.group) : column;
    }
    getColumnSorterState(column) {
        const columnKey = this.columnKey(column);
        const sorterValue = this.sorterValue();
        if (sorterValue?.column === columnKey) {
            return sorterValue?.state ?? 0;
        }
        return 0;
    }
    handleSortClick(column, i, order) {
        if (this.columnSorterEnabled(column)) {
            const columnKey = this.columnKey(column);
            this.sorterStateChange.emit({ columnKey, index: i, order });
        }
    }
    handleSortKeyDown($event, column, i) {
        const eventCode = $event.code;
        if (['Enter', 'Space', 'ArrowUp', 'ArrowDown'].includes(eventCode)) {
            $event.preventDefault();
            const order = eventCode === 'ArrowUp' ? 'asc' : eventCode === 'ArrowDown' ? 'desc' : undefined;
            this.handleSortClick(column, i, order);
        }
    }
    columnFilterEnabled(column) {
        return typeof column !== 'object' ? true : typeof column.filter === 'undefined' ? true : column.filter;
    }
    columnSorterEnabled(column) {
        if (!this.columnSorter()) {
            return false;
        }
        return typeof column !== 'object' ? true : typeof column.sorter === 'undefined' ? true : column.sorter;
    }
    handleColumnFilterValueChange($event) {
        const { value, type: eventType, column } = { ...$event };
        const columnKey = this.columnKey(column);
        const columnFilterValue = value;
        const columnFilter = this.columnFilter();
        const isLazy = typeof columnFilter === 'object' && columnFilter.lazy === true;
        if (((isLazy && eventType === 'input') || (!isLazy && eventType === 'change')) && columnFilterValue.length !== 0) {
            return;
        }
        this.columnFilterState()[columnKey] = columnFilterValue;
        this.columnFilterStateChange.emit({ column: columnKey, value: columnFilterValue, type: eventType });
    }
    handleSelectAllChecked($event) {
        $event.preventDefault();
        $event.target.checked = !this.#selectedAllPreviousValue;
        // ($event.target as HTMLInputElement).checked = !this.selectedAll;
        const selectAll = $event.target.checked;
        this.selectAllChange.emit(selectAll);
    }
    getColumnFilter(column) {
        return `columnFilter_${this.columnKey(column) ?? '_undefined'}`;
    }
    getColumnLabel(column) {
        if (typeof column === 'object' && column._labelTemplateId) {
            return `columnLabel_${column._labelTemplateId ?? '_undefined'}`;
        }
        return `columnLabel_${this.columnKey(column) ?? '_undefined'}`;
    }
    get columnFilterEvent() {
        const columnFilter = this.columnFilter();
        return typeof columnFilter === 'object' && columnFilter.lazy ? 'change' : 'input';
    }
    getAriaSort(column) {
        const sort = this.getColumnSorterState(column);
        return sort === 'asc' ? 'ascending' : sort === 'desc' ? 'descending' : null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableHeadComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SmartTableHeadComponent, isStandalone: true, selector: "c-smart-table-head", inputs: { columnFilter: { classPropertyName: "columnFilter", publicName: "columnFilter", isSignal: true, isRequired: false, transformFunction: null }, columnFilterStateInput: { classPropertyName: "columnFilterStateInput", publicName: "columnFilterState", isSignal: true, isRequired: false, transformFunction: null }, columnSorter: { classPropertyName: "columnSorter", publicName: "columnSorter", isSignal: true, isRequired: false, transformFunction: null }, component: { classPropertyName: "component", publicName: "component", isSignal: true, isRequired: false, transformFunction: null }, columns: { classPropertyName: "columns", publicName: "columns", isSignal: false, isRequired: false, transformFunction: null }, columnGroupsInput: { classPropertyName: "columnGroupsInput", publicName: "columnGroups", isSignal: true, isRequired: false, transformFunction: null }, selectable: { classPropertyName: "selectable", publicName: "selectable", isSignal: true, isRequired: false, transformFunction: null }, selectAll: { classPropertyName: "selectAll", publicName: "selectAll", isSignal: true, isRequired: false, transformFunction: null }, selectedAll: { classPropertyName: "selectedAll", publicName: "selectedAll", isSignal: false, isRequired: false, transformFunction: null }, showGroups: { classPropertyName: "showGroups", publicName: "showGroups", isSignal: true, isRequired: false, transformFunction: null }, sorterValue: { classPropertyName: "sorterValue", publicName: "sorterValue", isSignal: true, isRequired: false, transformFunction: null }, columnFilterTemplates: { classPropertyName: "columnFilterTemplates", publicName: "columnFilterTemplates", isSignal: false, isRequired: false, transformFunction: null }, columnLabelTemplates: { classPropertyName: "columnLabelTemplates", publicName: "columnLabelTemplates", isSignal: false, isRequired: false, transformFunction: null }, summaryRowTemplate: { classPropertyName: "summaryRowTemplate", publicName: "summaryRowTemplate", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { columnFilterStateChange: "columnFilterStateChange", sorterStateChange: "sorterStateChange", selectAllChange: "selectAllChange" }, host: { properties: { "class": "hostClasses()" } }, ngImport: i0, template: "@let colSorter = columnSorter();\n@let selectableRows = selectable();\n\n@if (component() === 'tfoot') {\n  <ng-container *ngTemplateOutlet=\"summaryRowTemplate\" />\n}\n@if (showGroups()) {\n  @for (row of columnGroups(); track row; let i = $index) {\n    <tr>\n      @if (selectableRows) {\n        <th></th>\n      }\n      @for (cell of row; track cell; let i = $index) {\n        <th [cHtmlAttr]=\"getTableHeaderCellProps(cell)\"\n            [cTableColor]=\"tableHeaderCellColor(cell)\"\n            [colSpan]=\"cell.colspan ?? 1\"\n            [style]=\"getTableHeaderCellStyles(cell, colSorter)\"\n        >\n          {{ cell.label }}\n        </th>\n      }\n    </tr>\n  }\n}\n\n<tr>\n  @if (selectableRows) {\n    <th>\n      <input\n        (change)=\"handleSelectAllChecked($event)\"\n        [checked]=\"selectedAll === true\"\n        [disabled]=\"!selectAll()\"\n        [indeterminate]=\"indeterminate$ | async\"\n        autocomplete=\"off\"\n        cFormCheckInput\n        type=\"checkbox\"\n      >\n    </th>\n  }\n  @for (column of columns; track column; let i = $index) {\n    <th\n      (click)=\"handleSortClick(column, i)\"\n      (keydown)=\"handleSortKeyDown($event, column, i)\"\n      [attr.aria-sort]=\"getAriaSort(column)\"\n      [cHtmlAttr]=\"getTableHeaderCellProps(column)\"\n      [cTableColor]=\"tableHeaderCellColor(column)\"\n      [class]=\"getTableHeaderCellClass(column)\"\n      [style]=\"getTableHeaderCellStyles(column, colSorter)\"\n      [tabindex]=\"colSorter && columnSorterEnabled(column) ? 0 : -1\"\n    >\n      @if (colSorter && columnSorterEnabled(column)) {\n        <ng-container\n          *ngTemplateOutlet=\"columnSorterIconTemplate; context: { $implicit: getColumnSorterState(column) }\"\n        />\n      }\n      <ng-container\n        *ngTemplateOutlet=\"columnLabelTemplates[getColumnLabel(column)] ?? defaultColumnLabel; context: {$implicit: column, header: this}\"\n      />\n    </th>\n  }\n</tr>\n@if (columnFilter()) {\n  <tr>\n    @if (selectableRows) {\n      <th></th>\n    }\n    @for (column of columns; track column; let i = $index) {\n      <th [cHtmlAttr]=\"getTableHeaderCellProps(column)\"\n          [cTableColor]=\"tableHeaderCellColor(column)\"\n          [style]=\"getTableHeaderCellStyles(column, colSorter)\"\n      >\n        <ng-container\n          *ngTemplateOutlet=\"columnFilterTemplates[getColumnFilter(column)] ?? defaultColumnFilter; context: {$implicit: column, header: this}\"\n        />\n      </th>\n    }\n  </tr>\n}\n\n<ng-template #defaultColumnLabel let-column>\n  <div class=\"d-inline\">{{ column | columnLabel }}</div>\n</ng-template>\n\n<ng-template #defaultColumnFilter let-column>\n  @if (columnFilterEnabled(column)) {\n    <c-smart-table-column-filter\n      (valueChange)=\"handleColumnFilterValueChange($event)\"\n      [column]=\"column\"\n      [delay]=\"300\"\n      [onEvent]=\"columnFilterEvent\"\n      [value]=\"columnFilterState()[columnKey(column)] ?? ''\"\n      cFilterInput\n      sizing=\"sm\"\n    />\n  }\n</ng-template>\n\n<ng-template #columnSorterIconTemplate let-columnSorterState>\n  @switch (columnSorterState) {\n    @case ('asc') {\n      <span class=\"float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconAscendingTemplate\" />\n      </span>\n    }\n    @case ('desc') {\n      <span class=\"float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconDescendingTemplate\" />\n      </span>\n    }\n    @default {\n      <span class=\"opacity-25 float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconTemplate\" />\n      </span>\n    }\n  }\n</ng-template>\n\n<!--todo: sortingIconCustom Templates-->\n<ng-template #sortingIconTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"384 433.373 384 160 352 160 352 434.51 282.177 364.687 259.55 387.313 367.432 495.196 475.313 387.313 452.687 364.687 384 433.373\"\n    ></polygon>\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"159.432 17.372 51.55 125.255 74.177 147.882 144 78.059 144 352 176 352 176 79.195 244.687 147.882 267.313 125.255 159.432 17.372\"\n    ></polygon>\n  </svg>\n</ng-template>\n\n<ng-template #sortingIconAscendingTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"390.624 150.625 256 16 121.376 150.625 144.004 173.252 240.001 77.254 240.001 495.236 272.001 495.236 272.001 77.257 367.996 173.252 390.624 150.625\"\n    ></polygon>\n  </svg>\n</ng-template>\n\n<ng-template #sortingIconDescendingTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"367.997 338.75 271.999 434.747 271.999 17.503 239.999 17.503 239.999 434.745 144.003 338.75 121.376 361.377 256 496 390.624 361.377 367.997 338.75\"\n    ></polygon>\n  </svg>\n</ng-template>\n", styles: [":host.thead{display:table-header-group;vertical-align:middle;border-color:inherit}:host.tfoot{display:table-footer-group;vertical-align:middle;border-color:inherit}:host{-webkit-user-select:none;user-select:none}\n"], dependencies: [{ kind: "directive", type: FilterInputDirective, selector: "[cFilterInput]", inputs: ["delay", "onEvent", "value"], outputs: ["valueChange"], exportAs: ["cFilterInput"] }, { kind: "directive", type: FormCheckInputDirective, selector: "input[cFormCheckInput]", inputs: ["type", "indeterminate", "valid"] }, { kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "component", type: SmartTableColumnFilterComponent, selector: "c-smart-table-column-filter", inputs: ["column", "sizing"] }, { kind: "directive", type: TableColorDirective, selector: "[cTableColor]", inputs: ["cTableColor"], exportAs: ["cTableColor"] }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "pipe", type: AsyncPipe, name: "async" }, { kind: "pipe", type: SmartTableColumnLabelPipe, name: "columnLabel" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableHeadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-table-head', changeDetection: ChangeDetectionStrategy.OnPush, imports: [
                        FilterInputDirective,
                        FormCheckInputDirective,
                        HtmlAttributesDirective,
                        SmartTableColumnFilterComponent,
                        TableColorDirective,
                        AsyncPipe,
                        NgTemplateOutlet,
                        SmartTableColumnLabelPipe
                    ], host: {
                        '[class]': 'hostClasses()'
                    }, template: "@let colSorter = columnSorter();\n@let selectableRows = selectable();\n\n@if (component() === 'tfoot') {\n  <ng-container *ngTemplateOutlet=\"summaryRowTemplate\" />\n}\n@if (showGroups()) {\n  @for (row of columnGroups(); track row; let i = $index) {\n    <tr>\n      @if (selectableRows) {\n        <th></th>\n      }\n      @for (cell of row; track cell; let i = $index) {\n        <th [cHtmlAttr]=\"getTableHeaderCellProps(cell)\"\n            [cTableColor]=\"tableHeaderCellColor(cell)\"\n            [colSpan]=\"cell.colspan ?? 1\"\n            [style]=\"getTableHeaderCellStyles(cell, colSorter)\"\n        >\n          {{ cell.label }}\n        </th>\n      }\n    </tr>\n  }\n}\n\n<tr>\n  @if (selectableRows) {\n    <th>\n      <input\n        (change)=\"handleSelectAllChecked($event)\"\n        [checked]=\"selectedAll === true\"\n        [disabled]=\"!selectAll()\"\n        [indeterminate]=\"indeterminate$ | async\"\n        autocomplete=\"off\"\n        cFormCheckInput\n        type=\"checkbox\"\n      >\n    </th>\n  }\n  @for (column of columns; track column; let i = $index) {\n    <th\n      (click)=\"handleSortClick(column, i)\"\n      (keydown)=\"handleSortKeyDown($event, column, i)\"\n      [attr.aria-sort]=\"getAriaSort(column)\"\n      [cHtmlAttr]=\"getTableHeaderCellProps(column)\"\n      [cTableColor]=\"tableHeaderCellColor(column)\"\n      [class]=\"getTableHeaderCellClass(column)\"\n      [style]=\"getTableHeaderCellStyles(column, colSorter)\"\n      [tabindex]=\"colSorter && columnSorterEnabled(column) ? 0 : -1\"\n    >\n      @if (colSorter && columnSorterEnabled(column)) {\n        <ng-container\n          *ngTemplateOutlet=\"columnSorterIconTemplate; context: { $implicit: getColumnSorterState(column) }\"\n        />\n      }\n      <ng-container\n        *ngTemplateOutlet=\"columnLabelTemplates[getColumnLabel(column)] ?? defaultColumnLabel; context: {$implicit: column, header: this}\"\n      />\n    </th>\n  }\n</tr>\n@if (columnFilter()) {\n  <tr>\n    @if (selectableRows) {\n      <th></th>\n    }\n    @for (column of columns; track column; let i = $index) {\n      <th [cHtmlAttr]=\"getTableHeaderCellProps(column)\"\n          [cTableColor]=\"tableHeaderCellColor(column)\"\n          [style]=\"getTableHeaderCellStyles(column, colSorter)\"\n      >\n        <ng-container\n          *ngTemplateOutlet=\"columnFilterTemplates[getColumnFilter(column)] ?? defaultColumnFilter; context: {$implicit: column, header: this}\"\n        />\n      </th>\n    }\n  </tr>\n}\n\n<ng-template #defaultColumnLabel let-column>\n  <div class=\"d-inline\">{{ column | columnLabel }}</div>\n</ng-template>\n\n<ng-template #defaultColumnFilter let-column>\n  @if (columnFilterEnabled(column)) {\n    <c-smart-table-column-filter\n      (valueChange)=\"handleColumnFilterValueChange($event)\"\n      [column]=\"column\"\n      [delay]=\"300\"\n      [onEvent]=\"columnFilterEvent\"\n      [value]=\"columnFilterState()[columnKey(column)] ?? ''\"\n      cFilterInput\n      sizing=\"sm\"\n    />\n  }\n</ng-template>\n\n<ng-template #columnSorterIconTemplate let-columnSorterState>\n  @switch (columnSorterState) {\n    @case ('asc') {\n      <span class=\"float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconAscendingTemplate\" />\n      </span>\n    }\n    @case ('desc') {\n      <span class=\"float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconDescendingTemplate\" />\n      </span>\n    }\n    @default {\n      <span class=\"opacity-25 float-end mx-1\">\n        <ng-container *ngTemplateOutlet=\"sortingIconTemplate\" />\n      </span>\n    }\n  }\n</ng-template>\n\n<!--todo: sortingIconCustom Templates-->\n<ng-template #sortingIconTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"384 433.373 384 160 352 160 352 434.51 282.177 364.687 259.55 387.313 367.432 495.196 475.313 387.313 452.687 364.687 384 433.373\"\n    ></polygon>\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"159.432 17.372 51.55 125.255 74.177 147.882 144 78.059 144 352 176 352 176 79.195 244.687 147.882 267.313 125.255 159.432 17.372\"\n    ></polygon>\n  </svg>\n</ng-template>\n\n<ng-template #sortingIconAscendingTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"390.624 150.625 256 16 121.376 150.625 144.004 173.252 240.001 77.254 240.001 495.236 272.001 495.236 272.001 77.257 367.996 173.252 390.624 150.625\"\n    ></polygon>\n  </svg>\n</ng-template>\n\n<ng-template #sortingIconDescendingTemplate>\n  <svg\n    class=\"icon icon-custom-size\"\n    role=\"img\"\n    viewBox=\"0 0 512 512\"\n    width=\"18\"\n    xmlns=\"http://www.w3.org/2000/svg\"\n  >\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"367.997 338.75 271.999 434.747 271.999 17.503 239.999 17.503 239.999 434.745 144.003 338.75 121.376 361.377 256 496 390.624 361.377 367.997 338.75\"\n    ></polygon>\n  </svg>\n</ng-template>\n", styles: [":host.thead{display:table-header-group;vertical-align:middle;border-color:inherit}:host.tfoot{display:table-footer-group;vertical-align:middle;border-color:inherit}:host{-webkit-user-select:none;user-select:none}\n"] }]
        }], propDecorators: { columnFilter: [{ type: i0.Input, args: [{ isSignal: true, alias: "columnFilter", required: false }] }], columnFilterStateInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "columnFilterState", required: false }] }], columnSorter: [{ type: i0.Input, args: [{ isSignal: true, alias: "columnSorter", required: false }] }], component: [{ type: i0.Input, args: [{ isSignal: true, alias: "component", required: false }] }], columns: [{
                type: Input
            }], columnGroupsInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "columnGroups", required: false }] }], selectable: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectable", required: false }] }], selectAll: [{ type: i0.Input, args: [{ isSignal: true, alias: "selectAll", required: false }] }], selectedAll: [{
                type: Input
            }], showGroups: [{ type: i0.Input, args: [{ isSignal: true, alias: "showGroups", required: false }] }], sorterValue: [{ type: i0.Input, args: [{ isSignal: true, alias: "sorterValue", required: false }] }], columnFilterTemplates: [{
                type: Input
            }], columnLabelTemplates: [{
                type: Input
            }], summaryRowTemplate: [{
                type: Input
            }], columnFilterStateChange: [{ type: i0.Output, args: ["columnFilterStateChange"] }], sorterStateChange: [{ type: i0.Output, args: ["sorterStateChange"] }], selectAllChange: [{ type: i0.Output, args: ["selectAllChange"] }] } });

class SmartTableItemsPerPageSelectorComponent {
    itemsPerPage = model(5, ...(ngDevMode ? [{ debugName: "itemsPerPage" }] : []));
    itemsPerPageLabel = input('Items per page:', ...(ngDevMode ? [{ debugName: "itemsPerPageLabel" }] : []));
    itemsPerPageOptions = input([5, 10, 20, 50], ...(ngDevMode ? [{ debugName: "itemsPerPageOptions" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableItemsPerPageSelectorComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SmartTableItemsPerPageSelectorComponent, isStandalone: true, selector: "c-smart-table-items-per-page-selector", inputs: { itemsPerPage: { classPropertyName: "itemsPerPage", publicName: "itemsPerPage", isSignal: true, isRequired: false, transformFunction: null }, itemsPerPageLabel: { classPropertyName: "itemsPerPageLabel", publicName: "itemsPerPageLabel", isSignal: true, isRequired: false, transformFunction: null }, itemsPerPageOptions: { classPropertyName: "itemsPerPageOptions", publicName: "itemsPerPageOptions", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { itemsPerPage: "itemsPerPageChange" }, host: { properties: { "class.row": "true" } }, ngImport: i0, template: "<label cLabel=\"col\" class=\"col-sm-auto\">{{ itemsPerPageLabel() }}</label>\n<div class=\"col-auto\">\n  <select [(ngModel)]=\"itemsPerPage\" cSelect name=\"itemsPerPageSelector\">\n    @for (option of itemsPerPageOptions(); track option; let i = $index) {\n      <option [ngValue]=\"option\">{{ option }}</option>\n    }\n  </select>\n</div>\n", dependencies: [{ kind: "directive", type: FormSelectDirective, selector: "select[cSelect]", inputs: ["sizing", "valid"] }, { kind: "directive", type: FormLabelDirective, selector: "[cLabel]", inputs: ["cLabel", "sizing"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i1.NgSelectOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.ɵNgSelectMultipleOption, selector: "option", inputs: ["ngValue", "value"] }, { kind: "directive", type: i1.SelectControlValueAccessor, selector: "select:not([multiple])[formControlName],select:not([multiple])[formControl],select:not([multiple])[ngModel]", inputs: ["compareWith"] }, { kind: "directive", type: i1.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableItemsPerPageSelectorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-table-items-per-page-selector', imports: [FormSelectDirective, FormLabelDirective, FormsModule], host: {
                        '[class.row]': 'true'
                    }, template: "<label cLabel=\"col\" class=\"col-sm-auto\">{{ itemsPerPageLabel() }}</label>\n<div class=\"col-auto\">\n  <select [(ngModel)]=\"itemsPerPage\" cSelect name=\"itemsPerPageSelector\">\n    @for (option of itemsPerPageOptions(); track option; let i = $index) {\n      <option [ngValue]=\"option\">{{ option }}</option>\n    }\n  </select>\n</div>\n" }]
        }], propDecorators: { itemsPerPage: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemsPerPage", required: false }] }, { type: i0.Output, args: ["itemsPerPageChange"] }], itemsPerPageLabel: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemsPerPageLabel", required: false }] }], itemsPerPageOptions: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemsPerPageOptions", required: false }] }] } });

class SmartTableComponent {
    keyValueDiffers = inject(KeyValueDiffers);
    iterableDiffers = inject(IterableDiffers);
    constructor() {
        this.itemsDiffer = this.iterableDiffers.find(this.items).create();
        this.itemsFilteredDiffer = this.iterableDiffers.find(this._columnFiltered).create();
        this.columFilterValueDiffer = this.keyValueDiffers.find(this.columnFilterValue).create();
        this.sorterValueDiffer = this.keyValueDiffers.find(this.sorterValue).create();
    }
    getColumnKey = getColumnKey;
    getTableDataCellClass = getTableDataCellClass;
    getTableDataCellProps = getTableDataCellProps;
    getTableDataCellStyles = getTableDataCellStyles;
    selectedAllSubject = new BehaviorSubject(false);
    itemsDiffer;
    itemsFilteredDiffer;
    columFilterValueDiffer;
    sorterValueDiffer;
    set activePage(value) {
        this._activePage = value || 1;
        this.activePageChange.emit(this._activePage);
    }
    get activePage() {
        return this._activePage;
    }
    _activePage = 1;
    cleaner = false;
    clickableRows = false;
    set columns(value) {
        const colObjects = value.map((column) => (typeof column === 'string' ? { key: column } : { ...column }));
        const columns = getColumns(colObjects);
        this.columns$.set(columns);
        // todo: Input columnGroups - separation?
        // console.log(getColumnGroups(colObjects ?? []));
        const groups = getColumnGroups(value ?? []);
        this.columnGroups$.set(groups);
    }
    get columns() {
        return this.columns$();
    }
    columns$ = signal([], ...(ngDevMode ? [{ debugName: "columns$" }] : []));
    columnGroups$ = signal([], ...(ngDevMode ? [{ debugName: "columnGroups$" }] : []));
    showGroups$ = computed(() => !!this.columnGroups$().length, ...(ngDevMode ? [{ debugName: "showGroups$" }] : []));
    columnFilter;
    set columnFilterValue(value) {
        if (this.columFilterValueDiffer) {
            const changes = this.columFilterValueDiffer.diff(value);
            if (changes) {
                this._columnFilterValue = { ...value };
                this.columnFilterValueChange.emit({ ...this._columnFilterValue });
                this.tableFiltered; // emits tableFiltered change
            }
        }
    }
    get columnFilterValue() {
        return this._columnFilterValue;
    }
    _columnFilterValue = {};
    columnSorter = input(false, ...(ngDevMode ? [{ debugName: "columnSorter" }] : []));
    footer = false;
    header = false;
    set items(value) {
        if (this.itemsDiffer) {
            const changes = this.itemsDiffer.diff(value);
            if (changes) {
                this._items = this.setItems(value);
                this.items$.set(this._items);
                if (!this.columns.length) {
                    this.columns = getColumnNamesFromItems(this.items$());
                }
            }
        }
    }
    get items() {
        return this._items;
    }
    items$ = signal([], ...(ngDevMode ? [{ debugName: "items$" }] : []));
    _items = [];
    set itemsPerPage(value) {
        this._itemsPerPage = value;
    }
    get itemsPerPage() {
        return this._itemsPerPage;
    }
    _itemsPerPage = 10;
    itemsPerPageLabel = 'Items per page:';
    itemsPerPageOptions = [5, 10, 20, 50];
    itemsPerPageSelect;
    loading;
    noItemsLabel = 'No items found';
    pagination = false;
    set selectable(value) {
        this._selectable = value;
    }
    get selectable() {
        return this._selectable;
    }
    _selectable = false;
    selectAll = true;
    set sorterValue(value) {
        if (this.sorterValueDiffer) {
            const changes = this.sorterValueDiffer.diff(value);
            if (changes) {
                this._sorterValue = value;
                this.sorterValueChange.emit({ ...value });
            }
        }
    }
    get sorterValue() {
        return this._sorterValue;
    }
    _sorterValue = {};
    set tableFilter(filter) {
        this._tableFilter = filter;
        if (typeof filter === 'object') {
            this.tableFilterLabel = typeof filter.label === 'string' ? filter.label : this.tableFilterLabel;
            this.tableFilterPlaceholder =
                typeof filter.placeholder === 'string' ? filter.placeholder : this.tableFilterPlaceholder;
            this.tableFilterValue = typeof filter.value === 'string' ? filter.value : this.tableFilterValue;
        }
    }
    get tableFilter() {
        return this._tableFilter;
    }
    _tableFilter;
    tableFilterLabel = 'Filter:';
    tableFilterPlaceholder = 'type string...';
    set tableFilterValue(value) {
        if (this._tableFilterValue !== value) {
            this._tableFilterValue = value;
            this.tableFilterValueChange.emit(value);
            this.tableFiltered; // emits tableFiltered change
        }
    }
    get tableFilterValue() {
        return this._tableFilterValue;
    }
    _tableFilterValue = '';
    tableBodyProps = {};
    tableFootProps = {};
    tableHeadProps = {};
    tableProps = {};
    selectedItemsChange = output();
    sorterValueChange = output();
    activePageChange = output();
    itemsPerPageChange = output();
    rowClick = output();
    columnFilterValueChange = output();
    filteredItemsChange = output();
    tableFilterValueChange = output();
    cleanerClick = output();
    columnTemplates;
    templates = { tableDetails: null, tableData: null };
    columnFilterTemplates = {};
    columnLabelTemplates = {};
    summaryRowTemplate = null;
    ngAfterContentInit() {
        this.columnTemplates.forEach((child) => {
            if (child.id.startsWith('columnFilter_')) {
                this.columnFilterTemplates[child.id] = child.templateRef;
                return;
            }
            if (child.id.startsWith('columnLabel_')) {
                this.columnLabelTemplates[child.id] = child.templateRef;
                return;
            }
            if (child.id === 'tableSummaryRow') {
                this.summaryRowTemplate = child.templateRef;
                return;
            }
            this.templates[child.id] = child.templateRef;
        });
        this.columnFilterTemplates = { ...this.columnFilterTemplates };
        this.columnLabelTemplates = { ...this.columnLabelTemplates };
        setTimeout(() => {
            this.selectedAllSubject.next(this.selectedAll);
        }, 0);
    }
    get selectedAll() {
        const selected = this.items.some((item) => item._selected === true);
        const unselected = this.items.some((item) => item._selected !== true);
        return selected && unselected ? 'indeterminate' : selected;
    }
    get tableFilterState() {
        return this.tableFilterValue ? this.tableFilterValue : '';
    }
    get columnFilterState() {
        return this.columnFilterValue ? this.columnFilterValue : {};
    }
    get numberOfPages() {
        return this._itemsPerPage ? Math.ceil(this.sortedItems.length / this._itemsPerPage) : 1;
    }
    get colspan() {
        return this._selectable ? this.rawColumnNames$().length + 1 : this.rawColumnNames$().length;
    }
    get firstItemOnActivePageIndex() {
        return this._activePage ? (this._activePage - 1) * this._itemsPerPage : 0;
    }
    get itemsOnActivePage() {
        return this.sortedItems.slice(this.firstItemOnActivePageIndex, this.firstItemOnActivePageIndex + this._itemsPerPage);
    }
    get currentItems() {
        return this._activePage ? this.itemsOnActivePage : (this.sortedItems ?? []);
    }
    rawColumnNames$ = computed(() => {
        return getColumnNames(this.columns$(), this.items$());
    }, ...(ngDevMode ? [{ debugName: "rawColumnNames$" }] : []));
    itemsDataColumns$ = computed(() => this.rawColumnNames$().filter((name) => getColumnNamesFromItems(this.items$()).includes(name)), ...(ngDevMode ? [{ debugName: "itemsDataColumns$" }] : []));
    get sortedItems() {
        const columnKey = this.sorterValue.column;
        const currentColumn = this.columns.find((column) => column.key === columnKey);
        const columnSorter = this.columnSorter();
        if (!columnKey ||
            (!this.itemsDataColumns$().includes(columnKey) && typeof currentColumn?.sorter !== 'function') ||
            (typeof columnSorter === 'object' && columnSorter.external)) {
            return this.tableFiltered;
        }
        const tableFiltered = [...this.tableFiltered];
        // if values in column are to be sorted by numeric value they all have to be a type of number
        const flip = this.sorterValue.state === 'asc' ? 1 : this.sorterValue.state === 'desc' ? -1 : 0;
        if (flip === 0) {
            return tableFiltered;
        }
        if (typeof currentColumn?.sorter === 'function') {
            const sorted = tableFiltered.slice().sort(currentColumn.sorter);
            return flip < 0 ? sorted.reverse() : sorted;
        }
        const compareFn = (item1, item2) => {
            const value1 = item1[columnKey];
            const value2 = item2[columnKey];
            const a = typeof value1 === 'number' ? value1 : String(value1).toLowerCase();
            const b = typeof value2 === 'number' ? value2 : String(value2).toLowerCase();
            return a > b ? 1 * flip : b > a ? -1 * flip : 0;
        };
        return tableFiltered.slice().sort(compareFn);
    }
    get tableFilterEvent() {
        return typeof this.tableFilter === 'object' && this.tableFilter.lazy ? 'change' : 'input';
    }
    _columnFiltered = [];
    get columnFiltered() {
        let _items = this._items;
        if (this.columnFilter && typeof this.columnFilter === 'object' && this.columnFilter.external) {
            this._columnFiltered = _items;
            return _items;
        }
        Object.entries(this.columnFilterState).forEach(([key, value]) => {
            if (value instanceof Function) {
                _items = _items.filter((item) => value(item[key]));
                return;
            }
            const currentColumn = this.columns.find((column) => column.key === key);
            if (typeof currentColumn?.filter === 'function') {
                _items = _items.filter((item) => (typeof currentColumn.filter === 'function' && currentColumn.filter(item, value)) ?? !value);
                return;
            }
            const columnFilter = String(value).toLowerCase();
            if (columnFilter && this.itemsDataColumns$().includes(key)) {
                _items = _items.filter((item) => String(item[key]).toLowerCase().includes(columnFilter));
            }
        });
        this._columnFiltered = _items;
        return _items;
    }
    get tableFiltered() {
        let items = this.columnFiltered;
        if (!this.tableFilterState ||
            (this.tableFilter && typeof this.tableFilter === 'object' && this.tableFilter.external)) {
            if (this.itemsFilteredDiffer) {
                const changes = this.itemsFilteredDiffer.diff(items);
                if (changes) {
                    this.filteredItemsChange.emit(items);
                }
            }
            return items;
        }
        const filter = this.tableFilterState.toLowerCase();
        const valueContainFilter = (val) => String(val).toLowerCase().includes(filter);
        // @ts-ignore
        items = items.filter((item) => {
            return !!this.itemsDataColumns$().find((key) => valueContainFilter(item[key]));
        });
        if (this.itemsFilteredDiffer) {
            const changes = this.itemsFilteredDiffer.diff(items);
            if (changes) {
                this.filteredItemsChange.emit(items);
            }
        }
        return items;
    }
    get isFiltered() {
        return this.tableFilterState || this.sorterValue?.column || Object.values(this.columnFilterState).join('');
    }
    setItems(items) {
        if (Array.isArray(items)) {
            return items.map((item, index) => {
                return { ...item, ___id: item['___id'] ?? index };
            });
        }
        return [];
    }
    handleSortChange($event) {
        this.handleSorterChange($event.columnKey, $event.index, $event.order);
    }
    isSortable(i) {
        const columnSorter = this.columnSorter();
        if (!columnSorter) {
            return false;
        }
        const columnKey = this.rawColumnNames$()[i];
        const isDataColumn = this.itemsDataColumns$().includes(columnKey);
        const column = this.columns[i];
        const isColumnSortable = !this.columns.length || typeof column !== 'object' || column.sorter !== false;
        const isExternalSorter = typeof columnSorter === 'object' && columnSorter.external;
        return (isColumnSortable && (isDataColumn || typeof column?.sorter === 'function')) || isExternalSorter;
    }
    handleSorterChange(column, index, order) {
        if (!this.isSortable(index)) {
            return;
        }
        //if column changed or sort was descending change asc to true
        const sorterState = { ...this.sorterValue };
        if (sorterState.column === column) {
            const columnSorter = this.columnSorter();
            const resettable = columnSorter === true ||
                (typeof columnSorter === 'object' && columnSorter.resetable !== false && columnSorter.resettable !== false);
            const state = sorterState.state;
            if (order === undefined) {
                sorterState.state = state === 0 ? 'asc' : state === 'asc' ? 'desc' : resettable ? 0 : 'asc';
            }
            else {
                sorterState.state = order === 0 && !resettable ? state : order;
            }
        }
        else {
            sorterState.column = column;
            sorterState.state = 'asc';
        }
        this.sorterValue = { ...sorterState };
    }
    setActivePage($event) {
        this.activePage = $event;
    }
    handleColumnFilterChange($event) {
        const { column, value, type } = $event;
        this.setActivePage(1);
        this.columnFilterValue = { ...this.columnFilterValue, [`${column}`]: value };
    }
    handleTableFilterChange($event) {
        const { value, type } = $event;
        const isLazy = typeof this.tableFilter === 'object' && this.tableFilter.lazy === true;
        if (((isLazy && type === 'input') || (!isLazy && type === 'change')) && value.length !== 0) {
            return;
        }
        this.setActivePage(1);
        this.tableFilterValue = value;
    }
    clean($event) {
        this.columnFilterValue = {};
        this.tableFilterValue = '';
        this.sorterValue = {};
        this.tableFiltered; //emits tableFiltered change
        this.cleanerClick.emit();
    }
    handleItemsPerPageChange(value) {
        if (value !== this._itemsPerPage) {
            this.setActivePage(1);
            this._itemsPerPage = value;
            this.itemsPerPageChange.emit(value);
        }
    }
    handleRowChecked($event, item) {
        item._selected = item._selectable !== false && $event.target.checked;
        this.selectedItemsChange.emit(this.items.filter((item) => item._selected === true));
        setTimeout(() => {
            this.selectedAllSubject.next(this.selectedAll);
        }, 0);
    }
    handleSelectAllChange($event) {
        const tableFiltered = [...this.tableFiltered];
        this._items = this.items.map((item) => {
            if (!tableFiltered.includes(item)) {
                return { ...item };
            }
            return { ...item, _selected: item._selectable !== false ? $event : !!item._selected };
        });
        this.selectedAllSubject.next($event);
        setTimeout(() => {
            this.selectedItemsChange.emit(this.items.filter((item) => item._selected === true));
            this.selectedAllSubject.next(this.selectedAll);
        }, 0);
    }
    handleRowClick(param) {
        const { $event, item, i } = { ...param };
        const target = $event.target;
        const columnNames = this.rawColumnNames$();
        const selectable = this._selectable;
        const columnName = getClickedColumnName(target, columnNames, selectable);
        this.rowClick.emit({ item: item, index: i + this.firstItemOnActivePageIndex, columnName, event: $event });
    }
    ngOnChanges(changes) {
        if (changes['columnFilterValue']) {
            const columnFilterValueChange = changes['columnFilterValue'];
            const currentValue = columnFilterValueChange.currentValue;
            if (typeof currentValue === 'object' && Object.keys(currentValue ?? {}).length) {
                this.setActivePage(1);
            }
        }
        if (changes['tableFilterValue']) {
            const tableFilterValueChange = changes['tableFilterValue'];
            const currentValue = tableFilterValueChange.currentValue;
            if (currentValue.length) {
                this.setActivePage(1);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: SmartTableComponent, isStandalone: true, selector: "c-smart-table", inputs: { activePage: { classPropertyName: "activePage", publicName: "activePage", isSignal: false, isRequired: false, transformFunction: numberAttribute }, cleaner: { classPropertyName: "cleaner", publicName: "cleaner", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, clickableRows: { classPropertyName: "clickableRows", publicName: "clickableRows", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, columns: { classPropertyName: "columns", publicName: "columns", isSignal: false, isRequired: false, transformFunction: null }, columnFilter: { classPropertyName: "columnFilter", publicName: "columnFilter", isSignal: false, isRequired: false, transformFunction: null }, columnFilterValue: { classPropertyName: "columnFilterValue", publicName: "columnFilterValue", isSignal: false, isRequired: false, transformFunction: null }, columnSorter: { classPropertyName: "columnSorter", publicName: "columnSorter", isSignal: true, isRequired: false, transformFunction: null }, footer: { classPropertyName: "footer", publicName: "footer", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, header: { classPropertyName: "header", publicName: "header", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, items: { classPropertyName: "items", publicName: "items", isSignal: false, isRequired: false, transformFunction: null }, itemsPerPage: { classPropertyName: "itemsPerPage", publicName: "itemsPerPage", isSignal: false, isRequired: false, transformFunction: numberAttribute }, itemsPerPageLabel: { classPropertyName: "itemsPerPageLabel", publicName: "itemsPerPageLabel", isSignal: false, isRequired: false, transformFunction: null }, itemsPerPageOptions: { classPropertyName: "itemsPerPageOptions", publicName: "itemsPerPageOptions", isSignal: false, isRequired: false, transformFunction: null }, itemsPerPageSelect: { classPropertyName: "itemsPerPageSelect", publicName: "itemsPerPageSelect", isSignal: false, isRequired: false, transformFunction: null }, loading: { classPropertyName: "loading", publicName: "loading", isSignal: false, isRequired: false, transformFunction: null }, noItemsLabel: { classPropertyName: "noItemsLabel", publicName: "noItemsLabel", isSignal: false, isRequired: false, transformFunction: null }, pagination: { classPropertyName: "pagination", publicName: "pagination", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, selectable: { classPropertyName: "selectable", publicName: "selectable", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, selectAll: { classPropertyName: "selectAll", publicName: "selectAll", isSignal: false, isRequired: false, transformFunction: booleanAttribute }, sorterValue: { classPropertyName: "sorterValue", publicName: "sorterValue", isSignal: false, isRequired: false, transformFunction: null }, tableFilter: { classPropertyName: "tableFilter", publicName: "tableFilter", isSignal: false, isRequired: false, transformFunction: null }, tableFilterLabel: { classPropertyName: "tableFilterLabel", publicName: "tableFilterLabel", isSignal: false, isRequired: false, transformFunction: null }, tableFilterPlaceholder: { classPropertyName: "tableFilterPlaceholder", publicName: "tableFilterPlaceholder", isSignal: false, isRequired: false, transformFunction: null }, tableFilterValue: { classPropertyName: "tableFilterValue", publicName: "tableFilterValue", isSignal: false, isRequired: false, transformFunction: null }, tableBodyProps: { classPropertyName: "tableBodyProps", publicName: "tableBodyProps", isSignal: false, isRequired: false, transformFunction: null }, tableFootProps: { classPropertyName: "tableFootProps", publicName: "tableFootProps", isSignal: false, isRequired: false, transformFunction: null }, tableHeadProps: { classPropertyName: "tableHeadProps", publicName: "tableHeadProps", isSignal: false, isRequired: false, transformFunction: null }, tableProps: { classPropertyName: "tableProps", publicName: "tableProps", isSignal: false, isRequired: false, transformFunction: null } }, outputs: { selectedItemsChange: "selectedItemsChange", sorterValueChange: "sorterValueChange", activePageChange: "activePageChange", itemsPerPageChange: "itemsPerPageChange", rowClick: "rowClick", columnFilterValueChange: "columnFilterValueChange", filteredItemsChange: "filteredItemsChange", tableFilterValueChange: "tableFilterValueChange", cleanerClick: "cleanerClick" }, host: { attributes: { "ngSkipHydration": "true" } }, queries: [{ propertyName: "columnTemplates", predicate: TemplateIdDirective }], exportAs: ["cSmartTable"], usesOnChanges: true, ngImport: i0, template: "@if (itemsPerPageSelect || tableFilter || cleaner || templates['tableCustomHeader']) {\n  <div class=\"row my-2 mx-0\">\n    @if (tableFilter) {\n      <div class=\"col-auto p-0\">\n        <ng-container *ngTemplateOutlet=\"tableFilterTemplate\" />\n      </div>\n    }\n    @if (cleaner) {\n      <div class=\"col-auto p-0\">\n        <ng-container *ngTemplateOutlet=\"tableCleanerTemplate\" />\n      </div>\n    }\n    <ng-container *ngTemplateOutlet=\"templates['tableCustomHeader'] || tableCustomHeaderTemplate\" />\n\n    <ng-template #tableFilterTemplate>\n      @if (tableFilter) {\n        <c-smart-table-filter\n          (valueChange)=\"handleTableFilterChange($event)\"\n          [delay]=\"300\"\n          [filterLabel]=\"tableFilterLabel\"\n          [filterPlaceholder]=\"tableFilterPlaceholder\"\n          [onEvent]=\"tableFilterEvent\"\n          [value]=\"tableFilterState\"\n          cFilterInput\n        />\n      }\n    </ng-template>\n\n    <ng-template #tableCleanerTemplate>\n      @if (cleaner) {\n        <button\n          (click)=\"clean($event)\"\n          [disabled]=\"!isFiltered\"\n          [class.ms-1]=\"tableFilter\"\n          cButton\n          color=\"transparent\"\n          tabindex=\"0\"\n        >\n          <ng-container *ngTemplateOutlet=\"filterIconTemplate\" />\n        </button>\n      }\n    </ng-template>\n\n    <ng-template #tableCustomHeaderTemplate>\n      <div class=\"col-auto p-0 ms-auto\">\n        <ng-content select=\"[customHeader]\" />\n      </div>\n    </ng-template>\n  </div>\n}\n<div class=\"position-relative\">\n  <table [align]=\"tableProps?.align\"\n         [borderColor]=\"tableProps?.borderColor\"\n         [bordered]=\"tableProps?.bordered ?? undefined\"\n         [borderless]=\"tableProps?.borderless ?? undefined\"\n         [cHtmlAttr]=\"tableProps?.attributes ?? {}\"\n         [cTableColor]=\"tableProps?.color\"\n         [caption]=\"tableProps?.caption\"\n         [color]=\"tableProps?.color\"\n         [hover]=\"tableProps?.hover ?? undefined\"\n         [responsive]=\"tableProps?.responsive\"\n         [small]=\"tableProps?.small ?? undefined\"\n         [striped]=\"tableProps?.striped ?? undefined\"\n         cTable\n  >\n    @if (header) {\n      <c-smart-table-head\n        (columnFilterStateChange)=\"handleColumnFilterChange($event)\"\n        (selectAllChange)=\"handleSelectAllChange($event)\"\n        (sorterStateChange)=\"handleSortChange($event)\"\n        [cAlign]=\"tableHeadProps?.align\"\n        [cHtmlAttr]=\"tableHeadProps?.attributes ?? {}\"\n        [cTableColor]=\"tableHeadProps?.color\"\n        [columnFilterState]=\"columnFilterState\"\n        [columnFilterTemplates]=\"columnFilterTemplates\"\n        [columnFilter]=\"columnFilter\"\n        [columnGroups]=\"columnGroups$()\"\n        [columnLabelTemplates]=\"columnLabelTemplates\"\n        [columnSorter]=\"columnSorter()\"\n        [columns]=\"columns$()\"\n        [selectAll]=\"selectAll\"\n        [selectable]=\"selectable\"\n        [selectedAll]=\"(selectedAllSubject | async) ?? false\"\n        [showGroups]=\"showGroups$()\"\n        [sorterValue]=\"sorterValue\"\n      />\n    }\n\n    <ng-container *ngTemplateOutlet=\"tbodyDefaultTemplate\" />\n\n    @if (summaryRowTemplate && !footer) {\n      <tfoot>\n      <ng-container *ngTemplateOutlet=\"summaryRowTemplate\" />\n      </tfoot>\n    }\n    @if (footer) {\n      <c-smart-table-head\n        (selectAllChange)=\"handleSelectAllChange($event)\"\n        (sorterStateChange)=\"handleSortChange($event)\"\n        [cAlign]=\"tableFootProps?.align\"\n        [cHtmlAttr]=\"tableFootProps?.attributes ?? {}\"\n        [cTableColor]=\"tableFootProps?.color\"\n        [columnFilterTemplates]=\"columnFilterTemplates\"\n        [columnLabelTemplates]=\"columnLabelTemplates\"\n        [columnSorter]=\"columnSorter()\"\n        [columns]=\"columns$()\"\n        [selectAll]=\"selectAll\"\n        [selectable]=\"selectable\"\n        [selectedAll]=\"(selectedAllSubject | async) ?? false\"\n        [sorterValue]=\"sorterValue\"\n        [summaryRowTemplate]=\"summaryRowTemplate\"\n        component=\"tfoot\"\n      />\n    }\n\n  </table>\n  @if (loading) {\n    <c-element-cover\n      [boundaries]=\"[\n        { sides: ['top'], query: 'td' },\n        { sides: ['bottom'], query: 'tbody' }\n      ]\"\n    />\n  }\n</div>\n\n@if (pagination || itemsPerPageSelect) {\n  <div class=\"row\">\n    <div class=\"col\">\n      @if (pagination && numberOfPages > 1) {\n        <c-smart-pagination\n          (activePageChange)=\"setActivePage($event)\"\n          [activePage]=\"activePage\"\n          [pages]=\"numberOfPages\"\n        />\n      }\n    </div>\n    <div class=\"col-auto ms-auto\">\n      @if (itemsPerPageSelect) {\n        <c-smart-table-items-per-page-selector\n          (itemsPerPageChange)=\"handleItemsPerPageChange($event)\"\n          [itemsPerPageLabel]=\"itemsPerPageLabel\"\n          [itemsPerPageOptions]=\"itemsPerPageOptions\"\n          [itemsPerPage]=\"itemsPerPage\"\n        />\n      }\n    </div>\n  </div>\n}\n<ng-template #tbodyDefaultTemplate>\n  <tbody [cAlign]=\"tableBodyProps?.align\"\n         [cHtmlAttr]=\"tableBodyProps?.attributes ?? {}\"\n         [cTableColor]=\"tableBodyProps?.color\"\n  >\n    @for (item of currentItems; track item; let i = $index, cnt = $count, isEven = $even, isOdd = $odd) {\n      <tr (click)=\"handleRowClick({$event, item, i})\"\n          [cAlign]=\"item._props?.align\"\n          [cTableActive]=\"item._props?.active ?? undefined\"\n          [cTableColor]=\"item._props?.color\"\n          [style.cursor]=\"clickableRows ? 'pointer' : 'auto'\"\n          [tabindex]=\"clickableRows ? 0 : -1\"\n      >\n        @if (selectable) {\n          <td [cAlign]=\"item?.['_cellProps']?._all?.align\"\n              [cTableActive]=\"item?.['_cellProps']?._all?.active ?? undefined\"\n              [cTableColor]=\"item?.['_cellProps']?._all?.color\"\n          >\n            <input (change)=\"handleRowChecked($event, item)\"\n                   [checked]=\"item._selected ?? false\"\n                   [disabled]=\"(item?._selectable === false)\"\n                   autocomplete=\"off\"\n                   cFormCheckInput\n            >\n          </td>\n        }\n        @for (column of columns$(); track column; let i = $index) {\n          @let columnKey = getColumnKey(column);\n          <ng-container\n            *ngTemplateOutlet=\"templates['tableData'] || columnDefaultTemplate; context: {column, item, columnName: getColumnKey(column), tdContent: item[columnKey], cellProps: getTableDataCellProps(column, item) }\"\n          />\n        }\n      </tr>\n      @if (templates['tableDetails']) {\n        <!--      <ng-container *ngTemplateOutlet=\"rowDetailsDefaultTemplate\"></ng-container>-->\n        <tr>\n          <td [colSpan]=\"colspan\" [style.border-bottom-width]=\"0\" class=\"p-0\" tabindex=\"-1\"></td>\n        </tr>\n        <tr class=\"p-0\">\n          <td [colSpan]=\"colspan\" [style.border]=\"0\" class=\"p-0\">\n            <ng-container *ngTemplateOutlet=\"templates['tableDetails']; context: {item, index: i}\" />\n          </td>\n        </tr>\n      }\n    }\n  <ng-container *ngTemplateOutlet=\"noItemsDefaultTemplate\" />\n  </tbody>\n</ng-template>\n\n<ng-template\n  #columnDefaultTemplate\n  let-cellProps=\"cellProps\"\n  let-column=\"column\"\n  let-columnName=\"columnName\"\n  let-item=\"item\"\n  let-tdContent=\"tdContent\"\n>\n  @if (tdContent !== undefined) {\n    <td [cAlign]=\"cellProps?.align\"\n        [cHtmlAttr]=\"cellProps?.['_attributes'] ?? undefined\"\n        [cTableActive]=\"cellProps?.['active']\"\n        [cTableColor]=\"cellProps?.color ?? column[columnName]?._colProps?.color ?? undefined\"\n        [class]=\"getTableDataCellClass(column, item)\"\n        [style]=\"getTableDataCellStyles(column, item)\"\n    >\n      {{ tdContent }}\n    </td>\n  }\n</ng-template>\n\n<!--<ng-template #rowDetailsDefaultTemplate let-item let-index>-->\n<!--  <tr><td [colSpan]=\"colspan\" class=\"p-0\" tabindex=\"-1\" [style]=\"{borderBottomWidth: 0}\"></td></tr>-->\n<!--  <tr class=\"p-0\">-->\n<!--    <td [colSpan]=\"colspan\" class=\"p-0\" [style]=\"{border: 0}\">-->\n<!--      <ng-container *ngTemplateOutlet=\"templates?.tableDetails\"></ng-container>-->\n<!--    </td>-->\n<!--  </tr>-->\n<!--</ng-template>-->\n\n<ng-template #noItemsDefaultTemplate>\n  @if (!currentItems.length) {\n    <tr>\n      <td [colSpan]=\"colspan\" class=\"justify-content-center\">{{ noItemsLabel }}</td>\n    </tr>\n  }\n</ng-template>\n\n<!--todo: filterIconCustomTemplate-->\n<ng-template #filterIconTemplate>\n  <svg class=\"icon icon-custom-size\" pointer-events=\"none\" role=\"img\" viewBox=\"0 0 512 512\"\n       width=\"18\" xmlns=\"http://www.w3.org/2000/svg\">\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"40 16 40 53.828 109.024 136 150.815 136 76.896 48 459.51 48 304 242.388 304 401.373 241.373 464 240 464 240 368 208 368 208 496 254.627 496 336 414.627 336 253.612 496 53.612 496 16 40 16\">\n    </polygon>\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"166.403 248.225 226.864 187.763 204.237 165.135 143.775 225.597 83.313 165.135 60.687 187.763 121.148 248.225 60.687 308.687 83.313 331.314 143.775 270.852 204.237 331.314 226.864 308.687 166.403 248.225\">\n    </polygon>\n  </svg>\n</ng-template>\n", styles: [":host{display:block}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: AlignDirective, selector: "[cAlign]", inputs: ["cAlign"], exportAs: ["cAlign"] }, { kind: "directive", type: ButtonDirective, selector: "[cButton]", inputs: ["active", "color", "disabled", "shape", "size", "tabindex", "type", "variant"], exportAs: ["cButton"] }, { kind: "component", type: ElementCoverComponent, selector: "c-element-cover, [cElementCover]", inputs: ["boundaries", "opacity"] }, { kind: "directive", type: FilterInputDirective, selector: "[cFilterInput]", inputs: ["delay", "onEvent", "value"], outputs: ["valueChange"], exportAs: ["cFilterInput"] }, { kind: "directive", type: FormCheckInputDirective, selector: "input[cFormCheckInput]", inputs: ["type", "indeterminate", "valid"] }, { kind: "directive", type: HtmlAttributesDirective, selector: "[cHtmlAttr]", inputs: ["cHtmlAttr"], exportAs: ["cHtmlAttr"] }, { kind: "component", type: SmartPaginationComponent, selector: "c-smart-pagination", inputs: ["activePage", "arrows", "doubleArrows", "dots", "limit", "pages", "firstButton", "lastButton", "nextButton", "previousButton", "align", "size", "role"], outputs: ["activePageChange"], exportAs: ["cSmartPagination"] }, { kind: "component", type: SmartTableFilterComponent, selector: "c-smart-table-filter", inputs: ["filterLabel", "filterPlaceholder", "sizing"] }, { kind: "component", type: SmartTableHeadComponent, selector: "c-smart-table-head", inputs: ["columnFilter", "columnFilterState", "columnSorter", "component", "columns", "columnGroups", "selectable", "selectAll", "selectedAll", "showGroups", "sorterValue", "columnFilterTemplates", "columnLabelTemplates", "summaryRowTemplate"], outputs: ["columnFilterStateChange", "sorterStateChange", "selectAllChange"] }, { kind: "component", type: SmartTableItemsPerPageSelectorComponent, selector: "c-smart-table-items-per-page-selector", inputs: ["itemsPerPage", "itemsPerPageLabel", "itemsPerPageOptions"], outputs: ["itemsPerPageChange"] }, { kind: "directive", type: TableActiveDirective, selector: "[cTableActive]", inputs: ["cTableActive"], exportAs: ["cTableActive"] }, { kind: "directive", type: TableColorDirective, selector: "[cTableColor]", inputs: ["cTableColor"], exportAs: ["cTableColor"] }, { kind: "directive", type: TableDirective, selector: "table[cTable]", inputs: ["align", "borderColor", "bordered", "borderless", "caption", "color", "hover", "responsive", "small", "striped", "stripedColumns"], exportAs: ["cTable"] }, { kind: "pipe", type: AsyncPipe, name: "async" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-smart-table', exportAs: 'cSmartTable', imports: [
                        AsyncPipe,
                        NgTemplateOutlet,
                        AlignDirective,
                        ButtonDirective,
                        ElementCoverComponent,
                        FilterInputDirective,
                        FormCheckInputDirective,
                        HtmlAttributesDirective,
                        SmartPaginationComponent,
                        SmartTableFilterComponent,
                        SmartTableHeadComponent,
                        SmartTableItemsPerPageSelectorComponent,
                        TableActiveDirective,
                        TableColorDirective,
                        TableDirective
                    ], host: {
                        ngSkipHydration: 'true'
                    }, template: "@if (itemsPerPageSelect || tableFilter || cleaner || templates['tableCustomHeader']) {\n  <div class=\"row my-2 mx-0\">\n    @if (tableFilter) {\n      <div class=\"col-auto p-0\">\n        <ng-container *ngTemplateOutlet=\"tableFilterTemplate\" />\n      </div>\n    }\n    @if (cleaner) {\n      <div class=\"col-auto p-0\">\n        <ng-container *ngTemplateOutlet=\"tableCleanerTemplate\" />\n      </div>\n    }\n    <ng-container *ngTemplateOutlet=\"templates['tableCustomHeader'] || tableCustomHeaderTemplate\" />\n\n    <ng-template #tableFilterTemplate>\n      @if (tableFilter) {\n        <c-smart-table-filter\n          (valueChange)=\"handleTableFilterChange($event)\"\n          [delay]=\"300\"\n          [filterLabel]=\"tableFilterLabel\"\n          [filterPlaceholder]=\"tableFilterPlaceholder\"\n          [onEvent]=\"tableFilterEvent\"\n          [value]=\"tableFilterState\"\n          cFilterInput\n        />\n      }\n    </ng-template>\n\n    <ng-template #tableCleanerTemplate>\n      @if (cleaner) {\n        <button\n          (click)=\"clean($event)\"\n          [disabled]=\"!isFiltered\"\n          [class.ms-1]=\"tableFilter\"\n          cButton\n          color=\"transparent\"\n          tabindex=\"0\"\n        >\n          <ng-container *ngTemplateOutlet=\"filterIconTemplate\" />\n        </button>\n      }\n    </ng-template>\n\n    <ng-template #tableCustomHeaderTemplate>\n      <div class=\"col-auto p-0 ms-auto\">\n        <ng-content select=\"[customHeader]\" />\n      </div>\n    </ng-template>\n  </div>\n}\n<div class=\"position-relative\">\n  <table [align]=\"tableProps?.align\"\n         [borderColor]=\"tableProps?.borderColor\"\n         [bordered]=\"tableProps?.bordered ?? undefined\"\n         [borderless]=\"tableProps?.borderless ?? undefined\"\n         [cHtmlAttr]=\"tableProps?.attributes ?? {}\"\n         [cTableColor]=\"tableProps?.color\"\n         [caption]=\"tableProps?.caption\"\n         [color]=\"tableProps?.color\"\n         [hover]=\"tableProps?.hover ?? undefined\"\n         [responsive]=\"tableProps?.responsive\"\n         [small]=\"tableProps?.small ?? undefined\"\n         [striped]=\"tableProps?.striped ?? undefined\"\n         cTable\n  >\n    @if (header) {\n      <c-smart-table-head\n        (columnFilterStateChange)=\"handleColumnFilterChange($event)\"\n        (selectAllChange)=\"handleSelectAllChange($event)\"\n        (sorterStateChange)=\"handleSortChange($event)\"\n        [cAlign]=\"tableHeadProps?.align\"\n        [cHtmlAttr]=\"tableHeadProps?.attributes ?? {}\"\n        [cTableColor]=\"tableHeadProps?.color\"\n        [columnFilterState]=\"columnFilterState\"\n        [columnFilterTemplates]=\"columnFilterTemplates\"\n        [columnFilter]=\"columnFilter\"\n        [columnGroups]=\"columnGroups$()\"\n        [columnLabelTemplates]=\"columnLabelTemplates\"\n        [columnSorter]=\"columnSorter()\"\n        [columns]=\"columns$()\"\n        [selectAll]=\"selectAll\"\n        [selectable]=\"selectable\"\n        [selectedAll]=\"(selectedAllSubject | async) ?? false\"\n        [showGroups]=\"showGroups$()\"\n        [sorterValue]=\"sorterValue\"\n      />\n    }\n\n    <ng-container *ngTemplateOutlet=\"tbodyDefaultTemplate\" />\n\n    @if (summaryRowTemplate && !footer) {\n      <tfoot>\n      <ng-container *ngTemplateOutlet=\"summaryRowTemplate\" />\n      </tfoot>\n    }\n    @if (footer) {\n      <c-smart-table-head\n        (selectAllChange)=\"handleSelectAllChange($event)\"\n        (sorterStateChange)=\"handleSortChange($event)\"\n        [cAlign]=\"tableFootProps?.align\"\n        [cHtmlAttr]=\"tableFootProps?.attributes ?? {}\"\n        [cTableColor]=\"tableFootProps?.color\"\n        [columnFilterTemplates]=\"columnFilterTemplates\"\n        [columnLabelTemplates]=\"columnLabelTemplates\"\n        [columnSorter]=\"columnSorter()\"\n        [columns]=\"columns$()\"\n        [selectAll]=\"selectAll\"\n        [selectable]=\"selectable\"\n        [selectedAll]=\"(selectedAllSubject | async) ?? false\"\n        [sorterValue]=\"sorterValue\"\n        [summaryRowTemplate]=\"summaryRowTemplate\"\n        component=\"tfoot\"\n      />\n    }\n\n  </table>\n  @if (loading) {\n    <c-element-cover\n      [boundaries]=\"[\n        { sides: ['top'], query: 'td' },\n        { sides: ['bottom'], query: 'tbody' }\n      ]\"\n    />\n  }\n</div>\n\n@if (pagination || itemsPerPageSelect) {\n  <div class=\"row\">\n    <div class=\"col\">\n      @if (pagination && numberOfPages > 1) {\n        <c-smart-pagination\n          (activePageChange)=\"setActivePage($event)\"\n          [activePage]=\"activePage\"\n          [pages]=\"numberOfPages\"\n        />\n      }\n    </div>\n    <div class=\"col-auto ms-auto\">\n      @if (itemsPerPageSelect) {\n        <c-smart-table-items-per-page-selector\n          (itemsPerPageChange)=\"handleItemsPerPageChange($event)\"\n          [itemsPerPageLabel]=\"itemsPerPageLabel\"\n          [itemsPerPageOptions]=\"itemsPerPageOptions\"\n          [itemsPerPage]=\"itemsPerPage\"\n        />\n      }\n    </div>\n  </div>\n}\n<ng-template #tbodyDefaultTemplate>\n  <tbody [cAlign]=\"tableBodyProps?.align\"\n         [cHtmlAttr]=\"tableBodyProps?.attributes ?? {}\"\n         [cTableColor]=\"tableBodyProps?.color\"\n  >\n    @for (item of currentItems; track item; let i = $index, cnt = $count, isEven = $even, isOdd = $odd) {\n      <tr (click)=\"handleRowClick({$event, item, i})\"\n          [cAlign]=\"item._props?.align\"\n          [cTableActive]=\"item._props?.active ?? undefined\"\n          [cTableColor]=\"item._props?.color\"\n          [style.cursor]=\"clickableRows ? 'pointer' : 'auto'\"\n          [tabindex]=\"clickableRows ? 0 : -1\"\n      >\n        @if (selectable) {\n          <td [cAlign]=\"item?.['_cellProps']?._all?.align\"\n              [cTableActive]=\"item?.['_cellProps']?._all?.active ?? undefined\"\n              [cTableColor]=\"item?.['_cellProps']?._all?.color\"\n          >\n            <input (change)=\"handleRowChecked($event, item)\"\n                   [checked]=\"item._selected ?? false\"\n                   [disabled]=\"(item?._selectable === false)\"\n                   autocomplete=\"off\"\n                   cFormCheckInput\n            >\n          </td>\n        }\n        @for (column of columns$(); track column; let i = $index) {\n          @let columnKey = getColumnKey(column);\n          <ng-container\n            *ngTemplateOutlet=\"templates['tableData'] || columnDefaultTemplate; context: {column, item, columnName: getColumnKey(column), tdContent: item[columnKey], cellProps: getTableDataCellProps(column, item) }\"\n          />\n        }\n      </tr>\n      @if (templates['tableDetails']) {\n        <!--      <ng-container *ngTemplateOutlet=\"rowDetailsDefaultTemplate\"></ng-container>-->\n        <tr>\n          <td [colSpan]=\"colspan\" [style.border-bottom-width]=\"0\" class=\"p-0\" tabindex=\"-1\"></td>\n        </tr>\n        <tr class=\"p-0\">\n          <td [colSpan]=\"colspan\" [style.border]=\"0\" class=\"p-0\">\n            <ng-container *ngTemplateOutlet=\"templates['tableDetails']; context: {item, index: i}\" />\n          </td>\n        </tr>\n      }\n    }\n  <ng-container *ngTemplateOutlet=\"noItemsDefaultTemplate\" />\n  </tbody>\n</ng-template>\n\n<ng-template\n  #columnDefaultTemplate\n  let-cellProps=\"cellProps\"\n  let-column=\"column\"\n  let-columnName=\"columnName\"\n  let-item=\"item\"\n  let-tdContent=\"tdContent\"\n>\n  @if (tdContent !== undefined) {\n    <td [cAlign]=\"cellProps?.align\"\n        [cHtmlAttr]=\"cellProps?.['_attributes'] ?? undefined\"\n        [cTableActive]=\"cellProps?.['active']\"\n        [cTableColor]=\"cellProps?.color ?? column[columnName]?._colProps?.color ?? undefined\"\n        [class]=\"getTableDataCellClass(column, item)\"\n        [style]=\"getTableDataCellStyles(column, item)\"\n    >\n      {{ tdContent }}\n    </td>\n  }\n</ng-template>\n\n<!--<ng-template #rowDetailsDefaultTemplate let-item let-index>-->\n<!--  <tr><td [colSpan]=\"colspan\" class=\"p-0\" tabindex=\"-1\" [style]=\"{borderBottomWidth: 0}\"></td></tr>-->\n<!--  <tr class=\"p-0\">-->\n<!--    <td [colSpan]=\"colspan\" class=\"p-0\" [style]=\"{border: 0}\">-->\n<!--      <ng-container *ngTemplateOutlet=\"templates?.tableDetails\"></ng-container>-->\n<!--    </td>-->\n<!--  </tr>-->\n<!--</ng-template>-->\n\n<ng-template #noItemsDefaultTemplate>\n  @if (!currentItems.length) {\n    <tr>\n      <td [colSpan]=\"colspan\" class=\"justify-content-center\">{{ noItemsLabel }}</td>\n    </tr>\n  }\n</ng-template>\n\n<!--todo: filterIconCustomTemplate-->\n<ng-template #filterIconTemplate>\n  <svg class=\"icon icon-custom-size\" pointer-events=\"none\" role=\"img\" viewBox=\"0 0 512 512\"\n       width=\"18\" xmlns=\"http://www.w3.org/2000/svg\">\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"40 16 40 53.828 109.024 136 150.815 136 76.896 48 459.51 48 304 242.388 304 401.373 241.373 464 240 464 240 368 208 368 208 496 254.627 496 336 414.627 336 253.612 496 53.612 496 16 40 16\">\n    </polygon>\n    <polygon\n      class=\"ci-primary\"\n      fill=\"var(--ci-primary-color, currentColor)\"\n      points=\"166.403 248.225 226.864 187.763 204.237 165.135 143.775 225.597 83.313 165.135 60.687 187.763 121.148 248.225 60.687 308.687 83.313 331.314 143.775 270.852 204.237 331.314 226.864 308.687 166.403 248.225\">\n    </polygon>\n  </svg>\n</ng-template>\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { activePage: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], cleaner: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], clickableRows: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], columns: [{
                type: Input
            }], columnFilter: [{
                type: Input
            }], columnFilterValue: [{
                type: Input
            }], columnSorter: [{ type: i0.Input, args: [{ isSignal: true, alias: "columnSorter", required: false }] }], footer: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], header: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], items: [{
                type: Input
            }], itemsPerPage: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], itemsPerPageLabel: [{
                type: Input
            }], itemsPerPageOptions: [{
                type: Input
            }], itemsPerPageSelect: [{
                type: Input
            }], loading: [{
                type: Input
            }], noItemsLabel: [{
                type: Input
            }], pagination: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], selectable: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], selectAll: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], sorterValue: [{
                type: Input
            }], tableFilter: [{
                type: Input
            }], tableFilterLabel: [{
                type: Input
            }], tableFilterPlaceholder: [{
                type: Input
            }], tableFilterValue: [{
                type: Input
            }], tableBodyProps: [{
                type: Input
            }], tableFootProps: [{
                type: Input
            }], tableHeadProps: [{
                type: Input
            }], tableProps: [{
                type: Input
            }], selectedItemsChange: [{ type: i0.Output, args: ["selectedItemsChange"] }], sorterValueChange: [{ type: i0.Output, args: ["sorterValueChange"] }], activePageChange: [{ type: i0.Output, args: ["activePageChange"] }], itemsPerPageChange: [{ type: i0.Output, args: ["itemsPerPageChange"] }], rowClick: [{ type: i0.Output, args: ["rowClick"] }], columnFilterValueChange: [{ type: i0.Output, args: ["columnFilterValueChange"] }], filteredItemsChange: [{ type: i0.Output, args: ["filteredItemsChange"] }], tableFilterValueChange: [{ type: i0.Output, args: ["tableFilterValueChange"] }], cleanerClick: [{ type: i0.Output, args: ["cleanerClick"] }], columnTemplates: [{
                type: ContentChildren,
                args: [TemplateIdDirective]
            }] } });

class SmartTableModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: SmartTableModule, imports: [SmartTableComponent, SmartTableFilterComponent], exports: [SmartTableComponent, SmartTableFilterComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableModule, imports: [SmartTableComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: SmartTableModule, decorators: [{
            type: NgModule,
            args: [{
                    exports: [SmartTableComponent, SmartTableFilterComponent],
                    imports: [SmartTableComponent, SmartTableFilterComponent]
                }]
        }] });

class StepperStepComponent {
    static ngAcceptInputType_disabled;
    static ngAcceptInputType_optional;
    static ngAcceptInputType_editable;
    /**
     * ViewContainerRef to render the step content.
     * This allows dynamic rendering of templates within the step.
     */
    viewContainerRef = inject(ViewContainerRef);
    /**
     * Whether the step is disabled.
     * @return boolean
     * @default false
     */
    disabled = input(false, { ...(ngDevMode ? { debugName: "disabled" } : {}), transform: booleanAttribute });
    /**
     * Unique identifier for the step.
     * @return string
     * @default undefined
     */
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    /**
     * Optional indicator to display in the step.
     * Can be a string or a TemplateRef for custom icons.
     * @return string | TemplateRef<any>
     * @default (step index will be used if not provided)
     */
    indicator = input(...(ngDevMode ? [undefined, { debugName: "indicator" }] : []));
    /**
     * Context for the indicator template.
     * If `indicator` is a TemplateRef, this context will be passed to it.
     * @return unknown
     * @default undefined
     */
    indicatorCtx = input(...(ngDevMode ? [undefined, { debugName: "indicatorCtx" }] : []));
    /**
     * Label for the step, which is required.
     * Can be a string or a TemplateRef for custom labels.
     * @return string | TemplateRef<any>
     */
    label = input.required(...(ngDevMode ? [{ debugName: "label" }] : []));
    /**
     * Context for the label template.
     * If `label` is a TemplateRef, this context will be passed to it.
     * @return unknown
     * @default undefined
     */
    labelCtx = input(...(ngDevMode ? [undefined, { debugName: "labelCtx" }] : []));
    /**
     * Whether the step is valid.
     * This can be used to indicate if the step has been completed successfully.
     * @return boolean | undefined | null
     * @default undefined
     */
    valid = input(...(ngDevMode ? [undefined, { debugName: "valid" }] : []));
    /**
     * Whether the step is editable.
     * If true, the step can be modified by the user.
     * @return boolean
     * @default true
     * todo - implement editable functionality
     */
    editable = input(true, { ...(ngDevMode ? { debugName: "editable" } : {}), transform: booleanAttribute });
    /**
     * Whether the step is optional.
     * If true, the step does not need to be completed for the overall process to succeed.
     * @return boolean
     * @default false
     * todo - implement optional functionality
     */
    optional = input(false, { ...(ngDevMode ? { debugName: "optional" } : {}), transform: booleanAttribute });
    /**
     * Template reference for the content of the step.
     * This is where the main content of the step will be rendered.
     */
    templateRef = viewChild.required('contentTpl', { read: TemplateRef });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperStepComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: StepperStepComponent, isStandalone: true, selector: "c-stepper-step", inputs: { disabled: { classPropertyName: "disabled", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, indicator: { classPropertyName: "indicator", publicName: "indicator", isSignal: true, isRequired: false, transformFunction: null }, indicatorCtx: { classPropertyName: "indicatorCtx", publicName: "indicatorCtx", isSignal: true, isRequired: false, transformFunction: null }, label: { classPropertyName: "label", publicName: "label", isSignal: true, isRequired: true, transformFunction: null }, labelCtx: { classPropertyName: "labelCtx", publicName: "labelCtx", isSignal: true, isRequired: false, transformFunction: null }, valid: { classPropertyName: "valid", publicName: "valid", isSignal: true, isRequired: false, transformFunction: null }, editable: { classPropertyName: "editable", publicName: "editable", isSignal: true, isRequired: false, transformFunction: null }, optional: { classPropertyName: "optional", publicName: "optional", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "style": "{ display: \"none\" }" } }, viewQueries: [{ propertyName: "templateRef", first: true, predicate: ["contentTpl"], descendants: true, read: TemplateRef, isSignal: true }], exportAs: ["cStepperStep"], ngImport: i0, template: `
    <ng-template #contentTpl>
      <ng-content />
    </ng-template>
  `, isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperStepComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'c-stepper-step',
                    exportAs: 'cStepperStep',
                    template: `
    <ng-template #contentTpl>
      <ng-content />
    </ng-template>
  `,
                    host: {
                        '[style]': '{ display: "none" }'
                    }
                }]
        }], propDecorators: { disabled: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], indicator: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicator", required: false }] }], indicatorCtx: [{ type: i0.Input, args: [{ isSignal: true, alias: "indicatorCtx", required: false }] }], label: [{ type: i0.Input, args: [{ isSignal: true, alias: "label", required: true }] }], labelCtx: [{ type: i0.Input, args: [{ isSignal: true, alias: "labelCtx", required: false }] }], valid: [{ type: i0.Input, args: [{ isSignal: true, alias: "valid", required: false }] }], editable: [{ type: i0.Input, args: [{ isSignal: true, alias: "editable", required: false }] }], optional: [{ type: i0.Input, args: [{ isSignal: true, alias: "optional", required: false }] }], templateRef: [{ type: i0.ViewChild, args: ['contentTpl', { ...{ read: TemplateRef }, isSignal: true }] }] } });

class StepButtonDirective {
    hostElement = inject(ElementRef);
    focus(origin) {
        this.hostElement.nativeElement.focus();
    }
    get disabled() {
        return this.hostElement.nativeElement.disabled;
    }
    getLabel() {
        return this.hostElement.nativeElement.innerText;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: StepButtonDirective, isStandalone: true, selector: "button[cStepButton]", exportAs: ["cStepButton"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[cStepButton]',
                    exportAs: 'cStepButton'
                }]
        }] });

let nextId$1 = 0;
class StepperComponent {
    static ngAcceptInputType_defaultActiveStepIndex;
    static ngAcceptInputType_linear;
    static ngAcceptInputType_validation;
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    #rtlService = inject(RtlService);
    #isRtl = signal(false, ...(ngDevMode ? [{ debugName: "#isRtl" }] : []));
    constructor() {
        afterEveryRender({
            read: () => {
                this.#isRtl.set(this.#rtlService.isRTL(this.#elementRef.nativeElement));
            }
        });
    }
    /**
     * Currently active step index
     * @returns number
     * @default 0
     */
    activeStepIndexInput = input(undefined, { ...(ngDevMode ? { debugName: "activeStepIndexInput" } : {}), alias: 'activeStepIndex' });
    /**
     * Linked signal for the active step index.
     * This signal updates based on the input value and emits changes.
     */
    activeStepIndex = linkedSignal({ ...(ngDevMode ? { debugName: "activeStepIndex" } : {}), source: this.activeStepIndexInput,
        computation: (newIndex, previous) => {
            if (previous === undefined && newIndex === undefined) {
                return this.defaultActiveStepIndex();
            }
            const prevValue = previous?.value ?? newIndex ?? this.defaultActiveStepIndex();
            // Return previous value in these cases
            if (previous?.source === undefined || this.isStepOutOfScope(newIndex ?? -1) || !this.isStepValid(prevValue)) {
                this.activeStepIndexChange.emit(prevValue);
                return prevValue;
            }
            return newIndex ?? this.defaultActiveStepIndex();
        } });
    #activeStepIdxEffect = effect(() => {
        const activeStepIndex = this.activeStepIndex() ?? untracked(this.defaultActiveStepIndex);
        if (this.#focusKeyManagerInit()) {
            const focusManager = this.#focusKeyManager;
            if (this.linear() && activeStepIndex > (focusManager?.activeItemIndex ?? -1)) {
                focusManager?.setNextItemActive();
            }
            else if (focusManager?.activeItemIndex !== activeStepIndex) {
                focusManager?.setActiveItem(activeStepIndex);
            }
            else {
                focusManager?.updateActiveItem(activeStepIndex);
            }
        }
    }, ...(ngDevMode ? [{ debugName: "#activeStepIdxEffect" }] : []));
    /**
     * Initial active step index
     * @return number
     * @default 0
     */
    defaultActiveStepIndex = input(0, { ...(ngDevMode ? { debugName: "defaultActiveStepIndex" } : {}), transform: numberAttribute });
    /**
     * Unique identifier for the Stepper component.
     * If not provided, a default ID will be generated.
     * @return string
     * @default 'stepper-<nextId>'
     */
    idInput = input(undefined, { ...(ngDevMode ? { debugName: "idInput" } : {}), alias: 'id' });
    /**
     * Unique identifier for the Stepper component.
     * If `idInput` is not provided, a default ID will be generated.
     * @return string
     */
    id = computed(() => this.idInput() ?? `stepper-${nextId$1++}`, ...(ngDevMode ? [{ debugName: "id" }] : []));
    /**
     * Layout orientation
     * - `'horizontal'`: Step indicators and content are placed horizontally (default).
     * - `'vertical'`: Step indicators and content are stacked vertically.
     *
     * Choose `'vertical'` layout for mobile or narrow designs.
     * @return 'horizontal' | 'vertical'
     * @default 'horizontal'
     */
    layout = input('horizontal', ...(ngDevMode ? [{ debugName: "layout" }] : []));
    /**
     * Enforces linear progression (cannot skip steps).
     * - `true`: Users must complete steps sequentially.
     * - `false`: Users can jump freely between steps.
     * @return boolean
     * @default true
     */
    linear = input(true, { ...(ngDevMode ? { debugName: "linear" } : {}), transform: booleanAttribute });
    /**
     * Layout of the step indicator (icon and label)
     * - `'vertical'` – Places the label below the indicator icon.
     * - `'horizontal'` – Places the label beside the indicator icon (default).
     * This prop has no effect when `layout="vertical"` is used.
     * @return 'vertical' | 'horizontal'
     * @default 'horizontal'
     */
    stepButtonLayout = input('horizontal', ...(ngDevMode ? [{ debugName: "stepButtonLayout" }] : []));
    /**
     * Enforces validation of steps.
     * Each step must be valid before advancing to the next.
     * @return boolean
     * @default false
     */
    validation = input(false, { ...(ngDevMode ? { debugName: "validation" } : {}), transform: booleanAttribute });
    /**
     * Event fired when the user completes the last step of the Form Wizard.
     * Use this to trigger a submit action or redirect after the final step.
     * @return boolean
     */
    finished = output();
    /**
     * Event fired when the user triggers the reset() action.
     * Use this to reset or clear related form data.
     * @return void
     */
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onReset = output();
    /**
     * Indicates if the Stepper is currently resetting.
     * This is set to `true` when the reset() method is called.
     * @return boolean
     */
    resetting = signal(false, ...(ngDevMode ? [{ debugName: "resetting" }] : []));
    /**
     * Event fired when the active step changes in the Angular Stepper component.
     * @return number
     */
    activeStepIndexChange = output();
    /**
     * Event fired after a validation check completes for a specific step in the Form Wizard.
     * Receives an object with the step index and the validation result (`true` or `false`).
     * Useful for custom validation handling across multistep forms.
     * todo - implement validation logic
     */
    // readonly stepValidationComplete = output<StepperStepValidationResult>();
    stepsContent = contentChildren(StepperStepComponent, { ...(ngDevMode ? { debugName: "stepsContent" } : {}), descendants: true });
    steps = computed(() => {
        const stepsContent = this.stepsContent();
        return stepsContent.map((step, index) => {
            const { disabled, editable, id, indicator, indicatorCtx, label, labelCtx, optional, templateRef, valid } = {
                ...step
            };
            return {
                templateRef: templateRef(),
                disabled: disabled(),
                editable: editable(),
                id: `${id() ?? this.id()}-step-${index}`,
                indicator: indicator() ?? index + 1,
                indicatorRef: indicator() instanceof TemplateRef ? indicator() : undefined,
                indicatorCtx: indicatorCtx() ?? {},
                label: label(),
                labelRef: label() instanceof TemplateRef ? label() : undefined,
                labelCtx: labelCtx() ?? {},
                optional: optional(),
                valid: valid()
            };
        });
    }, ...(ngDevMode ? [{ debugName: "steps" }] : []));
    /**
     * Returns the number of steps in the Stepper.
     * This is useful for determining how many steps are available.
     * @return number
     */
    stepsCount = computed(() => {
        return this.steps().length;
    }, ...(ngDevMode ? [{ debugName: "stepsCount" }] : []));
    /**
     * Indicates if the Stepper is currently finishing.
     * This is set to `true` when the finish() method is called and all steps are valid.
     * @return boolean
     */
    finishing = signal(false, ...(ngDevMode ? [{ debugName: "finishing" }] : []));
    #isFinishedEffect = effect(() => {
        this.finished.emit(this.finishing());
    }, ...(ngDevMode ? [{ debugName: "#isFinishedEffect" }] : []));
    #focusKeyManager;
    #focusKeyChange$;
    #focusKeyManagerInit = signal(false, ...(ngDevMode ? [{ debugName: "#focusKeyManagerInit" }] : []));
    stepButtons = viewChildren(StepButtonDirective, ...(ngDevMode ? [{ debugName: "stepButtons" }] : []));
    #stepButtonEffect = effect(() => {
        const isRtl = this.#isRtl();
        const isVertical = this.layout() === 'vertical';
        const linear = this.linear();
        if (untracked(this.#focusKeyManagerInit)) {
            this.#focusKeyManager
                .withHomeAndEnd(!linear)
                .withHorizontalOrientation(isVertical ? null : isRtl ? 'rtl' : 'ltr')
                .withVerticalOrientation(isVertical);
            return;
        }
        const stepButtons = this.stepButtons();
        if (stepButtons.length === 0) {
            return;
        }
        this.#focusKeyManager = new FocusKeyManager(stepButtons)
            .skipPredicate((item) => item.disabled)
            .withHomeAndEnd(!linear)
            .withHorizontalOrientation(isVertical ? null : isRtl ? 'rtl' : 'ltr')
            .withVerticalOrientation(isVertical);
        untracked(() => {
            this.#focusKeyChange$ = this.#focusKeyManager.change.pipe(distinctUntilChanged((previous, current) => {
                if (this.resetting()) {
                    this.resetting.set(false);
                    this.onReset.emit();
                    return false; // Resetting should always trigger a change
                }
                if (this.isStepOutOfScope(current)) {
                    return true;
                }
                if (this.isStepDisabled(current)) {
                    return true;
                }
                if (current > previous && this.linear() && !this.validation()) {
                    const nextIndex = this.findNextStepIndex(previous);
                    if (current !== nextIndex) {
                        this.#focusKeyManager.setActiveItem(previous);
                        return true;
                    }
                }
                if (current > previous && this.linear() && !this.previousStepsValid(current) && this.validation()) {
                    this.#focusKeyManager.setActiveItem(previous);
                    return true;
                }
                if (current > previous && !this.isStepValid(previous)) {
                    this.activeStepIndex.set(previous);
                    this.#focusKeyManager.setActiveItem(previous);
                    return true;
                }
                return false;
            }), tap((value) => {
                this.activeStepIndex.set(value);
                this.activeStepIndexChange.emit(value);
            }), takeUntilDestroyed(this.#destroyRef));
            this.#focusKeyChange$.subscribe();
            this.#focusKeyManagerInit.set(!!this.#focusKeyManager);
            this.activeStepIndex.update((prevIdx) => {
                return prevIdx ?? this.defaultActiveStepIndex();
            });
            // this.#focusKeyManager?.setFirstItemActive();
        });
    }, ...(ngDevMode ? [{ debugName: "#stepButtonEffect" }] : []));
    hostClasses = computed(() => {
        return {
            stepper: true,
            'stepper-vertical': this.layout() === 'vertical'
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    /**
     * Finish action. Attempts to finish the Stepper.
     */
    finish() {
        if (this.allStepsValid()) {
            this.finishing.set(true);
        }
    }
    /**
     * Next action. Moves to the next step in the Stepper.
     * If already on the last step, it will attempt to finish().
     */
    next() {
        const activeStepIndex = this.activeStepIndex();
        const stepsCount = this.stepsCount();
        if (activeStepIndex < stepsCount - 1) {
            this.#focusKeyManager?.setNextItemActive();
        }
        else {
            this.finish(); // Attempt finish if already on last step
        }
    }
    /**
     * Previous action. Moves to the previous step in the Stepper.
     * If already on the first step, it will do nothing.
     */
    prev() {
        if (this.activeStepIndex() > 0) {
            this.#focusKeyManager?.setPreviousItemActive();
        }
    }
    /**
     * Reset action. Resets the Stepper to its initial state.
     * This will reset the active step index and emit the onReset event.
     */
    reset() {
        this.finishing.set(false);
        this.resetting.set(true);
        setTimeout(() => {
            this.#focusKeyManager.setFirstItemActive();
        });
    }
    handleStepClick($event, idx) {
        this.#focusKeyManager?.setActiveItem(idx);
    }
    handleKeyDown($event) {
        if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes($event.key)) {
            this.#focusKeyManager?.onKeydown($event);
            return;
        }
        if (['Tab'].includes($event.key)) {
            this.#focusKeyManager?.tabOut.next();
        }
        return;
    }
    isStepDisabled(index) {
        return this.stepsContent()[index]?.disabled() ?? false;
    }
    isStepOutOfScope(index) {
        return index < 0 || index > this.stepsCount() - 1;
    }
    isStepValid(index) {
        if (!this.validation()) {
            return true;
        }
        return this.stepsContent()[index]?.valid() ?? true;
    }
    findNextStepIndex(startIndex) {
        const nextStepIndex = this.stepsContent().findIndex((step, index) => index > startIndex && !step.disabled() //&& step.valid() !== false
        );
        return nextStepIndex > startIndex ? nextStepIndex : startIndex;
    }
    previousStepsValid(index) {
        return !this.stepsContent()
            .slice(0, index)
            .find((step) => step.valid() === false && !step.disabled());
    }
    allStepsValid = computed(() => {
        if (!this.validation()) {
            return true;
        }
        return !this.stepsContent().find((step) => step.valid() === false && !step.disabled());
    }, ...(ngDevMode ? [{ debugName: "allStepsValid" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: StepperComponent, isStandalone: true, selector: "c-stepper", inputs: { activeStepIndexInput: { classPropertyName: "activeStepIndexInput", publicName: "activeStepIndex", isSignal: true, isRequired: false, transformFunction: null }, defaultActiveStepIndex: { classPropertyName: "defaultActiveStepIndex", publicName: "defaultActiveStepIndex", isSignal: true, isRequired: false, transformFunction: null }, idInput: { classPropertyName: "idInput", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, layout: { classPropertyName: "layout", publicName: "layout", isSignal: true, isRequired: false, transformFunction: null }, linear: { classPropertyName: "linear", publicName: "linear", isSignal: true, isRequired: false, transformFunction: null }, stepButtonLayout: { classPropertyName: "stepButtonLayout", publicName: "stepButtonLayout", isSignal: true, isRequired: false, transformFunction: null }, validation: { classPropertyName: "validation", publicName: "validation", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { finished: "finished", onReset: "onReset", activeStepIndexChange: "activeStepIndexChange" }, host: { properties: { "class": "hostClasses()", "id": "id()" }, classAttribute: "stepper" }, queries: [{ propertyName: "stepsContent", predicate: StepperStepComponent, descendants: true, isSignal: true }], viewQueries: [{ propertyName: "stepButtons", predicate: StepButtonDirective, descendants: true, isSignal: true }], exportAs: ["cStepper"], ngImport: i0, template: "@let isVertical = layout() === 'vertical';\n@let activeIdx = activeStepIndex();\n@let finished = finishing();\n<ol [attr.aria-orientation]=\"layout()\" class=\"stepper-steps\" role=\"tablist\">\n  @for (step of steps(); track step.id; let idx = $index, last = $last) {\n    @let isActive = !finished && activeIdx === idx;\n    @let isComplete = finished || idx < activeIdx;\n    @let isDisabled = step.disabled || finished || (linear() && idx > activeIdx + 1 && step.disabled);\n    @let stepId = step.id;\n    @let panelId = `${step.id}-panel`;\n    <li [class]=\"['stepper-step', stepButtonLayout()]\" role=\"presentation\">\n      <button\n        #stepButton\n        cStepButton\n        [attr.inert]=\"step.disabled || null\"\n        [attr.aria-selected]=\"isActive || null\"\n        [attr.aria-controls]=\"panelId\"\n        [tabindex]=\"isActive ? 0 : -1\"\n        [disabled]=\"isDisabled\"\n        [class]=\"{ 'stepper-step-button': true, active: isActive, complete: isComplete }\"\n        id=\"{{ stepId }}\"\n        type=\"button\"\n        role=\"tab\"\n        (click)=\"step.disabled || handleStepClick($event, idx)\"\n        (keydown)=\"handleKeyDown($event)\"\n      >\n        <span class=\"stepper-step-indicator\">\n          @if (isComplete) {\n            <span class=\"stepper-step-indicator-icon\"></span>\n          } @else {\n            <span class=\"stepper-step-indicator-text\">\n              <ng-container\n                [ngTemplateOutlet]=\"step.indicatorRef || indicatorTpl\"\n                [ngTemplateOutletContext]=\"step.indicatorCtx\"\n              />\n              <ng-template #indicatorTpl>{{ step.indicator }}</ng-template>\n            </span>\n          }\n        </span>\n        <span class=\"stepper-step-label\">\n          <ng-container [ngTemplateOutlet]=\"step.labelRef || labelTpl\" [ngTemplateOutletContext]=\"step.labelCtx\" />\n          <ng-template #labelTpl>{{ step.label }}</ng-template>\n        </span>\n      </button>\n      @if (!last) {\n        <div class=\"stepper-step-connector\"></div>\n      }\n      @if (isVertical) {\n        <div\n          class=\"stepper-step-content\"\n          cCollapse\n          [visible]=\"!resetting() && isActive\"\n          [attr.inert]=\"isActive ? null : true\"\n          id=\"{{ panelId }}\"\n          role=\"tabpanel\"\n          [attr.aria-hidden]=\"!isActive || null\"\n          [attr.aria-labelledby]=\"stepId\"\n          aria-live=\"polite\"\n        >\n          <ng-container *ngTemplateOutlet=\"step.templateRef ?? emptyTpl\" />\n        </div>\n      }\n    </li>\n  }\n</ol>\n@if (!isVertical) {\n  <div class=\"stepper-content\">\n    @for (step of steps(); track step.id; let idx = $index) {\n      @let isActive = !finished && activeIdx === idx;\n      @let stepId = step.id;\n      @let panelId = `${step.id}-panel`;\n      <div\n        [attr.inert]=\"isActive ? null : true\"\n        [class]=\"{ 'stepper-pane': true, show: isActive, active: isActive }\"\n        id=\"{{ panelId }}\"\n        role=\"tabpanel\"\n        [attr.aria-hidden]=\"!isActive || null\"\n        [attr.aria-labelledby]=\"stepId\"\n        aria-live=\"polite\"\n      >\n        <ng-container *ngTemplateOutlet=\"step.templateRef ?? emptyTpl\" />\n      </div>\n    }\n  </div>\n}\n\n<ng-template #contentTpl>\n  <ng-content />\n</ng-template>\n<ng-template #emptyTpl />\n", styles: [":host{display:block}\n"], dependencies: [{ kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: StepButtonDirective, selector: "button[cStepButton]", exportAs: ["cStepButton"] }, { kind: "directive", type: CollapseDirective, selector: "[cCollapse]", inputs: ["animate", "horizontal", "visible", "navbar", "duration", "transition"], outputs: ["visibleChange", "collapseChange"], exportAs: ["cCollapse"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-stepper', exportAs: 'cStepper', imports: [NgTemplateOutlet, StepButtonDirective, CollapseDirective], host: {
                        class: 'stepper',
                        '[class]': 'hostClasses()',
                        '[id]': 'id()'
                    }, template: "@let isVertical = layout() === 'vertical';\n@let activeIdx = activeStepIndex();\n@let finished = finishing();\n<ol [attr.aria-orientation]=\"layout()\" class=\"stepper-steps\" role=\"tablist\">\n  @for (step of steps(); track step.id; let idx = $index, last = $last) {\n    @let isActive = !finished && activeIdx === idx;\n    @let isComplete = finished || idx < activeIdx;\n    @let isDisabled = step.disabled || finished || (linear() && idx > activeIdx + 1 && step.disabled);\n    @let stepId = step.id;\n    @let panelId = `${step.id}-panel`;\n    <li [class]=\"['stepper-step', stepButtonLayout()]\" role=\"presentation\">\n      <button\n        #stepButton\n        cStepButton\n        [attr.inert]=\"step.disabled || null\"\n        [attr.aria-selected]=\"isActive || null\"\n        [attr.aria-controls]=\"panelId\"\n        [tabindex]=\"isActive ? 0 : -1\"\n        [disabled]=\"isDisabled\"\n        [class]=\"{ 'stepper-step-button': true, active: isActive, complete: isComplete }\"\n        id=\"{{ stepId }}\"\n        type=\"button\"\n        role=\"tab\"\n        (click)=\"step.disabled || handleStepClick($event, idx)\"\n        (keydown)=\"handleKeyDown($event)\"\n      >\n        <span class=\"stepper-step-indicator\">\n          @if (isComplete) {\n            <span class=\"stepper-step-indicator-icon\"></span>\n          } @else {\n            <span class=\"stepper-step-indicator-text\">\n              <ng-container\n                [ngTemplateOutlet]=\"step.indicatorRef || indicatorTpl\"\n                [ngTemplateOutletContext]=\"step.indicatorCtx\"\n              />\n              <ng-template #indicatorTpl>{{ step.indicator }}</ng-template>\n            </span>\n          }\n        </span>\n        <span class=\"stepper-step-label\">\n          <ng-container [ngTemplateOutlet]=\"step.labelRef || labelTpl\" [ngTemplateOutletContext]=\"step.labelCtx\" />\n          <ng-template #labelTpl>{{ step.label }}</ng-template>\n        </span>\n      </button>\n      @if (!last) {\n        <div class=\"stepper-step-connector\"></div>\n      }\n      @if (isVertical) {\n        <div\n          class=\"stepper-step-content\"\n          cCollapse\n          [visible]=\"!resetting() && isActive\"\n          [attr.inert]=\"isActive ? null : true\"\n          id=\"{{ panelId }}\"\n          role=\"tabpanel\"\n          [attr.aria-hidden]=\"!isActive || null\"\n          [attr.aria-labelledby]=\"stepId\"\n          aria-live=\"polite\"\n        >\n          <ng-container *ngTemplateOutlet=\"step.templateRef ?? emptyTpl\" />\n        </div>\n      }\n    </li>\n  }\n</ol>\n@if (!isVertical) {\n  <div class=\"stepper-content\">\n    @for (step of steps(); track step.id; let idx = $index) {\n      @let isActive = !finished && activeIdx === idx;\n      @let stepId = step.id;\n      @let panelId = `${step.id}-panel`;\n      <div\n        [attr.inert]=\"isActive ? null : true\"\n        [class]=\"{ 'stepper-pane': true, show: isActive, active: isActive }\"\n        id=\"{{ panelId }}\"\n        role=\"tabpanel\"\n        [attr.aria-hidden]=\"!isActive || null\"\n        [attr.aria-labelledby]=\"stepId\"\n        aria-live=\"polite\"\n      >\n        <ng-container *ngTemplateOutlet=\"step.templateRef ?? emptyTpl\" />\n      </div>\n    }\n  </div>\n}\n\n<ng-template #contentTpl>\n  <ng-content />\n</ng-template>\n<ng-template #emptyTpl />\n", styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { activeStepIndexInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "activeStepIndex", required: false }] }], defaultActiveStepIndex: [{ type: i0.Input, args: [{ isSignal: true, alias: "defaultActiveStepIndex", required: false }] }], idInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], layout: [{ type: i0.Input, args: [{ isSignal: true, alias: "layout", required: false }] }], linear: [{ type: i0.Input, args: [{ isSignal: true, alias: "linear", required: false }] }], stepButtonLayout: [{ type: i0.Input, args: [{ isSignal: true, alias: "stepButtonLayout", required: false }] }], validation: [{ type: i0.Input, args: [{ isSignal: true, alias: "validation", required: false }] }], finished: [{ type: i0.Output, args: ["finished"] }], onReset: [{ type: i0.Output, args: ["onReset"] }], activeStepIndexChange: [{ type: i0.Output, args: ["activeStepIndexChange"] }], stepsContent: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => StepperStepComponent), { ...{ descendants: true }, isSignal: true }] }], stepButtons: [{ type: i0.ViewChildren, args: [i0.forwardRef(() => StepButtonDirective), { isSignal: true }] }] } });

class StepperModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: StepperModule, imports: [StepperComponent, StepperStepComponent, StepButtonDirective], exports: [StepperComponent, StepperStepComponent, StepButtonDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: StepperModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [StepperComponent, StepperStepComponent, StepButtonDirective],
                    exports: [StepperComponent, StepperStepComponent, StepButtonDirective]
                }]
        }] });

class TabService {
    activeTabPaneIdx = new Subject();
    activeTabPaneIdx$ = this.activeTabPaneIdx.asObservable();
    setActiveTabIdx(tabContentState) {
        this.activeTabPaneIdx.next(tabContentState);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class TabPaneComponent {
    #changeDetectorRef = inject(ChangeDetectorRef);
    #tabService = inject(TabService);
    constructor() {
        this.subscribeTabService();
    }
    tabPaneIdx;
    tabContent;
    #tabServiceSubscription;
    set active(value) {
        const newValue = booleanAttribute(value);
        if (this.#active !== newValue) {
            this.#active = newValue;
            this.#changeDetectorRef.markForCheck();
        }
    }
    get active() {
        return this.#active;
    }
    #active = false;
    get hostClasses() {
        return {
            'tab-pane': true,
            fade: true,
            show: this.active,
            active: this.active
        };
    }
    role = 'tabpanel';
    ngOnDestroy() {
        this.subscribeTabService(false);
    }
    subscribeTabService(subscribe = true) {
        if (subscribe) {
            this.#tabServiceSubscription = this.#tabService.activeTabPaneIdx$.subscribe((tabContentState) => {
                if (tabContentState.tabContent === this.tabContent) {
                    this.active = tabContentState.activeIdx === this.tabPaneIdx;
                }
            });
        }
        else {
            this.#tabServiceSubscription?.unsubscribe();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabPaneComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: TabPaneComponent, isStandalone: true, selector: "c-tab-pane", inputs: { role: "role" }, host: { properties: { "class": "this.hostClasses", "attr.role": "this.role" }, classAttribute: "tab-pane" }, exportAs: ["cTabPane"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabPaneComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-tab-pane', template: '<ng-content />', exportAs: 'cTabPane', host: { class: 'tab-pane' }, styles: [":host{display:block}\n"] }]
        }], ctorParameters: () => [], propDecorators: { hostClasses: [{
                type: HostBinding,
                args: ['class']
            }], role: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.role']
            }] } });

class TabContentComponent {
    #changeDetectorRef = inject(ChangeDetectorRef);
    #tabService = inject(TabService);
    /**
     * Set active tabPane index
     * @type number
     */
    set activeTabPaneIdx(value) {
        const newValue = value;
        if (this.#activeTabPaneIdx != newValue) {
            this.#activeTabPaneIdx = newValue;
            this.activeTabPaneIdxChange?.emit(newValue);
            this.#changeDetectorRef.markForCheck();
            this.#changeDetectorRef.detectChanges();
        }
    }
    get activeTabPaneIdx() {
        return this.#activeTabPaneIdx;
    }
    #activeTabPaneIdx = -1;
    /**
     * Event emitted on the active tab pane index change.
     */
    activeTabPaneIdxChange = output();
    panes;
    #tabServiceSubscription;
    ngAfterContentInit() {
        this.subscribeTabService();
    }
    ngAfterContentChecked() {
        this.panes?.forEach((tabPane, index) => {
            tabPane.tabContent = this;
            tabPane.tabPaneIdx = index;
        });
        this.refreshTabPaneActive(this.activeTabPaneIdx);
        this.#tabService.setActiveTabIdx({ tabContent: this, activeIdx: this.activeTabPaneIdx });
    }
    ngOnChanges(changes) {
        if (changes['activeTabPaneIdx']?.currentValue) {
            this.#tabService.setActiveTabIdx({ tabContent: this, activeIdx: changes['activeTabPaneIdx'].currentValue });
        }
    }
    ngOnDestroy() {
        this.subscribeTabService(false);
    }
    subscribeTabService(subscribe = true) {
        if (subscribe) {
            this.#tabServiceSubscription = this.#tabService.activeTabPaneIdx$.subscribe((tabContentState) => {
                if (this === tabContentState.tabContent) {
                    this.activeTabPaneIdx = tabContentState.activeIdx;
                }
            });
        }
        else {
            this.#tabServiceSubscription?.unsubscribe();
        }
    }
    refreshTabPaneActive(idx) {
        // hack for active state pane refresh todo?
        this.panes?.forEach((tabPane, index) => {
            tabPane.active = idx === index;
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "21.1.4", type: TabContentComponent, isStandalone: true, selector: "c-tab-content", inputs: { activeTabPaneIdx: ["activeTabPaneIdx", "activeTabPaneIdx", numberAttribute] }, outputs: { activeTabPaneIdxChange: "activeTabPaneIdxChange" }, host: { classAttribute: "tab-content" }, queries: [{ propertyName: "panes", predicate: TabPaneComponent }], exportAs: ["cTabContent"], usesOnChanges: true, ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabContentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-tab-content', template: '<ng-content />', changeDetection: ChangeDetectionStrategy.OnPush, exportAs: 'cTabContent', host: { class: 'tab-content' }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { activeTabPaneIdx: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], activeTabPaneIdxChange: [{ type: i0.Output, args: ["activeTabPaneIdxChange"] }], panes: [{
                type: ContentChildren,
                args: [TabPaneComponent]
            }] } });

class TabContentRefDirective {
    #changeDetectorRef = inject(ChangeDetectorRef);
    #tabService = inject(TabService);
    constructor() {
        this.subscribeTabService();
    }
    #tabServiceSubscription;
    /**
     * Template Ref
     * @type TemplateRef
     */
    tabContentRef;
    /**
     * Set active state of tab content
     * @type boolean
     * @default false
     */
    set active(value) {
        const newValue = value;
        if (this.#active !== newValue) {
            this.#active = newValue;
            this.#changeDetectorRef.detectChanges();
        }
    }
    get active() {
        return this.#active;
    }
    #active = false;
    /**
     * Set disabled state of tab content
     * @type boolean
     */
    set disabled(value) {
        this.#disabled = value;
    }
    get disabled() {
        return this.#disabled || this.tabPaneIdx >= this.tabContentRef?.panes?.length;
    }
    #disabled = false;
    /**
     * c-tab-pane index respectively
     * @type number
     */
    tabPaneIdx = -1;
    get hostClasses() {
        return {
            active: this.active,
            disabled: this.disabled
        };
    }
    get isDisabled() {
        return this.disabled || null;
    }
    get attrDisabled() {
        return this.disabled ? '' : null;
    }
    get ariaSelected() {
        return this.active;
    }
    role = 'tab';
    get getTabindex() {
        return this.disabled ? '-1' : null;
    }
    ngOnChanges(changes) {
        if (changes['active']?.currentValue) {
            this.setActiveTabPane();
        }
    }
    toggleOpen($event) {
        $event.preventDefault();
        this.setActiveTabPane();
    }
    setActiveTabPane() {
        setTimeout(() => {
            if (this.tabPaneIdx < this.tabContentRef.panes.length) {
                this.active = true;
                this.#tabService.setActiveTabIdx({ tabContent: this.tabContentRef, activeIdx: this.tabPaneIdx });
            }
            else {
                this.active = false;
            }
        });
    }
    ngOnDestroy() {
        this.subscribeTabService(false);
    }
    subscribeTabService(subscribe = true) {
        if (subscribe) {
            this.#tabServiceSubscription = this.#tabService.activeTabPaneIdx$.subscribe((tabContentState) => {
                if (tabContentState.tabContent === this.tabContentRef) {
                    this.active = tabContentState.activeIdx === this.tabPaneIdx;
                }
            });
        }
        else {
            this.#tabServiceSubscription?.unsubscribe();
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabContentRefDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "16.1.0", version: "21.1.4", type: TabContentRefDirective, isStandalone: true, selector: "[cTabContent]", inputs: { tabContentRef: ["cTabContent", "tabContentRef"], active: ["active", "active", booleanAttribute], disabled: ["disabled", "disabled", booleanAttribute], tabPaneIdx: ["tabPaneIdx", "tabPaneIdx", numberAttribute], role: "role" }, host: { listeners: { "click": "toggleOpen($event)" }, properties: { "class": "this.hostClasses", "attr.aria-disabled": "this.isDisabled", "attr.disabled": "this.attrDisabled", "attr.aria-selected": "this.ariaSelected", "attr.role": "this.role", "attr.tabindex": "this.getTabindex" } }, usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabContentRefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cTabContent]'
                }]
        }], ctorParameters: () => [], propDecorators: { tabContentRef: [{
                type: Input,
                args: ['cTabContent']
            }], active: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], disabled: [{
                type: Input,
                args: [{ transform: booleanAttribute }]
            }], tabPaneIdx: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], hostClasses: [{
                type: HostBinding,
                args: ['class']
            }], isDisabled: [{
                type: HostBinding,
                args: ['attr.aria-disabled']
            }], attrDisabled: [{
                type: HostBinding,
                args: ['attr.disabled']
            }], ariaSelected: [{
                type: HostBinding,
                args: ['attr.aria-selected']
            }], role: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.role']
            }], getTabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], toggleOpen: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class TabsModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: TabsModule, imports: [TabContentComponent,
            TabContentRefDirective,
            TabPaneComponent], exports: [TabContentComponent,
            TabPaneComponent,
            TabContentRefDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsModule, providers: [
            TabService
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        TabContentComponent,
                        TabContentRefDirective,
                        TabPaneComponent
                    ],
                    exports: [
                        TabContentComponent,
                        TabPaneComponent,
                        TabContentRefDirective
                    ],
                    providers: [
                        TabService
                    ]
                }]
        }] });

class TabsService {
    activeItem = signal(undefined, ...(ngDevMode ? [{ debugName: "activeItem" }] : []));
    activeItemKey = signal(undefined, ...(ngDevMode ? [{ debugName: "activeItemKey" }] : []));
    id = signal(undefined, ...(ngDevMode ? [{ debugName: "id" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsService, decorators: [{
            type: Injectable
        }] });

let nextId = 0;
class TabsComponent {
    tabsService = inject(TabsService);
    /**
     * The active item key.
     * @type <string | number | undefined>
     */
    activeItemKey = model(...(ngDevMode ? [undefined, { debugName: "activeItemKey" }] : []));
    /**
     * The id attribute
     * @type string
     */
    tabsId = `tabs-${nextId++}`;
    id = input(this.tabsId, ...(ngDevMode ? [{ debugName: "id" }] : []));
    #activeItemEffect = effect(() => {
        this.tabsService.id.set(this.id());
        this.tabsService.activeItemKey.set(this.activeItemKey());
    }, ...(ngDevMode ? [{ debugName: "#activeItemEffect" }] : []));
    #tabsServiceEffect = effect(() => {
        this.activeItemKey.set(this.tabsService.activeItemKey());
    }, ...(ngDevMode ? [{ debugName: "#tabsServiceEffect" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: TabsComponent, isStandalone: true, selector: "c-tabs", inputs: { activeItemKey: { classPropertyName: "activeItemKey", publicName: "activeItemKey", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { activeItemKey: "activeItemKeyChange" }, host: { properties: { "id": "id()" }, classAttribute: "tabs" }, providers: [TabsService], exportAs: ["cTabs"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsComponent, decorators: [{
            type: Component,
            args: [{ exportAs: 'cTabs', selector: 'c-tabs', imports: [], template: '<ng-content />', providers: [TabsService], host: {
                        '[id]': 'id()',
                        class: 'tabs'
                    }, styles: [":host{display:block}\n"] }]
        }], propDecorators: { activeItemKey: [{ type: i0.Input, args: [{ isSignal: true, alias: "activeItemKey", required: false }] }, { type: i0.Output, args: ["activeItemKeyChange"] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }] } });

class TabDirective {
    #injector = inject(Injector);
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    #tabsService = inject(TabsService);
    /**
     * Disabled attribute
     * @return boolean
     * @default false
     */
    disabledInput = input(false, { ...(ngDevMode ? { debugName: "disabledInput" } : {}), transform: booleanAttribute, alias: 'disabled' });
    #disabled = linkedSignal(this.disabledInput, ...(ngDevMode ? [{ debugName: "#disabled" }] : []));
    attrDisabled = computed(() => this.#disabled() || null, ...(ngDevMode ? [{ debugName: "attrDisabled" }] : []));
    set disabled(value) {
        this.#disabled.set(value);
    }
    get disabled() {
        return this.#disabled();
    }
    /**
     * Item key.
     * @type string | number
     * @required
     */
    itemKey = input.required(...(ngDevMode ? [{ debugName: "itemKey" }] : []));
    /**
     * Element id attribute
     * @type string
     * @default undefined
     */
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    /**
     * aria-controls attribute
     * @type string
     * @default undefined
     */
    ariaControls = input(undefined, { ...(ngDevMode ? { debugName: "ariaControls" } : {}), alias: 'aria-controls' });
    isActive = signal(false, ...(ngDevMode ? [{ debugName: "isActive" }] : []));
    hostClasses = computed(() => {
        return {
            'nav-link': true,
            active: this.isActive(),
            disabled: this.#disabled()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    propId = computed(() => this.id() ?? `${this.#tabsService.id()}-tab-${this.itemKey()}`, ...(ngDevMode ? [{ debugName: "propId" }] : []));
    attrAriaControls = computed(() => this.ariaControls() ?? `${this.#tabsService.id()}-panel-${this.itemKey()}`, ...(ngDevMode ? [{ debugName: "attrAriaControls" }] : []));
    #disabledSignalEffect = effect(() => {
        const disabled = this.#disabled();
        if (!disabled) {
            const click$ = fromEvent(this.#elementRef.nativeElement, 'click');
            const focusIn$ = fromEvent(this.#elementRef.nativeElement, 'focusin');
            merge(focusIn$, click$)
                .pipe(filter(($event) => !disabled), tap(($event) => {
                this.#tabsService.activeItemKey.set(untracked(this.itemKey));
            }), takeWhile(() => !disabled), takeUntilDestroyed(this.#destroyRef))
                .subscribe();
        }
    }, ...(ngDevMode ? [{ debugName: "#disabledSignalEffect" }] : []));
    focus(origin) {
        this.#elementRef.nativeElement.focus();
    }
    ngOnInit() {
        runInInjectionContext(this.#injector, () => {
            effect(() => {
                const isActive = !this.#disabled() && this.#tabsService.activeItemKey() === this.itemKey();
                this.isActive.set(isActive);
            });
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: TabDirective, isStandalone: true, selector: "button[cTab]", inputs: { disabledInput: { classPropertyName: "disabledInput", publicName: "disabled", isSignal: true, isRequired: false, transformFunction: null }, itemKey: { classPropertyName: "itemKey", publicName: "itemKey", isSignal: true, isRequired: true, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, ariaControls: { classPropertyName: "ariaControls", publicName: "aria-controls", isSignal: true, isRequired: false, transformFunction: null } }, host: { attributes: { "type": "button", "role": "tab" }, properties: { "class": "hostClasses()", "attr.aria-selected": "isActive()", "attr.aria-controls": "attrAriaControls()", "attr.disabled": "attrDisabled() || null", "id": "propId()", "tabindex": "isActive() ? 0 : -1" } }, exportAs: ["cTab"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabDirective, decorators: [{
            type: Directive,
            args: [{
                    exportAs: 'cTab',
                    selector: 'button[cTab]',
                    host: {
                        '[class]': 'hostClasses()',
                        type: 'button',
                        role: 'tab',
                        '[attr.aria-selected]': 'isActive()',
                        '[attr.aria-controls]': 'attrAriaControls()',
                        '[attr.disabled]': 'attrDisabled() || null',
                        '[id]': 'propId()',
                        '[tabindex]': 'isActive() ? 0 : -1'
                    }
                }]
        }], propDecorators: { disabledInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "disabled", required: false }] }], itemKey: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemKey", required: true }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], ariaControls: [{ type: i0.Input, args: [{ isSignal: true, alias: "aria-controls", required: false }] }] } });

class TabsListComponent {
    #destroyRef = inject(DestroyRef);
    #elementRef = inject(ElementRef);
    tabsService = inject(TabsService);
    #rtlService = inject(RtlService);
    #isRtl = signal(false, ...(ngDevMode ? [{ debugName: "#isRtl" }] : []));
    constructor() {
        afterEveryRender({
            read: () => {
                this.#isRtl.set(this.#rtlService.isRTL(this.#elementRef.nativeElement));
            }
        });
    }
    /**
     * Specify a layout type for component.
     * @type 'fill' | 'justified' | undefined
     * @default undefined
     */
    layout = input(...(ngDevMode ? [undefined, { debugName: "layout" }] : []));
    /**
     * Set the variant to tabs, pills or underline.
     * @type 'pills' | 'tabs' | 'underline' | 'underline-border' | undefined
     * @default undefined
     */
    variant = input(...(ngDevMode ? [undefined, { debugName: "variant" }] : []));
    /**
     * Set the role to tab list.
     * @default 'tablist'
     */
    role = input('tablist', ...(ngDevMode ? [{ debugName: "role" }] : []));
    hostClasses = computed(() => {
        const layout = this.layout();
        const variant = this.variant();
        return {
            nav: true,
            [`nav-${layout}`]: layout,
            [`nav-${variant}`]: variant
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    tabs = contentChildren(TabDirective, ...(ngDevMode ? [{ debugName: "tabs" }] : []));
    #focusKeyManager;
    #tabsEffect = effect(() => {
        const tabs = this.tabs();
        if (tabs.length === 0) {
            return;
        }
        const isRtl = this.#isRtl();
        this.#focusKeyManager = new FocusKeyManager(tabs)
            .skipPredicate((tab) => tab.disabled === true)
            .withHorizontalOrientation(isRtl ? 'rtl' : 'ltr')
            .withHomeAndEnd()
            .withWrap();
        this.#focusKeyManager.change
            .pipe(tap((value) => {
            this.tabsService.activeItemKey.set(this.#focusKeyManager.activeItem?.itemKey());
            this.tabsService.activeItem.set(this.#focusKeyManager.activeItem);
        }), takeUntilDestroyed(this.#destroyRef))
            .subscribe();
        untracked(() => {
            setTimeout(() => {
                const activeItem = tabs.find((tab) => tab.isActive()) ?? tabs.find((tab) => !tab.disabled);
                const activeItemIndex = tabs.findIndex((tab) => tab === activeItem);
                this.#focusKeyManager?.updateActiveItem(activeItemIndex < 0 ? 0 : activeItemIndex);
                this.tabsService.activeItemKey.set(this.#focusKeyManager.activeItem?.itemKey());
                this.tabsService.activeItem.set(this.#focusKeyManager.activeItem);
            });
        });
    }, ...(ngDevMode ? [{ debugName: "#tabsEffect" }] : []));
    #tabsServiceEffect = effect(() => {
        const activeItemIndex = this.tabs().findIndex((tab) => untracked(tab.isActive) && untracked(tab.itemKey) === this.tabsService.activeItemKey());
        this.#focusKeyManager?.updateActiveItem(activeItemIndex < 0 ? 0 : activeItemIndex);
    }, ...(ngDevMode ? [{ debugName: "#tabsServiceEffect" }] : []));
    onKeyDown($event) {
        if (['ArrowLeft', 'ArrowRight'].includes($event.key)) {
            this.#focusKeyManager.onKeydown($event);
            return;
        }
        if (['Tab'].includes($event.key)) {
            this.#focusKeyManager?.tabOut.next();
        }
        return;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: TabsListComponent, isStandalone: true, selector: "c-tabs-list", inputs: { layout: { classPropertyName: "layout", publicName: "layout", isSignal: true, isRequired: false, transformFunction: null }, variant: { classPropertyName: "variant", publicName: "variant", isSignal: true, isRequired: false, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "keydown": "onKeyDown($event)" }, properties: { "attr.role": "role()", "class": "hostClasses()" } }, queries: [{ propertyName: "tabs", predicate: TabDirective, isSignal: true }], exportAs: ["cTabsList"], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsListComponent, decorators: [{
            type: Component,
            args: [{
                    exportAs: 'cTabsList',
                    selector: 'c-tabs-list',
                    template: '<ng-content />',
                    host: {
                        '[attr.role]': 'role()',
                        '[class]': 'hostClasses()',
                        '(keydown)': 'onKeyDown($event)'
                    }
                }]
        }], ctorParameters: () => [], propDecorators: { layout: [{ type: i0.Input, args: [{ isSignal: true, alias: "layout", required: false }] }], variant: [{ type: i0.Input, args: [{ isSignal: true, alias: "variant", required: false }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], tabs: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TabDirective), { isSignal: true }] }] } });

class TabsContentComponent {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsContentComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: TabsContentComponent, isStandalone: true, selector: "c-tabs-content", host: { classAttribute: "tab-content" }, exportAs: ["cTabsContent"], ngImport: i0, template: '<ng-content />', isInline: true });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabsContentComponent, decorators: [{
            type: Component,
            args: [{
                    exportAs: 'cTabsContent',
                    selector: 'c-tabs-content',
                    template: '<ng-content />',
                    host: {
                        class: 'tab-content'
                    }
                }]
        }] });

class TabPanelComponent {
    tabsService = inject(TabsService);
    /**
     * aria-labelledby attribute
     * @type string
     * @default undefined
     */
    ariaLabelledBy = input(undefined, { ...(ngDevMode ? { debugName: "ariaLabelledBy" } : {}), alias: 'aria-labelledby' });
    /**
     * Element id attribute
     * @type string
     * @default undefined
     */
    id = input(...(ngDevMode ? [undefined, { debugName: "id" }] : []));
    /**
     * Item key.
     * @type string | number
     * @required
     */
    itemKey = input.required(...(ngDevMode ? [{ debugName: "itemKey" }] : []));
    /**
     * Element role.
     * @type string
     * @default 'tabpanel'
     */
    role = input('tabpanel', ...(ngDevMode ? [{ debugName: "role" }] : []));
    /**
     * tabindex attribute.
     * @type number
     * @default 0
     */
    tabindex = input(0, { ...(ngDevMode ? { debugName: "tabindex" } : {}), transform: numberAttribute });
    /**
     * Enable fade in transition.
     * @type boolean
     * @default true
     */
    transition = input(true, ...(ngDevMode ? [{ debugName: "transition" }] : []));
    /**
     * visible change output
     * @type OutputEmitterRef<VisibleChangeEvent>
     */
    visibleChange = output();
    show = signal(false, ...(ngDevMode ? [{ debugName: "show" }] : []));
    visible = computed(() => {
        const visible = this.tabsService.activeItemKey() === this.itemKey() && !this.tabsService.activeItem()?.disabled;
        this.visibleChange?.emit({ itemKey: this.itemKey(), visible });
        return visible;
    }, ...(ngDevMode ? [{ debugName: "visible" }] : []));
    propId = computed(() => this.id() ?? `${this.tabsService.id()}-panel-${this.itemKey()}`, ...(ngDevMode ? [{ debugName: "propId" }] : []));
    attrAriaLabelledBy = computed(() => this.ariaLabelledBy() ?? `${this.tabsService.id()}-tab-${this.itemKey()}`, ...(ngDevMode ? [{ debugName: "attrAriaLabelledBy" }] : []));
    hostClasses = computed(() => ({
        'tab-pane': true,
        active: this.show(),
        fade: this.transition(),
        show: this.show(),
        invisible: this.tabsService.activeItem()?.disabled
    }), ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    onAnimationDone($event) {
        this.show.set(this.visible());
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: TabPanelComponent, isStandalone: true, selector: "c-tab-panel", inputs: { ariaLabelledBy: { classPropertyName: "ariaLabelledBy", publicName: "aria-labelledby", isSignal: true, isRequired: false, transformFunction: null }, id: { classPropertyName: "id", publicName: "id", isSignal: true, isRequired: false, transformFunction: null }, itemKey: { classPropertyName: "itemKey", publicName: "itemKey", isSignal: true, isRequired: true, transformFunction: null }, role: { classPropertyName: "role", publicName: "role", isSignal: true, isRequired: false, transformFunction: null }, tabindex: { classPropertyName: "tabindex", publicName: "tabindex", isSignal: true, isRequired: false, transformFunction: null }, transition: { classPropertyName: "transition", publicName: "transition", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange" }, host: { listeners: { "@fadeInOut.done": "onAnimationDone($event)" }, properties: { "class": "hostClasses()", "tabindex": "visible() ? tabindex() : -1", "attr.aria-labelledby": "attrAriaLabelledBy()", "id": "propId()", "attr.role": "role()", "@.disabled": "!transition()", "@fadeInOut": "visible() ? \"show\" : \"hide\"" } }, exportAs: ["cTabPanel"], ngImport: i0, template: '<ng-content />', isInline: true, animations: [
            trigger('fadeInOut', [
                state('show', style({ opacity: 1 })),
                state('hide', style({ opacity: 0 })),
                state('void', style({ opacity: 1 })),
                transition('* => *', [query('@*', [animateChild()], { optional: true }), animate('150ms linear')])
            ])
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: TabPanelComponent, decorators: [{
            type: Component,
            args: [{
                    exportAs: 'cTabPanel',
                    selector: 'c-tab-panel',
                    template: '<ng-content />',
                    host: {
                        '[class]': 'hostClasses()',
                        '[tabindex]': 'visible() ? tabindex() : -1',
                        '[attr.aria-labelledby]': 'attrAriaLabelledBy()',
                        '[id]': 'propId()',
                        '[attr.role]': 'role()',
                        '[@.disabled]': '!transition()',
                        '[@fadeInOut]': 'visible() ? "show" : "hide"',
                        '(@fadeInOut.done)': 'onAnimationDone($event)'
                    },
                    animations: [
                        trigger('fadeInOut', [
                            state('show', style({ opacity: 1 })),
                            state('hide', style({ opacity: 0 })),
                            state('void', style({ opacity: 1 })),
                            transition('* => *', [query('@*', [animateChild()], { optional: true }), animate('150ms linear')])
                        ])
                    ]
                }]
        }], propDecorators: { ariaLabelledBy: [{ type: i0.Input, args: [{ isSignal: true, alias: "aria-labelledby", required: false }] }], id: [{ type: i0.Input, args: [{ isSignal: true, alias: "id", required: false }] }], itemKey: [{ type: i0.Input, args: [{ isSignal: true, alias: "itemKey", required: true }] }], role: [{ type: i0.Input, args: [{ isSignal: true, alias: "role", required: false }] }], tabindex: [{ type: i0.Input, args: [{ isSignal: true, alias: "tabindex", required: false }] }], transition: [{ type: i0.Input, args: [{ isSignal: true, alias: "transition", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }] } });

class Tabs2Module {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: Tabs2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: Tabs2Module, imports: [TabsComponent, TabsListComponent, TabDirective, TabsContentComponent, TabPanelComponent], exports: [TabsComponent, TabsListComponent, TabDirective, TabsContentComponent, TabPanelComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: Tabs2Module, providers: [TabsService] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: Tabs2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [TabsComponent, TabsListComponent, TabDirective, TabsContentComponent, TabPanelComponent],
                    exports: [TabsComponent, TabsListComponent, TabDirective, TabsContentComponent, TabPanelComponent],
                    providers: [TabsService]
                }]
        }] });

class ToasterService {
    #toasterState = new BehaviorSubject({});
    toasterState$ = this.#toasterState.asObservable();
    setState(state) {
        this.#toasterState.next({ ...state });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class ToastComponent {
    changeDetectorRef = inject(ChangeDetectorRef);
    hostElement = inject(ElementRef);
    renderer = inject(Renderer2);
    toasterService = inject(ToasterService);
    dynamic = input(...(ngDevMode ? [undefined, { debugName: "dynamic" }] : []));
    placementInput = input(undefined, { ...(ngDevMode ? { debugName: "placementInput" } : {}), alias: 'placement' });
    get placement() {
        return this.placementInput();
    }
    /**
     * Auto hide the toast.
     * @return boolean
     */
    autohide = input(true, ...(ngDevMode ? [{ debugName: "autohide" }] : []));
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @return Colors
     */
    color = input('', ...(ngDevMode ? [{ debugName: "color" }] : []));
    /**
     * Delay hiding the toast (ms).
     * @return number
     */
    delay = input(5000, { ...(ngDevMode ? { debugName: "delay" } : {}), transform: numberAttribute });
    /**
     * Apply fade transition to the toast.
     * @return boolean
     */
    fade = input(true, ...(ngDevMode ? [{ debugName: "fade" }] : []));
    /**
     * Toggle the visibility of component.
     * @return boolean
     */
    visibleInput = input(false, { ...(ngDevMode ? { debugName: "visibleInput" } : {}), transform: booleanAttribute, alias: 'visible' });
    #visible = linkedSignal(this.visibleInput, ...(ngDevMode ? [{ debugName: "#visible" }] : []));
    #visibleEffect = effect(() => {
        const newValue = this.#visible();
        newValue ? this.setTimer() : this.clearTimer();
        this.visibleChange?.emit(newValue);
        this.changeDetectorRef.markForCheck();
    }, ...(ngDevMode ? [{ debugName: "#visibleEffect" }] : []));
    set visible(value) {
        this.#visible.set(value);
    }
    get visible() {
        return this.#visible();
    }
    /**
     * @ignore
     */
    index = input(0, { ...(ngDevMode ? { debugName: "index" } : {}), transform: numberAttribute });
    /**
     * Event emitted on visibility change. [docs]
     * @return <boolean>
     */
    visibleChange = output();
    /**
     * Event emitted on timer tick. [docs]
     * @return number
     */
    timer = output();
    timerId;
    clockId;
    clockTimerId;
    _clock;
    get clock() {
        return this._clock;
    }
    set clock(value) {
        this._clock = value;
        this.timer?.emit(this._clock);
        this.changeDetectorRef.markForCheck();
    }
    animationDisabled = computed(() => {
        return !this.fade();
    }, ...(ngDevMode ? [{ debugName: "animationDisabled" }] : []));
    get animateType() {
        return this.visible ? 'show' : 'hide';
    }
    hostClasses = computed(() => {
        const color = this.color();
        return {
            toast: true,
            show: true,
            [`bg-${color}`]: !!color,
            'border-0': !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnInit() {
        if (this.visible) {
            this.toasterService.setState({
                toast: this,
                show: this.visible,
                placement: this.placement
            });
            this.clearTimer();
            this.setTimer();
        }
    }
    ngOnDestroy() {
        this.clearTimer();
    }
    setTimer() {
        this.clearTimer();
        if (this.autohide() && this.visible) {
            this.timerId = this.delay() > 0 ? setTimeout(() => this.onClose(), this.delay()) : undefined;
            this.setClock();
        }
    }
    clearTimer() {
        this.clearClock();
        clearTimeout(this.timerId);
        this.timerId = undefined;
    }
    onClose() {
        this.clearTimer();
        this.toasterService.setState({
            toast: this,
            show: false,
            placement: this.placement
        });
    }
    setClock() {
        this.clearClock();
        this.clock = 0;
        this.clockId = setInterval(() => {
            this.clock += 1;
            this.changeDetectorRef.markForCheck();
        }, 1000);
        this.clockTimerId = setTimeout(() => {
            this.clearClock();
        }, this.delay());
    }
    clearClock() {
        clearTimeout(this.clockTimerId);
        clearInterval(this.clockId);
        this.clockId = undefined;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.1.0", version: "21.1.4", type: ToastComponent, isStandalone: true, selector: "c-toast", inputs: { dynamic: { classPropertyName: "dynamic", publicName: "dynamic", isSignal: true, isRequired: false, transformFunction: null }, placementInput: { classPropertyName: "placementInput", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, autohide: { classPropertyName: "autohide", publicName: "autohide", isSignal: true, isRequired: false, transformFunction: null }, color: { classPropertyName: "color", publicName: "color", isSignal: true, isRequired: false, transformFunction: null }, delay: { classPropertyName: "delay", publicName: "delay", isSignal: true, isRequired: false, transformFunction: null }, fade: { classPropertyName: "fade", publicName: "fade", isSignal: true, isRequired: false, transformFunction: null }, visibleInput: { classPropertyName: "visibleInput", publicName: "visible", isSignal: true, isRequired: false, transformFunction: null }, index: { classPropertyName: "index", publicName: "index", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { visibleChange: "visibleChange", timer: "timer" }, host: { listeners: { "mouseover": "clearTimer()", "mouseout": "setTimer()" }, properties: { "class": "hostClasses()", "@fadeInOut": "animateType", "@.disabled": "animationDisabled()" }, classAttribute: "toast show" }, exportAs: ["cToast"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block;overflow:hidden}\n"], animations: [
            trigger('fadeInOut', [
                state('show', style({ opacity: 1, height: '*', padding: '*', border: '*', margin: '*' })),
                state('hide', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                state('void', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                transition('show => hide', [animate('{{ time }} {{ easing }}')], {
                    params: { time: '300ms', easing: 'ease-out' }
                }),
                transition('hide => show', [animate('{{ time }} {{ easing }}')], {
                    params: { time: '300ms', easing: 'ease-in' }
                }),
                transition('show => void', [animate('{{ time }} {{ easing }}')], {
                    params: { time: '300ms', easing: 'ease-out' }
                }),
                transition('void => show', [animate('{{ time }} {{ easing }}')], {
                    params: { time: '300ms', easing: 'ease-in' }
                })
            ])
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-toast', template: '<ng-content />', exportAs: 'cToast', animations: [
                        trigger('fadeInOut', [
                            state('show', style({ opacity: 1, height: '*', padding: '*', border: '*', margin: '*' })),
                            state('hide', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                            state('void', style({ opacity: 0, height: 0, padding: 0, border: 0, margin: 0 })),
                            transition('show => hide', [animate('{{ time }} {{ easing }}')], {
                                params: { time: '300ms', easing: 'ease-out' }
                            }),
                            transition('hide => show', [animate('{{ time }} {{ easing }}')], {
                                params: { time: '300ms', easing: 'ease-in' }
                            }),
                            transition('show => void', [animate('{{ time }} {{ easing }}')], {
                                params: { time: '300ms', easing: 'ease-out' }
                            }),
                            transition('void => show', [animate('{{ time }} {{ easing }}')], {
                                params: { time: '300ms', easing: 'ease-in' }
                            })
                        ])
                    ], host: {
                        class: 'toast show',
                        '[class]': 'hostClasses()',
                        '(mouseover)': 'clearTimer()',
                        '(mouseout)': 'setTimer()',
                        '[@fadeInOut]': 'animateType',
                        '[@.disabled]': 'animationDisabled()'
                    }, styles: [":host{display:block;overflow:hidden}\n"] }]
        }], propDecorators: { dynamic: [{ type: i0.Input, args: [{ isSignal: true, alias: "dynamic", required: false }] }], placementInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], autohide: [{ type: i0.Input, args: [{ isSignal: true, alias: "autohide", required: false }] }], color: [{ type: i0.Input, args: [{ isSignal: true, alias: "color", required: false }] }], delay: [{ type: i0.Input, args: [{ isSignal: true, alias: "delay", required: false }] }], fade: [{ type: i0.Input, args: [{ isSignal: true, alias: "fade", required: false }] }], visibleInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "visible", required: false }] }], index: [{ type: i0.Input, args: [{ isSignal: true, alias: "index", required: false }] }], visibleChange: [{ type: i0.Output, args: ["visibleChange"] }], timer: [{ type: i0.Output, args: ["timer"] }] } });

class ToastBodyComponent {
    toast = inject(ToastComponent, { optional: true });
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "21.1.4", type: ToastBodyComponent, isStandalone: true, selector: "c-toast-body", host: { classAttribute: "toast-body" }, exportAs: ["cToastBody"], ngImport: i0, template: '<ng-content />', isInline: true, styles: [":host{display:block}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastBodyComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-toast-body', template: '<ng-content />', exportAs: 'cToastBody', host: {
                        class: 'toast-body',
                    }, styles: [":host{display:block}\n"] }]
        }] });

class ToastCloseDirective {
    #toasterService = inject(ToasterService);
    cToastClose = input(...(ngDevMode ? [undefined, { debugName: "cToastClose" }] : []));
    toggleOpen($event) {
        $event.preventDefault();
        this.#toasterService.setState({ show: false, toast: this.cToastClose() });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastCloseDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "17.1.0", version: "21.1.4", type: ToastCloseDirective, isStandalone: true, selector: "[cToastClose]", inputs: { cToastClose: { classPropertyName: "cToastClose", publicName: "cToastClose", isSignal: true, isRequired: false, transformFunction: null } }, host: { listeners: { "click": "toggleOpen($event)" } }, exportAs: ["cToastClose"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastCloseDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cToastClose]',
                    exportAs: 'cToastClose',
                    host: {
                        '(click)': 'toggleOpen($event)'
                    }
                }]
        }], propDecorators: { cToastClose: [{ type: i0.Input, args: [{ isSignal: true, alias: "cToastClose", required: false }] }] } });

class ToastHeaderComponent {
    #toast = inject(ToastComponent, { optional: true });
    toast = signal(this.#toast ?? undefined, ...(ngDevMode ? [{ debugName: "toast" }] : []));
    /**
     * Add close button to a toast header
     * @return boolean
     */
    closeButton = input(true, ...(ngDevMode ? [{ debugName: "closeButton" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: ToastHeaderComponent, isStandalone: true, selector: "c-toast-header", inputs: { closeButton: { classPropertyName: "closeButton", publicName: "closeButton", isSignal: true, isRequired: false, transformFunction: null } }, host: { classAttribute: "toast-header" }, exportAs: ["cToastHeader"], ngImport: i0, template: "<ng-container>\n  <ng-content />\n  @if (closeButton()) {\n    <button [cToastClose]=\"toast()\" [style]=\"{outline: 0}\" aria-label=\"close\" cButtonClose class=\"ms-auto\"></button>\n  }\n</ng-container>\n", dependencies: [{ kind: "directive", type: ToastCloseDirective, selector: "[cToastClose]", inputs: ["cToastClose"], exportAs: ["cToastClose"] }, { kind: "directive", type: ButtonCloseDirective, selector: "[cButtonClose]", inputs: ["white"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastHeaderComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-toast-header', exportAs: 'cToastHeader', imports: [ToastCloseDirective, ButtonCloseDirective], host: {
                        class: 'toast-header'
                    }, template: "<ng-container>\n  <ng-content />\n  @if (closeButton()) {\n    <button [cToastClose]=\"toast()\" [style]=\"{outline: 0}\" aria-label=\"close\" cButtonClose class=\"ms-auto\"></button>\n  }\n</ng-container>\n" }]
        }], propDecorators: { closeButton: [{ type: i0.Input, args: [{ isSignal: true, alias: "closeButton", required: false }] }] } });

class ToasterHostDirective {
    viewContainerRef = inject(ViewContainerRef);
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterHostDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "21.1.4", type: ToasterHostDirective, isStandalone: true, selector: "[cToasterHost]", exportAs: ["cToasterHost"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterHostDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cToasterHost]',
                    exportAs: 'cToasterHost'
                }]
        }] });

var ToasterPlacement;
(function (ToasterPlacement) {
    ToasterPlacement["Static"] = "static";
    ToasterPlacement["TopStart"] = "top-start";
    ToasterPlacement["TopCenter"] = "top-center";
    ToasterPlacement["TopEnd"] = "top-end";
    ToasterPlacement["MiddleStart"] = "middle-start";
    ToasterPlacement["MiddleCenter"] = "middle-center";
    ToasterPlacement["MiddleEnd"] = "middle-end";
    ToasterPlacement["BottomStart"] = "bottom-start";
    ToasterPlacement["BottomCenter"] = "bottom-center";
    ToasterPlacement["BottomEnd"] = "bottom-end";
})(ToasterPlacement || (ToasterPlacement = {}));
class ToasterComponent {
    #hostElement = inject(ElementRef);
    #renderer = inject(Renderer2);
    #toasterService = inject(ToasterService);
    #destroyRef = inject(DestroyRef);
    placements = Object.values(ToasterPlacement);
    toastsDynamic = [];
    /**
     * Toaster placement
     * @return TToasterPlacement
     */
    placementInput = input(ToasterPlacement.TopEnd, { ...(ngDevMode ? { debugName: "placementInput" } : {}), alias: 'placement' });
    get placement() {
        return this.placementInput();
    }
    /**
     * Toaster position
     * @return (string | 'absolute' | 'fixed' | 'static')
     */
    position = input('absolute', ...(ngDevMode ? [{ debugName: "position" }] : []));
    toasterHost = viewChild.required(ToasterHostDirective);
    contentToasts = contentChildren(ToastComponent, { ...(ngDevMode ? { debugName: "contentToasts" } : {}), read: ViewContainerRef });
    #contentToastsEffect = effect(() => {
        // Ensure that the contentToasts is available before accessing it
        // temp fix for: ASSERTION ERROR: Unexpected state: no hydration info available for a given TNode, which represents a view container. [Expected=> null != undefined <=Actual]
        this.contentToasts();
    }, ...(ngDevMode ? [{ debugName: "#contentToastsEffect" }] : []));
    hostClasses = computed(() => {
        const placement = this.placement;
        const position = this.position();
        return {
            toaster: true,
            'toast-container': true,
            [`position-${position}`]: !!position,
            'top-0': placement.includes('top'),
            'top-50': placement.includes('middle'),
            'bottom-0': placement.includes('bottom'),
            'start-0': placement.includes('start'),
            'start-50': placement.includes('center'),
            'end-0': placement.includes('end'),
            'translate-middle-x': placement.includes('center') && !placement.includes('middle'),
            'translate-middle-y': placement.includes('middle') && !placement.includes('center'),
            'translate-middle': placement.includes('middle') && placement.includes('center')
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    ngOnInit() {
        this.stateToasterSubscribe();
    }
    addToast(toast, props, options) {
        const componentRef = this.toasterHost().viewContainerRef.createComponent(toast, options);
        this.toastsDynamic.push(componentRef);
        const index = this.toastsDynamic.indexOf(componentRef);
        for (const [key, value] of Object.entries(props)) {
            componentRef.setInput(key, value);
        }
        componentRef.setInput('placement', this.placement);
        componentRef.setInput('dynamic', true);
        componentRef.setInput('index', index);
        componentRef.setInput('visible', true);
        componentRef.instance['visibleChange']?.emit(true);
        componentRef.changeDetectorRef?.detectChanges();
        return componentRef;
    }
    removeToast(state) {
        this.toastsDynamic?.forEach((item) => {
            if (state.toast?.dynamic() && item.instance === state.toast) {
                item.setInput('visible', false);
                item.instance['visibleChange'].emit(false);
                item.destroy();
            }
        });
        this.contentToasts()?.forEach((item) => {
            if (state.toast && item.element.nativeElement === state.toast.hostElement.nativeElement) {
                if (!state.toast.dynamic()) {
                    state.toast.visible = false;
                }
            }
        });
    }
    stateToasterSubscribe() {
        this.#toasterService.toasterState$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((state) => {
            if (state.show === false) {
                this.removeToast(state);
            }
            // if (state.show === true && state.toast?.dynamic() === undefined) {
            //   /* empty */
            // }
        });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.2.0", version: "21.1.4", type: ToasterComponent, isStandalone: true, selector: "c-toaster", inputs: { placementInput: { classPropertyName: "placementInput", publicName: "placement", isSignal: true, isRequired: false, transformFunction: null }, position: { classPropertyName: "position", publicName: "position", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "toaster toast-container" }, providers: [ToasterService], queries: [{ propertyName: "contentToasts", predicate: ToastComponent, read: ViewContainerRef, isSignal: true }], viewQueries: [{ propertyName: "toasterHost", first: true, predicate: ToasterHostDirective, descendants: true, isSignal: true }], exportAs: ["cToaster"], ngImport: i0, template: "<ng-template cToasterHost />\n<ng-content />\n", dependencies: [{ kind: "directive", type: ToasterHostDirective, selector: "[cToasterHost]", exportAs: ["cToasterHost"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToasterComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-toaster', exportAs: 'cToaster', imports: [ToasterHostDirective], host: {
                        class: 'toaster toast-container',
                        '[class]': 'hostClasses()'
                    }, providers: [ToasterService], template: "<ng-template cToasterHost />\n<ng-content />\n" }]
        }], propDecorators: { placementInput: [{ type: i0.Input, args: [{ isSignal: true, alias: "placement", required: false }] }], position: [{ type: i0.Input, args: [{ isSignal: true, alias: "position", required: false }] }], toasterHost: [{ type: i0.ViewChild, args: [i0.forwardRef(() => ToasterHostDirective), { isSignal: true }] }], contentToasts: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => ToastComponent), { ...{ read: ViewContainerRef }, isSignal: true }] }] } });

class ToastModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: ToastModule, imports: [ToastBodyComponent,
            ToastComponent,
            ToastCloseDirective,
            ToastHeaderComponent,
            ToasterComponent,
            ToasterHostDirective], exports: [ToastBodyComponent,
            ToastComponent,
            ToastCloseDirective,
            ToastHeaderComponent,
            ToasterComponent,
            ToasterHostDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastModule, providers: [ToasterService] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        ToastBodyComponent,
                        ToastComponent,
                        ToastCloseDirective,
                        ToastHeaderComponent,
                        ToasterComponent,
                        ToasterHostDirective
                    ],
                    providers: [ToasterService],
                    exports: [
                        ToastBodyComponent,
                        ToastComponent,
                        ToastCloseDirective,
                        ToastHeaderComponent,
                        ToasterComponent,
                        ToasterHostDirective
                    ]
                }]
        }] });

class WidgetStatAComponent extends CardComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    // override readonly color = input<Colors>();
    /**
     * Title of the widget to display
     * @type string
     */
    title = input(...(ngDevMode ? [undefined, { debugName: "title" }] : []));
    /**
     * Value for your widget to display
     * @type string
     */
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    templates = {};
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    #contentTemplatesEffect = effect(() => {
        this.contentTemplates().forEach((child) => {
            this.templates[child.id] = child.templateRef;
        });
    }, ...(ngDevMode ? [{ debugName: "#contentTemplatesEffect" }] : []));
    hostClasses = computed(() => {
        const color = this.color();
        return {
            card: true,
            [`bg-${color}`]: !!color,
            'text-white': !!color
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    get bodyClasses() {
        return {
            'pb-0': true,
            'd-flex': true,
            'justify-content-between': true,
            'align-items-start': true
        };
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatAComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatAComponent, isStandalone: true, selector: "c-widget-stat-a", inputs: { title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "card" }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cWidgetStatA"], usesInheritance: true, ngImport: i0, template: "<ng-container>\n  <c-card-body [class]=\"bodyClasses\">\n    <div>\n      @if (!!value() || templates?.['widgetValueTemplate']) {\n        <div class=\"fs-4 fw-semibold\">\n          <ng-container *ngTemplateOutlet=\"templates?.['widgetValueTemplate'] || defaultWidgetValueTemplate\" />\n        </div>\n      }\n      @if (!!title() || templates?.['widgetTitleTemplate']) {\n        <div>\n          <ng-container *ngTemplateOutlet=\"templates?.['widgetTitleTemplate'] || defaultWidgetTitleTemplate\" />\n        </div>\n      }\n    </div>\n    <ng-container *ngTemplateOutlet=\"templates?.['widgetActionTemplate'] || defaultWidgetActionTemplate\" />\n  </c-card-body>\n  <ng-container *ngTemplateOutlet=\"templates?.['widgetChartTemplate'] || defaultWidgetChartTemplate\" />\n</ng-container>\n\n<ng-template #defaultWidgetTitleTemplate>\n  {{ title() }}\n</ng-template>\n\n<ng-template #defaultWidgetValueTemplate>\n  {{ value() }}\n</ng-template>\n\n<ng-template #defaultWidgetChartTemplate>\n  <ng-content select=\".chart-wrapper\" />\n</ng-template>\n\n<ng-template #defaultWidgetActionTemplate>\n  <ng-content />\n</ng-template>\n", dependencies: [{ kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatAComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-a', exportAs: 'cWidgetStatA', imports: [CardBodyComponent, NgTemplateOutlet], host: { class: 'card', '[class]': 'hostClasses()' }, template: "<ng-container>\n  <c-card-body [class]=\"bodyClasses\">\n    <div>\n      @if (!!value() || templates?.['widgetValueTemplate']) {\n        <div class=\"fs-4 fw-semibold\">\n          <ng-container *ngTemplateOutlet=\"templates?.['widgetValueTemplate'] || defaultWidgetValueTemplate\" />\n        </div>\n      }\n      @if (!!title() || templates?.['widgetTitleTemplate']) {\n        <div>\n          <ng-container *ngTemplateOutlet=\"templates?.['widgetTitleTemplate'] || defaultWidgetTitleTemplate\" />\n        </div>\n      }\n    </div>\n    <ng-container *ngTemplateOutlet=\"templates?.['widgetActionTemplate'] || defaultWidgetActionTemplate\" />\n  </c-card-body>\n  <ng-container *ngTemplateOutlet=\"templates?.['widgetChartTemplate'] || defaultWidgetChartTemplate\" />\n</ng-container>\n\n<ng-template #defaultWidgetTitleTemplate>\n  {{ title() }}\n</ng-template>\n\n<ng-template #defaultWidgetValueTemplate>\n  {{ value() }}\n</ng-template>\n\n<ng-template #defaultWidgetChartTemplate>\n  <ng-content select=\".chart-wrapper\" />\n</ng-template>\n\n<ng-template #defaultWidgetActionTemplate>\n  <ng-content />\n</ng-template>\n" }]
        }], propDecorators: { title: [{ type: i0.Input, args: [{ isSignal: true, alias: "title", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class WidgetStatBComponent extends CardComponent {
    constructor() {
        super();
    }
    /**
     * Sets the color context of the component to one of CoreUI themed colors.
     * @type Colors
     */
    // override readonly color: InputSignal<Colors | undefined> = input();
    /**
     * Sets the text-color context of the component to one of CoreUI themed colors.
     * via TextColorDirective
     * @type TextColors
     */
    // override readonly textColor: InputSignal<TextColors | undefined> = input();
    /**
     * Title of the widget to display
     * @type string
     */
    title = input(...(ngDevMode ? [undefined, { debugName: "title" }] : []));
    /**
     * Helper text for your widget.
     * @type string
     */
    text = input(...(ngDevMode ? [undefined, { debugName: "text" }] : []));
    /**
     * Value for your widget to display
     * @type string
     */
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    /**
     * Invert colors from their default dark shade.
     * @type boolean
     */
    inverse = input(false, { ...(ngDevMode ? { debugName: "inverse" } : {}), transform: booleanAttribute });
    hostClasses = computed(() => {
        const color = this.color();
        const textColor = this.textColor();
        return {
            card: true,
            [`bg-${color}`]: !!color,
            [`text-${textColor}`]: !!textColor,
            'text-white': this.inverse()
        };
    }, ...(ngDevMode ? [{ debugName: "hostClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatBComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatBComponent, isStandalone: true, selector: "c-widget-stat-b", inputs: { title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, text: { classPropertyName: "text", publicName: "text", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, inverse: { classPropertyName: "inverse", publicName: "inverse", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "card" }, exportAs: ["cWidgetStatB"], usesInheritance: true, ngImport: i0, template: "<c-card-body>\n  @if (!!value()) {\n    <div class=\"fs-4 fw-semibold\">{{ value() }}</div>\n  }\n  @if (!!title()) {\n    <div>{{ title() }}</div>\n  }\n  <ng-content />\n  @if (text()) {\n    <small [class]=\"inverse() ? 'text-white text-opacity-75' : 'text-body-secondary'\">\n      {{ text() }}\n    </small>\n  }\n</c-card-body>\n", dependencies: [{ kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatBComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-b', exportAs: 'cWidgetStatB', imports: [CardBodyComponent], host: { class: 'card', '[class]': 'hostClasses()' }, template: "<c-card-body>\n  @if (!!value()) {\n    <div class=\"fs-4 fw-semibold\">{{ value() }}</div>\n  }\n  @if (!!title()) {\n    <div>{{ title() }}</div>\n  }\n  <ng-content />\n  @if (text()) {\n    <small [class]=\"inverse() ? 'text-white text-opacity-75' : 'text-body-secondary'\">\n      {{ text() }}\n    </small>\n  }\n</c-card-body>\n" }]
        }], ctorParameters: () => [], propDecorators: { title: [{ type: i0.Input, args: [{ isSignal: true, alias: "title", required: false }] }], text: [{ type: i0.Input, args: [{ isSignal: true, alias: "text", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], inverse: [{ type: i0.Input, args: [{ isSignal: true, alias: "inverse", required: false }] }] } });

class WidgetStatCComponent extends CardComponent {
    constructor() {
        super();
    }
    /**
     * Icon for your component.
     * @type string
     */
    icon = input(...(ngDevMode ? [undefined, { debugName: "icon" }] : []));
    /**
     * Title of the widget to display
     * @type string
     */
    title = input(...(ngDevMode ? [undefined, { debugName: "title" }] : []));
    /**
     * Value for your widget to display
     * @type string|number
     */
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    /**
     * Invert colors from their default dark shade.
     * @type boolean
     */
    inverse = input(false, { ...(ngDevMode ? { debugName: "inverse" } : {}), transform: booleanAttribute });
    templates = {};
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    #contentTemplatesEffect = effect(() => {
        this.contentTemplates().forEach((child) => {
            this.templates[child.id] = child.templateRef;
        });
    }, ...(ngDevMode ? [{ debugName: "#contentTemplatesEffect" }] : []));
    hostExtendedClass = computed(() => {
        return { ...this.hostClasses(), 'text-white': this.inverse() };
    }, ...(ngDevMode ? [{ debugName: "hostExtendedClass" }] : []));
    titleClasses = computed(() => {
        const inverse = this.inverse();
        return {
            'text-body-secondary': !inverse,
            'text-white': inverse,
            'text-opacity-75': inverse,
            [`text-${this.textColor()}`]: !!this.textColor()
        };
    }, ...(ngDevMode ? [{ debugName: "titleClasses" }] : []));
    valueClasses = computed(() => {
        return {
            'fs-4': !this.textColor(),
            'fw-semibold': true,
            ...this.titleClasses(),
            'text-opacity-75': false
        };
    }, ...(ngDevMode ? [{ debugName: "valueClasses" }] : []));
    iconClasses = computed(() => {
        return {
            'mb-4': !this.textColor(),
            'text-end': true,
            ...this.titleClasses()
        };
    }, ...(ngDevMode ? [{ debugName: "iconClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatCComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatCComponent, isStandalone: true, selector: "c-widget-stat-c", inputs: { icon: { classPropertyName: "icon", publicName: "icon", isSignal: true, isRequired: false, transformFunction: null }, title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null }, inverse: { classPropertyName: "inverse", publicName: "inverse", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostExtendedClass()" } }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cWidgetStatC"], usesInheritance: true, ngImport: i0, template: "<c-card-body>\n  @if (icon() || templates?.['widgetIconTemplate']) {\n    <div [class]=\"iconClasses()\">\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetIconTemplate'] || defaultWidgetIconTemplate\" />\n    </div>\n  }\n  @if (!!value()) {\n    <div [class]=\"valueClasses()\">\n      {{ value() }}\n    </div>\n  }\n  @if (!!title()) {\n    <div [class]=\"titleClasses()\">\n      {{ title() }}\n    </div>\n  }\n  @if (templates?.['widgetProgressTemplate']) {\n    <ng-container *ngTemplateOutlet=\"templates?.['widgetProgressTemplate'] || defaultWidgetProgressTemplate\" />\n  }\n</c-card-body>\n\n<ng-template #defaultWidgetIconTemplate>\n  {{ icon() }}\n</ng-template>\n\n<ng-template #defaultWidgetProgressTemplate>\n  <ng-content />\n</ng-template>\n", dependencies: [{ kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatCComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-c', exportAs: 'cWidgetStatC', imports: [CardBodyComponent, NgTemplateOutlet], host: { '[class]': 'hostExtendedClass()' }, template: "<c-card-body>\n  @if (icon() || templates?.['widgetIconTemplate']) {\n    <div [class]=\"iconClasses()\">\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetIconTemplate'] || defaultWidgetIconTemplate\" />\n    </div>\n  }\n  @if (!!value()) {\n    <div [class]=\"valueClasses()\">\n      {{ value() }}\n    </div>\n  }\n  @if (!!title()) {\n    <div [class]=\"titleClasses()\">\n      {{ title() }}\n    </div>\n  }\n  @if (templates?.['widgetProgressTemplate']) {\n    <ng-container *ngTemplateOutlet=\"templates?.['widgetProgressTemplate'] || defaultWidgetProgressTemplate\" />\n  }\n</c-card-body>\n\n<ng-template #defaultWidgetIconTemplate>\n  {{ icon() }}\n</ng-template>\n\n<ng-template #defaultWidgetProgressTemplate>\n  <ng-content />\n</ng-template>\n" }]
        }], ctorParameters: () => [], propDecorators: { icon: [{ type: i0.Input, args: [{ isSignal: true, alias: "icon", required: false }] }], title: [{ type: i0.Input, args: [{ isSignal: true, alias: "title", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], inverse: [{ type: i0.Input, args: [{ isSignal: true, alias: "inverse", required: false }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class WidgetStatDComponent extends CardComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    // override readonly color = input<Colors>();
    /**
     * Values and subtitles for your component.
     * @type WidgetStatDValue
     */
    values = input(...(ngDevMode ? [undefined, { debugName: "values" }] : []));
    headerClasses = computed(() => {
        return {
            'position-relative': true,
            'd-flex': true,
            'justify-content-center': true,
            'align-items-center': true,
            [`bg-${this.color()}`]: this.color()
        };
    }, ...(ngDevMode ? [{ debugName: "headerClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatDComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatDComponent, isStandalone: true, selector: "c-widget-stat-d", inputs: { values: { classPropertyName: "values", publicName: "values", isSignal: true, isRequired: false, transformFunction: null } }, host: { classAttribute: "card" }, exportAs: ["cWidgetStatD"], usesInheritance: true, ngImport: i0, template: "<c-card-header [class]=\"headerClasses()\">\n  <ng-content />\n</c-card-header>\n<c-card-body cRow class=\"text-center\">\n  @for (item of values(); track item; let i = $index) {\n    @if (i % 2 !== 0) {\n      <div class=\"vr\"></div>\n    }\n    <c-col>\n      <div class=\"fs-5 fw-semibold\">{{ item.value }}</div>\n      <div class=\"text-uppercase text-body-secondary small\">{{ item.title }}</div>\n    </c-col>\n  }\n</c-card-body>\n", dependencies: [{ kind: "component", type: CardHeaderComponent, selector: "c-card-header, [c-card-header]" }, { kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }, { kind: "component", type: ColComponent, selector: "c-col" }, { kind: "directive", type: RowDirective, selector: "[cRow]", inputs: ["xs", "sm", "md", "lg", "xl", "xxl"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatDComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-d', exportAs: 'cWidgetStatD', imports: [CardHeaderComponent, CardBodyComponent, ColComponent, RowDirective], host: { class: 'card' }, template: "<c-card-header [class]=\"headerClasses()\">\n  <ng-content />\n</c-card-header>\n<c-card-body cRow class=\"text-center\">\n  @for (item of values(); track item; let i = $index) {\n    @if (i % 2 !== 0) {\n      <div class=\"vr\"></div>\n    }\n    <c-col>\n      <div class=\"fs-5 fw-semibold\">{{ item.value }}</div>\n      <div class=\"text-uppercase text-body-secondary small\">{{ item.title }}</div>\n    </c-col>\n  }\n</c-card-body>\n" }]
        }], propDecorators: { values: [{ type: i0.Input, args: [{ isSignal: true, alias: "values", required: false }] }] } });

class WidgetStatEComponent extends CardComponent {
    constructor() {
        super();
    }
    /**
     * Title of the widget to display
     * @type string
     */
    title = input(...(ngDevMode ? [undefined, { debugName: "title" }] : []));
    /**
     * Value for your widget to display
     * @type string | number
     */
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    titleClasses = computed(() => {
        const textColor = this.textColor();
        return {
            'text-body-secondary': !textColor,
            small: true,
            'text-uppercase': true,
            'fw-semibold': true,
            [`text-${textColor}`]: !!textColor
        };
    }, ...(ngDevMode ? [{ debugName: "titleClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatEComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatEComponent, isStandalone: true, selector: "c-widget-stat-e", inputs: { title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, exportAs: ["cWidgetStatE"], usesInheritance: true, ngImport: i0, template: "<c-card-body class=\"text-center\">\n  @if (!!title()) {\n    <div [class]=\"titleClasses()\">{{ title() }}</div>\n  }\n  @if (!!value()) {\n    <div class=\"fs-6 fw-semibold py-3\">{{ value() }}</div>\n  }\n  <ng-content />\n</c-card-body>\n", dependencies: [{ kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatEComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-e', exportAs: 'cWidgetStatE', imports: [CardBodyComponent], template: "<c-card-body class=\"text-center\">\n  @if (!!title()) {\n    <div [class]=\"titleClasses()\">{{ title() }}</div>\n  }\n  @if (!!value()) {\n    <div class=\"fs-6 fw-semibold py-3\">{{ value() }}</div>\n  }\n  <ng-content />\n</c-card-body>\n" }]
        }], ctorParameters: () => [], propDecorators: { title: [{ type: i0.Input, args: [{ isSignal: true, alias: "title", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }] } });

class WidgetStatFComponent extends CardComponent {
    /**
     * Sets the color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    // override readonly color = input<Colors>();
    /**
     * Sets the text-color context of the component to one of CoreUI’s themed colors.
     * @type Colors
     */
    // override readonly textColor = input<TextColors | 'white' | 'muted'>();
    /**
     * Footer for your widget
     * @type string
     */
    footer = input(...(ngDevMode ? [undefined, { debugName: "footer" }] : []));
    /**
     * Icon for your widget
     * @type string
     */
    icon = input(...(ngDevMode ? [undefined, { debugName: "icon" }] : []));
    /**
     * Set padding of your component.
     * @type boolean
     */
    padding = input(false, { ...(ngDevMode ? { debugName: "padding" } : {}), transform: booleanAttribute });
    /**
     * Title of the widget to display
     * @type string
     */
    title = input(...(ngDevMode ? [undefined, { debugName: "title" }] : []));
    /**
     * Value for your widget to display
     * @type string
     */
    value = input(...(ngDevMode ? [undefined, { debugName: "value" }] : []));
    templates = {};
    contentTemplates = contentChildren(TemplateIdDirective, { ...(ngDevMode ? { debugName: "contentTemplates" } : {}), descendants: true });
    #contentTemplatesEffect = effect(() => {
        this.contentTemplates().forEach((child) => {
            this.templates[child.id] = child.templateRef;
        });
    }, ...(ngDevMode ? [{ debugName: "#contentTemplatesEffect" }] : []));
    cardBodyClasses = computed(() => {
        return {
            'd-flex': true,
            'align-items-center': true,
            'p-0': !this.padding()
        };
    }, ...(ngDevMode ? [{ debugName: "cardBodyClasses" }] : []));
    iconClasses = computed(() => {
        const color = this.color();
        const padding = this.padding();
        return {
            'me-3': !this.textColor(),
            'text-white': true,
            [`bg-${color}`]: !!color,
            'p-3': padding,
            'p-4': !padding,
            'rounded-start-1': !padding
        };
    }, ...(ngDevMode ? [{ debugName: "iconClasses" }] : []));
    titleClasses = computed(() => {
        const textColor = this.textColor();
        return {
            'text-body-secondary': !textColor,
            small: true,
            'text-uppercase': true,
            'fw-semibold': true,
            [`text-${textColor}`]: !!textColor
        };
    }, ...(ngDevMode ? [{ debugName: "titleClasses" }] : []));
    valueClasses = computed(() => {
        const textColor = this.textColor();
        return {
            'fs-6': !textColor,
            'fw-semibold': true,
            [`text-${textColor}`]: !!textColor
        };
    }, ...(ngDevMode ? [{ debugName: "valueClasses" }] : []));
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatFComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "21.1.4", type: WidgetStatFComponent, isStandalone: true, selector: "c-widget-stat-f", inputs: { footer: { classPropertyName: "footer", publicName: "footer", isSignal: true, isRequired: false, transformFunction: null }, icon: { classPropertyName: "icon", publicName: "icon", isSignal: true, isRequired: false, transformFunction: null }, padding: { classPropertyName: "padding", publicName: "padding", isSignal: true, isRequired: false, transformFunction: null }, title: { classPropertyName: "title", publicName: "title", isSignal: true, isRequired: false, transformFunction: null }, value: { classPropertyName: "value", publicName: "value", isSignal: true, isRequired: false, transformFunction: null } }, host: { properties: { "class": "hostClasses()" }, classAttribute: "card" }, queries: [{ propertyName: "contentTemplates", predicate: TemplateIdDirective, descendants: true, isSignal: true }], exportAs: ["cWidgetStatB"], usesInheritance: true, ngImport: i0, template: "<ng-container>\n  <c-card-body [class]=\"cardBodyClasses()\">\n    <div [class]=\"iconClasses()\">\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetIconTemplate'] || defaultWidgetIconTemplate\" />\n    </div>\n    <div>\n      <div [class]=\"valueClasses()\">{{ value() }}</div>\n      <div [class]=\"titleClasses()\">{{ title() }}</div>\n    </div>\n  </c-card-body>\n  @if (footer() || templates?.['widgetFooterTemplate']) {\n    <c-card-footer>\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetFooterTemplate'] || defaultFooterIconTemplate\" />\n    </c-card-footer>\n  }\n</ng-container>\n\n<ng-template #defaultWidgetIconTemplate>\n  <span>{{ icon() }}</span>\n</ng-template>\n\n<ng-template #defaultFooterIconTemplate>\n  <span>{{ footer() }}</span>\n</ng-template>\n", dependencies: [{ kind: "component", type: CardBodyComponent, selector: "c-card-body, [c-card-body]" }, { kind: "component", type: CardFooterComponent, selector: "c-card-footer, [c-card-footer]" }, { kind: "directive", type: NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetStatFComponent, decorators: [{
            type: Component,
            args: [{ selector: 'c-widget-stat-f', exportAs: 'cWidgetStatB', imports: [CardBodyComponent, CardFooterComponent, NgTemplateOutlet], host: { class: 'card', '[class]': 'hostClasses()' }, template: "<ng-container>\n  <c-card-body [class]=\"cardBodyClasses()\">\n    <div [class]=\"iconClasses()\">\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetIconTemplate'] || defaultWidgetIconTemplate\" />\n    </div>\n    <div>\n      <div [class]=\"valueClasses()\">{{ value() }}</div>\n      <div [class]=\"titleClasses()\">{{ title() }}</div>\n    </div>\n  </c-card-body>\n  @if (footer() || templates?.['widgetFooterTemplate']) {\n    <c-card-footer>\n      <ng-container *ngTemplateOutlet=\"templates?.['widgetFooterTemplate'] || defaultFooterIconTemplate\" />\n    </c-card-footer>\n  }\n</ng-container>\n\n<ng-template #defaultWidgetIconTemplate>\n  <span>{{ icon() }}</span>\n</ng-template>\n\n<ng-template #defaultFooterIconTemplate>\n  <span>{{ footer() }}</span>\n</ng-template>\n" }]
        }], propDecorators: { footer: [{ type: i0.Input, args: [{ isSignal: true, alias: "footer", required: false }] }], icon: [{ type: i0.Input, args: [{ isSignal: true, alias: "icon", required: false }] }], padding: [{ type: i0.Input, args: [{ isSignal: true, alias: "padding", required: false }] }], title: [{ type: i0.Input, args: [{ isSignal: true, alias: "title", required: false }] }], value: [{ type: i0.Input, args: [{ isSignal: true, alias: "value", required: false }] }], contentTemplates: [{ type: i0.ContentChildren, args: [i0.forwardRef(() => TemplateIdDirective), { ...{ descendants: true }, isSignal: true }] }] } });

class WidgetModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "21.1.4", ngImport: i0, type: WidgetModule, imports: [WidgetStatAComponent,
            WidgetStatBComponent,
            WidgetStatCComponent,
            WidgetStatDComponent,
            WidgetStatEComponent,
            WidgetStatFComponent], exports: [WidgetStatAComponent,
            WidgetStatBComponent,
            WidgetStatCComponent,
            WidgetStatDComponent,
            WidgetStatEComponent,
            WidgetStatFComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetModule });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "21.1.4", ngImport: i0, type: WidgetModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        WidgetStatAComponent,
                        WidgetStatBComponent,
                        WidgetStatCComponent,
                        WidgetStatDComponent,
                        WidgetStatEComponent,
                        WidgetStatFComponent
                    ],
                    exports: [
                        WidgetStatAComponent,
                        WidgetStatBComponent,
                        WidgetStatCComponent,
                        WidgetStatDComponent,
                        WidgetStatEComponent,
                        WidgetStatFComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of coreui-angular
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccordionButtonDirective, AccordionComponent, AccordionItemComponent, AccordionModule, AlertComponent, AlertHeadingDirective, AlertLinkDirective, AlertModule, AlignDirective, AutocompleteDirective, AutocompleteModule, AvatarComponent, AvatarModule, BackdropService, BadgeComponent, BadgeModule, BgColorDirective, BorderDirective, BreadcrumbComponent, BreadcrumbItemComponent, BreadcrumbModule, BreadcrumbRouterComponent, BreadcrumbRouterService, BreakpointInfix, ButtonCloseDirective, ButtonDirective, ButtonGroupComponent, ButtonGroupModule, ButtonModule, ButtonToolbarComponent, CalendarComponent, CalendarModule, CalendarMonthComponent, CalendarNavigationComponent, CalloutComponent, CalloutModule, CardBodyComponent, CardComponent, CardFooterComponent, CardGroupComponent, CardHeaderActionsComponent, CardHeaderComponent, CardImgDirective, CardImgOverlayComponent, CardLinkDirective, CardModule, CardSubtitleDirective, CardTextDirective, CardTitleDirective, CarouselCaptionComponent, CarouselComponent, CarouselConfig, CarouselControlComponent, CarouselIndicatorsComponent, CarouselInnerComponent, CarouselItemComponent, CarouselModule, ClassToggleService, ColComponent, ColDirective, CollapseDirective, CollapseModule, ColorModeService, ContainerComponent, DatePickerComponent, DatePickerModule, DateRangePickerComponent, DateRangePickerModule, DropdownCloseDirective, DropdownComponent, DropdownDividerDirective, DropdownHeaderDirective, DropdownItemDirective, DropdownItemPlainDirective, DropdownMenuDirective, DropdownModule, DropdownService, DropdownToggleDirective, ElementCoverComponent, ElementCoverModule, ElementRefDirective, FooterComponent, FooterModule, FormCheckComponent, FormCheckInputDirective, FormCheckLabelDirective, FormControlDirective, FormDirective, FormFeedbackComponent, FormFloatingDirective, FormLabelDirective, FormModule, FormPasswordDirective, FormSelectDirective, FormTextDirective, GridModule, GutterDirective, HeaderBrandComponent, HeaderComponent, HeaderDividerComponent, HeaderModule, HeaderNavComponent, HeaderTextComponent, HeaderTogglerDirective, HtmlAttributesDirective, ImgDirective, ImgModule, InMemoryStorageService, InputGroupComponent, InputGroupTextDirective, IntersectionService, ListGroupDirective, ListGroupItemDirective, ListGroupModule, ListenersService, LoadingButtonComponent, LoadingButtonModule, LocalStorageService, ModalBodyComponent, ModalComponent, ModalContentComponent, ModalDialogComponent, ModalFooterComponent, ModalHeaderComponent, ModalModule, ModalService, ModalTitleDirective, ModalToggleDirective, MultiSelectComponent, MultiSelectModule, MultiSelectOptgroupComponent, MultiSelectOptgroupLabelComponent, MultiSelectOptionComponent, NavComponent, NavItemComponent, NavLinkDirective, NavModule, NavbarBrandDirective, NavbarComponent, NavbarModule, NavbarNavComponent, NavbarTextComponent, NavbarTogglerDirective, OffcanvasBodyComponent, OffcanvasComponent, OffcanvasHeaderComponent, OffcanvasModule, OffcanvasService, OffcanvasTitleDirective, OffcanvasToggleDirective, OneTimePasswordComponent, OneTimePasswordModule, OtpDirective, PageItemComponent, PageItemDirective, PageLinkDirective, PaginationComponent, PaginationModule, PlaceholderAnimationDirective, PlaceholderDirective, PlaceholderModule, PopoverComponent, PopoverDirective, PopoverModule, ProgressBarComponent, ProgressBarDirective, ProgressComponent, ProgressModule, ProgressStackedComponent, RangeSliderComponent, RangeSliderModule, RatingComponent, RatingModule, RoundedDirective, RowComponent, RowDirective, RtlService, ShadowOnScrollDirective, SharedModule, SidebarBrandComponent, SidebarComponent, SidebarFooterComponent, SidebarHeaderComponent, SidebarModule, SidebarNavComponent, SidebarNavHelper, SidebarService, SidebarToggleDirective, SidebarTogglerDirective, SmartPaginationComponent, SmartPaginationModule, SmartTableComponent, SmartTableFilterComponent, SmartTableModule, SpinnerComponent, SpinnerModule, StepButtonDirective, StepperComponent, StepperModule, StepperStepComponent, TabContentComponent, TabContentRefDirective, TabDirective, TabPaneComponent, TabPanelComponent, TabService, TableActiveDirective, TableColorDirective, TableDirective, TableModule, Tabs2Module, TabsComponent, TabsContentComponent, TabsListComponent, TabsModule, TabsService, TemplateIdDirective, TextBgColorDirective, TextColorDirective, ThemeDirective, TimePickerComponent, TimePickerModule, ToastBodyComponent, ToastCloseDirective, ToastComponent, ToastHeaderComponent, ToastModule, ToasterComponent, ToasterHostDirective, ToasterPlacement, ToasterService, TooltipComponent, TooltipDirective, TooltipModule, UIDService, UtilitiesModule, WidgetModule, WidgetStatAComponent, WidgetStatBComponent, WidgetStatCComponent, WidgetStatDComponent, WidgetStatEComponent, WidgetStatFComponent };
